// Color theme definitions
export interface ColorTheme {
  name: string;
  tags?: string[];
  light: {
    primary: string;
    primaryShadow: string;
    accent: string;
    accentShadow: string;
    background: string;
    cardBackground: string;
    text: string;
    extra: string;
    h1Color: 'primary' | 'accent' | 'text';
    h2Color: 'primary' | 'accent' | 'text';
    h3Color: 'primary' | 'accent' | 'text';
  };
  dark: {
    primary: string;
    primaryShadow: string;
    accent: string;
    accentShadow: string;
    background: string;
    cardBackground: string;
    text: string;
    extra: string;
    h1Color: 'primary' | 'accent' | 'text';
    h2Color: 'primary' | 'accent' | 'text';
    h3Color: 'primary' | 'accent' | 'text';
  };
}

export const colorThemes: ColorTheme[] = [
  // Vibrant & Energetic
  {
    name: 'Electric Sunset',
    tags: ['vibrant', 'energetic', 'bold'],
    light: {
      primary: '#FF3366',
      primaryShadow: '#CC2952',
      accent: '#FFD600',
      accentShadow: '#CCAB00',
      background: '#FFFFFF',
      cardBackground: '#FFF8F0',
      text: '#1A1A1A',
      extra: '#FF6B00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5580',
      primaryShadow: '#FF3366',
      accent: '#FFE033',
      accentShadow: '#FFD600',
      background: '#0F0F0F',
      cardBackground: '#1F1F1F',
      text: '#F5F5F5',
      extra: '#FF8533',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neon Pulse',
    tags: ['vibrant', 'modern', 'tech'],
    light: {
      primary: '#00F5FF',
      primaryShadow: '#00C4CC',
      accent: '#FF00FF',
      accentShadow: '#CC00CC',
      background: '#FFFFFF',
      cardBackground: '#F0FEFF',
      text: '#2D2D2D',
      extra: '#7FFF00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#33F7FF',
      primaryShadow: '#00F5FF',
      accent: '#FF33FF',
      accentShadow: '#FF00FF',
      background: '#0A0A0A',
      cardBackground: '#1A1A1A',
      text: '#E8E8E8',
      extra: '#99FF33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Pastel & Soft
  {
    name: 'Lavender Dreams',
    tags: ['pastel', 'soft', 'calming'],
    light: {
      primary: '#B5A7C7',
      primaryShadow: '#9186A6',
      accent: '#F7C8D0',
      accentShadow: '#E0A7B0',
      background: '#FFFFFF',
      cardBackground: '#FAF7FB',
      text: '#4A4A4A',
      extra: '#D4C5E8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#C9BDD9',
      primaryShadow: '#B5A7C7',
      accent: '#F9DAE2',
      accentShadow: '#F7C8D0',
      background: '#1C1820',
      cardBackground: '#2C2430',
      text: '#E5E5E5',
      extra: '#E0D3F0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Mint Cream',
    tags: ['pastel', 'fresh', 'light'],
    light: {
      primary: '#98D8C8',
      primaryShadow: '#7ABAA8',
      accent: '#FFE5B4',
      accentShadow: '#E6CC94',
      background: '#FFFFFF',
      cardBackground: '#F7FCFA',
      text: '#3D3D3D',
      extra: '#B4E7CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AAE4D4',
      primaryShadow: '#98D8C8',
      accent: '#FFF0C6',
      accentShadow: '#FFE5B4',
      background: '#141A18',
      cardBackground: '#1F2825',
      text: '#E8E8E8',
      extra: '#C6F0DA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Corporate & Professional
  {
    name: 'Executive Blue',
    tags: ['corporate', 'professional', 'trustworthy'],
    light: {
      primary: '#1E3A8A',
      primaryShadow: '#1E2A5A',
      accent: '#F59E0B',
      accentShadow: '#C47E09',
      background: '#FFFFFF',
      cardBackground: '#F8FAFC',
      text: '#1F2937',
      extra: '#3B82F6',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#60A5FA',
      primaryShadow: '#3B82F6',
      accent: '#FBB428',
      accentShadow: '#F59E0B',
      background: '#0F172A',
      cardBackground: '#1E293B',
      text: '#F1F5F9',
      extra: '#93C5FD',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Slate & Steel',
    tags: ['corporate', 'minimal', 'sleek'],
    light: {
      primary: '#475569',
      primaryShadow: '#334155',
      accent: '#06B6D4',
      accentShadow: '#0891B2',
      background: '#FFFFFF',
      cardBackground: '#F1F5F9',
      text: '#1E293B',
      extra: '#64748B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#94A3B8',
      primaryShadow: '#64748B',
      accent: '#22D3EE',
      accentShadow: '#06B6D4',
      background: '#0F172A',
      cardBackground: '#1E293B',
      text: '#F8FAFC',
      extra: '#CBD5E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Playful & Fun
  {
    name: 'Bubble Gum Pop',
    tags: ['playful', 'fun', 'cheerful'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFFF',
      cardBackground: '#FFF5FB',
      text: '#2D2D2D',
      extra: '#FF1493',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF85C7',
      primaryShadow: '#FF69B4',
      accent: '#FFE433',
      accentShadow: '#FFD700',
      background: '#1A0F14',
      cardBackground: '#2A1A21',
      text: '#F0F0F0',
      extra: '#FF47A3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Candy Shop',
    tags: ['playful', 'sweet', 'colorful'],
    light: {
      primary: '#9B59B6',
      primaryShadow: '#7C4792',
      accent: '#E74C3C',
      accentShadow: '#B93D30',
      background: '#FFFFFF',
      cardBackground: '#FCF3FF',
      text: '#2C2C2C',
      extra: '#F1C40F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B070C9',
      primaryShadow: '#9B59B6',
      accent: '#ED6558',
      accentShadow: '#E74C3C',
      background: '#1A0F1C',
      cardBackground: '#2A1A2C',
      text: '#ECECEC',
      extra: '#F4D03F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Dark & Moody
  {
    name: 'Midnight Noir',
    tags: ['dark', 'elegant', 'mysterious'],
    light: {
      primary: '#2C3E50',
      primaryShadow: '#1A252F',
      accent: '#E67E22',
      accentShadow: '#B8651B',
      background: '#FFFFFF',
      cardBackground: '#ECF0F1',
      text: '#2C3E50',
      extra: '#34495E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7F8C8D',
      primaryShadow: '#5D6D6E',
      accent: '#F39C12',
      accentShadow: '#E67E22',
      background: '#0C1216',
      cardBackground: '#1C2428',
      text: '#ECF0F1',
      extra: '#95A5A6',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Deep Purple Haze',
    tags: ['dark', 'rich', 'luxurious'],
    light: {
      primary: '#5B2C6F',
      primaryShadow: '#42204F',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFFFF',
      cardBackground: '#F5F0F7',
      text: '#2D2D2D',
      extra: '#8E44AD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8E44AD',
      primaryShadow: '#5B2C6F',
      accent: '#F1C40F',
      accentShadow: '#D4AF37',
      background: '#0E0816',
      cardBackground: '#1E1426',
      text: '#E8E8E8',
      extra: '#A569BD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Nature & Earth Tones
  {
    name: 'Forest Canopy',
    tags: ['nature', 'earthy', 'organic'],
    light: {
      primary: '#2D5016',
      primaryShadow: '#1F3610',
      accent: '#C17817',
      accentShadow: '#9A6012',
      background: '#FFFFFF',
      cardBackground: '#F7F9F5',
      text: '#2D2D2D',
      extra: '#4A7C2C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5FA836',
      primaryShadow: '#4A7C2C',
      accent: '#E89B2F',
      accentShadow: '#C17817',
      background: '#0F1408',
      cardBackground: '#1F2818',
      text: '#E8EDE5',
      extra: '#7DBE4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Desert Sand',
    tags: ['nature', 'warm', 'earthy'],
    light: {
      primary: '#C97F3A',
      primaryShadow: '#A1662E',
      accent: '#8B4513',
      accentShadow: '#6F360F',
      background: '#FFFFFF',
      cardBackground: '#FBF8F3',
      text: '#3D2817',
      extra: '#DDA15E',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#DDA15E',
      primaryShadow: '#C97F3A',
      accent: '#B8631D',
      accentShadow: '#8B4513',
      background: '#1A1108',
      cardBackground: '#2A1F18',
      text: '#F5EFEB',
      extra: '#EDB84C',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // Retro & Vintage
  {
    name: 'Retro Sunset',
    tags: ['retro', 'vintage', '70s'],
    light: {
      primary: '#D2691E',
      primaryShadow: '#A85418',
      accent: '#FFB347',
      accentShadow: '#CC8F39',
      background: '#FFFAF0',
      cardBackground: '#FFF5E6',
      text: '#3E2723',
      extra: '#E67C3A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F4A460',
      primaryShadow: '#D2691E',
      accent: '#FFCC80',
      accentShadow: '#FFB347',
      background: '#1C0F08',
      cardBackground: '#2C1A10',
      text: '#F5E6D3',
      extra: '#FF9966',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Teal',
    tags: ['retro', 'vintage', '50s'],
    light: {
      primary: '#2F6B6D',
      primaryShadow: '#235254',
      accent: '#E94B3C',
      accentShadow: '#BA3C30',
      background: '#FFFFF0',
      cardBackground: '#F0F8F8',
      text: '#2D2D2D',
      extra: '#4A9599',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5FA3A6',
      primaryShadow: '#2F6B6D',
      accent: '#FF6F61',
      accentShadow: '#E94B3C',
      background: '#0D1414',
      cardBackground: '#1D2828',
      text: '#E8EFEF',
      extra: '#7DBEC1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Minimalist
  {
    name: 'Pure Monochrome',
    tags: ['minimalist', 'clean', 'simple'],
    light: {
      primary: '#000000',
      primaryShadow: '#1A1A1A',
      accent: '#666666',
      accentShadow: '#4D4D4D',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#000000',
      extra: '#333333',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#FFFFFF',
      primaryShadow: '#E6E6E6',
      accent: '#999999',
      accentShadow: '#B3B3B3',
      background: '#000000',
      cardBackground: '#1A1A1A',
      text: '#FFFFFF',
      extra: '#CCCCCC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Whisper Gray',
    tags: ['minimalist', 'subtle', 'sophisticated'],
    light: {
      primary: '#5D5D5D',
      primaryShadow: '#4A4A4A',
      accent: '#8C8C8C',
      accentShadow: '#737373',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#2D2D2D',
      extra: '#B3B3B3',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A6A6A6',
      primaryShadow: '#8C8C8C',
      accent: '#CCCCCC',
      accentShadow: '#B3B3B3',
      background: '#0D0D0D',
      cardBackground: '#1A1A1A',
      text: '#E6E6E6',
      extra: '#808080',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },

  // Ocean & Water Themes
  {
    name: 'Deep Ocean',
    tags: ['cool', 'serene', 'blue'],
    light: {
      primary: '#006994',
      primaryShadow: '#004D6B',
      accent: '#00CED1',
      accentShadow: '#00A3A7',
      background: '#FFFFFF',
      cardBackground: '#F0F8FF',
      text: '#1A3A4A',
      extra: '#4682B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#00A8D6',
      primaryShadow: '#006994',
      accent: '#33DDDF',
      accentShadow: '#00CED1',
      background: '#0A1418',
      cardBackground: '#1A2428',
      text: '#E0F2FF',
      extra: '#6CA5C9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Waters',
    tags: ['fresh', 'bright', 'aqua'],
    light: {
      primary: '#00BFA5',
      primaryShadow: '#009683',
      accent: '#FFA726',
      accentShadow: '#CC851E',
      background: '#FFFFFF',
      cardBackground: '#F0FFF8',
      text: '#2D2D2D',
      extra: '#26C6DA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#33CCB7',
      primaryShadow: '#00BFA5',
      accent: '#FFB852',
      accentShadow: '#FFA726',
      background: '#0F1A18',
      cardBackground: '#1F2A28',
      text: '#E8FFF5',
      extra: '#52D8EC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Classic Themes (restored from earlier version)
  {
    name: 'Ocean Blue',
    tags: ['cool', 'professional', 'blue'],
    light: {
      primary: '#0066CC',
      primaryShadow: '#004C99',
      accent: '#FF6B35',
      accentShadow: '#CC5529',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#333333',
      extra: '#00D4FF',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#3399FF',
      primaryShadow: '#0066CC',
      accent: '#FF8C5A',
      accentShadow: '#FF6B35',
      background: '#1A1A1A',
      cardBackground: '#2A2A2A',
      text: '#E0E0E0',
      extra: '#00D4FF',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Forest Green',
    tags: ['nature', 'earthy', 'organic'],
    light: {
      primary: '#2D6A4F',
      primaryShadow: '#1B4332',
      accent: '#D4A373',
      accentShadow: '#B8865B',
      background: '#FEFEFE',
      cardBackground: '#F1FAEE',
      text: '#1B4332',
      extra: '#52B788',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'primary',
    },
    dark: {
      primary: '#52B788',
      primaryShadow: '#2D6A4F',
      accent: '#E9C46A',
      accentShadow: '#D4A373',
      background: '#1B1B1B',
      cardBackground: '#2D3E2F',
      text: '#E8F3E8',
      extra: '#95D5B2',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunset Pink',
    tags: ['vibrant', 'warm', 'bold'],
    light: {
      primary: '#E63946',
      primaryShadow: '#C5303D',
      accent: '#FFB703',
      accentShadow: '#DC9F02',
      background: '#FFFFFF',
      cardBackground: '#FFF3F3',
      text: '#2B2D42',
      extra: '#FB5607',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FF5A67',
      primaryShadow: '#E63946',
      accent: '#FFD60A',
      accentShadow: '#FFB703',
      background: '#1A1A2E',
      cardBackground: '#2B2D42',
      text: '#EDF2F4',
      extra: '#FB5607',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'accent',
    },
  },
  {
    name: 'Purple Dream',
    tags: ['vibrant', 'modern', 'bold'],
    light: {
      primary: '#7209B7',
      primaryShadow: '#560BAD',
      accent: '#F72585',
      accentShadow: '#D90368',
      background: '#FFFFFF',
      cardBackground: '#F8F0FF',
      text: '#2B2D42',
      extra: '#4CC9F0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#B388FF',
      primaryShadow: '#7209B7',
      accent: '#FF4DA6',
      accentShadow: '#F72585',
      background: '#1A1A2E',
      cardBackground: '#2D2A3E',
      text: '#E0E0E0',
      extra: '#4CC9F0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Monochrome',
    tags: ['minimalist', 'clean', 'simple'],
    light: {
      primary: '#212529',
      primaryShadow: '#000000',
      accent: '#6C757D',
      accentShadow: '#495057',
      background: '#FFFFFF',
      cardBackground: '#F8F9FA',
      text: '#212529',
      extra: '#ADB5BD',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#F8F9FA',
      primaryShadow: '#DEE2E6',
      accent: '#ADB5BD',
      accentShadow: '#6C757D',
      background: '#212529',
      cardBackground: '#343A40',
      text: '#F8F9FA',
      extra: '#6C757D',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Midnight Blue',
    tags: ['corporate', 'professional', 'trustworthy'],
    light: {
      primary: '#1E3A8A',
      primaryShadow: '#1E3A8A',
      accent: '#F59E0B',
      accentShadow: '#D97706',
      background: '#FFFFFF',
      cardBackground: '#F0F9FF',
      text: '#1F2937',
      extra: '#60A5FA',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#60A5FA',
      primaryShadow: '#3B82F6',
      accent: '#FBBF24',
      accentShadow: '#F59E0B',
      background: '#0F172A',
      cardBackground: '#1E293B',
      text: '#F1F5F9',
      extra: '#93C5FD',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Coral Reef',
    tags: ['playful', 'warm', 'cheerful'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#E85D5D',
      accent: '#4ECDC4',
      accentShadow: '#45B8B0',
      background: '#FFFBF7',
      cardBackground: '#FFF5F0',
      text: '#2C3E50',
      extra: '#FFE66D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FF8787',
      primaryShadow: '#FF6B6B',
      accent: '#6EDDD6',
      accentShadow: '#4ECDC4',
      background: '#1A1A2E',
      cardBackground: '#16213E',
      text: '#F0F0F0',
      extra: '#FFE66D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'accent',
    },
  },
  {
    name: 'Lavender Fields',
    tags: ['pastel', 'soft', 'calming'],
    light: {
      primary: '#9D84B7',
      primaryShadow: '#8971A6',
      accent: '#C8A2C8',
      accentShadow: '#B88FB8',
      background: '#FFFFFF',
      cardBackground: '#F9F5FF',
      text: '#3D3D3D',
      extra: '#E0BBE4',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#C8A2C8',
      primaryShadow: '#9D84B7',
      accent: '#E0BBE4',
      accentShadow: '#C8A2C8',
      background: '#1A1625',
      cardBackground: '#2A2438',
      text: '#E8E4F3',
      extra: '#D4A5D4',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Autumn Harvest',
    tags: ['nature', 'warm', 'earthy'],
    light: {
      primary: '#C2410C',
      primaryShadow: '#9A3412',
      accent: '#F59E0B',
      accentShadow: '#D97706',
      background: '#FFFBEB',
      cardBackground: '#FEF3C7',
      text: '#451A03',
      extra: '#FB923C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FB923C',
      primaryShadow: '#F97316',
      accent: '#FCD34D',
      accentShadow: '#FBBF24',
      background: '#1C1917',
      cardBackground: '#292524',
      text: '#FEF3C7',
      extra: '#FDBA74',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mint Fresh',
    tags: ['fresh', 'light', 'cool'],
    light: {
      primary: '#10B981',
      primaryShadow: '#059669',
      accent: '#06B6D4',
      accentShadow: '#0891B2',
      background: '#FFFFFF',
      cardBackground: '#F0FDF4',
      text: '#064E3B',
      extra: '#6EE7B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#34D399',
      primaryShadow: '#10B981',
      accent: '#22D3EE',
      accentShadow: '#06B6D4',
      background: '#0F1419',
      cardBackground: '#1C2128',
      text: '#D1FAE5',
      extra: '#6EE7B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cherry Blossom',
    tags: ['pastel', 'soft', 'playful'],
    light: {
      primary: '#EC4899',
      primaryShadow: '#DB2777',
      accent: '#F472B6',
      accentShadow: '#EC4899',
      background: '#FFF5F7',
      cardBackground: '#FCE7F3',
      text: '#831843',
      extra: '#F9A8D4',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#F9A8D4',
      primaryShadow: '#F472B6',
      accent: '#FBCFE8',
      accentShadow: '#F9A8D4',
      background: '#1F1720',
      cardBackground: '#2D1B28',
      text: '#FCE7F3',
      extra: '#FBD5E9',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Deep Sea',
    tags: ['cool', 'serene', 'blue'],
    light: {
      primary: '#0E7490',
      primaryShadow: '#155E75',
      accent: '#06B6D4',
      accentShadow: '#0891B2',
      background: '#F0FDFA',
      cardBackground: '#CCFBF1',
      text: '#134E4A',
      extra: '#5EEAD4',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#22D3EE',
      primaryShadow: '#06B6D4',
      accent: '#67E8F9',
      accentShadow: '#22D3EE',
      background: '#0C1821',
      cardBackground: '#1B2838',
      text: '#CFFAFE',
      extra: '#A5F3FC',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Slate & Gold',
    tags: ['corporate', 'elegant', 'sophisticated'],
    light: {
      primary: '#475569',
      primaryShadow: '#334155',
      accent: '#F59E0B',
      accentShadow: '#D97706',
      background: '#FFFFFF',
      cardBackground: '#F8FAFC',
      text: '#1E293B',
      extra: '#94A3B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#94A3B8',
      primaryShadow: '#64748B',
      accent: '#FBBF24',
      accentShadow: '#F59E0B',
      background: '#0F172A',
      cardBackground: '#1E293B',
      text: '#F1F5F9',
      extra: '#CBD5E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Crimson & Cream',
    tags: ['bold', 'warm', 'elegant'],
    light: {
      primary: '#BE123C',
      primaryShadow: '#9F1239',
      accent: '#FDE68A',
      accentShadow: '#FCD34D',
      background: '#FFFBEB',
      cardBackground: '#FEF3C7',
      text: '#450A0A',
      extra: '#FCA5A5',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'text',
    },
    dark: {
      primary: '#FDA4AF',
      primaryShadow: '#FB7185',
      accent: '#FEF08A',
      accentShadow: '#FDE047',
      background: '#1C1917',
      cardBackground: '#292524',
      text: '#FFF1F2',
      extra: '#FECACA',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'text',
    },
  },
  {
    name: 'Emerald City',
    tags: ['nature', 'fresh', 'vibrant'],
    light: {
      primary: '#047857',
      primaryShadow: '#065F46',
      accent: '#84CC16',
      accentShadow: '#65A30D',
      background: '#F7FEF0',
      cardBackground: '#ECFCCB',
      text: '#14532D',
      extra: '#34D399',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#34D399',
      primaryShadow: '#10B981',
      accent: '#BEF264',
      accentShadow: '#A3E635',
      background: '#0A1F14',
      cardBackground: '#14532D',
      text: '#D1FAE5',
      extra: '#6EE7B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Royal Purple',
    tags: ['dark', 'luxurious', 'rich'],
    light: {
      primary: '#6B21A8',
      primaryShadow: '#581C87',
      accent: '#A855F7',
      accentShadow: '#9333EA',
      background: '#FAF5FF',
      cardBackground: '#F3E8FF',
      text: '#3B0764',
      extra: '#C084FC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C084FC',
      primaryShadow: '#A855F7',
      accent: '#D8B4FE',
      accentShadow: '#C084FC',
      background: '#1E1228',
      cardBackground: '#2E1A47',
      text: '#F3E8FF',
      extra: '#E9D5FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tangerine Dream',
    tags: ['vibrant', 'energetic', 'warm'],
    light: {
      primary: '#EA580C',
      primaryShadow: '#C2410C',
      accent: '#FBBF24',
      accentShadow: '#F59E0B',
      background: '#FFFBEB',
      cardBackground: '#FEF3C7',
      text: '#78350F',
      extra: '#FB923C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FB923C',
      primaryShadow: '#F97316',
      accent: '#FDE047',
      accentShadow: '#FACC15',
      background: '#1C1410',
      cardBackground: '#292524',
      text: '#FEF3C7',
      extra: '#FDBA74',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Arctic Ice',
    tags: ['cool', 'fresh', 'light'],
    light: {
      primary: '#0369A1',
      primaryShadow: '#075985',
      accent: '#67E8F9',
      accentShadow: '#22D3EE',
      background: '#F0F9FF',
      cardBackground: '#E0F2FE',
      text: '#0C4A6E',
      extra: '#BAE6FD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7DD3FC',
      primaryShadow: '#38BDF8',
      accent: '#A5F3FC',
      accentShadow: '#67E8F9',
      background: '#0A1929',
      cardBackground: '#1E293B',
      text: '#E0F2FE',
      extra: '#BAE6FD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Berry Blast',
    tags: ['vibrant', 'playful', 'bold'],
    light: {
      primary: '#BE185D',
      primaryShadow: '#9F1239',
      accent: '#7C3AED',
      accentShadow: '#6D28D9',
      background: '#FFF1F2',
      cardBackground: '#FCE7F3',
      text: '#4A044E',
      extra: '#F472B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F472B6',
      primaryShadow: '#EC4899',
      accent: '#A78BFA',
      accentShadow: '#8B5CF6',
      background: '#1F1129',
      cardBackground: '#2D1B3D',
      text: '#FCE7F3',
      extra: '#F9A8D4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coffee & Cream',
    tags: ['warm', 'earthy', 'sophisticated'],
    light: {
      primary: '#78350F',
      primaryShadow: '#451A03',
      accent: '#A16207',
      accentShadow: '#854D0E',
      background: '#FFFBEB',
      cardBackground: '#FEF3C7',
      text: '#292524',
      extra: '#D97706',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#FCD34D',
      primaryShadow: '#FBBF24',
      accent: '#FDE68A',
      accentShadow: '#FCD34D',
      background: '#1C1917',
      cardBackground: '#292524',
      text: '#FEF3C7',
      extra: '#F59E0B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },

  // ========================================
  // BATCH 1: Material Design Inspired
  // ========================================
  {
    name: 'Material Red',
    tags: ['material', 'bold', 'vibrant'],
    light: {
      primary: '#F44336',
      primaryShadow: '#D32F2F',
      accent: '#FF5252',
      accentShadow: '#FF1744',
      background: '#FFFFFF',
      cardBackground: '#FFEBEE',
      text: '#212121',
      extra: '#EF9A9A',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#F44336',
      accent: '#FF8A80',
      accentShadow: '#FF5252',
      background: '#1A0A0A',
      cardBackground: '#2D1515',
      text: '#FFEBEE',
      extra: '#E57373',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Pink',
    tags: ['material', 'playful', 'vibrant'],
    light: {
      primary: '#E91E63',
      primaryShadow: '#C2185B',
      accent: '#FF4081',
      accentShadow: '#F50057',
      background: '#FFFFFF',
      cardBackground: '#FCE4EC',
      text: '#212121',
      extra: '#F48FB1',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#EC407A',
      primaryShadow: '#E91E63',
      accent: '#FF80AB',
      accentShadow: '#FF4081',
      background: '#1A0A10',
      cardBackground: '#2D1520',
      text: '#FCE4EC',
      extra: '#F06292',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Purple',
    tags: ['material', 'rich', 'luxurious'],
    light: {
      primary: '#9C27B0',
      primaryShadow: '#7B1FA2',
      accent: '#E040FB',
      accentShadow: '#D500F9',
      background: '#FFFFFF',
      cardBackground: '#F3E5F5',
      text: '#212121',
      extra: '#CE93D8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#AB47BC',
      primaryShadow: '#9C27B0',
      accent: '#EA80FC',
      accentShadow: '#E040FB',
      background: '#120A14',
      cardBackground: '#251525',
      text: '#F3E5F5',
      extra: '#BA68C8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Deep Purple',
    tags: ['material', 'rich', 'elegant'],
    light: {
      primary: '#673AB7',
      primaryShadow: '#512DA8',
      accent: '#7C4DFF',
      accentShadow: '#651FFF',
      background: '#FFFFFF',
      cardBackground: '#EDE7F6',
      text: '#212121',
      extra: '#B39DDB',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#7E57C2',
      primaryShadow: '#673AB7',
      accent: '#B388FF',
      accentShadow: '#7C4DFF',
      background: '#0E0A14',
      cardBackground: '#1E1530',
      text: '#EDE7F6',
      extra: '#9575CD',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Indigo',
    tags: ['material', 'professional', 'trustworthy'],
    light: {
      primary: '#3F51B5',
      primaryShadow: '#303F9F',
      accent: '#536DFE',
      accentShadow: '#3D5AFE',
      background: '#FFFFFF',
      cardBackground: '#E8EAF6',
      text: '#212121',
      extra: '#9FA8DA',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#5C6BC0',
      primaryShadow: '#3F51B5',
      accent: '#8C9EFF',
      accentShadow: '#536DFE',
      background: '#0A0C14',
      cardBackground: '#151830',
      text: '#E8EAF6',
      extra: '#7986CB',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Blue',
    tags: ['material', 'professional', 'cool'],
    light: {
      primary: '#2196F3',
      primaryShadow: '#1976D2',
      accent: '#448AFF',
      accentShadow: '#2979FF',
      background: '#FFFFFF',
      cardBackground: '#E3F2FD',
      text: '#212121',
      extra: '#90CAF9',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#2196F3',
      accent: '#82B1FF',
      accentShadow: '#448AFF',
      background: '#0A1014',
      cardBackground: '#152535',
      text: '#E3F2FD',
      extra: '#64B5F6',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Light Blue',
    tags: ['material', 'fresh', 'bright'],
    light: {
      primary: '#03A9F4',
      primaryShadow: '#0288D1',
      accent: '#40C4FF',
      accentShadow: '#00B0FF',
      background: '#FFFFFF',
      cardBackground: '#E1F5FE',
      text: '#212121',
      extra: '#81D4FA',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#29B6F6',
      primaryShadow: '#03A9F4',
      accent: '#80D8FF',
      accentShadow: '#40C4FF',
      background: '#0A1214',
      cardBackground: '#152530',
      text: '#E1F5FE',
      extra: '#4FC3F7',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Cyan',
    tags: ['material', 'fresh', 'aqua'],
    light: {
      primary: '#00BCD4',
      primaryShadow: '#0097A7',
      accent: '#18FFFF',
      accentShadow: '#00E5FF',
      background: '#FFFFFF',
      cardBackground: '#E0F7FA',
      text: '#212121',
      extra: '#80DEEA',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#26C6DA',
      primaryShadow: '#00BCD4',
      accent: '#84FFFF',
      accentShadow: '#18FFFF',
      background: '#0A1414',
      cardBackground: '#152828',
      text: '#E0F7FA',
      extra: '#4DD0E1',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Teal',
    tags: ['material', 'nature', 'calm'],
    light: {
      primary: '#009688',
      primaryShadow: '#00796B',
      accent: '#64FFDA',
      accentShadow: '#1DE9B6',
      background: '#FFFFFF',
      cardBackground: '#E0F2F1',
      text: '#212121',
      extra: '#80CBC4',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#26A69A',
      primaryShadow: '#009688',
      accent: '#A7FFEB',
      accentShadow: '#64FFDA',
      background: '#0A1412',
      cardBackground: '#152825',
      text: '#E0F2F1',
      extra: '#4DB6AC',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Green',
    tags: ['material', 'nature', 'fresh'],
    light: {
      primary: '#4CAF50',
      primaryShadow: '#388E3C',
      accent: '#69F0AE',
      accentShadow: '#00E676',
      background: '#FFFFFF',
      cardBackground: '#E8F5E9',
      text: '#212121',
      extra: '#A5D6A7',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#4CAF50',
      accent: '#B9F6CA',
      accentShadow: '#69F0AE',
      background: '#0A140A',
      cardBackground: '#152815',
      text: '#E8F5E9',
      extra: '#81C784',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Light Green',
    tags: ['material', 'fresh', 'spring'],
    light: {
      primary: '#8BC34A',
      primaryShadow: '#689F38',
      accent: '#B2FF59',
      accentShadow: '#76FF03',
      background: '#FFFFFF',
      cardBackground: '#F1F8E9',
      text: '#212121',
      extra: '#C5E1A5',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#9CCC65',
      primaryShadow: '#8BC34A',
      accent: '#CCFF90',
      accentShadow: '#B2FF59',
      background: '#10140A',
      cardBackground: '#202810',
      text: '#F1F8E9',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Lime',
    tags: ['material', 'bright', 'energetic'],
    light: {
      primary: '#CDDC39',
      primaryShadow: '#AFB42B',
      accent: '#EEFF41',
      accentShadow: '#C6FF00',
      background: '#FFFFFF',
      cardBackground: '#F9FBE7',
      text: '#212121',
      extra: '#E6EE9C',
      h1Color: 'text',
      h2Color: 'text',
      h3Color: 'text',
    },
    dark: {
      primary: '#D4E157',
      primaryShadow: '#CDDC39',
      accent: '#F4FF81',
      accentShadow: '#EEFF41',
      background: '#14140A',
      cardBackground: '#28280F',
      text: '#F9FBE7',
      extra: '#DCE775',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Amber',
    tags: ['material', 'warm', 'golden'],
    light: {
      primary: '#FFC107',
      primaryShadow: '#FFA000',
      accent: '#FFD740',
      accentShadow: '#FFC400',
      background: '#FFFFFF',
      cardBackground: '#FFF8E1',
      text: '#212121',
      extra: '#FFE082',
      h1Color: 'text',
      h2Color: 'text',
      h3Color: 'text',
    },
    dark: {
      primary: '#FFCA28',
      primaryShadow: '#FFC107',
      accent: '#FFE57F',
      accentShadow: '#FFD740',
      background: '#141208',
      cardBackground: '#282510',
      text: '#FFF8E1',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Orange',
    tags: ['material', 'warm', 'energetic'],
    light: {
      primary: '#FF9800',
      primaryShadow: '#F57C00',
      accent: '#FFAB40',
      accentShadow: '#FF9100',
      background: '#FFFFFF',
      cardBackground: '#FFF3E0',
      text: '#212121',
      extra: '#FFCC80',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FFA726',
      primaryShadow: '#FF9800',
      accent: '#FFD180',
      accentShadow: '#FFAB40',
      background: '#14100A',
      cardBackground: '#282015',
      text: '#FFF3E0',
      extra: '#FFB74D',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Deep Orange',
    tags: ['material', 'bold', 'warm'],
    light: {
      primary: '#FF5722',
      primaryShadow: '#E64A19',
      accent: '#FF6E40',
      accentShadow: '#FF3D00',
      background: '#FFFFFF',
      cardBackground: '#FBE9E7',
      text: '#212121',
      extra: '#FFAB91',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FF7043',
      primaryShadow: '#FF5722',
      accent: '#FF9E80',
      accentShadow: '#FF6E40',
      background: '#140A08',
      cardBackground: '#281510',
      text: '#FBE9E7',
      extra: '#FF8A65',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Brown',
    tags: ['material', 'earthy', 'warm'],
    light: {
      primary: '#795548',
      primaryShadow: '#5D4037',
      accent: '#A1887F',
      accentShadow: '#8D6E63',
      background: '#FFFFFF',
      cardBackground: '#EFEBE9',
      text: '#212121',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#795548',
      accent: '#BCAAA4',
      accentShadow: '#A1887F',
      background: '#12100E',
      cardBackground: '#25201C',
      text: '#EFEBE9',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Material Blue Grey',
    tags: ['material', 'corporate', 'sleek'],
    light: {
      primary: '#607D8B',
      primaryShadow: '#455A64',
      accent: '#78909C',
      accentShadow: '#546E7A',
      background: '#FFFFFF',
      cardBackground: '#ECEFF1',
      text: '#212121',
      extra: '#B0BEC5',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#78909C',
      primaryShadow: '#607D8B',
      accent: '#B0BEC5',
      accentShadow: '#90A4AE',
      background: '#0E1214',
      cardBackground: '#1C2428',
      text: '#ECEFF1',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // Digital Synopsis Palettes (adapted)
  // ========================================
  {
    name: 'Beach Towels',
    tags: ['playful', 'summer', 'vibrant'],
    light: {
      primary: '#FE4A49',
      primaryShadow: '#CB3B3B',
      accent: '#2AB7CA',
      accentShadow: '#2292A2',
      background: '#F4F4F8',
      cardBackground: '#FFFFFF',
      text: '#2D2D2D',
      extra: '#FED766',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6B6A',
      primaryShadow: '#FE4A49',
      accent: '#4BCADB',
      accentShadow: '#2AB7CA',
      background: '#1A1A1E',
      cardBackground: '#2A2A30',
      text: '#F4F4F8',
      extra: '#FFE388',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blush Gradient',
    tags: ['pastel', 'soft', 'feminine'],
    light: {
      primary: '#F6ABB6',
      primaryShadow: '#F4B6C2',
      accent: '#EEC9D2',
      accentShadow: '#EAD5DC',
      background: '#FFFFFF',
      cardBackground: '#FDF8F9',
      text: '#4A4A4A',
      extra: '#EEE3E7',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#F8C0C9',
      primaryShadow: '#F6ABB6',
      accent: '#F2D8DF',
      accentShadow: '#EEC9D2',
      background: '#1A1416',
      cardBackground: '#2A2025',
      text: '#F5F0F2',
      extra: '#F4EAED',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Navy Depths',
    tags: ['corporate', 'professional', 'blue'],
    light: {
      primary: '#011F4B',
      primaryShadow: '#03396C',
      accent: '#6497B1',
      accentShadow: '#005B96',
      background: '#FFFFFF',
      cardBackground: '#F5F8FA',
      text: '#011F4B',
      extra: '#B3CDE0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#6497B1',
      primaryShadow: '#005B96',
      accent: '#B3CDE0',
      accentShadow: '#6497B1',
      background: '#010D1A',
      cardBackground: '#021A35',
      text: '#E8F1F8',
      extra: '#C5DAE8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Lost Songs',
    tags: ['dark', 'moody', 'gradient'],
    light: {
      primary: '#851E3E',
      primaryShadow: '#651E3E',
      accent: '#451E3E',
      accentShadow: '#251E3E',
      background: '#FFFFFF',
      cardBackground: '#FBF5F7',
      text: '#051E3E',
      extra: '#C54B6C',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A84B6C',
      primaryShadow: '#851E3E',
      accent: '#6B3B5C',
      accentShadow: '#451E3E',
      background: '#0A0812',
      cardBackground: '#1A1525',
      text: '#F5F0F3',
      extra: '#C87090',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Ivory Whisper',
    tags: ['minimalist', 'soft', 'clean'],
    light: {
      primary: '#DEC3C3',
      primaryShadow: '#C8AAAA',
      accent: '#E7D3D3',
      accentShadow: '#D4BEBE',
      background: '#FFFFFF',
      cardBackground: '#F9F4F4',
      text: '#4A4040',
      extra: '#F0E4E4',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E8D4D4',
      primaryShadow: '#DEC3C3',
      accent: '#F0E2E2',
      accentShadow: '#E7D3D3',
      background: '#1A1616',
      cardBackground: '#2A2424',
      text: '#F5F0F0',
      extra: '#F5EBEB',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Moonlight Bytes',
    tags: ['tech', 'modern', 'vibrant'],
    light: {
      primary: '#0E9AA7',
      primaryShadow: '#0B7A85',
      accent: '#FE8A71',
      accentShadow: '#CB6E5A',
      background: '#FFFFFF',
      cardBackground: '#F5FAFB',
      text: '#4A4E4D',
      extra: '#F6CD61',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3DB8C4',
      primaryShadow: '#0E9AA7',
      accent: '#FFA08A',
      accentShadow: '#FE8A71',
      background: '#12181A',
      cardBackground: '#1E2A2C',
      text: '#E8EAEA',
      extra: '#F8DA82',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Blues',
    tags: ['corporate', 'cool', 'professional'],
    light: {
      primary: '#2A4D69',
      primaryShadow: '#1E3850',
      accent: '#63ACE5',
      accentShadow: '#4B86B4',
      background: '#FFFFFF',
      cardBackground: '#E7EFF6',
      text: '#2A4D69',
      extra: '#ADCBE3',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#63ACE5',
      primaryShadow: '#4B86B4',
      accent: '#ADCBE3',
      accentShadow: '#63ACE5',
      background: '#0D151C',
      cardBackground: '#1A2A38',
      text: '#E7EFF6',
      extra: '#C5DBF0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Peach Sorbet',
    tags: ['pastel', 'warm', 'soft'],
    light: {
      primary: '#FE9C8F',
      primaryShadow: '#CB7D72',
      accent: '#F9CAA7',
      accentShadow: '#C7A286',
      background: '#FFFFFF',
      cardBackground: '#FFFAF8',
      text: '#5A4A45',
      extra: '#FEC8C1',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FFB5AA',
      primaryShadow: '#FE9C8F',
      accent: '#FAD8BE',
      accentShadow: '#F9CAA7',
      background: '#1A1412',
      cardBackground: '#2A2420',
      text: '#FFF5F2',
      extra: '#FFD8D2',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Android Mint',
    tags: ['tech', 'fresh', 'modern'],
    light: {
      primary: '#009688',
      primaryShadow: '#00796B',
      accent: '#65C3BA',
      accentShadow: '#54B2A9',
      background: '#FFFFFF',
      cardBackground: '#F0F9F8',
      text: '#263238',
      extra: '#83D0C9',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#35A79C',
      primaryShadow: '#009688',
      accent: '#83D0C9',
      accentShadow: '#65C3BA',
      background: '#0A1414',
      cardBackground: '#152828',
      text: '#E8F5F5',
      extra: '#A0DDD7',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Rainbow Bright',
    tags: ['vibrant', 'playful', 'colorful'],
    light: {
      primary: '#EE4035',
      primaryShadow: '#BE332A',
      accent: '#0392CF',
      accentShadow: '#0275A6',
      background: '#FFFFFF',
      cardBackground: '#FFF9F0',
      text: '#2D2D2D',
      extra: '#7BC043',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F26259',
      primaryShadow: '#EE4035',
      accent: '#2AA8E0',
      accentShadow: '#0392CF',
      background: '#1A1410',
      cardBackground: '#2A241C',
      text: '#F8F5F0',
      extra: '#95D15E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blueberry Basket',
    tags: ['cool', 'elegant', 'blue'],
    light: {
      primary: '#283655',
      primaryShadow: '#1E1F26',
      accent: '#4D648D',
      accentShadow: '#3A4C6B',
      background: '#FFFFFF',
      cardBackground: '#F5F8FC',
      text: '#1E1F26',
      extra: '#D0E1F9',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#4D648D',
      primaryShadow: '#283655',
      accent: '#8DA4CA',
      accentShadow: '#4D648D',
      background: '#0D0E12',
      cardBackground: '#1A1E28',
      text: '#E5EBF5',
      extra: '#D0E1F9',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Seaside Breeze',
    tags: ['nature', 'fresh', 'summer'],
    light: {
      primary: '#FF6F69',
      primaryShadow: '#CC5954',
      accent: '#88D8B0',
      accentShadow: '#6DAD8D',
      background: '#FFFFFF',
      cardBackground: '#FFFAF0',
      text: '#3D3D3D',
      extra: '#FFCC5C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8A85',
      primaryShadow: '#FF6F69',
      accent: '#A3E5C4',
      accentShadow: '#88D8B0',
      background: '#1A1410',
      cardBackground: '#2A241C',
      text: '#FFF8F0',
      extra: '#FFDA7A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cappuccino',
    tags: ['warm', 'earthy', 'cozy'],
    light: {
      primary: '#4B3832',
      primaryShadow: '#3C2F2F',
      accent: '#854442',
      accentShadow: '#6A3635',
      background: '#FFF4E6',
      cardBackground: '#FFFFFF',
      text: '#3C2F2F',
      extra: '#BE9B7B',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A0857A',
      primaryShadow: '#6A5750',
      accent: '#A66D6B',
      accentShadow: '#854442',
      background: '#1A1514',
      cardBackground: '#2A2522',
      text: '#F5EDE5',
      extra: '#D4B89C',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Metro Vibrant',
    tags: ['modern', 'bold', 'vibrant'],
    light: {
      primary: '#D11141',
      primaryShadow: '#A70D34',
      accent: '#00AEDB',
      accentShadow: '#008BAF',
      background: '#FFFFFF',
      cardBackground: '#FFF8F5',
      text: '#2D2D2D',
      extra: '#00B159',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E83D66',
      primaryShadow: '#D11141',
      accent: '#33C1E8',
      accentShadow: '#00AEDB',
      background: '#1A0D12',
      cardBackground: '#2A1A20',
      text: '#F8F5F5',
      extra: '#33C478',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Water Bearer',
    tags: ['cool', 'serene', 'aqua'],
    light: {
      primary: '#58668B',
      primaryShadow: '#46526F',
      accent: '#76B4BD',
      accentShadow: '#5E9097',
      background: '#FFFFFF',
      cardBackground: '#EBF4F6',
      text: '#5E5656',
      extra: '#BDEAEE',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#7A88AA',
      primaryShadow: '#58668B',
      accent: '#96CDD5',
      accentShadow: '#76B4BD',
      background: '#101418',
      cardBackground: '#1C2430',
      text: '#EBF4F6',
      extra: '#D4F2F5',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Program Catalog',
    tags: ['retro', 'bold', 'colorful'],
    light: {
      primary: '#CC2A36',
      primaryShadow: '#A3222B',
      accent: '#00A0B0',
      accentShadow: '#00808D',
      background: '#FFFFFF',
      cardBackground: '#FFF8F0',
      text: '#4F372D',
      extra: '#EDC951',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E04B55',
      primaryShadow: '#CC2A36',
      accent: '#33B5C4',
      accentShadow: '#00A0B0',
      background: '#14100D',
      cardBackground: '#28201A',
      text: '#F5F0E8',
      extra: '#F2D872',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Purple Skyline',
    tags: ['dark', 'elegant', 'gradient'],
    light: {
      primary: '#3D1E6D',
      primaryShadow: '#2E003E',
      accent: '#8874A3',
      accentShadow: '#6A5A82',
      background: '#FFFFFF',
      cardBackground: '#F8F5FC',
      text: '#2E003E',
      extra: '#E4DCF1',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#8874A3',
      primaryShadow: '#5A4578',
      accent: '#B5A8C8',
      accentShadow: '#8874A3',
      background: '#0E0514',
      cardBackground: '#1E1528',
      text: '#F0ECF5',
      extra: '#E4DCF1',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Space Gray',
    tags: ['tech', 'sleek', 'minimal'],
    light: {
      primary: '#343D46',
      primaryShadow: '#262E35',
      accent: '#65737E',
      accentShadow: '#4F5B66',
      background: '#FFFFFF',
      cardBackground: '#F5F7F8',
      text: '#343D46',
      extra: '#A7ADBA',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A7ADBA',
      primaryShadow: '#65737E',
      accent: '#C0C5CE',
      accentShadow: '#A7ADBA',
      background: '#0D0F12',
      cardBackground: '#1A1E24',
      text: '#E8EAED',
      extra: '#C0C5CE',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Armor Blue',
    tags: ['cool', 'professional', 'blue'],
    light: {
      primary: '#3B7DD8',
      primaryShadow: '#2F64AD',
      accent: '#64A1F4',
      accentShadow: '#4A91F2',
      background: '#FFFFFF',
      cardBackground: '#F5F9FF',
      text: '#2A4060',
      extra: '#8DBDFF',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#64A1F4',
      primaryShadow: '#3B7DD8',
      accent: '#8DBDFF',
      accentShadow: '#64A1F4',
      background: '#0A1420',
      cardBackground: '#152535',
      text: '#E8F0FA',
      extra: '#BFD6F6',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // Nature Themes - Forests
  // ========================================
  {
    name: 'Redwood Grove',
    tags: ['nature', 'earthy', 'forest'],
    light: {
      primary: '#5D4E37',
      primaryShadow: '#4A3E2C',
      accent: '#8B9A46',
      accentShadow: '#6F7B38',
      background: '#FFFFFF',
      cardBackground: '#F8F6F3',
      text: '#3D3328',
      extra: '#A7B35A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8A7B64',
      primaryShadow: '#5D4E37',
      accent: '#A5B55A',
      accentShadow: '#8B9A46',
      background: '#14120E',
      cardBackground: '#25221C',
      text: '#F0EDE8',
      extra: '#BCC86E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bamboo Forest',
    tags: ['nature', 'zen', 'asian'],
    light: {
      primary: '#4A6741',
      primaryShadow: '#3B5234',
      accent: '#C4B896',
      accentShadow: '#9D9378',
      background: '#FFFFFF',
      cardBackground: '#F7F8F5',
      text: '#2D3B28',
      extra: '#8FA880',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#6B8A60',
      primaryShadow: '#4A6741',
      accent: '#D8CFAF',
      accentShadow: '#C4B896',
      background: '#0E1410',
      cardBackground: '#1C251A',
      text: '#EEF2EC',
      extra: '#A8C09A',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Moss Garden',
    tags: ['nature', 'earthy', 'calm'],
    light: {
      primary: '#556B2F',
      primaryShadow: '#445626',
      accent: '#9DC183',
      accentShadow: '#7E9A69',
      background: '#FFFFFF',
      cardBackground: '#F5F8F2',
      text: '#2D3820',
      extra: '#B5D89E',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#7A954E',
      primaryShadow: '#556B2F',
      accent: '#B5D89E',
      accentShadow: '#9DC183',
      background: '#10140D',
      cardBackground: '#1E281A',
      text: '#EDF2EA',
      extra: '#C8E5B5',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Pine Needle',
    tags: ['nature', 'forest', 'cool'],
    light: {
      primary: '#1E453E',
      primaryShadow: '#183731',
      accent: '#528B6B',
      accentShadow: '#426F56',
      background: '#FFFFFF',
      cardBackground: '#F2F7F5',
      text: '#1E453E',
      extra: '#7AB394',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#528B6B',
      primaryShadow: '#1E453E',
      accent: '#7AB394',
      accentShadow: '#528B6B',
      background: '#0A1412',
      cardBackground: '#152520',
      text: '#E8F2EE',
      extra: '#9CC8B0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Birch Wood',
    tags: ['nature', 'minimal', 'nordic'],
    light: {
      primary: '#6B6B6B',
      primaryShadow: '#555555',
      accent: '#E8E4DD',
      accentShadow: '#C9C4BC',
      background: '#FFFFFF',
      cardBackground: '#FAFAF8',
      text: '#3D3D3D',
      extra: '#B0ADA5',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0A0A0',
      primaryShadow: '#6B6B6B',
      accent: '#F0EDE8',
      accentShadow: '#E8E4DD',
      background: '#14141A',
      cardBackground: '#202025',
      text: '#F0F0F0',
      extra: '#C5C2BC',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },

  // ========================================
  // Nature Themes - Ocean
  // ========================================
  {
    name: 'Coral Bay',
    tags: ['nature', 'ocean', 'tropical'],
    light: {
      primary: '#FF7F50',
      primaryShadow: '#CC6640',
      accent: '#40E0D0',
      accentShadow: '#33B3A6',
      background: '#FFFFFF',
      cardBackground: '#FFF8F5',
      text: '#2D4A5C',
      extra: '#FFB07C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9973',
      primaryShadow: '#FF7F50',
      accent: '#66E8DB',
      accentShadow: '#40E0D0',
      background: '#14181A',
      cardBackground: '#20282C',
      text: '#F0F8FA',
      extra: '#FFC299',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Kelp Forest',
    tags: ['nature', 'ocean', 'deep'],
    light: {
      primary: '#2E5D4B',
      primaryShadow: '#254A3C',
      accent: '#5BA88B',
      accentShadow: '#49866F',
      background: '#FFFFFF',
      cardBackground: '#F2F7F5',
      text: '#1D3830',
      extra: '#85C9AB',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#5BA88B',
      primaryShadow: '#2E5D4B',
      accent: '#85C9AB',
      accentShadow: '#5BA88B',
      background: '#0C1412',
      cardBackground: '#182520',
      text: '#E8F5F0',
      extra: '#A8DBC5',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Midnight Tide',
    tags: ['ocean', 'dark', 'mysterious'],
    light: {
      primary: '#1A3A5C',
      primaryShadow: '#142D48',
      accent: '#4A90A4',
      accentShadow: '#3B7384',
      background: '#FFFFFF',
      cardBackground: '#F0F5F8',
      text: '#1A3A5C',
      extra: '#7AB8C9',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#4A90A4',
      primaryShadow: '#1A3A5C',
      accent: '#7AB8C9',
      accentShadow: '#4A90A4',
      background: '#080E14',
      cardBackground: '#121C28',
      text: '#E5EEF2',
      extra: '#A8D4E0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Sandy Shore',
    tags: ['nature', 'beach', 'warm'],
    light: {
      primary: '#C2A66B',
      primaryShadow: '#9B8556',
      accent: '#5BA3B0',
      accentShadow: '#49828D',
      background: '#FFFFFF',
      cardBackground: '#FBF9F5',
      text: '#4A4030',
      extra: '#DEC88C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D9BD83',
      primaryShadow: '#C2A66B',
      accent: '#7BBAC5',
      accentShadow: '#5BA3B0',
      background: '#141210',
      cardBackground: '#25221C',
      text: '#F5F2EC',
      extra: '#E8D5A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Abyssal Blue',
    tags: ['ocean', 'dark', 'deep'],
    light: {
      primary: '#0B3D5C',
      primaryShadow: '#082D44',
      accent: '#1E6F8A',
      accentShadow: '#18586E',
      background: '#FFFFFF',
      cardBackground: '#EDF4F7',
      text: '#0B3D5C',
      extra: '#4A9AB8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#4A9AB8',
      primaryShadow: '#1E6F8A',
      accent: '#7AC0D8',
      accentShadow: '#4A9AB8',
      background: '#040D14',
      cardBackground: '#0C1A25',
      text: '#E0EDF2',
      extra: '#A0D5E8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // Nature Themes - Mountain
  // ========================================
  {
    name: 'Alpine Summit',
    tags: ['nature', 'mountain', 'crisp'],
    light: {
      primary: '#4A5568',
      primaryShadow: '#3B4453',
      accent: '#81E6D9',
      accentShadow: '#67B8AD',
      background: '#FFFFFF',
      cardBackground: '#F7FAFC',
      text: '#2D3748',
      extra: '#A0AEC0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0AEC0',
      primaryShadow: '#718096',
      accent: '#81E6D9',
      accentShadow: '#4FD1C5',
      background: '#0F1318',
      cardBackground: '#1A202C',
      text: '#E2E8F0',
      extra: '#CBD5E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rocky Granite',
    tags: ['nature', 'earthy', 'stone'],
    light: {
      primary: '#5C5C5C',
      primaryShadow: '#4A4A4A',
      accent: '#8B7355',
      accentShadow: '#6F5C44',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#3D3D3D',
      extra: '#A08E78',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9A9A9A',
      primaryShadow: '#5C5C5C',
      accent: '#B39B7D',
      accentShadow: '#8B7355',
      background: '#121212',
      cardBackground: '#1E1E1E',
      text: '#E8E8E8',
      extra: '#C5B5A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snow Peak',
    tags: ['nature', 'winter', 'clean'],
    light: {
      primary: '#5B7FA3',
      primaryShadow: '#496682',
      accent: '#B8D4E8',
      accentShadow: '#93AABA',
      background: '#FFFFFF',
      cardBackground: '#F5F9FC',
      text: '#3A4A5A',
      extra: '#D0E5F5',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#8BACC8',
      primaryShadow: '#5B7FA3',
      accent: '#D0E5F5',
      accentShadow: '#B8D4E8',
      background: '#101418',
      cardBackground: '#1A2028',
      text: '#E8F0F5',
      extra: '#E0EEF8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Volcanic Ash',
    tags: ['nature', 'dark', 'dramatic'],
    light: {
      primary: '#3D3D3D',
      primaryShadow: '#2D2D2D',
      accent: '#D4574A',
      accentShadow: '#AA463B',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#2D2D2D',
      extra: '#6D6D6D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0A0A0',
      primaryShadow: '#6D6D6D',
      accent: '#E07468',
      accentShadow: '#D4574A',
      background: '#0E0E0E',
      cardBackground: '#1A1A1A',
      text: '#E8E8E8',
      extra: '#B5B5B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ========================================
  // Nature Themes - Desert
  // ========================================
  {
    name: 'Sahara Dunes',
    tags: ['nature', 'desert', 'warm'],
    light: {
      primary: '#C4956A',
      primaryShadow: '#9D7755',
      accent: '#E8C99B',
      accentShadow: '#BA9F7C',
      background: '#FFFFFF',
      cardBackground: '#FBF8F3',
      text: '#5D4A35',
      extra: '#D9B07E',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#DAAF82',
      primaryShadow: '#C4956A',
      accent: '#F0D9B5',
      accentShadow: '#E8C99B',
      background: '#14110D',
      cardBackground: '#25201A',
      text: '#F5F0E8',
      extra: '#E5C598',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Sonoran Sunset',
    tags: ['nature', 'desert', 'vibrant'],
    light: {
      primary: '#C94B4B',
      primaryShadow: '#A13C3C',
      accent: '#FFB75E',
      accentShadow: '#CC924B',
      background: '#FFFFFF',
      cardBackground: '#FFF8F5',
      text: '#4A3030',
      extra: '#FF9A5C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DB6B6B',
      primaryShadow: '#C94B4B',
      accent: '#FFC87A',
      accentShadow: '#FFB75E',
      background: '#14100F',
      cardBackground: '#28201C',
      text: '#F8F2F0',
      extra: '#FFB07A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mojave Dust',
    tags: ['nature', 'desert', 'muted'],
    light: {
      primary: '#8B7355',
      primaryShadow: '#6F5C44',
      accent: '#C9B897',
      accentShadow: '#A19379',
      background: '#FFFFFF',
      cardBackground: '#F8F6F2',
      text: '#4A4030',
      extra: '#A08E78',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#B39B7D',
      primaryShadow: '#8B7355',
      accent: '#DDD0B5',
      accentShadow: '#C9B897',
      background: '#12100E',
      cardBackground: '#22201C',
      text: '#F0EDE8',
      extra: '#C5B5A0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Cactus Garden',
    tags: ['nature', 'desert', 'green'],
    light: {
      primary: '#5A7247',
      primaryShadow: '#485B39',
      accent: '#D4A76A',
      accentShadow: '#AA8555',
      background: '#FFFFFF',
      cardBackground: '#F7F9F5',
      text: '#3A4830',
      extra: '#8BAA6F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7D9965',
      primaryShadow: '#5A7247',
      accent: '#E2BC85',
      accentShadow: '#D4A76A',
      background: '#101410',
      cardBackground: '#1E251C',
      text: '#EEF2EB',
      extra: '#A5C088',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ========================================
  // Pastel Themes
  // ========================================
  {
    name: 'Cotton Candy',
    tags: ['pastel', 'playful', 'soft'],
    light: {
      primary: '#FFB5E8',
      primaryShadow: '#CC91BA',
      accent: '#B5DEFF',
      accentShadow: '#91B2CC',
      background: '#FFFFFF',
      cardBackground: '#FFF8FC',
      text: '#5A5A5A',
      extra: '#DCD3FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFC8EE',
      primaryShadow: '#FFB5E8',
      accent: '#C8E8FF',
      accentShadow: '#B5DEFF',
      background: '#1A161C',
      cardBackground: '#28222A',
      text: '#F0EEF2',
      extra: '#E8E0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Garden',
    tags: ['pastel', 'spring', 'fresh'],
    light: {
      primary: '#A8E6CF',
      primaryShadow: '#86B8A6',
      accent: '#FFAAA5',
      accentShadow: '#CC8884',
      background: '#FFFFFF',
      cardBackground: '#FAFFFE',
      text: '#4A5A52',
      extra: '#DCEDC1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B8F0DC',
      primaryShadow: '#A8E6CF',
      accent: '#FFB8B4',
      accentShadow: '#FFAAA5',
      background: '#141A18',
      cardBackground: '#202A26',
      text: '#EEF5F2',
      extra: '#E6F5D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Seashell Pink',
    tags: ['pastel', 'soft', 'feminine'],
    light: {
      primary: '#F8B4B4',
      primaryShadow: '#C69090',
      accent: '#FFDAB9',
      accentShadow: '#CCAE94',
      background: '#FFFFFF',
      cardBackground: '#FFFAF8',
      text: '#5A4A4A',
      extra: '#FFE5EC',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FAC8C8',
      primaryShadow: '#F8B4B4',
      accent: '#FFE5CC',
      accentShadow: '#FFDAB9',
      background: '#1A1516',
      cardBackground: '#2A2224',
      text: '#F5EFEF',
      extra: '#FFEEF2',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Baby Blue',
    tags: ['pastel', 'calm', 'soft'],
    light: {
      primary: '#AEC6CF',
      primaryShadow: '#8B9EA6',
      accent: '#C3B1E1',
      accentShadow: '#9C8EB4',
      background: '#FFFFFF',
      cardBackground: '#F8FBFC',
      text: '#4A5660',
      extra: '#B4D7E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C0D4DC',
      primaryShadow: '#AEC6CF',
      accent: '#D4C5EB',
      accentShadow: '#C3B1E1',
      background: '#141820',
      cardBackground: '#1E2530',
      text: '#EDF0F2',
      extra: '#C8E2F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Buttercream',
    tags: ['pastel', 'warm', 'cozy'],
    light: {
      primary: '#F9E79F',
      primaryShadow: '#C7B97F',
      accent: '#FADBD8',
      accentShadow: '#C8AFAD',
      background: '#FFFFFF',
      cardBackground: '#FFFEF8',
      text: '#5A5040',
      extra: '#FCF3CF',
      h1Color: 'text',
      h2Color: 'text',
      h3Color: 'text',
    },
    dark: {
      primary: '#FAEBB2',
      primaryShadow: '#F9E79F',
      accent: '#FCE9E6',
      accentShadow: '#FADBD8',
      background: '#18160E',
      cardBackground: '#28251A',
      text: '#F5F2E8',
      extra: '#FDF7E0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Lavender Mist',
    tags: ['pastel', 'calming', 'elegant'],
    light: {
      primary: '#D8BFD8',
      primaryShadow: '#AD99AD',
      accent: '#E6E6FA',
      accentShadow: '#B8B8C8',
      background: '#FFFFFF',
      cardBackground: '#FDFAFF',
      text: '#4A4A58',
      extra: '#F0E6FF',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E4D0E4',
      primaryShadow: '#D8BFD8',
      accent: '#F0F0FF',
      accentShadow: '#E6E6FA',
      background: '#1A181C',
      cardBackground: '#28252A',
      text: '#F2F0F5',
      extra: '#F5EDFF',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Pistachio',
    tags: ['pastel', 'fresh', 'nature'],
    light: {
      primary: '#C1E1C1',
      primaryShadow: '#9AB49A',
      accent: '#E8D5B7',
      accentShadow: '#BAAA92',
      background: '#FFFFFF',
      cardBackground: '#F9FDF9',
      text: '#4A5A4A',
      extra: '#D4EED4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D0EAD0',
      primaryShadow: '#C1E1C1',
      accent: '#F0E3CA',
      accentShadow: '#E8D5B7',
      background: '#141A14',
      cardBackground: '#202820',
      text: '#EEF5EE',
      extra: '#E0F5E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Peach Blossom',
    tags: ['pastel', 'warm', 'romantic'],
    light: {
      primary: '#FFCBA4',
      primaryShadow: '#CCA283',
      accent: '#FFE5D9',
      accentShadow: '#CCB7AE',
      background: '#FFFFFF',
      cardBackground: '#FFFCF8',
      text: '#5A4840',
      extra: '#FFD8BE',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FFD8BE',
      primaryShadow: '#FFCBA4',
      accent: '#FFEFE8',
      accentShadow: '#FFE5D9',
      background: '#1A1512',
      cardBackground: '#28221C',
      text: '#F5F0EA',
      extra: '#FFE5D0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // Neon & Cyberpunk Themes
  // ========================================
  {
    name: 'Cyber Punk',
    tags: ['neon', 'cyberpunk', 'futuristic'],
    light: {
      primary: '#FF00FF',
      primaryShadow: '#CC00CC',
      accent: '#00FFFF',
      accentShadow: '#00CCCC',
      background: '#FFFFFF',
      cardBackground: '#F5F0F8',
      text: '#2D2D2D',
      extra: '#FF6AD5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF33FF',
      primaryShadow: '#FF00FF',
      accent: '#33FFFF',
      accentShadow: '#00FFFF',
      background: '#0A0A14',
      cardBackground: '#1A1A28',
      text: '#E0E0E8',
      extra: '#FF80DD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neon Tokyo',
    tags: ['neon', 'asian', 'vibrant'],
    light: {
      primary: '#E040FB',
      primaryShadow: '#B333C9',
      accent: '#00E5FF',
      accentShadow: '#00B7CC',
      background: '#FFFFFF',
      cardBackground: '#F8F0FF',
      text: '#1A1A2E',
      extra: '#FF4081',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EA66FB',
      primaryShadow: '#E040FB',
      accent: '#33EBFF',
      accentShadow: '#00E5FF',
      background: '#0D0D1A',
      cardBackground: '#1A1A30',
      text: '#E8E8F0',
      extra: '#FF669A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Electric Lime',
    tags: ['neon', 'energetic', 'bold'],
    light: {
      primary: '#ADFF2F',
      primaryShadow: '#8ACC26',
      accent: '#FF1493',
      accentShadow: '#CC1076',
      background: '#FFFFFF',
      cardBackground: '#F8FFF0',
      text: '#1A2D10',
      extra: '#C6FF4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C0FF52',
      primaryShadow: '#ADFF2F',
      accent: '#FF47A6',
      accentShadow: '#FF1493',
      background: '#0D140A',
      cardBackground: '#1A2815',
      text: '#E8F5E0',
      extra: '#D4FF6D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Laser Grid',
    tags: ['neon', 'retro', '80s'],
    light: {
      primary: '#FF3366',
      primaryShadow: '#CC2952',
      accent: '#33CCFF',
      accentShadow: '#29A3CC',
      background: '#FFFFFF',
      cardBackground: '#FFF5F8',
      text: '#1A1A2E',
      extra: '#FF6B99',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5580',
      primaryShadow: '#FF3366',
      accent: '#55D6FF',
      accentShadow: '#33CCFF',
      background: '#0A0A18',
      cardBackground: '#1A1A30',
      text: '#E8E8F5',
      extra: '#FF88AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Synthwave',
    tags: ['neon', 'retro', '80s'],
    light: {
      primary: '#B026FF',
      primaryShadow: '#8D1ECC',
      accent: '#FF6EC7',
      accentShadow: '#CC589F',
      background: '#FFFFFF',
      cardBackground: '#FBF5FF',
      text: '#2E1A3E',
      extra: '#7B5FFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C34DFF',
      primaryShadow: '#B026FF',
      accent: '#FF8AD4',
      accentShadow: '#FF6EC7',
      background: '#0E0818',
      cardBackground: '#1E1430',
      text: '#E8E0F5',
      extra: '#9580FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Plasma Core',
    tags: ['neon', 'futuristic', 'sci-fi'],
    light: {
      primary: '#00FF87',
      primaryShadow: '#00CC6C',
      accent: '#FF0099',
      accentShadow: '#CC007A',
      background: '#FFFFFF',
      cardBackground: '#F0FFF8',
      text: '#1A2E1A',
      extra: '#33FF9F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#33FF9F',
      primaryShadow: '#00FF87',
      accent: '#FF33AD',
      accentShadow: '#FF0099',
      background: '#081410',
      cardBackground: '#142820',
      text: '#E0F5E8',
      extra: '#55FFB0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vaporwave',
    tags: ['neon', 'retro', 'aesthetic'],
    light: {
      primary: '#FF71CE',
      primaryShadow: '#CC5AA5',
      accent: '#01CDFE',
      accentShadow: '#01A4CB',
      background: '#FFFFFF',
      cardBackground: '#FFF5FA',
      text: '#3D2D35',
      extra: '#B967FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8DD8',
      primaryShadow: '#FF71CE',
      accent: '#34D7FE',
      accentShadow: '#01CDFE',
      background: '#0D0A10',
      cardBackground: '#1D1520',
      text: '#F0E8ED',
      extra: '#C885FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Matrix Green',
    tags: ['neon', 'tech', 'hacker'],
    light: {
      primary: '#00FF41',
      primaryShadow: '#00CC34',
      accent: '#008F11',
      accentShadow: '#00720E',
      background: '#FFFFFF',
      cardBackground: '#F0FFF2',
      text: '#0A2010',
      extra: '#33FF66',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#00FF41',
      primaryShadow: '#00CC34',
      accent: '#39FF14',
      accentShadow: '#00FF41',
      background: '#000A00',
      cardBackground: '#001A00',
      text: '#88FF88',
      extra: '#55FF77',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ========================================
  // Vintage & Retro Themes
  // ========================================
  {
    name: 'Muted Mauve',
    tags: ['vintage', 'muted', 'elegant'],
    light: {
      primary: '#967E76',
      primaryShadow: '#78655E',
      accent: '#D7C0AE',
      accentShadow: '#AC9A8B',
      background: '#FFFFFF',
      cardBackground: '#FAF6F3',
      text: '#4A403A',
      extra: '#EEE3CB',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#B09A92',
      primaryShadow: '#967E76',
      accent: '#E5D4C6',
      accentShadow: '#D7C0AE',
      background: '#141210',
      cardBackground: '#252220',
      text: '#EDE8E5',
      extra: '#F5EBD8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Retro Cream',
    tags: ['vintage', 'warm', '50s'],
    light: {
      primary: '#C9A959',
      primaryShadow: '#A18747',
      accent: '#7A918D',
      accentShadow: '#627471',
      background: '#FFFEF5',
      cardBackground: '#F5F2E8',
      text: '#4A4A38',
      extra: '#DFC27D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DBBF75',
      primaryShadow: '#C9A959',
      accent: '#98ADA9',
      accentShadow: '#7A918D',
      background: '#141410',
      cardBackground: '#25251E',
      text: '#F2F0E5',
      extra: '#EBD495',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Rose',
    tags: ['vintage', 'romantic', 'muted'],
    light: {
      primary: '#B5838D',
      primaryShadow: '#916971',
      accent: '#E5989B',
      accentShadow: '#B77A7C',
      background: '#FFFFFF',
      cardBackground: '#FDF8F8',
      text: '#4A3A3D',
      extra: '#FFB4A2',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#C99DA5',
      primaryShadow: '#B5838D',
      accent: '#EBADAF',
      accentShadow: '#E5989B',
      background: '#1A1416',
      cardBackground: '#2A2225',
      text: '#F2EAEB',
      extra: '#FFC8BA',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Art Deco Gold',
    tags: ['vintage', 'elegant', 'luxurious'],
    light: {
      primary: '#1A1A2E',
      primaryShadow: '#0F0F1A',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFFFF',
      cardBackground: '#FAF8F2',
      text: '#1A1A2E',
      extra: '#E6C860',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D4AF37',
      primaryShadow: '#AA8C2C',
      accent: '#E6C860',
      accentShadow: '#D4AF37',
      background: '#0A0A12',
      cardBackground: '#16161F',
      text: '#F0E8D8',
      extra: '#F0D978',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sepia Tone',
    tags: ['vintage', 'warm', 'nostalgic'],
    light: {
      primary: '#704214',
      primaryShadow: '#5A3510',
      accent: '#A67B5B',
      accentShadow: '#856249',
      background: '#FFF8E7',
      cardBackground: '#F5ECD8',
      text: '#4A3520',
      extra: '#C49A6C',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#B8935A',
      primaryShadow: '#8A6E44',
      accent: '#C9A878',
      accentShadow: '#A67B5B',
      background: '#14100A',
      cardBackground: '#252015',
      text: '#F0E8D8',
      extra: '#DAB88A',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Groovy Orange',
    tags: ['retro', '70s', 'bold'],
    light: {
      primary: '#FF6700',
      primaryShadow: '#CC5200',
      accent: '#EEAA00',
      accentShadow: '#BE8800',
      background: '#FFFAF0',
      cardBackground: '#FFF5E0',
      text: '#4A3A20',
      extra: '#FF8533',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8533',
      primaryShadow: '#FF6700',
      accent: '#F2BD33',
      accentShadow: '#EEAA00',
      background: '#140E08',
      cardBackground: '#281C10',
      text: '#F5EDE0',
      extra: '#FFA366',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Avocado Toast',
    tags: ['retro', '70s', 'earthy'],
    light: {
      primary: '#87A96B',
      primaryShadow: '#6C8756',
      accent: '#C5B358',
      accentShadow: '#9E8F46',
      background: '#FFFEF8',
      cardBackground: '#F8F8F0',
      text: '#3A4030',
      extra: '#A5C485',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A5C485',
      primaryShadow: '#87A96B',
      accent: '#D8CA76',
      accentShadow: '#C5B358',
      background: '#10140E',
      cardBackground: '#1E251A',
      text: '#EEF2E8',
      extra: '#B8D4A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Harvest Gold',
    tags: ['retro', '70s', 'warm'],
    light: {
      primary: '#DA9100',
      primaryShadow: '#AE7400',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFFBF0',
      cardBackground: '#FFF5E0',
      text: '#4A3810',
      extra: '#F0A820',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F0A820',
      primaryShadow: '#DA9100',
      accent: '#B06935',
      accentShadow: '#8B4513',
      background: '#14100A',
      cardBackground: '#281E10',
      text: '#F5EED8',
      extra: '#F8BC4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ========================================
  // Seasonal - Spring
  // ========================================
  {
    name: 'Spring Bloom',
    tags: ['spring', 'fresh', 'floral'],
    light: {
      primary: '#FF85A1',
      primaryShadow: '#CC6A81',
      accent: '#7FD8BE',
      accentShadow: '#66AD98',
      background: '#FFFFFF',
      cardBackground: '#FFF8FA',
      text: '#3A4040',
      extra: '#FFB5C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9DB4',
      primaryShadow: '#FF85A1',
      accent: '#99E2CC',
      accentShadow: '#7FD8BE',
      background: '#141518',
      cardBackground: '#202428',
      text: '#F0F2F2',
      extra: '#FFC8D5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cherry Orchard',
    tags: ['spring', 'asian', 'elegant'],
    light: {
      primary: '#FFB7C5',
      primaryShadow: '#CC929D',
      accent: '#98D9C2',
      accentShadow: '#7AAE9B',
      background: '#FFFFFF',
      cardBackground: '#FFFAFC',
      text: '#4A4048',
      extra: '#FFD0DC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFC8D4',
      primaryShadow: '#FFB7C5',
      accent: '#AEE4D0',
      accentShadow: '#98D9C2',
      background: '#1A1618',
      cardBackground: '#2A2428',
      text: '#F2EEF0',
      extra: '#FFE0E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Meadow Green',
    tags: ['spring', 'nature', 'fresh'],
    light: {
      primary: '#77DD77',
      primaryShadow: '#5FB15F',
      accent: '#FFFACD',
      accentShadow: '#CCC8A4',
      background: '#FFFFFF',
      cardBackground: '#F8FFF8',
      text: '#2D4030',
      extra: '#99E699',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'text',
    },
    dark: {
      primary: '#99E699',
      primaryShadow: '#77DD77',
      accent: '#FFFCE0',
      accentShadow: '#FFFACD',
      background: '#101410',
      cardBackground: '#1C251C',
      text: '#E8F5E8',
      extra: '#AAF0AA',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // Seasonal - Summer
  // ========================================
  {
    name: 'Tropical Paradise',
    tags: ['summer', 'tropical', 'vibrant'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#4ECDC4',
      accentShadow: '#3EA49D',
      background: '#FFFFFF',
      cardBackground: '#FFF8F0',
      text: '#2D3A3A',
      extra: '#FFE66D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8585',
      primaryShadow: '#FF6B6B',
      accent: '#6FDDD6',
      accentShadow: '#4ECDC4',
      background: '#141818',
      cardBackground: '#202828',
      text: '#F0F5F5',
      extra: '#FFEC8A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Watermelon',
    tags: ['summer', 'playful', 'fresh'],
    light: {
      primary: '#FE4A49',
      primaryShadow: '#CB3B3B',
      accent: '#97D99A',
      accentShadow: '#79AE7B',
      background: '#FFFFFF',
      cardBackground: '#FFF8F8',
      text: '#2D3830',
      extra: '#FF7474',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6B6B',
      primaryShadow: '#FE4A49',
      accent: '#AEE5B0',
      accentShadow: '#97D99A',
      background: '#14100F',
      cardBackground: '#28201E',
      text: '#F5F0EE',
      extra: '#FF8F8F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ocean Surf',
    tags: ['summer', 'beach', 'cool'],
    light: {
      primary: '#0077B6',
      primaryShadow: '#005F92',
      accent: '#90E0EF',
      accentShadow: '#73B3BF',
      background: '#FFFFFF',
      cardBackground: '#F0FAFF',
      text: '#023047',
      extra: '#00B4D8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#00B4D8',
      primaryShadow: '#0077B6',
      accent: '#ADE8F4',
      accentShadow: '#90E0EF',
      background: '#011620',
      cardBackground: '#012535',
      text: '#E0F4F8',
      extra: '#48CAE4',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // Seasonal - Autumn
  // ========================================
  {
    name: 'Maple Leaf',
    tags: ['autumn', 'warm', 'nature'],
    light: {
      primary: '#C1440E',
      primaryShadow: '#9A360B',
      accent: '#FCA311',
      accentShadow: '#CA820E',
      background: '#FFFAF5',
      cardBackground: '#FFF5EA',
      text: '#3D2510',
      extra: '#E57C23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E57C23',
      primaryShadow: '#C1440E',
      accent: '#FDB83D',
      accentShadow: '#FCA311',
      background: '#14100C',
      cardBackground: '#281C14',
      text: '#F5EDE5',
      extra: '#F09545',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pumpkin Spice',
    tags: ['autumn', 'cozy', 'warm'],
    light: {
      primary: '#E07B39',
      primaryShadow: '#B3622E',
      accent: '#744F28',
      accentShadow: '#5D3F20',
      background: '#FFFBF5',
      cardBackground: '#FFF8F0',
      text: '#3D2E1F',
      extra: '#F4A460',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F09555',
      primaryShadow: '#E07B39',
      accent: '#9A7045',
      accentShadow: '#744F28',
      background: '#14100C',
      cardBackground: '#25201A',
      text: '#F5EFE5',
      extra: '#F8B580',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Forest Floor',
    tags: ['autumn', 'earthy', 'organic'],
    light: {
      primary: '#6B4423',
      primaryShadow: '#56361C',
      accent: '#9B7B4A',
      accentShadow: '#7C623B',
      background: '#FFFEF8',
      cardBackground: '#F8F5F0',
      text: '#3D3020',
      extra: '#A5894F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A07345',
      primaryShadow: '#6B4423',
      accent: '#B99A6A',
      accentShadow: '#9B7B4A',
      background: '#12100C',
      cardBackground: '#221E18',
      text: '#F0EBE5',
      extra: '#C9AA75',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ========================================
  // Seasonal - Winter
  // ========================================
  {
    name: 'Frozen Lake',
    tags: ['winter', 'cool', 'serene'],
    light: {
      primary: '#5B8FA8',
      primaryShadow: '#497286',
      accent: '#B8E0F4',
      accentShadow: '#93B3C3',
      background: '#FFFFFF',
      cardBackground: '#F5FAFC',
      text: '#2A4050',
      extra: '#88C4DC',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#88C4DC',
      primaryShadow: '#5B8FA8',
      accent: '#D0ECF8',
      accentShadow: '#B8E0F4',
      background: '#0C1418',
      cardBackground: '#162028',
      text: '#E5F0F5',
      extra: '#A5D4E8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Evergreen',
    tags: ['winter', 'nature', 'classic'],
    light: {
      primary: '#355E3B',
      primaryShadow: '#2A4B2F',
      accent: '#C41E3A',
      accentShadow: '#9D182E',
      background: '#FFFFFF',
      cardBackground: '#F5F8F5',
      text: '#1F3620',
      extra: '#5A8A5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5A8A5F',
      primaryShadow: '#355E3B',
      accent: '#E03E58',
      accentShadow: '#C41E3A',
      background: '#0C1410',
      cardBackground: '#162318',
      text: '#E5F0E8',
      extra: '#7AA880',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snowstorm',
    tags: ['winter', 'minimal', 'clean'],
    light: {
      primary: '#778899',
      primaryShadow: '#5F6D7A',
      accent: '#B0C4DE',
      accentShadow: '#8D9DB2',
      background: '#FFFFFF',
      cardBackground: '#F8FAFC',
      text: '#3A4A5A',
      extra: '#C8D5E5',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A0B4C4',
      primaryShadow: '#778899',
      accent: '#C8D8EA',
      accentShadow: '#B0C4DE',
      background: '#101418',
      cardBackground: '#1A2028',
      text: '#E8F0F5',
      extra: '#DCE5F0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // BATCH 2: Gemstone & Mineral Themes
  // ========================================
  {
    name: 'Ruby Red',
    tags: ['gemstone', 'luxurious', 'bold'],
    light: {
      primary: '#E0115F',
      primaryShadow: '#B30E4C',
      accent: '#FFB6C1',
      accentShadow: '#CC929A',
      background: '#FFFFFF',
      cardBackground: '#FFF5F7',
      text: '#4A1A25',
      extra: '#FF6B8A',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FF4D7A',
      primaryShadow: '#E0115F',
      accent: '#FFC8D0',
      accentShadow: '#FFB6C1',
      background: '#1A0A10',
      cardBackground: '#2D1520',
      text: '#FFE8ED',
      extra: '#FF8AA5',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Sapphire Blue',
    tags: ['gemstone', 'elegant', 'royal'],
    light: {
      primary: '#0F52BA',
      primaryShadow: '#0C4295',
      accent: '#A7C7E7',
      accentShadow: '#869FB9',
      background: '#FFFFFF',
      cardBackground: '#F0F5FA',
      text: '#0A2A5A',
      extra: '#4682B4',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#4D8FE0',
      primaryShadow: '#0F52BA',
      accent: '#B8D4F0',
      accentShadow: '#A7C7E7',
      background: '#050D1A',
      cardBackground: '#0A1A30',
      text: '#E0ECF8',
      extra: '#6FA8D6',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Emerald Green',
    tags: ['gemstone', 'rich', 'nature'],
    light: {
      primary: '#50C878',
      primaryShadow: '#40A060',
      accent: '#98FB98',
      accentShadow: '#7AC97A',
      background: '#FFFFFF',
      cardBackground: '#F0FFF5',
      text: '#1A4A28',
      extra: '#3CB371',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#6FD99A',
      primaryShadow: '#50C878',
      accent: '#AFFFAF',
      accentShadow: '#98FB98',
      background: '#0A1A10',
      cardBackground: '#152D1C',
      text: '#E0FFE8',
      extra: '#5FD890',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Amethyst Purple',
    tags: ['gemstone', 'mystical', 'elegant'],
    light: {
      primary: '#9966CC',
      primaryShadow: '#7A52A3',
      accent: '#DDA0DD',
      accentShadow: '#B180B1',
      background: '#FFFFFF',
      cardBackground: '#F8F0FF',
      text: '#3D2A50',
      extra: '#B19CD9',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#B388DD',
      primaryShadow: '#9966CC',
      accent: '#E8B8E8',
      accentShadow: '#DDA0DD',
      background: '#12081A',
      cardBackground: '#221530',
      text: '#F0E8F8',
      extra: '#C8A8E8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Topaz Gold',
    tags: ['gemstone', 'warm', 'luxurious'],
    light: {
      primary: '#FFC87C',
      primaryShadow: '#CCA063',
      accent: '#FFE4B5',
      accentShadow: '#CCB691',
      background: '#FFFFFF',
      cardBackground: '#FFFAF0',
      text: '#5A4020',
      extra: '#FFD700',
      h1Color: 'text',
      h2Color: 'text',
      h3Color: 'text',
    },
    dark: {
      primary: '#FFD899',
      primaryShadow: '#FFC87C',
      accent: '#FFECC8',
      accentShadow: '#FFE4B5',
      background: '#1A1408',
      cardBackground: '#2D2410',
      text: '#FFF5E0',
      extra: '#FFE033',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Opal Iridescent',
    tags: ['gemstone', 'pastel', 'magical'],
    light: {
      primary: '#A8C8DC',
      primaryShadow: '#86A0B0',
      accent: '#E8D0E8',
      accentShadow: '#BAA6BA',
      background: '#FFFFFF',
      cardBackground: '#F8FAFC',
      text: '#3A4A55',
      extra: '#D0E8F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B8D8EC',
      primaryShadow: '#A8C8DC',
      accent: '#F0E0F0',
      accentShadow: '#E8D0E8',
      background: '#101418',
      cardBackground: '#1A2028',
      text: '#E8F0F5',
      extra: '#E0F0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Onyx Black',
    tags: ['gemstone', 'elegant', 'dark'],
    light: {
      primary: '#353935',
      primaryShadow: '#2A2E2A',
      accent: '#708090',
      accentShadow: '#5A6873',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1C1A',
      extra: '#505550',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A0A5A0',
      primaryShadow: '#707570',
      accent: '#B0C0D0',
      accentShadow: '#90A0B0',
      background: '#0A0C0A',
      cardBackground: '#151815',
      text: '#E0E2E0',
      extra: '#808580',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Rose Quartz',
    tags: ['gemstone', 'soft', 'romantic'],
    light: {
      primary: '#F7CAC9',
      primaryShadow: '#C5A2A1',
      accent: '#FADCD9',
      accentShadow: '#C8B0AE',
      background: '#FFFFFF',
      cardBackground: '#FFFAFA',
      text: '#5A4545',
      extra: '#F9E0DF',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#F9D8D7',
      primaryShadow: '#F7CAC9',
      accent: '#FCE8E6',
      accentShadow: '#FADCD9',
      background: '#1A1515',
      cardBackground: '#2A2222',
      text: '#F5EDED',
      extra: '#FBEAE9',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Turquoise Stone',
    tags: ['gemstone', 'fresh', 'vibrant'],
    light: {
      primary: '#40E0D0',
      primaryShadow: '#33B3A6',
      accent: '#AFEEEE',
      accentShadow: '#8CBEBE',
      background: '#FFFFFF',
      cardBackground: '#F0FFFF',
      text: '#1A4A48',
      extra: '#48D1CC',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#66E8DB',
      primaryShadow: '#40E0D0',
      accent: '#C2F4F4',
      accentShadow: '#AFEEEE',
      background: '#0A1818',
      cardBackground: '#152828',
      text: '#E0FFFF',
      extra: '#6FE8E0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Citrine Yellow',
    tags: ['gemstone', 'bright', 'cheerful'],
    light: {
      primary: '#E4D00A',
      primaryShadow: '#B6A608',
      accent: '#FFF8DC',
      accentShadow: '#CCC6B0',
      background: '#FFFFFF',
      cardBackground: '#FFFEF5',
      text: '#4A4510',
      extra: '#F0E68C',
      h1Color: 'text',
      h2Color: 'text',
      h3Color: 'text',
    },
    dark: {
      primary: '#EBD93D',
      primaryShadow: '#E4D00A',
      accent: '#FFFBE8',
      accentShadow: '#FFF8DC',
      background: '#14140A',
      cardBackground: '#28280F',
      text: '#FFFAE0',
      extra: '#F5EDA0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // Space & Cosmic Themes
  // ========================================
  {
    name: 'Nebula Pink',
    tags: ['space', 'cosmic', 'vibrant'],
    light: {
      primary: '#FF6B9D',
      primaryShadow: '#CC567E',
      accent: '#C44569',
      accentShadow: '#9D3754',
      background: '#FFFFFF',
      cardBackground: '#FFF5F8',
      text: '#3D1A28',
      extra: '#FF8DB4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8DB4',
      primaryShadow: '#FF6B9D',
      accent: '#D66A88',
      accentShadow: '#C44569',
      background: '#0D0610',
      cardBackground: '#1A1020',
      text: '#FFE8F0',
      extra: '#FFB0CA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Galaxy Purple',
    tags: ['space', 'cosmic', 'dark'],
    light: {
      primary: '#4B0082',
      primaryShadow: '#3C0068',
      accent: '#9400D3',
      accentShadow: '#7600A9',
      background: '#FFFFFF',
      cardBackground: '#F8F0FF',
      text: '#2D0050',
      extra: '#8B00FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9933FF',
      primaryShadow: '#7700CC',
      accent: '#B366FF',
      accentShadow: '#9400D3',
      background: '#08000D',
      cardBackground: '#14001F',
      text: '#E8D0FF',
      extra: '#AA55FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Starlight Silver',
    tags: ['space', 'minimal', 'elegant'],
    light: {
      primary: '#C0C0C0',
      primaryShadow: '#9A9A9A',
      accent: '#E8E8E8',
      accentShadow: '#BABABA',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#3A3A3A',
      extra: '#D0D0D0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#D0D0D0',
      primaryShadow: '#C0C0C0',
      accent: '#F0F0F0',
      accentShadow: '#E8E8E8',
      background: '#0A0A0D',
      cardBackground: '#151518',
      text: '#E8E8EA',
      extra: '#E0E0E0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Aurora Borealis',
    tags: ['space', 'vibrant', 'magical'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#98FB98',
      accentShadow: '#7AC97A',
      background: '#FFFFFF',
      cardBackground: '#F0FFFA',
      text: '#1A3A38',
      extra: '#7FFFD4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#33DDE0',
      primaryShadow: '#00CED1',
      accent: '#AFFFAF',
      accentShadow: '#98FB98',
      background: '#050D0C',
      cardBackground: '#0A1A18',
      text: '#E0FFFA',
      extra: '#99FFE0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mars Red',
    tags: ['space', 'warm', 'bold'],
    light: {
      primary: '#CD5C5C',
      primaryShadow: '#A44A4A',
      accent: '#F08080',
      accentShadow: '#C06666',
      background: '#FFFFFF',
      cardBackground: '#FFF8F8',
      text: '#4A2020',
      extra: '#E07070',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E07878',
      primaryShadow: '#CD5C5C',
      accent: '#F5A0A0',
      accentShadow: '#F08080',
      background: '#140808',
      cardBackground: '#281414',
      text: '#FFE8E8',
      extra: '#F09090',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Saturn Ring',
    tags: ['space', 'warm', 'elegant'],
    light: {
      primary: '#DAA520',
      primaryShadow: '#AE841A',
      accent: '#F4C430',
      accentShadow: '#C39D26',
      background: '#FFFFFF',
      cardBackground: '#FFFCF0',
      text: '#4A3810',
      extra: '#E8B830',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E8B830',
      primaryShadow: '#DAA520',
      accent: '#F8D050',
      accentShadow: '#F4C430',
      background: '#14120A',
      cardBackground: '#282414',
      text: '#FFF8E0',
      extra: '#F0C848',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Deep Space',
    tags: ['space', 'dark', 'mysterious'],
    light: {
      primary: '#191970',
      primaryShadow: '#14145A',
      accent: '#483D8B',
      accentShadow: '#3A316F',
      background: '#FFFFFF',
      cardBackground: '#F5F5FA',
      text: '#0A0A30',
      extra: '#6A5ACD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6A5ACD',
      primaryShadow: '#483D8B',
      accent: '#8A7AE0',
      accentShadow: '#6A5ACD',
      background: '#050508',
      cardBackground: '#0A0A14',
      text: '#D8D8F0',
      extra: '#9A8AE8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Supernova',
    tags: ['space', 'vibrant', 'energetic'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFFF',
      cardBackground: '#FFFAF5',
      text: '#4A2010',
      extra: '#FF6B33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6B33',
      primaryShadow: '#FF4500',
      accent: '#FFE033',
      accentShadow: '#FFD700',
      background: '#140A05',
      cardBackground: '#28140A',
      text: '#FFF0E0',
      extra: '#FF8855',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ========================================
  // Food & Beverage Themes
  // ========================================
  {
    name: 'Espresso',
    tags: ['food', 'warm', 'cozy'],
    light: {
      primary: '#3C2415',
      primaryShadow: '#301D11',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFFBF5',
      cardBackground: '#F5EDE5',
      text: '#2D1A10',
      extra: '#6F4E37',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A08070',
      primaryShadow: '#6F4E37',
      accent: '#B87A50',
      accentShadow: '#8B4513',
      background: '#120C08',
      cardBackground: '#251C14',
      text: '#F0E8E0',
      extra: '#C0A090',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Matcha Green',
    tags: ['food', 'fresh', 'zen'],
    light: {
      primary: '#78A63C',
      primaryShadow: '#608530',
      accent: '#D4E8B4',
      accentShadow: '#AABA90',
      background: '#FFFFFF',
      cardBackground: '#F8FCF5',
      text: '#3A4A28',
      extra: '#9AC060',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#9AC060',
      primaryShadow: '#78A63C',
      accent: '#E0F0C8',
      accentShadow: '#D4E8B4',
      background: '#101408',
      cardBackground: '#1E2810',
      text: '#E8F5D8',
      extra: '#B0D080',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Strawberry Cream',
    tags: ['food', 'sweet', 'playful'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#FFF0F0',
      accentShadow: '#CCC0C0',
      background: '#FFFFFF',
      cardBackground: '#FFF8F8',
      text: '#4A2828',
      extra: '#FF9999',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'text',
    },
    dark: {
      primary: '#FF8585',
      primaryShadow: '#FF6B6B',
      accent: '#FFF5F5',
      accentShadow: '#FFF0F0',
      background: '#1A0F0F',
      cardBackground: '#2D1A1A',
      text: '#FFEDEA',
      extra: '#FFAAAA',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Blueberry',
    tags: ['food', 'cool', 'fresh'],
    light: {
      primary: '#4F86F7',
      primaryShadow: '#3F6BC5',
      accent: '#8B93FF',
      accentShadow: '#6F76CC',
      background: '#FFFFFF',
      cardBackground: '#F5F8FF',
      text: '#1A2850',
      extra: '#7AA3FF',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#7AA3FF',
      primaryShadow: '#4F86F7',
      accent: '#A8AFFF',
      accentShadow: '#8B93FF',
      background: '#0A0E1A',
      cardBackground: '#141A30',
      text: '#E0E8FF',
      extra: '#99B8FF',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Lemon Zest',
    tags: ['food', 'bright', 'fresh'],
    light: {
      primary: '#FFF44F',
      primaryShadow: '#CCC33F',
      accent: '#FFFACD',
      accentShadow: '#CCC8A4',
      background: '#FFFFFF',
      cardBackground: '#FFFEF5',
      text: '#4A4820',
      extra: '#FFF76B',
      h1Color: 'text',
      h2Color: 'text',
      h3Color: 'text',
    },
    dark: {
      primary: '#FFF76B',
      primaryShadow: '#FFF44F',
      accent: '#FFFCE0',
      accentShadow: '#FFFACD',
      background: '#14140A',
      cardBackground: '#28280F',
      text: '#FFFBD8',
      extra: '#FFF988',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Mint Chocolate',
    tags: ['food', 'cool', 'refreshing'],
    light: {
      primary: '#3EB489',
      primaryShadow: '#32906E',
      accent: '#5D3A1A',
      accentShadow: '#4A2E15',
      background: '#FFFFFF',
      cardBackground: '#F5FFF8',
      text: '#2A4A3A',
      extra: '#5FD4A9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5FD4A9',
      primaryShadow: '#3EB489',
      accent: '#8A6040',
      accentShadow: '#5D3A1A',
      background: '#0A1410',
      cardBackground: '#15281C',
      text: '#E0FFF0',
      extra: '#80E8C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Caramel Swirl',
    tags: ['food', 'warm', 'sweet'],
    light: {
      primary: '#C68E17',
      primaryShadow: '#9E7112',
      accent: '#FFD59A',
      accentShadow: '#CCAA7B',
      background: '#FFFBF5',
      cardBackground: '#FFF8E8',
      text: '#4A3818',
      extra: '#E0A530',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E0A530',
      primaryShadow: '#C68E17',
      accent: '#FFE2B0',
      accentShadow: '#FFD59A',
      background: '#14100A',
      cardBackground: '#28201C',
      text: '#FFF5E0',
      extra: '#F0B848',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Red Wine',
    tags: ['food', 'elegant', 'rich'],
    light: {
      primary: '#722F37',
      primaryShadow: '#5B262C',
      accent: '#A8596D',
      accentShadow: '#864757',
      background: '#FFFFFF',
      cardBackground: '#FDF8F9',
      text: '#3D1820',
      extra: '#8B3D4A',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A8596D',
      primaryShadow: '#722F37',
      accent: '#C88090',
      accentShadow: '#A8596D',
      background: '#120A0C',
      cardBackground: '#251518',
      text: '#F5E8EA',
      extra: '#B87080',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Orange Creamsicle',
    tags: ['food', 'playful', 'summer'],
    light: {
      primary: '#FF7F50',
      primaryShadow: '#CC6640',
      accent: '#FFF5EE',
      accentShadow: '#CCC4BE',
      background: '#FFFFFF',
      cardBackground: '#FFFAF5',
      text: '#4A3028',
      extra: '#FF9970',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'text',
    },
    dark: {
      primary: '#FF9970',
      primaryShadow: '#FF7F50',
      accent: '#FFF8F2',
      accentShadow: '#FFF5EE',
      background: '#14100C',
      cardBackground: '#28201A',
      text: '#FFEDE0',
      extra: '#FFB090',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Avocado',
    tags: ['food', 'nature', 'trendy'],
    light: {
      primary: '#568203',
      primaryShadow: '#456802',
      accent: '#87A96B',
      accentShadow: '#6C8756',
      background: '#FFFFFF',
      cardBackground: '#F8FBF5',
      text: '#2D4010',
      extra: '#6FA020',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#6FA020',
      primaryShadow: '#568203',
      accent: '#A5C485',
      accentShadow: '#87A96B',
      background: '#0C1008',
      cardBackground: '#18240F',
      text: '#E8F5D8',
      extra: '#88B840',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // Weather & Sky Themes
  // ========================================
  {
    name: 'Sunny Day',
    tags: ['weather', 'bright', 'cheerful'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#FFFFFF',
      cardBackground: '#FFFFF0',
      text: '#4A4520',
      extra: '#FFE333',
      h1Color: 'text',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#FFE333',
      primaryShadow: '#FFD700',
      accent: '#A0DCF8',
      accentShadow: '#87CEEB',
      background: '#141408',
      cardBackground: '#28280F',
      text: '#FFFCE0',
      extra: '#FFEA55',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Overcast',
    tags: ['weather', 'muted', 'calm'],
    light: {
      primary: '#708090',
      primaryShadow: '#5A6673',
      accent: '#B0C4DE',
      accentShadow: '#8D9DB2',
      background: '#FFFFFF',
      cardBackground: '#F5F8FA',
      text: '#3A4550',
      extra: '#90A0B0',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#90A0B0',
      primaryShadow: '#708090',
      accent: '#C0D0E0',
      accentShadow: '#B0C4DE',
      background: '#101418',
      cardBackground: '#1A2028',
      text: '#E0E8F0',
      extra: '#A8B8C8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Thunderstorm',
    tags: ['weather', 'dramatic', 'dark'],
    light: {
      primary: '#4A4A5C',
      primaryShadow: '#3B3B4A',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFFF',
      cardBackground: '#F5F5F8',
      text: '#2A2A35',
      extra: '#6A6A80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8A8AA0',
      primaryShadow: '#6A6A80',
      accent: '#FFE333',
      accentShadow: '#FFD700',
      background: '#0A0A0E',
      cardBackground: '#15151A',
      text: '#E0E0E8',
      extra: '#A0A0B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainbow After Rain',
    tags: ['weather', 'vibrant', 'hopeful'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#4ECDC4',
      accentShadow: '#3EA49D',
      background: '#FFFFFF',
      cardBackground: '#F8FFFE',
      text: '#2D3A38',
      extra: '#FFE66D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8585',
      primaryShadow: '#FF6B6B',
      accent: '#6FDDD5',
      accentShadow: '#4ECDC4',
      background: '#101414',
      cardBackground: '#1A2422',
      text: '#E8F5F2',
      extra: '#FFEB8A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Foggy Morning',
    tags: ['weather', 'soft', 'muted'],
    light: {
      primary: '#A9A9A9',
      primaryShadow: '#878787',
      accent: '#D3D3D3',
      accentShadow: '#A9A9A9',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#4A4A4A',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#C0C0C0',
      primaryShadow: '#A9A9A9',
      accent: '#E0E0E0',
      accentShadow: '#D3D3D3',
      background: '#0E0E0E',
      cardBackground: '#1A1A1A',
      text: '#E5E5E5',
      extra: '#D0D0D0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Clear Night Sky',
    tags: ['weather', 'dark', 'serene'],
    light: {
      primary: '#1A1A3E',
      primaryShadow: '#141432',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFFF',
      cardBackground: '#F5F5FA',
      text: '#1A1A3E',
      extra: '#4A4A7A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6A6AAA',
      primaryShadow: '#4A4A7A',
      accent: '#FFE333',
      accentShadow: '#FFD700',
      background: '#05050A',
      cardBackground: '#0A0A14',
      text: '#D8D8F0',
      extra: '#8A8AC0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Hour',
    tags: ['weather', 'warm', 'romantic'],
    light: {
      primary: '#FF8C00',
      primaryShadow: '#CC7000',
      accent: '#FFB347',
      accentShadow: '#CC8F39',
      background: '#FFFAF0',
      cardBackground: '#FFF5E6',
      text: '#4A3020',
      extra: '#FFA833',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FFA833',
      primaryShadow: '#FF8C00',
      accent: '#FFC566',
      accentShadow: '#FFB347',
      background: '#140C05',
      cardBackground: '#28180A',
      text: '#FFF0D8',
      extra: '#FFC050',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Snowy White',
    tags: ['weather', 'clean', 'winter'],
    light: {
      primary: '#5B8FA8',
      primaryShadow: '#497286',
      accent: '#E8F4F8',
      accentShadow: '#BAC3C6',
      background: '#FFFFFF',
      cardBackground: '#FCFCFC',
      text: '#2A4050',
      extra: '#88C4DC',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'text',
    },
    dark: {
      primary: '#88C4DC',
      primaryShadow: '#5B8FA8',
      accent: '#F0F8FC',
      accentShadow: '#E8F4F8',
      background: '#0C1418',
      cardBackground: '#162028',
      text: '#E8F5FA',
      extra: '#A5D4E8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },

  // ========================================
  // Geographic & Cultural Themes
  // ========================================
  {
    name: 'Tuscan Villa',
    tags: ['geographic', 'italian', 'warm'],
    light: {
      primary: '#CC7722',
      primaryShadow: '#A35F1B',
      accent: '#8B6914',
      accentShadow: '#6F5410',
      background: '#FFFBF0',
      cardBackground: '#FFF5E0',
      text: '#4A3520',
      extra: '#E09040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E09040',
      primaryShadow: '#CC7722',
      accent: '#B08830',
      accentShadow: '#8B6914',
      background: '#14100A',
      cardBackground: '#28201A',
      text: '#F5EDD8',
      extra: '#F0A858',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Fjord',
    tags: ['geographic', 'scandinavian', 'cool'],
    light: {
      primary: '#4682B4',
      primaryShadow: '#386890',
      accent: '#E0F4FF',
      accentShadow: '#B3C3CC',
      background: '#FFFFFF',
      cardBackground: '#F5FAFC',
      text: '#2A4055',
      extra: '#6A9FC8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'text',
    },
    dark: {
      primary: '#6A9FC8',
      primaryShadow: '#4682B4',
      accent: '#E8F6FF',
      accentShadow: '#E0F4FF',
      background: '#0A1018',
      cardBackground: '#14202C',
      text: '#E0F0F8',
      extra: '#88B8DC',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Japanese Zen',
    tags: ['geographic', 'asian', 'minimal'],
    light: {
      primary: '#4A4A4A',
      primaryShadow: '#3B3B3B',
      accent: '#C8B896',
      accentShadow: '#A09378',
      background: '#FAF9F6',
      cardBackground: '#F5F4F0',
      text: '#2D2D2D',
      extra: '#8B8B8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0A0A0',
      primaryShadow: '#7A7A7A',
      accent: '#DDD0B8',
      accentShadow: '#C8B896',
      background: '#101010',
      cardBackground: '#1A1A1A',
      text: '#E8E8E5',
      extra: '#B5B5B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Moroccan Spice',
    tags: ['geographic', 'african', 'vibrant'],
    light: {
      primary: '#C35831',
      primaryShadow: '#9C4627',
      accent: '#E8B849',
      accentShadow: '#BA933A',
      background: '#FFFBF5',
      cardBackground: '#FFF5E8',
      text: '#4A2818',
      extra: '#D97040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D97040',
      primaryShadow: '#C35831',
      accent: '#F0C860',
      accentShadow: '#E8B849',
      background: '#140C08',
      cardBackground: '#281810',
      text: '#F5E8D8',
      extra: '#E88858',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Greek Island',
    tags: ['geographic', 'mediterranean', 'fresh'],
    light: {
      primary: '#0066CC',
      primaryShadow: '#0052A3',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#FFFFFF',
      cardBackground: '#F0F8FF',
      text: '#003366',
      extra: '#3399FF',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'text',
    },
    dark: {
      primary: '#3399FF',
      primaryShadow: '#0066CC',
      accent: '#F8F8F8',
      accentShadow: '#E8E8E8',
      background: '#001A33',
      cardBackground: '#002B55',
      text: '#E0F0FF',
      extra: '#66B3FF',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Indian Saffron',
    tags: ['geographic', 'asian', 'vibrant'],
    light: {
      primary: '#FF9933',
      primaryShadow: '#CC7A29',
      accent: '#128807',
      accentShadow: '#0E6C05',
      background: '#FFFFFF',
      cardBackground: '#FFFAF5',
      text: '#4A3520',
      extra: '#FFB366',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFB366',
      primaryShadow: '#FF9933',
      accent: '#2AAE1A',
      accentShadow: '#128807',
      background: '#141008',
      cardBackground: '#28200F',
      text: '#FFF5E0',
      extra: '#FFCC88',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Brazilian Carnival',
    tags: ['geographic', 'latin', 'vibrant'],
    light: {
      primary: '#009B3A',
      primaryShadow: '#007C2E',
      accent: '#FEDF00',
      accentShadow: '#CBB200',
      background: '#FFFFFF',
      cardBackground: '#F5FFF8',
      text: '#1A4028',
      extra: '#00CC4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#00CC4D',
      primaryShadow: '#009B3A',
      accent: '#FFE833',
      accentShadow: '#FEDF00',
      background: '#081408',
      cardBackground: '#102810',
      text: '#E0FFE8',
      extra: '#33E070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Australian Outback',
    tags: ['geographic', 'earthy', 'warm'],
    light: {
      primary: '#B7410E',
      primaryShadow: '#92340B',
      accent: '#E8A838',
      accentShadow: '#BA862D',
      background: '#FFFBF5',
      cardBackground: '#FFF5E8',
      text: '#4A2810',
      extra: '#D45828',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D45828',
      primaryShadow: '#B7410E',
      accent: '#F0B850',
      accentShadow: '#E8A838',
      background: '#140A05',
      cardBackground: '#28140A',
      text: '#F5E8D8',
      extra: '#E87040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ========================================
  // Monochromatic Themes
  // ========================================
  {
    name: 'Shades of Blue',
    tags: ['monochromatic', 'cool', 'professional'],
    light: {
      primary: '#1E3A5F',
      primaryShadow: '#182E4C',
      accent: '#3D6B99',
      accentShadow: '#31567A',
      background: '#FFFFFF',
      cardBackground: '#F0F5FA',
      text: '#1E3A5F',
      extra: '#5A9BD4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5A9BD4',
      primaryShadow: '#3D6B99',
      accent: '#7AB8E8',
      accentShadow: '#5A9BD4',
      background: '#0A1420',
      cardBackground: '#142030',
      text: '#E0EBF5',
      extra: '#9AC8F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shades of Green',
    tags: ['monochromatic', 'nature', 'calm'],
    light: {
      primary: '#1B4D3E',
      primaryShadow: '#163E32',
      accent: '#3A7D6E',
      accentShadow: '#2E6458',
      background: '#FFFFFF',
      cardBackground: '#F0FAF5',
      text: '#1B4D3E',
      extra: '#5AAF98',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5AAF98',
      primaryShadow: '#3A7D6E',
      accent: '#7AD0B8',
      accentShadow: '#5AAF98',
      background: '#0A1410',
      cardBackground: '#142820',
      text: '#E0F5ED',
      extra: '#9AE0C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shades of Purple',
    tags: ['monochromatic', 'elegant', 'luxurious'],
    light: {
      primary: '#4A1A6B',
      primaryShadow: '#3B1556',
      accent: '#7A3D9A',
      accentShadow: '#62317B',
      background: '#FFFFFF',
      cardBackground: '#F8F0FF',
      text: '#4A1A6B',
      extra: '#A05DC0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A05DC0',
      primaryShadow: '#7A3D9A',
      accent: '#C080E0',
      accentShadow: '#A05DC0',
      background: '#100818',
      cardBackground: '#1C1030',
      text: '#F0E0FF',
      extra: '#D0A0F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shades of Brown',
    tags: ['monochromatic', 'earthy', 'warm'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4A332C',
      accent: '#8D6E63',
      accentShadow: '#71584F',
      background: '#FFFFFF',
      cardBackground: '#FAF5F2',
      text: '#3E2723',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A1887F',
      primaryShadow: '#8D6E63',
      accent: '#BCAAA4',
      accentShadow: '#A1887F',
      background: '#12100E',
      cardBackground: '#25201C',
      text: '#EFEBE9',
      extra: '#D7CCC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shades of Teal',
    tags: ['monochromatic', 'cool', 'modern'],
    light: {
      primary: '#004D4D',
      primaryShadow: '#003D3D',
      accent: '#007A7A',
      accentShadow: '#006262',
      background: '#FFFFFF',
      cardBackground: '#F0FAFA',
      text: '#004D4D',
      extra: '#00A8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#00A8A8',
      primaryShadow: '#007A7A',
      accent: '#33CCCC',
      accentShadow: '#00A8A8',
      background: '#051414',
      cardBackground: '#0A2828',
      text: '#E0FFFF',
      extra: '#66E0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shades of Rose',
    tags: ['monochromatic', 'romantic', 'soft'],
    light: {
      primary: '#8B4A5A',
      primaryShadow: '#6F3B48',
      accent: '#B87A8A',
      accentShadow: '#93626E',
      background: '#FFFFFF',
      cardBackground: '#FFF5F7',
      text: '#5A3040',
      extra: '#D8A0B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D8A0B0',
      primaryShadow: '#B87A8A',
      accent: '#E8C0CC',
      accentShadow: '#D8A0B0',
      background: '#1A1015',
      cardBackground: '#2D1820',
      text: '#FFE8ED',
      extra: '#F0D0D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ========================================
  // Holiday Themes
  // ========================================
  {
    name: 'Christmas Classic',
    tags: ['holiday', 'festive', 'traditional'],
    light: {
      primary: '#165B33',
      primaryShadow: '#114829',
      accent: '#BB2528',
      accentShadow: '#961E20',
      background: '#FFFFFF',
      cardBackground: '#F5FAF5',
      text: '#1A3020',
      extra: '#F8B229',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2E8B57',
      primaryShadow: '#165B33',
      accent: '#DC3030',
      accentShadow: '#BB2528',
      background: '#0A140A',
      cardBackground: '#14281A',
      text: '#E8F5E8',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Halloween Spooky',
    tags: ['holiday', 'fun', 'autumn'],
    light: {
      primary: '#FF6600',
      primaryShadow: '#CC5200',
      accent: '#1A1A1A',
      accentShadow: '#000000',
      background: '#FFFFFF',
      cardBackground: '#FFF8F0',
      text: '#1A1A1A',
      extra: '#9B30FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8C33',
      primaryShadow: '#FF6600',
      accent: '#4A4A4A',
      accentShadow: '#2A2A2A',
      background: '#0A0808',
      cardBackground: '#151210',
      text: '#FFE8D0',
      extra: '#B34DFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Valentine Romance',
    tags: ['holiday', 'romantic', 'love'],
    light: {
      primary: '#FF1493',
      primaryShadow: '#CC1076',
      accent: '#FFB6C1',
      accentShadow: '#CC929A',
      background: '#FFFFFF',
      cardBackground: '#FFF5F8',
      text: '#4A1A28',
      extra: '#FF69B4',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FF47A6',
      primaryShadow: '#FF1493',
      accent: '#FFC8D4',
      accentShadow: '#FFB6C1',
      background: '#1A0A10',
      cardBackground: '#2D151E',
      text: '#FFE8F0',
      extra: '#FF88C8',
      h1Color: 'primary',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Easter Pastel',
    tags: ['holiday', 'spring', 'soft'],
    light: {
      primary: '#FFB7EB',
      primaryShadow: '#CC92BC',
      accent: '#BFFCC6',
      accentShadow: '#99CA9E',
      background: '#FFFFFF',
      cardBackground: '#FFFAF8',
      text: '#4A4050',
      extra: '#AEC6CF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFC8EF',
      primaryShadow: '#FFB7EB',
      accent: '#D0FFD8',
      accentShadow: '#BFFCC6',
      background: '#141218',
      cardBackground: '#221E28',
      text: '#F8F0F5',
      extra: '#C0D8E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Independence Day',
    tags: ['holiday', 'patriotic', 'bold'],
    light: {
      primary: '#002868',
      primaryShadow: '#002053',
      accent: '#BF0A30',
      accentShadow: '#990826',
      background: '#FFFFFF',
      cardBackground: '#F5F8FA',
      text: '#002868',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A78B8',
      primaryShadow: '#3060A0',
      accent: '#E83050',
      accentShadow: '#BF0A30',
      background: '#000814',
      cardBackground: '#001428',
      text: '#E8F0FF',
      extra: '#FFE033',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'St Patricks Day',
    tags: ['holiday', 'festive', 'irish'],
    light: {
      primary: '#009A49',
      primaryShadow: '#007B3A',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFFF',
      cardBackground: '#F0FFF5',
      text: '#1A4028',
      extra: '#00CC60',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#00CC60',
      primaryShadow: '#009A49',
      accent: '#FFE033',
      accentShadow: '#FFD700',
      background: '#081408',
      cardBackground: '#102810',
      text: '#E0FFE8',
      extra: '#33E080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'New Years Eve',
    tags: ['holiday', 'elegant', 'celebratory'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C0C0C0',
      primaryShadow: '#A0A0A0',
      accent: '#FFE033',
      accentShadow: '#FFD700',
      background: '#050505',
      cardBackground: '#0F0F0F',
      text: '#E8E8E8',
      extra: '#E0E0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Thanksgiving Harvest',
    tags: ['holiday', 'autumn', 'warm'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#FF8C00',
      accentShadow: '#CC7000',
      background: '#FFFBF0',
      cardBackground: '#FFF5E0',
      text: '#4A2810',
      extra: '#D2691E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06835',
      primaryShadow: '#8B4513',
      accent: '#FFA833',
      accentShadow: '#FF8C00',
      background: '#14100A',
      cardBackground: '#28201A',
      text: '#F5EDD8',
      extra: '#E8853A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Art Movement Themes
  {
    name: 'Impressionist Garden',
    tags: ['art', 'impressionism', 'soft', 'classic'],
    light: {
      primary: '#6B8E9F',
      primaryShadow: '#56717F',
      accent: '#E8B4A0',
      accentShadow: '#BA9080',
      background: '#FEFEFE',
      cardBackground: '#F8F5F0',
      text: '#3A4A52',
      extra: '#A8C8A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8AAFC0',
      primaryShadow: '#6B8E9F',
      accent: '#F0C8B8',
      accentShadow: '#E8B4A0',
      background: '#1A2025',
      cardBackground: '#2A3540',
      text: '#E8EFF2',
      extra: '#C0E0B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Art Nouveau',
    tags: ['art', 'nouveau', 'elegant', 'organic'],
    light: {
      primary: '#4A7043',
      primaryShadow: '#3B5936',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFEF5',
      cardBackground: '#F5F0E0',
      text: '#2C3B28',
      extra: '#8B6F47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6A9060',
      primaryShadow: '#4A7043',
      accent: '#E0B830',
      accentShadow: '#C9A227',
      background: '#151A13',
      cardBackground: '#252D22',
      text: '#E8F0E5',
      extra: '#A88A60',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bauhaus',
    tags: ['art', 'bauhaus', 'geometric', 'bold'],
    light: {
      primary: '#E53935',
      primaryShadow: '#B72C2A',
      accent: '#FFD600',
      accentShadow: '#CCAB00',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#1E88E5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#E53935',
      accent: '#FFEB3B',
      accentShadow: '#FFD600',
      background: '#121212',
      cardBackground: '#1E1E1E',
      text: '#FAFAFA',
      extra: '#42A5F5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Pop Art',
    tags: ['art', 'pop', 'bold', 'vibrant'],
    light: {
      primary: '#FF1493',
      primaryShadow: '#CC1076',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#FFFFFF',
      cardBackground: '#FFF8DC',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF69B4',
      primaryShadow: '#FF1493',
      accent: '#40E0D0',
      accentShadow: '#00CED1',
      background: '#0A0A15',
      cardBackground: '#1A1A2E',
      text: '#F8F8FF',
      extra: '#FFE44D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Minimalist',
    tags: ['art', 'minimalist', 'clean', 'simple'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#000000',
      accent: '#767676',
      accentShadow: '#5E5E5E',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#333333',
      extra: '#A0A0A0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FFFFFF',
      primaryShadow: '#E0E0E0',
      accent: '#A0A0A0',
      accentShadow: '#808080',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E8',
      extra: '#606060',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Art Deco',
    tags: ['art', 'deco', 'luxurious', 'geometric'],
    light: {
      primary: '#C9A227',
      primaryShadow: '#A1821F',
      accent: '#1A1A1A',
      accentShadow: '#000000',
      background: '#FFFEF8',
      cardBackground: '#F8F5E8',
      text: '#2C2C2C',
      extra: '#5C4033',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0B830',
      primaryShadow: '#C9A227',
      accent: '#F5F5F5',
      accentShadow: '#E0E0E0',
      background: '#0F0E08',
      cardBackground: '#1F1E15',
      text: '#F0EDE0',
      extra: '#8A7055',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Abstract Expressionism',
    tags: ['art', 'abstract', 'bold', 'expressive'],
    light: {
      primary: '#B22222',
      primaryShadow: '#8E1B1B',
      accent: '#4169E1',
      accentShadow: '#3454B4',
      background: '#FFFEF5',
      cardBackground: '#FFF8F0',
      text: '#2A2A2A',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DC3545',
      primaryShadow: '#B22222',
      accent: '#6495ED',
      accentShadow: '#4169E1',
      background: '#121008',
      cardBackground: '#221F18',
      text: '#F5F0E5',
      extra: '#32CD32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Surrealist Dream',
    tags: ['art', 'surrealism', 'dreamy', 'mystical'],
    light: {
      primary: '#6A5ACD',
      primaryShadow: '#5548A4',
      accent: '#FF6347',
      accentShadow: '#CC4F39',
      background: '#FFFEF8',
      cardBackground: '#F8F5FF',
      text: '#2A2840',
      extra: '#20B2AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8A7AED',
      primaryShadow: '#6A5ACD',
      accent: '#FF8066',
      accentShadow: '#FF6347',
      background: '#10101A',
      cardBackground: '#1A1A2E',
      text: '#E8E6F5',
      extra: '#40D0C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cubist',
    tags: ['art', 'cubism', 'geometric', 'angular'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#FDF5E6',
      cardBackground: '#F5EDD6',
      text: '#3A3020',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06835',
      primaryShadow: '#8B4513',
      accent: '#6CA0C8',
      accentShadow: '#4682B4',
      background: '#14120A',
      cardBackground: '#252218',
      text: '#F0E8D8',
      extra: '#E0A050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Renaissance',
    tags: ['art', 'renaissance', 'classic', 'elegant'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFEF5',
      cardBackground: '#F8F0E0',
      text: '#2A2218',
      extra: '#006400',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B83030',
      primaryShadow: '#8B0000',
      accent: '#E0B830',
      accentShadow: '#C9A227',
      background: '#120E08',
      cardBackground: '#221D15',
      text: '#F0EBE0',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Gradient-Inspired Themes
  {
    name: 'Sunset Gradient',
    tags: ['gradient', 'warm', 'vibrant'],
    light: {
      primary: '#FF512F',
      primaryShadow: '#CC4126',
      accent: '#F09819',
      accentShadow: '#C07A14',
      background: '#FFFFFF',
      cardBackground: '#FFF8F5',
      text: '#2A1A15',
      extra: '#DD2476',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6B4A',
      primaryShadow: '#FF512F',
      accent: '#FFB040',
      accentShadow: '#F09819',
      background: '#1A0F0A',
      cardBackground: '#2A1F1A',
      text: '#FFF5F0',
      extra: '#FF4090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ocean Gradient',
    tags: ['gradient', 'cool', 'calming'],
    light: {
      primary: '#2193B0',
      primaryShadow: '#1A768D',
      accent: '#6DD5ED',
      accentShadow: '#57AABE',
      background: '#FFFFFF',
      cardBackground: '#F5FEFF',
      text: '#152830',
      extra: '#00CED1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B0D0',
      primaryShadow: '#2193B0',
      accent: '#8AE0F0',
      accentShadow: '#6DD5ED',
      background: '#081518',
      cardBackground: '#102025',
      text: '#E5F8FA',
      extra: '#30E8EB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Purple Haze',
    tags: ['gradient', 'purple', 'mystical'],
    light: {
      primary: '#7B4397',
      primaryShadow: '#623579',
      accent: '#DC2430',
      accentShadow: '#B01D26',
      background: '#FFFFFF',
      cardBackground: '#FDF8FF',
      text: '#28182A',
      extra: '#A855F7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9B60B5',
      primaryShadow: '#7B4397',
      accent: '#F04048',
      accentShadow: '#DC2430',
      background: '#12081A',
      cardBackground: '#1E1028',
      text: '#F5EDF8',
      extra: '#C080FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mint Fresh',
    tags: ['gradient', 'green', 'fresh'],
    light: {
      primary: '#11998E',
      primaryShadow: '#0D7A72',
      accent: '#38EF7D',
      accentShadow: '#2DBF64',
      background: '#FFFFFF',
      cardBackground: '#F5FFFA',
      text: '#0A2820',
      extra: '#00D9A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#20B8AB',
      primaryShadow: '#11998E',
      accent: '#58FF95',
      accentShadow: '#38EF7D',
      background: '#081512',
      cardBackground: '#10221E',
      text: '#E5FFF5',
      extra: '#20F0B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cherry Blossom',
    tags: ['gradient', 'pink', 'soft'],
    light: {
      primary: '#EB3349',
      primaryShadow: '#BC293A',
      accent: '#F45C43',
      accentShadow: '#C34A36',
      background: '#FFFFFF',
      cardBackground: '#FFF5F6',
      text: '#2A1518',
      extra: '#FF8DA1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F05060',
      primaryShadow: '#EB3349',
      accent: '#FF7860',
      accentShadow: '#F45C43',
      background: '#1A0A0D',
      cardBackground: '#2A151A',
      text: '#FFF0F2',
      extra: '#FFA8B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Royal Blue',
    tags: ['gradient', 'blue', 'professional'],
    light: {
      primary: '#1A2980',
      primaryShadow: '#152166',
      accent: '#26D0CE',
      accentShadow: '#1EA6A5',
      background: '#FFFFFF',
      cardBackground: '#F5F8FF',
      text: '#101830',
      extra: '#4F46E5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3040A0',
      primaryShadow: '#1A2980',
      accent: '#40E8E6',
      accentShadow: '#26D0CE',
      background: '#080A18',
      cardBackground: '#101528',
      text: '#E8ECFF',
      extra: '#6B60F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Hour',
    tags: ['gradient', 'gold', 'warm'],
    light: {
      primary: '#F37335',
      primaryShadow: '#C25C2A',
      accent: '#FDC830',
      accentShadow: '#CAA026',
      background: '#FFFFFF',
      cardBackground: '#FFFBF5',
      text: '#2A1A10',
      extra: '#FF9F43',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8850',
      primaryShadow: '#F37335',
      accent: '#FFD848',
      accentShadow: '#FDC830',
      background: '#1A0F05',
      cardBackground: '#2A1F15',
      text: '#FFF8F0',
      extra: '#FFB060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Northern Lights',
    tags: ['gradient', 'aurora', 'magical'],
    light: {
      primary: '#43CEA2',
      primaryShadow: '#36A582',
      accent: '#185A9D',
      accentShadow: '#13487E',
      background: '#FFFFFF',
      cardBackground: '#F5FFFC',
      text: '#102820',
      extra: '#00C9FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#60E0B8',
      primaryShadow: '#43CEA2',
      accent: '#2878C0',
      accentShadow: '#185A9D',
      background: '#051510',
      cardBackground: '#0A221A',
      text: '#E8FFF8',
      extra: '#30E0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cosmic Fusion',
    tags: ['gradient', 'space', 'vibrant'],
    light: {
      primary: '#8E2DE2',
      primaryShadow: '#7224B5',
      accent: '#4A00E0',
      accentShadow: '#3B00B3',
      background: '#FFFFFF',
      cardBackground: '#FAF5FF',
      text: '#1A1030',
      extra: '#C84FEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A850F0',
      primaryShadow: '#8E2DE2',
      accent: '#6020F0',
      accentShadow: '#4A00E0',
      background: '#0A0515',
      cardBackground: '#150A28',
      text: '#F5EEFF',
      extra: '#E070FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Peach Melody',
    tags: ['gradient', 'peach', 'soft'],
    light: {
      primary: '#ED4264',
      primaryShadow: '#BE3550',
      accent: '#FFEDBC',
      accentShadow: '#CCBE96',
      background: '#FFFFFF',
      cardBackground: '#FFF8F5',
      text: '#2A1820',
      extra: '#F8A5B8',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06080',
      primaryShadow: '#ED4264',
      accent: '#FFF5D0',
      accentShadow: '#FFEDBC',
      background: '#1A0810',
      cardBackground: '#2A1520',
      text: '#FFF5F8',
      extra: '#FFC0D0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  // Technology Brand-Inspired Themes
  {
    name: 'Tech Blue',
    tags: ['tech', 'professional', 'modern'],
    light: {
      primary: '#0066CC',
      primaryShadow: '#0052A3',
      accent: '#00D4FF',
      accentShadow: '#00AACC',
      background: '#FFFFFF',
      cardBackground: '#F5F9FF',
      text: '#1A2A3A',
      extra: '#0088FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2080E0',
      primaryShadow: '#0066CC',
      accent: '#40E0FF',
      accentShadow: '#00D4FF',
      background: '#0A1018',
      cardBackground: '#101828',
      text: '#E8F0FF',
      extra: '#40A8FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Social Purple',
    tags: ['social', 'modern', 'vibrant'],
    light: {
      primary: '#833AB4',
      primaryShadow: '#692E90',
      accent: '#FD1D1D',
      accentShadow: '#CA1717',
      background: '#FFFFFF',
      cardBackground: '#FDF5FF',
      text: '#1A1025',
      extra: '#C13584',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A050D0',
      primaryShadow: '#833AB4',
      accent: '#FF4040',
      accentShadow: '#FD1D1D',
      background: '#100818',
      cardBackground: '#1A1028',
      text: '#F8F0FF',
      extra: '#E050A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Streaming Red',
    tags: ['entertainment', 'bold', 'dramatic'],
    light: {
      primary: '#E50914',
      primaryShadow: '#B70710',
      accent: '#141414',
      accentShadow: '#000000',
      background: '#FFFFFF',
      cardBackground: '#F8F5F5',
      text: '#1A1A1A',
      extra: '#B81D24',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F02020',
      primaryShadow: '#E50914',
      accent: '#E8E8E8',
      accentShadow: '#CCCCCC',
      background: '#0A0A0A',
      cardBackground: '#181818',
      text: '#FFFFFF',
      extra: '#D83030',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Music Green',
    tags: ['music', 'fresh', 'modern'],
    light: {
      primary: '#1DB954',
      primaryShadow: '#179443',
      accent: '#191414',
      accentShadow: '#000000',
      background: '#FFFFFF',
      cardBackground: '#F5FFF8',
      text: '#1A1A1A',
      extra: '#1ED760',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30D860',
      primaryShadow: '#1DB954',
      accent: '#B3B3B3',
      accentShadow: '#999999',
      background: '#0A0A0A',
      cardBackground: '#181818',
      text: '#FFFFFF',
      extra: '#40F080',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Creative Cloud',
    tags: ['creative', 'professional', 'modern'],
    light: {
      primary: '#FF0000',
      primaryShadow: '#CC0000',
      accent: '#FF9A00',
      accentShadow: '#CC7B00',
      background: '#FFFFFF',
      cardBackground: '#FFF8F5',
      text: '#1A1A1A',
      extra: '#EA4C89',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF3030',
      primaryShadow: '#FF0000',
      accent: '#FFB030',
      accentShadow: '#FF9A00',
      background: '#1A1A1A',
      cardBackground: '#2A2A2A',
      text: '#FAFAFA',
      extra: '#F06090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Developer Dark',
    tags: ['developer', 'dark', 'minimal'],
    light: {
      primary: '#24292E',
      primaryShadow: '#1B2025',
      accent: '#0366D6',
      accentShadow: '#0252AB',
      background: '#FFFFFF',
      cardBackground: '#F6F8FA',
      text: '#24292E',
      extra: '#6A737D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#C9D1D9',
      primaryShadow: '#B1BAC4',
      accent: '#58A6FF',
      accentShadow: '#388BFD',
      background: '#0D1117',
      cardBackground: '#161B22',
      text: '#C9D1D9',
      extra: '#8B949E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Search Engine',
    tags: ['search', 'clean', 'colorful'],
    light: {
      primary: '#4285F4',
      primaryShadow: '#356AC3',
      accent: '#EA4335',
      accentShadow: '#BB362A',
      background: '#FFFFFF',
      cardBackground: '#F8F9FA',
      text: '#202124',
      extra: '#34A853',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#8AB4F8',
      primaryShadow: '#4285F4',
      accent: '#F28B82',
      accentShadow: '#EA4335',
      background: '#202124',
      cardBackground: '#303134',
      text: '#E8EAED',
      extra: '#81C995',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Cloud Platform',
    tags: ['cloud', 'enterprise', 'modern'],
    light: {
      primary: '#FF9900',
      primaryShadow: '#CC7A00',
      accent: '#232F3E',
      accentShadow: '#1A242E',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#232F3E',
      extra: '#146EB4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#FFB030',
      primaryShadow: '#FF9900',
      accent: '#E8E8E8',
      accentShadow: '#CCCCCC',
      background: '#131A22',
      cardBackground: '#232F3E',
      text: '#E3E6E8',
      extra: '#2E8BD8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Enterprise Blue',
    tags: ['enterprise', 'professional', 'trustworthy'],
    light: {
      primary: '#0078D4',
      primaryShadow: '#0060AA',
      accent: '#50E6FF',
      accentShadow: '#40B8CC',
      background: '#FFFFFF',
      cardBackground: '#F3F2F1',
      text: '#201F1E',
      extra: '#8764B8',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2899F0',
      primaryShadow: '#0078D4',
      accent: '#70F0FF',
      accentShadow: '#50E6FF',
      background: '#11100F',
      cardBackground: '#1F1E1D',
      text: '#F3F2F1',
      extra: '#A080D0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Video Red',
    tags: ['video', 'bold', 'energetic'],
    light: {
      primary: '#FF0000',
      primaryShadow: '#CC0000',
      accent: '#282828',
      accentShadow: '#1A1A1A',
      background: '#FFFFFF',
      cardBackground: '#F9F9F9',
      text: '#030303',
      extra: '#606060',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FF3030',
      primaryShadow: '#FF0000',
      accent: '#AAAAAA',
      accentShadow: '#888888',
      background: '#0F0F0F',
      cardBackground: '#212121',
      text: '#FFFFFF',
      extra: '#909090',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  // Additional Color Palette Themes
  {
    name: 'Terracotta',
    tags: ['earthy', 'warm', 'natural'],
    light: {
      primary: '#E07A5F',
      primaryShadow: '#B3624C',
      accent: '#81B29A',
      accentShadow: '#678E7B',
      background: '#FFFBF5',
      cardBackground: '#F2E9E1',
      text: '#3D405B',
      extra: '#F2CC8F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E89A80',
      primaryShadow: '#E07A5F',
      accent: '#A0D0B8',
      accentShadow: '#81B29A',
      background: '#1A1818',
      cardBackground: '#2A2525',
      text: '#F0EDE5',
      extra: '#F5D8A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Blue',
    tags: ['professional', 'calm', 'modern'],
    light: {
      primary: '#5C6BC0',
      primaryShadow: '#4A569A',
      accent: '#26A69A',
      accentShadow: '#1E857B',
      background: '#FFFFFF',
      cardBackground: '#F5F7FA',
      text: '#263238',
      extra: '#7986CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7C8BE0',
      primaryShadow: '#5C6BC0',
      accent: '#40C0B0',
      accentShadow: '#26A69A',
      background: '#0D1015',
      cardBackground: '#1A2025',
      text: '#E8EBF0',
      extra: '#99A5E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Pink',
    tags: ['soft', 'romantic', 'feminine'],
    light: {
      primary: '#D4A5A5',
      primaryShadow: '#AA8484',
      accent: '#9CAFB7',
      accentShadow: '#7D8C92',
      background: '#FFFBFB',
      cardBackground: '#FFF0F0',
      text: '#4A3C3C',
      extra: '#E5C1C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0BABA',
      primaryShadow: '#D4A5A5',
      accent: '#B0C5CC',
      accentShadow: '#9CAFB7',
      background: '#1A1515',
      cardBackground: '#2A2222',
      text: '#F5EBEB',
      extra: '#F0D5D5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Forest Moss',
    tags: ['nature', 'earthy', 'calming'],
    light: {
      primary: '#4A5D23',
      primaryShadow: '#3B4A1C',
      accent: '#8B9A46',
      accentShadow: '#6F7B38',
      background: '#FCFDF5',
      cardBackground: '#F0F5E5',
      text: '#2A3210',
      extra: '#6B7D2D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6A7D40',
      primaryShadow: '#4A5D23',
      accent: '#A8B860',
      accentShadow: '#8B9A46',
      background: '#101408',
      cardBackground: '#1A2010',
      text: '#E8F0D8',
      extra: '#88A048',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Plum',
    tags: ['dark', 'luxurious', 'mysterious'],
    light: {
      primary: '#5B2C6F',
      primaryShadow: '#482359',
      accent: '#A569BD',
      accentShadow: '#845497',
      background: '#FFFBFF',
      cardBackground: '#F8F0FA',
      text: '#2C1338',
      extra: '#7D3C98',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7B4C90',
      primaryShadow: '#5B2C6F',
      accent: '#C088D0',
      accentShadow: '#A569BD',
      background: '#12081A',
      cardBackground: '#1E1028',
      text: '#F5E8FA',
      extra: '#9858B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coastal Breeze',
    tags: ['coastal', 'fresh', 'airy'],
    light: {
      primary: '#5DADE2',
      primaryShadow: '#4A8AB5',
      accent: '#F4D03F',
      accentShadow: '#C3A632',
      background: '#FFFFFF',
      cardBackground: '#F5FAFF',
      text: '#1A3040',
      extra: '#85C1E9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7DC0E8',
      primaryShadow: '#5DADE2',
      accent: '#F8E060',
      accentShadow: '#F4D03F',
      background: '#081520',
      cardBackground: '#102030',
      text: '#E8F5FF',
      extra: '#A0D0F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Burnt Sienna',
    tags: ['warm', 'rustic', 'bold'],
    light: {
      primary: '#CB4335',
      primaryShadow: '#A2362A',
      accent: '#D4AC0D',
      accentShadow: '#AA8A0A',
      background: '#FFFEFA',
      cardBackground: '#FAF0E8',
      text: '#2C1810',
      extra: '#E57E31',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E06050',
      primaryShadow: '#CB4335',
      accent: '#E8C020',
      accentShadow: '#D4AC0D',
      background: '#181008',
      cardBackground: '#281A10',
      text: '#FAF0E5',
      extra: '#F09048',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cool Gray',
    tags: ['neutral', 'professional', 'modern'],
    light: {
      primary: '#566573',
      primaryShadow: '#45515C',
      accent: '#2980B9',
      accentShadow: '#216694',
      background: '#FFFFFF',
      cardBackground: '#F4F6F7',
      text: '#2C3E50',
      extra: '#7F8C8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#788090',
      primaryShadow: '#566573',
      accent: '#45A0D8',
      accentShadow: '#2980B9',
      background: '#0D1015',
      cardBackground: '#1A2025',
      text: '#E8EBF0',
      extra: '#99A0A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Sage Green',
    tags: ['natural', 'calming', 'soft'],
    light: {
      primary: '#8FAE7F',
      primaryShadow: '#728B66',
      accent: '#C4A77D',
      accentShadow: '#9D8664',
      background: '#FAFDF8',
      cardBackground: '#F0F5EC',
      text: '#3A4230',
      extra: '#A8C490',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A8C495',
      primaryShadow: '#8FAE7F',
      accent: '#D8C098',
      accentShadow: '#C4A77D',
      background: '#101510',
      cardBackground: '#1A221A',
      text: '#E8F0E5',
      extra: '#C0D8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wine Red',
    tags: ['elegant', 'luxurious', 'warm'],
    light: {
      primary: '#722F37',
      primaryShadow: '#5B262C',
      accent: '#C9B037',
      accentShadow: '#A18D2C',
      background: '#FFFBF8',
      cardBackground: '#FAF0ED',
      text: '#2A1A1C',
      extra: '#943A44',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#944858',
      primaryShadow: '#722F37',
      accent: '#E0C850',
      accentShadow: '#C9B037',
      background: '#14080A',
      cardBackground: '#241518',
      text: '#F8F0ED',
      extra: '#B85060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // UI/UX Design System Themes
  {
    name: 'Material You',
    tags: ['material', 'modern', 'adaptive'],
    light: {
      primary: '#6750A4',
      primaryShadow: '#524083',
      accent: '#7D5260',
      accentShadow: '#64424D',
      background: '#FFFBFE',
      cardBackground: '#F3EDF7',
      text: '#1C1B1F',
      extra: '#B4A7D6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D0BCFF',
      primaryShadow: '#B4A0E0',
      accent: '#EFB8C8',
      accentShadow: '#D0A0B0',
      background: '#1C1B1F',
      cardBackground: '#2B2930',
      text: '#E6E1E5',
      extra: '#E8DEFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'iOS Light',
    tags: ['ios', 'clean', 'minimal'],
    light: {
      primary: '#007AFF',
      primaryShadow: '#0062CC',
      accent: '#34C759',
      accentShadow: '#2A9F47',
      background: '#FFFFFF',
      cardBackground: '#F2F2F7',
      text: '#000000',
      extra: '#5856D6',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#0A84FF',
      primaryShadow: '#007AFF',
      accent: '#30D158',
      accentShadow: '#28A745',
      background: '#000000',
      cardBackground: '#1C1C1E',
      text: '#FFFFFF',
      extra: '#5E5CE6',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Tailwind Slate',
    tags: ['tailwind', 'neutral', 'modern'],
    light: {
      primary: '#0F172A',
      primaryShadow: '#0C1322',
      accent: '#3B82F6',
      accentShadow: '#2F68C5',
      background: '#FFFFFF',
      cardBackground: '#F8FAFC',
      text: '#334155',
      extra: '#64748B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#F8FAFC',
      primaryShadow: '#E2E8F0',
      accent: '#60A5FA',
      accentShadow: '#3B82F6',
      background: '#0F172A',
      cardBackground: '#1E293B',
      text: '#CBD5E1',
      extra: '#94A3B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Bootstrap Primary',
    tags: ['bootstrap', 'classic', 'web'],
    light: {
      primary: '#0D6EFD',
      primaryShadow: '#0A58CA',
      accent: '#198754',
      accentShadow: '#146C43',
      background: '#FFFFFF',
      cardBackground: '#F8F9FA',
      text: '#212529',
      extra: '#6C757D',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#3D8BFD',
      primaryShadow: '#0D6EFD',
      accent: '#20C997',
      accentShadow: '#198754',
      background: '#212529',
      cardBackground: '#343A40',
      text: '#F8F9FA',
      extra: '#ADB5BD',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Chakra Teal',
    tags: ['chakra', 'fresh', 'modern'],
    light: {
      primary: '#319795',
      primaryShadow: '#277978',
      accent: '#DD6B20',
      accentShadow: '#B1561A',
      background: '#FFFFFF',
      cardBackground: '#F7FAFC',
      text: '#1A202C',
      extra: '#4FD1C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4FD1C5',
      primaryShadow: '#38B2AC',
      accent: '#F6AD55',
      accentShadow: '#ED8936',
      background: '#1A202C',
      cardBackground: '#2D3748',
      text: '#F7FAFC',
      extra: '#81E6D9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // More Geographic & Cultural Themes
  {
    name: 'Scandinavian',
    tags: ['nordic', 'minimal', 'clean'],
    light: {
      primary: '#2C3E50',
      primaryShadow: '#23323F',
      accent: '#E8E8E8',
      accentShadow: '#BABABA',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#95A5A6',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#ECF0F1',
      primaryShadow: '#BDC3C7',
      accent: '#3D5A6C',
      accentShadow: '#2C4251',
      background: '#1A1A1A',
      cardBackground: '#2C2C2C',
      text: '#F5F5F5',
      extra: '#7F8C8D',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mediterranean',
    tags: ['coastal', 'warm', 'vibrant'],
    light: {
      primary: '#1E8BC3',
      primaryShadow: '#186F9C',
      accent: '#F4D03F',
      accentShadow: '#C3A632',
      background: '#FFFEF8',
      cardBackground: '#FDF5E6',
      text: '#1A3050',
      extra: '#E67E22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40A8E0',
      primaryShadow: '#1E8BC3',
      accent: '#F8E060',
      accentShadow: '#F4D03F',
      background: '#0A1828',
      cardBackground: '#152535',
      text: '#F8F5E8',
      extra: '#F09838',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bohemian',
    tags: ['boho', 'eclectic', 'artistic'],
    light: {
      primary: '#B5651D',
      primaryShadow: '#915117',
      accent: '#7B2D8E',
      accentShadow: '#622472',
      background: '#FFFEF5',
      cardBackground: '#F5E6D3',
      text: '#3A2810',
      extra: '#DAA520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D88040',
      primaryShadow: '#B5651D',
      accent: '#A050B8',
      accentShadow: '#7B2D8E',
      background: '#1A1408',
      cardBackground: '#2A2015',
      text: '#F8F0E0',
      extra: '#F0C030',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Paradise',
    tags: ['tropical', 'vibrant', 'fun'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#4ECDC4',
      accentShadow: '#3EA49D',
      background: '#FFFFFF',
      cardBackground: '#FFF8F0',
      text: '#2A2A2A',
      extra: '#FFE66D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8A8A',
      primaryShadow: '#FF6B6B',
      accent: '#70E0D8',
      accentShadow: '#4ECDC4',
      background: '#1A1A1A',
      cardBackground: '#2A2525',
      text: '#F8F8F8',
      extra: '#FFF080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'African Sunset',
    tags: ['african', 'warm', 'bold'],
    light: {
      primary: '#C0392B',
      primaryShadow: '#9A2E23',
      accent: '#D35400',
      accentShadow: '#A94300',
      background: '#FFFBF0',
      cardBackground: '#FAE8D5',
      text: '#2C1810',
      extra: '#E67E22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E05545',
      primaryShadow: '#C0392B',
      accent: '#F07020',
      accentShadow: '#D35400',
      background: '#1A0D08',
      cardBackground: '#2A1A12',
      text: '#FAF0E0',
      extra: '#F09838',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cherry Blossom Japan',
    tags: ['japanese', 'delicate', 'spring'],
    light: {
      primary: '#FFB7C5',
      primaryShadow: '#CC929E',
      accent: '#8B4B62',
      accentShadow: '#6F3C4E',
      background: '#FFFAFA',
      cardBackground: '#FFF0F3',
      text: '#4A2A35',
      extra: '#FFC0CB',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FFD0DA',
      primaryShadow: '#FFB7C5',
      accent: '#B06880',
      accentShadow: '#8B4B62',
      background: '#1A1015',
      cardBackground: '#2A1820',
      text: '#FFF0F5',
      extra: '#FFD8E0',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Aztec Gold',
    tags: ['aztec', 'ancient', 'bold'],
    light: {
      primary: '#C09027',
      primaryShadow: '#9A731F',
      accent: '#2E4A3C',
      accentShadow: '#253B30',
      background: '#FFFDF5',
      cardBackground: '#F5ECD0',
      text: '#2A2010',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0B040',
      primaryShadow: '#C09027',
      accent: '#4A6A58',
      accentShadow: '#2E4A3C',
      background: '#141008',
      cardBackground: '#241D10',
      text: '#F8F0D0',
      extra: '#B06035',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Celtic Green',
    tags: ['celtic', 'irish', 'natural'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#C4A010',
      accentShadow: '#9D800D',
      background: '#FFFEF8',
      cardBackground: '#F0F8F0',
      text: '#1A2A1A',
      extra: '#2E8B57',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B840',
      primaryShadow: '#228B22',
      accent: '#E0C020',
      accentShadow: '#C4A010',
      background: '#081408',
      cardBackground: '#102210',
      text: '#E8F8E8',
      extra: '#50B878',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Industry-Specific Themes
  {
    name: 'Healthcare',
    tags: ['medical', 'clean', 'trustworthy'],
    light: {
      primary: '#0077B6',
      primaryShadow: '#005F92',
      accent: '#00B4D8',
      accentShadow: '#0090AD',
      background: '#FFFFFF',
      cardBackground: '#F0F8FF',
      text: '#023047',
      extra: '#48CAE4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2095D0',
      primaryShadow: '#0077B6',
      accent: '#40D0F0',
      accentShadow: '#00B4D8',
      background: '#011018',
      cardBackground: '#021C28',
      text: '#E8F4FF',
      extra: '#68E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Finance',
    tags: ['banking', 'professional', 'trustworthy'],
    light: {
      primary: '#003366',
      primaryShadow: '#002952',
      accent: '#006699',
      accentShadow: '#00527A',
      background: '#FFFFFF',
      cardBackground: '#F5F8FA',
      text: '#1A2A3A',
      extra: '#336699',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#2060A0',
      primaryShadow: '#003366',
      accent: '#2090C0',
      accentShadow: '#006699',
      background: '#0A1018',
      cardBackground: '#101C28',
      text: '#E8F0F8',
      extra: '#5080B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Legal',
    tags: ['law', 'professional', 'classic'],
    light: {
      primary: '#1C2841',
      primaryShadow: '#162034',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#FFFEF8',
      cardBackground: '#F5F0E5',
      text: '#2A2A2A',
      extra: '#4A5568',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#4A5A78',
      primaryShadow: '#1C2841',
      accent: '#B83030',
      accentShadow: '#8B0000',
      background: '#0A0C12',
      cardBackground: '#151A25',
      text: '#F0EDE5',
      extra: '#6A7588',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Education',
    tags: ['academic', 'friendly', 'approachable'],
    light: {
      primary: '#2B4570',
      primaryShadow: '#223759',
      accent: '#F39C12',
      accentShadow: '#C27D0E',
      background: '#FFFFFF',
      cardBackground: '#F8F9FA',
      text: '#1A2030',
      extra: '#3498DB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A6890',
      primaryShadow: '#2B4570',
      accent: '#F8B030',
      accentShadow: '#F39C12',
      background: '#0C1018',
      cardBackground: '#151D28',
      text: '#F0F5FA',
      extra: '#50B0F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Real Estate',
    tags: ['property', 'luxurious', 'professional'],
    light: {
      primary: '#2C3E50',
      primaryShadow: '#23323F',
      accent: '#27AE60',
      accentShadow: '#1F8B4D',
      background: '#FFFFFF',
      cardBackground: '#F8F9FA',
      text: '#1A1A1A',
      extra: '#E74C3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#4A6278',
      primaryShadow: '#2C3E50',
      accent: '#40D078',
      accentShadow: '#27AE60',
      background: '#0C1015',
      cardBackground: '#151C22',
      text: '#F8FAFA',
      extra: '#F06858',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Restaurant',
    tags: ['food', 'appetizing', 'warm'],
    light: {
      primary: '#A0522D',
      primaryShadow: '#804124',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFFBF5',
      cardBackground: '#FAF0E6',
      text: '#3A2010',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C07048',
      primaryShadow: '#A0522D',
      accent: '#40B840',
      accentShadow: '#228B22',
      background: '#140C08',
      cardBackground: '#241A10',
      text: '#FAF5E8',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fitness',
    tags: ['sports', 'energetic', 'bold'],
    light: {
      primary: '#E31837',
      primaryShadow: '#B6132C',
      accent: '#1A1A1A',
      accentShadow: '#000000',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F03050',
      primaryShadow: '#E31837',
      accent: '#E8E8E8',
      accentShadow: '#CCCCCC',
      background: '#0A0A0A',
      cardBackground: '#1A1A1A',
      text: '#FFFFFF',
      extra: '#FFE44D',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wedding',
    tags: ['romantic', 'elegant', 'soft'],
    light: {
      primary: '#D4AF37',
      primaryShadow: '#AA8C2C',
      accent: '#FAF0E6',
      accentShadow: '#C8C0B8',
      background: '#FFFEF8',
      cardBackground: '#FFF8F0',
      text: '#3A3020',
      extra: '#FFE4E1',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8C850',
      primaryShadow: '#D4AF37',
      accent: '#4A4540',
      accentShadow: '#3A3530',
      background: '#141008',
      cardBackground: '#241C10',
      text: '#FFF8F0',
      extra: '#503030',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  // More Mood & Emotion Themes
  {
    name: 'Calm Serenity',
    tags: ['calm', 'peaceful', 'relaxing'],
    light: {
      primary: '#6B9AC4',
      primaryShadow: '#567B9D',
      accent: '#97D8C4',
      accentShadow: '#79AD9D',
      background: '#FEFEFE',
      cardBackground: '#F5F8FA',
      text: '#2A3A4A',
      extra: '#C4E0F9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#88B8E0',
      primaryShadow: '#6B9AC4',
      accent: '#B0E8D8',
      accentShadow: '#97D8C4',
      background: '#0A1520',
      cardBackground: '#152030',
      text: '#E8F0F8',
      extra: '#D8F0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Joyful',
    tags: ['happy', 'cheerful', 'bright'],
    light: {
      primary: '#FFD93D',
      primaryShadow: '#CCAD31',
      accent: '#FF6B6B',
      accentShadow: '#CC5656',
      background: '#FFFEF5',
      cardBackground: '#FFF8E0',
      text: '#2A2A10',
      extra: '#6BCB77',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE060',
      primaryShadow: '#FFD93D',
      accent: '#FF8888',
      accentShadow: '#FF6B6B',
      background: '#1A1A08',
      cardBackground: '#2A2A15',
      text: '#FFF8E0',
      extra: '#88E090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Melancholic',
    tags: ['moody', 'emotional', 'deep'],
    light: {
      primary: '#5D5C61',
      primaryShadow: '#4A4A4E',
      accent: '#7395AE',
      accentShadow: '#5C778B',
      background: '#F7F7F7',
      cardBackground: '#EBEBEB',
      text: '#2A2A2A',
      extra: '#B1A296',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#7A7A80',
      primaryShadow: '#5D5C61',
      accent: '#90B0C8',
      accentShadow: '#7395AE',
      background: '#101012',
      cardBackground: '#1A1A1D',
      text: '#E0E0E0',
      extra: '#C8B8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Mysterious',
    tags: ['dark', 'enigmatic', 'dramatic'],
    light: {
      primary: '#2C2C54',
      primaryShadow: '#232343',
      accent: '#9E0031',
      accentShadow: '#7E0027',
      background: '#FFFEFE',
      cardBackground: '#F5F0F5',
      text: '#1A1A2A',
      extra: '#474787',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A4A78',
      primaryShadow: '#2C2C54',
      accent: '#C82050',
      accentShadow: '#9E0031',
      background: '#0A0A12',
      cardBackground: '#15151F',
      text: '#E8E8F0',
      extra: '#6A6AA8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Passionate',
    tags: ['intense', 'romantic', 'bold'],
    light: {
      primary: '#C41E3A',
      primaryShadow: '#9D182F',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFBF5',
      cardBackground: '#FFF0F0',
      text: '#2A1015',
      extra: '#E35D6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E04060',
      primaryShadow: '#C41E3A',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#1A0810',
      cardBackground: '#2A1018',
      text: '#FFF5F8',
      extra: '#F08088',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Zen',
    tags: ['peaceful', 'minimal', 'balanced'],
    light: {
      primary: '#4A5568',
      primaryShadow: '#3B4453',
      accent: '#718096',
      accentShadow: '#5A6678',
      background: '#FFFFFF',
      cardBackground: '#F7FAFC',
      text: '#2D3748',
      extra: '#A0AEC0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A0AEC0',
      primaryShadow: '#718096',
      accent: '#CBD5E0',
      accentShadow: '#A0AEC0',
      background: '#1A202C',
      cardBackground: '#2D3748',
      text: '#F7FAFC',
      extra: '#E2E8F0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  // Gaming & Entertainment Themes
  {
    name: 'Arcade',
    tags: ['gaming', 'retro', 'fun'],
    light: {
      primary: '#FF0080',
      primaryShadow: '#CC0066',
      accent: '#00FFFF',
      accentShadow: '#00CCCC',
      background: '#FFFFFF',
      cardBackground: '#FFF0F8',
      text: '#1A0020',
      extra: '#FFFF00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF40A0',
      primaryShadow: '#FF0080',
      accent: '#40FFFF',
      accentShadow: '#00FFFF',
      background: '#0A0015',
      cardBackground: '#1A0030',
      text: '#FFF0FF',
      extra: '#FFFF40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'RPG Fantasy',
    tags: ['gaming', 'fantasy', 'epic'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFEF5',
      cardBackground: '#F5EBD5',
      text: '#2A1A10',
      extra: '#5C4033',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06835',
      primaryShadow: '#8B4513',
      accent: '#E0B830',
      accentShadow: '#C9A227',
      background: '#12100A',
      cardBackground: '#221A12',
      text: '#F8F0D8',
      extra: '#7A5845',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sci-Fi Holo',
    tags: ['gaming', 'scifi', 'futuristic'],
    light: {
      primary: '#00E5FF',
      primaryShadow: '#00B8CC',
      accent: '#FF1744',
      accentShadow: '#CC1236',
      background: '#FFFFFF',
      cardBackground: '#F0FFFF',
      text: '#0A2030',
      extra: '#00B0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40F0FF',
      primaryShadow: '#00E5FF',
      accent: '#FF4060',
      accentShadow: '#FF1744',
      background: '#050A15',
      cardBackground: '#0A1525',
      text: '#E0F8FF',
      extra: '#40C8FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Horror',
    tags: ['gaming', 'dark', 'spooky'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#000000',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#4A0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#CCCCCC',
      primaryShadow: '#999999',
      accent: '#B83030',
      accentShadow: '#8B0000',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#DDDDDD',
      extra: '#701010',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Racing',
    tags: ['gaming', 'speed', 'dynamic'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#1E90FF',
      accentShadow: '#1873CC',
      background: '#FFFFFF',
      cardBackground: '#F8F5F0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#FF6030',
      primaryShadow: '#FF4500',
      accent: '#40A8FF',
      accentShadow: '#1E90FF',
      background: '#0A0A0A',
      cardBackground: '#1A1815',
      text: '#F8F8F8',
      extra: '#FFE44D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  // Animal & Wildlife Themes
  {
    name: 'Tiger Stripe',
    tags: ['animal', 'bold', 'wild'],
    light: {
      primary: '#FF6600',
      primaryShadow: '#CC5200',
      accent: '#1A1A1A',
      accentShadow: '#000000',
      background: '#FFFDF8',
      cardBackground: '#FFF5E0',
      text: '#2A1A00',
      extra: '#FFB347',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8030',
      primaryShadow: '#FF6600',
      accent: '#E0E0E0',
      accentShadow: '#C0C0C0',
      background: '#0A0800',
      cardBackground: '#1A1408',
      text: '#FFF5E0',
      extra: '#FFC860',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Peacock',
    tags: ['animal', 'colorful', 'elegant'],
    light: {
      primary: '#0B5345',
      primaryShadow: '#094337',
      accent: '#148F77',
      accentShadow: '#10725F',
      background: '#FEFEFE',
      cardBackground: '#F0FAF8',
      text: '#0A2A20',
      extra: '#1ABC9C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#208068',
      primaryShadow: '#0B5345',
      accent: '#30B098',
      accentShadow: '#148F77',
      background: '#050F0C',
      cardBackground: '#0A1A15',
      text: '#E0FAF5',
      extra: '#40D8B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Flamingo',
    tags: ['animal', 'tropical', 'playful'],
    light: {
      primary: '#FC8EAC',
      primaryShadow: '#CA728A',
      accent: '#FF6B8A',
      accentShadow: '#CC566E',
      background: '#FFFBFC',
      cardBackground: '#FFF0F5',
      text: '#4A2030',
      extra: '#FFB6C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFA8C0',
      primaryShadow: '#FC8EAC',
      accent: '#FF88A0',
      accentShadow: '#FF6B8A',
      background: '#1A0810',
      cardBackground: '#2A1520',
      text: '#FFF5F8',
      extra: '#FFD0D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Monarch Butterfly',
    tags: ['animal', 'nature', 'delicate'],
    light: {
      primary: '#FF8C00',
      primaryShadow: '#CC7000',
      accent: '#1A1A1A',
      accentShadow: '#000000',
      background: '#FFFEFA',
      cardBackground: '#FFF5E5',
      text: '#2A1800',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFA830',
      primaryShadow: '#FF8C00',
      accent: '#E0E0E0',
      accentShadow: '#C0C0C0',
      background: '#120A00',
      cardBackground: '#221505',
      text: '#FFF8E8',
      extra: '#FFE44D',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wolf Pack',
    tags: ['animal', 'wild', 'powerful'],
    light: {
      primary: '#4A4A4A',
      primaryShadow: '#3B3B3B',
      accent: '#708090',
      accentShadow: '#5A6A78',
      background: '#FAFAFA',
      cardBackground: '#F0F0F0',
      text: '#2A2A2A',
      extra: '#A0A0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#B0B0B0',
      primaryShadow: '#909090',
      accent: '#90A0B0',
      accentShadow: '#708090',
      background: '#0D0D0D',
      cardBackground: '#1A1A1A',
      text: '#E8E8E8',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  // Time Period Themes
  {
    name: 'Victorian Era',
    tags: ['historical', 'elegant', 'ornate'],
    light: {
      primary: '#4B0082',
      primaryShadow: '#3C0068',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFEF5',
      cardBackground: '#F5EDE5',
      text: '#2A2025',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7030A0',
      primaryShadow: '#4B0082',
      accent: '#E0B830',
      accentShadow: '#C9A227',
      background: '#100810',
      cardBackground: '#1A1018',
      text: '#F5F0E8',
      extra: '#B06835',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Roaring Twenties',
    tags: ['historical', 'glamorous', 'jazz'],
    light: {
      primary: '#C9A227',
      primaryShadow: '#A1821F',
      accent: '#1A1A1A',
      accentShadow: '#000000',
      background: '#FFFEF8',
      cardBackground: '#F8F2E0',
      text: '#2A2520',
      extra: '#B87333',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0C040',
      primaryShadow: '#C9A227',
      accent: '#F0F0F0',
      accentShadow: '#D0D0D0',
      background: '#0A0808',
      cardBackground: '#1A1510',
      text: '#F8F5E8',
      extra: '#D89050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mid Century Modern',
    tags: ['historical', 'retro', 'clean'],
    light: {
      primary: '#D2691E',
      primaryShadow: '#A85418',
      accent: '#2F4F4F',
      accentShadow: '#263F3F',
      background: '#FFFEF5',
      cardBackground: '#F5F0E5',
      text: '#2A2A20',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E08040',
      primaryShadow: '#D2691E',
      accent: '#4A6A6A',
      accentShadow: '#2F4F4F',
      background: '#121008',
      cardBackground: '#1E1A12',
      text: '#F5F0E0',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Disco Fever',
    tags: ['historical', 'funky', 'bold'],
    light: {
      primary: '#FF1493',
      primaryShadow: '#CC1076',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFFF',
      cardBackground: '#FFF8F5',
      text: '#1A1020',
      extra: '#FF4500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF50B0',
      primaryShadow: '#FF1493',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#100818',
      cardBackground: '#1A1028',
      text: '#FFF0F8',
      extra: '#FF6830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eighties Neon',
    tags: ['historical', 'bright', 'electric'],
    light: {
      primary: '#00FFFF',
      primaryShadow: '#00CCCC',
      accent: '#FF00FF',
      accentShadow: '#CC00CC',
      background: '#FFFFFF',
      cardBackground: '#F5FFFF',
      text: '#1A1A2A',
      extra: '#FFFF00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40FFFF',
      primaryShadow: '#00FFFF',
      accent: '#FF40FF',
      accentShadow: '#FF00FF',
      background: '#0A0A15',
      cardBackground: '#15152A',
      text: '#F5F5FF',
      extra: '#FFFF40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Grunge Nineties',
    tags: ['historical', 'edgy', 'raw'],
    light: {
      primary: '#4A4A4A',
      primaryShadow: '#3B3B3B',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#2A2A2A',
      extra: '#556B2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#909090',
      primaryShadow: '#707070',
      accent: '#B83030',
      accentShadow: '#8B0000',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E0E0E0',
      extra: '#6B8B4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Y2K',
    tags: ['historical', 'futuristic', 'shiny'],
    light: {
      primary: '#C0C0C0',
      primaryShadow: '#9A9A9A',
      accent: '#0080FF',
      accentShadow: '#0066CC',
      background: '#FFFFFF',
      cardBackground: '#F5F8FF',
      text: '#1A1A1A',
      extra: '#FF6699',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#C0C0C0',
      accent: '#40A0FF',
      accentShadow: '#0080FF',
      background: '#0A0A10',
      cardBackground: '#15151F',
      text: '#F5F5F5',
      extra: '#FF88B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Medieval',
    tags: ['historical', 'rustic', 'ancient'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#B8860B',
      accentShadow: '#937005',
      background: '#FFFEF5',
      cardBackground: '#F5E8D0',
      text: '#2A2010',
      extra: '#556B2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06835',
      primaryShadow: '#8B4513',
      accent: '#D0A020',
      accentShadow: '#B8860B',
      background: '#100C05',
      cardBackground: '#1A1408',
      text: '#F0E8D0',
      extra: '#708B50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Texture-Inspired Themes
  {
    name: 'Marble',
    tags: ['texture', 'elegant', 'luxurious'],
    light: {
      primary: '#2F2F2F',
      primaryShadow: '#262626',
      accent: '#C0C0C0',
      accentShadow: '#9A9A9A',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#2A2A2A',
      extra: '#A8A8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#E8E8E8',
      primaryShadow: '#D0D0D0',
      accent: '#808080',
      accentShadow: '#606060',
      background: '#1A1A1A',
      cardBackground: '#252525',
      text: '#F5F5F5',
      extra: '#B0B0B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Wood Grain',
    tags: ['texture', 'natural', 'warm'],
    light: {
      primary: '#8B5A2B',
      primaryShadow: '#6F4822',
      accent: '#D2B48C',
      accentShadow: '#A89070',
      background: '#FFFEF8',
      cardBackground: '#F5EBD5',
      text: '#3A2A18',
      extra: '#A0522D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B07848',
      primaryShadow: '#8B5A2B',
      accent: '#E8D0A8',
      accentShadow: '#D2B48C',
      background: '#12100A',
      cardBackground: '#1E1810',
      text: '#F5EBD5',
      extra: '#C07048',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Concrete',
    tags: ['texture', 'industrial', 'urban'],
    light: {
      primary: '#696969',
      primaryShadow: '#545454',
      accent: '#A9A9A9',
      accentShadow: '#888888',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#2A2A2A',
      extra: '#808080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#B0B0B0',
      primaryShadow: '#909090',
      accent: '#787878',
      accentShadow: '#606060',
      background: '#1A1A1A',
      cardBackground: '#252525',
      text: '#E8E8E8',
      extra: '#909090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Leather',
    tags: ['texture', 'classic', 'rich'],
    light: {
      primary: '#5C4033',
      primaryShadow: '#4A3329',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFDF8',
      cardBackground: '#F5EDE0',
      text: '#2A2018',
      extra: '#8B6914',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7A5848',
      primaryShadow: '#5C4033',
      accent: '#E0B830',
      accentShadow: '#C9A227',
      background: '#100A08',
      cardBackground: '#1A1410',
      text: '#F5EDE0',
      extra: '#B08820',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet',
    tags: ['texture', 'luxurious', 'soft'],
    light: {
      primary: '#722F37',
      primaryShadow: '#5B262C',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFBF8',
      cardBackground: '#F8F0ED',
      text: '#2A1A1C',
      extra: '#8B3A62',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#944858',
      primaryShadow: '#722F37',
      accent: '#E0C040',
      accentShadow: '#C9A227',
      background: '#12080A',
      cardBackground: '#1E1015',
      text: '#F8F0ED',
      extra: '#B05080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen',
    tags: ['texture', 'natural', 'soft'],
    light: {
      primary: '#8B8B7A',
      primaryShadow: '#6F6F62',
      accent: '#C4B89A',
      accentShadow: '#9D937B',
      background: '#FAF8F5',
      cardBackground: '#F0EDE5',
      text: '#3A3A30',
      extra: '#A8A090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#A8A898',
      primaryShadow: '#8B8B7A',
      accent: '#D8D0B8',
      accentShadow: '#C4B89A',
      background: '#12120F',
      cardBackground: '#1A1A15',
      text: '#F0EDE5',
      extra: '#C0B8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Glass',
    tags: ['texture', 'modern', 'transparent'],
    light: {
      primary: '#87CEEB',
      primaryShadow: '#6CA5BC',
      accent: '#B0E0E6',
      accentShadow: '#8DB4B8',
      background: '#FFFFFF',
      cardBackground: '#F5FAFA',
      text: '#2A3A40',
      extra: '#ADD8E6',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#A8E0F0',
      primaryShadow: '#87CEEB',
      accent: '#C8F0F8',
      accentShadow: '#B0E0E6',
      background: '#0A1518',
      cardBackground: '#152025',
      text: '#E8F5F8',
      extra: '#C8E8F0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Denim',
    tags: ['texture', 'casual', 'classic'],
    light: {
      primary: '#4169E1',
      primaryShadow: '#3454B4',
      accent: '#B0C4DE',
      accentShadow: '#8D9DB2',
      background: '#FFFFFF',
      cardBackground: '#F5F8FF',
      text: '#1A2050',
      extra: '#6495ED',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6088F0',
      primaryShadow: '#4169E1',
      accent: '#C8D8F0',
      accentShadow: '#B0C4DE',
      background: '#080A18',
      cardBackground: '#101525',
      text: '#E8ECFF',
      extra: '#88B0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Sports Themes
  {
    name: 'Soccer Field',
    tags: ['sports', 'fresh', 'competitive'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#FCFDF8',
      cardBackground: '#F0F8F0',
      text: '#1A2A1A',
      extra: '#1A1A1A',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B840',
      primaryShadow: '#228B22',
      accent: '#F0F0F0',
      accentShadow: '#D0D0D0',
      background: '#081408',
      cardBackground: '#102010',
      text: '#E8F8E8',
      extra: '#E0E0E0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Basketball Court',
    tags: ['sports', 'energetic', 'bold'],
    light: {
      primary: '#FF6600',
      primaryShadow: '#CC5200',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFFDF8',
      cardBackground: '#F8F0E5',
      text: '#2A1A10',
      extra: '#D2691E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8030',
      primaryShadow: '#FF6600',
      accent: '#B06835',
      accentShadow: '#8B4513',
      background: '#120A05',
      cardBackground: '#1E1408',
      text: '#F8F0E0',
      extra: '#E88040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tennis Ball',
    tags: ['sports', 'bright', 'active'],
    light: {
      primary: '#CCFF00',
      primaryShadow: '#A3CC00',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#FEFFF5',
      cardBackground: '#F8FFE8',
      text: '#2A3010',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0FF40',
      primaryShadow: '#CCFF00',
      accent: '#F0F0F0',
      accentShadow: '#D0D0D0',
      background: '#101508',
      cardBackground: '#182010',
      text: '#F5FFE8',
      extra: '#40B840',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golf Course',
    tags: ['sports', 'elegant', 'nature'],
    light: {
      primary: '#2E8B57',
      primaryShadow: '#256F46',
      accent: '#F5F5DC',
      accentShadow: '#C4C4B0',
      background: '#FDFDF8',
      cardBackground: '#F0F5E8',
      text: '#1A2A20',
      extra: '#6B8E23',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50B080',
      primaryShadow: '#2E8B57',
      accent: '#E8E8D0',
      accentShadow: '#D0D0B8',
      background: '#081510',
      cardBackground: '#102018',
      text: '#E8F5E8',
      extra: '#90B840',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Swimming Pool',
    tags: ['sports', 'refreshing', 'cool'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#F5FFFF',
      cardBackground: '#E8FAFA',
      text: '#1A3A40',
      extra: '#40E0D0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8EB',
      primaryShadow: '#00CED1',
      accent: '#F0F0F0',
      accentShadow: '#D0D0D0',
      background: '#081518',
      cardBackground: '#102025',
      text: '#E8FFFF',
      extra: '#60F0E8',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Boxing Ring',
    tags: ['sports', 'intense', 'powerful'],
    light: {
      primary: '#B22222',
      primaryShadow: '#8E1B1B',
      accent: '#1A1A1A',
      accentShadow: '#000000',
      background: '#FFFAFA',
      cardBackground: '#F8F0F0',
      text: '#2A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DC3545',
      primaryShadow: '#B22222',
      accent: '#E0E0E0',
      accentShadow: '#C0C0C0',
      background: '#120808',
      cardBackground: '#1E1010',
      text: '#F8F0F0',
      extra: '#FFE44D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ski Slope',
    tags: ['sports', 'winter', 'fresh'],
    light: {
      primary: '#4682B4',
      primaryShadow: '#386890',
      accent: '#FFFFFF',
      accentShadow: '#E8E8E8',
      background: '#FAFEFF',
      cardBackground: '#F0F8FF',
      text: '#1A2A3A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6CA0D0',
      primaryShadow: '#4682B4',
      accent: '#F8F8F8',
      accentShadow: '#E0E0E0',
      background: '#080F18',
      cardBackground: '#101A28',
      text: '#E8F5FF',
      extra: '#A8E0F8',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Track Field',
    tags: ['sports', 'dynamic', 'athletic'],
    light: {
      primary: '#CD5C5C',
      primaryShadow: '#A44A4A',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#FFFAFA',
      cardBackground: '#F8F0F0',
      text: '#2A1A1A',
      extra: '#B22222',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E07878',
      primaryShadow: '#CD5C5C',
      accent: '#F0F0F0',
      accentShadow: '#D0D0D0',
      background: '#120808',
      cardBackground: '#1E1212',
      text: '#FFF0F0',
      extra: '#DC4040',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  // Profession Themes
  {
    name: 'Doctor',
    tags: ['profession', 'clean', 'trustworthy'],
    light: {
      primary: '#00ACC1',
      primaryShadow: '#008A9A',
      accent: '#E8F5E9',
      accentShadow: '#B9C9BA',
      background: '#FFFFFF',
      cardBackground: '#F0FAFA',
      text: '#1A3040',
      extra: '#4DB6AC',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40C8D8',
      primaryShadow: '#00ACC1',
      accent: '#2E4030',
      accentShadow: '#1E3020',
      background: '#081520',
      cardBackground: '#102028',
      text: '#E8FAFA',
      extra: '#70D8D0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Architect',
    tags: ['profession', 'precise', 'modern'],
    light: {
      primary: '#37474F',
      primaryShadow: '#2C393F',
      accent: '#FF7043',
      accentShadow: '#CC5A36',
      background: '#FFFFFF',
      cardBackground: '#ECEFF1',
      text: '#263238',
      extra: '#607D8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#78909C',
      primaryShadow: '#546E7A',
      accent: '#FF8A65',
      accentShadow: '#FF7043',
      background: '#0D1215',
      cardBackground: '#1A2025',
      text: '#ECEFF1',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Chef',
    tags: ['profession', 'culinary', 'warm'],
    light: {
      primary: '#D84315',
      primaryShadow: '#AD3611',
      accent: '#FFF8E1',
      accentShadow: '#CCC6B4',
      background: '#FFFBF5',
      cardBackground: '#FFF3E0',
      text: '#3E2723',
      extra: '#FF8A65',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06040',
      primaryShadow: '#D84315',
      accent: '#4A4030',
      accentShadow: '#3A3020',
      background: '#1A0D08',
      cardBackground: '#281810',
      text: '#FFF3E0',
      extra: '#FFA080',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artist',
    tags: ['profession', 'creative', 'colorful'],
    light: {
      primary: '#9C27B0',
      primaryShadow: '#7D1F8D',
      accent: '#FFD54F',
      accentShadow: '#CCAA3F',
      background: '#FFFFFF',
      cardBackground: '#FCF5FF',
      text: '#1A102A',
      extra: '#E040FB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B850D0',
      primaryShadow: '#9C27B0',
      accent: '#FFE070',
      accentShadow: '#FFD54F',
      background: '#10081A',
      cardBackground: '#1A1028',
      text: '#F8F0FF',
      extra: '#F060FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pilot',
    tags: ['profession', 'aviation', 'professional'],
    light: {
      primary: '#1565C0',
      primaryShadow: '#11519A',
      accent: '#FFC107',
      accentShadow: '#CC9A05',
      background: '#F5FAFF',
      cardBackground: '#E3F2FD',
      text: '#0D2137',
      extra: '#42A5F5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4088E0',
      primaryShadow: '#1565C0',
      accent: '#FFD030',
      accentShadow: '#FFC107',
      background: '#050D18',
      cardBackground: '#0A1828',
      text: '#E3F2FD',
      extra: '#60B8FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scientist',
    tags: ['profession', 'analytical', 'precise'],
    light: {
      primary: '#00897B',
      primaryShadow: '#006D63',
      accent: '#B2EBF2',
      accentShadow: '#8EBCC2',
      background: '#FFFFFF',
      cardBackground: '#E0F7FA',
      text: '#004D40',
      extra: '#26A69A',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30B0A0',
      primaryShadow: '#00897B',
      accent: '#C8F8FF',
      accentShadow: '#B2EBF2',
      background: '#051510',
      cardBackground: '#0A2520',
      text: '#E0F7FA',
      extra: '#50D0C0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Musician',
    tags: ['profession', 'artistic', 'expressive'],
    light: {
      primary: '#6A1B9A',
      primaryShadow: '#55167B',
      accent: '#EA80FC',
      accentShadow: '#BB66CA',
      background: '#FFFBFF',
      cardBackground: '#F3E5F5',
      text: '#1A0828',
      extra: '#AB47BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9040B8',
      primaryShadow: '#6A1B9A',
      accent: '#F0A0FF',
      accentShadow: '#EA80FC',
      background: '#100818',
      cardBackground: '#1A1028',
      text: '#F8F0FF',
      extra: '#C070D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Photographer',
    tags: ['profession', 'creative', 'visual'],
    light: {
      primary: '#37474F',
      primaryShadow: '#2C393F',
      accent: '#F5F5F5',
      accentShadow: '#C4C4C4',
      background: '#FFFFFF',
      cardBackground: '#ECEFF1',
      text: '#263238',
      extra: '#78909C',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#CFD8DC',
      primaryShadow: '#B0BEC5',
      accent: '#37474F',
      accentShadow: '#263238',
      background: '#0D1215',
      cardBackground: '#1A2025',
      text: '#ECEFF1',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  // Flower & Botanical Themes
  {
    name: 'Rose Garden',
    tags: ['floral', 'romantic', 'elegant'],
    light: {
      primary: '#C71585',
      primaryShadow: '#9F116A',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFFBFC',
      cardBackground: '#FFF0F5',
      text: '#3A1A25',
      extra: '#FF69B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E040A0',
      primaryShadow: '#C71585',
      accent: '#40B840',
      accentShadow: '#228B22',
      background: '#1A0810',
      cardBackground: '#2A1520',
      text: '#FFF0F5',
      extra: '#FF88C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Fields',
    tags: ['floral', 'calming', 'aromatic'],
    light: {
      primary: '#9370DB',
      primaryShadow: '#765AB0',
      accent: '#90EE90',
      accentShadow: '#73BE73',
      background: '#FEFBFF',
      cardBackground: '#F5F0FF',
      text: '#2A203A',
      extra: '#B39DDB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B090F0',
      primaryShadow: '#9370DB',
      accent: '#A8FFB0',
      accentShadow: '#90EE90',
      background: '#100815',
      cardBackground: '#1A1025',
      text: '#F5F0FF',
      extra: '#D0B8F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunflower',
    tags: ['floral', 'cheerful', 'bright'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFFEF5',
      cardBackground: '#FFF8E0',
      text: '#2A2500',
      extra: '#FFA500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE44D',
      primaryShadow: '#FFD700',
      accent: '#B06835',
      accentShadow: '#8B4513',
      background: '#14120A',
      cardBackground: '#201C10',
      text: '#FFF8E0',
      extra: '#FFB830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid',
    tags: ['floral', 'exotic', 'elegant'],
    light: {
      primary: '#DA70D6',
      primaryShadow: '#AE5AAB',
      accent: '#2E8B57',
      accentShadow: '#256F46',
      background: '#FFFBFE',
      cardBackground: '#FAF0FA',
      text: '#3A1A3A',
      extra: '#EE82EE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E890E0',
      primaryShadow: '#DA70D6',
      accent: '#50B080',
      accentShadow: '#2E8B57',
      background: '#15081A',
      cardBackground: '#201028',
      text: '#FAF0FA',
      extra: '#F8A8F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tulip Garden',
    tags: ['floral', 'spring', 'vibrant'],
    light: {
      primary: '#FF6347',
      primaryShadow: '#CC4F39',
      accent: '#9ACD32',
      accentShadow: '#7BA428',
      background: '#FFFEFA',
      cardBackground: '#FFF8F5',
      text: '#2A1A15',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8060',
      primaryShadow: '#FF6347',
      accent: '#B0E050',
      accentShadow: '#9ACD32',
      background: '#1A0D0A',
      cardBackground: '#281A15',
      text: '#FFF8F5',
      extra: '#FFE44D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Succulent',
    tags: ['botanical', 'modern', 'calm'],
    light: {
      primary: '#669966',
      primaryShadow: '#527A52',
      accent: '#98D8C8',
      accentShadow: '#7AADA0',
      background: '#FAFDF8',
      cardBackground: '#F0F8F0',
      text: '#2A3A2A',
      extra: '#8FBC8F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#88B888',
      primaryShadow: '#669966',
      accent: '#B0F0E0',
      accentShadow: '#98D8C8',
      background: '#081208',
      cardBackground: '#101E10',
      text: '#E8F8E8',
      extra: '#A8D8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fern',
    tags: ['botanical', 'natural', 'fresh'],
    light: {
      primary: '#4A7023',
      primaryShadow: '#3B591C',
      accent: '#8DB600',
      accentShadow: '#719200',
      background: '#FAFDF5',
      cardBackground: '#F0F5E5',
      text: '#2A3518',
      extra: '#6B8E23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6A9040',
      primaryShadow: '#4A7023',
      accent: '#A8D820',
      accentShadow: '#8DB600',
      background: '#0A1208',
      cardBackground: '#121E10',
      text: '#E8F5E0',
      extra: '#88B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cactus',
    tags: ['botanical', 'desert', 'minimal'],
    light: {
      primary: '#6B8E23',
      primaryShadow: '#56721C',
      accent: '#DAA520',
      accentShadow: '#AE841A',
      background: '#FEFDF5',
      cardBackground: '#F8F5E8',
      text: '#2A2810',
      extra: '#9ACD32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#88B040',
      primaryShadow: '#6B8E23',
      accent: '#F0C040',
      accentShadow: '#DAA520',
      background: '#0C1008',
      cardBackground: '#151A10',
      text: '#F5F0D8',
      extra: '#B0E050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Music Genre Themes
  {
    name: 'Jazz Club',
    tags: ['music', 'sophisticated', 'warm'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFDF5',
      cardBackground: '#F5ECD5',
      text: '#2A1A10',
      extra: '#1A1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06835',
      primaryShadow: '#8B4513',
      accent: '#E0B830',
      accentShadow: '#C9A227',
      background: '#0A0805',
      cardBackground: '#15100A',
      text: '#F5ECD5',
      extra: '#888888',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rock Concert',
    tags: ['music', 'edgy', 'bold'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#000000',
      accent: '#DC143C',
      accentShadow: '#B0102F',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#E8E8E8',
      primaryShadow: '#C0C0C0',
      accent: '#FF3050',
      accentShadow: '#DC143C',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#707070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Classical',
    tags: ['music', 'elegant', 'timeless'],
    light: {
      primary: '#2C3E50',
      primaryShadow: '#23323F',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFEF8',
      cardBackground: '#F8F5F0',
      text: '#2A2A25',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A6278',
      primaryShadow: '#2C3E50',
      accent: '#E0B830',
      accentShadow: '#C9A227',
      background: '#0A0C10',
      cardBackground: '#151A20',
      text: '#F5F5F0',
      extra: '#B83030',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Electronic',
    tags: ['music', 'futuristic', 'vibrant'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#FF00FF',
      accentShadow: '#CC00CC',
      background: '#FFFFFF',
      cardBackground: '#F5FFFF',
      text: '#1A2A30',
      extra: '#00FF00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8EB',
      primaryShadow: '#00CED1',
      accent: '#FF40FF',
      accentShadow: '#FF00FF',
      background: '#05101A',
      cardBackground: '#0A1828',
      text: '#E8FFFF',
      extra: '#40FF40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Country',
    tags: ['music', 'rustic', 'warm'],
    light: {
      primary: '#D2691E',
      primaryShadow: '#A85418',
      accent: '#DAA520',
      accentShadow: '#AE841A',
      background: '#FFFDF5',
      cardBackground: '#F5ECD8',
      text: '#3A2810',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E08040',
      primaryShadow: '#D2691E',
      accent: '#F0C030',
      accentShadow: '#DAA520',
      background: '#120C05',
      cardBackground: '#1E1408',
      text: '#F5ECD8',
      extra: '#B06835',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hip Hop',
    tags: ['music', 'urban', 'bold'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#000000',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#FF4500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#C0C0C0',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#FF6830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Reggae',
    tags: ['music', 'relaxed', 'caribbean'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFEF5',
      cardBackground: '#F8F5E0',
      text: '#1A2810',
      extra: '#FF0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B840',
      primaryShadow: '#228B22',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#081208',
      cardBackground: '#101E10',
      text: '#F5F8E0',
      extra: '#FF4040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blues',
    tags: ['music', 'soulful', 'deep'],
    light: {
      primary: '#191970',
      primaryShadow: '#141459',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#FAFAFA',
      cardBackground: '#F0F5FA',
      text: '#1A1A30',
      extra: '#6495ED',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3030A0',
      primaryShadow: '#191970',
      accent: '#6CA0D0',
      accentShadow: '#4682B4',
      background: '#080810',
      cardBackground: '#101020',
      text: '#E8E8F8',
      extra: '#88B8FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Beverage & Drink Themes
  {
    name: 'Coffee Shop',
    tags: ['beverage', 'warm', 'cozy'],
    light: {
      primary: '#4A3728',
      primaryShadow: '#3B2C20',
      accent: '#D4A574',
      accentShadow: '#AA845D',
      background: '#FFFDF8',
      cardBackground: '#F5EDE0',
      text: '#2A2015',
      extra: '#8B7355',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6A5545',
      primaryShadow: '#4A3728',
      accent: '#E8C090',
      accentShadow: '#D4A574',
      background: '#0A0805',
      cardBackground: '#15100A',
      text: '#F5EDE0',
      extra: '#A89070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Green Tea',
    tags: ['beverage', 'calm', 'zen'],
    light: {
      primary: '#7BA05B',
      primaryShadow: '#628049',
      accent: '#D4E6B5',
      accentShadow: '#AAB891',
      background: '#FAFDF5',
      cardBackground: '#F0F5E5',
      text: '#2A3520',
      extra: '#98BF64',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#98C078',
      primaryShadow: '#7BA05B',
      accent: '#E8F5C8',
      accentShadow: '#D4E6B5',
      background: '#0A1008',
      cardBackground: '#121E10',
      text: '#E8F5E0',
      extra: '#B0D880',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Red Wine',
    tags: ['beverage', 'elegant', 'rich'],
    light: {
      primary: '#722F37',
      primaryShadow: '#5B262C',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFBF8',
      cardBackground: '#FAF0ED',
      text: '#2A1A1C',
      extra: '#943A44',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#944858',
      primaryShadow: '#722F37',
      accent: '#E0C040',
      accentShadow: '#C9A227',
      background: '#12080A',
      cardBackground: '#1E1012',
      text: '#F8F0ED',
      extra: '#B85868',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Champagne',
    tags: ['beverage', 'luxurious', 'celebration'],
    light: {
      primary: '#F7E7CE',
      primaryShadow: '#C5B9A5',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFEF8',
      cardBackground: '#FAF5E8',
      text: '#3A3520',
      extra: '#E8D5B0',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#F5E8D0',
      primaryShadow: '#D4C8A8',
      accent: '#E0B830',
      accentShadow: '#C9A227',
      background: '#14120A',
      cardBackground: '#201C12',
      text: '#FAF5E0',
      extra: '#F0E0C0',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Orange Juice',
    tags: ['beverage', 'fresh', 'energizing'],
    light: {
      primary: '#FF8C00',
      primaryShadow: '#CC7000',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFEF5',
      cardBackground: '#FFF8E5',
      text: '#2A2000',
      extra: '#FFA500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFA830',
      primaryShadow: '#FF8C00',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#14100A',
      cardBackground: '#201A10',
      text: '#FFF8E0',
      extra: '#FFB840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Berry Smoothie',
    tags: ['beverage', 'fruity', 'vibrant'],
    light: {
      primary: '#8E4585',
      primaryShadow: '#72376A',
      accent: '#FF6B8A',
      accentShadow: '#CC566E',
      background: '#FFFBFE',
      cardBackground: '#FAF0F5',
      text: '#2A1828',
      extra: '#B060A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B068A8',
      primaryShadow: '#8E4585',
      accent: '#FF88A0',
      accentShadow: '#FF6B8A',
      background: '#14081A',
      cardBackground: '#1E1028',
      text: '#FAF0F8',
      extra: '#C880B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lemonade',
    tags: ['beverage', 'refreshing', 'summer'],
    light: {
      primary: '#FFF44F',
      primaryShadow: '#CCC33F',
      accent: '#90EE90',
      accentShadow: '#73BE73',
      background: '#FFFFF5',
      cardBackground: '#FFFAE0',
      text: '#2A2A10',
      extra: '#FFFF00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFF870',
      primaryShadow: '#FFF44F',
      accent: '#A8FFA8',
      accentShadow: '#90EE90',
      background: '#14140A',
      cardBackground: '#201E10',
      text: '#FFFBE8',
      extra: '#FFFF50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hot Chocolate',
    tags: ['beverage', 'cozy', 'winter'],
    light: {
      primary: '#3B2415',
      primaryShadow: '#2F1D11',
      accent: '#F5DEB3',
      accentShadow: '#C4B28F',
      background: '#FFFDF8',
      cardBackground: '#F5EDE0',
      text: '#2A1A10',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5A4030',
      primaryShadow: '#3B2415',
      accent: '#F8E8C8',
      accentShadow: '#F5DEB3',
      background: '#0A0805',
      cardBackground: '#15100A',
      text: '#F5EDE0',
      extra: '#B06835',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Metal Themes
  {
    name: 'Gold',
    tags: ['metal', 'luxurious', 'premium'],
    light: {
      primary: '#D4AF37',
      primaryShadow: '#AA8C2C',
      accent: '#1A1A1A',
      accentShadow: '#000000',
      background: '#FFFEF5',
      cardBackground: '#FAF5E5',
      text: '#2A2510',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8C850',
      primaryShadow: '#D4AF37',
      accent: '#E0E0E0',
      accentShadow: '#C0C0C0',
      background: '#0A0A05',
      cardBackground: '#15140A',
      text: '#FAF5E0',
      extra: '#FFE060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silver',
    tags: ['metal', 'modern', 'sleek'],
    light: {
      primary: '#C0C0C0',
      primaryShadow: '#9A9A9A',
      accent: '#4A4A4A',
      accentShadow: '#3B3B3B',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#2A2A2A',
      extra: '#A8A8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#C0C0C0',
      accent: '#808080',
      accentShadow: '#606060',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#C8C8C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Bronze',
    tags: ['metal', 'vintage', 'classic'],
    light: {
      primary: '#CD7F32',
      primaryShadow: '#A46628',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFFDF5',
      cardBackground: '#F5ECD5',
      text: '#2A2010',
      extra: '#B87333',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E09850',
      primaryShadow: '#CD7F32',
      accent: '#B06835',
      accentShadow: '#8B4513',
      background: '#0A0805',
      cardBackground: '#15100A',
      text: '#F5ECD5',
      extra: '#D89050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper',
    tags: ['metal', 'warm', 'industrial'],
    light: {
      primary: '#B87333',
      primaryShadow: '#935C29',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFFDF5',
      cardBackground: '#FAF0E5',
      text: '#2A1A10',
      extra: '#CD7F32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D89050',
      primaryShadow: '#B87333',
      accent: '#40B840',
      accentShadow: '#228B22',
      background: '#0A0805',
      cardBackground: '#15100A',
      text: '#FAF0E0',
      extra: '#E09858',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Platinum',
    tags: ['metal', 'premium', 'elegant'],
    light: {
      primary: '#E5E4E2',
      primaryShadow: '#B8B6B5',
      accent: '#3D3D3D',
      accentShadow: '#313131',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#2A2A2A',
      extra: '#D0D0D0',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#F5F4F2',
      primaryShadow: '#E5E4E2',
      accent: '#909090',
      accentShadow: '#707070',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F8F8F8',
      extra: '#E8E8E8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Rose Gold',
    tags: ['metal', 'feminine', 'trendy'],
    light: {
      primary: '#B76E79',
      primaryShadow: '#925860',
      accent: '#F7E7CE',
      accentShadow: '#C5B9A5',
      background: '#FFFBFB',
      cardBackground: '#FAF0F0',
      text: '#3A2025',
      extra: '#E8A8B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D88898',
      primaryShadow: '#B76E79',
      accent: '#F8F0E0',
      accentShadow: '#E0D8C8',
      background: '#150A0C',
      cardBackground: '#201518',
      text: '#FAF0F0',
      extra: '#F0B8C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel',
    tags: ['metal', 'industrial', 'strong'],
    light: {
      primary: '#71797E',
      primaryShadow: '#5A6166',
      accent: '#B4B8BC',
      accentShadow: '#909396',
      background: '#F8F8F8',
      cardBackground: '#F0F0F0',
      text: '#2A2A2A',
      extra: '#899095',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#909598',
      primaryShadow: '#71797E',
      accent: '#C8CCD0',
      accentShadow: '#B4B8BC',
      background: '#0D0D0D',
      cardBackground: '#1A1A1A',
      text: '#E8E8E8',
      extra: '#A8AEB0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Brass',
    tags: ['metal', 'vintage', 'warm'],
    light: {
      primary: '#B5A642',
      primaryShadow: '#918535',
      accent: '#2F4F4F',
      accentShadow: '#263F3F',
      background: '#FFFEF5',
      cardBackground: '#F5F0E0',
      text: '#2A2810',
      extra: '#D4C84A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D0C060',
      primaryShadow: '#B5A642',
      accent: '#4A6A6A',
      accentShadow: '#2F4F4F',
      background: '#0A0A05',
      cardBackground: '#15150A',
      text: '#F5F0E0',
      extra: '#E0D070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Architecture Themes
  {
    name: 'Gothic Cathedral',
    tags: ['architecture', 'dramatic', 'historical'],
    light: {
      primary: '#2C2C2C',
      primaryShadow: '#232323',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#F8F8F5',
      cardBackground: '#E8E8E5',
      text: '#1A1A1A',
      extra: '#C9A227',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#C0C0C0',
      primaryShadow: '#A0A0A0',
      accent: '#B83030',
      accentShadow: '#8B0000',
      background: '#0A0A08',
      cardBackground: '#151512',
      text: '#E8E8E5',
      extra: '#E0B830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Modern Minimalist',
    tags: ['architecture', 'clean', 'contemporary'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#000000',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#808080',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FFFFFF',
      primaryShadow: '#E0E0E0',
      accent: '#2A2A2A',
      accentShadow: '#1A1A1A',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#FFFFFF',
      extra: '#909090',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Japanese Traditional',
    tags: ['architecture', 'zen', 'natural'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FAF8F0',
      cardBackground: '#F0EBD8',
      text: '#2A2015',
      extra: '#C9A227',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06835',
      primaryShadow: '#8B4513',
      accent: '#40B840',
      accentShadow: '#228B22',
      background: '#0A0805',
      cardBackground: '#15100A',
      text: '#F0EBD8',
      extra: '#E0B830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Art Deco Building',
    tags: ['architecture', 'glamorous', 'geometric'],
    light: {
      primary: '#C9A227',
      primaryShadow: '#A1821F',
      accent: '#1A1A2E',
      accentShadow: '#141425',
      background: '#FFFEF8',
      cardBackground: '#F8F5E8',
      text: '#1A1A2E',
      extra: '#E0D0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0B830',
      primaryShadow: '#C9A227',
      accent: '#4A4A68',
      accentShadow: '#2A2A48',
      background: '#0A0A10',
      cardBackground: '#15151E',
      text: '#F8F5E8',
      extra: '#F0E0B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Industrial Loft',
    tags: ['architecture', 'urban', 'raw'],
    light: {
      primary: '#4A4A4A',
      primaryShadow: '#3B3B3B',
      accent: '#CD7F32',
      accentShadow: '#A46628',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#2A2A2A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#909090',
      primaryShadow: '#707070',
      accent: '#E09850',
      accentShadow: '#CD7F32',
      background: '#0D0D0D',
      cardBackground: '#1A1A1A',
      text: '#E8E8E8',
      extra: '#B83030',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Mediterranean Villa',
    tags: ['architecture', 'warm', 'sunny'],
    light: {
      primary: '#1E90FF',
      primaryShadow: '#1873CC',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFEF8',
      cardBackground: '#FDF5E6',
      text: '#1A3050',
      extra: '#E07020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40A8FF',
      primaryShadow: '#1E90FF',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#0A1828',
      cardBackground: '#152535',
      text: '#F8F5E8',
      extra: '#F09038',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Brutalist',
    tags: ['architecture', 'bold', 'concrete'],
    light: {
      primary: '#606060',
      primaryShadow: '#4D4D4D',
      accent: '#909090',
      accentShadow: '#737373',
      background: '#E8E8E8',
      cardBackground: '#D0D0D0',
      text: '#2A2A2A',
      extra: '#787878',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#B0B0B0',
      primaryShadow: '#909090',
      accent: '#686868',
      accentShadow: '#505050',
      background: '#1A1A1A',
      cardBackground: '#252525',
      text: '#E0E0E0',
      extra: '#909090',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Victorian House',
    tags: ['architecture', 'ornate', 'romantic'],
    light: {
      primary: '#6A3D78',
      primaryShadow: '#553160',
      accent: '#C9A227',
      accentShadow: '#A1821F',
      background: '#FFFBFE',
      cardBackground: '#F5EDF5',
      text: '#2A1A30',
      extra: '#8B4585',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8858A0',
      primaryShadow: '#6A3D78',
      accent: '#E0C040',
      accentShadow: '#C9A227',
      background: '#10081A',
      cardBackground: '#1A1028',
      text: '#F5EDF5',
      extra: '#A868B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Abstract Themes
  {
    name: 'Geometric',
    tags: ['abstract', 'modern', 'sharp'],
    light: {
      primary: '#2196F3',
      primaryShadow: '#1A78C2',
      accent: '#FF5722',
      accentShadow: '#CC461B',
      background: '#FFFFFF',
      cardBackground: '#F5F8FF',
      text: '#1A2A40',
      extra: '#4CAF50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#2196F3',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#0A1020',
      cardBackground: '#152030',
      text: '#E8F0FF',
      extra: '#66BB6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Organic Flow',
    tags: ['abstract', 'natural', 'fluid'],
    light: {
      primary: '#2E8B57',
      primaryShadow: '#256F46',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#FAFDF8',
      cardBackground: '#F0F8F0',
      text: '#1A3020',
      extra: '#6B8E23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50B080',
      primaryShadow: '#2E8B57',
      accent: '#A8E0F0',
      accentShadow: '#87CEEB',
      background: '#081510',
      cardBackground: '#102018',
      text: '#E8F8E8',
      extra: '#90B840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pixelated',
    tags: ['abstract', 'digital', 'retro'],
    light: {
      primary: '#00FF00',
      primaryShadow: '#00CC00',
      accent: '#FF00FF',
      accentShadow: '#CC00CC',
      background: '#FFFFFF',
      cardBackground: '#F0FFF0',
      text: '#1A1A1A',
      extra: '#00FFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#40FF40',
      primaryShadow: '#00FF00',
      accent: '#FF40FF',
      accentShadow: '#FF00FF',
      background: '#0A0A0A',
      cardBackground: '#151A15',
      text: '#E8FFE8',
      extra: '#40FFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Watercolor',
    tags: ['abstract', 'artistic', 'soft'],
    light: {
      primary: '#7B9DC4',
      primaryShadow: '#627E9D',
      accent: '#E8A8B0',
      accentShadow: '#BA868D',
      background: '#FEFEFE',
      cardBackground: '#F8F8FA',
      text: '#3A4050',
      extra: '#A8C8A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#98B8D8',
      primaryShadow: '#7B9DC4',
      accent: '#F0C0C8',
      accentShadow: '#E8A8B0',
      background: '#101520',
      cardBackground: '#1A2030',
      text: '#E8EFF5',
      extra: '#C0E0B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mosaic',
    tags: ['abstract', 'colorful', 'eclectic'],
    light: {
      primary: '#E6194B',
      primaryShadow: '#B8143C',
      accent: '#3CB44B',
      accentShadow: '#309038',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#F58231',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#F04068',
      primaryShadow: '#E6194B',
      accent: '#50D868',
      accentShadow: '#3CB44B',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#FF9848',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Fractal',
    tags: ['abstract', 'complex', 'mathematical'],
    light: {
      primary: '#4B0082',
      primaryShadow: '#3C0068',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#FFFBFF',
      cardBackground: '#F5F0FA',
      text: '#1A1030',
      extra: '#9400D3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7030A0',
      primaryShadow: '#4B0082',
      accent: '#40E8EB',
      accentShadow: '#00CED1',
      background: '#100818',
      cardBackground: '#1A1028',
      text: '#F5F0FF',
      extra: '#B848F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gradient Blur',
    tags: ['abstract', 'modern', 'dreamy'],
    light: {
      primary: '#667EEA',
      primaryShadow: '#5264BB',
      accent: '#764BA2',
      accentShadow: '#5E3C82',
      background: '#FFFFFF',
      cardBackground: '#F5F5FF',
      text: '#1A2040',
      extra: '#9580FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8898F0',
      primaryShadow: '#667EEA',
      accent: '#9668C0',
      accentShadow: '#764BA2',
      background: '#0A0818',
      cardBackground: '#151028',
      text: '#E8ECFF',
      extra: '#B0A0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noise Texture',
    tags: ['abstract', 'grainy', 'tactile'],
    light: {
      primary: '#5D5C61',
      primaryShadow: '#4A4A4E',
      accent: '#939597',
      accentShadow: '#767879',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#2A2A2A',
      extra: '#747577',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#8A898E',
      primaryShadow: '#6A696E',
      accent: '#B0B2B5',
      accentShadow: '#939597',
      background: '#151515',
      cardBackground: '#202020',
      text: '#E0E0E0',
      extra: '#989A9C',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  // Candy & Sweet Themes
  {
    name: 'Bubblegum',
    tags: ['candy', 'playful', 'bright'],
    light: {
      primary: '#FF6EB4',
      primaryShadow: '#CC5890',
      accent: '#00D8FF',
      accentShadow: '#00ADCC',
      background: '#FFFBFE',
      cardBackground: '#FFF0F8',
      text: '#2A1828',
      extra: '#FF89C4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF6EB4',
      accent: '#40E8FF',
      accentShadow: '#00D8FF',
      background: '#1A0815',
      cardBackground: '#281020',
      text: '#FFF0F8',
      extra: '#FFA0D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Licorice',
    tags: ['candy', 'bold', 'classic'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#000000',
      accent: '#FF0000',
      accentShadow: '#CC0000',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#C0C0C0',
      accent: '#FF4040',
      accentShadow: '#FF0000',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#B83030',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'text',
    },
  },
  {
    name: 'Candy Cane',
    tags: ['candy', 'festive', 'cheerful'],
    light: {
      primary: '#FF0000',
      primaryShadow: '#CC0000',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#FFFAFA',
      cardBackground: '#FFF0F0',
      text: '#2A1A1A',
      extra: '#00A000',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF4040',
      primaryShadow: '#FF0000',
      accent: '#F0F0F0',
      accentShadow: '#D0D0D0',
      background: '#120808',
      cardBackground: '#1E1010',
      text: '#FFF0F0',
      extra: '#40C040',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cotton Candy Dream',
    tags: ['candy', 'dreamy', 'pastel'],
    light: {
      primary: '#FFB7D5',
      primaryShadow: '#CC92AA',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#FFFBFE',
      cardBackground: '#FFF5FA',
      text: '#3A2030',
      extra: '#E8A8C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFD0E8',
      primaryShadow: '#FFB7D5',
      accent: '#A8E0F8',
      accentShadow: '#87CEEB',
      background: '#180A15',
      cardBackground: '#251520',
      text: '#FFF5FA',
      extra: '#F0C0D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sour Apple',
    tags: ['candy', 'tangy', 'bright'],
    light: {
      primary: '#8DB600',
      primaryShadow: '#719200',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FAFDF5',
      cardBackground: '#F0F8E5',
      text: '#2A3510',
      extra: '#ADFF2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A8D820',
      primaryShadow: '#8DB600',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#0C1208',
      cardBackground: '#151E10',
      text: '#F0F8E0',
      extra: '#C8FF50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Caramel Swirl',
    tags: ['candy', 'warm', 'sweet'],
    light: {
      primary: '#C68E17',
      primaryShadow: '#9E7212',
      accent: '#F5DEB3',
      accentShadow: '#C4B28F',
      background: '#FFFDF5',
      cardBackground: '#FAF0E0',
      text: '#2A2010',
      extra: '#DEB887',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A830',
      primaryShadow: '#C68E17',
      accent: '#F8EBC8',
      accentShadow: '#E0D0A8',
      background: '#120A05',
      cardBackground: '#1E1508',
      text: '#FAF0D8',
      extra: '#F0D0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Ocean & Marine Themes
  {
    name: 'Coral Reef',
    tags: ['ocean', 'colorful', 'tropical'],
    light: {
      primary: '#FF7F50',
      primaryShadow: '#CC6640',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#FFFFF8',
      cardBackground: '#F8FFFF',
      text: '#1A3040',
      extra: '#FFB347',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9870',
      primaryShadow: '#FF7F50',
      accent: '#40E8EB',
      accentShadow: '#00CED1',
      background: '#0A1518',
      cardBackground: '#102025',
      text: '#E8FFFF',
      extra: '#FFC860',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Sea',
    tags: ['ocean', 'mysterious', 'dark'],
    light: {
      primary: '#003366',
      primaryShadow: '#002952',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#F5FAFF',
      cardBackground: '#E8F0F8',
      text: '#0A2040',
      extra: '#104E8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2060A0',
      primaryShadow: '#003366',
      accent: '#40E8EB',
      accentShadow: '#00CED1',
      background: '#050A15',
      cardBackground: '#0A1525',
      text: '#E0F0FF',
      extra: '#2878B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jellyfish',
    tags: ['ocean', 'ethereal', 'glowing'],
    light: {
      primary: '#DA70D6',
      primaryShadow: '#AE5AAB',
      accent: '#00D9FF',
      accentShadow: '#00ADCC',
      background: '#FEFBFF',
      cardBackground: '#F8F0FA',
      text: '#2A1838',
      extra: '#FF69B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E890E0',
      primaryShadow: '#DA70D6',
      accent: '#40E8FF',
      accentShadow: '#00D9FF',
      background: '#120818',
      cardBackground: '#1A1028',
      text: '#FAF0FF',
      extra: '#FF88C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Seashell',
    tags: ['ocean', 'soft', 'coastal'],
    light: {
      primary: '#FFF5EE',
      primaryShadow: '#CCC4BE',
      accent: '#E0C0A0',
      accentShadow: '#B39A80',
      background: '#FFFEFA',
      cardBackground: '#FAF5F0',
      text: '#3A3028',
      extra: '#FFE4C4',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A4038',
      primaryShadow: '#3A302A',
      accent: '#F0D8C0',
      accentShadow: '#E0C0A0',
      background: '#141008',
      cardBackground: '#1E1810',
      text: '#FAF5F0',
      extra: '#F8E8D0',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mermaid',
    tags: ['ocean', 'magical', 'iridescent'],
    light: {
      primary: '#20B2AA',
      primaryShadow: '#1A8E88',
      accent: '#9370DB',
      accentShadow: '#765AB0',
      background: '#F5FFFF',
      cardBackground: '#E8FAF8',
      text: '#1A3038',
      extra: '#48D1CC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40D0C8',
      primaryShadow: '#20B2AA',
      accent: '#B090F0',
      accentShadow: '#9370DB',
      background: '#081518',
      cardBackground: '#102028',
      text: '#E8FFFF',
      extra: '#68E8E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lighthouse',
    tags: ['ocean', 'nautical', 'classic'],
    light: {
      primary: '#DC143C',
      primaryShadow: '#B0102F',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#F8FAFF',
      cardBackground: '#E8F0F8',
      text: '#1A2A40',
      extra: '#003366',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F04060',
      primaryShadow: '#DC143C',
      accent: '#F0F0F0',
      accentShadow: '#D0D0D0',
      background: '#0A0F18',
      cardBackground: '#101A28',
      text: '#E8F0F8',
      extra: '#2060A0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
  },
  // Cinema & Film Themes
  {
    name: 'Film Noir',
    tags: ['cinema', 'classic', 'dramatic', 'monochromatic'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#B0B0B0',
      accentShadow: '#909090',
      background: '#F5F5F0',
      cardBackground: '#E8E8E0',
      text: '#1A1A1A',
      extra: '#505050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#C0C0C0',
      accent: '#808080',
      accentShadow: '#606060',
      background: '#0A0A0A',
      cardBackground: '#141414',
      text: '#E8E8E8',
      extra: '#A0A0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Hollywood',
    tags: ['cinema', 'glamorous', 'vintage', 'luxurious'],
    light: {
      primary: '#D4AF37',
      primaryShadow: '#AA8C2C',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#FFF8F0',
      cardBackground: '#F8F0E0',
      text: '#2A1A10',
      extra: '#C9A227',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F0C850',
      primaryShadow: '#D4AF37',
      accent: '#C04040',
      accentShadow: '#8B0000',
      background: '#0F0A05',
      cardBackground: '#1A1208',
      text: '#F8F0E0',
      extra: '#E0B840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sci-Fi Future',
    tags: ['cinema', 'futuristic', 'tech', 'cyberpunk'],
    light: {
      primary: '#00BFFF',
      primaryShadow: '#0099CC',
      accent: '#FF4500',
      accentShadow: '#CC3700',
      background: '#F0F8FF',
      cardBackground: '#E0F0FA',
      text: '#0A1A2A',
      extra: '#8A2BE2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40D0FF',
      primaryShadow: '#00BFFF',
      accent: '#FF6830',
      accentShadow: '#FF4500',
      background: '#050A10',
      cardBackground: '#0A1520',
      text: '#E0F0FF',
      extra: '#A050F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Horror Movie',
    tags: ['cinema', 'dark', 'dramatic', 'spooky'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#2F4F4F',
      accentShadow: '#253F3F',
      background: '#FAF8F5',
      cardBackground: '#E8E4E0',
      text: '#1A1515',
      extra: '#4A0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C04040',
      primaryShadow: '#8B0000',
      accent: '#608080',
      accentShadow: '#2F4F4F',
      background: '#080505',
      cardBackground: '#120A0A',
      text: '#E8E0E0',
      extra: '#802020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Romantic Comedy',
    tags: ['cinema', 'cheerful', 'soft', 'playful'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFAF5',
      cardBackground: '#FFF0F5',
      text: '#3A2A30',
      extra: '#FF1493',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#FFE040',
      accentShadow: '#FFD700',
      background: '#150A10',
      cardBackground: '#1F1018',
      text: '#FFF0F5',
      extra: '#FF50A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Animation Studio',
    tags: ['cinema', 'vibrant', 'playful', 'colorful'],
    light: {
      primary: '#FF6B35',
      primaryShadow: '#CC562A',
      accent: '#4ECDC4',
      accentShadow: '#3EA49D',
      background: '#FFFDF8',
      cardBackground: '#FFF8F0',
      text: '#1A2A3A',
      extra: '#FFE66D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8858',
      primaryShadow: '#FF6B35',
      accent: '#70E8E0',
      accentShadow: '#4ECDC4',
      background: '#0A0F14',
      cardBackground: '#121A22',
      text: '#FFF8F0',
      extra: '#FFF090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Documentary',
    tags: ['cinema', 'natural', 'earthy', 'authentic'],
    light: {
      primary: '#5D4E37',
      primaryShadow: '#4A3E2C',
      accent: '#8B7355',
      accentShadow: '#6F5C44',
      background: '#FAF8F5',
      cardBackground: '#F0EBE4',
      text: '#2A2420',
      extra: '#A08060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9A8A70',
      primaryShadow: '#5D4E37',
      accent: '#B0A088',
      accentShadow: '#8B7355',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0EBE4',
      extra: '#C0A880',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indie Film',
    tags: ['cinema', 'artistic', 'muted', 'thoughtful'],
    light: {
      primary: '#6B5B95',
      primaryShadow: '#564A78',
      accent: '#88B04B',
      accentShadow: '#6D8D3C',
      background: '#FAFAF8',
      cardBackground: '#F0F0EA',
      text: '#2A2830',
      extra: '#F7CAC9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8E7DB8',
      primaryShadow: '#6B5B95',
      accent: '#A8D068',
      accentShadow: '#88B04B',
      background: '#0A0A0C',
      cardBackground: '#141418',
      text: '#F0F0EA',
      extra: '#F8D8D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Science Themes
  {
    name: 'Laboratory',
    tags: ['science', 'clean', 'clinical', 'precise'],
    light: {
      primary: '#00A0A0',
      primaryShadow: '#008080',
      accent: '#FF6B6B',
      accentShadow: '#CC5656',
      background: '#FFFFFF',
      cardBackground: '#F5FAFA',
      text: '#1A2A30',
      extra: '#40C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40D0D0',
      primaryShadow: '#00A0A0',
      accent: '#FF8888',
      accentShadow: '#FF6B6B',
      background: '#0A1012',
      cardBackground: '#101A1C',
      text: '#F0FAFA',
      extra: '#60E0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Astronomy',
    tags: ['science', 'cosmic', 'dark', 'majestic'],
    light: {
      primary: '#1E3A5F',
      primaryShadow: '#182E4C',
      accent: '#FFA500',
      accentShadow: '#CC8400',
      background: '#F8F9FA',
      cardBackground: '#E8EEF5',
      text: '#1A2030',
      extra: '#4B0082',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4080C0',
      primaryShadow: '#1E3A5F',
      accent: '#FFB840',
      accentShadow: '#FFA500',
      background: '#050510',
      cardBackground: '#0A0A20',
      text: '#E8F0FF',
      extra: '#8040C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Chemistry',
    tags: ['science', 'precise', 'analytical', 'systematic'],
    light: {
      primary: '#2E7D32',
      primaryShadow: '#256428',
      accent: '#7B1FA2',
      accentShadow: '#621982',
      background: '#FAFFF8',
      cardBackground: '#F0F8F0',
      text: '#1A2A1A',
      extra: '#388E3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#60B060',
      primaryShadow: '#2E7D32',
      accent: '#B060D0',
      accentShadow: '#7B1FA2',
      background: '#080A08',
      cardBackground: '#101510',
      text: '#F0F8F0',
      extra: '#70C070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Biology',
    tags: ['science', 'organic', 'natural', 'living'],
    light: {
      primary: '#4CAF50',
      primaryShadow: '#3D8B40',
      accent: '#E91E63',
      accentShadow: '#BA184F',
      background: '#F8FFF8',
      cardBackground: '#E8F5E9',
      text: '#1A2818',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#70D070',
      primaryShadow: '#4CAF50',
      accent: '#F05088',
      accentShadow: '#E91E63',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E8F5E9',
      extra: '#A8D870',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Physics',
    tags: ['science', 'mathematical', 'fundamental', 'universal'],
    light: {
      primary: '#1565C0',
      primaryShadow: '#10509A',
      accent: '#FF5722',
      accentShadow: '#CC461B',
      background: '#F8FAFF',
      cardBackground: '#E8F0F8',
      text: '#1A2030',
      extra: '#42A5F5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5090E0',
      primaryShadow: '#1565C0',
      accent: '#FF7850',
      accentShadow: '#FF5722',
      background: '#080A10',
      cardBackground: '#101820',
      text: '#E8F0F8',
      extra: '#70B8F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Geology',
    tags: ['science', 'earthy', 'mineral', 'ancient'],
    light: {
      primary: '#795548',
      primaryShadow: '#61443A',
      accent: '#FF9800',
      accentShadow: '#CC7A00',
      background: '#FAF8F5',
      cardBackground: '#EFEBE9',
      text: '#2A2420',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A08070',
      primaryShadow: '#795548',
      accent: '#FFB040',
      accentShadow: '#FF9800',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#EFEBE9',
      extra: '#C0A898',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Botany',
    tags: ['science', 'botanical', 'green', 'growth'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#DA70D6',
      accentShadow: '#AE5AAB',
      background: '#F5FFF5',
      cardBackground: '#E0F0E0',
      text: '#1A2A1A',
      extra: '#90EE90',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50C050',
      primaryShadow: '#228B22',
      accent: '#E890E8',
      accentShadow: '#DA70D6',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E0F0E0',
      extra: '#A8F0A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Meteorology',
    tags: ['science', 'weather', 'atmospheric', 'dynamic'],
    light: {
      primary: '#5C9CE6',
      primaryShadow: '#4A7DB8',
      accent: '#FFD93D',
      accentShadow: '#CCAD31',
      background: '#F0F8FF',
      cardBackground: '#E0EFFF',
      text: '#1A2A40',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#80B8F0',
      primaryShadow: '#5C9CE6',
      accent: '#FFE868',
      accentShadow: '#FFD93D',
      background: '#0A1018',
      cardBackground: '#101828',
      text: '#E0EFFF',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Mythology Themes
  {
    name: 'Greek Gods',
    tags: ['mythology', 'classical', 'majestic', 'ancient'],
    light: {
      primary: '#1E3A5F',
      primaryShadow: '#182E4C',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFEFA',
      cardBackground: '#F0F0E8',
      text: '#1A2030',
      extra: '#4169E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4080C0',
      primaryShadow: '#1E3A5F',
      accent: '#F0C850',
      accentShadow: '#D4AF37',
      background: '#080A10',
      cardBackground: '#101520',
      text: '#F0F0E8',
      extra: '#6090F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Norse Mythology',
    tags: ['mythology', 'nordic', 'rugged', 'powerful'],
    light: {
      primary: '#2F4F4F',
      primaryShadow: '#253F3F',
      accent: '#ADD8E6',
      accentShadow: '#8AACB8',
      background: '#F8FAFA',
      cardBackground: '#E8F0F0',
      text: '#1A2020',
      extra: '#5F9EA0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#608080',
      primaryShadow: '#2F4F4F',
      accent: '#C0E8F0',
      accentShadow: '#ADD8E6',
      background: '#080A0A',
      cardBackground: '#101515',
      text: '#E8F0F0',
      extra: '#80C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Egyptian',
    tags: ['mythology', 'ancient', 'desert', 'royal'],
    light: {
      primary: '#C5A000',
      primaryShadow: '#9E8000',
      accent: '#00868B',
      accentShadow: '#006B6F',
      background: '#FFF8E8',
      cardBackground: '#F5ECD8',
      text: '#2A2010',
      extra: '#E6BE00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8C040',
      primaryShadow: '#C5A000',
      accent: '#40B0B8',
      accentShadow: '#00868B',
      background: '#0F0C05',
      cardBackground: '#1A1508',
      text: '#F5ECD8',
      extra: '#F0D050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Celtic',
    tags: ['mythology', 'mystical', 'green', 'ancient'],
    light: {
      primary: '#2E8B57',
      primaryShadow: '#256F46',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#F5FAF5',
      cardBackground: '#E0EEE0',
      text: '#1A2818',
      extra: '#DAA520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#60B880',
      primaryShadow: '#2E8B57',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E0EEE0',
      extra: '#F0C040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Japanese Shinto',
    tags: ['mythology', 'sacred', 'natural', 'serene'],
    light: {
      primary: '#BC002D',
      primaryShadow: '#960024',
      accent: '#4A766E',
      accentShadow: '#3B5E58',
      background: '#FFFAF8',
      cardBackground: '#F5F0EC',
      text: '#1A1815',
      extra: '#C9A227',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E03050',
      primaryShadow: '#BC002D',
      accent: '#70A098',
      accentShadow: '#4A766E',
      background: '#0A0808',
      cardBackground: '#151210',
      text: '#F5F0EC',
      extra: '#E0B840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hindu',
    tags: ['mythology', 'spiritual', 'vibrant', 'sacred'],
    light: {
      primary: '#FF6600',
      primaryShadow: '#CC5200',
      accent: '#9933CC',
      accentShadow: '#7A29A3',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8840',
      primaryShadow: '#FF6600',
      accent: '#B868E0',
      accentShadow: '#9933CC',
      background: '#100A05',
      cardBackground: '#1A1208',
      text: '#FFF0E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Aztec',
    tags: ['mythology', 'bold', 'geometric', 'ancient'],
    light: {
      primary: '#C04000',
      primaryShadow: '#9A3300',
      accent: '#00A86B',
      accentShadow: '#008756',
      background: '#FFF8F0',
      cardBackground: '#F5E8D8',
      text: '#2A1810',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E06030',
      primaryShadow: '#C04000',
      accent: '#40D090',
      accentShadow: '#00A86B',
      background: '#100805',
      cardBackground: '#1A1008',
      text: '#F5E8D8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Chinese Dragon',
    tags: ['mythology', 'powerful', 'imperial', 'auspicious'],
    light: {
      primary: '#DC143C',
      primaryShadow: '#B0102F',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFF8F5',
      cardBackground: '#FFF0E8',
      text: '#2A1510',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F04060',
      primaryShadow: '#DC143C',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#100805',
      cardBackground: '#1A100A',
      text: '#FFF0E8',
      extra: '#50C050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Gaming Themes
  {
    name: 'Arcade',
    tags: ['gaming', 'retro', 'neon', 'playful'],
    light: {
      primary: '#FF00FF',
      primaryShadow: '#CC00CC',
      accent: '#00FFFF',
      accentShadow: '#00CCCC',
      background: '#1A1A2E',
      cardBackground: '#16213E',
      text: '#E8E8FF',
      extra: '#FFFF00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF40FF',
      primaryShadow: '#FF00FF',
      accent: '#40FFFF',
      accentShadow: '#00FFFF',
      background: '#0A0A14',
      cardBackground: '#10101F',
      text: '#E8E8FF',
      extra: '#FFFF40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'RPG Fantasy',
    tags: ['gaming', 'fantasy', 'medieval', 'adventure'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#4169E1',
      accentShadow: '#3454B4',
      background: '#FAF5E8',
      cardBackground: '#F0E8D0',
      text: '#2A2015',
      extra: '#DAA520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#6090F0',
      accentShadow: '#4169E1',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F0E8D0',
      extra: '#F0C040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cyberpunk Game',
    tags: ['gaming', 'futuristic', 'neon', 'dystopian'],
    light: {
      primary: '#FF0090',
      primaryShadow: '#CC0073',
      accent: '#00F0FF',
      accentShadow: '#00C0CC',
      background: '#0D0D1A',
      cardBackground: '#15152A',
      text: '#E8E0FF',
      extra: '#FFD000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF40B0',
      primaryShadow: '#FF0090',
      accent: '#40F8FF',
      accentShadow: '#00F0FF',
      background: '#05050A',
      cardBackground: '#0A0A15',
      text: '#E8E0FF',
      extra: '#FFE040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sports Game',
    tags: ['gaming', 'athletic', 'energetic', 'competitive'],
    light: {
      primary: '#00AA00',
      primaryShadow: '#008800',
      accent: '#FF6600',
      accentShadow: '#CC5200',
      background: '#F8FFF8',
      cardBackground: '#E8F8E8',
      text: '#1A2A1A',
      extra: '#FFCC00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40D040',
      primaryShadow: '#00AA00',
      accent: '#FF8840',
      accentShadow: '#FF6600',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E8F8E8',
      extra: '#FFE040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Puzzle Game',
    tags: ['gaming', 'minimalist', 'clean', 'logical'],
    light: {
      primary: '#5C6BC0',
      primaryShadow: '#4A569A',
      accent: '#26A69A',
      accentShadow: '#1E857B',
      background: '#FAFAFA',
      cardBackground: '#F0F0F5',
      text: '#2A2A35',
      extra: '#7E57C2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8090E0',
      primaryShadow: '#5C6BC0',
      accent: '#50D0C0',
      accentShadow: '#26A69A',
      background: '#0A0A0C',
      cardBackground: '#121218',
      text: '#F0F0F5',
      extra: '#A080E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Console',
    tags: ['gaming', 'nostalgic', 'vintage', '8-bit'],
    light: {
      primary: '#E60012',
      primaryShadow: '#B8000E',
      accent: '#1E90FF',
      accentShadow: '#1873CC',
      background: '#F8F8F5',
      cardBackground: '#E8E8E0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F04040',
      primaryShadow: '#E60012',
      accent: '#50B0FF',
      accentShadow: '#1E90FF',
      background: '#0A0A08',
      cardBackground: '#141410',
      text: '#E8E8E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Racing Game',
    tags: ['gaming', 'fast', 'dynamic', 'exciting'],
    light: {
      primary: '#FF0000',
      primaryShadow: '#CC0000',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF4040',
      primaryShadow: '#FF0000',
      accent: '#B0B0B0',
      accentShadow: '#808080',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#FFE040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Strategy Game',
    tags: ['gaming', 'tactical', 'thoughtful', 'complex'],
    light: {
      primary: '#2C3E50',
      primaryShadow: '#233140',
      accent: '#E74C3C',
      accentShadow: '#B93D30',
      background: '#F8F9FA',
      cardBackground: '#ECF0F1',
      text: '#1A2530',
      extra: '#3498DB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5070A0',
      primaryShadow: '#2C3E50',
      accent: '#F07060',
      accentShadow: '#E74C3C',
      background: '#080A0C',
      cardBackground: '#101518',
      text: '#ECF0F1',
      extra: '#60B0F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Animal Themes
  {
    name: 'Tiger',
    tags: ['animal', 'bold', 'fierce', 'striped'],
    light: {
      primary: '#FF8C00',
      primaryShadow: '#CC7000',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFB040',
      primaryShadow: '#FF8C00',
      accent: '#A0A0A0',
      accentShadow: '#808080',
      background: '#0F0A05',
      cardBackground: '#1A1208',
      text: '#FFF0E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Peacock',
    tags: ['animal', 'colorful', 'majestic', 'iridescent'],
    light: {
      primary: '#0D6E6E',
      primaryShadow: '#0A5858',
      accent: '#7B68EE',
      accentShadow: '#6254BE',
      background: '#F0FFFF',
      cardBackground: '#E0F8F8',
      text: '#1A2828',
      extra: '#00CED1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40A8A8',
      primaryShadow: '#0D6E6E',
      accent: '#A090F0',
      accentShadow: '#7B68EE',
      background: '#080C0C',
      cardBackground: '#101818',
      text: '#E0F8F8',
      extra: '#40E8E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Flamingo',
    tags: ['animal', 'tropical', 'pink', 'elegant'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#FF6347',
      accentShadow: '#CC4F39',
      background: '#FFF5FA',
      cardBackground: '#FFE8F0',
      text: '#3A1A28',
      extra: '#FFB6C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#FF8870',
      accentShadow: '#FF6347',
      background: '#150810',
      cardBackground: '#1F1018',
      text: '#FFE8F0',
      extra: '#FFC8D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wolf',
    tags: ['animal', 'wild', 'mysterious', 'powerful'],
    light: {
      primary: '#4A4A4A',
      primaryShadow: '#3A3A3A',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#696969',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#909090',
      primaryShadow: '#4A4A4A',
      accent: '#A8E0F8',
      accentShadow: '#87CEEB',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E8',
      extra: '#A0A0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fox',
    tags: ['animal', 'clever', 'warm', 'rustic'],
    light: {
      primary: '#D35400',
      primaryShadow: '#A94300',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1810',
      extra: '#E67E22',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F07830',
      primaryShadow: '#D35400',
      accent: '#909090',
      accentShadow: '#707070',
      background: '#0F0805',
      cardBackground: '#1A1008',
      text: '#FFF0E0',
      extra: '#F09850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Owl',
    tags: ['animal', 'wise', 'nocturnal', 'mysterious'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4A332C',
      accent: '#FFA000',
      accentShadow: '#CC8000',
      background: '#FAF8F5',
      cardBackground: '#F0E8E0',
      text: '#2A2420',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8A7060',
      primaryShadow: '#5D4037',
      accent: '#FFB840',
      accentShadow: '#FFA000',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0E8E0',
      extra: '#A89080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Butterfly',
    tags: ['animal', 'delicate', 'colorful', 'graceful'],
    light: {
      primary: '#9B59B6',
      primaryShadow: '#7C4792',
      accent: '#F39C12',
      accentShadow: '#C27D0E',
      background: '#FFF8FF',
      cardBackground: '#F8F0F8',
      text: '#2A1830',
      extra: '#E74C3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B880D8',
      primaryShadow: '#9B59B6',
      accent: '#F8B840',
      accentShadow: '#F39C12',
      background: '#0C0810',
      cardBackground: '#151018',
      text: '#F8F0F8',
      extra: '#F07060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dolphin',
    tags: ['animal', 'playful', 'oceanic', 'intelligent'],
    light: {
      primary: '#0077BE',
      primaryShadow: '#005F98',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#F0FAFF',
      cardBackground: '#E0F5FF',
      text: '#1A2530',
      extra: '#48D1CC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40A0E0',
      primaryShadow: '#0077BE',
      accent: '#40E8E8',
      accentShadow: '#00CED1',
      background: '#081015',
      cardBackground: '#101A22',
      text: '#E0F5FF',
      extra: '#68E8E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Zodiac Themes
  {
    name: 'Aries',
    tags: ['zodiac', 'fire', 'bold', 'energetic'],
    light: {
      primary: '#FF0000',
      primaryShadow: '#CC0000',
      accent: '#FF6600',
      accentShadow: '#CC5200',
      background: '#FFF8F5',
      cardBackground: '#FFF0E8',
      text: '#2A1510',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF4040',
      primaryShadow: '#FF0000',
      accent: '#FF8840',
      accentShadow: '#FF6600',
      background: '#100505',
      cardBackground: '#1A0A08',
      text: '#FFF0E8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Taurus',
    tags: ['zodiac', 'earth', 'stable', 'luxurious'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#F8FAF5',
      cardBackground: '#F0F5E8',
      text: '#1A2818',
      extra: '#DAA520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50C050',
      primaryShadow: '#228B22',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#080A08',
      cardBackground: '#101510',
      text: '#F0F5E8',
      extra: '#F0C040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gemini',
    tags: ['zodiac', 'air', 'dual', 'communicative'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#8A2BE2',
      accentShadow: '#6E22B5',
      background: '#FFFEF5',
      cardBackground: '#FFF8E0',
      text: '#2A2810',
      extra: '#4169E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#B060F0',
      accentShadow: '#8A2BE2',
      background: '#0C0A05',
      cardBackground: '#151208',
      text: '#FFF8E0',
      extra: '#6090F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cancer',
    tags: ['zodiac', 'water', 'nurturing', 'intuitive'],
    light: {
      primary: '#C0C0C0',
      primaryShadow: '#9A9A9A',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#F8F8FA',
      cardBackground: '#F0F0F5',
      text: '#1A1A20',
      extra: '#87CEEB',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#C0C0C0',
      accent: '#70A8D0',
      accentShadow: '#4682B4',
      background: '#080808',
      cardBackground: '#121215',
      text: '#F0F0F5',
      extra: '#A8E0F8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Leo',
    tags: ['zodiac', 'fire', 'regal', 'dramatic'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#FF4500',
      accentShadow: '#CC3700',
      background: '#FFFAF0',
      cardBackground: '#FFF0D0',
      text: '#2A2010',
      extra: '#FFA500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#FF6830',
      accentShadow: '#FF4500',
      background: '#0C0A05',
      cardBackground: '#151008',
      text: '#FFF0D0',
      extra: '#FFB840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Virgo',
    tags: ['zodiac', 'earth', 'practical', 'analytical'],
    light: {
      primary: '#6B8E23',
      primaryShadow: '#56721C',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FAFAF8',
      cardBackground: '#F0F0E8',
      text: '#2A2A20',
      extra: '#BDB76B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#98B850',
      primaryShadow: '#6B8E23',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0A08',
      cardBackground: '#151510',
      text: '#F0F0E8',
      extra: '#D0D090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Libra',
    tags: ['zodiac', 'air', 'balanced', 'harmonious'],
    light: {
      primary: '#FFC0CB',
      primaryShadow: '#CC9AA2',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#FFFAF8',
      cardBackground: '#FFF0F0',
      text: '#2A1820',
      extra: '#E6E6FA',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#FFD8E0',
      primaryShadow: '#FFC0CB',
      accent: '#70A8D0',
      accentShadow: '#4682B4',
      background: '#0C0808',
      cardBackground: '#151012',
      text: '#FFF0F0',
      extra: '#E8E8FF',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Scorpio',
    tags: ['zodiac', 'water', 'intense', 'mysterious'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FAF8F5',
      cardBackground: '#F0E8E0',
      text: '#1A1015',
      extra: '#800020',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C04040',
      primaryShadow: '#8B0000',
      accent: '#909090',
      accentShadow: '#606060',
      background: '#080505',
      cardBackground: '#120808',
      text: '#F0E8E0',
      extra: '#A02040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sagittarius',
    tags: ['zodiac', 'fire', 'adventurous', 'optimistic'],
    light: {
      primary: '#800080',
      primaryShadow: '#660066',
      accent: '#FF6600',
      accentShadow: '#CC5200',
      background: '#FFF8FA',
      cardBackground: '#F8F0F5',
      text: '#2A1825',
      extra: '#9932CC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B050B0',
      primaryShadow: '#800080',
      accent: '#FF8840',
      accentShadow: '#FF6600',
      background: '#0A0508',
      cardBackground: '#150810',
      text: '#F8F0F5',
      extra: '#C060E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Capricorn',
    tags: ['zodiac', 'earth', 'ambitious', 'disciplined'],
    light: {
      primary: '#36454F',
      primaryShadow: '#2B373F',
      accent: '#704214',
      accentShadow: '#5A3510',
      background: '#F8F8F8',
      cardBackground: '#F0F0F0',
      text: '#1A1A1A',
      extra: '#2F4F4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#607080',
      primaryShadow: '#36454F',
      accent: '#A07040',
      accentShadow: '#704214',
      background: '#080808',
      cardBackground: '#121212',
      text: '#F0F0F0',
      extra: '#508080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Aquarius',
    tags: ['zodiac', 'air', 'innovative', 'humanitarian'],
    light: {
      primary: '#00BFFF',
      primaryShadow: '#0099CC',
      accent: '#8A2BE2',
      accentShadow: '#6E22B5',
      background: '#F0FAFF',
      cardBackground: '#E0F5FF',
      text: '#1A2530',
      extra: '#40E0D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40D0FF',
      primaryShadow: '#00BFFF',
      accent: '#B060F0',
      accentShadow: '#8A2BE2',
      background: '#081018',
      cardBackground: '#101A28',
      text: '#E0F5FF',
      extra: '#60F0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pisces',
    tags: ['zodiac', 'water', 'dreamy', 'compassionate'],
    light: {
      primary: '#7B68EE',
      primaryShadow: '#6254BE',
      accent: '#20B2AA',
      accentShadow: '#1A8E88',
      background: '#F8F5FF',
      cardBackground: '#F0EAFA',
      text: '#1A1830',
      extra: '#DA70D6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A090F0',
      primaryShadow: '#7B68EE',
      accent: '#40D0C8',
      accentShadow: '#20B2AA',
      background: '#080810',
      cardBackground: '#101018',
      text: '#F0EAFA',
      extra: '#E890E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // City & Urban Themes
  {
    name: 'Tokyo Neon',
    tags: ['city', 'neon', 'vibrant', 'modern'],
    light: {
      primary: '#FF1493',
      primaryShadow: '#CC1076',
      accent: '#00FFFF',
      accentShadow: '#00CCCC',
      background: '#1A1A2E',
      cardBackground: '#16213E',
      text: '#E8E8FF',
      extra: '#FF00FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF50B0',
      primaryShadow: '#FF1493',
      accent: '#40FFFF',
      accentShadow: '#00FFFF',
      background: '#0A0A14',
      cardBackground: '#10101F',
      text: '#E8E8FF',
      extra: '#FF40FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'New York Skyline',
    tags: ['city', 'urban', 'metropolitan', 'classic'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F8F8F5',
      cardBackground: '#E8E8E0',
      text: '#1A1A1A',
      extra: '#4169E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#B0B0B0',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E0',
      extra: '#6090F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Paris Romance',
    tags: ['city', 'romantic', 'elegant', 'classic'],
    light: {
      primary: '#C9A0A0',
      primaryShadow: '#A18080',
      accent: '#4A4A4A',
      accentShadow: '#3A3A3A',
      background: '#FFF8F8',
      cardBackground: '#F8F0F0',
      text: '#2A2020',
      extra: '#DEB887',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0C0C0',
      primaryShadow: '#C9A0A0',
      accent: '#909090',
      accentShadow: '#707070',
      background: '#0C0808',
      cardBackground: '#151010',
      text: '#F8F0F0',
      extra: '#F0D0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'London Fog',
    tags: ['city', 'moody', 'classic', 'sophisticated'],
    light: {
      primary: '#4A5568',
      primaryShadow: '#3B4453',
      accent: '#DC143C',
      accentShadow: '#B0102F',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A20',
      extra: '#708090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#90A0B0',
      primaryShadow: '#4A5568',
      accent: '#F04060',
      accentShadow: '#DC143C',
      background: '#0A0A0C',
      cardBackground: '#141418',
      text: '#E8E8E8',
      extra: '#98A8B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dubai Gold',
    tags: ['city', 'luxurious', 'opulent', 'modern'],
    light: {
      primary: '#D4AF37',
      primaryShadow: '#AA8C2C',
      accent: '#1E90FF',
      accentShadow: '#1873CC',
      background: '#FFFEF8',
      cardBackground: '#F8F0E0',
      text: '#2A2010',
      extra: '#B8860B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F0C850',
      primaryShadow: '#D4AF37',
      accent: '#50B0FF',
      accentShadow: '#1E90FF',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F8F0E0',
      extra: '#E0B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sydney Harbor',
    tags: ['city', 'coastal', 'bright', 'fresh'],
    light: {
      primary: '#0077B6',
      primaryShadow: '#005F92',
      accent: '#FF8C00',
      accentShadow: '#CC7000',
      background: '#F0FAFF',
      cardBackground: '#E0F5FF',
      text: '#1A2530',
      extra: '#00B4D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40A0E0',
      primaryShadow: '#0077B6',
      accent: '#FFB040',
      accentShadow: '#FF8C00',
      background: '#081015',
      cardBackground: '#101A22',
      text: '#E0F5FF',
      extra: '#40D0F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rio Carnival',
    tags: ['city', 'festive', 'tropical', 'vibrant'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#00AA00',
      accentShadow: '#008800',
      background: '#FFFFF8',
      cardBackground: '#FFF8E0',
      text: '#2A2A10',
      extra: '#FF4500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#40D040',
      accentShadow: '#00AA00',
      background: '#0A0A05',
      cardBackground: '#151208',
      text: '#FFF8E0',
      extra: '#FF6830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amsterdam Canal',
    tags: ['city', 'charming', 'cozy', 'traditional'],
    light: {
      primary: '#B34D00',
      primaryShadow: '#8F3E00',
      accent: '#2E8B57',
      accentShadow: '#256F46',
      background: '#FAF8F5',
      cardBackground: '#F0E8E0',
      text: '#2A2420',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E07830',
      primaryShadow: '#B34D00',
      accent: '#60B880',
      accentShadow: '#2E8B57',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0E8E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Venice Sunset',
    tags: ['city', 'romantic', 'warm', 'historic'],
    light: {
      primary: '#E2725B',
      primaryShadow: '#B55B49',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#FFF8F5',
      cardBackground: '#FFF0E8',
      text: '#2A1A18',
      extra: '#F4A460',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F09080',
      primaryShadow: '#E2725B',
      accent: '#70A8D0',
      accentShadow: '#4682B4',
      background: '#0C0808',
      cardBackground: '#151010',
      text: '#FFF0E8',
      extra: '#F8C080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'San Francisco Tech',
    tags: ['city', 'modern', 'innovative', 'clean'],
    light: {
      primary: '#0066CC',
      primaryShadow: '#0052A3',
      accent: '#FF6B6B',
      accentShadow: '#CC5656',
      background: '#FAFAFA',
      cardBackground: '#F0F0F5',
      text: '#1A2030',
      extra: '#34C759',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4090F0',
      primaryShadow: '#0066CC',
      accent: '#FF8888',
      accentShadow: '#FF6B6B',
      background: '#0A0A0C',
      cardBackground: '#121218',
      text: '#F0F0F5',
      extra: '#50E070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Emotion Themes
  {
    name: 'Joy',
    tags: ['emotion', 'happy', 'bright', 'cheerful'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#FF69B4',
      accentShadow: '#CC5490',
      background: '#FFFFF5',
      cardBackground: '#FFF8E0',
      text: '#2A2810',
      extra: '#00CED1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#FF88C8',
      accentShadow: '#FF69B4',
      background: '#0C0A05',
      cardBackground: '#151208',
      text: '#FFF8E0',
      extra: '#40E8E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serenity',
    tags: ['emotion', 'calm', 'peaceful', 'tranquil'],
    light: {
      primary: '#87CEEB',
      primaryShadow: '#6CA5BC',
      accent: '#98D8C8',
      accentShadow: '#7AADA0',
      background: '#F5FFFE',
      cardBackground: '#E8F8F8',
      text: '#1A2828',
      extra: '#B0E0E6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A8E0F8',
      primaryShadow: '#87CEEB',
      accent: '#B0F0E0',
      accentShadow: '#98D8C8',
      background: '#080C0C',
      cardBackground: '#101818',
      text: '#E8F8F8',
      extra: '#C8F0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Passion',
    tags: ['emotion', 'intense', 'romantic', 'deep'],
    light: {
      primary: '#DC143C',
      primaryShadow: '#B0102F',
      accent: '#800020',
      accentShadow: '#66001A',
      background: '#FFF8F5',
      cardBackground: '#FFF0E8',
      text: '#2A1510',
      extra: '#FF4500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F04060',
      primaryShadow: '#DC143C',
      accent: '#B04060',
      accentShadow: '#800020',
      background: '#100505',
      cardBackground: '#1A0808',
      text: '#FFF0E8',
      extra: '#FF6830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Melancholy',
    tags: ['emotion', 'moody', 'reflective', 'subdued'],
    light: {
      primary: '#4A5568',
      primaryShadow: '#3B4453',
      accent: '#718096',
      accentShadow: '#5A6678',
      background: '#F5F5F8',
      cardBackground: '#E8E8EE',
      text: '#1A1A25',
      extra: '#A0AEC0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#90A0B0',
      primaryShadow: '#4A5568',
      accent: '#A0B0C0',
      accentShadow: '#718096',
      background: '#08080A',
      cardBackground: '#101015',
      text: '#E8E8EE',
      extra: '#B8C8D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Excitement',
    tags: ['emotion', 'energetic', 'dynamic', 'bold'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#9400D3',
      accentShadow: '#7600A9',
      background: '#FFFAF5',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6830',
      primaryShadow: '#FF4500',
      accent: '#B040F0',
      accentShadow: '#9400D3',
      background: '#100805',
      cardBackground: '#1A1008',
      text: '#FFF0E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tranquility',
    tags: ['emotion', 'zen', 'minimal', 'peaceful'],
    light: {
      primary: '#7FCDCD',
      primaryShadow: '#66A4A4',
      accent: '#DDA0DD',
      accentShadow: '#B180B1',
      background: '#FAFFFE',
      cardBackground: '#F0FAF8',
      text: '#1A2525',
      extra: '#B0E0E6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0E8E8',
      primaryShadow: '#7FCDCD',
      accent: '#F0C0F0',
      accentShadow: '#DDA0DD',
      background: '#080A0A',
      cardBackground: '#101515',
      text: '#F0FAF8',
      extra: '#C8F0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Energy',
    tags: ['emotion', 'vibrant', 'powerful', 'electric'],
    light: {
      primary: '#32CD32',
      primaryShadow: '#28A428',
      accent: '#00BFFF',
      accentShadow: '#0099CC',
      background: '#F8FFF8',
      cardBackground: '#E8F8E8',
      text: '#1A2A1A',
      extra: '#FFFF00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#60F060',
      primaryShadow: '#32CD32',
      accent: '#40D0FF',
      accentShadow: '#00BFFF',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E8F8E8',
      extra: '#FFFF50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Romance',
    tags: ['emotion', 'loving', 'soft', 'warm'],
    light: {
      primary: '#FF6B9D',
      primaryShadow: '#CC567E',
      accent: '#C9A0DC',
      accentShadow: '#A180B0',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#3A1A28',
      extra: '#FFB6C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF90B8',
      primaryShadow: '#FF6B9D',
      accent: '#E0C0F0',
      accentShadow: '#C9A0DC',
      background: '#120810',
      cardBackground: '#1A1018',
      text: '#FFF0F5',
      extra: '#FFC8D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystery',
    tags: ['emotion', 'enigmatic', 'dark', 'intriguing'],
    light: {
      primary: '#4B0082',
      primaryShadow: '#3C0068',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#F8F5FA',
      cardBackground: '#F0E8F0',
      text: '#1A1520',
      extra: '#9370DB',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8060C0',
      primaryShadow: '#4B0082',
      accent: '#808080',
      accentShadow: '#606060',
      background: '#08050A',
      cardBackground: '#100A12',
      text: '#F0E8F0',
      extra: '#B090F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hope',
    tags: ['emotion', 'optimistic', 'uplifting', 'bright'],
    light: {
      primary: '#87CEEB',
      primaryShadow: '#6CA5BC',
      accent: '#90EE90',
      accentShadow: '#73BE73',
      background: '#F8FFFF',
      cardBackground: '#E8F8F8',
      text: '#1A2828',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A8E0F8',
      primaryShadow: '#87CEEB',
      accent: '#A8F8A8',
      accentShadow: '#90EE90',
      background: '#080C0C',
      cardBackground: '#101818',
      text: '#E8F8F8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Fabric & Textile Themes
  {
    name: 'Velvet',
    tags: ['fabric', 'luxurious', 'rich', 'elegant'],
    light: {
      primary: '#722F37',
      primaryShadow: '#5B262C',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FAF5F5',
      cardBackground: '#F0E8E8',
      text: '#2A1818',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A06070',
      primaryShadow: '#722F37',
      accent: '#F0C850',
      accentShadow: '#D4AF37',
      background: '#0A0606',
      cardBackground: '#150A0A',
      text: '#F0E8E8',
      extra: '#C04040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Denim',
    tags: ['fabric', 'casual', 'classic', 'rugged'],
    light: {
      primary: '#1560BD',
      primaryShadow: '#114D97',
      accent: '#CD853F',
      accentShadow: '#A46A32',
      background: '#F8FAFF',
      cardBackground: '#E8F0F8',
      text: '#1A2030',
      extra: '#4682B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5090E0',
      primaryShadow: '#1560BD',
      accent: '#E0A060',
      accentShadow: '#CD853F',
      background: '#080A10',
      cardBackground: '#101520',
      text: '#E8F0F8',
      extra: '#70A8D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silk',
    tags: ['fabric', 'elegant', 'smooth', 'refined'],
    light: {
      primary: '#E6C3C3',
      primaryShadow: '#B89C9C',
      accent: '#C0A080',
      accentShadow: '#9A8066',
      background: '#FFFAF8',
      cardBackground: '#F8F0EC',
      text: '#2A2020',
      extra: '#DDA0DD',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#F0D8D8',
      primaryShadow: '#E6C3C3',
      accent: '#D8C0A0',
      accentShadow: '#C0A080',
      background: '#0C0808',
      cardBackground: '#151010',
      text: '#F8F0EC',
      extra: '#E8C0E8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Linen',
    tags: ['fabric', 'natural', 'light', 'organic'],
    light: {
      primary: '#C4B8A8',
      primaryShadow: '#9D9386',
      accent: '#8B7355',
      accentShadow: '#6F5C44',
      background: '#FAF8F5',
      cardBackground: '#F0ECE5',
      text: '#2A2820',
      extra: '#D2B48C',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E0D0C0',
      primaryShadow: '#C4B8A8',
      accent: '#B09070',
      accentShadow: '#8B7355',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0ECE5',
      extra: '#E8D0A8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Leather',
    tags: ['fabric', 'rugged', 'classic', 'masculine'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FAF5F0',
      cardBackground: '#F0E8D8',
      text: '#2A1A15',
      extra: '#D2691E',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#808080',
      accentShadow: '#606060',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F0E8D8',
      extra: '#E08840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wool',
    tags: ['fabric', 'warm', 'cozy', 'textured'],
    light: {
      primary: '#B89470',
      primaryShadow: '#93765A',
      accent: '#5C4033',
      accentShadow: '#4A3329',
      background: '#FAF8F5',
      cardBackground: '#F0E8E0',
      text: '#2A2420',
      extra: '#D2B48C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D0B090',
      primaryShadow: '#B89470',
      accent: '#8A6850',
      accentShadow: '#5C4033',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0E8E0',
      extra: '#E8D0A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cotton',
    tags: ['fabric', 'clean', 'fresh', 'soft'],
    light: {
      primary: '#F5F5F5',
      primaryShadow: '#D0D0D0',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#2A2A2A',
      extra: '#E8E8E8',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#404040',
      primaryShadow: '#2A2A2A',
      accent: '#A8E0F8',
      accentShadow: '#87CEEB',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#303030',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Suede',
    tags: ['fabric', 'soft', 'luxurious', 'earthy'],
    light: {
      primary: '#9F8170',
      primaryShadow: '#7F685A',
      accent: '#4A3728',
      accentShadow: '#3B2C20',
      background: '#FAF8F5',
      cardBackground: '#F0EAE0',
      text: '#2A2420',
      extra: '#C4A484',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C0A090',
      primaryShadow: '#9F8170',
      accent: '#806048',
      accentShadow: '#4A3728',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0EAE0',
      extra: '#E0C8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Landscape Themes
  {
    name: 'Desert Dunes',
    tags: ['landscape', 'sandy', 'warm', 'vast'],
    light: {
      primary: '#C2B280',
      primaryShadow: '#9B8E66',
      accent: '#D2691E',
      accentShadow: '#A85418',
      background: '#FFF8F0',
      cardBackground: '#F8F0E0',
      text: '#3A3020',
      extra: '#DEB887',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0D0A0',
      primaryShadow: '#C2B280',
      accent: '#F08840',
      accentShadow: '#D2691E',
      background: '#0C0A05',
      cardBackground: '#151008',
      text: '#F8F0E0',
      extra: '#F0D0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mountain Peak',
    tags: ['landscape', 'majestic', 'alpine', 'crisp'],
    light: {
      primary: '#4A5568',
      primaryShadow: '#3B4453',
      accent: '#48BB78',
      accentShadow: '#3A9660',
      background: '#F8FAFA',
      cardBackground: '#E8F0F0',
      text: '#1A2020',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#90A0B0',
      primaryShadow: '#4A5568',
      accent: '#70D098',
      accentShadow: '#48BB78',
      background: '#080A0A',
      cardBackground: '#101515',
      text: '#E8F0F0',
      extra: '#A8E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Beach',
    tags: ['landscape', 'coastal', 'relaxing', 'vibrant'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#FF6B6B',
      accentShadow: '#CC5656',
      background: '#F0FFFF',
      cardBackground: '#E0F8F8',
      text: '#1A2A30',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8E8',
      primaryShadow: '#00CED1',
      accent: '#FF8888',
      accentShadow: '#FF6B6B',
      background: '#080C0C',
      cardBackground: '#101818',
      text: '#E0F8F8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enchanted Forest',
    tags: ['landscape', 'mystical', 'green', 'deep'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#F5FAF5',
      cardBackground: '#E0EEE0',
      text: '#1A2818',
      extra: '#90EE90',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50C050',
      primaryShadow: '#228B22',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E0EEE0',
      extra: '#A8F8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Arctic Tundra',
    tags: ['landscape', 'cold', 'pristine', 'minimal'],
    light: {
      primary: '#B0C4DE',
      primaryShadow: '#8D9DB2',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#F8FAFF',
      cardBackground: '#E8F0FA',
      text: '#1A2030',
      extra: '#E0E8F0',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#C8D8F0',
      primaryShadow: '#B0C4DE',
      accent: '#70A8D0',
      accentShadow: '#4682B4',
      background: '#080A10',
      cardBackground: '#101520',
      text: '#E8F0FA',
      extra: '#E8F0F8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Rainforest',
    tags: ['landscape', 'lush', 'tropical', 'dense'],
    light: {
      primary: '#006400',
      primaryShadow: '#005000',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#F0FFF0',
      cardBackground: '#E0F0E0',
      text: '#1A2A1A',
      extra: '#32CD32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40A040',
      primaryShadow: '#006400',
      accent: '#C04040',
      accentShadow: '#8B0000',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E0F0E0',
      extra: '#60E060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Savanna',
    tags: ['landscape', 'warm', 'golden', 'vast'],
    light: {
      primary: '#CD853F',
      primaryShadow: '#A46A32',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFF8F0',
      cardBackground: '#F8F0E0',
      text: '#2A2010',
      extra: '#DAA520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A060',
      primaryShadow: '#CD853F',
      accent: '#50C050',
      accentShadow: '#228B22',
      background: '#0C0A05',
      cardBackground: '#151008',
      text: '#F8F0E0',
      extra: '#F0C040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Grand Canyon',
    tags: ['landscape', 'dramatic', 'layered', 'ancient'],
    light: {
      primary: '#B35A2D',
      primaryShadow: '#8F4824',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFF5F0',
      cardBackground: '#F8E8E0',
      text: '#2A1810',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E08050',
      primaryShadow: '#B35A2D',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0605',
      cardBackground: '#150A08',
      text: '#F8E8E0',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Meadow',
    tags: ['landscape', 'pastoral', 'fresh', 'spring'],
    light: {
      primary: '#7CB342',
      primaryShadow: '#638F35',
      accent: '#FFC107',
      accentShadow: '#CC9A06',
      background: '#F8FFF5',
      cardBackground: '#E8F8E0',
      text: '#1A2A18',
      extra: '#9CCC65',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0D868',
      primaryShadow: '#7CB342',
      accent: '#FFD840',
      accentShadow: '#FFC107',
      background: '#080A08',
      cardBackground: '#101510',
      text: '#E8F8E0',
      extra: '#B8E080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Volcano',
    tags: ['landscape', 'dramatic', 'fiery', 'powerful'],
    light: {
      primary: '#D32F2F',
      primaryShadow: '#A92626',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFF5F5',
      cardBackground: '#F8E8E0',
      text: '#2A1515',
      extra: '#FF6B00',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F05050',
      primaryShadow: '#D32F2F',
      accent: '#808080',
      accentShadow: '#606060',
      background: '#0A0505',
      cardBackground: '#150808',
      text: '#F8E8E0',
      extra: '#FF9040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Music Genre Themes
  {
    name: 'Jazz Club',
    tags: ['music', 'sophisticated', 'smoky', 'classic'],
    light: {
      primary: '#1A1A2E',
      primaryShadow: '#141425',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F8F5F0',
      cardBackground: '#E8E0D8',
      text: '#1A1A1A',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40405A',
      primaryShadow: '#1A1A2E',
      accent: '#F0C850',
      accentShadow: '#D4AF37',
      background: '#0A0A0C',
      cardBackground: '#141418',
      text: '#E8E0D8',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classical',
    tags: ['music', 'elegant', 'refined', 'timeless'],
    light: {
      primary: '#2C2C2C',
      primaryShadow: '#1A1A1A',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#FFFEF8',
      cardBackground: '#F8F0E8',
      text: '#1A1A1A',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0B0B0',
      primaryShadow: '#808080',
      accent: '#C04040',
      accentShadow: '#8B0000',
      background: '#0A0A08',
      cardBackground: '#141410',
      text: '#F8F0E8',
      extra: '#F0C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rock Concert',
    tags: ['music', 'bold', 'energetic', 'rebellious'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#FF0000',
      accentShadow: '#CC0000',
      background: '#F8F5F5',
      cardBackground: '#E8E0E0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#B0B0B0',
      primaryShadow: '#808080',
      accent: '#FF4040',
      accentShadow: '#FF0000',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E0E0',
      extra: '#FFE840',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Electronic',
    tags: ['music', 'futuristic', 'neon', 'synthetic'],
    light: {
      primary: '#00FFFF',
      primaryShadow: '#00CCCC',
      accent: '#FF00FF',
      accentShadow: '#CC00CC',
      background: '#0A0A1A',
      cardBackground: '#101028',
      text: '#E8E8FF',
      extra: '#00FF00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40FFFF',
      primaryShadow: '#00FFFF',
      accent: '#FF40FF',
      accentShadow: '#FF00FF',
      background: '#05050A',
      cardBackground: '#0A0A14',
      text: '#E8E8FF',
      extra: '#40FF40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Country',
    tags: ['music', 'rustic', 'warm', 'americana'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#CD853F',
      accentShadow: '#A46A32',
      background: '#FFF8F0',
      cardBackground: '#F8F0E0',
      text: '#2A2010',
      extra: '#D2691E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#E0A060',
      accentShadow: '#CD853F',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F8F0E0',
      extra: '#F08840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blues',
    tags: ['music', 'soulful', 'deep', 'emotional'],
    light: {
      primary: '#1E3A5F',
      primaryShadow: '#182E4C',
      accent: '#4169E1',
      accentShadow: '#3454B4',
      background: '#F8FAFF',
      cardBackground: '#E8F0F8',
      text: '#1A2030',
      extra: '#6495ED',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4080C0',
      primaryShadow: '#1E3A5F',
      accent: '#6090F0',
      accentShadow: '#4169E1',
      background: '#080A10',
      cardBackground: '#101520',
      text: '#E8F0F8',
      extra: '#88B0F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Reggae',
    tags: ['music', 'tropical', 'chill', 'vibrant'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F8FFF5',
      cardBackground: '#E8F8E0',
      text: '#1A2A18',
      extra: '#FF0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50C050',
      primaryShadow: '#228B22',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E8F8E0',
      extra: '#FF4040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hip Hop',
    tags: ['music', 'urban', 'bold', 'contemporary'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#FF6600',
      accentShadow: '#CC5200',
      background: '#F8F8F5',
      cardBackground: '#E8E8E0',
      text: '#1A1A1A',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0B0B0',
      primaryShadow: '#808080',
      accent: '#FF8840',
      accentShadow: '#FF6600',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E0',
      extra: '#F0C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Folk',
    tags: ['music', 'acoustic', 'natural', 'storytelling'],
    light: {
      primary: '#6B4423',
      primaryShadow: '#56361C',
      accent: '#8FBC8F',
      accentShadow: '#739673',
      background: '#FAF8F5',
      cardBackground: '#F0EBE4',
      text: '#2A2420',
      extra: '#DEB887',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A07850',
      primaryShadow: '#6B4423',
      accent: '#A8D8A8',
      accentShadow: '#8FBC8F',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0EBE4',
      extra: '#F0D0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Punk Rock',
    tags: ['music', 'rebellious', 'raw', 'energetic'],
    light: {
      primary: '#FF1493',
      primaryShadow: '#CC1076',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#F8F5F5',
      cardBackground: '#E8E0E0',
      text: '#1A1A1A',
      extra: '#00FF00',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF50B0',
      primaryShadow: '#FF1493',
      accent: '#808080',
      accentShadow: '#606060',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E0E0',
      extra: '#40FF40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Cocktail Themes
  {
    name: 'Mojito',
    tags: ['cocktail', 'refreshing', 'tropical', 'minty'],
    light: {
      primary: '#98FB98',
      primaryShadow: '#7AC97A',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#F5FFFA',
      cardBackground: '#E8FAE8',
      text: '#1A3020',
      extra: '#00FF7F',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B8FFB8',
      primaryShadow: '#98FB98',
      accent: '#F0F0F0',
      accentShadow: '#D0D0D0',
      background: '#081008',
      cardBackground: '#101A10',
      text: '#E8FAE8',
      extra: '#40FF90',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Martini',
    tags: ['cocktail', 'sophisticated', 'classic', 'elegant'],
    light: {
      primary: '#C0C0C0',
      primaryShadow: '#9A9A9A',
      accent: '#90EE90',
      accentShadow: '#73BE73',
      background: '#FAFAFA',
      cardBackground: '#F0F0F0',
      text: '#1A1A1A',
      extra: '#E8E8E8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#C0C0C0',
      accent: '#A8F8A8',
      accentShadow: '#90EE90',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#F0F0F0',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Margarita',
    tags: ['cocktail', 'festive', 'citrus', 'vibrant'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#32CD32',
      accentShadow: '#28A428',
      background: '#FFFFF5',
      cardBackground: '#FFF8E0',
      text: '#2A2810',
      extra: '#FF6347',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#60E060',
      accentShadow: '#32CD32',
      background: '#0C0A05',
      cardBackground: '#151208',
      text: '#FFF8E0',
      extra: '#FF8870',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Old Fashioned',
    tags: ['cocktail', 'classic', 'warm', 'rich'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#FF8C00',
      accentShadow: '#CC7000',
      background: '#FAF5F0',
      cardBackground: '#F0E8D8',
      text: '#2A1A15',
      extra: '#D2691E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#FFB040',
      accentShadow: '#FF8C00',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F0E8D8',
      extra: '#F08840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cosmopolitan',
    tags: ['cocktail', 'glamorous', 'pink', 'chic'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#FF6347',
      accentShadow: '#CC4F39',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#3A1A28',
      extra: '#DC143C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#FF8870',
      accentShadow: '#FF6347',
      background: '#120810',
      cardBackground: '#1A1018',
      text: '#FFF0F5',
      extra: '#F04060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Piña Colada',
    tags: ['cocktail', 'tropical', 'creamy', 'sweet'],
    light: {
      primary: '#FFFACD',
      primaryShadow: '#CCC8A4',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFF5',
      cardBackground: '#FFF8E0',
      text: '#3A3820',
      extra: '#228B22',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#4A4820',
      primaryShadow: '#3A3818',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0C0A05',
      cardBackground: '#151208',
      text: '#FFF8E0',
      extra: '#50C050',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Negroni',
    tags: ['cocktail', 'bitter', 'bold', 'italian'],
    light: {
      primary: '#DC143C',
      primaryShadow: '#B0102F',
      accent: '#FF4500',
      accentShadow: '#CC3700',
      background: '#FFF5F5',
      cardBackground: '#F8E8E0',
      text: '#2A1515',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F04060',
      primaryShadow: '#DC143C',
      accent: '#FF6830',
      accentShadow: '#FF4500',
      background: '#100505',
      cardBackground: '#1A0808',
      text: '#F8E8E0',
      extra: '#C04040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Moscow Mule',
    tags: ['cocktail', 'refreshing', 'copper', 'zesty'],
    light: {
      primary: '#B87333',
      primaryShadow: '#935C29',
      accent: '#90EE90',
      accentShadow: '#73BE73',
      background: '#FFF8F0',
      cardBackground: '#F8F0E0',
      text: '#2A2010',
      extra: '#CD7F32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D89858',
      primaryShadow: '#B87333',
      accent: '#A8F8A8',
      accentShadow: '#90EE90',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F8F0E0',
      extra: '#E8A058',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Literature Themes
  {
    name: 'Gothic Novel',
    tags: ['literature', 'dark', 'mysterious', 'romantic'],
    light: {
      primary: '#4A0E4E',
      primaryShadow: '#3B0B3F',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#FAF8FA',
      cardBackground: '#F0E8F0',
      text: '#1A1520',
      extra: '#2F2F4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8050A0',
      primaryShadow: '#4A0E4E',
      accent: '#C04040',
      accentShadow: '#8B0000',
      background: '#08050A',
      cardBackground: '#100A12',
      text: '#F0E8F0',
      extra: '#505080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sci-Fi Novel',
    tags: ['literature', 'futuristic', 'cosmic', 'tech'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#9400D3',
      accentShadow: '#7600A9',
      background: '#F0FAFF',
      cardBackground: '#E0F5FF',
      text: '#1A2530',
      extra: '#00BFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8E8',
      primaryShadow: '#00CED1',
      accent: '#B040F0',
      accentShadow: '#9400D3',
      background: '#080C10',
      cardBackground: '#101820',
      text: '#E0F5FF',
      extra: '#40D0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Romance Novel',
    tags: ['literature', 'romantic', 'soft', 'passionate'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#DDA0DD',
      accentShadow: '#B180B1',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#3A1A28',
      extra: '#FFB6C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#F0C0F0',
      accentShadow: '#DDA0DD',
      background: '#120810',
      cardBackground: '#1A1018',
      text: '#FFF0F5',
      extra: '#FFC8D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystery Novel',
    tags: ['literature', 'suspenseful', 'dark', 'intriguing'],
    light: {
      primary: '#2F4F4F',
      primaryShadow: '#253F3F',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#F8F8F5',
      cardBackground: '#E8E8E0',
      text: '#1A1A1A',
      extra: '#4A4A4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#608080',
      primaryShadow: '#2F4F4F',
      accent: '#C04040',
      accentShadow: '#8B0000',
      background: '#0A0A08',
      cardBackground: '#141410',
      text: '#E8E8E0',
      extra: '#808080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fantasy Epic',
    tags: ['literature', 'magical', 'epic', 'adventurous'],
    light: {
      primary: '#4B0082',
      primaryShadow: '#3C0068',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F8F5FA',
      cardBackground: '#F0E8F0',
      text: '#1A1520',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8060C0',
      primaryShadow: '#4B0082',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#08050A',
      cardBackground: '#100A12',
      text: '#F0E8F0',
      extra: '#50C050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Poetry',
    tags: ['literature', 'artistic', 'expressive', 'elegant'],
    light: {
      primary: '#6B5B95',
      primaryShadow: '#564A78',
      accent: '#F7CAC9',
      accentShadow: '#C5A2A1',
      background: '#FAFAF8',
      cardBackground: '#F0F0EA',
      text: '#2A2830',
      extra: '#92A8D1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8E7DB8',
      primaryShadow: '#6B5B95',
      accent: '#F8E0E0',
      accentShadow: '#F7CAC9',
      background: '#0A0A0C',
      cardBackground: '#141418',
      text: '#F0F0EA',
      extra: '#A8C0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Children Book',
    tags: ['literature', 'playful', 'colorful', 'whimsical'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#4ECDC4',
      accentShadow: '#3EA49D',
      background: '#FFFDF8',
      cardBackground: '#FFF8F0',
      text: '#2A2A3A',
      extra: '#FFE66D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8888',
      primaryShadow: '#FF6B6B',
      accent: '#70E8E0',
      accentShadow: '#4ECDC4',
      background: '#0A0A10',
      cardBackground: '#121218',
      text: '#FFF8F0',
      extra: '#FFF090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noir Detective',
    tags: ['literature', 'gritty', 'urban', 'dramatic'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#C0C0C0',
      accentShadow: '#9A9A9A',
      background: '#F5F5F0',
      cardBackground: '#E8E8E0',
      text: '#1A1A1A',
      extra: '#4A4A4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#B0B0B0',
      accent: '#808080',
      accentShadow: '#606060',
      background: '#0A0A0A',
      cardBackground: '#141414',
      text: '#E8E8E0',
      extra: '#909090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Planet Themes
  {
    name: 'Mercury',
    tags: ['planet', 'metallic', 'extreme', 'cratered'],
    light: {
      primary: '#708090',
      primaryShadow: '#5A6673',
      accent: '#FF6347',
      accentShadow: '#CC4F39',
      background: '#F8F8FA',
      cardBackground: '#E8E8F0',
      text: '#1A1A20',
      extra: '#A0A0A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#98A8B8',
      primaryShadow: '#708090',
      accent: '#FF8870',
      accentShadow: '#FF6347',
      background: '#08080A',
      cardBackground: '#101018',
      text: '#E8E8F0',
      extra: '#B8B8C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Venus',
    tags: ['planet', 'volcanic', 'hot', 'acidic'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#FF8C00',
      accentShadow: '#CC7000',
      background: '#FFF8F0',
      cardBackground: '#F8F0E0',
      text: '#2A2010',
      extra: '#FFA500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#FFB040',
      accentShadow: '#FF8C00',
      background: '#0C0A05',
      cardBackground: '#151008',
      text: '#F8F0E0',
      extra: '#FFB840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mars',
    tags: ['planet', 'red', 'dusty', 'ancient'],
    light: {
      primary: '#CD5C5C',
      primaryShadow: '#A44A4A',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFF5F0',
      cardBackground: '#F8E8E0',
      text: '#2A1815',
      extra: '#B22222',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E08080',
      primaryShadow: '#CD5C5C',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0605',
      cardBackground: '#150A08',
      text: '#F8E8E0',
      extra: '#D04040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jupiter',
    tags: ['planet', 'giant', 'stormy', 'banded'],
    light: {
      primary: '#CD853F',
      primaryShadow: '#A46A32',
      accent: '#DEB887',
      accentShadow: '#B29368',
      background: '#FFF8F0',
      cardBackground: '#F8F0E0',
      text: '#2A2010',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A060',
      primaryShadow: '#CD853F',
      accent: '#F0D0A0',
      accentShadow: '#DEB887',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F8F0E0',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Saturn',
    tags: ['planet', 'ringed', 'golden', 'majestic'],
    light: {
      primary: '#DAA520',
      primaryShadow: '#AE841A',
      accent: '#DEB887',
      accentShadow: '#B29368',
      background: '#FFF8E8',
      cardBackground: '#F8F0D8',
      text: '#2A2010',
      extra: '#B8860B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F0C040',
      primaryShadow: '#DAA520',
      accent: '#F0D0A0',
      accentShadow: '#DEB887',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F8F0D8',
      extra: '#E0A840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Uranus',
    tags: ['planet', 'icy', 'tilted', 'cyan'],
    light: {
      primary: '#40E0D0',
      primaryShadow: '#33B3A6',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#F0FFFF',
      cardBackground: '#E0F8F8',
      text: '#1A2828',
      extra: '#00CED1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#70F0E0',
      primaryShadow: '#40E0D0',
      accent: '#A8E0F8',
      accentShadow: '#87CEEB',
      background: '#080C0C',
      cardBackground: '#101818',
      text: '#E0F8F8',
      extra: '#40E8E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neptune',
    tags: ['planet', 'blue', 'windy', 'distant'],
    light: {
      primary: '#1E90FF',
      primaryShadow: '#1873CC',
      accent: '#4169E1',
      accentShadow: '#3454B4',
      background: '#F0FAFF',
      cardBackground: '#E0F0FF',
      text: '#1A2030',
      extra: '#0000CD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50B0FF',
      primaryShadow: '#1E90FF',
      accent: '#6090F0',
      accentShadow: '#4169E1',
      background: '#080A10',
      cardBackground: '#101520',
      text: '#E0F0FF',
      extra: '#4040E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pluto',
    tags: ['planet', 'dwarf', 'frozen', 'distant'],
    light: {
      primary: '#A0522D',
      primaryShadow: '#804224',
      accent: '#B0C4DE',
      accentShadow: '#8D9DB2',
      background: '#FAF8F5',
      cardBackground: '#F0E8E0',
      text: '#2A2420',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C07850',
      primaryShadow: '#A0522D',
      accent: '#C8D8F0',
      accentShadow: '#B0C4DE',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0E8E0',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Instrument Themes
  {
    name: 'Piano',
    tags: ['instrument', 'classic', 'elegant', 'musical'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#FAFAFA',
      cardBackground: '#F0F0F0',
      text: '#1A1A1A',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#B0B0B0',
      accent: '#303030',
      accentShadow: '#1A1A1A',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#F0C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Guitar',
    tags: ['instrument', 'warm', 'acoustic', 'wooden'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#CD853F',
      accentShadow: '#A46A32',
      background: '#FAF5F0',
      cardBackground: '#F0E8D8',
      text: '#2A1A15',
      extra: '#D2691E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#E0A060',
      accentShadow: '#CD853F',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F0E8D8',
      extra: '#F08840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violin',
    tags: ['instrument', 'elegant', 'classical', 'rich'],
    light: {
      primary: '#722F37',
      primaryShadow: '#5B262C',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FAF5F5',
      cardBackground: '#F0E8E8',
      text: '#2A1818',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A06070',
      primaryShadow: '#722F37',
      accent: '#F0C850',
      accentShadow: '#D4AF37',
      background: '#0A0606',
      cardBackground: '#150A0A',
      text: '#F0E8E8',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Drums',
    tags: ['instrument', 'energetic', 'bold', 'rhythmic'],
    light: {
      primary: '#DC143C',
      primaryShadow: '#B0102F',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FAF5F5',
      cardBackground: '#F0E8E0',
      text: '#2A1515',
      extra: '#1A1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F04060',
      primaryShadow: '#DC143C',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0A0505',
      cardBackground: '#150808',
      text: '#F0E8E0',
      extra: '#808080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Saxophone',
    tags: ['instrument', 'jazzy', 'smooth', 'brass'],
    light: {
      primary: '#B8860B',
      primaryShadow: '#936B09',
      accent: '#4169E1',
      accentShadow: '#3454B4',
      background: '#FFF8F0',
      cardBackground: '#F8F0E0',
      text: '#2A2010',
      extra: '#DAA520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A840',
      primaryShadow: '#B8860B',
      accent: '#6090F0',
      accentShadow: '#4169E1',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F8F0E0',
      extra: '#F0C040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Trumpet',
    tags: ['instrument', 'bold', 'bright', 'brass'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#DC143C',
      accentShadow: '#B0102F',
      background: '#FFFAF0',
      cardBackground: '#F8F0D8',
      text: '#2A2010',
      extra: '#B8860B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#F04060',
      accentShadow: '#DC143C',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F8F0D8',
      extra: '#E0A840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Mineral Themes
  {
    name: 'Granite',
    tags: ['mineral', 'solid', 'speckled', 'natural'],
    light: {
      primary: '#808080',
      primaryShadow: '#666666',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#F8F8F8',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#A0A0A0',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0B0B0',
      primaryShadow: '#808080',
      accent: '#505050',
      accentShadow: '#303030',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E8',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble',
    tags: ['mineral', 'elegant', 'veined', 'luxurious'],
    light: {
      primary: '#F5F5F5',
      primaryShadow: '#D0D0D0',
      accent: '#708090',
      accentShadow: '#5A6673',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#2A2A2A',
      extra: '#E8E8E8',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#404040',
      primaryShadow: '#2A2A2A',
      accent: '#98A8B8',
      accentShadow: '#708090',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#303030',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Quartz',
    tags: ['mineral', 'crystalline', 'clear', 'sparkling'],
    light: {
      primary: '#E6E6FA',
      primaryShadow: '#B8B8C8',
      accent: '#DDA0DD',
      accentShadow: '#B180B1',
      background: '#FAFAFF',
      cardBackground: '#F0F0F8',
      text: '#2A2A35',
      extra: '#D8BFD8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#404048',
      primaryShadow: '#30303A',
      accent: '#F0C0F0',
      accentShadow: '#DDA0DD',
      background: '#0A0A0C',
      cardBackground: '#141418',
      text: '#F0F0F8',
      extra: '#E8D0E8',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Obsidian',
    tags: ['mineral', 'volcanic', 'glossy', 'dark'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#708090',
      accentShadow: '#5A6673',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#2F2F2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0B0B0',
      primaryShadow: '#808080',
      accent: '#98A8B8',
      accentShadow: '#708090',
      background: '#050505',
      cardBackground: '#0A0A0A',
      text: '#E8E8E8',
      extra: '#505050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jade',
    tags: ['mineral', 'precious', 'green', 'serene'],
    light: {
      primary: '#00A86B',
      primaryShadow: '#008756',
      accent: '#2E8B57',
      accentShadow: '#256F46',
      background: '#F5FFFA',
      cardBackground: '#E0F0E8',
      text: '#1A2A20',
      extra: '#40E0D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40D090',
      primaryShadow: '#00A86B',
      accent: '#60B880',
      accentShadow: '#2E8B57',
      background: '#080C0A',
      cardBackground: '#101810',
      text: '#E0F0E8',
      extra: '#70F0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Onyx',
    tags: ['mineral', 'elegant', 'banded', 'sophisticated'],
    light: {
      primary: '#0F0F0F',
      primaryShadow: '#050505',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F8F8F8',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#2A2A2A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C0C0C0',
      primaryShadow: '#909090',
      accent: '#F0C850',
      accentShadow: '#D4AF37',
      background: '#050505',
      cardBackground: '#0A0A0A',
      text: '#E8E8E8',
      extra: '#404040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Limestone',
    tags: ['mineral', 'natural', 'warm', 'sedimentary'],
    light: {
      primary: '#D3C3A3',
      primaryShadow: '#A99C82',
      accent: '#8B7355',
      accentShadow: '#6F5C44',
      background: '#FAF8F5',
      cardBackground: '#F0EBE4',
      text: '#2A2820',
      extra: '#E8DCC8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E8D8C0',
      primaryShadow: '#D3C3A3',
      accent: '#B09070',
      accentShadow: '#8B7355',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0EBE4',
      extra: '#F0E8D8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Sandstone',
    tags: ['mineral', 'warm', 'layered', 'desert'],
    light: {
      primary: '#C2B280',
      primaryShadow: '#9B8E66',
      accent: '#CD853F',
      accentShadow: '#A46A32',
      background: '#FFF8F0',
      cardBackground: '#F8F0E0',
      text: '#2A2010',
      extra: '#DEB887',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0D0A0',
      primaryShadow: '#C2B280',
      accent: '#E0A060',
      accentShadow: '#CD853F',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F8F0E0',
      extra: '#F0D0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Tech-Inspired Themes
  {
    name: 'Search Engine',
    tags: ['tech', 'colorful', 'clean', 'modern'],
    light: {
      primary: '#4285F4',
      primaryShadow: '#356AC3',
      accent: '#EA4335',
      accentShadow: '#BB362A',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#34A853',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6AA8FF',
      primaryShadow: '#4285F4',
      accent: '#F06858',
      accentShadow: '#EA4335',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#60D080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fruit Device',
    tags: ['tech', 'minimalist', 'elegant', 'sleek'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#0071E3',
      accentShadow: '#005AB5',
      background: '#FFFFFF',
      cardBackground: '#F5F5F7',
      text: '#1A1A1A',
      extra: '#86868B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F5F5F7',
      primaryShadow: '#D0D0D0',
      accent: '#0A84FF',
      accentShadow: '#0071E3',
      background: '#0A0A0A',
      cardBackground: '#1C1C1E',
      text: '#F5F5F7',
      extra: '#A0A0A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Window System',
    tags: ['tech', 'corporate', 'blue', 'professional'],
    light: {
      primary: '#0078D4',
      primaryShadow: '#0060AA',
      accent: '#107C10',
      accentShadow: '#0C630C',
      background: '#FFFFFF',
      cardBackground: '#F3F3F3',
      text: '#1A1A1A',
      extra: '#FFB900',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40A8FF',
      primaryShadow: '#0078D4',
      accent: '#40A040',
      accentShadow: '#107C10',
      background: '#0A0A0A',
      cardBackground: '#1F1F1F',
      text: '#F0F0F0',
      extra: '#FFD040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Streaming Video',
    tags: ['tech', 'entertainment', 'bold', 'red'],
    light: {
      primary: '#E50914',
      primaryShadow: '#B70710',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#B81D24',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F04048',
      primaryShadow: '#E50914',
      accent: '#B0B0B0',
      accentShadow: '#808080',
      background: '#0A0A0A',
      cardBackground: '#141414',
      text: '#F0F0F0',
      extra: '#D03038',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Music Stream',
    tags: ['tech', 'music', 'green', 'energetic'],
    light: {
      primary: '#1DB954',
      primaryShadow: '#179443',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#1ED760',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E070',
      primaryShadow: '#1DB954',
      accent: '#B0B0B0',
      accentShadow: '#808080',
      background: '#0A0A0A',
      cardBackground: '#121212',
      text: '#F0F0F0',
      extra: '#50F080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shopping Cart',
    tags: ['tech', 'commerce', 'orange', 'friendly'],
    light: {
      primary: '#FF9900',
      primaryShadow: '#CC7A00',
      accent: '#232F3E',
      accentShadow: '#1B2531',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#146EB4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFB840',
      primaryShadow: '#FF9900',
      accent: '#506070',
      accentShadow: '#232F3E',
      background: '#0A0A0A',
      cardBackground: '#131A22',
      text: '#F0F0F0',
      extra: '#40A0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Social Network',
    tags: ['tech', 'social', 'blue', 'connected'],
    light: {
      primary: '#1877F2',
      primaryShadow: '#135FC2',
      accent: '#42B72A',
      accentShadow: '#359222',
      background: '#FFFFFF',
      cardBackground: '#F0F2F5',
      text: '#1A1A1A',
      extra: '#E4E6EB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50A0FF',
      primaryShadow: '#1877F2',
      accent: '#60D850',
      accentShadow: '#42B72A',
      background: '#0A0A0A',
      cardBackground: '#18191A',
      text: '#F0F0F0',
      extra: '#3A3B3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Micro Blog',
    tags: ['tech', 'social', 'blue', 'concise'],
    light: {
      primary: '#1DA1F2',
      primaryShadow: '#1781C2',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFFFF',
      cardBackground: '#F5F8FA',
      text: '#1A1A1A',
      extra: '#AAB8C2',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50C0FF',
      primaryShadow: '#1DA1F2',
      accent: '#B0B0B0',
      accentShadow: '#808080',
      background: '#0A0A0A',
      cardBackground: '#15202B',
      text: '#F0F0F0',
      extra: '#38444D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gamer Chat',
    tags: ['tech', 'gaming', 'purple', 'community'],
    light: {
      primary: '#5865F2',
      primaryShadow: '#4651C2',
      accent: '#EB459E',
      accentShadow: '#BC377E',
      background: '#FFFFFF',
      cardBackground: '#F2F3F5',
      text: '#1A1A1A',
      extra: '#57F287',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8090FF',
      primaryShadow: '#5865F2',
      accent: '#F070B8',
      accentShadow: '#EB459E',
      background: '#0A0A10',
      cardBackground: '#2F3136',
      text: '#F0F0F0',
      extra: '#80F0A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Professional Net',
    tags: ['tech', 'business', 'blue', 'professional'],
    light: {
      primary: '#0A66C2',
      primaryShadow: '#08529B',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFFFF',
      cardBackground: '#F3F2EF',
      text: '#1A1A1A',
      extra: '#057642',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4090E0',
      primaryShadow: '#0A66C2',
      accent: '#B0B0B0',
      accentShadow: '#808080',
      background: '#0A0A0A',
      cardBackground: '#1B1F23',
      text: '#F0F0F0',
      extra: '#40A868',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Dessert Themes
  {
    name: 'Chocolate Cake',
    tags: ['dessert', 'rich', 'sweet', 'indulgent'],
    light: {
      primary: '#3D1C02',
      primaryShadow: '#311602',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FAF5F0',
      cardBackground: '#F0E8D8',
      text: '#2A1A15',
      extra: '#D2691E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#804020',
      primaryShadow: '#3D1C02',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F0E8D8',
      extra: '#F08840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vanilla Dream',
    tags: ['dessert', 'soft', 'sweet', 'creamy'],
    light: {
      primary: '#F3E5AB',
      primaryShadow: '#C2B789',
      accent: '#D2B48C',
      accentShadow: '#A89070',
      background: '#FFFFF8',
      cardBackground: '#FFF8E8',
      text: '#3A3020',
      extra: '#FFE4B5',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#504838',
      primaryShadow: '#403828',
      accent: '#E8D0A8',
      accentShadow: '#D2B48C',
      background: '#0C0A05',
      cardBackground: '#151008',
      text: '#FFF8E8',
      extra: '#FFF0C0',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Strawberry Swirl',
    tags: ['dessert', 'fruity', 'sweet', 'pink'],
    light: {
      primary: '#FF6B8A',
      primaryShadow: '#CC566E',
      accent: '#FFB6C1',
      accentShadow: '#CC929A',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#3A1A28',
      extra: '#FF1493',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF90A8',
      primaryShadow: '#FF6B8A',
      accent: '#FFC8D0',
      accentShadow: '#FFB6C1',
      background: '#120810',
      cardBackground: '#1A1018',
      text: '#FFF0F5',
      extra: '#FF50B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tiramisu',
    tags: ['dessert', 'coffee', 'italian', 'elegant'],
    light: {
      primary: '#6B4423',
      primaryShadow: '#56361C',
      accent: '#D2B48C',
      accentShadow: '#A89070',
      background: '#FAF8F5',
      cardBackground: '#F0EBE4',
      text: '#2A2420',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A07850',
      primaryShadow: '#6B4423',
      accent: '#E8D0A8',
      accentShadow: '#D2B48C',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0EBE4',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Creme Brulee',
    tags: ['dessert', 'caramel', 'creamy', 'french'],
    light: {
      primary: '#E3A857',
      primaryShadow: '#B68646',
      accent: '#FFFDD0',
      accentShadow: '#CCCAA6',
      background: '#FFF8E8',
      cardBackground: '#FFF0D8',
      text: '#3A3020',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F0C070',
      primaryShadow: '#E3A857',
      accent: '#504830',
      accentShadow: '#403820',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#FFF0D8',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Macaron',
    tags: ['dessert', 'pastel', 'delicate', 'french'],
    light: {
      primary: '#FFB6C1',
      primaryShadow: '#CC929A',
      accent: '#98FB98',
      accentShadow: '#7AC97A',
      background: '#FFFAFA',
      cardBackground: '#FFF0F5',
      text: '#3A2A30',
      extra: '#E6E6FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFC8D0',
      primaryShadow: '#FFB6C1',
      accent: '#B0FFB0',
      accentShadow: '#98FB98',
      background: '#0C080A',
      cardBackground: '#151015',
      text: '#FFF0F5',
      extra: '#E8E8FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Brownie',
    tags: ['dessert', 'chocolate', 'fudgy', 'rich'],
    light: {
      primary: '#4A2C1A',
      primaryShadow: '#3B2315',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FAF5F0',
      cardBackground: '#F0E8D8',
      text: '#2A1A15',
      extra: '#D2691E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#906040',
      primaryShadow: '#4A2C1A',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F0E8D8',
      extra: '#F08840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cupcake',
    tags: ['dessert', 'colorful', 'sweet', 'playful'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#3A1A28',
      extra: '#FFE4E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#A8E0F8',
      accentShadow: '#87CEEB',
      background: '#120810',
      cardBackground: '#1A1018',
      text: '#FFF0F5',
      extra: '#FFF0E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Bird Themes
  {
    name: 'Robin',
    tags: ['bird', 'cheerful', 'garden', 'spring'],
    light: {
      primary: '#CC3300',
      primaryShadow: '#A32900',
      accent: '#8B7355',
      accentShadow: '#6F5C44',
      background: '#FAF8F5',
      cardBackground: '#F0EBE4',
      text: '#2A2420',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06040',
      primaryShadow: '#CC3300',
      accent: '#B09070',
      accentShadow: '#8B7355',
      background: '#0A0908',
      cardBackground: '#151210',
      text: '#F0EBE4',
      extra: '#50C050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cardinal',
    tags: ['bird', 'bold', 'vibrant', 'striking'],
    light: {
      primary: '#C41E3A',
      primaryShadow: '#9D182E',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFF8F5',
      cardBackground: '#FFF0E8',
      text: '#2A1515',
      extra: '#FF6B6B',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E05060',
      primaryShadow: '#C41E3A',
      accent: '#808080',
      accentShadow: '#606060',
      background: '#100505',
      cardBackground: '#1A0808',
      text: '#FFF0E8',
      extra: '#FF8888',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bluebird',
    tags: ['bird', 'serene', 'sky', 'cheerful'],
    light: {
      primary: '#1E90FF',
      primaryShadow: '#1873CC',
      accent: '#FF8C00',
      accentShadow: '#CC7000',
      background: '#F0FAFF',
      cardBackground: '#E0F5FF',
      text: '#1A2530',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50B0FF',
      primaryShadow: '#1E90FF',
      accent: '#FFB040',
      accentShadow: '#FF8C00',
      background: '#080A10',
      cardBackground: '#101520',
      text: '#E0F5FF',
      extra: '#A8E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Raven',
    tags: ['bird', 'mysterious', 'dark', 'intelligent'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#4B0082',
      accentShadow: '#3C0068',
      background: '#F8F8FA',
      cardBackground: '#E8E8F0',
      text: '#1A1A1A',
      extra: '#2F2F4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0B0B0',
      primaryShadow: '#808080',
      accent: '#8060C0',
      accentShadow: '#4B0082',
      background: '#050505',
      cardBackground: '#0A0A10',
      text: '#E8E8F0',
      extra: '#505080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hummingbird',
    tags: ['bird', 'iridescent', 'energetic', 'delicate'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#FF1493',
      accentShadow: '#CC1076',
      background: '#F0FFFF',
      cardBackground: '#E0F8F8',
      text: '#1A2828',
      extra: '#00FF7F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8E8',
      primaryShadow: '#00CED1',
      accent: '#FF50B0',
      accentShadow: '#FF1493',
      background: '#080C0C',
      cardBackground: '#101818',
      text: '#E0F8F8',
      extra: '#40FF90',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Toucan',
    tags: ['bird', 'tropical', 'colorful', 'exotic'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFF5',
      cardBackground: '#FFF8E0',
      text: '#2A2810',
      extra: '#1A1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6830',
      primaryShadow: '#FF4500',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0A0A05',
      cardBackground: '#151008',
      text: '#FFF8E0',
      extra: '#808080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Parrot',
    tags: ['bird', 'vibrant', 'tropical', 'playful'],
    light: {
      primary: '#00AA00',
      primaryShadow: '#008800',
      accent: '#FF0000',
      accentShadow: '#CC0000',
      background: '#F8FFF5',
      cardBackground: '#E8F8E0',
      text: '#1A2A18',
      extra: '#0000FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40D040',
      primaryShadow: '#00AA00',
      accent: '#FF4040',
      accentShadow: '#FF0000',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E8F8E0',
      extra: '#6060FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Swan',
    tags: ['bird', 'elegant', 'graceful', 'pure'],
    light: {
      primary: '#FFFFFF',
      primaryShadow: '#D0D0D0',
      accent: '#FF8C00',
      accentShadow: '#CC7000',
      background: '#F0F8FF',
      cardBackground: '#E8F0F8',
      text: '#1A2030',
      extra: '#B0C4DE',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#404040',
      primaryShadow: '#2A2A2A',
      accent: '#FFB040',
      accentShadow: '#FF8C00',
      background: '#080A10',
      cardBackground: '#101520',
      text: '#E8F0F8',
      extra: '#C8D8F0',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  // Board Game Themes
  {
    name: 'Chess',
    tags: ['game', 'strategic', 'classic', 'checkered'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'text',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#B0B0B0',
      accent: '#404040',
      accentShadow: '#2A2A2A',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E8',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Word Game',
    tags: ['game', 'intellectual', 'classic', 'wooden'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#D2B48C',
      accentShadow: '#A89070',
      background: '#FAF5F0',
      cardBackground: '#F0E8D8',
      text: '#2A1A15',
      extra: '#FFEFD5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#E8D0A8',
      accentShadow: '#D2B48C',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F0E8D8',
      extra: '#FFF8E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Property Trade',
    tags: ['game', 'colorful', 'classic', 'economic'],
    light: {
      primary: '#008000',
      primaryShadow: '#006600',
      accent: '#DC143C',
      accentShadow: '#B0102F',
      background: '#F5FFF5',
      cardBackground: '#E0F0E0',
      text: '#1A2A18',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40C040',
      primaryShadow: '#008000',
      accent: '#F04060',
      accentShadow: '#DC143C',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E0F0E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystery Mansion',
    tags: ['game', 'detective', 'classic', 'suspenseful'],
    light: {
      primary: '#722F37',
      primaryShadow: '#5B262C',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FAF5F5',
      cardBackground: '#F0E8E8',
      text: '#2A1818',
      extra: '#2F4F4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A06070',
      primaryShadow: '#722F37',
      accent: '#F0C850',
      accentShadow: '#D4AF37',
      background: '#0A0606',
      cardBackground: '#150A0A',
      text: '#F0E8E8',
      extra: '#608080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'World Conquest',
    tags: ['game', 'strategic', 'warfare', 'territorial'],
    light: {
      primary: '#1E3A5F',
      primaryShadow: '#182E4C',
      accent: '#DC143C',
      accentShadow: '#B0102F',
      background: '#F8F9FA',
      cardBackground: '#E8EEF5',
      text: '#1A2030',
      extra: '#DAA520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4080C0',
      primaryShadow: '#1E3A5F',
      accent: '#F04060',
      accentShadow: '#DC143C',
      background: '#080A10',
      cardBackground: '#101520',
      text: '#E8EEF5',
      extra: '#F0C040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Island Settlers',
    tags: ['game', 'resource', 'building', 'colorful'],
    light: {
      primary: '#CD853F',
      primaryShadow: '#A46A32',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFF8E8',
      cardBackground: '#F8F0D8',
      text: '#2A2010',
      extra: '#1E90FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A060',
      primaryShadow: '#CD853F',
      accent: '#50C050',
      accentShadow: '#228B22',
      background: '#0A0805',
      cardBackground: '#151008',
      text: '#F8F0D8',
      extra: '#50B0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Additional Creative Themes
  {
    name: 'Origami',
    tags: ['craft', 'japanese', 'paper', 'geometric'],
    light: {
      primary: '#E8E8E8',
      primaryShadow: '#B8B8B8',
      accent: '#DC143C',
      accentShadow: '#B0102F',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
    dark: {
      primary: '#404040',
      primaryShadow: '#2A2A2A',
      accent: '#F04060',
      accentShadow: '#DC143C',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#FFE840',
      h1Color: 'accent',
      h2Color: 'text',
      h3Color: 'accent',
    },
  },
  {
    name: 'Waterfall',
    tags: ['nature', 'water', 'refreshing', 'powerful'],
    light: {
      primary: '#4682B4',
      primaryShadow: '#386890',
      accent: '#98FB98',
      accentShadow: '#7AC97A',
      background: '#F0FAFF',
      cardBackground: '#E0F5FF',
      text: '#1A2A30',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#70A8D0',
      primaryShadow: '#4682B4',
      accent: '#B0FFB0',
      accentShadow: '#98FB98',
      background: '#081015',
      cardBackground: '#101A22',
      text: '#E0F5FF',
      extra: '#A8E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bonfire',
    tags: ['nature', 'warm', 'camping', 'cozy'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFF5F0',
      cardBackground: '#F8E8D8',
      text: '#2A1A10',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6830',
      primaryShadow: '#FF4500',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0A0605',
      cardBackground: '#150A05',
      text: '#F8E8D8',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Northern Lights',
    tags: ['nature', 'cosmic', 'magical', 'colorful'],
    light: {
      primary: '#00FF7F',
      primaryShadow: '#00CC66',
      accent: '#9400D3',
      accentShadow: '#7600A9',
      background: '#0A0A20',
      cardBackground: '#10103A',
      text: '#E8E8FF',
      extra: '#00BFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40FF90',
      primaryShadow: '#00FF7F',
      accent: '#B040F0',
      accentShadow: '#9400D3',
      background: '#050510',
      cardBackground: '#0A0A1A',
      text: '#E8E8FF',
      extra: '#40D0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blossom',
    tags: ['nature', 'spring', 'floral', 'fresh'],
    light: {
      primary: '#FFB7C5',
      primaryShadow: '#CC929D',
      accent: '#98FB98',
      accentShadow: '#7AC97A',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#2A1A20',
      extra: '#FF69B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFC8D8',
      primaryShadow: '#FFB7C5',
      accent: '#B0FFB0',
      accentShadow: '#98FB98',
      background: '#0C0810',
      cardBackground: '#151018',
      text: '#FFF0F5',
      extra: '#FF88C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Thunderstorm',
    tags: ['nature', 'dramatic', 'powerful', 'electric'],
    light: {
      primary: '#2F4F4F',
      primaryShadow: '#253F3F',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F5F5F8',
      cardBackground: '#E0E0E8',
      text: '#1A1A20',
      extra: '#4169E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#608080',
      primaryShadow: '#2F4F4F',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#08080A',
      cardBackground: '#101015',
      text: '#E0E0E8',
      extra: '#6090F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Batch 10: Superhero Themes
  {
    name: 'Caped Crusader',
    tags: ['superhero', 'dark', 'heroic', 'noir'],
    light: {
      primary: '#1A1A2E',
      primaryShadow: '#101020',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F5F5F8',
      cardBackground: '#E8E8F0',
      text: '#1A1A1A',
      extra: '#4A4A6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A4A7A',
      primaryShadow: '#1A1A2E',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0A0A12',
      cardBackground: '#12121A',
      text: '#E8E8F0',
      extra: '#6A6A9A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Defender',
    tags: ['superhero', 'tech', 'bold', 'metallic'],
    light: {
      primary: '#B22222',
      primaryShadow: '#8E1B1B',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F8F8FA',
      cardBackground: '#E8E8EC',
      text: '#1A1A1A',
      extra: '#4682B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E03030',
      primaryShadow: '#B22222',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0A0808',
      cardBackground: '#151210',
      text: '#F0F0F0',
      extra: '#6090C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Web Slinger',
    tags: ['superhero', 'bold', 'dynamic', 'urban'],
    light: {
      primary: '#C41E3A',
      primaryShadow: '#9D182E',
      accent: '#1E90FF',
      accentShadow: '#1873CC',
      background: '#FFFAFA',
      cardBackground: '#FFF0F0',
      text: '#1A1A1A',
      extra: '#2F4F4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E83050',
      primaryShadow: '#C41E3A',
      accent: '#50B0FF',
      accentShadow: '#1E90FF',
      background: '#0C0808',
      cardBackground: '#151010',
      text: '#FFF0F0',
      extra: '#608080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amazonian Warrior',
    tags: ['superhero', 'regal', 'bold', 'powerful'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#4169E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C02020',
      primaryShadow: '#8B0000',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0C0808',
      cardBackground: '#151010',
      text: '#FFF5E8',
      extra: '#6090F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Speedster Crimson',
    tags: ['superhero', 'dynamic', 'fast', 'electric'],
    light: {
      primary: '#DC143C',
      primaryShadow: '#B01030',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFAFA',
      cardBackground: '#FFF0F0',
      text: '#1A1A1A',
      extra: '#FF4500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF3050',
      primaryShadow: '#DC143C',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0A0606',
      cardBackground: '#120C0C',
      text: '#FFF0F0',
      extra: '#FF6830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Emerald Archer',
    tags: ['superhero', 'forest', 'bold', 'skilled'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#2F4F4F',
      accentShadow: '#253F3F',
      background: '#F5FFF5',
      cardBackground: '#E8F5E8',
      text: '#1A1A1A',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B040',
      primaryShadow: '#228B22',
      accent: '#608080',
      accentShadow: '#2F4F4F',
      background: '#060C06',
      cardBackground: '#0C150C',
      text: '#E8F5E8',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ocean Monarch',
    tags: ['superhero', 'ocean', 'regal', 'powerful'],
    light: {
      primary: '#006994',
      primaryShadow: '#005476',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F0FFFF',
      cardBackground: '#E0F8F8',
      text: '#1A1A1A',
      extra: '#20B2AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2090C0',
      primaryShadow: '#006994',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#060A0C',
      cardBackground: '#0C1215',
      text: '#E0F8F8',
      extra: '#40D0C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cosmic Guardian',
    tags: ['superhero', 'space', 'powerful', 'cosmic'],
    light: {
      primary: '#4B0082',
      primaryShadow: '#3C0068',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#FAF8FF',
      cardBackground: '#F0E8FF',
      text: '#1A1A1A',
      extra: '#9400D3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7030B0',
      primaryShadow: '#4B0082',
      accent: '#40E8EA',
      accentShadow: '#00CED1',
      background: '#0A080C',
      cardBackground: '#120C18',
      text: '#F0E8FF',
      extra: '#B040F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Fashion/Clothing Themes
  {
    name: 'Runway Black',
    tags: ['fashion', 'elegant', 'minimal', 'luxury'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#C0A080',
      accentShadow: '#9A8066',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#808080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#1A1A1A',
      accent: '#D8B898',
      accentShadow: '#C0A080',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#A0A0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Haute Couture',
    tags: ['fashion', 'luxurious', 'sophisticated', 'french'],
    light: {
      primary: '#2C1810',
      primaryShadow: '#22120C',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFEF8',
      cardBackground: '#FFF8F0',
      text: '#1A1A1A',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A08060',
      primaryShadow: '#2C1810',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF8F0',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Streetwear',
    tags: ['fashion', 'urban', 'bold', 'youth'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#32CD32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6830',
      primaryShadow: '#FF4500',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#50E050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bohemian',
    tags: ['fashion', 'earthy', 'free-spirited', 'artistic'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#CD853F',
      accentShadow: '#A46A32',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#D2691E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#E0A060',
      accentShadow: '#CD853F',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#E08840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Preppy Classic',
    tags: ['fashion', 'traditional', 'clean', 'academic'],
    light: {
      primary: '#002366',
      primaryShadow: '#001C52',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFFFFE',
      cardBackground: '#F5F8F5',
      text: '#1A1A1A',
      extra: '#DC143C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4060A0',
      primaryShadow: '#002366',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0A0A0C',
      cardBackground: '#101218',
      text: '#F5F8F5',
      extra: '#FF3050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Athleisure',
    tags: ['fashion', 'sporty', 'modern', 'comfortable'],
    light: {
      primary: '#2F4F4F',
      primaryShadow: '#253F3F',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#F8FAFA',
      cardBackground: '#F0F5F5',
      text: '#1A1A1A',
      extra: '#FF6347',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#608080',
      primaryShadow: '#2F4F4F',
      accent: '#40E8EA',
      accentShadow: '#00CED1',
      background: '#080A0A',
      cardBackground: '#101515',
      text: '#F0F5F5',
      extra: '#FF8070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Glamour',
    tags: ['fashion', 'retro', 'elegant', 'hollywood'],
    light: {
      primary: '#722F37',
      primaryShadow: '#5B262C',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFEF8',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A05060',
      primaryShadow: '#722F37',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0808',
      cardBackground: '#151010',
      text: '#FFF5E8',
      extra: '#C02020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Minimalist White',
    tags: ['fashion', 'minimal', 'clean', 'scandinavian'],
    light: {
      primary: '#2A2A2A',
      primaryShadow: '#1A1A1A',
      accent: '#707070',
      accentShadow: '#5A5A5A',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#1A1A1A',
      extra: '#B0B0B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#2A2A2A',
      accent: '#A0A0A0',
      accentShadow: '#707070',
      background: '#0A0A0A',
      cardBackground: '#121212',
      text: '#FAFAFA',
      extra: '#C8C8C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Furniture/Interior Themes
  {
    name: 'Mid-Century Modern',
    tags: ['furniture', 'retro', 'warm', 'sophisticated'],
    light: {
      primary: '#C04000',
      primaryShadow: '#9A3300',
      accent: '#F5DEB3',
      accentShadow: '#C4B28F',
      background: '#FFFAF0',
      cardBackground: '#FFF5E0',
      text: '#2A1A10',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E06020',
      primaryShadow: '#C04000',
      accent: '#F8E8C8',
      accentShadow: '#F5DEB3',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E0',
      extra: '#40B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scandinavian',
    tags: ['furniture', 'minimal', 'light', 'nordic'],
    light: {
      primary: '#4A5568',
      primaryShadow: '#3B4453',
      accent: '#A3B8CC',
      accentShadow: '#8293A3',
      background: '#FFFFFF',
      cardBackground: '#F7FAFC',
      text: '#1A1A1A',
      extra: '#CBD5E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0B0C0',
      primaryShadow: '#4A5568',
      accent: '#C0D0E0',
      accentShadow: '#A3B8CC',
      background: '#0A0C0E',
      cardBackground: '#121518',
      text: '#F7FAFC',
      extra: '#D8E0E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Industrial Loft',
    tags: ['furniture', 'urban', 'raw', 'modern'],
    light: {
      primary: '#3C3C3C',
      primaryShadow: '#2C2C2C',
      accent: '#B87333',
      accentShadow: '#935C29',
      background: '#F5F5F0',
      cardBackground: '#E8E8E0',
      text: '#1A1A1A',
      extra: '#708090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0A0A0',
      primaryShadow: '#3C3C3C',
      accent: '#D09050',
      accentShadow: '#B87333',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E0',
      extra: '#90A0B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Art Deco',
    tags: ['furniture', 'glamorous', 'geometric', 'luxury'],
    light: {
      primary: '#1A1A2E',
      primaryShadow: '#101020',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFEF8',
      cardBackground: '#F8F5E8',
      text: '#1A1A1A',
      extra: '#4A4A6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A4A7A',
      primaryShadow: '#1A1A2E',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#08080C',
      cardBackground: '#101018',
      text: '#F8F5E8',
      extra: '#6A6A9A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Farmhouse',
    tags: ['furniture', 'country', 'warm', 'cozy'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4A332C',
      accent: '#8FBC8F',
      accentShadow: '#729672',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#907060',
      primaryShadow: '#5D4037',
      accent: '#A8D8A8',
      accentShadow: '#8FBC8F',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Japanese Zen',
    tags: ['furniture', 'minimal', 'peaceful', 'natural'],
    light: {
      primary: '#2F2F2F',
      primaryShadow: '#1F1F1F',
      accent: '#8B9467',
      accentShadow: '#6F7652',
      background: '#FAF8F5',
      cardBackground: '#F0EBE0',
      text: '#1A1A1A',
      extra: '#BC8F8F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#909090',
      primaryShadow: '#2F2F2F',
      accent: '#A8B080',
      accentShadow: '#8B9467',
      background: '#080808',
      cardBackground: '#121210',
      text: '#F0EBE0',
      extra: '#D0A8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Transportation Themes
  {
    name: 'Classic Muscle Car',
    tags: ['transport', 'retro', 'bold', 'american'],
    light: {
      primary: '#C41E3A',
      primaryShadow: '#9D182E',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFAFA',
      cardBackground: '#FFF0F0',
      text: '#1A1A1A',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E83050',
      primaryShadow: '#C41E3A',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0A0808',
      cardBackground: '#151010',
      text: '#FFF0F0',
      extra: '#D8D8D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Electric Future',
    tags: ['transport', 'modern', 'eco', 'innovative'],
    light: {
      primary: '#00A86B',
      primaryShadow: '#008756',
      accent: '#4169E1',
      accentShadow: '#3454B4',
      background: '#F5FFFA',
      cardBackground: '#E8FFF0',
      text: '#1A1A1A',
      extra: '#20B2AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30C088',
      primaryShadow: '#00A86B',
      accent: '#6090F0',
      accentShadow: '#4169E1',
      background: '#060C0A',
      cardBackground: '#0C1510',
      text: '#E8FFF0',
      extra: '#40D0C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Luxury Sedan',
    tags: ['transport', 'luxury', 'elegant', 'sophisticated'],
    light: {
      primary: '#1C1C1C',
      primaryShadow: '#0C0C0C',
      accent: '#B8860B',
      accentShadow: '#936B09',
      background: '#FFFEFA',
      cardBackground: '#F8F5E8',
      text: '#1A1A1A',
      extra: '#708090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C8C8C8',
      primaryShadow: '#1C1C1C',
      accent: '#D4A020',
      accentShadow: '#B8860B',
      background: '#080808',
      cardBackground: '#121210',
      text: '#F8F5E8',
      extra: '#90A0B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Racing Formula',
    tags: ['transport', 'fast', 'bold', 'sport'],
    light: {
      primary: '#FF0000',
      primaryShadow: '#CC0000',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF4040',
      primaryShadow: '#FF0000',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0A0606',
      cardBackground: '#150C0C',
      text: '#F8F8F8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Locomotive',
    tags: ['transport', 'retro', 'industrial', 'nostalgic'],
    light: {
      primary: '#2F2F2F',
      primaryShadow: '#1F1F1F',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#F5F0E8',
      cardBackground: '#E8E0D0',
      text: '#1A1A1A',
      extra: '#B87333',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#909090',
      primaryShadow: '#2F2F2F',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#080806',
      cardBackground: '#12100C',
      text: '#E8E0D0',
      extra: '#D09050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ocean Liner',
    tags: ['transport', 'nautical', 'elegant', 'classic'],
    light: {
      primary: '#003366',
      primaryShadow: '#002952',
      accent: '#F5F5DC',
      accentShadow: '#C4C4B0',
      background: '#F8FBFF',
      cardBackground: '#E8F0F8',
      text: '#1A1A1A',
      extra: '#B8860B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4080B0',
      primaryShadow: '#003366',
      accent: '#F8F8E0',
      accentShadow: '#F5F5DC',
      background: '#060810',
      cardBackground: '#0C1018',
      text: '#E8F0F8',
      extra: '#D4A020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Aviation Classic',
    tags: ['transport', 'vintage', 'adventurous', 'sky'],
    light: {
      primary: '#4682B4',
      primaryShadow: '#386890',
      accent: '#D2691E',
      accentShadow: '#A85418',
      background: '#F0F8FF',
      cardBackground: '#E0F0FF',
      text: '#1A1A1A',
      extra: '#B0C4DE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6AA0D0',
      primaryShadow: '#4682B4',
      accent: '#E88840',
      accentShadow: '#D2691E',
      background: '#060A0E',
      cardBackground: '#0C1218',
      text: '#E0F0FF',
      extra: '#C8D8F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Motorcycle Rebel',
    tags: ['transport', 'edgy', 'bold', 'freedom'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#FF4500',
      accentShadow: '#CC3700',
      background: '#F8F8F8',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D0D0D0',
      primaryShadow: '#1A1A1A',
      accent: '#FF6830',
      accentShadow: '#FF4500',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E8',
      extra: '#D8D8D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Beverage Themes
  {
    name: 'Espresso Shot',
    tags: ['beverage', 'coffee', 'bold', 'energetic'],
    light: {
      primary: '#3C2415',
      primaryShadow: '#2E1C10',
      accent: '#D4A574',
      accentShadow: '#AA845D',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#6F4E37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A08060',
      primaryShadow: '#3C2415',
      accent: '#E8B890',
      accentShadow: '#D4A574',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#907050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Matcha Green',
    tags: ['beverage', 'tea', 'calm', 'natural'],
    light: {
      primary: '#7B8B4E',
      primaryShadow: '#626F3E',
      accent: '#E8E4C9',
      accentShadow: '#BAB6A1',
      background: '#FAFFF5',
      cardBackground: '#F0F8E8',
      text: '#1A1A1A',
      extra: '#9CAF88',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#98A860',
      primaryShadow: '#7B8B4E',
      accent: '#F0F0D8',
      accentShadow: '#E8E4C9',
      background: '#080A06',
      cardBackground: '#10150C',
      text: '#F0F8E8',
      extra: '#B0C0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Citrus Soda',
    tags: ['beverage', 'refreshing', 'bright', 'fun'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#32CD32',
      accentShadow: '#28A428',
      background: '#FFFFF5',
      cardBackground: '#FFFAE0',
      text: '#1A1A1A',
      extra: '#FFA500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#50E050',
      accentShadow: '#32CD32',
      background: '#0A0A06',
      cardBackground: '#15150C',
      text: '#FFFAE0',
      extra: '#FFC040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Berry Smoothie',
    tags: ['beverage', 'fruity', 'vibrant', 'healthy'],
    light: {
      primary: '#8E4585',
      primaryShadow: '#72376A',
      accent: '#FF6B81',
      accentShadow: '#CC5567',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#1A1A1A',
      extra: '#6B5B95',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B060A0',
      primaryShadow: '#8E4585',
      accent: '#FF8898',
      accentShadow: '#FF6B81',
      background: '#0C080A',
      cardBackground: '#15101A',
      text: '#FFF0F5',
      extra: '#8878B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Champagne Toast',
    tags: ['beverage', 'celebration', 'elegant', 'luxury'],
    light: {
      primary: '#C9A961',
      primaryShadow: '#A1874D',
      accent: '#F5F5DC',
      accentShadow: '#C4C4B0',
      background: '#FFFEFA',
      cardBackground: '#FFF8E8',
      text: '#2A2A1A',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0C078',
      primaryShadow: '#C9A961',
      accent: '#F8F8E0',
      accentShadow: '#F5F5DC',
      background: '#0A0A08',
      cardBackground: '#151510',
      text: '#FFF8E8',
      extra: '#E8C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Punch',
    tags: ['beverage', 'tropical', 'vibrant', 'party'],
    light: {
      primary: '#FF6347',
      primaryShadow: '#CC4F39',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#FF4500',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8070',
      primaryShadow: '#FF6347',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0C0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#FF6830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Red Wine',
    tags: ['beverage', 'sophisticated', 'rich', 'evening'],
    light: {
      primary: '#722F37',
      primaryShadow: '#5B262C',
      accent: '#800020',
      accentShadow: '#66001A',
      background: '#FFF8F5',
      cardBackground: '#FFF0E8',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A05060',
      primaryShadow: '#722F37',
      accent: '#B03040',
      accentShadow: '#800020',
      background: '#0C0808',
      cardBackground: '#151010',
      text: '#FFF0E8',
      extra: '#C02020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mint Mojito',
    tags: ['beverage', 'refreshing', 'cool', 'summer'],
    light: {
      primary: '#3CB371',
      primaryShadow: '#308F5A',
      accent: '#F5FFFA',
      accentShadow: '#C4CCC8',
      background: '#F5FFFA',
      cardBackground: '#E8FFF0',
      text: '#1A1A1A',
      extra: '#2E8B57',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#58D090',
      primaryShadow: '#3CB371',
      accent: '#F8FFF8',
      accentShadow: '#F5FFFA',
      background: '#060C08',
      cardBackground: '#0C1510',
      text: '#E8FFF0',
      extra: '#48B070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Weather Themes
  {
    name: 'Sunny Day',
    tags: ['weather', 'bright', 'cheerful', 'warm'],
    light: {
      primary: '#FFB300',
      primaryShadow: '#CC8F00',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#FFFFF5',
      cardBackground: '#FFFAE0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFD040',
      primaryShadow: '#FFB300',
      accent: '#A0E0F8',
      accentShadow: '#87CEEB',
      background: '#0A0A06',
      cardBackground: '#15150C',
      text: '#FFFAE0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Afternoon',
    tags: ['weather', 'calm', 'cozy', 'melancholy'],
    light: {
      primary: '#4682B4',
      primaryShadow: '#386890',
      accent: '#708090',
      accentShadow: '#5A6673',
      background: '#F0F5F8',
      cardBackground: '#E0E8F0',
      text: '#1A1A1A',
      extra: '#B0C4DE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6AA0D0',
      primaryShadow: '#4682B4',
      accent: '#90A0B0',
      accentShadow: '#708090',
      background: '#060A0C',
      cardBackground: '#0C1015',
      text: '#E0E8F0',
      extra: '#C8D8F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snowy Winter',
    tags: ['weather', 'cold', 'clean', 'peaceful'],
    light: {
      primary: '#4A5568',
      primaryShadow: '#3B4453',
      accent: '#E0E7FF',
      accentShadow: '#B3B9CC',
      background: '#FFFFFF',
      cardBackground: '#F8FAFC',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0B0C0',
      primaryShadow: '#4A5568',
      accent: '#E8F0FF',
      accentShadow: '#E0E7FF',
      background: '#080A0C',
      cardBackground: '#101518',
      text: '#F8FAFC',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Foggy Morning',
    tags: ['weather', 'mysterious', 'soft', 'calm'],
    light: {
      primary: '#696969',
      primaryShadow: '#545454',
      accent: '#A9A9A9',
      accentShadow: '#878787',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#B0B0B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#989898',
      primaryShadow: '#696969',
      accent: '#C0C0C0',
      accentShadow: '#A9A9A9',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E8',
      extra: '#C8C8C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lightning Storm',
    tags: ['weather', 'electric', 'dramatic', 'powerful'],
    light: {
      primary: '#2F2F4F',
      primaryShadow: '#252540',
      accent: '#FFFF00',
      accentShadow: '#CCCC00',
      background: '#F5F5FA',
      cardBackground: '#E8E8F0',
      text: '#1A1A1A',
      extra: '#6A5ACD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#606090',
      primaryShadow: '#2F2F4F',
      accent: '#FFFF60',
      accentShadow: '#FFFF00',
      background: '#080810',
      cardBackground: '#101018',
      text: '#E8E8F0',
      extra: '#8878E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainbow Sky',
    tags: ['weather', 'colorful', 'hopeful', 'magical'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#4ECDC4',
      accentShadow: '#3EA49D',
      background: '#FFFFFE',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#FFE66D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8888',
      primaryShadow: '#FF6B6B',
      accent: '#70E0D8',
      accentShadow: '#4ECDC4',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F8F8F8',
      extra: '#FFF090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Breeze',
    tags: ['weather', 'warm', 'cozy', 'seasonal'],
    light: {
      primary: '#D2691E',
      primaryShadow: '#A85418',
      accent: '#DAA520',
      accentShadow: '#AE841A',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E88840',
      primaryShadow: '#D2691E',
      accent: '#F0C040',
      accentShadow: '#DAA520',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spring Bloom',
    tags: ['weather', 'fresh', 'hopeful', 'renewal'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#98FB98',
      accentShadow: '#7AC97A',
      background: '#FFFAFC',
      cardBackground: '#FFF0F8',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#B0FFB0',
      accentShadow: '#98FB98',
      background: '#0C080A',
      cardBackground: '#150C12',
      text: '#FFF0F8',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Batch 11: Sports Themes
  {
    name: 'Basketball Court',
    tags: ['sports', 'dynamic', 'energetic', 'orange'],
    light: {
      primary: '#FF6B00',
      primaryShadow: '#CC5600',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#1A1A1A',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8830',
      primaryShadow: '#FF6B00',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Soccer Pitch',
    tags: ['sports', 'natural', 'global', 'green'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#FFFFFF',
      accentShadow: '#CCCCCC',
      background: '#F5FFF5',
      cardBackground: '#E8F5E8',
      text: '#1A1A1A',
      extra: '#1A1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B040',
      primaryShadow: '#228B22',
      accent: '#F8F8F8',
      accentShadow: '#FFFFFF',
      background: '#060C06',
      cardBackground: '#0C150C',
      text: '#E8F5E8',
      extra: '#E0E0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tennis Match',
    tags: ['sports', 'elegant', 'classic', 'green'],
    light: {
      primary: '#2E8B57',
      primaryShadow: '#256F46',
      accent: '#CCFF00',
      accentShadow: '#A3CC00',
      background: '#F8FFF8',
      cardBackground: '#F0F8F0',
      text: '#1A1A1A',
      extra: '#FFFFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#48B070',
      primaryShadow: '#2E8B57',
      accent: '#E0FF40',
      accentShadow: '#CCFF00',
      background: '#060A08',
      cardBackground: '#0C1510',
      text: '#F0F8F0',
      extra: '#F8F8F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Swimming Pool',
    tags: ['sports', 'aquatic', 'refreshing', 'blue'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#FFFFFF',
      accentShadow: '#CCCCCC',
      background: '#F0FFFF',
      cardBackground: '#E0F8F8',
      text: '#1A1A1A',
      extra: '#4682B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8EA',
      primaryShadow: '#00CED1',
      accent: '#F8F8F8',
      accentShadow: '#FFFFFF',
      background: '#060A0C',
      cardBackground: '#0C1215',
      text: '#E0F8F8',
      extra: '#6AA0D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golf Green',
    tags: ['sports', 'elegant', 'natural', 'refined'],
    light: {
      primary: '#355E3B',
      primaryShadow: '#2A4B2F',
      accent: '#F5F5DC',
      accentShadow: '#C4C4B0',
      background: '#FAFFF5',
      cardBackground: '#F0F8E8',
      text: '#1A1A1A',
      extra: '#8FBC8F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#508050',
      primaryShadow: '#355E3B',
      accent: '#F8F8E0',
      accentShadow: '#F5F5DC',
      background: '#060806',
      cardBackground: '#0C1210',
      text: '#F0F8E8',
      extra: '#A8D8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Boxing Ring',
    tags: ['sports', 'intense', 'bold', 'fighting'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFF8F8',
      cardBackground: '#FFF0F0',
      text: '#1A1A1A',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C02020',
      primaryShadow: '#8B0000',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0C0606',
      cardBackground: '#150C0C',
      text: '#FFF0F0',
      extra: '#D8D8D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ice Hockey',
    tags: ['sports', 'cold', 'fast', 'intense'],
    light: {
      primary: '#003366',
      primaryShadow: '#002952',
      accent: '#FF4500',
      accentShadow: '#CC3700',
      background: '#F5F8FF',
      cardBackground: '#E8F0FF',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4080B0',
      primaryShadow: '#003366',
      accent: '#FF6830',
      accentShadow: '#FF4500',
      background: '#060810',
      cardBackground: '#0C1018',
      text: '#E8F0FF',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cycling Race',
    tags: ['sports', 'endurance', 'colorful', 'outdoor'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#FF0000',
      accentShadow: '#CC0000',
      background: '#FFFFF8',
      cardBackground: '#FFF8E0',
      text: '#1A1A1A',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#FF4040',
      accentShadow: '#FF0000',
      background: '#0A0A06',
      cardBackground: '#15150C',
      text: '#FFF8E0',
      extra: '#40B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Cuisine Themes
  {
    name: 'Italian Trattoria',
    tags: ['cuisine', 'mediterranean', 'warm', 'rustic'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#FF0000',
      accentShadow: '#CC0000',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B040',
      primaryShadow: '#228B22',
      accent: '#FF4040',
      accentShadow: '#FF0000',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Japanese Izakaya',
    tags: ['cuisine', 'asian', 'elegant', 'minimal'],
    light: {
      primary: '#C41E3A',
      primaryShadow: '#9D182E',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FAF8F5',
      cardBackground: '#F0EBE0',
      text: '#1A1A1A',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E83050',
      primaryShadow: '#C41E3A',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0A0808',
      cardBackground: '#151010',
      text: '#F0EBE0',
      extra: '#E8C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mexican Cantina',
    tags: ['cuisine', 'vibrant', 'festive', 'colorful'],
    light: {
      primary: '#FF6B00',
      primaryShadow: '#CC5600',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFFAF0',
      cardBackground: '#FFF5E0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8830',
      primaryShadow: '#FF6B00',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'French Bistro',
    tags: ['cuisine', 'elegant', 'sophisticated', 'classic'],
    light: {
      primary: '#002366',
      primaryShadow: '#001C52',
      accent: '#C41E3A',
      accentShadow: '#9D182E',
      background: '#FFFEFA',
      cardBackground: '#F8F8F5',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4060A0',
      primaryShadow: '#002366',
      accent: '#E83050',
      accentShadow: '#C41E3A',
      background: '#080810',
      cardBackground: '#101018',
      text: '#F8F8F5',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indian Spice',
    tags: ['cuisine', 'vibrant', 'warm', 'rich'],
    light: {
      primary: '#FF6B00',
      primaryShadow: '#CC5600',
      accent: '#9400D3',
      accentShadow: '#7600A8',
      background: '#FFFAF0',
      cardBackground: '#FFF5E0',
      text: '#1A1A1A',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8830',
      primaryShadow: '#FF6B00',
      accent: '#B040F0',
      accentShadow: '#9400D3',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E0',
      extra: '#E8C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Thai Kitchen',
    tags: ['cuisine', 'tropical', 'bold', 'aromatic'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#FF4500',
      accentShadow: '#CC3700',
      background: '#F5FFF5',
      cardBackground: '#E8F5E8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B040',
      primaryShadow: '#228B22',
      accent: '#FF6830',
      accentShadow: '#FF4500',
      background: '#060C06',
      cardBackground: '#0C150C',
      text: '#E8F5E8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Greek Taverna',
    tags: ['cuisine', 'mediterranean', 'coastal', 'fresh'],
    light: {
      primary: '#0077BE',
      primaryShadow: '#005F98',
      accent: '#FFFFFF',
      accentShadow: '#CCCCCC',
      background: '#F8FBFF',
      cardBackground: '#E8F4FF',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3090D0',
      primaryShadow: '#0077BE',
      accent: '#F8F8F8',
      accentShadow: '#FFFFFF',
      background: '#060A10',
      cardBackground: '#0C1218',
      text: '#E8F4FF',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Korean BBQ',
    tags: ['cuisine', 'bold', 'savory', 'modern'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#32CD32',
      accentShadow: '#28A428',
      background: '#FFF8F5',
      cardBackground: '#FFF0E8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C02020',
      primaryShadow: '#8B0000',
      accent: '#50E050',
      accentShadow: '#32CD32',
      background: '#0C0806',
      cardBackground: '#15100C',
      text: '#FFF0E8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Architecture Themes
  {
    name: 'Gothic Cathedral',
    tags: ['architecture', 'dramatic', 'historic', 'ornate'],
    light: {
      primary: '#2F2F4F',
      primaryShadow: '#252540',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F5F5FA',
      cardBackground: '#E8E8F0',
      text: '#1A1A1A',
      extra: '#4B0082',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#606090',
      primaryShadow: '#2F2F4F',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#08080C',
      cardBackground: '#101018',
      text: '#E8E8F0',
      extra: '#7030B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Modern Skyscraper',
    tags: ['architecture', 'sleek', 'urban', 'glass'],
    light: {
      primary: '#4682B4',
      primaryShadow: '#386890',
      accent: '#C0C0C0',
      accentShadow: '#9A9A9A',
      background: '#F8FBFF',
      cardBackground: '#E8F0F8',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6AA0D0',
      primaryShadow: '#4682B4',
      accent: '#D8D8D8',
      accentShadow: '#C0C0C0',
      background: '#060A0E',
      cardBackground: '#0C1218',
      text: '#E8F0F8',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Adobe Pueblo',
    tags: ['architecture', 'earthy', 'natural', 'southwestern'],
    light: {
      primary: '#CD853F',
      primaryShadow: '#A46A32',
      accent: '#40E0D0',
      accentShadow: '#33B3A6',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A060',
      primaryShadow: '#CD853F',
      accent: '#60F0E0',
      accentShadow: '#40E0D0',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Brutalist Concrete',
    tags: ['architecture', 'raw', 'bold', 'industrial'],
    light: {
      primary: '#5F5F5F',
      primaryShadow: '#4C4C4C',
      accent: '#808080',
      accentShadow: '#666666',
      background: '#F0F0F0',
      cardBackground: '#E0E0E0',
      text: '#1A1A1A',
      extra: '#A0A0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#909090',
      primaryShadow: '#5F5F5F',
      accent: '#A8A8A8',
      accentShadow: '#808080',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E0E0E0',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Victorian Manor',
    tags: ['architecture', 'ornate', 'elegant', 'historic'],
    light: {
      primary: '#800020',
      primaryShadow: '#66001A',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFEF8',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#2F4F4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B03040',
      primaryShadow: '#800020',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0808',
      cardBackground: '#151010',
      text: '#FFF5E8',
      extra: '#608080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Greek Temple',
    tags: ['architecture', 'classical', 'noble', 'ancient'],
    light: {
      primary: '#F5F5DC',
      primaryShadow: '#C4C4B0',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#FFFFFF',
      cardBackground: '#F8F8F5',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#F8F8E0',
      primaryShadow: '#F5F5DC',
      accent: '#6AA0D0',
      accentShadow: '#4682B4',
      background: '#0A0A0A',
      cardBackground: '#151512',
      text: '#F8F8F5',
      extra: '#A0E0F8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Pagoda Garden',
    tags: ['architecture', 'asian', 'peaceful', 'natural'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FAF8F5',
      cardBackground: '#F0EBE0',
      text: '#1A1A1A',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C02020',
      primaryShadow: '#8B0000',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0A0808',
      cardBackground: '#151010',
      text: '#F0EBE0',
      extra: '#E8C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Art Nouveau',
    tags: ['architecture', 'organic', 'elegant', 'decorative'],
    light: {
      primary: '#556B2F',
      primaryShadow: '#445626',
      accent: '#CD853F',
      accentShadow: '#A46A32',
      background: '#FAFFF5',
      cardBackground: '#F0F8E8',
      text: '#1A1A1A',
      extra: '#8FBC8F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#708040',
      primaryShadow: '#556B2F',
      accent: '#E0A060',
      accentShadow: '#CD853F',
      background: '#080A06',
      cardBackground: '#10150C',
      text: '#F0F8E8',
      extra: '#A8D8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Season Detail Themes
  {
    name: 'Early Spring',
    tags: ['season', 'fresh', 'gentle', 'awakening'],
    light: {
      primary: '#9ACD32',
      primaryShadow: '#7BA428',
      accent: '#FFB7C5',
      accentShadow: '#CC929D',
      background: '#FAFFF5',
      cardBackground: '#F0F8E8',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0E048',
      primaryShadow: '#9ACD32',
      accent: '#FFC8D8',
      accentShadow: '#FFB7C5',
      background: '#080A06',
      cardBackground: '#10150C',
      text: '#F0F8E8',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Late Spring',
    tags: ['season', 'blooming', 'colorful', 'lush'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFFAFC',
      cardBackground: '#FFF0F8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0C080A',
      cardBackground: '#150C12',
      text: '#FFF0F8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Early Summer',
    tags: ['season', 'bright', 'warm', 'vibrant'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFFFF5',
      cardBackground: '#FFF8E0',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0A0A06',
      cardBackground: '#15150C',
      text: '#FFF8E0',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midsummer',
    tags: ['season', 'hot', 'intense', 'peak'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6830',
      primaryShadow: '#FF4500',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0C0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Late Summer',
    tags: ['season', 'golden', 'relaxed', 'harvest'],
    light: {
      primary: '#DAA520',
      primaryShadow: '#AE841A',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F0C040',
      primaryShadow: '#DAA520',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#40B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Early Autumn',
    tags: ['season', 'changing', 'warm', 'crisp'],
    light: {
      primary: '#D2691E',
      primaryShadow: '#A85418',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E88840',
      primaryShadow: '#D2691E',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Peak Fall',
    tags: ['season', 'vivid', 'colorful', 'dramatic'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#FFFAF0',
      cardBackground: '#FFF5E0',
      text: '#1A1A1A',
      extra: '#DAA520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6830',
      primaryShadow: '#FF4500',
      accent: '#C02020',
      accentShadow: '#8B0000',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E0',
      extra: '#F0C040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Late Autumn',
    tags: ['season', 'rustic', 'bare', 'transitional'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#696969',
      accentShadow: '#545454',
      background: '#F5F0E8',
      cardBackground: '#E8E0D0',
      text: '#1A1A1A',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#909090',
      accentShadow: '#696969',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#E8E0D0',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Early Winter',
    tags: ['season', 'cold', 'stark', 'quiet'],
    light: {
      primary: '#4A5568',
      primaryShadow: '#3B4453',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#F8FAFC',
      cardBackground: '#F0F5F8',
      text: '#1A1A1A',
      extra: '#B0C4DE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0B0C0',
      primaryShadow: '#4A5568',
      accent: '#A0E0F8',
      accentShadow: '#87CEEB',
      background: '#080A0C',
      cardBackground: '#101518',
      text: '#F0F5F8',
      extra: '#C8D8F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Winter',
    tags: ['season', 'frozen', 'silent', 'crystalline'],
    light: {
      primary: '#FFFFFF',
      primaryShadow: '#CCCCCC',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#F0F8FF',
      cardBackground: '#E8F4FF',
      text: '#1A1A1A',
      extra: '#B0C4DE',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E8E8E8',
      primaryShadow: '#FFFFFF',
      accent: '#6AA0D0',
      accentShadow: '#4682B4',
      background: '#060A0E',
      cardBackground: '#0C1218',
      text: '#E8F4FF',
      extra: '#C8D8F0',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Festive Winter',
    tags: ['season', 'celebratory', 'warm', 'joyful'],
    light: {
      primary: '#C41E3A',
      primaryShadow: '#9D182E',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFFEF8',
      cardBackground: '#FFF8F0',
      text: '#1A1A1A',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E83050',
      primaryShadow: '#C41E3A',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0A0808',
      cardBackground: '#151010',
      text: '#FFF8F0',
      extra: '#E8C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Late Winter',
    tags: ['season', 'hopeful', 'transitional', 'brightening'],
    light: {
      primary: '#6A5ACD',
      primaryShadow: '#5548A4',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#F8F8FF',
      cardBackground: '#F0F0FA',
      text: '#1A1A1A',
      extra: '#DDA0DD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8878E0',
      primaryShadow: '#6A5ACD',
      accent: '#A0E0F8',
      accentShadow: '#87CEEB',
      background: '#08080C',
      cardBackground: '#101018',
      text: '#F0F0FA',
      extra: '#E8B8E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Batch 12: Gemstone Themes
  {
    name: 'Ruby Red',
    tags: ['gemstone', 'precious', 'vibrant', 'luxurious'],
    light: {
      primary: '#E0115F',
      primaryShadow: '#B30E4C',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFAFA',
      cardBackground: '#FFF0F0',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F04080',
      primaryShadow: '#E0115F',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0C0808',
      cardBackground: '#151010',
      text: '#FFF0F0',
      extra: '#C02020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Blue',
    tags: ['gemstone', 'precious', 'royal', 'deep'],
    light: {
      primary: '#0F52BA',
      primaryShadow: '#0C4295',
      accent: '#C0C0C0',
      accentShadow: '#9A9A9A',
      background: '#F5F8FF',
      cardBackground: '#E8F0FF',
      text: '#1A1A1A',
      extra: '#4169E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3070D8',
      primaryShadow: '#0F52BA',
      accent: '#D8D8D8',
      accentShadow: '#C0C0C0',
      background: '#060810',
      cardBackground: '#0C1018',
      text: '#E8F0FF',
      extra: '#6090F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Emerald Green',
    tags: ['gemstone', 'precious', 'rich', 'verdant'],
    light: {
      primary: '#50C878',
      primaryShadow: '#40A060',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F5FFF8',
      cardBackground: '#E8FFF0',
      text: '#1A1A1A',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#70E898',
      primaryShadow: '#50C878',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#060C08',
      cardBackground: '#0C1510',
      text: '#E8FFF0',
      extra: '#40B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amethyst Purple',
    tags: ['gemstone', 'precious', 'mystical', 'regal'],
    light: {
      primary: '#9966CC',
      primaryShadow: '#7A52A3',
      accent: '#C0C0C0',
      accentShadow: '#9A9A9A',
      background: '#FAF8FF',
      cardBackground: '#F0E8FF',
      text: '#1A1A1A',
      extra: '#6A5ACD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B088E0',
      primaryShadow: '#9966CC',
      accent: '#D8D8D8',
      accentShadow: '#C0C0C0',
      background: '#0A080C',
      cardBackground: '#120C18',
      text: '#F0E8FF',
      extra: '#8878E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Topaz Gold',
    tags: ['gemstone', 'precious', 'warm', 'sunny'],
    light: {
      primary: '#FFC87C',
      primaryShadow: '#CCA063',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFFEF5',
      cardBackground: '#FFF8E0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFD898',
      primaryShadow: '#FFC87C',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0A06',
      cardBackground: '#15150C',
      text: '#FFF8E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opal Iridescent',
    tags: ['gemstone', 'precious', 'ethereal', 'multicolor'],
    light: {
      primary: '#A8C3BC',
      primaryShadow: '#869C96',
      accent: '#E6C7C2',
      accentShadow: '#B89F9B',
      background: '#FAFCFF',
      cardBackground: '#F0F5F8',
      text: '#1A1A1A',
      extra: '#D4C4E4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C0D8D0',
      primaryShadow: '#A8C3BC',
      accent: '#F0D8D0',
      accentShadow: '#E6C7C2',
      background: '#08080A',
      cardBackground: '#101215',
      text: '#F0F5F8',
      extra: '#E0D0F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pearl White',
    tags: ['gemstone', 'precious', 'elegant', 'classic'],
    light: {
      primary: '#F5F5F0',
      primaryShadow: '#C4C4C0',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#1A1A1A',
      extra: '#E8E0D0',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#E8E8E0',
      primaryShadow: '#F5F5F0',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#FAFAFA',
      extra: '#F0E8D8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  {
    name: 'Onyx Black',
    tags: ['gemstone', 'precious', 'dramatic', 'sophisticated'],
    light: {
      primary: '#353839',
      primaryShadow: '#2A2D2E',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F8F8F8',
      cardBackground: '#F0F0F0',
      text: '#1A1A1A',
      extra: '#505050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0A0A0',
      primaryShadow: '#353839',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F0F0F0',
      extra: '#808080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Festival Themes
  {
    name: 'Chinese New Year',
    tags: ['festival', 'festive', 'asian', 'prosperous'],
    light: {
      primary: '#FF0000',
      primaryShadow: '#CC0000',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF4040',
      primaryShadow: '#FF0000',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0C0606',
      cardBackground: '#150C0C',
      text: '#FFF5E8',
      extra: '#C02020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Diwali Lights',
    tags: ['festival', 'festive', 'indian', 'luminous'],
    light: {
      primary: '#FF6B00',
      primaryShadow: '#CC5600',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFAF0',
      cardBackground: '#FFF5E0',
      text: '#1A1A1A',
      extra: '#9400D3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8830',
      primaryShadow: '#FF6B00',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E0',
      extra: '#B040F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mardi Gras',
    tags: ['festival', 'festive', 'carnival', 'colorful'],
    light: {
      primary: '#9400D3',
      primaryShadow: '#7600A8',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FAF8FF',
      cardBackground: '#F0E8FF',
      text: '#1A1A1A',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B040F0',
      primaryShadow: '#9400D3',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A080C',
      cardBackground: '#120C18',
      text: '#F0E8FF',
      extra: '#40B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oktoberfest',
    tags: ['festival', 'festive', 'german', 'autumn'],
    light: {
      primary: '#0077BE',
      primaryShadow: '#005F98',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F8FBFF',
      cardBackground: '#E8F4FF',
      text: '#1A1A1A',
      extra: '#FFFFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3090D0',
      primaryShadow: '#0077BE',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#060A10',
      cardBackground: '#0C1218',
      text: '#E8F4FF',
      extra: '#F8F8F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Day of the Dead',
    tags: ['festival', 'festive', 'mexican', 'vibrant'],
    light: {
      primary: '#9400D3',
      primaryShadow: '#7600A8',
      accent: '#FF6B00',
      accentShadow: '#CC5600',
      background: '#1A1A1A',
      cardBackground: '#2A2A2A',
      text: '#F5F5F5',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B040F0',
      primaryShadow: '#9400D3',
      accent: '#FF8830',
      accentShadow: '#FF6B00',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Holi Colors',
    tags: ['festival', 'festive', 'indian', 'rainbow'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#FFFFFE',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#40E8EA',
      accentShadow: '#00CED1',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F8F8F8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cherry Blossom Festival',
    tags: ['festival', 'festive', 'japanese', 'spring'],
    light: {
      primary: '#FFB7C5',
      primaryShadow: '#CC929D',
      accent: '#5D8AA8',
      accentShadow: '#4A6E86',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#1A1A1A',
      extra: '#FF69B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFC8D8',
      primaryShadow: '#FFB7C5',
      accent: '#78A8C8',
      accentShadow: '#5D8AA8',
      background: '#0C080A',
      cardBackground: '#15101A',
      text: '#FFF0F5',
      extra: '#FF88C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Carnival Brazil',
    tags: ['festival', 'festive', 'brazilian', 'tropical'],
    light: {
      primary: '#009B3A',
      primaryShadow: '#007C2E',
      accent: '#FFDF00',
      accentShadow: '#CCB300',
      background: '#F5FFF5',
      cardBackground: '#E8F5E8',
      text: '#1A1A1A',
      extra: '#002776',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30C058',
      primaryShadow: '#009B3A',
      accent: '#FFF040',
      accentShadow: '#FFDF00',
      background: '#060C06',
      cardBackground: '#0C150C',
      text: '#E8F5E8',
      extra: '#4060A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Time of Day Themes
  {
    name: 'Dawn Breaking',
    tags: ['time', 'morning', 'gentle', 'hopeful'],
    light: {
      primary: '#FFB347',
      primaryShadow: '#CC8F39',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFC870',
      primaryShadow: '#FFB347',
      accent: '#A0E0F8',
      accentShadow: '#87CEEB',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Glory',
    tags: ['time', 'morning', 'fresh', 'energizing'],
    light: {
      primary: '#87CEEB',
      primaryShadow: '#6CA5BC',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F0F8FF',
      cardBackground: '#E8F4FF',
      text: '#1A1A1A',
      extra: '#FFFFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0E0F8',
      primaryShadow: '#87CEEB',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#060A0E',
      cardBackground: '#0C1218',
      text: '#E8F4FF',
      extra: '#F8F8F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'High Noon',
    tags: ['time', 'afternoon', 'bright', 'intense'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFFF5',
      cardBackground: '#FFF8E0',
      text: '#1A1A1A',
      extra: '#FFFFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0A0A06',
      cardBackground: '#15150C',
      text: '#FFF8E0',
      extra: '#F8F8F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Hour',
    tags: ['time', 'evening', 'warm', 'magical'],
    light: {
      primary: '#FFB300',
      primaryShadow: '#CC8F00',
      accent: '#FF6347',
      accentShadow: '#CC4F39',
      background: '#FFFAF0',
      cardBackground: '#FFF5E0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFD040',
      primaryShadow: '#FFB300',
      accent: '#FF8070',
      accentShadow: '#FF6347',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Twilight Glow',
    tags: ['time', 'evening', 'transitional', 'romantic'],
    light: {
      primary: '#6A5ACD',
      primaryShadow: '#5548A4',
      accent: '#FF6347',
      accentShadow: '#CC4F39',
      background: '#FAF8FF',
      cardBackground: '#F0E8FF',
      text: '#1A1A1A',
      extra: '#FFB347',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8878E0',
      primaryShadow: '#6A5ACD',
      accent: '#FF8070',
      accentShadow: '#FF6347',
      background: '#08080C',
      cardBackground: '#101018',
      text: '#F0E8FF',
      extra: '#FFC870',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blue Hour',
    tags: ['time', 'evening', 'serene', 'contemplative'],
    light: {
      primary: '#4682B4',
      primaryShadow: '#386890',
      accent: '#2F4F4F',
      accentShadow: '#253F3F',
      background: '#F0F5FA',
      cardBackground: '#E0E8F0',
      text: '#1A1A1A',
      extra: '#6A5ACD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6AA0D0',
      primaryShadow: '#4682B4',
      accent: '#608080',
      accentShadow: '#2F4F4F',
      background: '#060810',
      cardBackground: '#0C1018',
      text: '#E0E8F0',
      extra: '#8878E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Starry Night',
    tags: ['time', 'night', 'mystical', 'cosmic'],
    light: {
      primary: '#191970',
      primaryShadow: '#141458',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F0F0FA',
      cardBackground: '#E0E0F0',
      text: '#1A1A1A',
      extra: '#4B0082',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4040A0',
      primaryShadow: '#191970',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#06060C',
      cardBackground: '#0C0C15',
      text: '#E0E0F0',
      extra: '#7030B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Deep',
    tags: ['time', 'night', 'mysterious', 'quiet'],
    light: {
      primary: '#1A1A2E',
      primaryShadow: '#101020',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#F5F5F8',
      cardBackground: '#E8E8F0',
      text: '#1A1A1A',
      extra: '#2F2F4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A4A7A',
      primaryShadow: '#1A1A2E',
      accent: '#6AA0D0',
      accentShadow: '#4682B4',
      background: '#060608',
      cardBackground: '#0C0C10',
      text: '#E8E8F0',
      extra: '#606090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Material Themes
  {
    name: 'Brushed Steel',
    tags: ['material', 'metallic', 'industrial', 'modern'],
    light: {
      primary: '#708090',
      primaryShadow: '#5A6673',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0B0C0',
      primaryShadow: '#708090',
      accent: '#6AA0D0',
      accentShadow: '#4682B4',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E8',
      extra: '#D8D8D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Polished Copper',
    tags: ['material', 'metallic', 'warm', 'rustic'],
    light: {
      primary: '#B87333',
      primaryShadow: '#935C29',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#1A1A1A',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D09050',
      primaryShadow: '#B87333',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rose Gold',
    tags: ['material', 'metallic', 'elegant', 'feminine'],
    light: {
      primary: '#B76E79',
      primaryShadow: '#925861',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#1A1A1A',
      extra: '#E6C7C2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D08898',
      primaryShadow: '#B76E79',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0C080A',
      cardBackground: '#15101A',
      text: '#FFF0F5',
      extra: '#F0D8D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Weathered Bronze',
    tags: ['material', 'metallic', 'antique', 'patina'],
    light: {
      primary: '#CD7F32',
      primaryShadow: '#A46628',
      accent: '#2E8B57',
      accentShadow: '#256F46',
      background: '#F5F0E8',
      cardBackground: '#E8E0D0',
      text: '#1A1A1A',
      extra: '#8B4513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A050',
      primaryShadow: '#CD7F32',
      accent: '#48B070',
      accentShadow: '#2E8B57',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#E8E0D0',
      extra: '#B06830',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Wood',
    tags: ['material', 'organic', 'warm', 'rustic'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Clear Glass',
    tags: ['material', 'transparent', 'modern', 'clean'],
    light: {
      primary: '#87CEEB',
      primaryShadow: '#6CA5BC',
      accent: '#E0E0E0',
      accentShadow: '#B3B3B3',
      background: '#FFFFFF',
      cardBackground: '#F8FAFC',
      text: '#1A1A1A',
      extra: '#B0C4DE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0E0F8',
      primaryShadow: '#87CEEB',
      accent: '#F0F0F0',
      accentShadow: '#E0E0E0',
      background: '#080A0C',
      cardBackground: '#101518',
      text: '#F8FAFC',
      extra: '#C8D8F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Aged Paper',
    tags: ['material', 'vintage', 'warm', 'nostalgic'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4A332C',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#F5EBE0',
      cardBackground: '#EBE0D0',
      text: '#2A1A10',
      extra: '#D2B48C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#907060',
      primaryShadow: '#5D4037',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#EBE0D0',
      extra: '#E8C8A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Polished Chrome',
    tags: ['material', 'metallic', 'sleek', 'futuristic'],
    light: {
      primary: '#C0C0C0',
      primaryShadow: '#9A9A9A',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#1A1A1A',
      extra: '#E8E8E8',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
    dark: {
      primary: '#D8D8D8',
      primaryShadow: '#C0C0C0',
      accent: '#6AA0D0',
      accentShadow: '#4682B4',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#F0F0F0',
      h1Color: 'accent',
      h2Color: 'primary',
      h3Color: 'accent',
    },
  },
  // Batch 13: Historical Era Themes
  {
    name: 'Ancient Egypt',
    tags: ['historical', 'ancient', 'golden', 'mystical'],
    light: {
      primary: '#D4AF37',
      primaryShadow: '#AA8C2C',
      accent: '#00008B',
      accentShadow: '#00006F',
      background: '#FFF8E0',
      cardBackground: '#FFF0C8',
      text: '#1A1A1A',
      extra: '#CD853F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8C850',
      primaryShadow: '#D4AF37',
      accent: '#4040B0',
      accentShadow: '#00008B',
      background: '#0A0A06',
      cardBackground: '#15150C',
      text: '#FFF0C8',
      extra: '#E0A060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Medieval Castle',
    tags: ['historical', 'medieval', 'dark', 'noble'],
    light: {
      primary: '#5D3A1A',
      primaryShadow: '#4A2E15',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#F5F0E8',
      cardBackground: '#E8E0D0',
      text: '#1A1A1A',
      extra: '#2F4F4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#906040',
      primaryShadow: '#5D3A1A',
      accent: '#C02020',
      accentShadow: '#8B0000',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#E8E0D0',
      extra: '#608080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Renaissance Art',
    tags: ['historical', 'renaissance', 'rich', 'artistic'],
    light: {
      primary: '#800020',
      primaryShadow: '#66001A',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFEF5',
      cardBackground: '#FFF8E8',
      text: '#1A1A1A',
      extra: '#00008B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B03040',
      primaryShadow: '#800020',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0808',
      cardBackground: '#151010',
      text: '#FFF8E8',
      extra: '#4040B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Victorian London',
    tags: ['historical', 'victorian', 'elegant', 'moody'],
    light: {
      primary: '#2F4F4F',
      primaryShadow: '#253F3F',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#F5F0E8',
      cardBackground: '#E8E0D0',
      text: '#1A1A1A',
      extra: '#696969',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#608080',
      primaryShadow: '#2F4F4F',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#080808',
      cardBackground: '#121212',
      text: '#E8E0D0',
      extra: '#909090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Art Deco Twenties',
    tags: ['historical', '1920s', 'glamorous', 'geometric'],
    light: {
      primary: '#1A1A2E',
      primaryShadow: '#101020',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFFEF8',
      cardBackground: '#F8F5E8',
      text: '#1A1A1A',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A4A7A',
      primaryShadow: '#1A1A2E',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#08080C',
      cardBackground: '#101018',
      text: '#F8F5E8',
      extra: '#D8D8D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Atomic Fifties',
    tags: ['historical', '1950s', 'retro', 'optimistic'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#40E0D0',
      accentShadow: '#33B3A6',
      background: '#FFFFF8',
      cardBackground: '#FFF8F0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8888',
      primaryShadow: '#FF6B6B',
      accent: '#60F0E0',
      accentShadow: '#40E0D0',
      background: '#0A0A08',
      cardBackground: '#15150C',
      text: '#FFF8F0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Groovy Sixties',
    tags: ['historical', '1960s', 'psychedelic', 'colorful'],
    light: {
      primary: '#FF6B00',
      primaryShadow: '#CC5600',
      accent: '#9400D3',
      accentShadow: '#7600A8',
      background: '#FFFAF0',
      cardBackground: '#FFF5E0',
      text: '#1A1A1A',
      extra: '#32CD32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8830',
      primaryShadow: '#FF6B00',
      accent: '#B040F0',
      accentShadow: '#9400D3',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E0',
      extra: '#50E050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Disco Seventies',
    tags: ['historical', '1970s', 'glamorous', 'funky'],
    light: {
      primary: '#D4AF37',
      primaryShadow: '#AA8C2C',
      accent: '#FF69B4',
      accentShadow: '#CC5490',
      background: '#1A1A1A',
      cardBackground: '#2A2A2A',
      text: '#F5F5F5',
      extra: '#00CED1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8C850',
      primaryShadow: '#D4AF37',
      accent: '#FF88C8',
      accentShadow: '#FF69B4',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#40E8EA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Ocean Zone Themes
  {
    name: 'Sunlit Shallows',
    tags: ['ocean', 'bright', 'tropical', 'coastal'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F0FFFF',
      cardBackground: '#E0F8F8',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8EA',
      primaryShadow: '#00CED1',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#060A0C',
      cardBackground: '#0C1215',
      text: '#E0F8F8',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coral Reef',
    tags: ['ocean', 'colorful', 'vibrant', 'tropical'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#FFFAFA',
      cardBackground: '#FFF0F0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8888',
      primaryShadow: '#FF6B6B',
      accent: '#40E8EA',
      accentShadow: '#00CED1',
      background: '#0C0808',
      cardBackground: '#151010',
      text: '#FFF0F0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Open Ocean',
    tags: ['ocean', 'vast', 'deep', 'endless'],
    light: {
      primary: '#0077BE',
      primaryShadow: '#005F98',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#F0F8FF',
      cardBackground: '#E0F0FF',
      text: '#1A1A1A',
      extra: '#FFFFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3090D0',
      primaryShadow: '#0077BE',
      accent: '#A0E0F8',
      accentShadow: '#87CEEB',
      background: '#060A10',
      cardBackground: '#0C1218',
      text: '#E0F0FF',
      extra: '#F8F8F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Twilight Zone',
    tags: ['ocean', 'mysterious', 'deep', 'transitional'],
    light: {
      primary: '#003366',
      primaryShadow: '#002952',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#F0F5FA',
      cardBackground: '#E0E8F0',
      text: '#1A1A1A',
      extra: '#191970',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4080B0',
      primaryShadow: '#003366',
      accent: '#6AA0D0',
      accentShadow: '#4682B4',
      background: '#060810',
      cardBackground: '#0C1018',
      text: '#E0E8F0',
      extra: '#4040A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Abyss',
    tags: ['ocean', 'dark', 'mysterious', 'deep'],
    light: {
      primary: '#0A0A2E',
      primaryShadow: '#060620',
      accent: '#4682B4',
      accentShadow: '#386890',
      background: '#F0F0FA',
      cardBackground: '#E0E0F0',
      text: '#1A1A1A',
      extra: '#191970',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#303060',
      primaryShadow: '#0A0A2E',
      accent: '#6AA0D0',
      accentShadow: '#4682B4',
      background: '#04040A',
      cardBackground: '#080812',
      text: '#E0E0F0',
      extra: '#4040A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bioluminescent',
    tags: ['ocean', 'glowing', 'magical', 'deep'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#00FF7F',
      accentShadow: '#00CC66',
      background: '#0A0A2E',
      cardBackground: '#101040',
      text: '#E0F0FF',
      extra: '#9370DB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8EA',
      primaryShadow: '#00CED1',
      accent: '#40FF9F',
      accentShadow: '#00FF7F',
      background: '#040410',
      cardBackground: '#080820',
      text: '#E0F0FF',
      extra: '#B090E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Flower Themes
  {
    name: 'Rose Garden',
    tags: ['flower', 'romantic', 'elegant', 'classic'],
    light: {
      primary: '#FF007F',
      primaryShadow: '#CC0066',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#1A1A1A',
      extra: '#FF69B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF40A0',
      primaryShadow: '#FF007F',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0C080A',
      cardBackground: '#150C12',
      text: '#FFF0F5',
      extra: '#FF88C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunflower Field',
    tags: ['flower', 'bright', 'cheerful', 'sunny'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFFFF5',
      cardBackground: '#FFF8E0',
      text: '#1A1A1A',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0A06',
      cardBackground: '#15150C',
      text: '#FFF8E0',
      extra: '#40B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Dreams',
    tags: ['flower', 'calm', 'fragrant', 'provence'],
    light: {
      primary: '#9370DB',
      primaryShadow: '#7658B0',
      accent: '#556B2F',
      accentShadow: '#445626',
      background: '#FAF8FF',
      cardBackground: '#F0E8FF',
      text: '#1A1A1A',
      extra: '#E6E6FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B090E0',
      primaryShadow: '#9370DB',
      accent: '#708040',
      accentShadow: '#556B2F',
      background: '#0A080C',
      cardBackground: '#120C18',
      text: '#F0E8FF',
      extra: '#F0F0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tulip Festival',
    tags: ['flower', 'vibrant', 'spring', 'colorful'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#FF69B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6830',
      primaryShadow: '#FF4500',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0C0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#FF88C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lotus Pond',
    tags: ['flower', 'peaceful', 'spiritual', 'asian'],
    light: {
      primary: '#FFB7C5',
      primaryShadow: '#CC929D',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#F8FFFA',
      cardBackground: '#F0FFF5',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFC8D8',
      primaryShadow: '#FFB7C5',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#060C08',
      cardBackground: '#0C1510',
      text: '#F0FFF5',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wildflower Meadow',
    tags: ['flower', 'natural', 'rustic', 'colorful'],
    light: {
      primary: '#9400D3',
      primaryShadow: '#7600A8',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FAFFF5',
      cardBackground: '#F0F8E8',
      text: '#1A1A1A',
      extra: '#FF6347',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B040F0',
      primaryShadow: '#9400D3',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#080A06',
      cardBackground: '#10150C',
      text: '#F0F8E8',
      extra: '#FF8070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Mood Variant Themes
  {
    name: 'Blissful',
    tags: ['mood', 'happy', 'light', 'carefree'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#CCAC00',
      accent: '#FF69B4',
      accentShadow: '#CC5490',
      background: '#FFFFF8',
      cardBackground: '#FFF8E0',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE840',
      primaryShadow: '#FFD700',
      accent: '#FF88C8',
      accentShadow: '#FF69B4',
      background: '#0A0A06',
      cardBackground: '#15150C',
      text: '#FFF8E0',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Contemplative',
    tags: ['mood', 'thoughtful', 'calm', 'introspective'],
    light: {
      primary: '#4682B4',
      primaryShadow: '#386890',
      accent: '#708090',
      accentShadow: '#5A6673',
      background: '#F5F8FA',
      cardBackground: '#E8F0F5',
      text: '#1A1A1A',
      extra: '#6A5ACD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6AA0D0',
      primaryShadow: '#4682B4',
      accent: '#90A0B0',
      accentShadow: '#708090',
      background: '#060810',
      cardBackground: '#0C1018',
      text: '#E8F0F5',
      extra: '#8878E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nostalgic',
    tags: ['mood', 'wistful', 'warm', 'remembrance'],
    light: {
      primary: '#CD853F',
      primaryShadow: '#A46A32',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#D2B48C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A060',
      primaryShadow: '#CD853F',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#E8C8A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Inspired',
    tags: ['mood', 'creative', 'energetic', 'motivated'],
    light: {
      primary: '#FF6B00',
      primaryShadow: '#CC5600',
      accent: '#4169E1',
      accentShadow: '#3454B4',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8830',
      primaryShadow: '#FF6B00',
      accent: '#6090F0',
      accentShadow: '#4169E1',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cozy',
    tags: ['mood', 'warm', 'comfortable', 'inviting'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#FF6347',
      accentShadow: '#CC4F39',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#2A1A10',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#FF8070',
      accentShadow: '#FF6347',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dreamy',
    tags: ['mood', 'ethereal', 'soft', 'whimsical'],
    light: {
      primary: '#DDA0DD',
      primaryShadow: '#B180B1',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#FFF8FF',
      cardBackground: '#FFF0FF',
      text: '#1A1A1A',
      extra: '#E6E6FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8B8E8',
      primaryShadow: '#DDA0DD',
      accent: '#A0E0F8',
      accentShadow: '#87CEEB',
      background: '#0A080A',
      cardBackground: '#150C15',
      text: '#FFF0FF',
      extra: '#F0F0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bold',
    tags: ['mood', 'confident', 'strong', 'assertive'],
    light: {
      primary: '#FF0000',
      primaryShadow: '#CC0000',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFFFF',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF4040',
      primaryShadow: '#FF0000',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0A0606',
      cardBackground: '#150C0C',
      text: '#F8F8F8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Zen',
    tags: ['mood', 'peaceful', 'balanced', 'mindful'],
    light: {
      primary: '#8FBC8F',
      primaryShadow: '#729672',
      accent: '#BC8F8F',
      accentShadow: '#967272',
      background: '#F8FAF8',
      cardBackground: '#F0F5F0',
      text: '#1A1A1A',
      extra: '#D2B48C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A8D8A8',
      primaryShadow: '#8FBC8F',
      accent: '#D0A8A8',
      accentShadow: '#BC8F8F',
      background: '#080A08',
      cardBackground: '#101510',
      text: '#F0F5F0',
      extra: '#E8C8A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Batch 14: Country-Inspired Themes
  {
    name: 'Nordic Frost',
    tags: ['country', 'scandinavian', 'cold', 'minimal'],
    light: {
      primary: '#4A5568',
      primaryShadow: '#3B4453',
      accent: '#81A1C1',
      accentShadow: '#6781A1',
      background: '#ECEFF4',
      cardBackground: '#E5E9F0',
      text: '#2E3440',
      extra: '#5E81AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D8DEE9',
      primaryShadow: '#4A5568',
      accent: '#88C0D0',
      accentShadow: '#81A1C1',
      background: '#2E3440',
      cardBackground: '#3B4252',
      text: '#ECEFF4',
      extra: '#5E81AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Japanese Minimalist',
    tags: ['country', 'asian', 'peaceful', 'zen'],
    light: {
      primary: '#2F2F2F',
      primaryShadow: '#1F1F1F',
      accent: '#C41E3A',
      accentShadow: '#9D182E',
      background: '#FAF8F5',
      cardBackground: '#F0EBE0',
      text: '#1A1A1A',
      extra: '#8B9467',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D8D8D0',
      primaryShadow: '#2F2F2F',
      accent: '#E83050',
      accentShadow: '#C41E3A',
      background: '#1A1A1A',
      cardBackground: '#252525',
      text: '#F0EBE0',
      extra: '#A8B080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Moroccan Spice',
    tags: ['country', 'african', 'warm', 'exotic'],
    light: {
      primary: '#C1440E',
      primaryShadow: '#9A360B',
      accent: '#1E5631',
      accentShadow: '#184527',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#1A1A1A',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E06030',
      primaryShadow: '#C1440E',
      accent: '#308050',
      accentShadow: '#1E5631',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#E8C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Brazilian Carnival',
    tags: ['country', 'south-american', 'vibrant', 'tropical'],
    light: {
      primary: '#009B3A',
      primaryShadow: '#007C2E',
      accent: '#FFDF00',
      accentShadow: '#CCB300',
      background: '#F5FFF8',
      cardBackground: '#E8FFF0',
      text: '#1A1A1A',
      extra: '#002776',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30C058',
      primaryShadow: '#009B3A',
      accent: '#FFF040',
      accentShadow: '#FFDF00',
      background: '#060C08',
      cardBackground: '#0C1510',
      text: '#E8FFF0',
      extra: '#4060A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Australian Outback',
    tags: ['country', 'oceanian', 'earthy', 'warm'],
    light: {
      primary: '#B35628',
      primaryShadow: '#8F4520',
      accent: '#5D4037',
      accentShadow: '#4A332C',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D07040',
      primaryShadow: '#B35628',
      accent: '#907060',
      accentShadow: '#5D4037',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scottish Highland',
    tags: ['country', 'european', 'moody', 'natural'],
    light: {
      primary: '#355E3B',
      primaryShadow: '#2A4B2F',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#F5F8F5',
      cardBackground: '#E8F0E8',
      text: '#1A1A1A',
      extra: '#4682B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#508050',
      primaryShadow: '#355E3B',
      accent: '#C02020',
      accentShadow: '#8B0000',
      background: '#060806',
      cardBackground: '#0C1210',
      text: '#E8F0E8',
      extra: '#6AA0D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Greek Islands',
    tags: ['country', 'mediterranean', 'coastal', 'bright'],
    light: {
      primary: '#0077BE',
      primaryShadow: '#005F98',
      accent: '#FFFFFF',
      accentShadow: '#CCCCCC',
      background: '#F8FBFF',
      cardBackground: '#E8F4FF',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3090D0',
      primaryShadow: '#0077BE',
      accent: '#F8F8F8',
      accentShadow: '#FFFFFF',
      background: '#060A10',
      cardBackground: '#0C1218',
      text: '#E8F4FF',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Swiss Alps',
    tags: ['country', 'european', 'fresh', 'natural'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#FFFFFF',
      accentShadow: '#CCCCCC',
      background: '#F8FAFC',
      cardBackground: '#F0F5F8',
      text: '#1A1A1A',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C02020',
      primaryShadow: '#8B0000',
      accent: '#F8F8F8',
      accentShadow: '#FFFFFF',
      background: '#080A0C',
      cardBackground: '#101518',
      text: '#F0F5F8',
      extra: '#40B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Video Game Inspired Themes
  {
    name: 'Retro Pixel',
    tags: ['game', 'retro', '8bit', 'nostalgic'],
    light: {
      primary: '#5C3566',
      primaryShadow: '#4A2B52',
      accent: '#6AAE46',
      accentShadow: '#558B38',
      background: '#E8E0F0',
      cardBackground: '#D8D0E0',
      text: '#1A1A1A',
      extra: '#3465A4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8050A0',
      primaryShadow: '#5C3566',
      accent: '#88D060',
      accentShadow: '#6AAE46',
      background: '#1A1020',
      cardBackground: '#251830',
      text: '#E0D8F0',
      extra: '#5080C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neon Arcade',
    tags: ['game', 'retro', 'neon', 'vibrant'],
    light: {
      primary: '#FF00FF',
      primaryShadow: '#CC00CC',
      accent: '#00FFFF',
      accentShadow: '#00CCCC',
      background: '#1A1A2E',
      cardBackground: '#252540',
      text: '#E0E0F0',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF40FF',
      primaryShadow: '#FF00FF',
      accent: '#40FFFF',
      accentShadow: '#00FFFF',
      background: '#0A0A15',
      cardBackground: '#151520',
      text: '#E0E0F0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fantasy Quest',
    tags: ['game', 'fantasy', 'medieval', 'adventure'],
    light: {
      primary: '#5D3A1A',
      primaryShadow: '#4A2E15',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F5EBE0',
      cardBackground: '#EBE0D0',
      text: '#1A1A1A',
      extra: '#228B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#906040',
      primaryShadow: '#5D3A1A',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#EBE0D0',
      extra: '#40B040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sci-Fi Explorer',
    tags: ['game', 'sci-fi', 'futuristic', 'space'],
    light: {
      primary: '#00CED1',
      primaryShadow: '#00A5A7',
      accent: '#9400D3',
      accentShadow: '#7600A8',
      background: '#0A0A2E',
      cardBackground: '#151540',
      text: '#E0F0FF',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8EA',
      primaryShadow: '#00CED1',
      accent: '#B040F0',
      accentShadow: '#9400D3',
      background: '#040410',
      cardBackground: '#0A0A20',
      text: '#E0F0FF',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Racing Turbo',
    tags: ['game', 'racing', 'speed', 'adrenaline'],
    light: {
      primary: '#FF0000',
      primaryShadow: '#CC0000',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#1A1A1A',
      cardBackground: '#2A2A2A',
      text: '#F5F5F5',
      extra: '#00FF00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF4040',
      primaryShadow: '#FF0000',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#40FF40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Survival Horror',
    tags: ['game', 'horror', 'dark', 'tense'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#2F2F2F',
      accentShadow: '#1F1F1F',
      background: '#F0E8E0',
      cardBackground: '#E0D0C0',
      text: '#1A1A1A',
      extra: '#696969',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C02020',
      primaryShadow: '#8B0000',
      accent: '#606060',
      accentShadow: '#2F2F2F',
      background: '#0A0808',
      cardBackground: '#15100C',
      text: '#E0D0C0',
      extra: '#909090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Platformer Fun',
    tags: ['game', 'platformer', 'colorful', 'playful'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#4ECDC4',
      accentShadow: '#3EA49D',
      background: '#FFFFFE',
      cardBackground: '#F8F8F8',
      text: '#1A1A1A',
      extra: '#FFE66D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8888',
      primaryShadow: '#FF6B6B',
      accent: '#70E0D8',
      accentShadow: '#4ECDC4',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F8F8F8',
      extra: '#FFF090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Simulation City',
    tags: ['game', 'simulation', 'urban', 'modern'],
    light: {
      primary: '#4682B4',
      primaryShadow: '#386890',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#F0F5FA',
      cardBackground: '#E0E8F0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6AA0D0',
      primaryShadow: '#4682B4',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#060A10',
      cardBackground: '#0C1018',
      text: '#E0E8F0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Movie Genre Themes
  {
    name: 'Film Noir Classic',
    tags: ['movie', 'noir', 'dramatic', 'moody'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#0A0A0A',
      accent: '#C0C0C0',
      accentShadow: '#9A9A9A',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D0D0D0',
      primaryShadow: '#1A1A1A',
      accent: '#D8D8D8',
      accentShadow: '#C0C0C0',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E8',
      extra: '#C02020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Romantic Comedy',
    tags: ['movie', 'romantic', 'cheerful', 'warm'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#CC5490',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFF8FA',
      cardBackground: '#FFF0F5',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0C080A',
      cardBackground: '#150C12',
      text: '#FFF0F5',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Action Blockbuster',
    tags: ['movie', 'action', 'explosive', 'bold'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#1A1A1A',
      accentShadow: '#0A0A0A',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6830',
      primaryShadow: '#FF4500',
      accent: '#E0E0E0',
      accentShadow: '#1A1A1A',
      background: '#0C0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sci-Fi Epic',
    tags: ['movie', 'sci-fi', 'futuristic', 'cosmic'],
    light: {
      primary: '#4B0082',
      primaryShadow: '#3C0068',
      accent: '#00CED1',
      accentShadow: '#00A5A7',
      background: '#F0F0FA',
      cardBackground: '#E0E0F0',
      text: '#1A1A1A',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7030B0',
      primaryShadow: '#4B0082',
      accent: '#40E8EA',
      accentShadow: '#00CED1',
      background: '#06060C',
      cardBackground: '#0C0C18',
      text: '#E0E0F0',
      extra: '#D8D8D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Western Frontier',
    tags: ['movie', 'western', 'rustic', 'rugged'],
    light: {
      primary: '#8B4513',
      primaryShadow: '#6F370F',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B06830',
      primaryShadow: '#8B4513',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Musical Theater',
    tags: ['movie', 'musical', 'theatrical', 'dramatic'],
    light: {
      primary: '#D4AF37',
      primaryShadow: '#AA8C2C',
      accent: '#C41E3A',
      accentShadow: '#9D182E',
      background: '#FFFEF8',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#1A1A2E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8C850',
      primaryShadow: '#D4AF37',
      accent: '#E83050',
      accentShadow: '#C41E3A',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#4A4A7A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Thriller Suspense',
    tags: ['movie', 'thriller', 'tense', 'mysterious'],
    light: {
      primary: '#2F2F4F',
      primaryShadow: '#252540',
      accent: '#8B0000',
      accentShadow: '#6F0000',
      background: '#F5F5FA',
      cardBackground: '#E8E8F0',
      text: '#1A1A1A',
      extra: '#696969',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#606090',
      primaryShadow: '#2F2F4F',
      accent: '#C02020',
      accentShadow: '#8B0000',
      background: '#08080C',
      cardBackground: '#101018',
      text: '#E8E8F0',
      extra: '#909090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Documentary Natural',
    tags: ['movie', 'documentary', 'natural', 'authentic'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#8B4513',
      accentShadow: '#6F370F',
      background: '#F5FFF5',
      cardBackground: '#E8F5E8',
      text: '#1A1A1A',
      extra: '#87CEEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B040',
      primaryShadow: '#228B22',
      accent: '#B06830',
      accentShadow: '#8B4513',
      background: '#060C06',
      cardBackground: '#0C150C',
      text: '#E8F5E8',
      extra: '#A0E0F8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // TV Show Inspired Themes
  {
    name: 'Medical Drama',
    tags: ['tv', 'medical', 'professional', 'clean'],
    light: {
      primary: '#0077BE',
      primaryShadow: '#005F98',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#F8FBFF',
      cardBackground: '#E8F4FF',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3090D0',
      primaryShadow: '#0077BE',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#060A10',
      cardBackground: '#0C1218',
      text: '#E8F4FF',
      extra: '#C02020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crime Procedural',
    tags: ['tv', 'crime', 'dark', 'urban'],
    light: {
      primary: '#2F4F4F',
      primaryShadow: '#253F3F',
      accent: '#DAA520',
      accentShadow: '#AE841A',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#608080',
      primaryShadow: '#2F4F4F',
      accent: '#F0C040',
      accentShadow: '#DAA520',
      background: '#080808',
      cardBackground: '#121212',
      text: '#E8E8E8',
      extra: '#C02020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sitcom Bright',
    tags: ['tv', 'sitcom', 'cheerful', 'warm'],
    light: {
      primary: '#FF6B6B',
      primaryShadow: '#CC5656',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFFFF8',
      cardBackground: '#FFF8E0',
      text: '#1A1A1A',
      extra: '#4ECDC4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8888',
      primaryShadow: '#FF6B6B',
      accent: '#FFE840',
      accentShadow: '#FFD700',
      background: '#0A0A08',
      cardBackground: '#15150C',
      text: '#FFF8E0',
      extra: '#70E0D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fantasy Epic TV',
    tags: ['tv', 'fantasy', 'epic', 'medieval'],
    light: {
      primary: '#5D3A1A',
      primaryShadow: '#4A2E15',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F5EBE0',
      cardBackground: '#EBE0D0',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#906040',
      primaryShadow: '#5D3A1A',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#EBE0D0',
      extra: '#C02020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Reality Competition',
    tags: ['tv', 'reality', 'energetic', 'bold'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#32CD32',
      accentShadow: '#28A428',
      background: '#FFFAF5',
      cardBackground: '#FFF5E8',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6830',
      primaryShadow: '#FF4500',
      accent: '#50E050',
      accentShadow: '#32CD32',
      background: '#0C0806',
      cardBackground: '#15100C',
      text: '#FFF5E8',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cooking Show',
    tags: ['tv', 'cooking', 'warm', 'appetizing'],
    light: {
      primary: '#8B0000',
      primaryShadow: '#6F0000',
      accent: '#228B22',
      accentShadow: '#1B6F1B',
      background: '#FFF8F0',
      cardBackground: '#FFF0E0',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C02020',
      primaryShadow: '#8B0000',
      accent: '#40B040',
      accentShadow: '#228B22',
      background: '#0A0806',
      cardBackground: '#15100C',
      text: '#FFF0E0',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'News Broadcast',
    tags: ['tv', 'news', 'professional', 'authoritative'],
    light: {
      primary: '#002366',
      primaryShadow: '#001C52',
      accent: '#C41E3A',
      accentShadow: '#9D182E',
      background: '#F8FAFF',
      cardBackground: '#E8F0FF',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4060A0',
      primaryShadow: '#002366',
      accent: '#E83050',
      accentShadow: '#C41E3A',
      background: '#060810',
      cardBackground: '#0C1018',
      text: '#E8F0FF',
      extra: '#FFE840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Talk Show Late Night',
    tags: ['tv', 'talk', 'entertainment', 'urban'],
    light: {
      primary: '#1A1A2E',
      primaryShadow: '#101020',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F5F5FA',
      cardBackground: '#E8E8F0',
      text: '#1A1A1A',
      extra: '#C41E3A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A4A7A',
      primaryShadow: '#1A1A2E',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#08080C',
      cardBackground: '#101018',
      text: '#E8E8F0',
      extra: '#E83050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ============================================
  // BATCH 15: Candy, National Parks, Art Movements, Music Eras, Holidays
  // ============================================

  // Candy/Confectionery Themes
  {
    name: 'Cotton Candy',
    tags: ['candy', 'sweet', 'pastel', 'playful'],
    light: {
      primary: '#FF6B9D',
      primaryShadow: '#E85587',
      accent: '#7DD3FC',
      accentShadow: '#5BB8E0',
      background: '#FFF5F8',
      cardBackground: '#FFE8EF',
      text: '#4A1A2A',
      extra: '#C084FC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8FB8',
      primaryShadow: '#FF6B9D',
      accent: '#93E0FF',
      accentShadow: '#7DD3FC',
      background: '#1A0A10',
      cardBackground: '#2A1520',
      text: '#FFE8EF',
      extra: '#D4A8FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Caramel Swirl',
    tags: ['candy', 'sweet', 'warm', 'cozy'],
    light: {
      primary: '#B5651D',
      primaryShadow: '#8B4513',
      accent: '#F4A460',
      accentShadow: '#CD853F',
      background: '#FFF8F0',
      cardBackground: '#FFEFD5',
      text: '#3D2314',
      extra: '#DEB887',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D4883A',
      primaryShadow: '#B5651D',
      accent: '#FFB87A',
      accentShadow: '#F4A460',
      background: '#1A0F08',
      cardBackground: '#2A1A10',
      text: '#FFEFD5',
      extra: '#F0CFA0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mint Chocolate',
    tags: ['candy', 'sweet', 'cool', 'refreshing'],
    light: {
      primary: '#3EB489',
      primaryShadow: '#2E9070',
      accent: '#5D4037',
      accentShadow: '#4A332C',
      background: '#F0FFF8',
      cardBackground: '#E0F5ED',
      text: '#1A3028',
      extra: '#8B7355',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5ED4A8',
      primaryShadow: '#3EB489',
      accent: '#8B6B5A',
      accentShadow: '#5D4037',
      background: '#0A1810',
      cardBackground: '#102820',
      text: '#E0F5ED',
      extra: '#A89078',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lemon Drop',
    tags: ['candy', 'sweet', 'bright', 'citrus'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#DAA520',
      accent: '#FFF44F',
      accentShadow: '#E8D83A',
      background: '#FFFEF0',
      cardBackground: '#FFFCE0',
      text: '#4A4000',
      extra: '#F0E68C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE44D',
      primaryShadow: '#FFD700',
      accent: '#FFFF6B',
      accentShadow: '#FFF44F',
      background: '#1A1800',
      cardBackground: '#2A2800',
      text: '#FFFCE0',
      extra: '#F5EFA0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cherry Bomb',
    tags: ['candy', 'sweet', 'bold', 'vibrant'],
    light: {
      primary: '#DC143C',
      primaryShadow: '#B01030',
      accent: '#FF6B6B',
      accentShadow: '#E85555',
      background: '#FFF5F5',
      cardBackground: '#FFE8E8',
      text: '#3D0A15',
      extra: '#FF8C8C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F03058',
      primaryShadow: '#DC143C',
      accent: '#FF8888',
      accentShadow: '#FF6B6B',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFE8E8',
      extra: '#FFA8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Grape Taffy',
    tags: ['candy', 'sweet', 'purple', 'playful'],
    light: {
      primary: '#8B5CF6',
      primaryShadow: '#7048D8',
      accent: '#A78BFA',
      accentShadow: '#8B6FE0',
      background: '#FAF5FF',
      cardBackground: '#F0E8FF',
      text: '#2D1F4A',
      extra: '#C4B5FD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A07AF8',
      primaryShadow: '#8B5CF6',
      accent: '#BDA4FC',
      accentShadow: '#A78BFA',
      background: '#0F0A18',
      cardBackground: '#1A1028',
      text: '#F0E8FF',
      extra: '#D4C8FE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bubblegum Pop',
    tags: ['candy', 'sweet', 'pink', 'fun'],
    light: {
      primary: '#FF69B4',
      primaryShadow: '#E8559A',
      accent: '#FF1493',
      accentShadow: '#DB1080',
      background: '#FFF0F5',
      cardBackground: '#FFE4EC',
      text: '#4A1530',
      extra: '#FFB6C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF88C8',
      primaryShadow: '#FF69B4',
      accent: '#FF40A8',
      accentShadow: '#FF1493',
      background: '#1A080F',
      cardBackground: '#2A101A',
      text: '#FFE4EC',
      extra: '#FFCAD5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Licorice Twist',
    tags: ['candy', 'sweet', 'dark', 'bold'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#000000',
      accent: '#E74C3C',
      accentShadow: '#C0392B',
      background: '#F5F5F5',
      cardBackground: '#E8E8E8',
      text: '#1A1A1A',
      extra: '#7F8C8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A4A4A',
      primaryShadow: '#1A1A1A',
      accent: '#F06050',
      accentShadow: '#E74C3C',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#E8E8E8',
      extra: '#95A5A6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // National Park Themes
  {
    name: 'Yellowstone Geyser',
    tags: ['nature', 'park', 'geothermal', 'dramatic'],
    light: {
      primary: '#FF6F00',
      primaryShadow: '#E56200',
      accent: '#00BCD4',
      accentShadow: '#00A0B8',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#795548',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8F30',
      primaryShadow: '#FF6F00',
      accent: '#26C6DA',
      accentShadow: '#00BCD4',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Grand Canyon',
    tags: ['nature', 'park', 'desert', 'majestic'],
    light: {
      primary: '#BF360C',
      primaryShadow: '#9A2A08',
      accent: '#FF8A65',
      accentShadow: '#E87050',
      background: '#FBE9E7',
      cardBackground: '#FFCCBC',
      text: '#3E2723',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E85A30',
      primaryShadow: '#BF360C',
      accent: '#FFAB91',
      accentShadow: '#FF8A65',
      background: '#1A0A08',
      cardBackground: '#2A1510',
      text: '#FFCCBC',
      extra: '#D7CCC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Yosemite Valley',
    tags: ['nature', 'park', 'forest', 'granite'],
    light: {
      primary: '#455A64',
      primaryShadow: '#37474F',
      accent: '#4CAF50',
      accentShadow: '#388E3C',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#78909C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#607D8B',
      primaryShadow: '#455A64',
      accent: '#66BB6A',
      accentShadow: '#4CAF50',
      background: '#0A1014',
      cardBackground: '#151E24',
      text: '#CFD8DC',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Everglades Marsh',
    tags: ['nature', 'park', 'wetland', 'tropical'],
    light: {
      primary: '#2E7D32',
      primaryShadow: '#1B5E20',
      accent: '#00897B',
      accentShadow: '#00695C',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B3D1C',
      extra: '#4DB6AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4CAF50',
      primaryShadow: '#2E7D32',
      accent: '#26A69A',
      accentShadow: '#00897B',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#80CBC4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glacier Blue',
    tags: ['nature', 'park', 'mountain', 'cold'],
    light: {
      primary: '#0288D1',
      primaryShadow: '#0277BD',
      accent: '#4FC3F7',
      accentShadow: '#29B6F6',
      background: '#E1F5FE',
      cardBackground: '#B3E5FC',
      text: '#01579B',
      extra: '#81D4FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#29B6F6',
      primaryShadow: '#0288D1',
      accent: '#81D4FA',
      accentShadow: '#4FC3F7',
      background: '#001824',
      cardBackground: '#002840',
      text: '#B3E5FC',
      extra: '#B3E5FC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Redwood Forest',
    tags: ['nature', 'park', 'forest', 'ancient'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4E342E',
      accent: '#8D6E63',
      accentShadow: '#795548',
      background: '#EFEBE9',
      cardBackground: '#D7CCC8',
      text: '#3E2723',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#5D4037',
      accent: '#BCAAA4',
      accentShadow: '#8D6E63',
      background: '#1A0F0A',
      cardBackground: '#2A1A15',
      text: '#D7CCC8',
      extra: '#D7CCC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Acadia Coastal',
    tags: ['nature', 'park', 'ocean', 'rocky'],
    light: {
      primary: '#1565C0',
      primaryShadow: '#0D47A1',
      accent: '#5E35B1',
      accentShadow: '#4527A0',
      background: '#E3F2FD',
      cardBackground: '#BBDEFB',
      text: '#0D2B4A',
      extra: '#7986CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#1565C0',
      accent: '#7E57C2',
      accentShadow: '#5E35B1',
      background: '#0A1424',
      cardBackground: '#101E34',
      text: '#BBDEFB',
      extra: '#9FA8DA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Zion Sandstone',
    tags: ['nature', 'park', 'desert', 'canyon'],
    light: {
      primary: '#D84315',
      primaryShadow: '#BF360C',
      accent: '#FF7043',
      accentShadow: '#F4511E',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#4E2600',
      extra: '#FFAB91',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5722',
      primaryShadow: '#D84315',
      accent: '#FF8A65',
      accentShadow: '#FF7043',
      background: '#1A0A05',
      cardBackground: '#2A150A',
      text: '#FFE0B2',
      extra: '#FFCCBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Art Movement Themes
  {
    name: 'Impressionist Garden',
    tags: ['art', 'impressionism', 'classic', 'soft'],
    light: {
      primary: '#7E57C2',
      primaryShadow: '#5E35B1',
      accent: '#81C784',
      accentShadow: '#66BB6A',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#311B92',
      extra: '#90CAF9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B39DDB',
      primaryShadow: '#7E57C2',
      accent: '#A5D6A7',
      accentShadow: '#81C784',
      background: '#120A1A',
      cardBackground: '#1E142A',
      text: '#E1BEE7',
      extra: '#BBDEFB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cubist Geometry',
    tags: ['art', 'cubism', 'modern', 'geometric'],
    light: {
      primary: '#37474F',
      primaryShadow: '#263238',
      accent: '#FF9800',
      accentShadow: '#F57C00',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#607D8B',
      primaryShadow: '#37474F',
      accent: '#FFB74D',
      accentShadow: '#FF9800',
      background: '#0A0E10',
      cardBackground: '#151C20',
      text: '#CFD8DC',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pop Art Bright',
    tags: ['art', 'pop', 'modern', 'bold'],
    light: {
      primary: '#E91E63',
      primaryShadow: '#C2185B',
      accent: '#FFEB3B',
      accentShadow: '#FBC02D',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#880E4F',
      extra: '#00BCD4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06292',
      primaryShadow: '#E91E63',
      accent: '#FFF176',
      accentShadow: '#FFEB3B',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#F8BBD0',
      extra: '#4DD0E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Baroque Gold',
    tags: ['art', 'baroque', 'classic', 'ornate'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4E342E',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A1887F',
      primaryShadow: '#5D4037',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Surrealist Dream',
    tags: ['art', 'surrealism', 'modern', 'dreamy'],
    light: {
      primary: '#4A148C',
      primaryShadow: '#311B92',
      accent: '#FF6F00',
      accentShadow: '#E65100',
      background: '#EDE7F6',
      cardBackground: '#D1C4E9',
      text: '#1A0A38',
      extra: '#00BFA5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C27B0',
      primaryShadow: '#4A148C',
      accent: '#FF8F00',
      accentShadow: '#FF6F00',
      background: '#0F0820',
      cardBackground: '#1A1030',
      text: '#D1C4E9',
      extra: '#1DE9B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Minimalist White',
    tags: ['art', 'minimalism', 'modern', 'clean'],
    light: {
      primary: '#212121',
      primaryShadow: '#000000',
      accent: '#757575',
      accentShadow: '#616161',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#212121',
      extra: '#BDBDBD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#212121',
      accent: '#9E9E9E',
      accentShadow: '#757575',
      background: '#0A0A0A',
      cardBackground: '#141414',
      text: '#FAFAFA',
      extra: '#616161',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Expressionist Emotion',
    tags: ['art', 'expressionism', 'modern', 'intense'],
    light: {
      primary: '#B71C1C',
      primaryShadow: '#8B0000',
      accent: '#1A237E',
      accentShadow: '#0D1442',
      background: '#FFEBEE',
      cardBackground: '#FFCDD2',
      text: '#4A0A0A',
      extra: '#FFC107',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#B71C1C',
      accent: '#5C6BC0',
      accentShadow: '#1A237E',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFCDD2',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Renaissance Master',
    tags: ['art', 'renaissance', 'classic', 'refined'],
    light: {
      primary: '#6D4C41',
      primaryShadow: '#5D4037',
      accent: '#1565C0',
      accentShadow: '#0D47A1',
      background: '#FFF8E1',
      cardBackground: '#FFE0B2',
      text: '#3E2723',
      extra: '#C62828',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A1887F',
      primaryShadow: '#6D4C41',
      accent: '#42A5F5',
      accentShadow: '#1565C0',
      background: '#1A140A',
      cardBackground: '#2A2015',
      text: '#FFE0B2',
      extra: '#EF5350',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Music Era Themes
  {
    name: 'Jazz Age',
    tags: ['music', 'jazz', 'vintage', 'sophisticated'],
    light: {
      primary: '#1A1A1A',
      primaryShadow: '#000000',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F5F0E6',
      cardBackground: '#E8E0D0',
      text: '#1A1A1A',
      extra: '#8B0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8E0D0',
      primaryShadow: '#1A1A1A',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0A0A08',
      cardBackground: '#15140F',
      text: '#E8E0D0',
      extra: '#B22222',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rock And Roll',
    tags: ['music', 'rock', 'bold', 'energetic'],
    light: {
      primary: '#D32F2F',
      primaryShadow: '#B71C1C',
      accent: '#1976D2',
      accentShadow: '#0D47A1',
      background: '#FAFAFA',
      cardBackground: '#EEEEEE',
      text: '#212121',
      extra: '#FFC107',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#D32F2F',
      accent: '#42A5F5',
      accentShadow: '#1976D2',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#EEEEEE',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Disco Fever',
    tags: ['music', 'disco', 'retro', 'glamorous'],
    light: {
      primary: '#7B1FA2',
      primaryShadow: '#4A148C',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#311B92',
      extra: '#00BCD4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BA68C8',
      primaryShadow: '#7B1FA2',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#120A18',
      cardBackground: '#1E1428',
      text: '#E1BEE7',
      extra: '#4DD0E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Punk Rock',
    tags: ['music', 'punk', 'rebellious', 'raw'],
    light: {
      primary: '#212121',
      primaryShadow: '#000000',
      accent: '#FF1744',
      accentShadow: '#D50000',
      background: '#FAFAFA',
      cardBackground: '#E0E0E0',
      text: '#212121',
      extra: '#FFEB3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BDBDBD',
      primaryShadow: '#212121',
      accent: '#FF5252',
      accentShadow: '#FF1744',
      background: '#0A0A0A',
      cardBackground: '#141414',
      text: '#E0E0E0',
      extra: '#FFF176',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hip Hop Culture',
    tags: ['music', 'hiphop', 'urban', 'bold'],
    light: {
      primary: '#FF6F00',
      primaryShadow: '#E65100',
      accent: '#00C853',
      accentShadow: '#00A043',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#1A1A1A',
      extra: '#D500F9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9100',
      primaryShadow: '#FF6F00',
      accent: '#69F0AE',
      accentShadow: '#00C853',
      background: '#1A0A00',
      cardBackground: '#2A1500',
      text: '#FFE0B2',
      extra: '#EA80FC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Electronic Wave',
    tags: ['music', 'electronic', 'modern', 'digital'],
    light: {
      primary: '#00BCD4',
      primaryShadow: '#0097A7',
      accent: '#E91E63',
      accentShadow: '#C2185B',
      background: '#E0F7FA',
      cardBackground: '#B2EBF2',
      text: '#006064',
      extra: '#7C4DFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4DD0E1',
      primaryShadow: '#00BCD4',
      accent: '#F06292',
      accentShadow: '#E91E63',
      background: '#00181C',
      cardBackground: '#002830',
      text: '#B2EBF2',
      extra: '#B388FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classical Symphony',
    tags: ['music', 'classical', 'elegant', 'refined'],
    light: {
      primary: '#37474F',
      primaryShadow: '#263238',
      accent: '#8D6E63',
      accentShadow: '#6D4C41',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#78909C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#78909C',
      primaryShadow: '#37474F',
      accent: '#BCAAA4',
      accentShadow: '#8D6E63',
      background: '#0A0C0E',
      cardBackground: '#141618',
      text: '#F5F5F5',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Reggae Vibes',
    tags: ['music', 'reggae', 'tropical', 'relaxed'],
    light: {
      primary: '#388E3C',
      primaryShadow: '#2E7D32',
      accent: '#FFC107',
      accentShadow: '#FFA000',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B5E20',
      extra: '#D32F2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#388E3C',
      accent: '#FFD54F',
      accentShadow: '#FFC107',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#EF5350',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Holiday Themes
  {
    name: 'Christmas Classic',
    tags: ['holiday', 'christmas', 'festive', 'traditional'],
    light: {
      primary: '#B71C1C',
      primaryShadow: '#7F0000',
      accent: '#1B5E20',
      accentShadow: '#0D4010',
      background: '#FFFAF0',
      cardBackground: '#FFF8DC',
      text: '#1A1A1A',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#B71C1C',
      accent: '#4CAF50',
      accentShadow: '#1B5E20',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFF8DC',
      extra: '#FFE44D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Halloween Spooky',
    tags: ['holiday', 'halloween', 'spooky', 'dark'],
    light: {
      primary: '#E65100',
      primaryShadow: '#BF360C',
      accent: '#4A148C',
      accentShadow: '#311B92',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#212121',
      extra: '#1B5E20',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9100',
      primaryShadow: '#E65100',
      accent: '#7C4DFF',
      accentShadow: '#4A148C',
      background: '#0A0505',
      cardBackground: '#151010',
      text: '#FFE0B2',
      extra: '#4CAF50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Easter Spring',
    tags: ['holiday', 'easter', 'pastel', 'fresh'],
    light: {
      primary: '#AB47BC',
      primaryShadow: '#7B1FA2',
      accent: '#66BB6A',
      accentShadow: '#43A047',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#4A148C',
      extra: '#FFB74D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CE93D8',
      primaryShadow: '#AB47BC',
      accent: '#A5D6A7',
      accentShadow: '#66BB6A',
      background: '#140A18',
      cardBackground: '#201428',
      text: '#FFECB3',
      extra: '#FFCC80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Thanksgiving Harvest',
    tags: ['holiday', 'thanksgiving', 'autumn', 'warm'],
    light: {
      primary: '#BF360C',
      primaryShadow: '#8B2500',
      accent: '#FF8F00',
      accentShadow: '#FF6F00',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#6D4C41',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5722',
      primaryShadow: '#BF360C',
      accent: '#FFB300',
      accentShadow: '#FF8F00',
      background: '#1A0A05',
      cardBackground: '#2A150A',
      text: '#FFECB3',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Valentines Romance',
    tags: ['holiday', 'valentines', 'romantic', 'love'],
    light: {
      primary: '#C2185B',
      primaryShadow: '#880E4F',
      accent: '#F8BBD0',
      accentShadow: '#F48FB1',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#880E4F',
      extra: '#D81B60',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06292',
      primaryShadow: '#C2185B',
      accent: '#F8BBD0',
      accentShadow: '#F48FB1',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#F8BBD0',
      extra: '#EC407A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Independence Day',
    tags: ['holiday', 'patriotic', 'american', 'bold'],
    light: {
      primary: '#1565C0',
      primaryShadow: '#0D47A1',
      accent: '#C62828',
      accentShadow: '#B71C1C',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#FFFFFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#1565C0',
      accent: '#EF5350',
      accentShadow: '#C62828',
      background: '#0A0A14',
      cardBackground: '#10101E',
      text: '#F5F5F5',
      extra: '#E0E0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'St Patricks Green',
    tags: ['holiday', 'irish', 'lucky', 'green'],
    light: {
      primary: '#2E7D32',
      primaryShadow: '#1B5E20',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B5E20',
      extra: '#4CAF50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#2E7D32',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#81C784',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'New Years Eve',
    tags: ['holiday', 'celebration', 'glamorous', 'festive'],
    light: {
      primary: '#212121',
      primaryShadow: '#000000',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#212121',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#050505',
      cardBackground: '#0F0F0F',
      text: '#F5F5F5',
      extra: '#B0B0B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ============================================
  // BATCH 16: Animals, Cocktails, Professions, Technology Eras
  // ============================================

  // Animal Themes
  {
    name: 'Peacock Feather',
    tags: ['animal', 'bird', 'colorful', 'exotic'],
    light: {
      primary: '#00695C',
      primaryShadow: '#004D40',
      accent: '#7B1FA2',
      accentShadow: '#4A148C',
      background: '#E0F2F1',
      cardBackground: '#B2DFDB',
      text: '#004D40',
      extra: '#1565C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A69A',
      primaryShadow: '#00695C',
      accent: '#AB47BC',
      accentShadow: '#7B1FA2',
      background: '#0A1A18',
      cardBackground: '#102A28',
      text: '#B2DFDB',
      extra: '#42A5F5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tiger Stripe',
    tags: ['animal', 'wild', 'bold', 'fierce'],
    light: {
      primary: '#E65100',
      primaryShadow: '#BF360C',
      accent: '#1A1A1A',
      accentShadow: '#000000',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#3E2723',
      extra: '#FF9800',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9100',
      primaryShadow: '#E65100',
      accent: '#424242',
      accentShadow: '#1A1A1A',
      background: '#1A0A00',
      cardBackground: '#2A1500',
      text: '#FFE0B2',
      extra: '#FFB74D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Polar Bear',
    tags: ['animal', 'arctic', 'clean', 'cold'],
    light: {
      primary: '#546E7A',
      primaryShadow: '#37474F',
      accent: '#90A4AE',
      accentShadow: '#78909C',
      background: '#FFFFFF',
      cardBackground: '#F5F5F5',
      text: '#263238',
      extra: '#B0BEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0BEC5',
      primaryShadow: '#546E7A',
      accent: '#CFD8DC',
      accentShadow: '#90A4AE',
      background: '#0A0E10',
      cardBackground: '#151A1E',
      text: '#ECEFF1',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Flamingo Pink',
    tags: ['animal', 'bird', 'tropical', 'playful'],
    light: {
      primary: '#E91E63',
      primaryShadow: '#C2185B',
      accent: '#FF8A80',
      accentShadow: '#FF5252',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#880E4F',
      extra: '#FF80AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06292',
      primaryShadow: '#E91E63',
      accent: '#FFAB91',
      accentShadow: '#FF8A80',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#F8BBD0',
      extra: '#FF80AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bumblebee',
    tags: ['animal', 'insect', 'bright', 'cheerful'],
    light: {
      primary: '#FFC107',
      primaryShadow: '#FFA000',
      accent: '#212121',
      accentShadow: '#000000',
      background: '#FFFDE7',
      cardBackground: '#FFF9C4',
      text: '#212121',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFD54F',
      primaryShadow: '#FFC107',
      accent: '#616161',
      accentShadow: '#212121',
      background: '#1A1800',
      cardBackground: '#2A2800',
      text: '#FFF9C4',
      extra: '#FFE082',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wolf Grey',
    tags: ['animal', 'wild', 'mysterious', 'powerful'],
    light: {
      primary: '#455A64',
      primaryShadow: '#263238',
      accent: '#78909C',
      accentShadow: '#546E7A',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#78909C',
      primaryShadow: '#455A64',
      accent: '#B0BEC5',
      accentShadow: '#78909C',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Parrot Tropical',
    tags: ['animal', 'bird', 'colorful', 'vibrant'],
    light: {
      primary: '#D32F2F',
      primaryShadow: '#B71C1C',
      accent: '#388E3C',
      accentShadow: '#2E7D32',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#1A1A1A',
      extra: '#1976D2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#D32F2F',
      accent: '#66BB6A',
      accentShadow: '#388E3C',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#42A5F5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dolphin Ocean',
    tags: ['animal', 'ocean', 'playful', 'sleek'],
    light: {
      primary: '#0277BD',
      primaryShadow: '#01579B',
      accent: '#4FC3F7',
      accentShadow: '#29B6F6',
      background: '#E1F5FE',
      cardBackground: '#B3E5FC',
      text: '#01579B',
      extra: '#81D4FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#29B6F6',
      primaryShadow: '#0277BD',
      accent: '#81D4FA',
      accentShadow: '#4FC3F7',
      background: '#00141C',
      cardBackground: '#002030',
      text: '#B3E5FC',
      extra: '#4FC3F7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Cocktail/Drink Themes
  {
    name: 'Margarita',
    tags: ['drink', 'cocktail', 'fresh', 'citrus'],
    light: {
      primary: '#7CB342',
      primaryShadow: '#558B2F',
      accent: '#FDD835',
      accentShadow: '#F9A825',
      background: '#F1F8E9',
      cardBackground: '#DCEDC8',
      text: '#33691E',
      extra: '#FFEE58',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9CCC65',
      primaryShadow: '#7CB342',
      accent: '#FFEE58',
      accentShadow: '#FDD835',
      background: '#0A1A08',
      cardBackground: '#15280F',
      text: '#DCEDC8',
      extra: '#FFF59D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bloody Mary',
    tags: ['drink', 'cocktail', 'bold', 'brunch'],
    light: {
      primary: '#C62828',
      primaryShadow: '#B71C1C',
      accent: '#558B2F',
      accentShadow: '#33691E',
      background: '#FFEBEE',
      cardBackground: '#FFCDD2',
      text: '#B71C1C',
      extra: '#F4511E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#C62828',
      accent: '#8BC34A',
      accentShadow: '#558B2F',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFCDD2',
      extra: '#FF7043',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blue Lagoon',
    tags: ['drink', 'cocktail', 'tropical', 'refreshing'],
    light: {
      primary: '#00ACC1',
      primaryShadow: '#00838F',
      accent: '#FFCA28',
      accentShadow: '#FFB300',
      background: '#E0F7FA',
      cardBackground: '#B2EBF2',
      text: '#006064',
      extra: '#26C6DA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4DD0E1',
      primaryShadow: '#00ACC1',
      accent: '#FFD54F',
      accentShadow: '#FFCA28',
      background: '#00181C',
      cardBackground: '#002830',
      text: '#B2EBF2',
      extra: '#80DEEA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cosmopolitan',
    tags: ['drink', 'cocktail', 'elegant', 'pink'],
    light: {
      primary: '#D81B60',
      primaryShadow: '#AD1457',
      accent: '#F48FB1',
      accentShadow: '#EC407A',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#880E4F',
      extra: '#FF80AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC407A',
      primaryShadow: '#D81B60',
      accent: '#F8BBD0',
      accentShadow: '#F48FB1',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#F8BBD0',
      extra: '#FF80AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Old Fashioned',
    tags: ['drink', 'cocktail', 'classic', 'sophisticated'],
    light: {
      primary: '#6D4C41',
      primaryShadow: '#4E342E',
      accent: '#FF8F00',
      accentShadow: '#FF6F00',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A1887F',
      primaryShadow: '#6D4C41',
      accent: '#FFB300',
      accentShadow: '#FF8F00',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mojito Fresh',
    tags: ['drink', 'cocktail', 'fresh', 'minty'],
    light: {
      primary: '#2E7D32',
      primaryShadow: '#1B5E20',
      accent: '#C0CA33',
      accentShadow: '#9E9D24',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B5E20',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#2E7D32',
      accent: '#DCE775',
      accentShadow: '#C0CA33',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#C5E1A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tequila Sunrise',
    tags: ['drink', 'cocktail', 'warm', 'gradient'],
    light: {
      primary: '#FF5722',
      primaryShadow: '#E64A19',
      accent: '#FFC107',
      accentShadow: '#FFA000',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#BF360C',
      extra: '#FF9800',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF7043',
      primaryShadow: '#FF5722',
      accent: '#FFD54F',
      accentShadow: '#FFC107',
      background: '#1A0A05',
      cardBackground: '#2A150A',
      text: '#FFE0B2',
      extra: '#FFB74D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Espresso Martini',
    tags: ['drink', 'cocktail', 'coffee', 'sophisticated'],
    light: {
      primary: '#4E342E',
      primaryShadow: '#3E2723',
      accent: '#8D6E63',
      accentShadow: '#6D4C41',
      background: '#EFEBE9',
      cardBackground: '#D7CCC8',
      text: '#3E2723',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#4E342E',
      accent: '#BCAAA4',
      accentShadow: '#8D6E63',
      background: '#1A0F0A',
      cardBackground: '#2A1A15',
      text: '#D7CCC8',
      extra: '#D7CCC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Profession Themes
  {
    name: 'Doctor Medical',
    tags: ['profession', 'medical', 'clean', 'trust'],
    light: {
      primary: '#1976D2',
      primaryShadow: '#0D47A1',
      accent: '#26A69A',
      accentShadow: '#00897B',
      background: '#E3F2FD',
      cardBackground: '#BBDEFB',
      text: '#0D47A1',
      extra: '#EF5350',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#1976D2',
      accent: '#4DB6AC',
      accentShadow: '#26A69A',
      background: '#0A1424',
      cardBackground: '#101E34',
      text: '#BBDEFB',
      extra: '#FF8A80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Chef Kitchen',
    tags: ['profession', 'culinary', 'warm', 'appetizing'],
    light: {
      primary: '#D32F2F',
      primaryShadow: '#B71C1C',
      accent: '#FF8F00',
      accentShadow: '#FF6F00',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#388E3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#D32F2F',
      accent: '#FFB300',
      accentShadow: '#FF8F00',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFECB3',
      extra: '#66BB6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lawyer Professional',
    tags: ['profession', 'legal', 'formal', 'authoritative'],
    light: {
      primary: '#1A237E',
      primaryShadow: '#0D1442',
      accent: '#8D6E63',
      accentShadow: '#6D4C41',
      background: '#E8EAF6',
      cardBackground: '#C5CAE9',
      text: '#1A237E',
      extra: '#5C6BC0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5C6BC0',
      primaryShadow: '#1A237E',
      accent: '#BCAAA4',
      accentShadow: '#8D6E63',
      background: '#0A0A14',
      cardBackground: '#10101E',
      text: '#C5CAE9',
      extra: '#7986CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artist Creative',
    tags: ['profession', 'creative', 'colorful', 'expressive'],
    light: {
      primary: '#7B1FA2',
      primaryShadow: '#4A148C',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#4A148C',
      extra: '#00BCD4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BA68C8',
      primaryShadow: '#7B1FA2',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#120A18',
      cardBackground: '#1E1428',
      text: '#E1BEE7',
      extra: '#4DD0E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Engineer Technical',
    tags: ['profession', 'engineering', 'precise', 'industrial'],
    light: {
      primary: '#37474F',
      primaryShadow: '#263238',
      accent: '#FF9800',
      accentShadow: '#F57C00',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#607D8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#607D8B',
      primaryShadow: '#37474F',
      accent: '#FFB74D',
      accentShadow: '#FF9800',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#78909C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teacher Academic',
    tags: ['profession', 'education', 'warm', 'welcoming'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4E342E',
      accent: '#2E7D32',
      accentShadow: '#1B5E20',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#D32F2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#5D4037',
      accent: '#66BB6A',
      accentShadow: '#2E7D32',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#EF5350',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pilot Aviation',
    tags: ['profession', 'aviation', 'sleek', 'professional'],
    light: {
      primary: '#0D47A1',
      primaryShadow: '#002171',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#E3F2FD',
      cardBackground: '#BBDEFB',
      text: '#0D47A1',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#1976D2',
      primaryShadow: '#0D47A1',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#0A0A14',
      cardBackground: '#10101E',
      text: '#BBDEFB',
      extra: '#B0BEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Firefighter Brave',
    tags: ['profession', 'emergency', 'bold', 'heroic'],
    light: {
      primary: '#D32F2F',
      primaryShadow: '#B71C1C',
      accent: '#FFC107',
      accentShadow: '#FFA000',
      background: '#FFEBEE',
      cardBackground: '#FFCDD2',
      text: '#B71C1C',
      extra: '#212121',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#D32F2F',
      accent: '#FFD54F',
      accentShadow: '#FFC107',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFCDD2',
      extra: '#757575',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Technology Era Themes
  {
    name: 'Mainframe Retro',
    tags: ['technology', 'retro', 'computing', 'vintage'],
    light: {
      primary: '#388E3C',
      primaryShadow: '#2E7D32',
      accent: '#212121',
      accentShadow: '#000000',
      background: '#1A1A1A',
      cardBackground: '#2A2A2A',
      text: '#4CAF50',
      extra: '#66BB6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#388E3C',
      accent: '#424242',
      accentShadow: '#212121',
      background: '#0A0A0A',
      cardBackground: '#141414',
      text: '#A5D6A7',
      extra: '#81C784',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dot Com Boom',
    tags: ['technology', 'internet', '90s', 'digital'],
    light: {
      primary: '#1565C0',
      primaryShadow: '#0D47A1',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#E3F2FD',
      cardBackground: '#BBDEFB',
      text: '#0D47A1',
      extra: '#FFC107',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#1565C0',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#0A0A14',
      cardBackground: '#10101E',
      text: '#BBDEFB',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Web Two Point Oh',
    tags: ['technology', 'internet', '2000s', 'glossy'],
    light: {
      primary: '#7B1FA2',
      primaryShadow: '#4A148C',
      accent: '#00BCD4',
      accentShadow: '#0097A7',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#4A148C',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BA68C8',
      primaryShadow: '#7B1FA2',
      accent: '#4DD0E1',
      accentShadow: '#00BCD4',
      background: '#120A18',
      cardBackground: '#1E1428',
      text: '#E1BEE7',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Flat Design Era',
    tags: ['technology', 'design', '2010s', 'minimal'],
    light: {
      primary: '#1976D2',
      primaryShadow: '#0D47A1',
      accent: '#26A69A',
      accentShadow: '#00897B',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#EF5350',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#1976D2',
      accent: '#4DB6AC',
      accentShadow: '#26A69A',
      background: '#121212',
      cardBackground: '#1E1E1E',
      text: '#FAFAFA',
      extra: '#FF8A80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'AI Future',
    tags: ['technology', 'ai', 'futuristic', 'modern'],
    light: {
      primary: '#00BFA5',
      primaryShadow: '#00897B',
      accent: '#7C4DFF',
      accentShadow: '#651FFF',
      background: '#E0F2F1',
      cardBackground: '#B2DFDB',
      text: '#004D40',
      extra: '#00E676',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#1DE9B6',
      primaryShadow: '#00BFA5',
      accent: '#B388FF',
      accentShadow: '#7C4DFF',
      background: '#0A1A18',
      cardBackground: '#102A28',
      text: '#B2DFDB',
      extra: '#69F0AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blockchain Digital',
    tags: ['technology', 'crypto', 'digital', 'futuristic'],
    light: {
      primary: '#FF9100',
      primaryShadow: '#FF6D00',
      accent: '#3F51B5',
      accentShadow: '#303F9F',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#E65100',
      extra: '#4CAF50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFAB40',
      primaryShadow: '#FF9100',
      accent: '#5C6BC0',
      accentShadow: '#3F51B5',
      background: '#1A0A00',
      cardBackground: '#2A1500',
      text: '#FFE0B2',
      extra: '#81C784',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'VR Immersive',
    tags: ['technology', 'vr', 'futuristic', 'immersive'],
    light: {
      primary: '#6200EA',
      primaryShadow: '#4A0080',
      accent: '#00E5FF',
      accentShadow: '#00B8D4',
      background: '#EDE7F6',
      cardBackground: '#D1C4E9',
      text: '#311B92',
      extra: '#FF4081',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B388FF',
      primaryShadow: '#6200EA',
      accent: '#84FFFF',
      accentShadow: '#00E5FF',
      background: '#0F0820',
      cardBackground: '#1A1030',
      text: '#D1C4E9',
      extra: '#FF80AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Smart Home',
    tags: ['technology', 'iot', 'modern', 'connected'],
    light: {
      primary: '#00ACC1',
      primaryShadow: '#00838F',
      accent: '#FFC107',
      accentShadow: '#FFA000',
      background: '#E0F7FA',
      cardBackground: '#B2EBF2',
      text: '#006064',
      extra: '#66BB6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4DD0E1',
      primaryShadow: '#00ACC1',
      accent: '#FFD54F',
      accentShadow: '#FFC107',
      background: '#00181C',
      cardBackground: '#002830',
      text: '#B2EBF2',
      extra: '#A5D6A7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ============================================
  // BATCH 17: Emotions, World Cultures, Space, Textures
  // ============================================

  // Emotion/Feeling Themes
  {
    name: 'Joyful Sunshine',
    tags: ['emotion', 'happy', 'bright', 'cheerful'],
    light: {
      primary: '#FFC107',
      primaryShadow: '#FFA000',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#FFFDE7',
      cardBackground: '#FFF9C4',
      text: '#F57F17',
      extra: '#FFEB3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFD54F',
      primaryShadow: '#FFC107',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#1A1800',
      cardBackground: '#2A2800',
      text: '#FFF9C4',
      extra: '#FFF176',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Melancholy Blue',
    tags: ['emotion', 'sad', 'moody', 'reflective'],
    light: {
      primary: '#37474F',
      primaryShadow: '#263238',
      accent: '#546E7A',
      accentShadow: '#455A64',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#607D8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#78909C',
      primaryShadow: '#37474F',
      accent: '#90A4AE',
      accentShadow: '#546E7A',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#B0BEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Passionate Fire',
    tags: ['emotion', 'passionate', 'intense', 'bold'],
    light: {
      primary: '#C62828',
      primaryShadow: '#B71C1C',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#FFEBEE',
      cardBackground: '#FFCDD2',
      text: '#B71C1C',
      extra: '#EF5350',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#C62828',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFCDD2',
      extra: '#FF8A80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Peaceful Serenity',
    tags: ['emotion', 'calm', 'peaceful', 'tranquil'],
    light: {
      primary: '#4DB6AC',
      primaryShadow: '#26A69A',
      accent: '#81D4FA',
      accentShadow: '#4FC3F7',
      background: '#E0F2F1',
      cardBackground: '#B2DFDB',
      text: '#004D40',
      extra: '#80CBC4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#80CBC4',
      primaryShadow: '#4DB6AC',
      accent: '#B3E5FC',
      accentShadow: '#81D4FA',
      background: '#0A1A18',
      cardBackground: '#102A28',
      text: '#B2DFDB',
      extra: '#A7FFEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Anxious Storm',
    tags: ['emotion', 'anxious', 'moody', 'electric'],
    light: {
      primary: '#5E35B1',
      primaryShadow: '#4527A0',
      accent: '#7C4DFF',
      accentShadow: '#651FFF',
      background: '#EDE7F6',
      cardBackground: '#D1C4E9',
      text: '#311B92',
      extra: '#B388FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9575CD',
      primaryShadow: '#5E35B1',
      accent: '#B388FF',
      accentShadow: '#7C4DFF',
      background: '#0F0820',
      cardBackground: '#1A1030',
      text: '#D1C4E9',
      extra: '#EA80FC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hopeful Dawn',
    tags: ['emotion', 'hopeful', 'optimistic', 'fresh'],
    light: {
      primary: '#FF8F00',
      primaryShadow: '#FF6F00',
      accent: '#FFC107',
      accentShadow: '#FFA000',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#E65100',
      extra: '#FFAB40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFB74D',
      primaryShadow: '#FF8F00',
      accent: '#FFD54F',
      accentShadow: '#FFC107',
      background: '#1A0A00',
      cardBackground: '#2A1500',
      text: '#FFE0B2',
      extra: '#FFCC80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Romantic Blush',
    tags: ['emotion', 'romantic', 'soft', 'loving'],
    light: {
      primary: '#EC407A',
      primaryShadow: '#D81B60',
      accent: '#F8BBD0',
      accentShadow: '#F48FB1',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#880E4F',
      extra: '#FF80AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F48FB1',
      primaryShadow: '#EC407A',
      accent: '#F8BBD0',
      accentShadow: '#F48FB1',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#F8BBD0',
      extra: '#FF80AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Confident Bold',
    tags: ['emotion', 'confident', 'strong', 'powerful'],
    light: {
      primary: '#1A237E',
      primaryShadow: '#0D1442',
      accent: '#C62828',
      accentShadow: '#B71C1C',
      background: '#E8EAF6',
      cardBackground: '#C5CAE9',
      text: '#1A237E',
      extra: '#3F51B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5C6BC0',
      primaryShadow: '#1A237E',
      accent: '#EF5350',
      accentShadow: '#C62828',
      background: '#0A0A14',
      cardBackground: '#10101E',
      text: '#C5CAE9',
      extra: '#7986CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // World Culture Themes
  {
    name: 'Japanese Sakura',
    tags: ['culture', 'japanese', 'elegant', 'spring'],
    light: {
      primary: '#F8BBD0',
      primaryShadow: '#F48FB1',
      accent: '#1A237E',
      accentShadow: '#0D1442',
      background: '#FFF0F5',
      cardBackground: '#FCE4EC',
      text: '#880E4F',
      extra: '#FF80AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F8BBD0',
      primaryShadow: '#F48FB1',
      accent: '#5C6BC0',
      accentShadow: '#1A237E',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#FCE4EC',
      extra: '#FF80AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indian Marigold',
    tags: ['culture', 'indian', 'vibrant', 'festive'],
    light: {
      primary: '#FF6F00',
      primaryShadow: '#E65100',
      accent: '#C62828',
      accentShadow: '#B71C1C',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#E65100',
      extra: '#FFD600',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9100',
      primaryShadow: '#FF6F00',
      accent: '#EF5350',
      accentShadow: '#C62828',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#FFEA00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mexican Fiesta',
    tags: ['culture', 'mexican', 'colorful', 'festive'],
    light: {
      primary: '#E91E63',
      primaryShadow: '#C2185B',
      accent: '#00BCD4',
      accentShadow: '#0097A7',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#880E4F',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06292',
      primaryShadow: '#E91E63',
      accent: '#4DD0E1',
      accentShadow: '#00BCD4',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Egyptian Gold',
    tags: ['culture', 'egyptian', 'ancient', 'royal'],
    light: {
      primary: '#FFD700',
      primaryShadow: '#DAA520',
      accent: '#1A237E',
      accentShadow: '#0D1442',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#5D4037',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFE44D',
      primaryShadow: '#FFD700',
      accent: '#5C6BC0',
      accentShadow: '#1A237E',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'African Sunset',
    tags: ['culture', 'african', 'warm', 'earthy'],
    light: {
      primary: '#BF360C',
      primaryShadow: '#8B2500',
      accent: '#FF6F00',
      accentShadow: '#E65100',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#3E2723',
      extra: '#795548',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5722',
      primaryShadow: '#BF360C',
      accent: '#FF9100',
      accentShadow: '#FF6F00',
      background: '#1A0A05',
      cardBackground: '#2A150A',
      text: '#FFE0B2',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Chinese Dynasty',
    tags: ['culture', 'chinese', 'regal', 'traditional'],
    light: {
      primary: '#C62828',
      primaryShadow: '#B71C1C',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#FFEBEE',
      cardBackground: '#FFCDD2',
      text: '#B71C1C',
      extra: '#212121',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#C62828',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFCDD2',
      extra: '#757575',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Greek Mediterranean',
    tags: ['culture', 'greek', 'coastal', 'classic'],
    light: {
      primary: '#1565C0',
      primaryShadow: '#0D47A1',
      accent: '#FFFFFF',
      accentShadow: '#E0E0E0',
      background: '#E3F2FD',
      cardBackground: '#BBDEFB',
      text: '#0D47A1',
      extra: '#26C6DA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#1565C0',
      accent: '#E0E0E0',
      accentShadow: '#BDBDBD',
      background: '#0A1424',
      cardBackground: '#101E34',
      text: '#BBDEFB',
      extra: '#4DD0E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Brazilian Tropics',
    tags: ['culture', 'brazilian', 'vibrant', 'tropical'],
    light: {
      primary: '#2E7D32',
      primaryShadow: '#1B5E20',
      accent: '#FFC107',
      accentShadow: '#FFA000',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B5E20',
      extra: '#1565C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#2E7D32',
      accent: '#FFD54F',
      accentShadow: '#FFC107',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#42A5F5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Space/Astronomy Themes
  {
    name: 'Mars Colony',
    tags: ['space', 'mars', 'futuristic', 'desert'],
    light: {
      primary: '#BF360C',
      primaryShadow: '#8B2500',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#FBE9E7',
      cardBackground: '#FFCCBC',
      text: '#3E2723',
      extra: '#FF8A65',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5722',
      primaryShadow: '#BF360C',
      accent: '#FF8A65',
      accentShadow: '#FF5722',
      background: '#1A0A05',
      cardBackground: '#2A150A',
      text: '#FFCCBC',
      extra: '#FFAB91',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nebula Purple',
    tags: ['space', 'nebula', 'cosmic', 'mystical'],
    light: {
      primary: '#6A1B9A',
      primaryShadow: '#4A148C',
      accent: '#E91E63',
      accentShadow: '#C2185B',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#4A148C',
      extra: '#7C4DFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AB47BC',
      primaryShadow: '#6A1B9A',
      accent: '#F06292',
      accentShadow: '#E91E63',
      background: '#0F0820',
      cardBackground: '#1A1030',
      text: '#E1BEE7',
      extra: '#B388FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Flare',
    tags: ['space', 'sun', 'energetic', 'bright'],
    light: {
      primary: '#FF6F00',
      primaryShadow: '#E65100',
      accent: '#FFD600',
      accentShadow: '#FFC400',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#E65100',
      extra: '#FF9100',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9100',
      primaryShadow: '#FF6F00',
      accent: '#FFEA00',
      accentShadow: '#FFD600',
      background: '#1A0A00',
      cardBackground: '#2A1500',
      text: '#FFECB3',
      extra: '#FFAB40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Space',
    tags: ['space', 'dark', 'cosmic', 'mysterious'],
    light: {
      primary: '#1A237E',
      primaryShadow: '#0D1442',
      accent: '#311B92',
      accentShadow: '#1A0A60',
      background: '#E8EAF6',
      cardBackground: '#C5CAE9',
      text: '#0D1442',
      extra: '#3F51B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5C6BC0',
      primaryShadow: '#1A237E',
      accent: '#7E57C2',
      accentShadow: '#311B92',
      background: '#050508',
      cardBackground: '#0A0A10',
      text: '#C5CAE9',
      extra: '#7986CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Aurora Borealis',
    tags: ['space', 'aurora', 'magical', 'colorful'],
    light: {
      primary: '#00BFA5',
      primaryShadow: '#00897B',
      accent: '#7C4DFF',
      accentShadow: '#651FFF',
      background: '#E0F2F1',
      cardBackground: '#B2DFDB',
      text: '#004D40',
      extra: '#1DE9B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#1DE9B6',
      primaryShadow: '#00BFA5',
      accent: '#B388FF',
      accentShadow: '#7C4DFF',
      background: '#0A1A18',
      cardBackground: '#102A28',
      text: '#B2DFDB',
      extra: '#64FFDA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Saturn Rings',
    tags: ['space', 'saturn', 'elegant', 'golden'],
    light: {
      primary: '#8D6E63',
      primaryShadow: '#6D4C41',
      accent: '#FFD54F',
      accentShadow: '#FFCA28',
      background: '#EFEBE9',
      cardBackground: '#D7CCC8',
      text: '#3E2723',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BCAAA4',
      primaryShadow: '#8D6E63',
      accent: '#FFE082',
      accentShadow: '#FFD54F',
      background: '#1A0F0A',
      cardBackground: '#2A1A15',
      text: '#D7CCC8',
      extra: '#D7CCC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Moon Base',
    tags: ['space', 'moon', 'minimal', 'grey'],
    light: {
      primary: '#546E7A',
      primaryShadow: '#37474F',
      accent: '#90A4AE',
      accentShadow: '#78909C',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#78909C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#90A4AE',
      primaryShadow: '#546E7A',
      accent: '#B0BEC5',
      accentShadow: '#90A4AE',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#B0BEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Black Hole',
    tags: ['space', 'dark', 'intense', 'powerful'],
    light: {
      primary: '#212121',
      primaryShadow: '#000000',
      accent: '#7C4DFF',
      accentShadow: '#651FFF',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#424242',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#616161',
      primaryShadow: '#212121',
      accent: '#B388FF',
      accentShadow: '#7C4DFF',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#E0E0E0',
      extra: '#757575',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Texture Themes
  {
    name: 'Velvet Luxury',
    tags: ['texture', 'velvet', 'luxurious', 'soft'],
    light: {
      primary: '#6A1B9A',
      primaryShadow: '#4A148C',
      accent: '#D4AF37',
      accentShadow: '#AA8C2C',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#4A148C',
      extra: '#9C27B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AB47BC',
      primaryShadow: '#6A1B9A',
      accent: '#E8C850',
      accentShadow: '#D4AF37',
      background: '#0F0820',
      cardBackground: '#1A1030',
      text: '#E1BEE7',
      extra: '#CE93D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rough Stone',
    tags: ['texture', 'stone', 'natural', 'rustic'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4E342E',
      accent: '#78909C',
      accentShadow: '#546E7A',
      background: '#EFEBE9',
      cardBackground: '#D7CCC8',
      text: '#3E2723',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#5D4037',
      accent: '#90A4AE',
      accentShadow: '#78909C',
      background: '#1A0F0A',
      cardBackground: '#2A1A15',
      text: '#D7CCC8',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silk Smooth',
    tags: ['texture', 'silk', 'elegant', 'smooth'],
    light: {
      primary: '#EC407A',
      primaryShadow: '#D81B60',
      accent: '#B0BEC5',
      accentShadow: '#90A4AE',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#880E4F',
      extra: '#F48FB1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F48FB1',
      primaryShadow: '#EC407A',
      accent: '#CFD8DC',
      accentShadow: '#B0BEC5',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#F8BBD0',
      extra: '#F8BBD0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Leather Bound',
    tags: ['texture', 'leather', 'classic', 'rich'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#3E2723',
      accent: '#8D6E63',
      accentShadow: '#6D4C41',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#5D4037',
      accent: '#BCAAA4',
      accentShadow: '#8D6E63',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#D7CCC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Woven Linen',
    tags: ['texture', 'linen', 'natural', 'organic'],
    light: {
      primary: '#795548',
      primaryShadow: '#5D4037',
      accent: '#A1887F',
      accentShadow: '#8D6E63',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A1887F',
      primaryShadow: '#795548',
      accent: '#BCAAA4',
      accentShadow: '#A1887F',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosted Glass',
    tags: ['texture', 'glass', 'modern', 'translucent'],
    light: {
      primary: '#90A4AE',
      primaryShadow: '#78909C',
      accent: '#4DD0E1',
      accentShadow: '#26C6DA',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#37474F',
      extra: '#B0BEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0BEC5',
      primaryShadow: '#90A4AE',
      accent: '#80DEEA',
      accentShadow: '#4DD0E1',
      background: '#0A0E10',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Brushed Metal',
    tags: ['texture', 'metal', 'industrial', 'sleek'],
    light: {
      primary: '#607D8B',
      primaryShadow: '#455A64',
      accent: '#78909C',
      accentShadow: '#546E7A',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#90A4AE',
      primaryShadow: '#607D8B',
      accent: '#B0BEC5',
      accentShadow: '#78909C',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#78909C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Corduroy Warm',
    tags: ['texture', 'corduroy', 'cozy', 'vintage'],
    light: {
      primary: '#6D4C41',
      primaryShadow: '#5D4037',
      accent: '#FF8F00',
      accentShadow: '#FF6F00',
      background: '#EFEBE9',
      cardBackground: '#D7CCC8',
      text: '#3E2723',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A1887F',
      primaryShadow: '#6D4C41',
      accent: '#FFB300',
      accentShadow: '#FF8F00',
      background: '#1A0F0A',
      cardBackground: '#2A1A15',
      text: '#D7CCC8',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ============================================
  // BATCH 18: Fantasy, Weather Patterns, Decades, Gardens
  // ============================================

  // Fantasy/Mythical Themes
  {
    name: 'Dragon Fire',
    tags: ['fantasy', 'dragon', 'fierce', 'legendary'],
    light: {
      primary: '#C62828',
      primaryShadow: '#B71C1C',
      accent: '#FF6F00',
      accentShadow: '#E65100',
      background: '#FFEBEE',
      cardBackground: '#FFCDD2',
      text: '#B71C1C',
      extra: '#212121',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#C62828',
      accent: '#FF9100',
      accentShadow: '#FF6F00',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFCDD2',
      extra: '#757575',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Unicorn Rainbow',
    tags: ['fantasy', 'unicorn', 'magical', 'pastel'],
    light: {
      primary: '#AB47BC',
      primaryShadow: '#7B1FA2',
      accent: '#4DD0E1',
      accentShadow: '#26C6DA',
      background: '#FFF0F5',
      cardBackground: '#FCE4EC',
      text: '#4A148C',
      extra: '#F8BBD0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CE93D8',
      primaryShadow: '#AB47BC',
      accent: '#80DEEA',
      accentShadow: '#4DD0E1',
      background: '#140A18',
      cardBackground: '#201428',
      text: '#FCE4EC',
      extra: '#F8BBD0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wizard Mystique',
    tags: ['fantasy', 'wizard', 'mysterious', 'arcane'],
    light: {
      primary: '#4A148C',
      primaryShadow: '#311B92',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#EDE7F6',
      cardBackground: '#D1C4E9',
      text: '#311B92',
      extra: '#7C4DFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C27B0',
      primaryShadow: '#4A148C',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#0F0820',
      cardBackground: '#1A1030',
      text: '#D1C4E9',
      extra: '#B388FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mermaid Lagoon',
    tags: ['fantasy', 'mermaid', 'aquatic', 'shimmering'],
    light: {
      primary: '#00897B',
      primaryShadow: '#00695C',
      accent: '#7C4DFF',
      accentShadow: '#651FFF',
      background: '#E0F2F1',
      cardBackground: '#B2DFDB',
      text: '#004D40',
      extra: '#4DB6AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A69A',
      primaryShadow: '#00897B',
      accent: '#B388FF',
      accentShadow: '#7C4DFF',
      background: '#0A1A18',
      cardBackground: '#102A28',
      text: '#B2DFDB',
      extra: '#80CBC4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Phoenix Rising',
    tags: ['fantasy', 'phoenix', 'rebirth', 'fiery'],
    light: {
      primary: '#FF5722',
      primaryShadow: '#E64A19',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#BF360C',
      extra: '#FF8F00',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF7043',
      primaryShadow: '#FF5722',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#1A0A05',
      cardBackground: '#2A150A',
      text: '#FFE0B2',
      extra: '#FFAB40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Elven Forest',
    tags: ['fantasy', 'elf', 'nature', 'ethereal'],
    light: {
      primary: '#2E7D32',
      primaryShadow: '#1B5E20',
      accent: '#8BC34A',
      accentShadow: '#7CB342',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B5E20',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#2E7D32',
      accent: '#C5E1A5',
      accentShadow: '#8BC34A',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#DCEDC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fairy Dust',
    tags: ['fantasy', 'fairy', 'sparkle', 'whimsical'],
    light: {
      primary: '#EC407A',
      primaryShadow: '#D81B60',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#880E4F',
      extra: '#CE93D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F48FB1',
      primaryShadow: '#EC407A',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#F8BBD0',
      extra: '#E1BEE7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vampire Gothic',
    tags: ['fantasy', 'vampire', 'dark', 'elegant'],
    light: {
      primary: '#880E4F',
      primaryShadow: '#560027',
      accent: '#212121',
      accentShadow: '#000000',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#560027',
      extra: '#C62828',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD1457',
      primaryShadow: '#880E4F',
      accent: '#616161',
      accentShadow: '#212121',
      background: '#0A0505',
      cardBackground: '#150A0A',
      text: '#F8BBD0',
      extra: '#EF5350',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Weather Pattern Themes
  {
    name: 'Thunderstorm',
    tags: ['weather', 'storm', 'dramatic', 'electric'],
    light: {
      primary: '#37474F',
      primaryShadow: '#263238',
      accent: '#FFEB3B',
      accentShadow: '#FBC02D',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#7C4DFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#78909C',
      primaryShadow: '#37474F',
      accent: '#FFF176',
      accentShadow: '#FFEB3B',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#B388FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Drizzle',
    tags: ['weather', 'rain', 'calm', 'moody'],
    light: {
      primary: '#546E7A',
      primaryShadow: '#455A64',
      accent: '#78909C',
      accentShadow: '#607D8B',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#37474F',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#90A4AE',
      primaryShadow: '#546E7A',
      accent: '#B0BEC5',
      accentShadow: '#78909C',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#B0BEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blizzard White',
    tags: ['weather', 'snow', 'cold', 'intense'],
    light: {
      primary: '#607D8B',
      primaryShadow: '#455A64',
      accent: '#B0BEC5',
      accentShadow: '#90A4AE',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#37474F',
      extra: '#E0E0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0BEC5',
      primaryShadow: '#607D8B',
      accent: '#CFD8DC',
      accentShadow: '#B0BEC5',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#ECEFF1',
      extra: '#78909C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Heat Wave',
    tags: ['weather', 'hot', 'intense', 'summer'],
    light: {
      primary: '#FF6F00',
      primaryShadow: '#E65100',
      accent: '#FFC107',
      accentShadow: '#FFA000',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#E65100',
      extra: '#FF5722',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9100',
      primaryShadow: '#FF6F00',
      accent: '#FFD54F',
      accentShadow: '#FFC107',
      background: '#1A0A00',
      cardBackground: '#2A1500',
      text: '#FFECB3',
      extra: '#FF7043',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Mist',
    tags: ['weather', 'fog', 'soft', 'mysterious'],
    light: {
      primary: '#78909C',
      primaryShadow: '#607D8B',
      accent: '#B0BEC5',
      accentShadow: '#90A4AE',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#455A64',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0BEC5',
      primaryShadow: '#78909C',
      accent: '#CFD8DC',
      accentShadow: '#B0BEC5',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#ECEFF1',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Double Rainbow',
    tags: ['weather', 'rainbow', 'colorful', 'hopeful'],
    light: {
      primary: '#E91E63',
      primaryShadow: '#C2185B',
      accent: '#00BCD4',
      accentShadow: '#0097A7',
      background: '#FFFDE7',
      cardBackground: '#FFF9C4',
      text: '#212121',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06292',
      primaryShadow: '#E91E63',
      accent: '#4DD0E1',
      accentShadow: '#00BCD4',
      background: '#1A1800',
      cardBackground: '#2A2800',
      text: '#FFF9C4',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tornado Grey',
    tags: ['weather', 'tornado', 'dramatic', 'powerful'],
    light: {
      primary: '#455A64',
      primaryShadow: '#263238',
      accent: '#78909C',
      accentShadow: '#546E7A',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#37474F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#78909C',
      primaryShadow: '#455A64',
      accent: '#90A4AE',
      accentShadow: '#78909C',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#CFD8DC',
      extra: '#607D8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Clear Skies',
    tags: ['weather', 'sunny', 'clear', 'optimistic'],
    light: {
      primary: '#1976D2',
      primaryShadow: '#0D47A1',
      accent: '#FFC107',
      accentShadow: '#FFA000',
      background: '#E3F2FD',
      cardBackground: '#BBDEFB',
      text: '#0D47A1',
      extra: '#81D4FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#1976D2',
      accent: '#FFD54F',
      accentShadow: '#FFC107',
      background: '#0A1424',
      cardBackground: '#101E34',
      text: '#BBDEFB',
      extra: '#B3E5FC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Decade Fashion Themes
  {
    name: 'Roaring Twenties',
    tags: ['decade', '1920s', 'art-deco', 'glamorous'],
    light: {
      primary: '#212121',
      primaryShadow: '#000000',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#212121',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#212121',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#0A0A08',
      cardBackground: '#15140F',
      text: '#FFECB3',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fifties Diner',
    tags: ['decade', '1950s', 'retro', 'cheerful'],
    light: {
      primary: '#D32F2F',
      primaryShadow: '#B71C1C',
      accent: '#26C6DA',
      accentShadow: '#00ACC1',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#FFC107',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#D32F2F',
      accent: '#4DD0E1',
      accentShadow: '#26C6DA',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Swinging Sixties',
    tags: ['decade', '1960s', 'mod', 'bold'],
    light: {
      primary: '#FF5722',
      primaryShadow: '#E64A19',
      accent: '#FFEB3B',
      accentShadow: '#FBC02D',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#E91E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF7043',
      primaryShadow: '#FF5722',
      accent: '#FFF176',
      accentShadow: '#FFEB3B',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#F06292',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Groovy Seventies',
    tags: ['decade', '1970s', 'earthy', 'bohemian'],
    light: {
      primary: '#BF360C',
      primaryShadow: '#8B2500',
      accent: '#FFA000',
      accentShadow: '#FF8F00',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#558B2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5722',
      primaryShadow: '#BF360C',
      accent: '#FFB74D',
      accentShadow: '#FFA000',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rad Eighties',
    tags: ['decade', '1980s', 'neon', 'energetic'],
    light: {
      primary: '#E91E63',
      primaryShadow: '#C2185B',
      accent: '#00E5FF',
      accentShadow: '#00B8D4',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#76FF03',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06292',
      primaryShadow: '#E91E63',
      accent: '#84FFFF',
      accentShadow: '#00E5FF',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#F5F5F5',
      extra: '#B2FF59',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Grunge Nineties',
    tags: ['decade', '1990s', 'grunge', 'alternative'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4E342E',
      accent: '#7B1FA2',
      accentShadow: '#4A148C',
      background: '#EFEBE9',
      cardBackground: '#D7CCC8',
      text: '#3E2723',
      extra: '#455A64',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#5D4037',
      accent: '#BA68C8',
      accentShadow: '#7B1FA2',
      background: '#1A0F0A',
      cardBackground: '#2A1A15',
      text: '#D7CCC8',
      extra: '#78909C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Y2K Millennium',
    tags: ['decade', '2000s', 'shiny', 'tech'],
    light: {
      primary: '#00BCD4',
      primaryShadow: '#0097A7',
      accent: '#E91E63',
      accentShadow: '#C2185B',
      background: '#E0F7FA',
      cardBackground: '#B2EBF2',
      text: '#006064',
      extra: '#C0C0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4DD0E1',
      primaryShadow: '#00BCD4',
      accent: '#F06292',
      accentShadow: '#E91E63',
      background: '#00181C',
      cardBackground: '#002830',
      text: '#B2EBF2',
      extra: '#9E9E9E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Modern Minimal',
    tags: ['decade', '2010s', 'minimal', 'clean'],
    light: {
      primary: '#212121',
      primaryShadow: '#000000',
      accent: '#1976D2',
      accentShadow: '#0D47A1',
      background: '#FFFFFF',
      cardBackground: '#FAFAFA',
      text: '#212121',
      extra: '#BDBDBD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#212121',
      accent: '#42A5F5',
      accentShadow: '#1976D2',
      background: '#121212',
      cardBackground: '#1E1E1E',
      text: '#FAFAFA',
      extra: '#616161',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Garden/Plant Themes
  {
    name: 'Succulent Garden',
    tags: ['garden', 'plant', 'desert', 'organic'],
    light: {
      primary: '#558B2F',
      primaryShadow: '#33691E',
      accent: '#8D6E63',
      accentShadow: '#6D4C41',
      background: '#F1F8E9',
      cardBackground: '#DCEDC8',
      text: '#33691E',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8BC34A',
      primaryShadow: '#558B2F',
      accent: '#BCAAA4',
      accentShadow: '#8D6E63',
      background: '#0A1A08',
      cardBackground: '#15280F',
      text: '#DCEDC8',
      extra: '#C5E1A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Herb Kitchen',
    tags: ['garden', 'herb', 'fresh', 'culinary'],
    light: {
      primary: '#2E7D32',
      primaryShadow: '#1B5E20',
      accent: '#C0CA33',
      accentShadow: '#9E9D24',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B5E20',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#2E7D32',
      accent: '#DCE775',
      accentShadow: '#C0CA33',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Jungle',
    tags: ['garden', 'tropical', 'lush', 'vibrant'],
    light: {
      primary: '#1B5E20',
      primaryShadow: '#0D4010',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#0D4010',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4CAF50',
      primaryShadow: '#1B5E20',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'English Garden',
    tags: ['garden', 'english', 'romantic', 'classic'],
    light: {
      primary: '#388E3C',
      primaryShadow: '#2E7D32',
      accent: '#EC407A',
      accentShadow: '#D81B60',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B5E20',
      extra: '#AB47BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#388E3C',
      accent: '#F48FB1',
      accentShadow: '#EC407A',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#CE93D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Zen Rock Garden',
    tags: ['garden', 'zen', 'minimal', 'peaceful'],
    light: {
      primary: '#78909C',
      primaryShadow: '#607D8B',
      accent: '#4CAF50',
      accentShadow: '#43A047',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#37474F',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0BEC5',
      primaryShadow: '#78909C',
      accent: '#81C784',
      accentShadow: '#4CAF50',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cottage Wildflower',
    tags: ['garden', 'cottage', 'wildflower', 'rustic'],
    light: {
      primary: '#7B1FA2',
      primaryShadow: '#4A148C',
      accent: '#FFC107',
      accentShadow: '#FFA000',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#4A148C',
      extra: '#4CAF50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BA68C8',
      primaryShadow: '#7B1FA2',
      accent: '#FFD54F',
      accentShadow: '#FFC107',
      background: '#120A18',
      cardBackground: '#1E1428',
      text: '#E1BEE7',
      extra: '#81C784',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bamboo Grove',
    tags: ['garden', 'bamboo', 'asian', 'serene'],
    light: {
      primary: '#689F38',
      primaryShadow: '#558B2F',
      accent: '#5D4037',
      accentShadow: '#4E342E',
      background: '#F1F8E9',
      cardBackground: '#DCEDC8',
      text: '#33691E',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9CCC65',
      primaryShadow: '#689F38',
      accent: '#8D6E63',
      accentShadow: '#5D4037',
      background: '#0A1A08',
      cardBackground: '#15280F',
      text: '#DCEDC8',
      extra: '#C5E1A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Harvest Garden',
    tags: ['garden', 'autumn', 'harvest', 'warm'],
    light: {
      primary: '#E65100',
      primaryShadow: '#BF360C',
      accent: '#6D4C41',
      accentShadow: '#5D4037',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#3E2723',
      extra: '#AFB42B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9100',
      primaryShadow: '#E65100',
      accent: '#A1887F',
      accentShadow: '#6D4C41',
      background: '#1A0A05',
      cardBackground: '#2A150A',
      text: '#FFE0B2',
      extra: '#D4E157',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // ============================================
  // BATCH 19: Zodiac, Music Genres, Interior Design, Season Variations
  // ============================================

  // Zodiac Sign Themes
  {
    name: 'Aries Fire',
    tags: ['zodiac', 'aries', 'fire', 'bold'],
    light: {
      primary: '#D32F2F',
      primaryShadow: '#B71C1C',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#FFEBEE',
      cardBackground: '#FFCDD2',
      text: '#B71C1C',
      extra: '#FF8A65',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#D32F2F',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFCDD2',
      extra: '#FFAB91',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Taurus Earth',
    tags: ['zodiac', 'taurus', 'earth', 'stable'],
    light: {
      primary: '#558B2F',
      primaryShadow: '#33691E',
      accent: '#8D6E63',
      accentShadow: '#6D4C41',
      background: '#F1F8E9',
      cardBackground: '#DCEDC8',
      text: '#33691E',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8BC34A',
      primaryShadow: '#558B2F',
      accent: '#BCAAA4',
      accentShadow: '#8D6E63',
      background: '#0A1A08',
      cardBackground: '#15280F',
      text: '#DCEDC8',
      extra: '#C5E1A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gemini Air',
    tags: ['zodiac', 'gemini', 'air', 'dual'],
    light: {
      primary: '#FFC107',
      primaryShadow: '#FFA000',
      accent: '#00BCD4',
      accentShadow: '#0097A7',
      background: '#FFFDE7',
      cardBackground: '#FFF9C4',
      text: '#F57F17',
      extra: '#E0F7FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFD54F',
      primaryShadow: '#FFC107',
      accent: '#4DD0E1',
      accentShadow: '#00BCD4',
      background: '#1A1800',
      cardBackground: '#2A2800',
      text: '#FFF9C4',
      extra: '#B2EBF2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cancer Water',
    tags: ['zodiac', 'cancer', 'water', 'nurturing'],
    light: {
      primary: '#90A4AE',
      primaryShadow: '#78909C',
      accent: '#4FC3F7',
      accentShadow: '#29B6F6',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#37474F',
      extra: '#81D4FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0BEC5',
      primaryShadow: '#90A4AE',
      accent: '#81D4FA',
      accentShadow: '#4FC3F7',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#B3E5FC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Leo Sun',
    tags: ['zodiac', 'leo', 'fire', 'regal'],
    light: {
      primary: '#FF6F00',
      primaryShadow: '#E65100',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#E65100',
      extra: '#FFC107',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9100',
      primaryShadow: '#FF6F00',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#1A0A00',
      cardBackground: '#2A1500',
      text: '#FFECB3',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Virgo Earth',
    tags: ['zodiac', 'virgo', 'earth', 'precise'],
    light: {
      primary: '#689F38',
      primaryShadow: '#558B2F',
      accent: '#795548',
      accentShadow: '#5D4037',
      background: '#F1F8E9',
      cardBackground: '#DCEDC8',
      text: '#33691E',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9CCC65',
      primaryShadow: '#689F38',
      accent: '#A1887F',
      accentShadow: '#795548',
      background: '#0A1A08',
      cardBackground: '#15280F',
      text: '#DCEDC8',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Libra Balance',
    tags: ['zodiac', 'libra', 'air', 'harmonious'],
    light: {
      primary: '#EC407A',
      primaryShadow: '#D81B60',
      accent: '#4DD0E1',
      accentShadow: '#26C6DA',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#880E4F',
      extra: '#80DEEA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F48FB1',
      primaryShadow: '#EC407A',
      accent: '#80DEEA',
      accentShadow: '#4DD0E1',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#F8BBD0',
      extra: '#B2EBF2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scorpio Deep',
    tags: ['zodiac', 'scorpio', 'water', 'intense'],
    light: {
      primary: '#880E4F',
      primaryShadow: '#560027',
      accent: '#212121',
      accentShadow: '#000000',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#560027',
      extra: '#C62828',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD1457',
      primaryShadow: '#880E4F',
      accent: '#616161',
      accentShadow: '#212121',
      background: '#0A0505',
      cardBackground: '#150A0A',
      text: '#F8BBD0',
      extra: '#EF5350',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sagittarius Adventure',
    tags: ['zodiac', 'sagittarius', 'fire', 'exploratory'],
    light: {
      primary: '#7B1FA2',
      primaryShadow: '#4A148C',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#4A148C',
      extra: '#9C27B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BA68C8',
      primaryShadow: '#7B1FA2',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#120A18',
      cardBackground: '#1E1428',
      text: '#E1BEE7',
      extra: '#CE93D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Capricorn Mountain',
    tags: ['zodiac', 'capricorn', 'earth', 'ambitious'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4E342E',
      accent: '#607D8B',
      accentShadow: '#455A64',
      background: '#EFEBE9',
      cardBackground: '#D7CCC8',
      text: '#3E2723',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#5D4037',
      accent: '#90A4AE',
      accentShadow: '#607D8B',
      background: '#1A0F0A',
      cardBackground: '#2A1A15',
      text: '#D7CCC8',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Aquarius Vision',
    tags: ['zodiac', 'aquarius', 'air', 'innovative'],
    light: {
      primary: '#00BCD4',
      primaryShadow: '#0097A7',
      accent: '#7C4DFF',
      accentShadow: '#651FFF',
      background: '#E0F7FA',
      cardBackground: '#B2EBF2',
      text: '#006064',
      extra: '#26C6DA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4DD0E1',
      primaryShadow: '#00BCD4',
      accent: '#B388FF',
      accentShadow: '#7C4DFF',
      background: '#00181C',
      cardBackground: '#002830',
      text: '#B2EBF2',
      extra: '#80DEEA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pisces Dream',
    tags: ['zodiac', 'pisces', 'water', 'dreamy'],
    light: {
      primary: '#7E57C2',
      primaryShadow: '#5E35B1',
      accent: '#4FC3F7',
      accentShadow: '#29B6F6',
      background: '#EDE7F6',
      cardBackground: '#D1C4E9',
      text: '#311B92',
      extra: '#B39DDB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B39DDB',
      primaryShadow: '#7E57C2',
      accent: '#81D4FA',
      accentShadow: '#4FC3F7',
      background: '#0F0820',
      cardBackground: '#1A1030',
      text: '#D1C4E9',
      extra: '#CE93D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Music Genre Themes
  {
    name: 'Blues Soul',
    tags: ['music', 'blues', 'soulful', 'deep'],
    light: {
      primary: '#1565C0',
      primaryShadow: '#0D47A1',
      accent: '#5D4037',
      accentShadow: '#4E342E',
      background: '#E3F2FD',
      cardBackground: '#BBDEFB',
      text: '#0D47A1',
      extra: '#42A5F5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A5F5',
      primaryShadow: '#1565C0',
      accent: '#8D6E63',
      accentShadow: '#5D4037',
      background: '#0A1424',
      cardBackground: '#101E34',
      text: '#BBDEFB',
      extra: '#64B5F6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Country Western',
    tags: ['music', 'country', 'rustic', 'warm'],
    light: {
      primary: '#8D6E63',
      primaryShadow: '#6D4C41',
      accent: '#FF8F00',
      accentShadow: '#FF6F00',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BCAAA4',
      primaryShadow: '#8D6E63',
      accent: '#FFB300',
      accentShadow: '#FF8F00',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#D7CCC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Metal Heavy',
    tags: ['music', 'metal', 'dark', 'intense'],
    light: {
      primary: '#212121',
      primaryShadow: '#000000',
      accent: '#C62828',
      accentShadow: '#B71C1C',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#424242',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#757575',
      primaryShadow: '#212121',
      accent: '#EF5350',
      accentShadow: '#C62828',
      background: '#0A0A0A',
      cardBackground: '#141414',
      text: '#E0E0E0',
      extra: '#616161',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indie Folk',
    tags: ['music', 'indie', 'organic', 'authentic'],
    light: {
      primary: '#689F38',
      primaryShadow: '#558B2F',
      accent: '#795548',
      accentShadow: '#5D4037',
      background: '#F1F8E9',
      cardBackground: '#DCEDC8',
      text: '#33691E',
      extra: '#8BC34A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9CCC65',
      primaryShadow: '#689F38',
      accent: '#A1887F',
      accentShadow: '#795548',
      background: '#0A1A08',
      cardBackground: '#15280F',
      text: '#DCEDC8',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'R And B Smooth',
    tags: ['music', 'rnb', 'smooth', 'sophisticated'],
    light: {
      primary: '#6A1B9A',
      primaryShadow: '#4A148C',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#4A148C',
      extra: '#9C27B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AB47BC',
      primaryShadow: '#6A1B9A',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#0F0820',
      cardBackground: '#1A1030',
      text: '#E1BEE7',
      extra: '#CE93D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Latin Passion',
    tags: ['music', 'latin', 'vibrant', 'passionate'],
    light: {
      primary: '#C62828',
      primaryShadow: '#B71C1C',
      accent: '#FF6F00',
      accentShadow: '#E65100',
      background: '#FFEBEE',
      cardBackground: '#FFCDD2',
      text: '#B71C1C',
      extra: '#FFC107',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF5350',
      primaryShadow: '#C62828',
      accent: '#FF9100',
      accentShadow: '#FF6F00',
      background: '#1A0808',
      cardBackground: '#2A1010',
      text: '#FFCDD2',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ambient Chill',
    tags: ['music', 'ambient', 'relaxed', 'atmospheric'],
    light: {
      primary: '#4DB6AC',
      primaryShadow: '#26A69A',
      accent: '#7E57C2',
      accentShadow: '#5E35B1',
      background: '#E0F2F1',
      cardBackground: '#B2DFDB',
      text: '#004D40',
      extra: '#80CBC4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#80CBC4',
      primaryShadow: '#4DB6AC',
      accent: '#B39DDB',
      accentShadow: '#7E57C2',
      background: '#0A1A18',
      cardBackground: '#102A28',
      text: '#B2DFDB',
      extra: '#A7FFEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Funk Groove',
    tags: ['music', 'funk', 'groovy', 'energetic'],
    light: {
      primary: '#7B1FA2',
      primaryShadow: '#4A148C',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#F3E5F5',
      cardBackground: '#E1BEE7',
      text: '#4A148C',
      extra: '#FFC107',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BA68C8',
      primaryShadow: '#7B1FA2',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#120A18',
      cardBackground: '#1E1428',
      text: '#E1BEE7',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Interior Design Themes
  {
    name: 'Bohemian Eclectic',
    tags: ['interior', 'bohemian', 'eclectic', 'colorful'],
    light: {
      primary: '#8D6E63',
      primaryShadow: '#6D4C41',
      accent: '#E91E63',
      accentShadow: '#C2185B',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#009688',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BCAAA4',
      primaryShadow: '#8D6E63',
      accent: '#F06292',
      accentShadow: '#E91E63',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#4DB6AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coastal Beach',
    tags: ['interior', 'coastal', 'beach', 'relaxed'],
    light: {
      primary: '#0288D1',
      primaryShadow: '#0277BD',
      accent: '#A1887F',
      accentShadow: '#8D6E63',
      background: '#E1F5FE',
      cardBackground: '#B3E5FC',
      text: '#01579B',
      extra: '#81D4FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#29B6F6',
      primaryShadow: '#0288D1',
      accent: '#BCAAA4',
      accentShadow: '#A1887F',
      background: '#001824',
      cardBackground: '#002840',
      text: '#B3E5FC',
      extra: '#4FC3F7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Modern Farmhouse',
    tags: ['interior', 'farmhouse', 'rustic', 'modern'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4E342E',
      accent: '#388E3C',
      accentShadow: '#2E7D32',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#5D4037',
      accent: '#66BB6A',
      accentShadow: '#388E3C',
      background: '#121212',
      cardBackground: '#1E1E1E',
      text: '#FAFAFA',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hollywood Glam',
    tags: ['interior', 'glamorous', 'luxurious', 'dramatic'],
    light: {
      primary: '#212121',
      primaryShadow: '#000000',
      accent: '#FFD700',
      accentShadow: '#DAA520',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#212121',
      extra: '#7B1FA2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#212121',
      accent: '#FFE44D',
      accentShadow: '#FFD700',
      background: '#0A0A0A',
      cardBackground: '#151515',
      text: '#FAFAFA',
      extra: '#BA68C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Japandi Fusion',
    tags: ['interior', 'japandi', 'minimal', 'warm'],
    light: {
      primary: '#5D4037',
      primaryShadow: '#4E342E',
      accent: '#78909C',
      accentShadow: '#607D8B',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D6E63',
      primaryShadow: '#5D4037',
      accent: '#90A4AE',
      accentShadow: '#78909C',
      background: '#1A1408',
      cardBackground: '#2A2010',
      text: '#FFECB3',
      extra: '#A1887F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Maximalist Bold',
    tags: ['interior', 'maximalist', 'bold', 'colorful'],
    light: {
      primary: '#E91E63',
      primaryShadow: '#C2185B',
      accent: '#00BCD4',
      accentShadow: '#0097A7',
      background: '#FCE4EC',
      cardBackground: '#F8BBD0',
      text: '#880E4F',
      extra: '#FFC107',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F06292',
      primaryShadow: '#E91E63',
      accent: '#4DD0E1',
      accentShadow: '#00BCD4',
      background: '#1A080E',
      cardBackground: '#2A1018',
      text: '#F8BBD0',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Industrial',
    tags: ['interior', 'industrial', 'urban', 'raw'],
    light: {
      primary: '#455A64',
      primaryShadow: '#37474F',
      accent: '#FF5722',
      accentShadow: '#E64A19',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#607D8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#78909C',
      primaryShadow: '#455A64',
      accent: '#FF7043',
      accentShadow: '#FF5722',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'French Provincial',
    tags: ['interior', 'french', 'elegant', 'romantic'],
    light: {
      primary: '#78909C',
      primaryShadow: '#607D8B',
      accent: '#F8BBD0',
      accentShadow: '#F48FB1',
      background: '#FAFAFA',
      cardBackground: '#F5F5F5',
      text: '#37474F',
      extra: '#D4AF37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0BEC5',
      primaryShadow: '#78909C',
      accent: '#F8BBD0',
      accentShadow: '#F48FB1',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#ECEFF1',
      extra: '#E8C850',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },

  // Season Variation Themes
  {
    name: 'Crisp Winter Morning',
    tags: ['season', 'winter', 'crisp', 'fresh'],
    light: {
      primary: '#546E7A',
      primaryShadow: '#455A64',
      accent: '#81D4FA',
      accentShadow: '#4FC3F7',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#263238',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#90A4AE',
      primaryShadow: '#546E7A',
      accent: '#B3E5FC',
      accentShadow: '#81D4FA',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#CFD8DC',
      extra: '#B0BEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spring Awakening',
    tags: ['season', 'spring', 'fresh', 'renewal'],
    light: {
      primary: '#66BB6A',
      primaryShadow: '#43A047',
      accent: '#F8BBD0',
      accentShadow: '#F48FB1',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B5E20',
      extra: '#AED581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A5D6A7',
      primaryShadow: '#66BB6A',
      accent: '#F8BBD0',
      accentShadow: '#F48FB1',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#C5E1A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Summer Solstice',
    tags: ['season', 'summer', 'bright', 'energetic'],
    light: {
      primary: '#FFC107',
      primaryShadow: '#FFA000',
      accent: '#00BCD4',
      accentShadow: '#0097A7',
      background: '#FFFDE7',
      cardBackground: '#FFF9C4',
      text: '#F57F17',
      extra: '#FFEB3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFD54F',
      primaryShadow: '#FFC107',
      accent: '#4DD0E1',
      accentShadow: '#00BCD4',
      background: '#1A1800',
      cardBackground: '#2A2800',
      text: '#FFF9C4',
      extra: '#FFF176',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Equinox',
    tags: ['season', 'autumn', 'balanced', 'transitional'],
    light: {
      primary: '#E65100',
      primaryShadow: '#BF360C',
      accent: '#558B2F',
      accentShadow: '#33691E',
      background: '#FFF3E0',
      cardBackground: '#FFE0B2',
      text: '#3E2723',
      extra: '#FF9800',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9100',
      primaryShadow: '#E65100',
      accent: '#8BC34A',
      accentShadow: '#558B2F',
      background: '#1A0A05',
      cardBackground: '#2A150A',
      text: '#FFE0B2',
      extra: '#FFB74D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Monsoon Season',
    tags: ['season', 'monsoon', 'rain', 'lush'],
    light: {
      primary: '#2E7D32',
      primaryShadow: '#1B5E20',
      accent: '#607D8B',
      accentShadow: '#455A64',
      background: '#E8F5E9',
      cardBackground: '#C8E6C9',
      text: '#1B5E20',
      extra: '#66BB6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66BB6A',
      primaryShadow: '#2E7D32',
      accent: '#90A4AE',
      accentShadow: '#607D8B',
      background: '#081A08',
      cardBackground: '#0F280F',
      text: '#C8E6C9',
      extra: '#A5D6A7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dry Season',
    tags: ['season', 'dry', 'warm', 'earthy'],
    light: {
      primary: '#BF360C',
      primaryShadow: '#8B2500',
      accent: '#FFB300',
      accentShadow: '#FF8F00',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#3E2723',
      extra: '#8D6E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5722',
      primaryShadow: '#BF360C',
      accent: '#FFD54F',
      accentShadow: '#FFB300',
      background: '#1A0A05',
      cardBackground: '#2A150A',
      text: '#FFECB3',
      extra: '#BCAAA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Harvest Moon',
    tags: ['season', 'harvest', 'autumn', 'golden'],
    light: {
      primary: '#FF8F00',
      primaryShadow: '#FF6F00',
      accent: '#5D4037',
      accentShadow: '#4E342E',
      background: '#FFF8E1',
      cardBackground: '#FFECB3',
      text: '#E65100',
      extra: '#FFC107',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFB74D',
      primaryShadow: '#FF8F00',
      accent: '#8D6E63',
      accentShadow: '#5D4037',
      background: '#1A0A00',
      cardBackground: '#2A1500',
      text: '#FFECB3',
      extra: '#FFD54F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'First Frost',
    tags: ['season', 'frost', 'winter', 'delicate'],
    light: {
      primary: '#78909C',
      primaryShadow: '#607D8B',
      accent: '#E1F5FE',
      accentShadow: '#B3E5FC',
      background: '#ECEFF1',
      cardBackground: '#CFD8DC',
      text: '#37474F',
      extra: '#B0BEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B0BEC5',
      primaryShadow: '#78909C',
      accent: '#B3E5FC',
      accentShadow: '#E1F5FE',
      background: '#0A0C0E',
      cardBackground: '#151A1E',
      text: '#ECEFF1',
      extra: '#90A4AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Batch 20: Final push to 1000+ themes
  {
    name: 'Aurora Cascade',
    tags: ['nature', 'aurora', 'ethereal', 'vibrant'],
    light: {
      primary: '#00D4AA',
      primaryShadow: '#00A888',
      accent: '#FF6B9D',
      accentShadow: '#CC567E',
      background: '#F0FAFA',
      cardBackground: '#E0F5F5',
      text: '#1A2A2A',
      extra: '#7B68EE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40E8C4',
      primaryShadow: '#00D4AA',
      accent: '#FF88B4',
      accentShadow: '#FF6B9D',
      background: '#080E0E',
      cardBackground: '#101818',
      text: '#E0F8F8',
      extra: '#9890F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Midnight',
    tags: ['luxury', 'dark', 'elegant', 'rich'],
    light: {
      primary: '#4A0080',
      primaryShadow: '#380060',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FAF5FF',
      cardBackground: '#F0E8FF',
      text: '#1A1020',
      extra: '#8B008B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8040C0',
      primaryShadow: '#4A0080',
      accent: '#FFE540',
      accentShadow: '#FFD700',
      background: '#0A0610',
      cardBackground: '#140C1C',
      text: '#F5EAFF',
      extra: '#C040C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Citrus Burst',
    tags: ['fresh', 'vibrant', 'energetic', 'summer'],
    light: {
      primary: '#FF6B00',
      primaryShadow: '#CC5500',
      accent: '#32CD32',
      accentShadow: '#28A428',
      background: '#FFFDF5',
      cardBackground: '#FFF8E8',
      text: '#1A1A10',
      extra: '#FFD700',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8830',
      primaryShadow: '#FF6B00',
      accent: '#50E050',
      accentShadow: '#32CD32',
      background: '#100C08',
      cardBackground: '#181410',
      text: '#FFF8E8',
      extra: '#FFE540',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Storm Brewing',
    tags: ['weather', 'dramatic', 'moody', 'powerful'],
    light: {
      primary: '#2F4F4F',
      primaryShadow: '#1C3030',
      accent: '#778899',
      accentShadow: '#5F6B7A',
      background: '#F0F4F8',
      cardBackground: '#E0E8F0',
      text: '#1A1F24',
      extra: '#4682B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4F7070',
      primaryShadow: '#2F4F4F',
      accent: '#98AAB8',
      accentShadow: '#778899',
      background: '#08090C',
      cardBackground: '#101418',
      text: '#E8EEF4',
      extra: '#6098CC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Desert Dusk',
    tags: ['desert', 'warm', 'sunset', 'earthy'],
    light: {
      primary: '#CD853F',
      primaryShadow: '#A46A32',
      accent: '#DC143C',
      accentShadow: '#B01030',
      background: '#FDF5E6',
      cardBackground: '#FAE8D0',
      text: '#2A1F14',
      extra: '#DAA520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A060',
      primaryShadow: '#CD853F',
      accent: '#F03050',
      accentShadow: '#DC143C',
      background: '#100C08',
      cardBackground: '#1A140E',
      text: '#FAE8D0',
      extra: '#F0C040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glacier Blue',
    tags: ['ice', 'cool', 'crisp', 'arctic'],
    light: {
      primary: '#4169E1',
      primaryShadow: '#3454B4',
      accent: '#87CEEB',
      accentShadow: '#6CA5BC',
      background: '#F0F8FF',
      cardBackground: '#E0F0FF',
      text: '#1A202A',
      extra: '#00CED1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6088F0',
      primaryShadow: '#4169E1',
      accent: '#A8E0F8',
      accentShadow: '#87CEEB',
      background: '#060810',
      cardBackground: '#0C1018',
      text: '#E8F4FF',
      extra: '#40E8EA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Glow',
    tags: ['fire', 'warm', 'cozy', 'autumn'],
    light: {
      primary: '#FF4500',
      primaryShadow: '#CC3700',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#FFF8F5',
      cardBackground: '#FFF0E8',
      text: '#1A1410',
      extra: '#FF6347',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6030',
      primaryShadow: '#FF4500',
      accent: '#FFE540',
      accentShadow: '#FFD700',
      background: '#100808',
      cardBackground: '#181010',
      text: '#FFF4E8',
      extra: '#FF8868',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystic Forest',
    tags: ['forest', 'mystical', 'deep', 'enchanted'],
    light: {
      primary: '#228B22',
      primaryShadow: '#1B6F1B',
      accent: '#9932CC',
      accentShadow: '#7A28A4',
      background: '#F5FFF5',
      cardBackground: '#E8FFE8',
      text: '#1A2A1A',
      extra: '#20B2AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40B040',
      primaryShadow: '#228B22',
      accent: '#B860E0',
      accentShadow: '#9932CC',
      background: '#080C08',
      cardBackground: '#101810',
      text: '#E8FFE8',
      extra: '#40D0C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Patina',
    tags: ['metal', 'aged', 'vintage', 'industrial'],
    light: {
      primary: '#B87333',
      primaryShadow: '#935C29',
      accent: '#40E0D0',
      accentShadow: '#33B4A6',
      background: '#FAF8F5',
      cardBackground: '#F0EBE0',
      text: '#2A2418',
      extra: '#CD7F32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D09050',
      primaryShadow: '#B87333',
      accent: '#60F0E0',
      accentShadow: '#40E0D0',
      background: '#0C0A08',
      cardBackground: '#141210',
      text: '#F5F0E8',
      extra: '#E0A050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pearl Essence',
    tags: ['elegant', 'soft', 'iridescent', 'luxurious'],
    light: {
      primary: '#808080',
      primaryShadow: '#666666',
      accent: '#FFE4E1',
      accentShadow: '#CCB7B4',
      background: '#FFFAFA',
      cardBackground: '#FFF5F5',
      text: '#2A2020',
      extra: '#DCDCDC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0A0A0',
      primaryShadow: '#808080',
      accent: '#FFF0EE',
      accentShadow: '#FFE4E1',
      background: '#0A0808',
      cardBackground: '#141010',
      text: '#FFF8F8',
      extra: '#E8E8E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Thunder Strike',
    tags: ['electric', 'powerful', 'dramatic', 'energy'],
    light: {
      primary: '#1C1C1C',
      primaryShadow: '#0A0A0A',
      accent: '#FFD700',
      accentShadow: '#CCAC00',
      background: '#F5F5F5',
      cardBackground: '#EAEAEA',
      text: '#1A1A1A',
      extra: '#4169E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0E0E0',
      primaryShadow: '#1C1C1C',
      accent: '#FFE540',
      accentShadow: '#FFD700',
      background: '#050505',
      cardBackground: '#0C0C0C',
      text: '#F5F5F5',
      extra: '#6088F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sage Wisdom',
    tags: ['calm', 'natural', 'healing', 'serene'],
    light: {
      primary: '#9CAF88',
      primaryShadow: '#7D8C6D',
      accent: '#D4A574',
      accentShadow: '#AA845D',
      background: '#F8FAF5',
      cardBackground: '#EEF4E8',
      text: '#2A2E28',
      extra: '#B8C4A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B8C8A8',
      primaryShadow: '#9CAF88',
      accent: '#E8BF94',
      accentShadow: '#D4A574',
      background: '#0A0C0A',
      cardBackground: '#121614',
      text: '#F4F8F0',
      extra: '#C8D4B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  // Lospec Palette Themes - 1165 curated color palettes
  {
    name: 'Lasting Rain',
    tags: ['curated'],
    light: {
      primary: '#BF3D34',
      primaryShadow: '#99312A',
      accent: '#A62121',
      accentShadow: '#851A1A',
      background: '#FFE9B2',
      cardBackground: '#F2DDA9',
      text: '#08011A',
      extra: '#E6DD78',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C95A52',
      primaryShadow: '#BF3D34',
      accent: '#B34242',
      accentShadow: '#A62121',
      background: '#08011A',
      cardBackground: '#120B23',
      text: '#FFE9B2',
      extra: '#E9E086',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Heath',
    tags: ['curated'],
    light: {
      primary: '#2CAF7F',
      primaryShadow: '#238C66',
      accent: '#E29A27',
      accentShadow: '#B57B1F',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#2B2437',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4CBB92',
      primaryShadow: '#2CAF7F',
      accent: '#E6A947',
      accentShadow: '#E29A27',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#403A4B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mediterranean Spirit',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9C4827',
      primaryShadow: '#7D3A1F',
      accent: '#DF9A73',
      accentShadow: '#B27B5C',
      background: '#ECDFBF',
      cardBackground: '#E0D4B5',
      text: '#161412',
      extra: '#6D8577',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AB6347',
      primaryShadow: '#9C4827',
      accent: '#E4A988',
      accentShadow: '#DF9A73',
      background: '#161412',
      cardBackground: '#1F1D1B',
      text: '#ECDFBF',
      extra: '#7C9185',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ethereal Permafrost',
    tags: ['curated'],
    light: {
      primary: '#9F5F2A',
      primaryShadow: '#7F4C22',
      accent: '#CA9532',
      accentShadow: '#A27728',
      background: '#F4E7E7',
      cardBackground: '#E8DBDB',
      text: '#202043',
      extra: '#202043',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD774A',
      primaryShadow: '#9F5F2A',
      accent: '#D2A551',
      accentShadow: '#CA9532',
      background: '#202043',
      cardBackground: '#29294B',
      text: '#F4E7E7',
      extra: '#363656',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Fortress',
    tags: ['curated'],
    light: {
      primary: '#4B80CA',
      primaryShadow: '#3C66A2',
      accent: '#80493A',
      accentShadow: '#663A2E',
      background: '#F2F0E5',
      cardBackground: '#E6E4DA',
      text: '#212123',
      extra: '#B8B5B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6693D2',
      primaryShadow: '#4B80CA',
      accent: '#936458',
      accentShadow: '#80493A',
      background: '#212123',
      cardBackground: '#2A2A2C',
      text: '#F2F0E5',
      extra: '#BFBCC0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Crystal',
    tags: ['curated'],
    light: {
      primary: '#008B8B',
      primaryShadow: '#006F6F',
      accent: '#006554',
      accentShadow: '#005143',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#10121C',
      extra: '#2C1E31',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#269C9C',
      primaryShadow: '#008B8B',
      accent: '#267C6E',
      accentShadow: '#006554',
      background: '#10121C',
      cardBackground: '#1A1B25',
      text: '#FFFFFF',
      extra: '#413546',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Granite Temple',
    tags: ['curated'],
    light: {
      primary: '#0EAF9B',
      primaryShadow: '#0B8C7C',
      accent: '#F79617',
      accentShadow: '#C67812',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#2E222F',
      extra: '#2E222F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#32BBAA',
      primaryShadow: '#0EAF9B',
      accent: '#F8A63A',
      accentShadow: '#F79617',
      background: '#080D11',
      cardBackground: '#0F1418',
      text: '#FFFFFF',
      extra: '#433844',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Citadel',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D08159',
      primaryShadow: '#A66747',
      accent: '#0D2B45',
      accentShadow: '#0A2237',
      background: '#FFECD6',
      cardBackground: '#F2E0CB',
      text: '#0D2B45',
      extra: '#203C56',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D79472',
      primaryShadow: '#D08159',
      accent: '#314B61',
      accentShadow: '#0D2B45',
      background: '#0D2B45',
      cardBackground: '#17334C',
      text: '#FFECD6',
      extra: '#365067',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Lake',
    tags: ['curated'],
    light: {
      primary: '#494D7E',
      primaryShadow: '#3A3E65',
      accent: '#8B6D9C',
      accentShadow: '#6F577D',
      background: '#FBF5EF',
      cardBackground: '#EEE9E3',
      text: '#272744',
      extra: '#F2D3AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#646891',
      primaryShadow: '#494D7E',
      accent: '#9C83AB',
      accentShadow: '#8B6D9C',
      background: '#0A0A10',
      cardBackground: '#111117',
      text: '#FBF5EF',
      extra: '#F3D7B3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Misty Nave',
    tags: ['curated'],
    light: {
      primary: '#BE772B',
      primaryShadow: '#985F22',
      accent: '#DE9E41',
      accentShadow: '#B27E34',
      background: '#EBEDE9',
      cardBackground: '#DFE1DD',
      text: '#090A14',
      extra: '#172038',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C88B4B',
      primaryShadow: '#BE772B',
      accent: '#E3AD5E',
      accentShadow: '#DE9E41',
      background: '#090A14',
      cardBackground: '#13141D',
      text: '#EBEDE9',
      extra: '#2E364C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Nebula',
    tags: ['curated'],
    light: {
      primary: '#416AA3',
      primaryShadow: '#345582',
      accent: '#BE955C',
      accentShadow: '#98774A',
      background: '#E2E5EA',
      cardBackground: '#D9DCE1',
      text: '#000000',
      extra: '#6F6776',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E80B1',
      primaryShadow: '#416AA3',
      accent: '#C8A574',
      accentShadow: '#BE955C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#C5CCB8',
      extra: '#7D7684',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silver Wave',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AE5D40',
      primaryShadow: '#8B4A33',
      accent: '#C77B58',
      accentShadow: '#9F6246',
      background: '#EBE4E2',
      cardBackground: '#E2DBD9',
      text: '#4B3D44',
      extra: '#D1B187',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BA755D',
      primaryShadow: '#AE5D40',
      accent: '#CF8F71',
      accentShadow: '#C77B58',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#D2C9A5',
      extra: '#D6B993',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Prairie',
    tags: ['curated'],
    light: {
      primary: '#4A7A96',
      primaryShadow: '#3B6278',
      accent: '#EE8695',
      accentShadow: '#BE6B77',
      background: '#E3E7E9',
      cardBackground: '#DADEE0',
      text: '#292831',
      extra: '#FBBBAD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#658EA6',
      primaryShadow: '#4A7A96',
      accent: '#F198A5',
      accentShadow: '#EE8695',
      background: '#0A0C11',
      cardBackground: '#111318',
      text: '#FBBBAD',
      extra: '#FBC2B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indigo Dunes',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#D53C6A',
      primaryShadow: '#AA3055',
      accent: '#7C183C',
      accentShadow: '#631330',
      background: '#EEE2E5',
      cardBackground: '#E4D9DC',
      text: '#130208',
      extra: '#FF8274',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DB5980',
      primaryShadow: '#D53C6A',
      accent: '#903B59',
      accentShadow: '#7C183C',
      background: '#130208',
      cardBackground: '#1C0C12',
      text: '#F0F0F0',
      extra: '#FF8F82',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Alpine Sculpture',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B55945',
      primaryShadow: '#914737',
      accent: '#DE9F47',
      accentShadow: '#B27F39',
      background: '#F1F6F0',
      cardBackground: '#E5EAE4',
      text: '#14233A',
      extra: '#636663',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C07261',
      primaryShadow: '#B55945',
      accent: '#E3AD63',
      accentShadow: '#DE9F47',
      background: '#14233A',
      cardBackground: '#1D2C42',
      text: '#F1F6F0',
      extra: '#737573',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Current',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#EF3A0C',
      primaryShadow: '#BF2E0A',
      accent: '#EF692F',
      accentShadow: '#BF5426',
      background: '#F0E1DE',
      cardBackground: '#E6D8D5',
      text: '#300F0A',
      extra: '#1F240A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F15830',
      primaryShadow: '#EF3A0C',
      accent: '#F1804E',
      accentShadow: '#EF692F',
      background: '#300F0A',
      cardBackground: '#381914',
      text: '#EFD8A1',
      extra: '#353A23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Olive Star',
    tags: ['curated'],
    light: {
      primary: '#0099DB',
      primaryShadow: '#007AAF',
      accent: '#FF0044',
      accentShadow: '#CC0036',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#181425',
      extra: '#BE4A2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A8E0',
      primaryShadow: '#0099DB',
      accent: '#FF2660',
      accentShadow: '#FF0044',
      background: '#181425',
      cardBackground: '#211D2E',
      text: '#FFFFFF',
      extra: '#C55C44',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Bastion',
    tags: ['bright', 'curated'],
    light: {
      primary: '#74569B',
      primaryShadow: '#5D457C',
      accent: '#96FBC7',
      accentShadow: '#78C99F',
      background: '#F7FFAE',
      cardBackground: '#EBF2A5',
      text: '#1A1A1A',
      extra: '#FFB3CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#896FAA',
      primaryShadow: '#74569B',
      accent: '#A6FCCF',
      accentShadow: '#96FBC7',
      background: '#0B0B11',
      cardBackground: '#121218',
      text: '#F7FFAE',
      extra: '#FFBBD0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Springs',
    tags: ['cool', 'curated'],
    light: {
      primary: '#41A6F6',
      primaryShadow: '#3485C5',
      accent: '#3B5DC9',
      accentShadow: '#2F4AA1',
      background: '#F4F4F4',
      cardBackground: '#E8E8E8',
      text: '#1A1C2C',
      extra: '#1A1C2C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5EB3F7',
      primaryShadow: '#41A6F6',
      accent: '#5875D1',
      accentShadow: '#3B5DC9',
      background: '#1A1C2C',
      cardBackground: '#232534',
      text: '#F4F4F4',
      extra: '#313341',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Matte Eclipse',
    tags: ['curated'],
    light: {
      primary: '#EB564B',
      primaryShadow: '#BC453C',
      accent: '#B0305C',
      accentShadow: '#8D264A',
      background: '#FFFFEB',
      cardBackground: '#F2F2DF',
      text: '#272736',
      extra: '#5E315B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EE6F66',
      primaryShadow: '#EB564B',
      accent: '#BC4F74',
      accentShadow: '#B0305C',
      background: '#0F0B0E',
      cardBackground: '#161215',
      text: '#FFFFEB',
      extra: '#6E466B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Minimalist Terrace',
    tags: ['dark', 'curated'],
    light: {
      primary: '#0F2A3F',
      primaryShadow: '#0C2232',
      accent: '#C3A38A',
      accentShadow: '#9C826E',
      background: '#F6D6BD',
      cardBackground: '#EACBB4',
      text: '#08141E',
      extra: '#20394F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#334A5C',
      primaryShadow: '#0F2A3F',
      accent: '#CCB19C',
      accentShadow: '#C3A38A',
      background: '#08141E',
      cardBackground: '#121D27',
      text: '#F6D6BD',
      extra: '#364D61',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Vine',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#3A604A',
      primaryShadow: '#2E4D3B',
      accent: '#77744F',
      accentShadow: '#5F5D3F',
      background: '#E1E4E3',
      cardBackground: '#D8DBDA',
      text: '#170E19',
      extra: '#213B25',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#587865',
      primaryShadow: '#3A604A',
      accent: '#8B8969',
      accentShadow: '#77744F',
      background: '#170E19',
      cardBackground: '#201822',
      text: '#C0D1CC',
      extra: '#374F3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silver Altar',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#AC6B26',
      primaryShadow: '#8A561E',
      accent: '#F6CD26',
      accentShadow: '#C5A41E',
      background: '#EBE5E0',
      cardBackground: '#E2DCD7',
      text: '#202020',
      extra: '#563226',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B88147',
      primaryShadow: '#AC6B26',
      accent: '#F7D547',
      accentShadow: '#F6CD26',
      background: '#202020',
      cardBackground: '#292929',
      text: '#F6CD26',
      extra: '#67473C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pearl Chapel',
    tags: ['dark', 'curated'],
    light: {
      primary: '#C53A9D',
      primaryShadow: '#9E2E7E',
      accent: '#051F39',
      accentShadow: '#04192E',
      background: '#EDE1E9',
      cardBackground: '#E4D8E0',
      text: '#051F39',
      extra: '#4A2480',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CE58AC',
      primaryShadow: '#C53A9D',
      accent: '#2B4157',
      accentShadow: '#051F39',
      background: '#051F39',
      cardBackground: '#0F2841',
      text: '#F0F0F0',
      extra: '#5C3A8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ancient Essence',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#EB6B6F',
      primaryShadow: '#BC5659',
      accent: '#7C3F58',
      accentShadow: '#633246',
      background: '#FFF6D3',
      cardBackground: '#F2EAC8',
      text: '#1A1A1A',
      extra: '#F9A875',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EE8185',
      primaryShadow: '#EB6B6F',
      accent: '#905C71',
      accentShadow: '#7C3F58',
      background: '#0F0B0F',
      cardBackground: '#161216',
      text: '#FFF6D3',
      extra: '#FAB183',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Smoke Sunrise',
    tags: ['cool', 'curated'],
    light: {
      primary: '#0098DB',
      primaryShadow: '#007AAF',
      accent: '#0CE6F2',
      accentShadow: '#0AB8C2',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#201533',
      extra: '#1E579C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A7E0',
      primaryShadow: '#0098DB',
      accent: '#30EAF4',
      accentShadow: '#0CE6F2',
      background: '#201533',
      cardBackground: '#291E3B',
      text: '#FFFFFF',
      extra: '#3568A6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bold Sunset',
    tags: ['curated'],
    light: {
      primary: '#CC425E',
      primaryShadow: '#A3354B',
      accent: '#A32858',
      accentShadow: '#822046',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1F102A',
      extra: '#5BA675',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D45E76',
      primaryShadow: '#CC425E',
      accent: '#B14871',
      accentShadow: '#A32858',
      background: '#1F102A',
      cardBackground: '#281A33',
      text: '#FFFFFF',
      extra: '#6BAF83',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Vineyard',
    tags: ['curated'],
    light: {
      primary: '#555568',
      primaryShadow: '#444453',
      accent: '#A0A08B',
      accentShadow: '#80806F',
      background: '#E9EFEC',
      cardBackground: '#DDE3E0',
      text: '#211E20',
      extra: '#211E20',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6F6F7F',
      primaryShadow: '#555568',
      accent: '#AEAE9C',
      accentShadow: '#A0A08B',
      background: '#211E20',
      cardBackground: '#2A2729',
      text: '#E9EFEC',
      extra: '#373536',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Petal',
    tags: ['curated'],
    light: {
      primary: '#00B9BE',
      primaryShadow: '#009498',
      accent: '#15788C',
      accentShadow: '#116070',
      background: '#FFEECC',
      cardBackground: '#F2E2C2',
      text: '#46425E',
      extra: '#46425E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C4C8',
      primaryShadow: '#00B9BE',
      accent: '#388C9D',
      accentShadow: '#15788C',
      background: '#080E12',
      cardBackground: '#0F1519',
      text: '#FFEECC',
      extra: '#59556E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Valley',
    tags: ['cool', 'curated'],
    light: {
      primary: '#89A257',
      primaryShadow: '#6E8246',
      accent: '#305D42',
      accentShadow: '#264A35',
      background: '#EEFFCC',
      cardBackground: '#E2F2C2',
      text: '#040C06',
      extra: '#112318',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9BB070',
      primaryShadow: '#89A257',
      accent: '#4F755E',
      accentShadow: '#305D42',
      background: '#040C06',
      cardBackground: '#0E1610',
      text: '#EEFFCC',
      extra: '#29392F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gleam Tundra',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A48D6A',
      primaryShadow: '#837155',
      accent: '#73654A',
      accentShadow: '#5C513B',
      background: '#EAE8E5',
      cardBackground: '#E1DFDC',
      text: '#292418',
      extra: '#292418',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B29E80',
      primaryShadow: '#A48D6A',
      accent: '#887C65',
      accentShadow: '#73654A',
      background: '#292418',
      cardBackground: '#322D21',
      text: '#E6CEAC',
      extra: '#3E3A2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Clear Sunset',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#EF9D7F',
      primaryShadow: '#BF7E66',
      accent: '#FCB08C',
      accentShadow: '#CA8D70',
      background: '#F0E9E7',
      cardBackground: '#E6E0DE',
      text: '#1A1A1A',
      extra: '#D6938A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F1AC92',
      primaryShadow: '#EF9D7F',
      accent: '#FCBC9D',
      accentShadow: '#FCB08C',
      background: '#0F0D10',
      cardBackground: '#161417',
      text: '#FCB08C',
      extra: '#DA9E96',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Snowfall',
    tags: ['curated'],
    light: {
      primary: '#FA6A0A',
      primaryShadow: '#C85508',
      accent: '#14A02E',
      accentShadow: '#108025',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#060608',
      extra: '#3B1725',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FB802F',
      primaryShadow: '#FA6A0A',
      accent: '#37AE4D',
      accentShadow: '#14A02E',
      background: '#060608',
      cardBackground: '#101012',
      text: '#FFFFFF',
      extra: '#4F2E3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Fire',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D4804D',
      primaryShadow: '#AA663E',
      accent: '#B03A48',
      accentShadow: '#8D2E3A',
      background: '#EEE7E3',
      cardBackground: '#E4DEDA',
      text: '#2B2821',
      extra: '#2B2821',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA9368',
      primaryShadow: '#D4804D',
      accent: '#BC5863',
      accentShadow: '#B03A48',
      background: '#0E0C0E',
      cardBackground: '#151315',
      text: '#E3CFB4',
      extra: '#403E37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Iron Peak',
    tags: ['curated'],
    light: {
      primary: '#00E436',
      primaryShadow: '#00B62B',
      accent: '#008751',
      accentShadow: '#006C41',
      background: '#FFF1E8',
      cardBackground: '#F2E5DC',
      text: '#000000',
      extra: '#1D2B53',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26E854',
      primaryShadow: '#00E436',
      accent: '#26996B',
      accentShadow: '#008751',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFF1E8',
      extra: '#344064',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Modern Branch',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#906C30',
      primaryShadow: '#735626',
      accent: '#245273',
      accentShadow: '#1D425C',
      background: '#E3DDD1',
      cardBackground: '#D8D2C7',
      text: '#0B1016',
      extra: '#A6884A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A1824F',
      primaryShadow: '#906C30',
      accent: '#456C88',
      accentShadow: '#245273',
      background: '#0B1016',
      cardBackground: '#151A1F',
      text: '#E3DDD1',
      extra: '#AF945C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Alpine Vineyard',
    tags: ['curated'],
    light: {
      primary: '#0F0F1B',
      primaryShadow: '#0C0C16',
      accent: '#565A75',
      accentShadow: '#45485E',
      background: '#FAFBF6',
      cardBackground: '#EEEEEA',
      text: '#0F0F1B',
      extra: '#C6B7BE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#33333D',
      primaryShadow: '#0F0F1B',
      accent: '#6F738A',
      accentShadow: '#565A75',
      background: '#0F0F1B',
      cardBackground: '#191924',
      text: '#FAFBF6',
      extra: '#CCBEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Aura',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AE6A47',
      primaryShadow: '#8B5539',
      accent: '#CAA05A',
      accentShadow: '#A28048',
      background: '#EBE5E2',
      cardBackground: '#E2DCD9',
      text: '#543344',
      extra: '#C9CCA1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BA8063',
      primaryShadow: '#AE6A47',
      accent: '#D2AE73',
      accentShadow: '#CAA05A',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#C9CCA1',
      extra: '#CED1AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silk Fresco',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#36868F',
      primaryShadow: '#2B6B72',
      accent: '#203671',
      accentShadow: '#1A2B5A',
      background: '#E1E8E8',
      cardBackground: '#D8DFDF',
      text: '#0F052D',
      extra: '#5FC75D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5498A0',
      primaryShadow: '#36868F',
      accent: '#415486',
      accentShadow: '#203671',
      background: '#0F052D',
      cardBackground: '#190F35',
      text: '#F0F0F0',
      extra: '#6FCD6D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Radiant Current',
    tags: ['curated'],
    light: {
      primary: '#FF5000',
      primaryShadow: '#CC4000',
      accent: '#00CDF9',
      accentShadow: '#00A4C7',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#0E071B',
      extra: '#FF0040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6A26',
      primaryShadow: '#FF5000',
      accent: '#26D5FA',
      accentShadow: '#00CDF9',
      background: '#0E071B',
      cardBackground: '#181124',
      text: '#FFFFFF',
      extra: '#FF1A53',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bronze Reef',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#34ACBA',
      primaryShadow: '#2A8A95',
      accent: '#72DCBB',
      accentShadow: '#5BB096',
      background: '#FFE7D6',
      cardBackground: '#F2DBCB',
      text: '#1A1A1A',
      extra: '#73464C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#52B8C4',
      primaryShadow: '#34ACBA',
      accent: '#87E1C5',
      accentShadow: '#72DCBB',
      background: '#0A0D12',
      cardBackground: '#111419',
      text: '#FFE7D6',
      extra: '#81595E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ruby Ridge',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4593A5',
      primaryShadow: '#377684',
      accent: '#FF5DCC',
      accentShadow: '#CC4AA3',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#32313B',
      extra: '#85DAEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#61A3B3',
      primaryShadow: '#4593A5',
      accent: '#FF75D4',
      accentShadow: '#FF5DCC',
      background: '#0A0C11',
      cardBackground: '#111318',
      text: '#FFFFFF',
      extra: '#91DEED',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wild Crystal',
    tags: ['curated'],
    light: {
      primary: '#D03791',
      primaryShadow: '#A62C74',
      accent: '#87286A',
      accentShadow: '#6C2055',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#260D34',
      extra: '#FE6C90',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D755A2',
      primaryShadow: '#D03791',
      accent: '#994880',
      accentShadow: '#87286A',
      background: '#260D34',
      cardBackground: '#2F173C',
      text: '#FFFFFF',
      extra: '#FE7B9B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Misty Relief',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#6F1D5C',
      primaryShadow: '#59174A',
      accent: '#0D001A',
      accentShadow: '#0A0015',
      background: '#E6DFE4',
      cardBackground: '#DDD6DB',
      text: '#0D001A',
      extra: '#6CEDED',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#853F74',
      primaryShadow: '#6F1D5C',
      accent: '#31263C',
      accentShadow: '#0D001A',
      background: '#0D001A',
      cardBackground: '#170A23',
      text: '#6CEDED',
      extra: '#7BEFEF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Azure Spectrum',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E4943A',
      primaryShadow: '#B6762E',
      accent: '#34859D',
      accentShadow: '#2A6A7E',
      background: '#F5EDBA',
      cardBackground: '#E9E1B1',
      text: '#1F0E1C',
      extra: '#8C8FAE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8A458',
      primaryShadow: '#E4943A',
      accent: '#5297AC',
      accentShadow: '#34859D',
      background: '#1F0E1C',
      cardBackground: '#281825',
      text: '#F5EDBA',
      extra: '#989AB6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Sunset',
    tags: ['curated'],
    light: {
      primary: '#D46E33',
      primaryShadow: '#AA5829',
      accent: '#F2B63D',
      accentShadow: '#C29231',
      background: '#FFF4E0',
      cardBackground: '#F2E8D5',
      text: '#2C1B2E',
      extra: '#8FCCCB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA8452',
      primaryShadow: '#D46E33',
      accent: '#F4C15A',
      accentShadow: '#F2B63D',
      background: '#2C1B2E',
      cardBackground: '#342436',
      text: '#FFF4E0',
      extra: '#9AD1D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vivid Basin',
    tags: ['curated'],
    light: {
      primary: '#F69197',
      primaryShadow: '#C57479',
      accent: '#282D3C',
      accentShadow: '#202430',
      background: '#F0E8E9',
      cardBackground: '#E6DFE0',
      text: '#282D3C',
      extra: '#5B5D70',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F7A2A7',
      primaryShadow: '#F69197',
      accent: '#484D59',
      accentShadow: '#282D3C',
      background: '#0F0C11',
      cardBackground: '#161318',
      text: '#FFC4B8',
      extra: '#6B6D7E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bronze Cliff',
    tags: ['cool', 'curated'],
    light: {
      primary: '#166E7A',
      primaryShadow: '#125862',
      accent: '#52C33F',
      accentShadow: '#429C32',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#201533',
      extra: '#B2D942',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#39848E',
      primaryShadow: '#166E7A',
      accent: '#6CCC5C',
      accentShadow: '#52C33F',
      background: '#201533',
      cardBackground: '#291E3B',
      text: '#FFFFFF',
      extra: '#BADD55',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gleam Comet',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#F98284',
      primaryShadow: '#C7686A',
      accent: '#FFC384',
      accentShadow: '#CC9C6A',
      background: '#FFF7E4',
      cardBackground: '#F2EBD9',
      text: '#28282E',
      extra: '#28282E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FA9596',
      primaryShadow: '#F98284',
      accent: '#FFCC96',
      accentShadow: '#FFC384',
      background: '#0F0C10',
      cardBackground: '#161317',
      text: '#FFF7E4',
      extra: '#3E3E43',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fog Bay',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#00303B',
      primaryShadow: '#00262F',
      accent: '#FF7777',
      accentShadow: '#CC5F5F',
      background: '#F1F2DA',
      cardBackground: '#E5E6CF',
      text: '#00303B',
      extra: '#FFCE96',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#264F58',
      primaryShadow: '#00303B',
      accent: '#FF8B8B',
      accentShadow: '#FF7777',
      background: '#00303B',
      cardBackground: '#0A3843',
      text: '#F1F2DA',
      extra: '#FFD3A1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Grove',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CD683D',
      primaryShadow: '#A45331',
      accent: '#993D41',
      accentShadow: '#7A3134',
      background: '#F2EC8B',
      cardBackground: '#E6E084',
      text: '#2E222F',
      extra: '#2E222F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D57F5A',
      primaryShadow: '#CD683D',
      accent: '#A85A5E',
      accentShadow: '#993D41',
      background: '#0E0B0E',
      cardBackground: '#151215',
      text: '#F2EC8B',
      extra: '#433844',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Balmy Vineyard',
    tags: ['curated'],
    light: {
      primary: '#11ADC1',
      primaryShadow: '#0E8A9A',
      accent: '#C92464',
      accentShadow: '#A11D50',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#393457',
      extra: '#6DF7C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#35B9CA',
      primaryShadow: '#11ADC1',
      accent: '#D1457B',
      accentShadow: '#C92464',
      background: '#090D12',
      cardBackground: '#101419',
      text: '#FFFFFF',
      extra: '#7CF8C7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Valley',
    tags: ['cool', 'curated'],
    light: {
      primary: '#596070',
      primaryShadow: '#474D5A',
      accent: '#1F1F29',
      accentShadow: '#191921',
      background: '#EAF0D8',
      cardBackground: '#DEE4CD',
      text: '#1F1F29',
      extra: '#413A42',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#727885',
      primaryShadow: '#596070',
      accent: '#414149',
      accentShadow: '#1F1F29',
      background: '#1F1F29',
      cardBackground: '#282832',
      text: '#EAF0D8',
      extra: '#544E55',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Dusk',
    tags: ['curated'],
    light: {
      primary: '#00AAA5',
      primaryShadow: '#008884',
      accent: '#27D3CB',
      accentShadow: '#1FA9A2',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#12173D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B7B3',
      primaryShadow: '#00AAA5',
      accent: '#47DAD3',
      accentShadow: '#27D3CB',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#2A2E50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Jungle',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#3F2C5F',
      primaryShadow: '#32234C',
      accent: '#4C5C87',
      accentShadow: '#3D4A6C',
      background: '#E2E0E4',
      cardBackground: '#D9D7DB',
      text: '#2A173B',
      extra: '#2A173B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5C4C77',
      primaryShadow: '#3F2C5F',
      accent: '#677499',
      accentShadow: '#4C5C87',
      background: '#2A173B',
      cardBackground: '#332043',
      text: '#95C5AC',
      extra: '#3F2E4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glow Sky',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#ABA47B',
      primaryShadow: '#898362',
      accent: '#CAC18A',
      accentShadow: '#A29A6E',
      background: '#FFECED',
      cardBackground: '#F2E0E1',
      text: '#1A1A1A',
      extra: '#E2E4DF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B8B28F',
      primaryShadow: '#ABA47B',
      accent: '#D2CA9C',
      accentShadow: '#CAC18A',
      background: '#0D0D10',
      cardBackground: '#141417',
      text: '#FFECED',
      extra: '#E5E7E2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crimson Basin',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B97A60',
      primaryShadow: '#94624D',
      accent: '#9C524E',
      accentShadow: '#7D423E',
      background: '#ECE7E4',
      cardBackground: '#E3DEDB',
      text: '#4B3D44',
      extra: '#DDCF99',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C48E78',
      primaryShadow: '#B97A60',
      accent: '#AB6C69',
      accentShadow: '#9C524E',
      background: '#0E0C0F',
      cardBackground: '#151316',
      text: '#DDCF99',
      extra: '#E0D4A3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oceanic Plaza',
    tags: ['dark', 'curated'],
    light: {
      primary: '#10908E',
      primaryShadow: '#0D7372',
      accent: '#28C074',
      accentShadow: '#209A5D',
      background: '#FAFFFF',
      cardBackground: '#EEF2F2',
      text: '#050914',
      extra: '#3B063A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#34A19F',
      primaryShadow: '#10908E',
      accent: '#48C989',
      accentShadow: '#28C074',
      background: '#050914',
      cardBackground: '#0F131D',
      text: '#FAFFFF',
      extra: '#4F1F4E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Branch',
    tags: ['dark', 'curated'],
    light: {
      primary: '#0098DB',
      primaryShadow: '#007AAF',
      accent: '#FF0546',
      accentShadow: '#CC0438',
      background: '#DDE9EE',
      cardBackground: '#D4E0E4',
      text: '#09010D',
      extra: '#9C173B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A7E0',
      primaryShadow: '#0098DB',
      accent: '#FF2B62',
      accentShadow: '#FF0546',
      background: '#09010D',
      cardBackground: '#130B17',
      text: '#F0F0F0',
      extra: '#A62E4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Soul',
    tags: ['cool', 'curated'],
    light: {
      primary: '#94E344',
      primaryShadow: '#76B636',
      accent: '#46878F',
      accentShadow: '#386C72',
      background: '#E2F3E4',
      cardBackground: '#D7E7D9',
      text: '#332C50',
      extra: '#332C50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A4E760',
      primaryShadow: '#94E344',
      accent: '#6299A0',
      accentShadow: '#46878F',
      background: '#0C0F0E',
      cardBackground: '#131615',
      text: '#E2F3E4',
      extra: '#474162',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Obelisk',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#6B61FF',
      primaryShadow: '#564ECC',
      accent: '#66A1FF',
      accentShadow: '#5281CC',
      background: '#F0EFF4',
      cardBackground: '#E4E3E8',
      text: '#1A1A1A',
      extra: '#FADDA2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8179FF',
      primaryShadow: '#6B61FF',
      accent: '#7DAFFF',
      accentShadow: '#66A1FF',
      background: '#0B0B14',
      cardBackground: '#12121B',
      text: '#F0EFF4',
      extra: '#FBE0AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Mountain',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A8605D',
      primaryShadow: '#864D4A',
      accent: '#60AE7B',
      accentShadow: '#4D8B62',
      background: '#F6E79C',
      cardBackground: '#EADB94',
      text: '#1F244B',
      extra: '#1F244B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B57875',
      primaryShadow: '#A8605D',
      accent: '#78BA8F',
      accentShadow: '#60AE7B',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#F6E79C',
      extra: '#353A5D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Gorge',
    tags: ['cool', 'curated'],
    light: {
      primary: '#E64E4B',
      primaryShadow: '#B83E3C',
      accent: '#365A9B',
      accentShadow: '#2B487C',
      background: '#EDEDED',
      cardBackground: '#E1E1E1',
      text: '#31222C',
      extra: '#F7E5B2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EA6966',
      primaryShadow: '#E64E4B',
      accent: '#5473AA',
      accentShadow: '#365A9B',
      background: '#0F0A0E',
      cardBackground: '#161115',
      text: '#EDEDED',
      extra: '#F8E8BA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glossy Arcade',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#CB8175',
      primaryShadow: '#A2675E',
      accent: '#E2A97E',
      accentShadow: '#B58765',
      background: '#F6EDCD',
      cardBackground: '#EAE1C3',
      text: '#1A1A1A',
      extra: '#F0CF8E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D3948A',
      primaryShadow: '#CB8175',
      accent: '#E6B691',
      accentShadow: '#E2A97E',
      background: '#0E0C10',
      cardBackground: '#151317',
      text: '#F6EDCD',
      extra: '#F2D499',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jade Gorge',
    tags: ['warm', 'curated'],
    light: {
      primary: '#3368DC',
      primaryShadow: '#2953B0',
      accent: '#FF4F69',
      accentShadow: '#CC3F54',
      background: '#FFF7F8',
      cardBackground: '#F2EBEC',
      text: '#2B0F54',
      extra: '#2B0F54',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#527FE1',
      primaryShadow: '#3368DC',
      accent: '#FF6980',
      accentShadow: '#FF4F69',
      background: '#2B0F54',
      cardBackground: '#33195B',
      text: '#FFF7F8',
      extra: '#402765',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silk Summit',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CA5A2E',
      primaryShadow: '#A24825',
      accent: '#FF7831',
      accentShadow: '#CC6027',
      background: '#EDE4E0',
      cardBackground: '#E4DBD7',
      text: '#202215',
      extra: '#DFD785',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D2734D',
      primaryShadow: '#CA5A2E',
      accent: '#FF8C50',
      accentShadow: '#FF7831',
      background: '#202215',
      cardBackground: '#292B1E',
      text: '#DFD785',
      extra: '#E2DB91',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fog Wave',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A96868',
      primaryShadow: '#875353',
      accent: '#764462',
      accentShadow: '#5E364E',
      background: '#EAE5E5',
      cardBackground: '#E1DCDC',
      text: '#2C2137',
      extra: '#2C2137',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B67F7F',
      primaryShadow: '#A96868',
      accent: '#8B607A',
      accentShadow: '#764462',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#EDB4A1',
      extra: '#41374B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rich Temple',
    tags: ['bright', 'curated'],
    light: {
      primary: '#3C42C4',
      primaryShadow: '#30359D',
      accent: '#A065CD',
      accentShadow: '#8051A4',
      background: '#F4DFBE',
      cardBackground: '#E8D4B5',
      text: '#1A1A1A',
      extra: '#6E51C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#595ECD',
      primaryShadow: '#3C42C4',
      accent: '#AE7CD5',
      accentShadow: '#A065CD',
      background: '#0A0A12',
      cardBackground: '#111119',
      text: '#F4DFBE',
      extra: '#7D62CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Navy Aura',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#3B4152',
      primaryShadow: '#2F3442',
      accent: '#150E10',
      accentShadow: '#110B0D',
      background: '#E2E2E3',
      cardBackground: '#D9D9DA',
      text: '#150E10',
      extra: '#272739',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#585E6C',
      primaryShadow: '#3B4152',
      accent: '#383234',
      accentShadow: '#150E10',
      background: '#150E10',
      cardBackground: '#1E181A',
      text: '#F0F0F0',
      extra: '#3D3D4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Canvas',
    tags: ['warm', 'curated'],
    light: {
      primary: '#2BA8B5',
      primaryShadow: '#228691',
      accent: '#F0C209',
      accentShadow: '#C09B07',
      background: '#F9F5D2',
      cardBackground: '#EDE9C8',
      text: '#060329',
      extra: '#CE3B26',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4BB5C0',
      primaryShadow: '#2BA8B5',
      accent: '#F2CB2E',
      accentShadow: '#F0C209',
      background: '#060329',
      cardBackground: '#100D32',
      text: '#F9F5D2',
      extra: '#D34F3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Soft Lake',
    tags: ['curated'],
    light: {
      primary: '#C56981',
      primaryShadow: '#9E5467',
      accent: '#545C7E',
      accentShadow: '#434A65',
      background: '#EDE5E7',
      cardBackground: '#E4DCDE',
      text: '#282328',
      extra: '#282328',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CE8094',
      primaryShadow: '#C56981',
      accent: '#6E7491',
      accentShadow: '#545C7E',
      background: '#282328',
      cardBackground: '#312C31',
      text: '#F0F0F0',
      extra: '#3E393E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Avant Gradient',
    tags: ['cool', 'curated'],
    light: {
      primary: '#7550E8',
      primaryShadow: '#5E40BA',
      accent: '#608FCF',
      accentShadow: '#4D72A6',
      background: '#E6E3EF',
      cardBackground: '#DDDAE5',
      text: '#622E4C',
      extra: '#622E4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8A6AEB',
      primaryShadow: '#7550E8',
      accent: '#78A0D6',
      accentShadow: '#608FCF',
      background: '#0C0A13',
      cardBackground: '#13111A',
      text: '#8BE5FF',
      extra: '#72435E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crimson Vision',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00AAA5',
      primaryShadow: '#008884',
      accent: '#27D3CB',
      accentShadow: '#1FA9A2',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#12173D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B7B3',
      primaryShadow: '#00AAA5',
      accent: '#47DAD3',
      accentShadow: '#27D3CB',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#2A2E50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coral Harbor',
    tags: ['curated'],
    light: {
      primary: '#FB6B1D',
      primaryShadow: '#C95617',
      accent: '#0B8A8F',
      accentShadow: '#096E72',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#3E3546',
      extra: '#E83B3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FC813F',
      primaryShadow: '#FB6B1D',
      accent: '#309CA0',
      accentShadow: '#0B8A8F',
      background: '#100B0D',
      cardBackground: '#171214',
      text: '#FFFFFF',
      extra: '#EA4F4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cyan Relief',
    tags: ['curated'],
    light: {
      primary: '#2D1B00',
      primaryShadow: '#241600',
      accent: '#1E606E',
      accentShadow: '#184D58',
      background: '#C4F0C2',
      cardBackground: '#BAE4B8',
      text: '#2D1B00',
      extra: '#5AB9A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4D3D26',
      primaryShadow: '#2D1B00',
      accent: '#407884',
      accentShadow: '#1E606E',
      background: '#2D1B00',
      cardBackground: '#35240A',
      text: '#C4F0C2',
      extra: '#6BC0B1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bold Terrace',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#2176CC',
      primaryShadow: '#1A5EA3',
      accent: '#FF7D6E',
      accentShadow: '#CC6458',
      background: '#E8E7CB',
      cardBackground: '#DCDBC1',
      text: '#1A1A1A',
      extra: '#FCA6AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#428BD4',
      primaryShadow: '#2176CC',
      accent: '#FF9184',
      accentShadow: '#FF7D6E',
      background: '#090C12',
      cardBackground: '#101319',
      text: '#E8E7CB',
      extra: '#FCAFB4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crisp Gorge',
    tags: ['cool', 'curated'],
    light: {
      primary: '#F14E52',
      primaryShadow: '#C13E42',
      accent: '#9B222B',
      accentShadow: '#7C1B22',
      background: '#DAF2E9',
      cardBackground: '#CFE6DD',
      text: '#1C2638',
      extra: '#95E0CC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F3696C',
      primaryShadow: '#F14E52',
      accent: '#AA434B',
      accentShadow: '#9B222B',
      background: '#1C2638',
      cardBackground: '#252F40',
      text: '#DAF2E9',
      extra: '#A0E3D1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Thicket',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#0EAF9B',
      primaryShadow: '#0B8C7C',
      accent: '#30E1B9',
      accentShadow: '#26B494',
      background: '#DEEBE9',
      cardBackground: '#D5E2E0',
      text: '#411D31',
      extra: '#411D31',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#32BBAA',
      primaryShadow: '#0EAF9B',
      accent: '#4FE6C4',
      accentShadow: '#30E1B9',
      background: '#080D11',
      cardBackground: '#0F1418',
      text: '#F0F0F0',
      extra: '#543446',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Ravine',
    tags: ['curated'],
    light: {
      primary: '#10D275',
      primaryShadow: '#0DA85E',
      accent: '#007899',
      accentShadow: '#00607A',
      background: '#FAFDFF',
      cardBackground: '#EEF0F2',
      text: '#16171A',
      extra: '#7F0622',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#34D98A',
      primaryShadow: '#10D275',
      accent: '#268CA8',
      accentShadow: '#007899',
      background: '#16171A',
      cardBackground: '#1F2023',
      text: '#FAFDFF',
      extra: '#8C1F38',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Navy Volcano',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D1851E',
      primaryShadow: '#A76A18',
      accent: '#D01946',
      accentShadow: '#A61438',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#25131A',
      extra: '#3D253B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D89740',
      primaryShadow: '#D1851E',
      accent: '#D73C62',
      accentShadow: '#D01946',
      background: '#25131A',
      cardBackground: '#2E1C23',
      text: '#FFFFFF',
      extra: '#503B4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Summer Coast',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BA5044',
      primaryShadow: '#954036',
      accent: '#7A1C4B',
      accentShadow: '#62163C',
      background: '#EFF9D6',
      cardBackground: '#E3EDCB',
      text: '#1B0326',
      extra: '#7A1C4B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C46A60',
      primaryShadow: '#BA5044',
      accent: '#8E3E66',
      accentShadow: '#7A1C4B',
      background: '#1B0326',
      cardBackground: '#240D2F',
      text: '#EFF9D6',
      extra: '#87335D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bold Tapestry',
    tags: ['curated'],
    light: {
      primary: '#BF2651',
      primaryShadow: '#991E41',
      accent: '#F54F4F',
      accentShadow: '#C43F3F',
      background: '#FAFAC3',
      cardBackground: '#EEEEB9',
      text: '#242B4A',
      extra: '#3D3957',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C9476B',
      primaryShadow: '#BF2651',
      accent: '#F76969',
      accentShadow: '#F54F4F',
      background: '#0E090E',
      cardBackground: '#151015',
      text: '#FAFAC3',
      extra: '#504D68',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Warm Foyer',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D8725E',
      primaryShadow: '#AD5B4B',
      accent: '#A94B54',
      accentShadow: '#873C43',
      background: '#FCECD1',
      cardBackground: '#EFE0C7',
      text: '#2D1E2F',
      extra: '#A0DDD3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE8776',
      primaryShadow: '#D8725E',
      accent: '#B6666E',
      accentShadow: '#A94B54',
      background: '#2D1E2F',
      cardBackground: '#352737',
      text: '#FCECD1',
      extra: '#AAE0D7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neo Loft',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#3FA0A4',
      primaryShadow: '#328083',
      accent: '#286080',
      accentShadow: '#204D66',
      background: '#E3F5F1',
      cardBackground: '#D8E9E5',
      text: '#070C1D',
      extra: '#9F3C91',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5CAEB2',
      primaryShadow: '#3FA0A4',
      accent: '#487893',
      accentShadow: '#286080',
      background: '#070C1D',
      cardBackground: '#111626',
      text: '#E3F5F1',
      extra: '#A9509C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Vision',
    tags: ['curated'],
    light: {
      primary: '#96485B',
      primaryShadow: '#783A49',
      accent: '#612447',
      accentShadow: '#4E1D39',
      background: '#FAFFE0',
      cardBackground: '#EEF2D5',
      text: '#421E42',
      extra: '#6074AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A66374',
      primaryShadow: '#96485B',
      accent: '#794563',
      accentShadow: '#612447',
      background: '#0D0A0F',
      cardBackground: '#141116',
      text: '#FAFFE0',
      extra: '#7082B3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Ridge',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C75533',
      primaryShadow: '#9F4429',
      accent: '#E76D46',
      accentShadow: '#B95738',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1B2026',
      extra: '#2F1E1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF6F52',
      primaryShadow: '#C75533',
      accent: '#EB8362',
      accentShadow: '#E76D46',
      background: '#1B2026',
      cardBackground: '#24292F',
      text: '#FFFFFF',
      extra: '#443531',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Battlement',
    tags: ['bright', 'curated'],
    light: {
      primary: '#2BA9B4',
      primaryShadow: '#228790',
      accent: '#634B7D',
      accentShadow: '#4F3C64',
      background: '#F0F6E8',
      cardBackground: '#E4EADC',
      text: '#1A1A1A',
      extra: '#F0DAB1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4BB6BF',
      primaryShadow: '#2BA9B4',
      accent: '#7A6691',
      accentShadow: '#634B7D',
      background: '#090D11',
      cardBackground: '#101418',
      text: '#F0F6E8',
      extra: '#F2DEB9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Veranda',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CD5F2A',
      primaryShadow: '#A44C22',
      accent: '#F2AB37',
      accentShadow: '#C2892C',
      background: '#FAF5D8',
      cardBackground: '#EEE9CD',
      text: '#21181B',
      extra: '#D8AE8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D5774A',
      primaryShadow: '#CD5F2A',
      accent: '#F4B855',
      accentShadow: '#F2AB37',
      background: '#21181B',
      cardBackground: '#2A2124',
      text: '#FAF5D8',
      extra: '#DCB697',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cool Patio',
    tags: ['curated'],
    light: {
      primary: '#F8401B',
      primaryShadow: '#C63316',
      accent: '#F87B1B',
      accentShadow: '#C66216',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#28192F',
      extra: '#CECAC9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F95D3D',
      primaryShadow: '#F8401B',
      accent: '#F98F3D',
      accentShadow: '#F87B1B',
      background: '#28192F',
      cardBackground: '#312237',
      text: '#FFFFFF',
      extra: '#D3CFCE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Volcano',
    tags: ['warm', 'curated'],
    light: {
      primary: '#718245',
      primaryShadow: '#5A6837',
      accent: '#675239',
      accentShadow: '#52422E',
      background: '#E6E7E2',
      cardBackground: '#DDDED9',
      text: '#313432',
      extra: '#313432',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#869561',
      primaryShadow: '#718245',
      accent: '#7E6C57',
      accentShadow: '#675239',
      background: '#0B0C0E',
      cardBackground: '#121315',
      text: '#BCB7A5',
      extra: '#464847',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amber Shade',
    tags: ['cool', 'curated'],
    light: {
      primary: '#392C31',
      primaryShadow: '#2E2327',
      accent: '#73817B',
      accentShadow: '#5C6762',
      background: '#E6EAEE',
      cardBackground: '#DBDEE2',
      text: '#392C31',
      extra: '#4A3C4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#574C50',
      primaryShadow: '#392C31',
      accent: '#88948F',
      accentShadow: '#73817B',
      background: '#0A090D',
      cardBackground: '#111014',
      text: '#E6EAEE',
      extra: '#5C505C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Jungle',
    tags: ['curated'],
    light: {
      primary: '#355D69',
      primaryShadow: '#2A4A54',
      accent: '#6AAE9D',
      accentShadow: '#558B7E',
      background: '#F4E9D4',
      cardBackground: '#E8DDC9',
      text: '#37364E',
      extra: '#37364E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#537580',
      primaryShadow: '#355D69',
      accent: '#80BAAC',
      accentShadow: '#6AAE9D',
      background: '#0A0B0F',
      cardBackground: '#111216',
      text: '#F4E9D4',
      extra: '#4B4A60',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rich Stream',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C57835',
      primaryShadow: '#9E602A',
      accent: '#C33C40',
      accentShadow: '#9C3033',
      background: '#E5EFEF',
      cardBackground: '#DAE3E3',
      text: '#1A1F2E',
      extra: '#3F4328',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CE8C53',
      primaryShadow: '#C57835',
      accent: '#CC595D',
      accentShadow: '#C33C40',
      background: '#1A1F2E',
      cardBackground: '#232836',
      text: '#E5EFEF',
      extra: '#52563E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spring Chapel',
    tags: ['cool', 'curated'],
    light: {
      primary: '#C4651C',
      primaryShadow: '#9D5116',
      accent: '#F3C220',
      accentShadow: '#C29B1A',
      background: '#C1E5EA',
      cardBackground: '#B7DADE',
      text: '#2E2C3B',
      extra: '#2E2C3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CD7C3E',
      primaryShadow: '#C4651C',
      accent: '#F5CB41',
      accentShadow: '#F3C220',
      background: '#0E0B0D',
      cardBackground: '#151214',
      text: '#C1E5EA',
      extra: '#43414F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tranquil Horizon',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#412752',
      primaryShadow: '#341F42',
      accent: '#9775A6',
      accentShadow: '#795E85',
      background: '#E2E0E3',
      cardBackground: '#D9D7DA',
      text: '#2D162C',
      extra: '#2D162C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E476C',
      primaryShadow: '#412752',
      accent: '#A78AB3',
      accentShadow: '#9775A6',
      background: '#2D162C',
      cardBackground: '#351F34',
      text: '#F0F0F0',
      extra: '#422D41',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Bloom',
    tags: ['curated'],
    light: {
      primary: '#D06549',
      primaryShadow: '#A6513A',
      accent: '#052B7E',
      accentShadow: '#042265',
      background: '#FCE9AF',
      cardBackground: '#EFDDA6',
      text: '#280722',
      extra: '#FB7C86',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D77C64',
      primaryShadow: '#D06549',
      accent: '#2B4B91',
      accentShadow: '#052B7E',
      background: '#280722',
      cardBackground: '#31112B',
      text: '#FCE9AF',
      extra: '#FB8992',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Island',
    tags: ['curated'],
    light: {
      primary: '#C7424F',
      primaryShadow: '#9F353F',
      accent: '#E06B51',
      accentShadow: '#B35641',
      background: '#FFFFE0',
      cardBackground: '#F2F2D5',
      text: '#3D003D',
      extra: '#7B334C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF5E69',
      primaryShadow: '#C7424F',
      accent: '#E5816B',
      accentShadow: '#E06B51',
      background: '#3D003D',
      cardBackground: '#450A45',
      text: '#FFFFE0',
      extra: '#88475E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Vent',
    tags: ['curated'],
    light: {
      primary: '#00BFA3',
      primaryShadow: '#009982',
      accent: '#008279',
      accentShadow: '#006861',
      background: '#F5F7FA',
      cardBackground: '#E9EBEE',
      text: '#000000',
      extra: '#222323',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C9B1',
      primaryShadow: '#00BFA3',
      accent: '#26958D',
      accentShadow: '#008279',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F5F7FA',
      extra: '#383939',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ice Aura',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B86343',
      primaryShadow: '#934F36',
      accent: '#AA8F68',
      accentShadow: '#887253',
      background: '#ECE5E2',
      cardBackground: '#E3DCD9',
      text: '#49343D',
      extra: '#B65C5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C37A5F',
      primaryShadow: '#B86343',
      accent: '#B7A07F',
      accentShadow: '#AA8F68',
      background: '#0E0B0E',
      cardBackground: '#151215',
      text: '#D2C9A5',
      extra: '#BD6C6F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Galaxy',
    tags: ['curated'],
    light: {
      primary: '#158968',
      primaryShadow: '#116E53',
      accent: '#EC614A',
      accentShadow: '#BD4E3B',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#8CFFDE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#389B7F',
      primaryShadow: '#158968',
      accent: '#EF7965',
      accentShadow: '#EC614A',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#98FFE1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Burgundy Branch',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#A06389',
      primaryShadow: '#804F6E',
      accent: '#CB7CA2',
      accentShadow: '#A26382',
      background: '#FFFCF1',
      cardBackground: '#F2EFE5',
      text: '#1A1A1A',
      extra: '#684971',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AE7A9B',
      primaryShadow: '#A06389',
      accent: '#D390B0',
      accentShadow: '#CB7CA2',
      background: '#0D0B10',
      cardBackground: '#141217',
      text: '#FFFCF1',
      extra: '#775B7F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Balmy Root',
    tags: ['cool', 'curated'],
    light: {
      primary: '#535373',
      primaryShadow: '#42425C',
      accent: '#8080A4',
      accentShadow: '#666683',
      background: '#E6E6EC',
      cardBackground: '#DBDBE0',
      text: '#0E0E12',
      extra: '#1A1A24',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6D6D88',
      primaryShadow: '#535373',
      accent: '#9393B2',
      accentShadow: '#8080A4',
      background: '#0E0E12',
      cardBackground: '#18181B',
      text: '#E6E6EC',
      extra: '#31313A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen Prism',
    tags: ['curated'],
    light: {
      primary: '#0A89FF',
      primaryShadow: '#086ECC',
      accent: '#6AB417',
      accentShadow: '#559012',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#E03C28',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2F9BFF',
      primaryShadow: '#0A89FF',
      accent: '#80BF3A',
      accentShadow: '#6AB417',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#E3503E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Prairie',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00B9BE',
      primaryShadow: '#009498',
      accent: '#005F8C',
      accentShadow: '#004C70',
      background: '#9FF4E5',
      cardBackground: '#97E8DA',
      text: '#002B59',
      extra: '#002B59',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C4C8',
      primaryShadow: '#00B9BE',
      accent: '#26779D',
      accentShadow: '#005F8C',
      background: '#002B59',
      cardBackground: '#0A3360',
      text: '#9FF4E5',
      extra: '#1A406A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ethereal Heath',
    tags: ['warm', 'curated'],
    light: {
      primary: '#708028',
      primaryShadow: '#5A6620',
      accent: '#405010',
      accentShadow: '#33400D',
      background: '#E6E7E0',
      cardBackground: '#DDDED7',
      text: '#405010',
      extra: '#D0D058',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#859348',
      primaryShadow: '#708028',
      accent: '#5D6A34',
      accentShadow: '#405010',
      background: '#0B0C0D',
      cardBackground: '#121314',
      text: '#D0D058',
      extra: '#D5D569',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Misty Sunrise',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#FF252B',
      primaryShadow: '#CC1E22',
      accent: '#A8141D',
      accentShadow: '#861017',
      background: '#F1E0E0',
      cardBackground: '#E7D7D7',
      text: '#130310',
      extra: '#3C0921',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF464B',
      primaryShadow: '#FF252B',
      accent: '#B5373F',
      accentShadow: '#A8141D',
      background: '#130310',
      cardBackground: '#1C0D1A',
      text: '#F0F0F0',
      extra: '#502237',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ice Stream',
    tags: ['curated'],
    light: {
      primary: '#CC3495',
      primaryShadow: '#A32A77',
      accent: '#6B1FB1',
      accentShadow: '#56198E',
      background: '#F8E3C4',
      cardBackground: '#ECD8BA',
      text: '#0B0630',
      extra: '#6B1FB1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D452A5',
      primaryShadow: '#CC3495',
      accent: '#8141BD',
      accentShadow: '#6B1FB1',
      background: '#0B0630',
      cardBackground: '#151038',
      text: '#F8E3C4',
      extra: '#7A35B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Peak',
    tags: ['dark', 'curated'],
    light: {
      primary: '#AA11BB',
      primaryShadow: '#880E96',
      accent: '#B837EA',
      accentShadow: '#932CBB',
      background: '#FFF6F6',
      cardBackground: '#F2EAEA',
      text: '#000000',
      extra: '#AA81FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B735C5',
      primaryShadow: '#AA11BB',
      accent: '#C355ED',
      accentShadow: '#B837EA',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFF6F6',
      extra: '#B38EFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Galaxy',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AA644D',
      primaryShadow: '#88503E',
      accent: '#372A39',
      accentShadow: '#2C222E',
      background: '#F5E9BF',
      cardBackground: '#E9DDB5',
      text: '#372A39',
      extra: '#788374',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B77B68',
      primaryShadow: '#AA644D',
      accent: '#554A57',
      accentShadow: '#372A39',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#F5E9BF',
      extra: '#868F82',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Windy Vision',
    tags: ['cool', 'curated'],
    light: {
      primary: '#986F9C',
      primaryShadow: '#7A597D',
      accent: '#6F577E',
      accentShadow: '#594665',
      background: '#EAE4DD',
      cardBackground: '#DED9D2',
      text: '#413652',
      extra: '#413652',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A785AB',
      primaryShadow: '#986F9C',
      accent: '#857091',
      accentShadow: '#6F577E',
      background: '#0D0B11',
      cardBackground: '#141218',
      text: '#EAE4DD',
      extra: '#544A63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Prairie',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E76D8A',
      primaryShadow: '#B9576E',
      accent: '#253657',
      accentShadow: '#1E2B46',
      background: '#F8E9E5',
      cardBackground: '#ECDDDA',
      text: '#253657',
      extra: '#6F4367',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EB839C',
      primaryShadow: '#E76D8A',
      accent: '#465470',
      accentShadow: '#253657',
      background: '#0F0B10',
      cardBackground: '#161217',
      text: '#F8E9E5',
      extra: '#7D5676',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pearl Island',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#E84A5F',
      primaryShadow: '#BA3B4C',
      accent: '#900C3F',
      accentShadow: '#730A32',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#900C3F',
      extra: '#FF847C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EB6577',
      primaryShadow: '#E84A5F',
      accent: '#A1305C',
      accentShadow: '#900C3F',
      background: '#0F0A0F',
      cardBackground: '#161116',
      text: '#FFFFFF',
      extra: '#FF9089',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Arid Mural',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4888B7',
      primaryShadow: '#3A6D92',
      accent: '#6DBCB9',
      accentShadow: '#579694',
      background: '#E3E8EB',
      cardBackground: '#DADFE2',
      text: '#372134',
      extra: '#372134',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#639AC2',
      primaryShadow: '#4888B7',
      accent: '#83C6C4',
      accentShadow: '#6DBCB9',
      background: '#0A0C11',
      cardBackground: '#111318',
      text: '#8CEFB6',
      extra: '#4B3748',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Loft',
    tags: ['dark', 'curated'],
    light: {
      primary: '#713141',
      primaryShadow: '#5A2734',
      accent: '#312137',
      accentShadow: '#271A2C',
      background: '#E6E1E2',
      cardBackground: '#DDD8D9',
      text: '#1A2129',
      extra: '#1A2129',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#86505E',
      primaryShadow: '#713141',
      accent: '#504255',
      accentShadow: '#312137',
      background: '#1A2129',
      cardBackground: '#232A32',
      text: '#F0F0F0',
      extra: '#31373E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Mural',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#E55388',
      primaryShadow: '#B7426D',
      accent: '#5AC5CC',
      accentShadow: '#489EA3',
      background: '#EFE3E8',
      cardBackground: '#E5DADF',
      text: '#1A1A1A',
      extra: '#E57D88',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E96D9A',
      primaryShadow: '#E55388',
      accent: '#73CED4',
      accentShadow: '#5AC5CC',
      background: '#0F0A10',
      cardBackground: '#161117',
      text: '#E3D5CC',
      extra: '#E88A94',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bright Hotspring',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#579C9A',
      primaryShadow: '#467D7B',
      accent: '#294257',
      accentShadow: '#213546',
      background: '#E4E9E9',
      cardBackground: '#DBE0E0',
      text: '#191B1A',
      extra: '#191B1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#70ABA9',
      primaryShadow: '#579C9A',
      accent: '#495E70',
      accentShadow: '#294257',
      background: '#191B1A',
      cardBackground: '#222423',
      text: '#99C9B3',
      extra: '#303231',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eternal Sunset',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DD4900',
      primaryShadow: '#B13A00',
      accent: '#FF7700',
      accentShadow: '#CC5F00',
      background: '#FFF594',
      cardBackground: '#F2E98D',
      text: '#391513',
      extra: '#391513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E26426',
      primaryShadow: '#DD4900',
      accent: '#FF8B26',
      accentShadow: '#FF7700',
      background: '#391513',
      cardBackground: '#411E1C',
      text: '#FFF594',
      extra: '#4D2C2B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Twilight Seed',
    tags: ['dark', 'curated'],
    light: {
      primary: '#EE243D',
      primaryShadow: '#BE1D31',
      accent: '#6B2341',
      accentShadow: '#561C34',
      background: '#F0E0E2',
      cardBackground: '#E6D7D9',
      text: '#0D101B',
      extra: '#281A2D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F1455A',
      primaryShadow: '#EE243D',
      accent: '#81445E',
      accentShadow: '#6B2341',
      background: '#0D101B',
      cardBackground: '#171A24',
      text: '#F0F0F0',
      extra: '#3E3142',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Nova',
    tags: ['cool', 'curated'],
    light: {
      primary: '#8C76BE',
      primaryShadow: '#705E98',
      accent: '#EA92AB',
      accentShadow: '#BB7589',
      background: '#E8E6EC',
      cardBackground: '#DFDDE3',
      text: '#1A1A1A',
      extra: '#F4C4D4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9D8BC8',
      primaryShadow: '#8C76BE',
      accent: '#EDA2B8',
      accentShadow: '#EA92AB',
      background: '#0C0C12',
      cardBackground: '#131319',
      text: '#F4C4D4',
      extra: '#F5CAD8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oceanic Moss',
    tags: ['warm', 'curated'],
    light: {
      primary: '#74A33F',
      primaryShadow: '#5D8232',
      accent: '#EE9C5D',
      accentShadow: '#BE7D4A',
      background: '#FCFFC0',
      cardBackground: '#EFF2B6',
      text: '#2F142F',
      extra: '#6EB8A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#89B15C',
      primaryShadow: '#74A33F',
      accent: '#F1AB75',
      accentShadow: '#EE9C5D',
      background: '#2F142F',
      cardBackground: '#371D37',
      text: '#FCFFC0',
      extra: '#7DBFB1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Ocean',
    tags: ['curated'],
    light: {
      primary: '#C7786F',
      primaryShadow: '#9F6059',
      accent: '#2E2A4F',
      accentShadow: '#25223F',
      background: '#EDE6E6',
      cardBackground: '#E4DDDD',
      text: '#211D38',
      extra: '#211D38',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF8C85',
      primaryShadow: '#C7786F',
      accent: '#4D4A69',
      accentShadow: '#2E2A4F',
      background: '#211D38',
      cardBackground: '#2A2640',
      text: '#CDD4A5',
      extra: '#37344C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Arid Comet',
    tags: ['curated'],
    light: {
      primary: '#D17C7C',
      primaryShadow: '#A76363',
      accent: '#5B768D',
      accentShadow: '#495E71',
      background: '#EEE7E7',
      cardBackground: '#E4DEDE',
      text: '#46425E',
      extra: '#46425E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D89090',
      primaryShadow: '#D17C7C',
      accent: '#748B9E',
      accentShadow: '#5B768D',
      background: '#0E0C10',
      cardBackground: '#151317',
      text: '#F6C6A8',
      extra: '#59556E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Satin Petal',
    tags: ['curated'],
    light: {
      primary: '#BB474F',
      primaryShadow: '#96393F',
      accent: '#7A9C96',
      accentShadow: '#627D78',
      background: '#ECE2E3',
      cardBackground: '#E3D9DA',
      text: '#392B35',
      extra: '#392B35',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C56369',
      primaryShadow: '#BB474F',
      accent: '#8EABA6',
      accentShadow: '#7A9C96',
      background: '#0E0A0E',
      cardBackground: '#151115',
      text: '#D1BFB0',
      extra: '#4D4049',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Stone',
    tags: ['curated'],
    light: {
      primary: '#F89020',
      primaryShadow: '#C6731A',
      accent: '#300030',
      accentShadow: '#260026',
      background: '#F8F088',
      cardBackground: '#ECE481',
      text: '#300030',
      extra: '#602878',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F9A141',
      primaryShadow: '#F89020',
      accent: '#4F264F',
      accentShadow: '#300030',
      background: '#300030',
      cardBackground: '#380A38',
      text: '#F8F088',
      extra: '#703E86',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pearl Savanna',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#CFAB51',
      primaryShadow: '#A68941',
      accent: '#9D654C',
      accentShadow: '#7E513D',
      background: '#EDEAE3',
      cardBackground: '#E4E1DA',
      text: '#210B1B',
      extra: '#4D222C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D6B86B',
      primaryShadow: '#CFAB51',
      accent: '#AC7C67',
      accentShadow: '#9D654C',
      background: '#210B1B',
      cardBackground: '#2A1524',
      text: '#F0F0F0',
      extra: '#5F3841',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Wind',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4185D8',
      primaryShadow: '#346AAD',
      accent: '#14665B',
      accentShadow: '#105249',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#12173D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E97DE',
      primaryShadow: '#4185D8',
      accent: '#377D74',
      accentShadow: '#14665B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#2A2E50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Courtyard',
    tags: ['bright', 'curated'],
    light: {
      primary: '#933A63',
      primaryShadow: '#762E4F',
      accent: '#7679DB',
      accentShadow: '#5E61AF',
      background: '#FFECF0',
      cardBackground: '#F2E0E4',
      text: '#44387A',
      extra: '#FF95B1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A3587A',
      primaryShadow: '#933A63',
      accent: '#8B8DE0',
      accentShadow: '#7679DB',
      background: '#0C0A0F',
      cardBackground: '#131116',
      text: '#FFECF0',
      extra: '#FFA0B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Patio',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00BE91',
      primaryShadow: '#009874',
      accent: '#085562',
      accentShadow: '#06444E',
      background: '#F2FF66',
      cardBackground: '#E6F261',
      text: '#001D2A',
      extra: '#9AF089',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C8A2',
      primaryShadow: '#00BE91',
      accent: '#2D6F7A',
      accentShadow: '#085562',
      background: '#001D2A',
      cardBackground: '#0A2633',
      text: '#F2FF66',
      extra: '#A4F295',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Emerald Nebula',
    tags: ['curated'],
    light: {
      primary: '#BD555F',
      primaryShadow: '#97444C',
      accent: '#6699CC',
      accentShadow: '#527AA3',
      background: '#F5F5F5',
      cardBackground: '#E9E9E9',
      text: '#1A1A1A',
      extra: '#597080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C76F77',
      primaryShadow: '#BD555F',
      accent: '#7DA8D4',
      accentShadow: '#6699CC',
      background: '#1A1A1A',
      cardBackground: '#232323',
      text: '#F5F5F5',
      extra: '#6A7E8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ephemeral Vine',
    tags: ['curated'],
    light: {
      primary: '#1793E6',
      primaryShadow: '#1276B8',
      accent: '#068051',
      accentShadow: '#056641',
      background: '#F2F2DA',
      cardBackground: '#E6E6CF',
      text: '#000000',
      extra: '#FABBAF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3AA3EA',
      primaryShadow: '#1793E6',
      accent: '#2B936B',
      accentShadow: '#068051',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F2F2DA',
      extra: '#FBC2B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bronze Aura',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E56F4B',
      primaryShadow: '#B7593C',
      accent: '#A94949',
      accentShadow: '#873A3A',
      background: '#EDE4DA',
      cardBackground: '#E1D9CF',
      text: '#353540',
      extra: '#BFB8B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E98566',
      primaryShadow: '#E56F4B',
      accent: '#B66464',
      accentShadow: '#A94949',
      background: '#0F0B0E',
      cardBackground: '#161215',
      text: '#EDE4DA',
      extra: '#C5BFBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crisp Fortress',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4C5797',
      primaryShadow: '#3D4679',
      accent: '#757AB2',
      accentShadow: '#5E628E',
      background: '#FDFDFF',
      cardBackground: '#F0F0F2',
      text: '#323348',
      extra: '#323348',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6770A7',
      primaryShadow: '#4C5797',
      accent: '#8A8EBE',
      accentShadow: '#757AB2',
      background: '#0A0B11',
      cardBackground: '#111218',
      text: '#FDFDFF',
      extra: '#47475A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Nebula',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#04373B',
      primaryShadow: '#032C2F',
      accent: '#1A644E',
      accentShadow: '#15503E',
      background: '#DDE1E2',
      cardBackground: '#D4D8D9',
      text: '#0A1A2F',
      extra: '#D1CB95',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2A5558',
      primaryShadow: '#04373B',
      accent: '#3C7B69',
      accentShadow: '#1A644E',
      background: '#0A1A2F',
      cardBackground: '#142337',
      text: '#D1CB95',
      extra: '#D6D0A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Parlor',
    tags: ['warm', 'curated'],
    light: {
      primary: '#1C92A7',
      primaryShadow: '#167586',
      accent: '#890027',
      accentShadow: '#6E001F',
      background: '#FFFFE4',
      cardBackground: '#F2F2D9',
      text: '#030710',
      extra: '#D9243C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3EA2B4',
      primaryShadow: '#1C92A7',
      accent: '#9B2647',
      accentShadow: '#890027',
      background: '#030710',
      cardBackground: '#0D111A',
      text: '#FFFFE4',
      extra: '#DD3A50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Arcade',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#8854F3',
      primaryShadow: '#6D43C2',
      accent: '#000871',
      accentShadow: '#00065A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000871',
      extra: '#639BFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9A6EF5',
      primaryShadow: '#8854F3',
      accent: '#262D86',
      accentShadow: '#000871',
      background: '#000871',
      cardBackground: '#0A1277',
      text: '#FFFFFF',
      extra: '#73A5FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Alpine Fresco',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A2856C',
      primaryShadow: '#826A56',
      accent: '#6E5F52',
      accentShadow: '#584C42',
      background: '#FFFEFE',
      cardBackground: '#F2F1F1',
      text: '#2B3634',
      extra: '#2B3634',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B09782',
      primaryShadow: '#A2856C',
      accent: '#84776C',
      accentShadow: '#6E5F52',
      background: '#0D0C0F',
      cardBackground: '#141316',
      text: '#FFFEFE',
      extra: '#404A48',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snowy Galaxy',
    tags: ['curated'],
    light: {
      primary: '#0484D1',
      primaryShadow: '#036AA7',
      accent: '#FB922B',
      accentShadow: '#C97522',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#3F2832',
      extra: '#E4A672',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2A96D8',
      primaryShadow: '#0484D1',
      accent: '#FCA24B',
      accentShadow: '#FB922B',
      background: '#080C12',
      cardBackground: '#0F1319',
      text: '#FFFFFF',
      extra: '#E7AF80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blaze Altar',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C57938',
      primaryShadow: '#9E612D',
      accent: '#975330',
      accentShadow: '#794226',
      background: '#FFE596',
      cardBackground: '#F2DA8F',
      text: '#313638',
      extra: '#313638',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CE8D56',
      primaryShadow: '#C57938',
      accent: '#A76D4F',
      accentShadow: '#975330',
      background: '#0E0C0E',
      cardBackground: '#151315',
      text: '#FFE596',
      extra: '#464A4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Vine',
    tags: ['dark', 'curated'],
    light: {
      primary: '#C4181F',
      primaryShadow: '#9D1319',
      accent: '#120A19',
      accentShadow: '#0E0814',
      background: '#ECDFDF',
      cardBackground: '#E3D6D6',
      text: '#120A19',
      extra: '#7E1F23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CD3B41',
      primaryShadow: '#C4181F',
      accent: '#362F3C',
      accentShadow: '#120A19',
      background: '#120A19',
      cardBackground: '#1B1422',
      text: '#F0F0F0',
      extra: '#8B3539',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cool Heath',
    tags: ['bright', 'curated'],
    light: {
      primary: '#755F9C',
      primaryShadow: '#5E4C7D',
      accent: '#8D89C7',
      accentShadow: '#716E9F',
      background: '#FFE2DB',
      cardBackground: '#F2D7D0',
      text: '#1A1A1A',
      extra: '#D9A7C6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8A77AB',
      primaryShadow: '#755F9C',
      accent: '#9E9BCF',
      accentShadow: '#8D89C7',
      background: '#0C0B11',
      cardBackground: '#131218',
      text: '#FFE2DB',
      extra: '#DDB0CC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shine Sky',
    tags: ['cool', 'curated'],
    light: {
      primary: '#508CD7',
      primaryShadow: '#4070AC',
      accent: '#E6C86E',
      accentShadow: '#B8A058',
      background: '#DCF5FF',
      cardBackground: '#D1E9F2',
      text: '#000000',
      extra: '#55415F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6A9DDD',
      primaryShadow: '#508CD7',
      accent: '#EAD084',
      accentShadow: '#E6C86E',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#DCF5FF',
      extra: '#66546F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Moor',
    tags: ['curated'],
    light: {
      primary: '#DF7126',
      primaryShadow: '#B25A1E',
      accent: '#AC3232',
      accentShadow: '#8A2828',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#222034',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E48647',
      primaryShadow: '#DF7126',
      accent: '#B85151',
      accentShadow: '#AC3232',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#383648',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oceanic Orchard',
    tags: ['curated'],
    light: {
      primary: '#C96D45',
      primaryShadow: '#A15737',
      accent: '#A52D27',
      accentShadow: '#84241F',
      background: '#D4EDED',
      cardBackground: '#C9E1E1',
      text: '#28221F',
      extra: '#D1CA80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D18361',
      primaryShadow: '#C96D45',
      accent: '#B34D47',
      accentShadow: '#A52D27',
      background: '#28221F',
      cardBackground: '#312B28',
      text: '#D4EDED',
      extra: '#D6CF8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Meadow',
    tags: ['curated'],
    light: {
      primary: '#00FAAC',
      primaryShadow: '#00C88A',
      accent: '#FF3796',
      accentShadow: '#CC2C78',
      background: '#FFFDAF',
      cardBackground: '#F2F0A6',
      text: '#302387',
      extra: '#302387',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26FBB8',
      primaryShadow: '#00FAAC',
      accent: '#FF55A6',
      accentShadow: '#FF3796',
      background: '#081011',
      cardBackground: '#0F1718',
      text: '#FFFDAF',
      extra: '#453993',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glow Leaf',
    tags: ['dark', 'curated'],
    light: {
      primary: '#4B974F',
      primaryShadow: '#3C793F',
      accent: '#2F6B4E',
      accentShadow: '#26563E',
      background: '#CAEBE0',
      cardBackground: '#C0DFD5',
      text: '#070109',
      extra: '#CAEBE0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#66A769',
      primaryShadow: '#4B974F',
      accent: '#4E8169',
      accentShadow: '#2F6B4E',
      background: '#070109',
      cardBackground: '#110B13',
      text: '#CAEBE0',
      extra: '#CFEDE3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vibrant Sunset',
    tags: ['curated'],
    light: {
      primary: '#EE3046',
      primaryShadow: '#BE2638',
      accent: '#A62654',
      accentShadow: '#851E43',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#0C082A',
      extra: '#175145',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F14F62',
      primaryShadow: '#EE3046',
      accent: '#B3476E',
      accentShadow: '#A62654',
      background: '#0C082A',
      cardBackground: '#161233',
      text: '#FFFFFF',
      extra: '#2E6258',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silver Floe',
    tags: ['dark', 'curated'],
    light: {
      primary: '#B6834C',
      primaryShadow: '#92693D',
      accent: '#3A708E',
      accentShadow: '#2E5A72',
      background: '#EDE6CB',
      cardBackground: '#E1DBC1',
      text: '#111215',
      extra: '#699FAD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C19667',
      primaryShadow: '#B6834C',
      accent: '#58859F',
      accentShadow: '#3A708E',
      background: '#111215',
      cardBackground: '#1B1B1E',
      text: '#EDE6CB',
      extra: '#78A9B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Tapestry',
    tags: ['curated'],
    light: {
      primary: '#FF4D6D',
      primaryShadow: '#CC3E57',
      accent: '#012824',
      accentShadow: '#01201D',
      background: '#FCDEEA',
      cardBackground: '#EFD3DE',
      text: '#012824',
      extra: '#265935',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6883',
      primaryShadow: '#FF4D6D',
      accent: '#274845',
      accentShadow: '#012824',
      background: '#012824',
      cardBackground: '#0B312D',
      text: '#FCDEEA',
      extra: '#3C6A49',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fertile Twilight',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A4653E',
      primaryShadow: '#835132',
      accent: '#902E29',
      accentShadow: '#732521',
      background: '#EAE5E2',
      cardBackground: '#E1DCD9',
      text: '#24171B',
      extra: '#495435',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B27C5B',
      primaryShadow: '#A4653E',
      accent: '#A14D49',
      accentShadow: '#902E29',
      background: '#24171B',
      cardBackground: '#2D2024',
      text: '#DEBF89',
      extra: '#5B6549',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Magenta Root',
    tags: ['curated'],
    light: {
      primary: '#CF752B',
      primaryShadow: '#A65E22',
      accent: '#CC2F7B',
      accentShadow: '#A32662',
      background: '#F5FFE8',
      cardBackground: '#E9F2DC',
      text: '#14182E',
      extra: '#21181B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D68A4B',
      primaryShadow: '#CF752B',
      accent: '#D44E8F',
      accentShadow: '#CC2F7B',
      background: '#14182E',
      cardBackground: '#1D2136',
      text: '#F5FFE8',
      extra: '#372F32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Cloister',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#382843',
      primaryShadow: '#2D2036',
      accent: '#7C6D80',
      accentShadow: '#635766',
      background: '#E1E0E2',
      cardBackground: '#D8D7D9',
      text: '#000000',
      extra: '#C7C6C6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#56485F',
      primaryShadow: '#382843',
      accent: '#908393',
      accentShadow: '#7C6D80',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#C7C6C6',
      extra: '#CDCCCC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Navy Plaza',
    tags: ['warm', 'curated'],
    light: {
      primary: '#944431',
      primaryShadow: '#763627',
      accent: '#AD9D62',
      accentShadow: '#8A7E4E',
      background: '#F1E2BE',
      cardBackground: '#E5D7B5',
      text: '#000000',
      extra: '#F1E2BE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A46050',
      primaryShadow: '#944431',
      accent: '#B9AC7A',
      accentShadow: '#AD9D62',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F1E2BE',
      extra: '#F2E5C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coral Hotspring',
    tags: ['bright', 'curated'],
    light: {
      primary: '#FDA9A9',
      primaryShadow: '#CA8787',
      accent: '#82939B',
      accentShadow: '#68767C',
      background: '#F3EDED',
      cardBackground: '#E7E1E1',
      text: '#1A1A1A',
      extra: '#B9EEDC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FDB6B6',
      primaryShadow: '#FDA9A9',
      accent: '#95A3AA',
      accentShadow: '#82939B',
      background: '#100D11',
      cardBackground: '#171418',
      text: '#F3EDED',
      extra: '#C0F0E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bold Sculpture',
    tags: ['curated'],
    light: {
      primary: '#02AACF',
      primaryShadow: '#0288A6',
      accent: '#F99F0A',
      accentShadow: '#C77F08',
      background: '#FDE2A4',
      cardBackground: '#F0D79C',
      text: '#502D54',
      extra: '#F9BDB4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#28B7D6',
      primaryShadow: '#02AACF',
      accent: '#FAAD2F',
      accentShadow: '#F99F0A',
      background: '#080D12',
      cardBackground: '#0F1419',
      text: '#FDE2A4',
      extra: '#FAC4BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mellow Stone',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D26730',
      primaryShadow: '#A85226',
      accent: '#DE9A28',
      accentShadow: '#B27B20',
      background: '#EEE5E1',
      cardBackground: '#E4DCD8',
      text: '#202020',
      extra: '#202020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D97E4F',
      primaryShadow: '#D26730',
      accent: '#E3A948',
      accentShadow: '#DE9A28',
      background: '#202020',
      cardBackground: '#292929',
      text: '#E8D8A5',
      extra: '#363636',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble Courtyard',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#8C7F0B',
      primaryShadow: '#706609',
      accent: '#686700',
      accentShadow: '#535200',
      background: '#DBDBD0',
      cardBackground: '#D0D0C6',
      text: '#292225',
      extra: '#7A2D30',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9D9230',
      primaryShadow: '#8C7F0B',
      accent: '#7F7E26',
      accentShadow: '#686700',
      background: '#292225',
      cardBackground: '#322B2E',
      text: '#DBDBD0',
      extra: '#874245',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ephemeral Springs',
    tags: ['cool', 'curated'],
    light: {
      primary: '#F42E1F',
      primaryShadow: '#C32519',
      accent: '#2F256B',
      accentShadow: '#261E56',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#060608',
      extra: '#2F256B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F64D41',
      primaryShadow: '#F42E1F',
      accent: '#4E4681',
      accentShadow: '#2F256B',
      background: '#060608',
      cardBackground: '#101012',
      text: '#FFFFFF',
      extra: '#443B7A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Futuristic Studio',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FF26A8',
      primaryShadow: '#CC1E86',
      accent: '#CC1825',
      accentShadow: '#A3131E',
      background: '#FEFFE5',
      cardBackground: '#F1F2DA',
      text: '#120A1A',
      extra: '#2A2A41',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF47B5',
      primaryShadow: '#FF26A8',
      accent: '#D43B46',
      accentShadow: '#CC1825',
      background: '#120A1A',
      cardBackground: '#1B1423',
      text: '#FEFFE5',
      extra: '#3F3F54',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Veranda',
    tags: ['cool', 'curated'],
    light: {
      primary: '#0C6987',
      primaryShadow: '#0A546C',
      accent: '#2BA4A6',
      accentShadow: '#228385',
      background: '#DEE5E8',
      cardBackground: '#D5DCDF',
      text: '#0D2147',
      extra: '#54CEA7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#308099',
      primaryShadow: '#0C6987',
      accent: '#4BB2B3',
      accentShadow: '#2BA4A6',
      background: '#0D2147',
      cardBackground: '#172A4E',
      text: '#FFB0BF',
      extra: '#65D3B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Root',
    tags: ['curated'],
    light: {
      primary: '#CD894A',
      primaryShadow: '#A46E3B',
      accent: '#754232',
      accentShadow: '#5E3528',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#361C1B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D59B65',
      primaryShadow: '#CD894A',
      accent: '#8A5E51',
      accentShadow: '#754232',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#4A3332',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classic Bloom',
    tags: ['warm', 'curated'],
    light: {
      primary: '#EB7E17',
      primaryShadow: '#BC6512',
      accent: '#BD4B1E',
      accentShadow: '#973C18',
      background: '#FAEFE6',
      cardBackground: '#EEE3DB',
      text: '#101012',
      extra: '#C93426',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EE913A',
      primaryShadow: '#EB7E17',
      accent: '#C76640',
      accentShadow: '#BD4B1E',
      background: '#101012',
      cardBackground: '#1A1A1B',
      text: '#FAEFE6',
      extra: '#CE483C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Digital Magma',
    tags: ['curated'],
    light: {
      primary: '#A1563C',
      primaryShadow: '#814530',
      accent: '#C38252',
      accentShadow: '#9C6842',
      background: '#F7EFC7',
      cardBackground: '#EBE3BD',
      text: '#191F22',
      extra: '#BBB094',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AF6F59',
      primaryShadow: '#A1563C',
      accent: '#CC956C',
      accentShadow: '#C38252',
      background: '#191F22',
      cardBackground: '#22282B',
      text: '#F7EFC7',
      extra: '#C2B89F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blaze Cascade',
    tags: ['cool', 'curated'],
    light: {
      primary: '#5429C2',
      primaryShadow: '#43219B',
      accent: '#5463D9',
      accentShadow: '#434FAE',
      background: '#DDF8D0',
      cardBackground: '#D2ECC6',
      text: '#000000',
      extra: '#1F123A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6E49CB',
      primaryShadow: '#5429C2',
      accent: '#6E7ADF',
      accentShadow: '#5463D9',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#DDF8D0',
      extra: '#352A4E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Luminous Coast',
    tags: ['curated'],
    light: {
      primary: '#01CBCF',
      primaryShadow: '#01A2A6',
      accent: '#0188A5',
      accentShadow: '#016D84',
      background: '#FFFDFF',
      cardBackground: '#F2F0F2',
      text: '#352A55',
      extra: '#DF0772',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#27D3D6',
      primaryShadow: '#01CBCF',
      accent: '#279AB3',
      accentShadow: '#0188A5',
      background: '#080E12',
      cardBackground: '#0F1519',
      text: '#FFFDFF',
      extra: '#E22080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Heart',
    tags: ['warm', 'curated'],
    light: {
      primary: '#186F5E',
      primaryShadow: '#13594B',
      accent: '#46A45F',
      accentShadow: '#38834C',
      background: '#DFE6E4',
      cardBackground: '#D6DDDB',
      text: '#501733',
      extra: '#EFD0AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3B8576',
      primaryShadow: '#186F5E',
      accent: '#62B277',
      accentShadow: '#46A45F',
      background: '#090B0F',
      cardBackground: '#101216',
      text: '#EFD0AE',
      extra: '#F1D5B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Smoke Ridge',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#717FB0',
      primaryShadow: '#5A668D',
      accent: '#90B4DE',
      accentShadow: '#7390B2',
      background: '#E0E0DC',
      cardBackground: '#D5D5D1',
      text: '#1A1A1A',
      extra: '#615E85',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8692BC',
      primaryShadow: '#717FB0',
      accent: '#A1BFE3',
      accentShadow: '#90B4DE',
      background: '#0B0C11',
      cardBackground: '#121318',
      text: '#E0E0DC',
      extra: '#716E91',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Wave',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#A97E5C',
      primaryShadow: '#87654A',
      accent: '#CC9970',
      accentShadow: '#A37A5A',
      background: '#EEE1C4',
      cardBackground: '#E2D6BA',
      text: '#1A1A1A',
      extra: '#EEE1C4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B69174',
      primaryShadow: '#A97E5C',
      accent: '#D4A885',
      accentShadow: '#CC9970',
      background: '#0D0C0F',
      cardBackground: '#141316',
      text: '#EEE1C4',
      extra: '#F0E4CA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Modern Seed',
    tags: ['curated'],
    light: {
      primary: '#4D4D80',
      primaryShadow: '#3E3E66',
      accent: '#131626',
      accentShadow: '#0F121E',
      background: '#FFE6EA',
      cardBackground: '#F2DBDE',
      text: '#131626',
      extra: '#E6A1CF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#686893',
      primaryShadow: '#4D4D80',
      accent: '#363947',
      accentShadow: '#131626',
      background: '#131626',
      cardBackground: '#1C1F2F',
      text: '#FFE6EA',
      extra: '#E9AAD4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ivory Mirage',
    tags: ['curated'],
    light: {
      primary: '#0C7475',
      primaryShadow: '#0A5D5E',
      accent: '#BC4A9B',
      accentShadow: '#963B7C',
      background: '#FFD8BA',
      cardBackground: '#F2CDB1',
      text: '#3A2B3B',
      extra: '#3A2B3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30898A',
      primaryShadow: '#0C7475',
      accent: '#C665AA',
      accentShadow: '#BC4A9B',
      background: '#080B10',
      cardBackground: '#0F1217',
      text: '#FFD8BA',
      extra: '#4E404F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Soft Cloister',
    tags: ['curated'],
    light: {
      primary: '#732D72',
      primaryShadow: '#5C245B',
      accent: '#A75B80',
      accentShadow: '#864966',
      background: '#FFDDCB',
      cardBackground: '#F2D2C1',
      text: '#100B27',
      extra: '#C9DAB8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#884D87',
      primaryShadow: '#732D72',
      accent: '#B47493',
      accentShadow: '#A75B80',
      background: '#100B27',
      cardBackground: '#1A1530',
      text: '#FFDDCB',
      extra: '#CEDEBF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cool Stone',
    tags: ['cool', 'curated'],
    light: {
      primary: '#8977C9',
      primaryShadow: '#6E5FA1',
      accent: '#BC8DFF',
      accentShadow: '#9671CC',
      background: '#D4FAFF',
      cardBackground: '#C9EEF2',
      text: '#332B48',
      extra: '#332B48',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9B8BD1',
      primaryShadow: '#8977C9',
      accent: '#C69EFF',
      accentShadow: '#BC8DFF',
      background: '#0C0C12',
      cardBackground: '#131319',
      text: '#D4FAFF',
      extra: '#47405A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Soft Clearing',
    tags: ['warm', 'curated'],
    light: {
      primary: '#917A46',
      primaryShadow: '#746238',
      accent: '#99E550',
      accentShadow: '#7AB740',
      background: '#E8E7E2',
      cardBackground: '#DFDED9',
      text: '#323C39',
      extra: '#323C39',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A28E62',
      primaryShadow: '#917A46',
      accent: '#A8E96A',
      accentShadow: '#99E550',
      background: '#0C0C0E',
      cardBackground: '#131315',
      text: '#99E550',
      extra: '#47504D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Orchard',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#CF7862',
      primaryShadow: '#A6604E',
      accent: '#A75252',
      accentShadow: '#864242',
      background: '#EDE6E5',
      cardBackground: '#E4DDDC',
      text: '#1A080E',
      extra: '#3D203B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D68C7A',
      primaryShadow: '#CF7862',
      accent: '#B46C6C',
      accentShadow: '#A75252',
      background: '#1A080E',
      cardBackground: '#231218',
      text: '#FFCE9C',
      extra: '#50364F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ephemeral Mind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D03C32',
      primaryShadow: '#A63028',
      accent: '#FBA64C',
      accentShadow: '#C9853D',
      background: '#EDE2E1',
      cardBackground: '#E4D9D8',
      text: '#17090C',
      extra: '#DCD1B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D75951',
      primaryShadow: '#D03C32',
      accent: '#FCB367',
      accentShadow: '#FBA64C',
      background: '#17090C',
      cardBackground: '#201316',
      text: '#DCD1B8',
      extra: '#E0D6BF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teal Jungle',
    tags: ['dark', 'curated'],
    light: {
      primary: '#F4680B',
      primaryShadow: '#C35309',
      accent: '#118337',
      accentShadow: '#0E692C',
      background: '#FFFDF0',
      cardBackground: '#F2F0E4',
      text: '#0E092F',
      extra: '#361027',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F67F30',
      primaryShadow: '#F4680B',
      accent: '#359655',
      accentShadow: '#118337',
      background: '#0E092F',
      cardBackground: '#181337',
      text: '#FFFDF0',
      extra: '#4A283D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cotton Fresco',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#38405D',
      primaryShadow: '#2D334A',
      accent: '#181923',
      accentShadow: '#13141C',
      background: '#E1E2E4',
      cardBackground: '#D8D9DB',
      text: '#181923',
      extra: '#292D41',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#565D75',
      primaryShadow: '#38405D',
      accent: '#3B3C44',
      accentShadow: '#181923',
      background: '#181923',
      cardBackground: '#21222C',
      text: '#F0F0F0',
      extra: '#3E4254',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Cove',
    tags: ['cool', 'curated'],
    light: {
      primary: '#98585B',
      primaryShadow: '#7A4649',
      accent: '#6F3C50',
      accentShadow: '#593040',
      background: '#E9E4E4',
      cardBackground: '#E0DBDB',
      text: '#2D222E',
      extra: '#BCAFB6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A77174',
      primaryShadow: '#98585B',
      accent: '#85596A',
      accentShadow: '#6F3C50',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#BCAFB6',
      extra: '#C3B7BD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Phosphor Stream',
    tags: ['cool', 'curated'],
    light: {
      primary: '#3F6D9E',
      primaryShadow: '#32577E',
      accent: '#151640',
      accentShadow: '#111233',
      background: '#E6F2EF',
      cardBackground: '#DBE6E3',
      text: '#151640',
      extra: '#F783B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5C83AD',
      primaryShadow: '#3F6D9E',
      accent: '#38395D',
      accentShadow: '#151640',
      background: '#151640',
      cardBackground: '#1E1F48',
      text: '#E6F2EF',
      extra: '#F88FB8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Fire',
    tags: ['cool', 'curated'],
    light: {
      primary: '#7E7E00',
      primaryShadow: '#656500',
      accent: '#06FF04',
      accentShadow: '#05CC03',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#7E7E7E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#919126',
      primaryShadow: '#7E7E00',
      accent: '#2BFF2A',
      accentShadow: '#06FF04',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#8B8B8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ethereal Peak',
    tags: ['curated'],
    light: {
      primary: '#A73169',
      primaryShadow: '#862754',
      accent: '#D9626B',
      accentShadow: '#AE4E56',
      background: '#FFEB99',
      cardBackground: '#F2DF91',
      text: '#201127',
      extra: '#201433',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B45080',
      primaryShadow: '#A73169',
      accent: '#DF7A81',
      accentShadow: '#D9626B',
      background: '#201127',
      cardBackground: '#291B30',
      text: '#FFEB99',
      extra: '#362C47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spark Ridge',
    tags: ['warm', 'curated'],
    light: {
      primary: '#1B958D',
      primaryShadow: '#167771',
      accent: '#105390',
      accentShadow: '#0D4273',
      background: '#DFE9E8',
      cardBackground: '#D6E0DF',
      text: '#381631',
      extra: '#381631',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3DA59E',
      primaryShadow: '#1B958D',
      accent: '#346DA1',
      accentShadow: '#105390',
      background: '#381631',
      cardBackground: '#401F39',
      text: '#D8DCB4',
      extra: '#4C2D46',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Twilight Orchard',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AF4B3B',
      primaryShadow: '#8C3C2F',
      accent: '#C18549',
      accentShadow: '#9A6A3A',
      background: '#EBE3E2',
      cardBackground: '#E2DAD9',
      text: '#1A1417',
      extra: '#67413A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BB6658',
      primaryShadow: '#AF4B3B',
      accent: '#CA9764',
      accentShadow: '#C18549',
      background: '#1A1417',
      cardBackground: '#231D20',
      text: '#D9CBAE',
      extra: '#76544E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ivory Study',
    tags: ['cool', 'curated'],
    light: {
      primary: '#6D5412',
      primaryShadow: '#57430E',
      accent: '#A1683C',
      accentShadow: '#815330',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#626262',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#836E36',
      primaryShadow: '#6D5412',
      accent: '#AF7F59',
      accentShadow: '#A1683C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#727272',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Magenta Lake',
    tags: ['curated'],
    light: {
      primary: '#D85F28',
      primaryShadow: '#AD4C20',
      accent: '#F08E39',
      accentShadow: '#C0722E',
      background: '#E8FAB6',
      cardBackground: '#DCEEAD',
      text: '#121532',
      extra: '#141B27',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE7748',
      primaryShadow: '#D85F28',
      accent: '#F29F57',
      accentShadow: '#F08E39',
      background: '#121532',
      cardBackground: '#1B1E3A',
      text: '#E8FAB6',
      extra: '#2C323D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Valley',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FF327C',
      primaryShadow: '#CC2863',
      accent: '#B41360',
      accentShadow: '#900F4D',
      background: '#FFDAE8',
      cardBackground: '#F2CFDC',
      text: '#480A30',
      extra: '#480A30',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5190',
      primaryShadow: '#FF327C',
      accent: '#BF3678',
      accentShadow: '#B41360',
      background: '#480A30',
      cardBackground: '#4F1438',
      text: '#FFDAE8',
      extra: '#5A2345',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eternal Seed',
    tags: ['dark', 'curated'],
    light: {
      primary: '#BB474F',
      primaryShadow: '#96393F',
      accent: '#713F52',
      accentShadow: '#5A3242',
      background: '#ECE2E3',
      cardBackground: '#E3D9DA',
      text: '#392B35',
      extra: '#D1BFB0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C56369',
      primaryShadow: '#BB474F',
      accent: '#865C6C',
      accentShadow: '#713F52',
      background: '#0E0A0E',
      cardBackground: '#151115',
      text: '#D1BFB0',
      extra: '#D6C5B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Snowfall',
    tags: ['curated'],
    light: {
      primary: '#0BA6DA',
      primaryShadow: '#0985AE',
      accent: '#0081CC',
      accentShadow: '#0067A3',
      background: '#E4EAF6',
      cardBackground: '#D9DEEA',
      text: '#080F1C',
      extra: '#392532',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30B3E0',
      primaryShadow: '#0BA6DA',
      accent: '#2694D4',
      accentShadow: '#0081CC',
      background: '#080F1C',
      cardBackground: '#121925',
      text: '#E4EAF6',
      extra: '#4D3B47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Sky',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C33846',
      primaryShadow: '#9C2D38',
      accent: '#320011',
      accentShadow: '#28000E',
      background: '#ECE8C2',
      cardBackground: '#E0DCB8',
      text: '#320011',
      extra: '#5F3A60',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CC5662',
      primaryShadow: '#C33846',
      accent: '#512635',
      accentShadow: '#320011',
      background: '#320011',
      cardBackground: '#3A0A1B',
      text: '#ECE8C2',
      extra: '#6F4E70',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Comet',
    tags: ['cool', 'curated'],
    light: {
      primary: '#52A5DE',
      primaryShadow: '#4284B2',
      accent: '#18284A',
      accentShadow: '#13203B',
      background: '#EBF9FF',
      cardBackground: '#DFEDF2',
      text: '#070810',
      extra: '#ACD6F6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6CB3E3',
      primaryShadow: '#52A5DE',
      accent: '#3B4865',
      accentShadow: '#18284A',
      background: '#070810',
      cardBackground: '#11121A',
      text: '#EBF9FF',
      extra: '#B4DAF7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dynamic Dunes',
    tags: ['curated'],
    light: {
      primary: '#002C38',
      primaryShadow: '#00232D',
      accent: '#6C4C44',
      accentShadow: '#563D36',
      background: '#FCFCFC',
      cardBackground: '#EFEFEF',
      text: '#000000',
      extra: '#143444',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#264C56',
      primaryShadow: '#002C38',
      accent: '#826760',
      accentShadow: '#6C4C44',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FCFCFC',
      extra: '#2C4857',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Grove',
    tags: ['cool', 'curated'],
    light: {
      primary: '#0A777A',
      primaryShadow: '#085F62',
      accent: '#4AA881',
      accentShadow: '#3B8667',
      background: '#ECF3B0',
      cardBackground: '#E0E7A7',
      text: '#313638',
      extra: '#313638',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2F8B8E',
      primaryShadow: '#0A777A',
      accent: '#65B594',
      accentShadow: '#4AA881',
      background: '#080C10',
      cardBackground: '#0F1317',
      text: '#ECF3B0',
      extra: '#464A4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Timeless Desert',
    tags: ['warm', 'curated'],
    light: {
      primary: '#692F11',
      primaryShadow: '#54260E',
      accent: '#89542F',
      accentShadow: '#6E4326',
      background: '#ECDDBA',
      cardBackground: '#E0D2B1',
      text: '#0F2C2E',
      extra: '#64988E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#804E35',
      primaryShadow: '#692F11',
      accent: '#9B6E4E',
      accentShadow: '#89542F',
      background: '#0F2C2E',
      cardBackground: '#193436',
      text: '#ECDDBA',
      extra: '#74A299',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rust Floe',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#2684CE',
      primaryShadow: '#1E6AA5',
      accent: '#0A4CA0',
      accentShadow: '#083D80',
      background: '#E0E7ED',
      cardBackground: '#D7DEE4',
      text: '#030206',
      extra: '#091E4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4796D5',
      primaryShadow: '#2684CE',
      accent: '#2F67AE',
      accentShadow: '#0A4CA0',
      background: '#030206',
      cardBackground: '#0D0C10',
      text: '#3AFFFB',
      extra: '#22355F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Dusk',
    tags: ['warm', 'curated'],
    light: {
      primary: '#67584C',
      primaryShadow: '#52463D',
      accent: '#4A393D',
      accentShadow: '#3B2E31',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#28211F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7E7167',
      primaryShadow: '#67584C',
      accent: '#65575A',
      accentShadow: '#4A393D',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#3E3735',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oceanic Vine',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CF6A4F',
      primaryShadow: '#A6553F',
      accent: '#E0B94A',
      accentShadow: '#B3943B',
      background: '#EDE5E3',
      cardBackground: '#E4DCDA',
      text: '#25272A',
      extra: '#DDA963',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D68069',
      primaryShadow: '#CF6A4F',
      accent: '#E5C465',
      accentShadow: '#E0B94A',
      background: '#0E0B0E',
      cardBackground: '#151215',
      text: '#DBC1AF',
      extra: '#E0B273',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Dusk',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D27D2C',
      primaryShadow: '#A86423',
      accent: '#D04648',
      accentShadow: '#A6383A',
      background: '#DEEED6',
      cardBackground: '#D3E2CB',
      text: '#140C1C',
      extra: '#442434',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D9914C',
      primaryShadow: '#D27D2C',
      accent: '#D76263',
      accentShadow: '#D04648',
      background: '#140C1C',
      cardBackground: '#1D1625',
      text: '#DEEED6',
      extra: '#573A48',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Phosphor Atrium',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FF8A27',
      primaryShadow: '#CC6E1F',
      accent: '#3D94C0',
      accentShadow: '#31769A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1A1A1A',
      extra: '#E8D5AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9C47',
      primaryShadow: '#FF8A27',
      accent: '#5AA4C9',
      accentShadow: '#3D94C0',
      background: '#100C0D',
      cardBackground: '#171314',
      text: '#FFFFFF',
      extra: '#EAD9B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Summer Tapestry',
    tags: ['cool', 'curated'],
    light: {
      primary: '#228399',
      primaryShadow: '#1B697A',
      accent: '#31B0B0',
      accentShadow: '#278D8D',
      background: '#D9FFE2',
      cardBackground: '#CEF2D7',
      text: '#000924',
      extra: '#093659',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4396A8',
      primaryShadow: '#228399',
      accent: '#50BCBC',
      accentShadow: '#31B0B0',
      background: '#000924',
      cardBackground: '#0A132D',
      text: '#D9FFE2',
      extra: '#224A6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classic Arcade',
    tags: ['curated'],
    light: {
      primary: '#426E5D',
      primaryShadow: '#35584A',
      accent: '#E5B083',
      accentShadow: '#B78D69',
      background: '#FBF7F3',
      cardBackground: '#EEEBE7',
      text: '#20283D',
      extra: '#20283D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E8475',
      primaryShadow: '#426E5D',
      accent: '#E9BC96',
      accentShadow: '#E5B083',
      background: '#0A0B0F',
      cardBackground: '#111216',
      text: '#FBF7F3',
      extra: '#363E50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dynamic Tide',
    tags: ['curated'],
    light: {
      primary: '#2485A6',
      primaryShadow: '#1D6A85',
      accent: '#A1BF41',
      accentShadow: '#819934',
      background: '#EBECDC',
      cardBackground: '#DFE0D1',
      text: '#232228',
      extra: '#232228',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4597B3',
      primaryShadow: '#2485A6',
      accent: '#AFC95E',
      accentShadow: '#A1BF41',
      background: '#232228',
      cardBackground: '#2C2B31',
      text: '#EBECDC',
      extra: '#39383E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Aurora',
    tags: ['curated'],
    light: {
      primary: '#00BC9F',
      primaryShadow: '#00967F',
      accent: '#97530F',
      accentShadow: '#79420C',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#2D1A71',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C6AD',
      primaryShadow: '#00BC9F',
      accent: '#A76D33',
      accentShadow: '#97530F',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#42317F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Synthetic Plateau',
    tags: ['curated'],
    light: {
      primary: '#C65550',
      primaryShadow: '#9E4440',
      accent: '#8A2E3F',
      accentShadow: '#6E2532',
      background: '#EDE4E3',
      cardBackground: '#E4DBDA',
      text: '#2A202A',
      extra: '#A7776B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF6F6A',
      primaryShadow: '#C65550',
      accent: '#9C4D5C',
      accentShadow: '#8A2E3F',
      background: '#2A202A',
      cardBackground: '#332933',
      text: '#D9D3D9',
      extra: '#B0857A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Iron Coast',
    tags: ['warm', 'curated'],
    light: {
      primary: '#00E436',
      primaryShadow: '#00B62B',
      accent: '#00B543',
      accentShadow: '#009136',
      background: '#FFF1E8',
      cardBackground: '#F2E5DC',
      text: '#000000',
      extra: '#1D2B53',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26E854',
      primaryShadow: '#00E436',
      accent: '#26C05F',
      accentShadow: '#00B543',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFF1E8',
      extra: '#344064',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Courtyard',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A87C25',
      primaryShadow: '#86631E',
      accent: '#D1952E',
      accentShadow: '#A77725',
      background: '#FFE6D1',
      cardBackground: '#F2DBC7',
      text: '#1C080C',
      extra: '#C28EAD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B59046',
      primaryShadow: '#A87C25',
      accent: '#D8A54D',
      accentShadow: '#D1952E',
      background: '#1C080C',
      cardBackground: '#251216',
      text: '#FFE6D1',
      extra: '#C899B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Smoke Mesa',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#AB3B1E',
      primaryShadow: '#892F18',
      accent: '#552804',
      accentShadow: '#442003',
      background: '#EAE2DF',
      cardBackground: '#E1D9D6',
      text: '#0A0D11',
      extra: '#B1743D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B85840',
      primaryShadow: '#AB3B1E',
      accent: '#6F482A',
      accentShadow: '#552804',
      background: '#0A0D11',
      cardBackground: '#14171B',
      text: '#F0F0F0',
      extra: '#B98250',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glossy Grove',
    tags: ['cool', 'curated'],
    light: {
      primary: '#3875A1',
      primaryShadow: '#2D5E81',
      accent: '#2C4A78',
      accentShadow: '#233B60',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#20284E',
      extra: '#20284E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#568AAF',
      primaryShadow: '#3875A1',
      accent: '#4C658C',
      accentShadow: '#2C4A78',
      background: '#0A0C11',
      cardBackground: '#111318',
      text: '#FFFFFF',
      extra: '#363E60',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fertile Waterfall',
    tags: ['cool', 'curated'],
    light: {
      primary: '#07687B',
      primaryShadow: '#065362',
      accent: '#378DAE',
      accentShadow: '#2C718B',
      background: '#E8F8D7',
      cardBackground: '#DCECCC',
      text: '#00245C',
      extra: '#5DB8CA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2C7F8F',
      primaryShadow: '#07687B',
      accent: '#559EBA',
      accentShadow: '#378DAE',
      background: '#00245C',
      cardBackground: '#0A2D63',
      text: '#E8F8D7',
      extra: '#6DBFCF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amber Dusk',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DB604C',
      primaryShadow: '#AF4D3D',
      accent: '#B13353',
      accentShadow: '#8E2942',
      background: '#F6DBBA',
      cardBackground: '#EAD0B1',
      text: '#1A1016',
      extra: '#F6DBBA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E07867',
      primaryShadow: '#DB604C',
      accent: '#BD526D',
      accentShadow: '#B13353',
      background: '#1A1016',
      cardBackground: '#231A1F',
      text: '#F6DBBA',
      extra: '#F7DFC1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bright Garden',
    tags: ['dark', 'curated'],
    light: {
      primary: '#D32836',
      primaryShadow: '#A9202B',
      accent: '#871C3E',
      accentShadow: '#6C1632',
      background: '#EEE0E1',
      cardBackground: '#E4D7D8',
      text: '#0A202F',
      extra: '#0A202F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA4854',
      primaryShadow: '#D32836',
      accent: '#993E5B',
      accentShadow: '#871C3E',
      background: '#0A202F',
      cardBackground: '#142937',
      text: '#F0F0F0',
      extra: '#233644',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jade Shade',
    tags: ['cool', 'curated'],
    light: {
      primary: '#3FAC95',
      primaryShadow: '#328A77',
      accent: '#446176',
      accentShadow: '#364E5E',
      background: '#E2EBE9',
      cardBackground: '#D9E2E0',
      text: '#2C2137',
      extra: '#2C2137',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5CB8A5',
      primaryShadow: '#3FAC95',
      accent: '#60798B',
      accentShadow: '#446176',
      background: '#0A0D10',
      cardBackground: '#111417',
      text: '#A1EF8C',
      extra: '#41374B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Radiant Obelisk',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#A67935',
      primaryShadow: '#85612A',
      accent: '#A8A743',
      accentShadow: '#868636',
      background: '#EAE6E1',
      cardBackground: '#E1DDD8',
      text: '#171015',
      extra: '#332628',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B38D53',
      primaryShadow: '#A67935',
      accent: '#B5B45F',
      accentShadow: '#A8A743',
      background: '#171015',
      cardBackground: '#201A1E',
      text: '#F0F0F0',
      extra: '#473C3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glossy Savanna',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#AD2020',
      primaryShadow: '#8A1A1A',
      accent: '#560909',
      accentShadow: '#450707',
      background: '#F2E6E6',
      cardBackground: '#E6DBDB',
      text: '#190000',
      extra: '#AD2020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B94141',
      primaryShadow: '#AD2020',
      accent: '#6F2E2E',
      accentShadow: '#560909',
      background: '#190000',
      cardBackground: '#220A0A',
      text: '#F2E6E6',
      extra: '#B53636',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Western Vineyard',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B8560F',
      primaryShadow: '#93450C',
      accent: '#DC9824',
      accentShadow: '#B07A1D',
      background: '#EDEFE2',
      cardBackground: '#E1E3D7',
      text: '#1D1B24',
      extra: '#2C4941',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C36F33',
      primaryShadow: '#B8560F',
      accent: '#E1A745',
      accentShadow: '#DC9824',
      background: '#1D1B24',
      cardBackground: '#26242D',
      text: '#EDEFE2',
      extra: '#415B54',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Basin',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C23A73',
      primaryShadow: '#9B2E5C',
      accent: '#2C1E74',
      accentShadow: '#23185D',
      background: '#ECE1E6',
      cardBackground: '#E3D8DD',
      text: '#2C1E74',
      extra: '#DAD3AF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CB5888',
      primaryShadow: '#C23A73',
      accent: '#4C4089',
      accentShadow: '#2C1E74',
      background: '#0E0A0F',
      cardBackground: '#151116',
      text: '#DAD3AF',
      extra: '#DED7B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Hue',
    tags: ['curated'],
    light: {
      primary: '#B07972',
      primaryShadow: '#8D615B',
      accent: '#90B870',
      accentShadow: '#73935A',
      background: '#EBE6E6',
      cardBackground: '#E2DDDD',
      text: '#524352',
      extra: '#D9AF80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BC8D87',
      primaryShadow: '#B07972',
      accent: '#A1C385',
      accentShadow: '#90B870',
      background: '#0D0C0F',
      cardBackground: '#141316',
      text: '#BFD4B0',
      extra: '#DDB78D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Peak',
    tags: ['curated'],
    light: {
      primary: '#2C2851',
      primaryShadow: '#232041',
      accent: '#585783',
      accentShadow: '#464669',
      background: '#ECE6DF',
      cardBackground: '#E0DBD4',
      text: '#2C2851',
      extra: '#CEBDB0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4C486B',
      primaryShadow: '#2C2851',
      accent: '#717096',
      accentShadow: '#585783',
      background: '#09090E',
      cardBackground: '#101015',
      text: '#ECE6DF',
      extra: '#D3C4B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Coast',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FFA01B',
      primaryShadow: '#CC8016',
      accent: '#E64667',
      accentShadow: '#B83852',
      background: '#F2EEF1',
      cardBackground: '#E6E2E5',
      text: '#2C0F30',
      extra: '#FFA7BF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFAE3D',
      primaryShadow: '#FFA01B',
      accent: '#EA627E',
      accentShadow: '#E64667',
      background: '#2C0F30',
      cardBackground: '#341938',
      text: '#F2EEF1',
      extra: '#FFB0C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rust Lava',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#E96F1B',
      primaryShadow: '#BA5916',
      accent: '#3C1402',
      accentShadow: '#301002',
      background: '#EFE6DF',
      cardBackground: '#E5DDD6',
      text: '#3C1402',
      extra: '#CA9A57',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC853D',
      primaryShadow: '#E96F1B',
      accent: '#593728',
      accentShadow: '#3C1402',
      background: '#3C1402',
      cardBackground: '#441D0C',
      text: '#F0F0F0',
      extra: '#CFA468',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Summit',
    tags: ['curated'],
    light: {
      primary: '#D45C1D',
      primaryShadow: '#AA4A17',
      accent: '#E38524',
      accentShadow: '#B66A1D',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#09090E',
      extra: '#1A1C26',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA743F',
      primaryShadow: '#D45C1D',
      accent: '#E79745',
      accentShadow: '#E38524',
      background: '#09090E',
      cardBackground: '#131318',
      text: '#FFFFFF',
      extra: '#31333C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Satin Bastion',
    tags: ['curated'],
    light: {
      primary: '#08B23B',
      primaryShadow: '#068E2F',
      accent: '#ECAB11',
      accentShadow: '#BD890E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#353658',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2DBE58',
      primaryShadow: '#08B23B',
      accent: '#EFB835',
      accentShadow: '#ECAB11',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#494A69',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Ridge',
    tags: ['curated'],
    light: {
      primary: '#13B2F2',
      primaryShadow: '#0F8EC2',
      accent: '#0E82CE',
      accentShadow: '#0B68A5',
      background: '#DBE0E7',
      cardBackground: '#D0D5DB',
      text: '#141020',
      extra: '#DBE0E7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#36BEF4',
      primaryShadow: '#13B2F2',
      accent: '#3295D5',
      accentShadow: '#0E82CE',
      background: '#141020',
      cardBackground: '#1D1A29',
      text: '#DBE0E7',
      extra: '#DFE3E9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Dawn',
    tags: ['cool', 'curated'],
    light: {
      primary: '#778299',
      primaryShadow: '#5F687A',
      accent: '#251D29',
      accentShadow: '#1E1721',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#251D29',
      extra: '#433D4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8B95A8',
      primaryShadow: '#778299',
      accent: '#463F49',
      accentShadow: '#251D29',
      background: '#251D29',
      cardBackground: '#2E2632',
      text: '#FFFFFF',
      extra: '#56505E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spark Wind',
    tags: ['cool', 'curated'],
    light: {
      primary: '#6A9395',
      primaryShadow: '#557677',
      accent: '#545E72',
      accentShadow: '#434B5B',
      background: '#E5E9E9',
      cardBackground: '#DCE0E0',
      text: '#372F3A',
      extra: '#372F3A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#80A3A5',
      primaryShadow: '#6A9395',
      accent: '#6E7687',
      accentShadow: '#545E72',
      background: '#0B0C10',
      cardBackground: '#121317',
      text: '#D3D8D9',
      extra: '#4B444E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Minimalist Vine',
    tags: ['warm', 'curated'],
    light: {
      primary: '#F95142',
      primaryShadow: '#C74135',
      accent: '#FF8F46',
      accentShadow: '#CC7238',
      background: '#F3EDE3',
      cardBackground: '#E7E1D8',
      text: '#1E1610',
      extra: '#D3AD8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FA6B5E',
      primaryShadow: '#F95142',
      accent: '#FFA062',
      accentShadow: '#FF8F46',
      background: '#1E1610',
      cardBackground: '#271F1A',
      text: '#F3EDE3',
      extra: '#D7B597',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Horizon',
    tags: ['bright', 'curated'],
    light: {
      primary: '#3EAAAE',
      primaryShadow: '#32888B',
      accent: '#FF7A8F',
      accentShadow: '#CC6272',
      background: '#FFFEE4',
      cardBackground: '#F2F1D9',
      text: '#1A1A1A',
      extra: '#446176',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5BB7BA',
      primaryShadow: '#3EAAAE',
      accent: '#FF8EA0',
      accentShadow: '#FF7A8F',
      background: '#0A0D11',
      cardBackground: '#111418',
      text: '#FFFEE4',
      extra: '#577184',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teal Meadow',
    tags: ['curated'],
    light: {
      primary: '#FF004D',
      primaryShadow: '#CC003E',
      accent: '#00FFCC',
      accentShadow: '#00CCA3',
      background: '#FFF1E8',
      cardBackground: '#F2E5DC',
      text: '#000000',
      extra: '#430067',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF2668',
      primaryShadow: '#FF004D',
      accent: '#26FFD4',
      accentShadow: '#00FFCC',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFF1E8',
      extra: '#561A76',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Tide',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DC6A36',
      primaryShadow: '#B0552B',
      accent: '#1DE9DE',
      accentShadow: '#17BAB2',
      background: '#ECEBE7',
      cardBackground: '#E0DFDB',
      text: '#1A0A01',
      extra: '#CBC6C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E18054',
      primaryShadow: '#DC6A36',
      accent: '#3FECE3',
      accentShadow: '#1DE9DE',
      background: '#1A0A01',
      cardBackground: '#23140B',
      text: '#ECEBE7',
      extra: '#D0CCC7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Seed',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B25E46',
      primaryShadow: '#8E4B38',
      accent: '#933F45',
      accentShadow: '#763237',
      background: '#F0E9C9',
      cardBackground: '#E4DDBF',
      text: '#352B40',
      extra: '#352B40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BE7662',
      primaryShadow: '#B25E46',
      accent: '#A35C61',
      accentShadow: '#933F45',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#F0E9C9',
      extra: '#494053',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen Promenade',
    tags: ['bright', 'curated'],
    light: {
      primary: '#97ABD4',
      primaryShadow: '#7989AA',
      accent: '#7D7390',
      accentShadow: '#645C73',
      background: '#FFF7F0',
      cardBackground: '#F2EBE4',
      text: '#1A1A1A',
      extra: '#C09DC3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A7B8DA',
      primaryShadow: '#97ABD4',
      accent: '#9188A1',
      accentShadow: '#7D7390',
      background: '#0D0D12',
      cardBackground: '#141419',
      text: '#FFF7F0',
      extra: '#C6A7C9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Desert Shade',
    tags: ['warm', 'curated'],
    light: {
      primary: '#F1641F',
      primaryShadow: '#C15019',
      accent: '#B9451D',
      accentShadow: '#943717',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#050403',
      extra: '#2D1B1E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F37B41',
      primaryShadow: '#F1641F',
      accent: '#C4613F',
      accentShadow: '#B9451D',
      background: '#050403',
      cardBackground: '#0F0E0D',
      text: '#FFFFFF',
      extra: '#423235',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy Turret',
    tags: ['cool', 'curated'],
    light: {
      primary: '#AB4646',
      primaryShadow: '#893838',
      accent: '#8F9BF6',
      accentShadow: '#727CC5',
      background: '#F0F0F0',
      cardBackground: '#E4E4E4',
      text: '#161616',
      extra: '#8F9BF6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B86262',
      primaryShadow: '#AB4646',
      accent: '#A0AAF7',
      accentShadow: '#8F9BF6',
      background: '#161616',
      cardBackground: '#1F1F1F',
      text: '#F0F0F0',
      extra: '#9AA5F7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Meadow',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B0764F',
      primaryShadow: '#8D5E3F',
      accent: '#BAAC81',
      accentShadow: '#958A67',
      background: '#EBE6E3',
      cardBackground: '#E2DDDA',
      text: '#47323B',
      extra: '#47323B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BC8B69',
      primaryShadow: '#B0764F',
      accent: '#C4B894',
      accentShadow: '#BAAC81',
      background: '#0D0C0E',
      cardBackground: '#141315',
      text: '#E9D79D',
      extra: '#59474F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Field',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DE523E',
      primaryShadow: '#B24232',
      accent: '#AD2F45',
      accentShadow: '#8A2637',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1A1516',
      extra: '#21181B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E36C5B',
      primaryShadow: '#DE523E',
      accent: '#B94E61',
      accentShadow: '#AD2F45',
      background: '#1A1516',
      cardBackground: '#231E1F',
      text: '#FFFFFF',
      extra: '#372F32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fierce Vent',
    tags: ['curated'],
    light: {
      primary: '#D0763E',
      primaryShadow: '#A65E32',
      accent: '#C74446',
      accentShadow: '#9F3638',
      background: '#ECE6DF',
      cardBackground: '#E0DBD4',
      text: '#2A1C31',
      extra: '#4D2235',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D78B5B',
      primaryShadow: '#D0763E',
      accent: '#CF6062',
      accentShadow: '#C74446',
      background: '#2A1C31',
      cardBackground: '#332539',
      text: '#ECE6DF',
      extra: '#5F3849',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jade Bay',
    tags: ['warm', 'curated'],
    light: {
      primary: '#F05B5B',
      primaryShadow: '#C04949',
      accent: '#8F325F',
      accentShadow: '#72284C',
      background: '#F5EEB0',
      cardBackground: '#E9E2A7',
      text: '#452B3F',
      extra: '#FABF61',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F27474',
      primaryShadow: '#F05B5B',
      accent: '#A05177',
      accentShadow: '#8F325F',
      background: '#0F0B0F',
      cardBackground: '#161216',
      text: '#F5EEB0',
      extra: '#FBC571',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Copse',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E16D50',
      primaryShadow: '#B45740',
      accent: '#E8B85C',
      accentShadow: '#BA934A',
      background: '#F2E9D3',
      cardBackground: '#E6DDC8',
      text: '#3F3645',
      extra: '#BAAB99',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E6836A',
      primaryShadow: '#E16D50',
      accent: '#EBC374',
      accentShadow: '#E8B85C',
      background: '#0F0B0E',
      cardBackground: '#161215',
      text: '#F2E9D3',
      extra: '#C1B3A3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classic Cove',
    tags: ['warm', 'curated'],
    light: {
      primary: '#8B6A44',
      primaryShadow: '#6F5536',
      accent: '#B4C788',
      accentShadow: '#909F6D',
      background: '#F7FFC5',
      cardBackground: '#EBF2BB',
      text: '#312629',
      extra: '#8A8969',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C8060',
      primaryShadow: '#8B6A44',
      accent: '#BFCF9A',
      accentShadow: '#B4C788',
      background: '#0C0B0E',
      cardBackground: '#131215',
      text: '#F7FFC5',
      extra: '#969578',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lunar Prism',
    tags: ['curated'],
    light: {
      primary: '#A1234D',
      primaryShadow: '#811C3E',
      accent: '#540E46',
      accentShadow: '#430B38',
      background: '#F2FFFF',
      cardBackground: '#E6F2F2',
      text: '#120612',
      extra: '#821653',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AF4468',
      primaryShadow: '#A1234D',
      accent: '#6E3262',
      accentShadow: '#540E46',
      background: '#120612',
      cardBackground: '#1B101B',
      text: '#F2FFFF',
      extra: '#8F2D64',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Field',
    tags: ['bright', 'curated'],
    light: {
      primary: '#00BCAA',
      primaryShadow: '#009688',
      accent: '#2C6F99',
      accentShadow: '#23597A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1A1A1A',
      extra: '#53437F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C6B7',
      primaryShadow: '#00BCAA',
      accent: '#4C85A8',
      accentShadow: '#2C6F99',
      background: '#080E11',
      cardBackground: '#0F1518',
      text: '#FFFFFF',
      extra: '#64568C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Waterfall',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#F0B695',
      primaryShadow: '#C09277',
      accent: '#877286',
      accentShadow: '#6C5B6B',
      background: '#E9F5DA',
      cardBackground: '#DDE9CF',
      text: '#3E3A42',
      extra: '#3E3A42',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F2C1A5',
      primaryShadow: '#F0B695',
      accent: '#998798',
      accentShadow: '#877286',
      background: '#0F0D10',
      cardBackground: '#161417',
      text: '#E9F5DA',
      extra: '#514E55',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Bloom',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#A13F2F',
      primaryShadow: '#813226',
      accent: '#BF5E4D',
      accentShadow: '#994B3E',
      background: '#EAE2E1',
      cardBackground: '#E1D9D8',
      text: '#160D10',
      extra: '#503146',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AF5C4E',
      primaryShadow: '#A13F2F',
      accent: '#C97668',
      accentShadow: '#BF5E4D',
      background: '#160D10',
      cardBackground: '#1F171A',
      text: '#F0F0F0',
      extra: '#624659',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Hotspring',
    tags: ['warm', 'curated'],
    light: {
      primary: '#856D52',
      primaryShadow: '#6A5742',
      accent: '#95C798',
      accentShadow: '#779F7A',
      background: '#FBFFE0',
      cardBackground: '#EEF2D5',
      text: '#40332F',
      extra: '#40332F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#97836C',
      primaryShadow: '#856D52',
      accent: '#A5CFA7',
      accentShadow: '#95C798',
      background: '#0C0B0E',
      cardBackground: '#131215',
      text: '#FBFFE0',
      extra: '#534744',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Western Oasis',
    tags: ['curated'],
    light: {
      primary: '#14A0CD',
      primaryShadow: '#1080A4',
      accent: '#1469C3',
      accentShadow: '#10549C',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000005',
      extra: '#C8E1EB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#37AED5',
      primaryShadow: '#14A0CD',
      accent: '#3780CC',
      accentShadow: '#1469C3',
      background: '#000005',
      cardBackground: '#0A0A0F',
      text: '#FFFFFF',
      extra: '#CEE4ED',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Turret',
    tags: ['warm', 'curated'],
    light: {
      primary: '#260D1C',
      primaryShadow: '#1E0A16',
      accent: '#4F3A54',
      accentShadow: '#3F2E43',
      background: '#E4DBBA',
      cardBackground: '#D9D0B1',
      text: '#260D1C',
      extra: '#E4DBBA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#47313E',
      primaryShadow: '#260D1C',
      accent: '#69586E',
      accentShadow: '#4F3A54',
      background: '#260D1C',
      cardBackground: '#2F1725',
      text: '#E4DBBA',
      extra: '#E7DFC1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gleam Palette',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#471E4C',
      primaryShadow: '#39183D',
      accent: '#876BB2',
      accentShadow: '#6C568E',
      background: '#FFEFFF',
      cardBackground: '#F2E3F2',
      text: '#471E4C',
      extra: '#F7B58C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#634067',
      primaryShadow: '#471E4C',
      accent: '#9981BE',
      accentShadow: '#876BB2',
      background: '#0A090E',
      cardBackground: '#111015',
      text: '#FFEFFF',
      extra: '#F8BC98',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indigo Harbor',
    tags: ['dark', 'curated'],
    light: {
      primary: '#081611',
      primaryShadow: '#06120E',
      accent: '#34311F',
      accentShadow: '#2A2719',
      background: '#DDDFDE',
      cardBackground: '#D4D6D5',
      text: '#081611',
      extra: '#162627',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2D3935',
      primaryShadow: '#081611',
      accent: '#525041',
      accentShadow: '#34311F',
      background: '#081611',
      cardBackground: '#121F1B',
      text: '#F0F0F0',
      extra: '#2D3C3D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystic Horizon',
    tags: ['curated'],
    light: {
      primary: '#C46833',
      primaryShadow: '#9D5329',
      accent: '#2E44AE',
      accentShadow: '#25368B',
      background: '#FCFCFC',
      cardBackground: '#EFEFEF',
      text: '#000000',
      extra: '#C4C7EE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CD7F52',
      primaryShadow: '#C46833',
      accent: '#4D60BA',
      accentShadow: '#2E44AE',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FCFCFC',
      extra: '#CACDF0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dynamic Wind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A1281C',
      primaryShadow: '#812016',
      accent: '#E8D6C0',
      accentShadow: '#BAAB9A',
      background: '#E8D6C0',
      cardBackground: '#DCCBB6',
      text: '#000000',
      extra: '#92938D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AF483E',
      primaryShadow: '#A1281C',
      accent: '#EBDCC9',
      accentShadow: '#E8D6C0',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#E8D6C0',
      extra: '#9D9E98',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ethereal Aurora',
    tags: ['dark', 'curated'],
    light: {
      primary: '#CDB27B',
      primaryShadow: '#A48E62',
      accent: '#8B7D73',
      accentShadow: '#6F645C',
      background: '#EDEBE7',
      cardBackground: '#E4E2DE',
      text: '#0C0C0D',
      extra: '#3F3D47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D5BE8F',
      primaryShadow: '#CDB27B',
      accent: '#9C9188',
      accentShadow: '#8B7D73',
      background: '#0C0C0D',
      cardBackground: '#161617',
      text: '#CDB27B',
      extra: '#525059',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scarlet Field',
    tags: ['bright', 'curated'],
    light: {
      primary: '#68518A',
      primaryShadow: '#53416E',
      accent: '#F4949C',
      accentShadow: '#C3767D',
      background: '#FFD0A4',
      cardBackground: '#F2C69C',
      text: '#1A1A1A',
      extra: '#FFD0A4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7F6B9C',
      primaryShadow: '#68518A',
      accent: '#F6A4AB',
      accentShadow: '#F4949C',
      background: '#0B0A10',
      cardBackground: '#121117',
      text: '#FFD0A4',
      extra: '#FFD5AD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Petal',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B9684F',
      primaryShadow: '#94533F',
      accent: '#8F3641',
      accentShadow: '#722B34',
      background: '#ECE5E3',
      cardBackground: '#E3DCDA',
      text: '#3E2A2E',
      extra: '#D2B2A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C47F69',
      primaryShadow: '#B9684F',
      accent: '#A0545E',
      accentShadow: '#8F3641',
      background: '#0E0B0E',
      cardBackground: '#151215',
      text: '#D2B2A0',
      extra: '#D7BAAA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Parlor',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E36433',
      primaryShadow: '#B65029',
      accent: '#AB0A2A',
      accentShadow: '#890822',
      background: '#FBFBF2',
      cardBackground: '#EEEEE6',
      text: '#27032A',
      extra: '#4B083D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E77B52',
      primaryShadow: '#E36433',
      accent: '#B82F4A',
      accentShadow: '#AB0A2A',
      background: '#27032A',
      cardBackground: '#300D33',
      text: '#FBFBF2',
      extra: '#5D2150',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noon Stream',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#E04217',
      primaryShadow: '#B33512',
      accent: '#F26D1F',
      accentShadow: '#C25719',
      background: '#EFE2DF',
      cardBackground: '#E5D9D6',
      text: '#3A0000',
      extra: '#590000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E55E3A',
      primaryShadow: '#E04217',
      accent: '#F48341',
      accentShadow: '#F26D1F',
      background: '#3A0000',
      cardBackground: '#420A0A',
      text: '#F0F0F0',
      extra: '#6A1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Caldera',
    tags: ['curated'],
    light: {
      primary: '#D44F26',
      primaryShadow: '#AA3F1E',
      accent: '#EF7E45',
      accentShadow: '#BF6537',
      background: '#EBE8E2',
      cardBackground: '#DFDCD7',
      text: '#091D21',
      extra: '#F0C188',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA6947',
      primaryShadow: '#D44F26',
      accent: '#F19161',
      accentShadow: '#EF7E45',
      background: '#091D21',
      cardBackground: '#13262A',
      text: '#EBE8E2',
      extra: '#F2C794',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fog Foyer',
    tags: ['cool', 'curated'],
    light: {
      primary: '#008AC5',
      primaryShadow: '#006E9E',
      accent: '#0AFF52',
      accentShadow: '#08CC42',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#00033C',
      extra: '#005260',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#269CCE',
      primaryShadow: '#008AC5',
      accent: '#2FFF6C',
      accentShadow: '#0AFF52',
      background: '#00033C',
      cardBackground: '#0A0D44',
      text: '#FFFFFF',
      extra: '#1A6370',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coral Steppe',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#EE0E64',
      primaryShadow: '#BE0B50',
      accent: '#FF3E8B',
      accentShadow: '#CC326F',
      background: '#FDE6EE',
      cardBackground: '#F0DBE2',
      text: '#1A1A1A',
      extra: '#F6CDCB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F1327B',
      primaryShadow: '#EE0E64',
      accent: '#FF5B9C',
      accentShadow: '#FF3E8B',
      background: '#0F080F',
      cardBackground: '#160F16',
      text: '#FDE6EE',
      extra: '#F7D2D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crisp Geyser',
    tags: ['warm', 'curated'],
    light: {
      primary: '#030201',
      primaryShadow: '#020201',
      accent: '#24211A',
      accentShadow: '#1D1A15',
      background: '#FAFAFA',
      cardBackground: '#EEEEEE',
      text: '#030201',
      extra: '#D4D8E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#292827',
      primaryShadow: '#030201',
      accent: '#45423C',
      accentShadow: '#24211A',
      background: '#030201',
      cardBackground: '#0D0C0B',
      text: '#FAFAFA',
      extra: '#D8DCE3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Navy Moon',
    tags: ['cool', 'curated'],
    light: {
      primary: '#E624AF',
      primaryShadow: '#B81D8C',
      accent: '#5433BE',
      accentShadow: '#432998',
      background: '#EFFAFA',
      cardBackground: '#E3EEEE',
      text: '#0E0E0E',
      extra: '#3DF9EA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EA45BB',
      primaryShadow: '#E624AF',
      accent: '#6E52C8',
      accentShadow: '#5433BE',
      background: '#0E0E0E',
      cardBackground: '#181818',
      text: '#EFFAFA',
      extra: '#50FAEC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Grove',
    tags: ['curated'],
    light: {
      primary: '#DA810B',
      primaryShadow: '#AE6709',
      accent: '#BC5810',
      accentShadow: '#96460D',
      background: '#F5FEFF',
      cardBackground: '#E9F1F2',
      text: '#100F24',
      extra: '#300E39',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E09430',
      primaryShadow: '#DA810B',
      accent: '#C67134',
      accentShadow: '#BC5810',
      background: '#100F24',
      cardBackground: '#1A192D',
      text: '#F5FEFF',
      extra: '#45264D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Relief',
    tags: ['curated'],
    light: {
      primary: '#A15947',
      primaryShadow: '#814739',
      accent: '#B8835A',
      accentShadow: '#936948',
      background: '#DFEBDD',
      cardBackground: '#D4DFD2',
      text: '#273145',
      extra: '#273145',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AF7263',
      primaryShadow: '#A15947',
      accent: '#C39673',
      accentShadow: '#B8835A',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#DFEBDD',
      extra: '#3D4658',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Dream',
    tags: ['curated'],
    light: {
      primary: '#CD7900',
      primaryShadow: '#A46100',
      accent: '#AE5800',
      accentShadow: '#8B4600',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#252525',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D58D26',
      primaryShadow: '#CD7900',
      accent: '#BA7126',
      accentShadow: '#AE5800',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#3B3B3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Moor',
    tags: ['cool', 'curated'],
    light: {
      primary: '#0097EC',
      primaryShadow: '#0079BD',
      accent: '#00F3FC',
      accentShadow: '#00C2CA',
      background: '#DDE9F0',
      cardBackground: '#D4E0E6',
      text: '#040026',
      extra: '#004BC0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A7EF',
      primaryShadow: '#0097EC',
      accent: '#26F5FC',
      accentShadow: '#00F3FC',
      background: '#040026',
      cardBackground: '#0E0A2F',
      text: '#FFBCFF',
      extra: '#1A5DC6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Cloister',
    tags: ['curated'],
    light: {
      primary: '#0B9D5F',
      primaryShadow: '#097E4C',
      accent: '#FA6735',
      accentShadow: '#C8522A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#040519',
      extra: '#22122C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30AC77',
      primaryShadow: '#0B9D5F',
      accent: '#FB7E53',
      accentShadow: '#FA6735',
      background: '#040519',
      cardBackground: '#0E0F22',
      text: '#FFFFFF',
      extra: '#382A41',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystic Mountain',
    tags: ['curated'],
    light: {
      primary: '#C65550',
      primaryShadow: '#9E4440',
      accent: '#8A2E3F',
      accentShadow: '#6E2532',
      background: '#EDE4E3',
      cardBackground: '#E4DBDA',
      text: '#2A202A',
      extra: '#D9D3D9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF6F6A',
      primaryShadow: '#C65550',
      accent: '#9C4D5C',
      accentShadow: '#8A2E3F',
      background: '#2A202A',
      cardBackground: '#332933',
      text: '#D9D3D9',
      extra: '#DDD7DD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Timeless Dunes',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D96624',
      primaryShadow: '#AE521D',
      accent: '#DB9421',
      accentShadow: '#AF761A',
      background: '#FFF4B0',
      cardBackground: '#F2E8A7',
      text: '#390C14',
      extra: '#390C14',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DF7D45',
      primaryShadow: '#D96624',
      accent: '#E0A442',
      accentShadow: '#DB9421',
      background: '#390C14',
      cardBackground: '#41161D',
      text: '#FFF4B0',
      extra: '#4D242C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Tapestry',
    tags: ['curated'],
    light: {
      primary: '#A53551',
      primaryShadow: '#842A41',
      accent: '#431837',
      accentShadow: '#36132C',
      background: '#E8E3E3',
      cardBackground: '#DCD8D8',
      text: '#000000',
      extra: '#1F232E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B3536B',
      primaryShadow: '#A53551',
      accent: '#5F3B55',
      accentShadow: '#431837',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#E8E3E3',
      extra: '#353943',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Moss',
    tags: ['bright', 'curated'],
    light: {
      primary: '#8DDB34',
      primaryShadow: '#71AF2A',
      accent: '#F8C868',
      accentShadow: '#C6A053',
      background: '#E8EEE1',
      cardBackground: '#DFE4D8',
      text: '#1A1A1A',
      extra: '#574368',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9EE052',
      primaryShadow: '#8DDB34',
      accent: '#F9D07F',
      accentShadow: '#F8C868',
      background: '#0C0F0E',
      cardBackground: '#131615',
      text: '#CFD3C1',
      extra: '#685677',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bright Fumarole',
    tags: ['curated'],
    light: {
      primary: '#459201',
      primaryShadow: '#377501',
      accent: '#EA7D10',
      accentShadow: '#BB640D',
      background: '#FFFF99',
      cardBackground: '#F2F291',
      text: '#020918',
      extra: '#424E66',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#61A227',
      primaryShadow: '#459201',
      accent: '#ED9134',
      accentShadow: '#EA7D10',
      background: '#020918',
      cardBackground: '#0C1321',
      text: '#FFFF99',
      extra: '#556075',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classic Aurora',
    tags: ['dark', 'curated'],
    light: {
      primary: '#FD724E',
      primaryShadow: '#CA5B3E',
      accent: '#A02F40',
      accentShadow: '#802633',
      background: '#F1E6E3',
      cardBackground: '#E7DDDA',
      text: '#201727',
      extra: '#201727',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FD8769',
      primaryShadow: '#FD724E',
      accent: '#AE4E5D',
      accentShadow: '#A02F40',
      background: '#201727',
      cardBackground: '#292030',
      text: '#F0F0F0',
      extra: '#362E3D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cosmic Dusk',
    tags: ['dark', 'curated'],
    light: {
      primary: '#A56850',
      primaryShadow: '#845340',
      accent: '#30716F',
      accentShadow: '#265A59',
      background: '#EAE5E3',
      cardBackground: '#E1DCDA',
      text: '#262D2F',
      extra: '#778C96',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B37F6A',
      primaryShadow: '#A56850',
      accent: '#4F8685',
      accentShadow: '#30716F',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#F0F0F0',
      extra: '#8598A1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Shore',
    tags: ['cool', 'curated'],
    light: {
      primary: '#16254A',
      primaryShadow: '#121E3B',
      accent: '#3A4278',
      accentShadow: '#2E3560',
      background: '#FCCFFF',
      cardBackground: '#EFC5F2',
      text: '#16254A',
      extra: '#4D4E8F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#394665',
      primaryShadow: '#16254A',
      accent: '#585E8C',
      accentShadow: '#3A4278',
      background: '#16254A',
      cardBackground: '#1F2E51',
      text: '#FCCFFF',
      extra: '#5F609A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Organic Garden',
    tags: ['warm', 'curated'],
    light: {
      primary: '#748530',
      primaryShadow: '#5D6A26',
      accent: '#C4A44D',
      accentShadow: '#9D833E',
      background: '#E6E7E1',
      cardBackground: '#DDDED8',
      text: '#292829',
      extra: '#292829',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#89974F',
      primaryShadow: '#748530',
      accent: '#CDB268',
      accentShadow: '#C4A44D',
      background: '#0B0C0D',
      cardBackground: '#121314',
      text: '#D6C0AE',
      extra: '#3E3E3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Savanna',
    tags: ['cool', 'curated'],
    light: {
      primary: '#FF4ADC',
      primaryShadow: '#CC3BB0',
      accent: '#3DFF98',
      accentShadow: '#31CC7A',
      background: '#F0F6F0',
      cardBackground: '#E4EAE4',
      text: '#222323',
      extra: '#222323',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF65E1',
      primaryShadow: '#FF4ADC',
      accent: '#5AFFA7',
      accentShadow: '#3DFF98',
      background: '#222323',
      cardBackground: '#2B2C2C',
      text: '#F0F6F0',
      extra: '#383939',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blaze Vision',
    tags: ['bright', 'curated'],
    light: {
      primary: '#D86969',
      primaryShadow: '#AD5454',
      accent: '#78B3D6',
      accentShadow: '#608FAB',
      background: '#DEE3E2',
      cardBackground: '#D3D8D7',
      text: '#1A1A1A',
      extra: '#DEE3E2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE8080',
      primaryShadow: '#D86969',
      accent: '#8CBEDC',
      accentShadow: '#78B3D6',
      background: '#0E0B0F',
      cardBackground: '#151216',
      text: '#DEE3E2',
      extra: '#E1E6E5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunny Nebula',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BE8E03',
      primaryShadow: '#987202',
      accent: '#916803',
      accentShadow: '#745302',
      background: '#ECE8DD',
      cardBackground: '#E3DFD4',
      text: '#181E28',
      extra: '#DED4C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C89F29',
      primaryShadow: '#BE8E03',
      accent: '#A27F29',
      accentShadow: '#916803',
      background: '#181E28',
      cardBackground: '#212731',
      text: '#DED4C8',
      extra: '#E1D8CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Magenta Loft',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DF6939',
      primaryShadow: '#B2542E',
      accent: '#C22E35',
      accentShadow: '#9B252A',
      background: '#FFF4B0',
      cardBackground: '#F2E8A7',
      text: '#430F43',
      extra: '#430F43',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E48057',
      primaryShadow: '#DF6939',
      accent: '#CB4D53',
      accentShadow: '#C22E35',
      background: '#430F43',
      cardBackground: '#4B194B',
      text: '#FFF4B0',
      extra: '#562756',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ephemeral Keep',
    tags: ['curated'],
    light: {
      primary: '#D89544',
      primaryShadow: '#AD7736',
      accent: '#275F77',
      accentShadow: '#1F4C5F',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#402859',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DEA560',
      primaryShadow: '#D89544',
      accent: '#47778B',
      accentShadow: '#275F77',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#533E6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Luminous Mosaic',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#230019',
      primaryShadow: '#1C0014',
      accent: '#396D7D',
      accentShadow: '#2E5764',
      background: '#E0DDDF',
      cardBackground: '#D7D4D6',
      text: '#230019',
      extra: '#1F2439',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#44263C',
      primaryShadow: '#230019',
      accent: '#578391',
      accentShadow: '#396D7D',
      background: '#230019',
      cardBackground: '#2C0A22',
      text: '#F0F0F0',
      extra: '#353A4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rich Star',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E01F3F',
      primaryShadow: '#B31932',
      accent: '#271D2C',
      accentShadow: '#1F1723',
      background: '#FFF8ED',
      cardBackground: '#F2ECE1',
      text: '#271D2C',
      extra: '#7B6960',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E5415C',
      primaryShadow: '#E01F3F',
      accent: '#473F4C',
      accentShadow: '#271D2C',
      background: '#271D2C',
      cardBackground: '#302634',
      text: '#FFF8ED',
      extra: '#887870',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Terrace',
    tags: ['cool', 'curated'],
    light: {
      primary: '#3B7961',
      primaryShadow: '#2F614E',
      accent: '#64D0B8',
      accentShadow: '#50A693',
      background: '#F4F2AF',
      cardBackground: '#E8E6A6',
      text: '#291F3E',
      extra: '#291F3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#588D79',
      primaryShadow: '#3B7961',
      accent: '#7BD7C3',
      accentShadow: '#64D0B8',
      background: '#291F3E',
      cardBackground: '#322846',
      text: '#F4F2AF',
      extra: '#3E3551',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Fire',
    tags: ['cool', 'curated'],
    light: {
      primary: '#8F596C',
      primaryShadow: '#724756',
      accent: '#5B3F69',
      accentShadow: '#493254',
      background: '#CCE3E3',
      cardBackground: '#C2D8D8',
      text: '#231C24',
      extra: '#231C24',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A07282',
      primaryShadow: '#8F596C',
      accent: '#745C80',
      accentShadow: '#5B3F69',
      background: '#231C24',
      cardBackground: '#2C252D',
      text: '#CCE3E3',
      extra: '#39333A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bold Stone',
    tags: ['curated'],
    light: {
      primary: '#0BAFE6',
      primaryShadow: '#098CB8',
      accent: '#119955',
      accentShadow: '#0E7A44',
      background: '#F0EDD8',
      cardBackground: '#E4E1CD',
      text: '#000000',
      extra: '#1C284D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30BBEA',
      primaryShadow: '#0BAFE6',
      accent: '#35A86F',
      accentShadow: '#119955',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F0EDD8',
      extra: '#333E5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indigo Spectrum',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CB765C',
      primaryShadow: '#A25E4A',
      accent: '#FFB873',
      accentShadow: '#CC935C',
      background: '#FFECD6',
      cardBackground: '#F2E0CB',
      text: '#25213E',
      extra: '#7A4A5A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D38B74',
      primaryShadow: '#CB765C',
      accent: '#FFC388',
      accentShadow: '#FFB873',
      background: '#25213E',
      cardBackground: '#2E2A46',
      text: '#FFECD6',
      extra: '#875C6B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pure Summit',
    tags: ['warm', 'curated'],
    light: {
      primary: '#ED572D',
      primaryShadow: '#BE4624',
      accent: '#B3A720',
      accentShadow: '#8F861A',
      background: '#FFF4B0',
      cardBackground: '#F2E8A7',
      text: '#1A0E14',
      extra: '#321921',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F0704D',
      primaryShadow: '#ED572D',
      accent: '#BEB441',
      accentShadow: '#B3A720',
      background: '#1A0E14',
      cardBackground: '#23181D',
      text: '#FFF4B0',
      extra: '#473037',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noon Floe',
    tags: ['warm', 'curated'],
    light: {
      primary: '#5E6745',
      primaryShadow: '#4B5237',
      accent: '#AEBA89',
      accentShadow: '#8B956E',
      background: '#E3EEC0',
      cardBackground: '#D8E2B6',
      text: '#202020',
      extra: '#202020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#767E61',
      primaryShadow: '#5E6745',
      accent: '#BAC49B',
      accentShadow: '#AEBA89',
      background: '#202020',
      cardBackground: '#292929',
      text: '#E3EEC0',
      extra: '#363636',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snowy Dusk',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D35B2B',
      primaryShadow: '#A94922',
      accent: '#F27947',
      accentShadow: '#C26139',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#27141D',
      extra: '#B0766A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA744B',
      primaryShadow: '#D35B2B',
      accent: '#F48D63',
      accentShadow: '#F27947',
      background: '#27141D',
      cardBackground: '#301D26',
      text: '#FFFFFF',
      extra: '#B88479',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Aura',
    tags: ['warm', 'curated'],
    light: {
      primary: '#293425',
      primaryShadow: '#212A1E',
      accent: '#B7BB9F',
      accentShadow: '#92967F',
      background: '#F9FFF0',
      cardBackground: '#EDF2E4',
      text: '#293425',
      extra: '#323B2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#495246',
      primaryShadow: '#293425',
      accent: '#C2C5AD',
      accentShadow: '#B7BB9F',
      background: '#090A0D',
      cardBackground: '#101114',
      text: '#F9FFF0',
      extra: '#474F44',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fog Permafrost',
    tags: ['cool', 'curated'],
    light: {
      primary: '#53A788',
      primaryShadow: '#42866D',
      accent: '#70D38B',
      accentShadow: '#5AA96F',
      background: '#CBFFD8',
      cardBackground: '#C1F2CD',
      text: '#0C0D14',
      extra: '#181C28',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6DB49A',
      primaryShadow: '#53A788',
      accent: '#85DA9C',
      accentShadow: '#70D38B',
      background: '#0C0D14',
      cardBackground: '#16171D',
      text: '#CBFFD8',
      extra: '#2F333E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Spire',
    tags: ['cool', 'curated'],
    light: {
      primary: '#528C9F',
      primaryShadow: '#42707F',
      accent: '#425879',
      accentShadow: '#354661',
      background: '#F1F2B6',
      cardBackground: '#E5E6AD',
      text: '#2F325D',
      extra: '#2F325D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6C9DAD',
      primaryShadow: '#528C9F',
      accent: '#5E718D',
      accentShadow: '#425879',
      background: '#0A0C11',
      cardBackground: '#111318',
      text: '#F1F2B6',
      extra: '#44476D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indigo Mosaic',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#3B1D36',
      primaryShadow: '#2F172B',
      accent: '#998B74',
      accentShadow: '#7A6F5D',
      background: '#E2DFE1',
      cardBackground: '#D9D6D8',
      text: '#171318',
      extra: '#291B17',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#583F54',
      primaryShadow: '#3B1D36',
      accent: '#A89C89',
      accentShadow: '#998B74',
      background: '#171318',
      cardBackground: '#201C21',
      text: '#F0F0F0',
      extra: '#3E322E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Organic Cove',
    tags: ['curated'],
    light: {
      primary: '#408898',
      primaryShadow: '#336D7A',
      accent: '#57BD91',
      accentShadow: '#469774',
      background: '#F0EB88',
      cardBackground: '#E4DF81',
      text: '#1C1A3A',
      extra: '#1C1A3A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5D9AA7',
      primaryShadow: '#408898',
      accent: '#70C7A2',
      accentShadow: '#57BD91',
      background: '#1C1A3A',
      cardBackground: '#252342',
      text: '#F0EB88',
      extra: '#33314E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Meadow',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D58353',
      primaryShadow: '#AA6942',
      accent: '#E0BB68',
      accentShadow: '#B39653',
      background: '#EEDBC8',
      cardBackground: '#E2D0BE',
      text: '#5A473E',
      extra: '#EEDBC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DB966D',
      primaryShadow: '#D58353',
      accent: '#E5C57F',
      accentShadow: '#E0BB68',
      background: '#0E0C0E',
      cardBackground: '#151315',
      text: '#EEDBC8',
      extra: '#F0DFCE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fertile Reef',
    tags: ['curated'],
    light: {
      primary: '#C06852',
      primaryShadow: '#9A5342',
      accent: '#9D4343',
      accentShadow: '#7E3636',
      background: '#FCFBF3',
      cardBackground: '#EFEEE7',
      text: '#2A152D',
      extra: '#2E2E43',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C97F6C',
      primaryShadow: '#C06852',
      accent: '#AC5F5F',
      accentShadow: '#9D4343',
      background: '#2A152D',
      cardBackground: '#331E35',
      text: '#FCFBF3',
      extra: '#434356',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Savanna',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#A7673E',
      primaryShadow: '#865232',
      accent: '#BB8B4C',
      accentShadow: '#966F3D',
      background: '#EAE5E2',
      cardBackground: '#E1DCD9',
      text: '#1F1919',
      extra: '#30353A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B47E5B',
      primaryShadow: '#A7673E',
      accent: '#C59C67',
      accentShadow: '#BB8B4C',
      background: '#1F1919',
      cardBackground: '#282222',
      text: '#D5D3C1',
      extra: '#45494E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rocky Harbor',
    tags: ['cool', 'curated'],
    light: {
      primary: '#5A8F78',
      primaryShadow: '#487260',
      accent: '#3A5068',
      accentShadow: '#2E4053',
      background: '#F5F6DF',
      cardBackground: '#E9EAD4',
      text: '#372A51',
      extra: '#372A51',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#73A08C',
      primaryShadow: '#5A8F78',
      accent: '#586A7F',
      accentShadow: '#3A5068',
      background: '#0B0C10',
      cardBackground: '#121317',
      text: '#F5F6DF',
      extra: '#4B3F62',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Vine',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FD724E',
      primaryShadow: '#CA5B3E',
      accent: '#A02F40',
      accentShadow: '#802633',
      background: '#F5DDBC',
      cardBackground: '#E9D2B3',
      text: '#5F2F45',
      extra: '#F5DDBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FD8769',
      primaryShadow: '#FD724E',
      accent: '#AE4E5D',
      accentShadow: '#A02F40',
      background: '#100B0E',
      cardBackground: '#171215',
      text: '#F5DDBC',
      extra: '#F6E0C3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ethereal Balcony',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#73C9EB',
      primaryShadow: '#5CA1BC',
      accent: '#7B8AC6',
      accentShadow: '#626E9E',
      background: '#FFFCFF',
      cardBackground: '#F2EFF2',
      text: '#000000',
      extra: '#E6E1F2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#88D1EE',
      primaryShadow: '#73C9EB',
      accent: '#8F9CCF',
      accentShadow: '#7B8AC6',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFCFF',
      extra: '#E9E4F3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spring Plaza',
    tags: ['cool', 'curated'],
    light: {
      primary: '#E53AFF',
      primaryShadow: '#B72ECC',
      accent: '#9B20B7',
      accentShadow: '#7C1A92',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#623EA2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E958FF',
      primaryShadow: '#E53AFF',
      accent: '#AA41C2',
      accentShadow: '#9B20B7',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#7251AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Daybreak Eclipse',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#EA9971',
      primaryShadow: '#BB7A5A',
      accent: '#9D5D7A',
      accentShadow: '#7E4A62',
      background: '#F0E9E6',
      cardBackground: '#E6E0DD',
      text: '#1A1A1A',
      extra: '#E6D3B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EDA886',
      primaryShadow: '#EA9971',
      accent: '#AC758E',
      accentShadow: '#9D5D7A',
      background: '#0F0D0F',
      cardBackground: '#161416',
      text: '#E6D3B9',
      extra: '#E9D7C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Cliff',
    tags: ['warm', 'curated'],
    light: {
      primary: '#73303E',
      primaryShadow: '#5C2632',
      accent: '#C99C69',
      accentShadow: '#A17D54',
      background: '#E6E1E2',
      cardBackground: '#DDD8D9',
      text: '#212236',
      extra: '#212236',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#884F5B',
      primaryShadow: '#73303E',
      accent: '#D1AB80',
      accentShadow: '#C99C69',
      background: '#212236',
      cardBackground: '#2A2B3E',
      text: '#D0C4BD',
      extra: '#37384A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Sculpture',
    tags: ['cool', 'curated'],
    light: {
      primary: '#5B8C7C',
      primaryShadow: '#497063',
      accent: '#0D1A1A',
      accentShadow: '#0A1515',
      background: '#F2FFF2',
      cardBackground: '#E6F2E6',
      text: '#0D1A1A',
      extra: '#ADD9BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#749D90',
      primaryShadow: '#5B8C7C',
      accent: '#313C3C',
      accentShadow: '#0D1A1A',
      background: '#0D1A1A',
      cardBackground: '#172323',
      text: '#F2FFF2',
      extra: '#B5DDC3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pure Shade',
    tags: ['dark', 'curated'],
    light: {
      primary: '#000100',
      primaryShadow: '#000100',
      accent: '#431723',
      accentShadow: '#36121C',
      background: '#DDDDDD',
      cardBackground: '#D4D4D4',
      text: '#000100',
      extra: '#46464F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#262726',
      primaryShadow: '#000100',
      accent: '#5F3A44',
      accentShadow: '#431723',
      background: '#000100',
      cardBackground: '#0A0B0A',
      text: '#F0F0F0',
      extra: '#595961',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Rampart',
    tags: ['warm', 'curated'],
    light: {
      primary: '#965D25',
      primaryShadow: '#784A1E',
      accent: '#D19F3F',
      accentShadow: '#A77F32',
      background: '#E9E4E0',
      cardBackground: '#E0DBD7',
      text: '#241E1E',
      extra: '#CACBCC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A67546',
      primaryShadow: '#965D25',
      accent: '#D8AD5C',
      accentShadow: '#D19F3F',
      background: '#241E1E',
      cardBackground: '#2D2727',
      text: '#CACBCC',
      extra: '#CFD0D1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Peak',
    tags: ['curated'],
    light: {
      primary: '#EE1242',
      primaryShadow: '#BE0E35',
      accent: '#9F6E1E',
      accentShadow: '#7F5818',
      background: '#F6F4A4',
      cardBackground: '#EAE89C',
      text: '#512617',
      extra: '#F9BBAE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F1365E',
      primaryShadow: '#EE1242',
      accent: '#AD8440',
      accentShadow: '#9F6E1E',
      background: '#0F090E',
      cardBackground: '#161015',
      text: '#F6F4A4',
      extra: '#FAC2B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Spirit',
    tags: ['dark', 'curated'],
    light: {
      primary: '#97933A',
      primaryShadow: '#79762E',
      accent: '#00070D',
      accentShadow: '#00060A',
      background: '#E9E9E1',
      cardBackground: '#E0E0D8',
      text: '#00070D',
      extra: '#1F2C3D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A7A358',
      primaryShadow: '#97933A',
      accent: '#262C31',
      accentShadow: '#00070D',
      background: '#00070D',
      cardBackground: '#0A1117',
      text: '#DECA54',
      extra: '#354150',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Balcony',
    tags: ['curated'],
    light: {
      primary: '#E527D2',
      primaryShadow: '#B71FA8',
      accent: '#2FB6C3',
      accentShadow: '#26929C',
      background: '#FFFB76',
      cardBackground: '#F2EE70',
      text: '#0F0E11',
      extra: '#2D2C33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E947D9',
      primaryShadow: '#E527D2',
      accent: '#4EC1CC',
      accentShadow: '#2FB6C3',
      background: '#0F0E11',
      cardBackground: '#19181B',
      text: '#FFFB76',
      extra: '#424147',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fleeting Steppe',
    tags: ['warm', 'curated'],
    light: {
      primary: '#00303B',
      primaryShadow: '#00262F',
      accent: '#D4984A',
      accentShadow: '#AA7A3B',
      background: '#FFFFC7',
      cardBackground: '#F2F2BD',
      text: '#00303B',
      extra: '#4E494C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#264F58',
      primaryShadow: '#00303B',
      accent: '#DAA765',
      accentShadow: '#D4984A',
      background: '#00303B',
      cardBackground: '#0A3843',
      text: '#FFFFC7',
      extra: '#605B5E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cool Hotspring',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00CC8B',
      primaryShadow: '#00A36F',
      accent: '#005A75',
      accentShadow: '#00485E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#212B5E',
      extra: '#212B5E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26D49C',
      primaryShadow: '#00CC8B',
      accent: '#26738A',
      accentShadow: '#005A75',
      background: '#080E10',
      cardBackground: '#0F1517',
      text: '#FFFFFF',
      extra: '#37406E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Fortress',
    tags: ['curated'],
    light: {
      primary: '#B78F43',
      primaryShadow: '#927236',
      accent: '#3E8E92',
      accentShadow: '#327275',
      background: '#EBE8E2',
      cardBackground: '#E2DFD9',
      text: '#1C1F2B',
      extra: '#97C89B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C2A05F',
      primaryShadow: '#B78F43',
      accent: '#5B9FA2',
      accentShadow: '#3E8E92',
      background: '#1C1F2B',
      cardBackground: '#252833',
      text: '#DFCD6D',
      extra: '#A1CEA5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glossy Star',
    tags: ['curated'],
    light: {
      primary: '#9150E9',
      primaryShadow: '#7440BA',
      accent: '#80BE3B',
      accentShadow: '#66982F',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#C1A3FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A26AEC',
      primaryShadow: '#9150E9',
      accent: '#93C858',
      accentShadow: '#80BE3B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#C7ACFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Copse',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#BA5258',
      primaryShadow: '#954246',
      accent: '#744758',
      accentShadow: '#5D3946',
      background: '#ECE3E4',
      cardBackground: '#E3DADB',
      text: '#261C22',
      extra: '#9E4E58',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C46C71',
      primaryShadow: '#BA5258',
      accent: '#896371',
      accentShadow: '#744758',
      background: '#261C22',
      cardBackground: '#2F252B',
      text: '#F0F0F0',
      extra: '#A86069',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Rain',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00AAAA',
      primaryShadow: '#008888',
      accent: '#AA00AA',
      accentShadow: '#880088',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#555555',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B7B7',
      primaryShadow: '#00AAAA',
      accent: '#B726B7',
      accentShadow: '#AA00AA',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#666666',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Field',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#FE0094',
      primaryShadow: '#CB0076',
      accent: '#70B9FB',
      accentShadow: '#5A94C9',
      background: '#F1DDE9',
      cardBackground: '#E7D4E0',
      text: '#000000',
      extra: '#5F4886',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FE26A4',
      primaryShadow: '#FE0094',
      accent: '#85C4FC',
      accentShadow: '#70B9FB',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F0F0F0',
      extra: '#6F5A92',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Ocean',
    tags: ['curated'],
    light: {
      primary: '#D71A21',
      primaryShadow: '#AC151A',
      accent: '#01334E',
      accentShadow: '#01293E',
      background: '#FCE4A8',
      cardBackground: '#EFD9A0',
      text: '#01334E',
      extra: '#FCE4A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DD3C42',
      primaryShadow: '#D71A21',
      accent: '#275269',
      accentShadow: '#01334E',
      background: '#0E090D',
      cardBackground: '#151014',
      text: '#FCE4A8',
      extra: '#FCE7B1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Fresco',
    tags: ['cool', 'curated'],
    light: {
      primary: '#F81CA6',
      primaryShadow: '#C61685',
      accent: '#22C278',
      accentShadow: '#1B9B60',
      background: '#FFFDEB',
      cardBackground: '#F2F0DF',
      text: '#000000',
      extra: '#30286D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F93EB3',
      primaryShadow: '#F81CA6',
      accent: '#43CB8C',
      accentShadow: '#22C278',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFDEB',
      extra: '#453E7C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vivid Canvas',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AB6C84',
      primaryShadow: '#89566A',
      accent: '#613854',
      accentShadow: '#4E2D43',
      background: '#FFE8E1',
      cardBackground: '#F2DCD6',
      text: '#292B30',
      extra: '#292B30',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B88296',
      primaryShadow: '#AB6C84',
      accent: '#79566E',
      accentShadow: '#613854',
      background: '#0D0B10',
      cardBackground: '#141217',
      text: '#FFE8E1',
      extra: '#3E4045',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snowy Soul',
    tags: ['curated'],
    light: {
      primary: '#220780',
      primaryShadow: '#1B0666',
      accent: '#615FED',
      accentShadow: '#4E4CBE',
      background: '#FFFAB2',
      cardBackground: '#F2EEA9',
      text: '#220780',
      extra: '#FFBA5A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#432C93',
      primaryShadow: '#220780',
      accent: '#7977F0',
      accentShadow: '#615FED',
      background: '#220780',
      cardBackground: '#2B1185',
      text: '#FFFAB2',
      extra: '#FFC16B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tranquil Field',
    tags: ['cool', 'curated'],
    light: {
      primary: '#65AED6',
      primaryShadow: '#518BAB',
      accent: '#5F699C',
      accentShadow: '#4C547D',
      background: '#EFFAE6',
      cardBackground: '#E3EEDB',
      text: '#372E4D',
      extra: '#372E4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7CBADC',
      primaryShadow: '#65AED6',
      accent: '#7780AB',
      accentShadow: '#5F699C',
      background: '#0B0D12',
      cardBackground: '#121419',
      text: '#EFFAE6',
      extra: '#4B435F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artisan Clearing',
    tags: ['curated'],
    light: {
      primary: '#20A5A6',
      primaryShadow: '#1A8485',
      accent: '#0F1B26',
      accentShadow: '#0C161E',
      background: '#F5E8D1',
      cardBackground: '#E9DCC7',
      text: '#0F1B26',
      extra: '#DD5639',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#41B3B3',
      primaryShadow: '#20A5A6',
      accent: '#333D47',
      accentShadow: '#0F1B26',
      background: '#0F1B26',
      cardBackground: '#19242F',
      text: '#F5E8D1',
      extra: '#E0674D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Soft Wind',
    tags: ['cool', 'curated'],
    light: {
      primary: '#B0455A',
      primaryShadow: '#8D3748',
      accent: '#702782',
      accentShadow: '#5A1F68',
      background: '#EBE2E4',
      cardBackground: '#E2D9DB',
      text: '#471B6E',
      extra: '#8BC7BF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BC6173',
      primaryShadow: '#B0455A',
      accent: '#854795',
      accentShadow: '#702782',
      background: '#0D0A0F',
      cardBackground: '#141116',
      text: '#EBD694',
      extra: '#97CDC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Ocean',
    tags: ['curated'],
    light: {
      primary: '#40806A',
      primaryShadow: '#336655',
      accent: '#B37D62',
      accentShadow: '#8F644E',
      background: '#E2E7E5',
      cardBackground: '#D9DEDC',
      text: '#332B33',
      extra: '#7ACCCC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5D9380',
      primaryShadow: '#40806A',
      accent: '#BE917A',
      accentShadow: '#B37D62',
      background: '#0A0C0F',
      cardBackground: '#111316',
      text: '#CCCCCC',
      extra: '#87D1D1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lunar Sculpture',
    tags: ['curated'],
    light: {
      primary: '#E6A47E',
      primaryShadow: '#B88365',
      accent: '#8F5D84',
      accentShadow: '#724A6A',
      background: '#DEE6C1',
      cardBackground: '#D3DBB7',
      text: '#1A1A1A',
      extra: '#4C4961',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EAB291',
      primaryShadow: '#E6A47E',
      accent: '#A07596',
      accentShadow: '#8F5D84',
      background: '#0F0D10',
      cardBackground: '#161417',
      text: '#DEE6C1',
      extra: '#5E5B71',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Spire',
    tags: ['curated'],
    light: {
      primary: '#D86858',
      primaryShadow: '#AD5346',
      accent: '#A83848',
      accentShadow: '#862D3A',
      background: '#EEE5E4',
      cardBackground: '#E4DCDB',
      text: '#281828',
      extra: '#582838',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE7F71',
      primaryShadow: '#D86858',
      accent: '#B55663',
      accentShadow: '#A83848',
      background: '#281828',
      cardBackground: '#312131',
      text: '#D8D8D8',
      extra: '#693E4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Mesa',
    tags: ['warm', 'curated'],
    light: {
      primary: '#8B506D',
      primaryShadow: '#6F4057',
      accent: '#381A3E',
      accentShadow: '#2D1532',
      background: '#F6EDC1',
      cardBackground: '#EAE1B7',
      text: '#381A3E',
      extra: '#F890A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C6A83',
      primaryShadow: '#8B506D',
      accent: '#563C5B',
      accentShadow: '#381A3E',
      background: '#0C0A0F',
      cardBackground: '#131116',
      text: '#F6EDC1',
      extra: '#F99BB1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hazy Mural',
    tags: ['curated'],
    light: {
      primary: '#BE772B',
      primaryShadow: '#985F22',
      accent: '#A83131',
      accentShadow: '#862727',
      background: '#F1F6F0',
      cardBackground: '#E5EAE4',
      text: '#111522',
      extra: '#F1D3A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C88B4B',
      primaryShadow: '#BE772B',
      accent: '#B55050',
      accentShadow: '#A83131',
      background: '#111522',
      cardBackground: '#1B1E2B',
      text: '#F1F6F0',
      extra: '#F2D7B1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Contemporary Peak',
    tags: ['curated'],
    light: {
      primary: '#2BDB72',
      primaryShadow: '#22AF5B',
      accent: '#DB4B3D',
      accentShadow: '#AF3C31',
      background: '#FAFFFC',
      cardBackground: '#EEF2EF',
      text: '#222533',
      extra: '#222533',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4BE087',
      primaryShadow: '#2BDB72',
      accent: '#E0665A',
      accentShadow: '#DB4B3D',
      background: '#222533',
      cardBackground: '#2B2E3B',
      text: '#FAFFFC',
      extra: '#383B47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Winter Sky',
    tags: ['cool', 'curated'],
    light: {
      primary: '#221E31',
      primaryShadow: '#1B1827',
      accent: '#41485D',
      accentShadow: '#343A4A',
      background: '#E0DFE1',
      cardBackground: '#D7D6D8',
      text: '#221E31',
      extra: '#778E98',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#434050',
      primaryShadow: '#221E31',
      accent: '#5E6375',
      accentShadow: '#41485D',
      background: '#221E31',
      cardBackground: '#2B2739',
      text: '#C5DBD4',
      extra: '#8599A2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Olive Harbor',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A4825D',
      primaryShadow: '#83684A',
      accent: '#6F4143',
      accentShadow: '#593436',
      background: '#EAE7E4',
      cardBackground: '#E1DEDB',
      text: '#3B392A',
      extra: '#3B392A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B29575',
      primaryShadow: '#A4825D',
      accent: '#855E5F',
      accentShadow: '#6F4143',
      background: '#0D0C0F',
      cardBackground: '#141316',
      text: '#D6BAA5',
      extra: '#4F4D3F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Forest',
    tags: ['curated'],
    light: {
      primary: '#9E20B8',
      primaryShadow: '#7E1A93',
      accent: '#460198',
      accentShadow: '#38017A',
      background: '#FFE0DC',
      cardBackground: '#F2D5D1',
      text: '#460198',
      extra: '#6A1794',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD41C3',
      primaryShadow: '#9E20B8',
      accent: '#6227A7',
      accentShadow: '#460198',
      background: '#0D0912',
      cardBackground: '#141019',
      text: '#FFE0DC',
      extra: '#792E9F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Timeless Vent',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CE6442',
      primaryShadow: '#A55035',
      accent: '#B2434D',
      accentShadow: '#8E363E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#211D25',
      extra: '#211D25',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D57B5E',
      primaryShadow: '#CE6442',
      accent: '#BE5F68',
      accentShadow: '#B2434D',
      background: '#211D25',
      cardBackground: '#2A262E',
      text: '#FFFFFF',
      extra: '#37343B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Thicket',
    tags: ['dark', 'curated'],
    light: {
      primary: '#952452',
      primaryShadow: '#771D42',
      accent: '#B24E3D',
      accentShadow: '#8E3E31',
      background: '#F2E9CD',
      cardBackground: '#E6DDC3',
      text: '#0E020A',
      extra: '#8EC5B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A5456C',
      primaryShadow: '#952452',
      accent: '#BE695A',
      accentShadow: '#B24E3D',
      background: '#0E020A',
      cardBackground: '#180C14',
      text: '#F2E9CD',
      extra: '#99CBBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Winter Tundra',
    tags: ['cool', 'curated'],
    light: {
      primary: '#7FFF00',
      primaryShadow: '#66CC00',
      accent: '#F03C79',
      accentShadow: '#C03061',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#2121FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#92FF26',
      primaryShadow: '#7FFF00',
      accent: '#F2598D',
      accentShadow: '#F03C79',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#3737FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Ocean',
    tags: ['curated'],
    light: {
      primary: '#B56303',
      primaryShadow: '#914F02',
      accent: '#E38D18',
      accentShadow: '#B67113',
      background: '#FFFEFF',
      cardBackground: '#F2F1F2',
      text: '#010000',
      extra: '#D6E0F9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C07A29',
      primaryShadow: '#B56303',
      accent: '#E79E3B',
      accentShadow: '#E38D18',
      background: '#010000',
      cardBackground: '#0B0A0A',
      text: '#FFFEFF',
      extra: '#DAE3FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Caldera',
    tags: ['warm', 'curated'],
    light: {
      primary: '#EC773D',
      primaryShadow: '#BD5F31',
      accent: '#B75834',
      accentShadow: '#92462A',
      background: '#FFF8E1',
      cardBackground: '#F2ECD6',
      text: '#0D0B0D',
      extra: '#C8B89F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EF8B5A',
      primaryShadow: '#EC773D',
      accent: '#C27152',
      accentShadow: '#B75834',
      background: '#0D0B0D',
      cardBackground: '#171517',
      text: '#FFF8E1',
      extra: '#CEBFA9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Shade',
    tags: ['curated'],
    light: {
      primary: '#6B72D4',
      primaryShadow: '#565BAA',
      accent: '#A8D455',
      accentShadow: '#86AA44',
      background: '#F9F8DD',
      cardBackground: '#EDECD2',
      text: '#272120',
      extra: '#D2E291',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8187DA',
      primaryShadow: '#6B72D4',
      accent: '#B5DA6F',
      accentShadow: '#A8D455',
      background: '#272120',
      cardBackground: '#302A29',
      text: '#F9F8DD',
      extra: '#D7E59C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Veranda',
    tags: ['cool', 'curated'],
    light: {
      primary: '#DE39E9',
      primaryShadow: '#B22EBA',
      accent: '#8623AE',
      accentShadow: '#6B1C8B',
      background: '#F7FAEA',
      cardBackground: '#EBEEDE',
      text: '#120F28',
      extra: '#FFD2DE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E357EC',
      primaryShadow: '#DE39E9',
      accent: '#9844BA',
      accentShadow: '#8623AE',
      background: '#120F28',
      cardBackground: '#1B1931',
      text: '#F7FAEA',
      extra: '#FFD7E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy Desert',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#976749',
      primaryShadow: '#79523A',
      accent: '#773F37',
      accentShadow: '#5F322C',
      background: '#E9E5E3',
      cardBackground: '#E0DCDA',
      text: '#000000',
      extra: '#2B2422',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A77E64',
      primaryShadow: '#976749',
      accent: '#8B5C55',
      accentShadow: '#773F37',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#CBC6BE',
      extra: '#403A38',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Permafrost',
    tags: ['curated'],
    light: {
      primary: '#2D80A6',
      primaryShadow: '#246685',
      accent: '#B68241',
      accentShadow: '#926834',
      background: '#E0E7EA',
      cardBackground: '#D7DEE1',
      text: '#2B3F41',
      extra: '#2B3F41',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4D93B3',
      primaryShadow: '#2D80A6',
      accent: '#C1955E',
      accentShadow: '#B68241',
      background: '#090C11',
      cardBackground: '#101318',
      text: '#C1BCAC',
      extra: '#405254',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Stream',
    tags: ['curated'],
    light: {
      primary: '#A84A5E',
      primaryShadow: '#863B4B',
      accent: '#69304C',
      accentShadow: '#54263D',
      background: '#EAE3E4',
      cardBackground: '#E1DADB',
      text: '#401E2F',
      extra: '#3C3669',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B56576',
      primaryShadow: '#A84A5E',
      accent: '#804F67',
      accentShadow: '#69304C',
      background: '#0D0A0F',
      cardBackground: '#141116',
      text: '#B6DBDA',
      extra: '#504A78',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hazy Balcony',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AA644D',
      primaryShadow: '#88503E',
      accent: '#72403B',
      accentShadow: '#5B332F',
      background: '#F5E9BF',
      cardBackground: '#E9DDB5',
      text: '#161317',
      extra: '#2C262D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B77B68',
      primaryShadow: '#AA644D',
      accent: '#875D58',
      accentShadow: '#72403B',
      background: '#161317',
      cardBackground: '#1F1C20',
      text: '#F5E9BF',
      extra: '#413C42',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Minimalist Mesa',
    tags: ['warm', 'curated'],
    light: {
      primary: '#7B505C',
      primaryShadow: '#62404A',
      accent: '#322623',
      accentShadow: '#281E1C',
      background: '#E4D6CF',
      cardBackground: '#D9CBC5',
      text: '#322623',
      extra: '#9B9880',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8F6A74',
      primaryShadow: '#7B505C',
      accent: '#514744',
      accentShadow: '#322623',
      background: '#0C0A0F',
      cardBackground: '#131116',
      text: '#E4D6CF',
      extra: '#A5A28D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Mesa',
    tags: ['dark', 'curated'],
    light: {
      primary: '#4D7D65',
      primaryShadow: '#3E6451',
      accent: '#593A5F',
      accentShadow: '#472E4C',
      background: '#E3E7E5',
      cardBackground: '#DADEDC',
      text: '#3B252E',
      extra: '#D1ADA1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#68917C',
      primaryShadow: '#4D7D65',
      accent: '#725877',
      accentShadow: '#593A5F',
      background: '#0A0C0F',
      cardBackground: '#111316',
      text: '#D1ADA1',
      extra: '#D6B5AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Forest',
    tags: ['curated'],
    light: {
      primary: '#D86830',
      primaryShadow: '#AD5326',
      accent: '#B22741',
      accentShadow: '#8E1F34',
      background: '#E2E2E2',
      cardBackground: '#D7D7D7',
      text: '#1F1723',
      extra: '#1F1723',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE7F4F',
      primaryShadow: '#D86830',
      accent: '#BE475E',
      accentShadow: '#B22741',
      background: '#1F1723',
      cardBackground: '#28202C',
      text: '#E2E2E2',
      extra: '#352E39',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Azure Study',
    tags: ['curated'],
    light: {
      primary: '#00A3DA',
      primaryShadow: '#0082AE',
      accent: '#009247',
      accentShadow: '#007539',
      background: '#F4F4F4',
      cardBackground: '#E8E8E8',
      text: '#000000',
      extra: '#656766',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B1E0',
      primaryShadow: '#00A3DA',
      accent: '#26A263',
      accentShadow: '#009247',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F4F4F4',
      extra: '#747675',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vivid Rain',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D58B39',
      primaryShadow: '#AA6F2E',
      accent: '#B55B39',
      accentShadow: '#91492E',
      background: '#EEE8E1',
      cardBackground: '#E4DFD8',
      text: '#272223',
      extra: '#F2D3AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DB9C57',
      primaryShadow: '#D58B39',
      accent: '#C07457',
      accentShadow: '#B55B39',
      background: '#272223',
      cardBackground: '#302B2C',
      text: '#F2D3AC',
      extra: '#F3D7B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Olive Mirage',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#7100B5',
      primaryShadow: '#5A0091',
      accent: '#426FFF',
      accentShadow: '#3559CC',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#312D6C',
      extra: '#312D6C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8626C0',
      primaryShadow: '#7100B5',
      accent: '#5E85FF',
      accentShadow: '#426FFF',
      background: '#0B0811',
      cardBackground: '#120F18',
      text: '#FFFFFF',
      extra: '#46427B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Winter Studio',
    tags: ['warm', 'curated'],
    light: {
      primary: '#726D7D',
      primaryShadow: '#5B5764',
      accent: '#393B33',
      accentShadow: '#2E2F29',
      background: '#E8D3DF',
      cardBackground: '#DCC8D4',
      text: '#393B33',
      extra: '#E8D3DF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#878391',
      primaryShadow: '#726D7D',
      accent: '#575852',
      accentShadow: '#393B33',
      background: '#0B0B10',
      cardBackground: '#121217',
      text: '#E8D3DF',
      extra: '#EAD7E2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Cloud',
    tags: ['curated'],
    light: {
      primary: '#D56247',
      primaryShadow: '#AA4E39',
      accent: '#59160E',
      accentShadow: '#47120B',
      background: '#DADDEE',
      cardBackground: '#CFD2E2',
      text: '#0C0F32',
      extra: '#DADDEE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DB7A63',
      primaryShadow: '#D56247',
      accent: '#723932',
      accentShadow: '#59160E',
      background: '#0C0F32',
      cardBackground: '#16193A',
      text: '#DADDEE',
      extra: '#DEE0F0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silk Island',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C78539',
      primaryShadow: '#9F6A2E',
      accent: '#B74132',
      accentShadow: '#923428',
      background: '#FFF1A9',
      cardBackground: '#F2E5A1',
      text: '#120E23',
      extra: '#AEA47E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF9757',
      primaryShadow: '#C78539',
      accent: '#C25E51',
      accentShadow: '#B74132',
      background: '#120E23',
      cardBackground: '#1B182C',
      text: '#FFF1A9',
      extra: '#B6AD8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teal Loft',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#CD7A3F',
      primaryShadow: '#A46232',
      accent: '#815934',
      accentShadow: '#67472A',
      background: '#EDE7E2',
      cardBackground: '#E4DED9',
      text: '#0A1C19',
      extra: '#7BDCE4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D58E5C',
      primaryShadow: '#CD7A3F',
      accent: '#947252',
      accentShadow: '#815934',
      background: '#0A1C19',
      cardBackground: '#142522',
      text: '#7BDCE4',
      extra: '#88E0E7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Dream',
    tags: ['curated'],
    light: {
      primary: '#E57239',
      primaryShadow: '#B75B2E',
      accent: '#257C49',
      accentShadow: '#1E633A',
      background: '#FFFFF2',
      cardBackground: '#F2F2E6',
      text: '#07000E',
      extra: '#330E22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E98757',
      primaryShadow: '#E57239',
      accent: '#469064',
      accentShadow: '#257C49',
      background: '#07000E',
      cardBackground: '#110A18',
      text: '#FFFFF2',
      extra: '#472638',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Spire',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#B45102',
      primaryShadow: '#904102',
      accent: '#8F2100',
      accentShadow: '#721A00',
      background: '#EFEDE9',
      cardBackground: '#E3E1DD',
      text: '#201114',
      extra: '#30171A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF6B28',
      primaryShadow: '#B45102',
      accent: '#A04226',
      accentShadow: '#8F2100',
      background: '#201114',
      cardBackground: '#291B1D',
      text: '#EFEDE9',
      extra: '#452E31',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Thicket',
    tags: ['cool', 'curated'],
    light: {
      primary: '#507DBA',
      primaryShadow: '#406495',
      accent: '#5ACCA0',
      accentShadow: '#48A380',
      background: '#E5F7D2',
      cardBackground: '#DAEBC8',
      text: '#1A1A1A',
      extra: '#564756',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6A91C4',
      primaryShadow: '#507DBA',
      accent: '#73D4AE',
      accentShadow: '#5ACCA0',
      background: '#0A0C12',
      cardBackground: '#111319',
      text: '#E5F7D2',
      extra: '#675967',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Hotspring',
    tags: ['bright', 'curated'],
    light: {
      primary: '#E64270',
      primaryShadow: '#B8355A',
      accent: '#64C1BD',
      accentShadow: '#509A97',
      background: '#E3E6E8',
      cardBackground: '#D8DBDC',
      text: '#1A1A1A',
      extra: '#EAD762',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EA5E85',
      primaryShadow: '#E64270',
      accent: '#7BCAC7',
      accentShadow: '#64C1BD',
      background: '#0F0A0F',
      cardBackground: '#161116',
      text: '#E3E6E8',
      extra: '#ECDB72',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Navy Bastion',
    tags: ['warm', 'curated'],
    light: {
      primary: '#3870BE',
      primaryShadow: '#2D5A98',
      accent: '#7A1338',
      accentShadow: '#620F2D',
      background: '#E1E6EC',
      cardBackground: '#D8DDE3',
      text: '#160712',
      extra: '#CBD1BE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5685C8',
      primaryShadow: '#3870BE',
      accent: '#8E3656',
      accentShadow: '#7A1338',
      background: '#160712',
      cardBackground: '#1F111B',
      text: '#CBD1BE',
      extra: '#D0D6C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Contemporary Bastion',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AE6138',
      primaryShadow: '#8B4E2D',
      accent: '#D07A47',
      accentShadow: '#A66239',
      background: '#FFECBD',
      cardBackground: '#F2E0B4',
      text: '#362719',
      extra: '#FFDB8F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BA7956',
      primaryShadow: '#AE6138',
      accent: '#D78E63',
      accentShadow: '#D07A47',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#FFECBD',
      extra: '#FFDF9A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Volcano',
    tags: ['dark', 'curated'],
    light: {
      primary: '#01141A',
      primaryShadow: '#011015',
      accent: '#703A28',
      accentShadow: '#5A2E20',
      background: '#DDDEDF',
      cardBackground: '#D4D5D6',
      text: '#01141A',
      extra: '#92503F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#27373C',
      primaryShadow: '#01141A',
      accent: '#855848',
      accentShadow: '#703A28',
      background: '#01141A',
      cardBackground: '#0B1D23',
      text: '#CFBC95',
      extra: '#9D6252',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Stone',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#A2423A',
      primaryShadow: '#82352E',
      accent: '#4B372C',
      accentShadow: '#3C2C23',
      background: '#EAE2E1',
      cardBackground: '#E1D9D8',
      text: '#181322',
      extra: '#CDC1A7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B05E58',
      primaryShadow: '#A2423A',
      accent: '#66554C',
      accentShadow: '#4B372C',
      background: '#181322',
      cardBackground: '#211C2B',
      text: '#CDC1A7',
      extra: '#D2C7B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Ravine',
    tags: ['bright', 'curated'],
    light: {
      primary: '#14E1FF',
      primaryShadow: '#10B4CC',
      accent: '#5C4BFF',
      accentShadow: '#4A3CCC',
      background: '#FDFFF2',
      cardBackground: '#F0F2E6',
      text: '#1A1A1A',
      extra: '#FF7AD6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#37E6FF',
      primaryShadow: '#14E1FF',
      accent: '#7466FF',
      accentShadow: '#5C4BFF',
      background: '#090F14',
      cardBackground: '#10161B',
      text: '#FDFFF2',
      extra: '#FF87DA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Study',
    tags: ['dark', 'curated'],
    light: {
      primary: '#0FA10D',
      primaryShadow: '#0C810A',
      accent: '#64C224',
      accentShadow: '#509B1D',
      background: '#DEEADE',
      cardBackground: '#D5E1D5',
      text: '#091837',
      extra: '#097938',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#33AF31',
      primaryShadow: '#0FA10D',
      accent: '#7BCB45',
      accentShadow: '#64C224',
      background: '#091837',
      cardBackground: '#13213F',
      text: '#E1D483',
      extra: '#22864C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lasting Temple',
    tags: ['warm', 'curated'],
    light: {
      primary: '#8F853C',
      primaryShadow: '#726A30',
      accent: '#D0A654',
      accentShadow: '#A68543',
      background: '#F7E4CD',
      cardBackground: '#EBD9C3',
      text: '#403639',
      extra: '#954F4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A09759',
      primaryShadow: '#8F853C',
      accent: '#D7B36E',
      accentShadow: '#D0A654',
      background: '#0C0C0E',
      cardBackground: '#131315',
      text: '#F7E4CD',
      extra: '#A0615F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Spirit',
    tags: ['curated'],
    light: {
      primary: '#E84545',
      primaryShadow: '#BA3737',
      accent: '#9E2083',
      accentShadow: '#7E1A69',
      background: '#FFF7FF',
      cardBackground: '#F2EBF2',
      text: '#1B112C',
      extra: '#413047',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EB6161',
      primaryShadow: '#E84545',
      accent: '#AD4196',
      accentShadow: '#9E2083',
      background: '#1B112C',
      cardBackground: '#241B34',
      text: '#FFF7FF',
      extra: '#544559',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Moor',
    tags: ['curated'],
    light: {
      primary: '#E97B21',
      primaryShadow: '#BA621A',
      accent: '#12916B',
      accentShadow: '#0E7456',
      background: '#FFF7E9',
      cardBackground: '#F2EBDD',
      text: '#2C0008',
      extra: '#0B2458',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC8F42',
      primaryShadow: '#E97B21',
      accent: '#36A281',
      accentShadow: '#12916B',
      background: '#2C0008',
      cardBackground: '#340A12',
      text: '#FFF7E9',
      extra: '#233A69',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Mosaic',
    tags: ['curated'],
    light: {
      primary: '#AC8A71',
      primaryShadow: '#8A6E5A',
      accent: '#4D6476',
      accentShadow: '#3E505E',
      background: '#EEECDE',
      cardBackground: '#E2E0D3',
      text: '#1F1A24',
      extra: '#422C33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B89C86',
      primaryShadow: '#AC8A71',
      accent: '#687B8B',
      accentShadow: '#4D6476',
      background: '#1F1A24',
      cardBackground: '#28232D',
      text: '#EEECDE',
      extra: '#554147',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Harbor',
    tags: ['cool', 'curated'],
    light: {
      primary: '#2668C2',
      primaryShadow: '#1E539B',
      accent: '#419BC9',
      accentShadow: '#347CA1',
      background: '#E0E5EC',
      cardBackground: '#D7DCE3',
      text: '#3C004E',
      extra: '#3C004E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#477FCB',
      primaryShadow: '#2668C2',
      accent: '#5EAAD1',
      accentShadow: '#419BC9',
      background: '#3C004E',
      cardBackground: '#440A55',
      text: '#76FFD6',
      extra: '#501A60',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Emerald Monument',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#E95050',
      primaryShadow: '#BA4040',
      accent: '#A43838',
      accentShadow: '#832D2D',
      background: '#FFD7D7',
      cardBackground: '#F2CCCC',
      text: '#191E23',
      extra: '#191E23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC6A6A',
      primaryShadow: '#E95050',
      accent: '#B25656',
      accentShadow: '#A43838',
      background: '#191E23',
      cardBackground: '#22272C',
      text: '#FFD7D7',
      extra: '#303539',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Twilight',
    tags: ['curated'],
    light: {
      primary: '#F04181',
      primaryShadow: '#C03467',
      accent: '#981FAB',
      accentShadow: '#7A1989',
      background: '#F0FAC8',
      cardBackground: '#E4EEBE',
      text: '#07102E',
      extra: '#431480',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F25E94',
      primaryShadow: '#F04181',
      accent: '#A741B8',
      accentShadow: '#981FAB',
      background: '#07102E',
      cardBackground: '#111A36',
      text: '#F0FAC8',
      extra: '#562C8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Copse',
    tags: ['dark', 'curated'],
    light: {
      primary: '#DF803E',
      primaryShadow: '#B26632',
      accent: '#B82D46',
      accentShadow: '#932438',
      background: '#EFE7E2',
      cardBackground: '#E5DED9',
      text: '#0D0812',
      extra: '#162C54',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E4935B',
      primaryShadow: '#DF803E',
      accent: '#C34D62',
      accentShadow: '#B82D46',
      background: '#0D0812',
      cardBackground: '#17121B',
      text: '#EDCE5E',
      extra: '#2D4165',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ice Vision',
    tags: ['bright', 'curated'],
    light: {
      primary: '#EEB9C7',
      primaryShadow: '#BE949F',
      accent: '#555568',
      accentShadow: '#444453',
      background: '#F3EDED',
      cardBackground: '#E7E1E1',
      text: '#1A1A1A',
      extra: '#8E9191',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F1C4CF',
      primaryShadow: '#EEB9C7',
      accent: '#6F6F7F',
      accentShadow: '#555568',
      background: '#0F0E12',
      cardBackground: '#161519',
      text: '#F3EDED',
      extra: '#999C9C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Flame Vineyard',
    tags: ['cool', 'curated'],
    light: {
      primary: '#001B2E',
      primaryShadow: '#001625',
      accent: '#2D757E',
      accentShadow: '#245E65',
      background: '#EEFDED',
      cardBackground: '#E2F0E1',
      text: '#001B2E',
      extra: '#9A7BBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#263D4D',
      primaryShadow: '#001B2E',
      accent: '#4D8A91',
      accentShadow: '#2D757E',
      background: '#001B2E',
      cardBackground: '#0A2436',
      text: '#EEFDED',
      extra: '#A488C3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shine Bloom',
    tags: ['curated'],
    light: {
      primary: '#C55650',
      primaryShadow: '#9E4540',
      accent: '#8A2E3F',
      accentShadow: '#6E2532',
      background: '#EDE4E3',
      cardBackground: '#E4DBDA',
      text: '#2A202A',
      extra: '#D9D3D9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CE6F6A',
      primaryShadow: '#C55650',
      accent: '#9C4D5C',
      accentShadow: '#8A2E3F',
      background: '#2A202A',
      cardBackground: '#332933',
      text: '#D9D3D9',
      extra: '#DDD7DD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ruby Battlement',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A95A3F',
      primaryShadow: '#874832',
      accent: '#EDBB70',
      accentShadow: '#BE965A',
      background: '#F8EEBF',
      cardBackground: '#ECE2B5',
      text: '#242828',
      extra: '#242828',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B6735C',
      primaryShadow: '#A95A3F',
      accent: '#F0C585',
      accentShadow: '#EDBB70',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#F8EEBF',
      extra: '#3A3E3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Digital Island',
    tags: ['bright', 'curated'],
    light: {
      primary: '#92199D',
      primaryShadow: '#75147E',
      accent: '#F25E93',
      accentShadow: '#C24B76',
      background: '#FBFDB6',
      cardBackground: '#EEF0AD',
      text: '#92199D',
      extra: '#8EABC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A23CAC',
      primaryShadow: '#92199D',
      accent: '#F476A3',
      accentShadow: '#F25E93',
      background: '#0C0911',
      cardBackground: '#131018',
      text: '#FBFDB6',
      extra: '#99B3CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Synthetic Savanna',
    tags: ['dark', 'curated'],
    light: {
      primary: '#ED233D',
      primaryShadow: '#BE1C31',
      accent: '#810F74',
      accentShadow: '#670C5D',
      background: '#F0E0E2',
      cardBackground: '#E6D7D9',
      text: '#0A0219',
      extra: '#360748',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F0445A',
      primaryShadow: '#ED233D',
      accent: '#943389',
      accentShadow: '#810F74',
      background: '#0A0219',
      cardBackground: '#140C22',
      text: '#F0F0F0',
      extra: '#4A205A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Moon',
    tags: ['bright', 'curated'],
    light: {
      primary: '#A776C1',
      primaryShadow: '#865E9A',
      accent: '#7BE098',
      accentShadow: '#62B37A',
      background: '#FFFF8F',
      cardBackground: '#F2F288',
      text: '#1A1A1A',
      extra: '#849AD8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B48BCA',
      primaryShadow: '#A776C1',
      accent: '#8FE5A7',
      accentShadow: '#7BE098',
      background: '#0D0C12',
      cardBackground: '#141319',
      text: '#FFFF8F',
      extra: '#90A4DC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Phosphor Gradient',
    tags: ['curated'],
    light: {
      primary: '#339CA3',
      primaryShadow: '#297D82',
      accent: '#BB9A3E',
      accentShadow: '#967B32',
      background: '#F4F5EF',
      cardBackground: '#E8E9E3',
      text: '#191023',
      extra: '#F8C7A4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#52ABB1',
      primaryShadow: '#339CA3',
      accent: '#C5A95B',
      accentShadow: '#BB9A3E',
      background: '#191023',
      cardBackground: '#221A2C',
      text: '#F4F5EF',
      extra: '#F9CDAD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy Leaf',
    tags: ['curated'],
    light: {
      primary: '#C17B5C',
      primaryShadow: '#9A624A',
      accent: '#85444A',
      accentShadow: '#6A363B',
      background: '#DFE6E0',
      cardBackground: '#D4DBD5',
      text: '#222228',
      extra: '#DFE6E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CA8F74',
      primaryShadow: '#C17B5C',
      accent: '#976065',
      accentShadow: '#85444A',
      background: '#222228',
      cardBackground: '#2B2B31',
      text: '#DFE6E0',
      extra: '#E2E9E3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Matte Gorge',
    tags: ['curated'],
    light: {
      primary: '#00C908',
      primaryShadow: '#00A106',
      accent: '#0066FF',
      accentShadow: '#0052CC',
      background: '#FFFCFF',
      cardBackground: '#F2EFF2',
      text: '#000000',
      extra: '#9F1211',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26D12D',
      primaryShadow: '#00C908',
      accent: '#267DFF',
      accentShadow: '#0066FF',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFCFF',
      extra: '#A92A29',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystic Woodland',
    tags: ['curated'],
    light: {
      primary: '#00B7BD',
      primaryShadow: '#009297',
      accent: '#008B9E',
      accentShadow: '#006F7E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#3D3B38',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C2C7',
      primaryShadow: '#00B7BD',
      accent: '#269CAD',
      accentShadow: '#008B9E',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#504F4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cotton Tower',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#943C0C',
      primaryShadow: '#76300A',
      accent: '#5C200A',
      accentShadow: '#4A1A08',
      background: '#E9E2DE',
      cardBackground: '#E0D9D5',
      text: '#000000',
      extra: '#6B6974',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A45930',
      primaryShadow: '#943C0C',
      accent: '#74412F',
      accentShadow: '#5C200A',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F0F0F0',
      extra: '#7A7882',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vibrant Harbor',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#D35600',
      primaryShadow: '#A94500',
      accent: '#FED018',
      accentShadow: '#CBA613',
      background: '#EEE4DD',
      cardBackground: '#E4DBD4',
      text: '#0D0405',
      extra: '#5E1210',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA6F26',
      primaryShadow: '#D35600',
      accent: '#FED73B',
      accentShadow: '#FED018',
      background: '#0D0405',
      cardBackground: '#170E0F',
      text: '#FED018',
      extra: '#6E2A28',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Synthetic Lake',
    tags: ['cool', 'curated'],
    light: {
      primary: '#409F42',
      primaryShadow: '#337F35',
      accent: '#8FD345',
      accentShadow: '#72A937',
      background: '#CEF1FF',
      cardBackground: '#C4E5F2',
      text: '#000000',
      extra: '#1E405B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5DAD5E',
      primaryShadow: '#409F42',
      accent: '#A0DA61',
      accentShadow: '#8FD345',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#CEF1FF',
      extra: '#35536B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Altar',
    tags: ['curated'],
    light: {
      primary: '#E43254',
      primaryShadow: '#B62843',
      accent: '#3C566C',
      accentShadow: '#304556',
      background: '#D4E2CC',
      cardBackground: '#C9D7C2',
      text: '#17152E',
      extra: '#84AEA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E8516E',
      primaryShadow: '#E43254',
      accent: '#596F82',
      accentShadow: '#3C566C',
      background: '#17152E',
      cardBackground: '#201E36',
      text: '#D4E2CC',
      extra: '#90B6AD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Foyer',
    tags: ['warm', 'curated'],
    light: {
      primary: '#2C0016',
      primaryShadow: '#230012',
      accent: '#4F4E80',
      accentShadow: '#3F3E66',
      background: '#E0DDDF',
      cardBackground: '#D7D4D6',
      text: '#2C0016',
      extra: '#E4CBBF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4C2639',
      primaryShadow: '#2C0016',
      accent: '#696993',
      accentShadow: '#4F4E80',
      background: '#2C0016',
      cardBackground: '#340A1F',
      text: '#E4CBBF',
      extra: '#E7D0C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Harbor',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FF924F',
      primaryShadow: '#CC753F',
      accent: '#FFD191',
      accentShadow: '#CCA774',
      background: '#F1E8E3',
      cardBackground: '#E7DFDA',
      text: '#211E20',
      extra: '#211E20',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFA269',
      primaryShadow: '#FF924F',
      accent: '#FFD8A2',
      accentShadow: '#FFD191',
      background: '#211E20',
      cardBackground: '#2A2729',
      text: '#FFD191',
      extra: '#373536',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amber Magma',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#320F3B',
      primaryShadow: '#280C2F',
      accent: '#5766A1',
      accentShadow: '#465281',
      background: '#C6E5EA',
      cardBackground: '#BCDADE',
      text: '#320F3B',
      extra: '#C6E5EA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#513358',
      primaryShadow: '#320F3B',
      accent: '#707DAF',
      accentShadow: '#5766A1',
      background: '#320F3B',
      cardBackground: '#3A1943',
      text: '#C6E5EA',
      extra: '#CCE8EC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Pack',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A66916',
      primaryShadow: '#855412',
      accent: '#6A3C0B',
      accentShadow: '#553009',
      background: '#F3EDEA',
      cardBackground: '#E7E1DE',
      text: '#1A1A1A',
      extra: '#1A1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B38039',
      primaryShadow: '#A66916',
      accent: '#805930',
      accentShadow: '#6A3C0B',
      background: '#1A1A1A',
      cardBackground: '#232323',
      text: '#F3EDEA',
      extra: '#313131',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Smoke Nave',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#052A32',
      primaryShadow: '#042228',
      accent: '#204C48',
      accentShadow: '#1A3D3A',
      background: '#DDE0E1',
      cardBackground: '#D4D7D8',
      text: '#06080A',
      extra: '#184042',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2B4A51',
      primaryShadow: '#052A32',
      accent: '#416763',
      accentShadow: '#204C48',
      background: '#06080A',
      cardBackground: '#101214',
      text: '#F0F0F0',
      extra: '#2F5355',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Alpine Lake',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B47D58',
      primaryShadow: '#906446',
      accent: '#8C5C47',
      accentShadow: '#704A39',
      background: '#EBE7E4',
      cardBackground: '#E2DEDB',
      text: '#212529',
      extra: '#212529',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF9171',
      primaryShadow: '#B47D58',
      accent: '#9D7463',
      accentShadow: '#8C5C47',
      background: '#212529',
      cardBackground: '#2A2E32',
      text: '#EAD9A7',
      extra: '#373B3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amber Loft',
    tags: ['bright', 'curated'],
    light: {
      primary: '#25BCC6',
      primaryShadow: '#1E969E',
      accent: '#257D9B',
      accentShadow: '#1E647C',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#2F2556',
      extra: '#2F2556',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#46C6CF',
      primaryShadow: '#25BCC6',
      accent: '#4691AA',
      accentShadow: '#257D9B',
      background: '#090E12',
      cardBackground: '#101519',
      text: '#FFFFFF',
      extra: '#443B67',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vivid Glacier',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4192D9',
      primaryShadow: '#3475AE',
      accent: '#3670B3',
      accentShadow: '#2B5A8F',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#111126',
      extra: '#17174D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5EA2DF',
      primaryShadow: '#4192D9',
      accent: '#5485BE',
      accentShadow: '#3670B3',
      background: '#111126',
      cardBackground: '#1B1B2F',
      text: '#FFFFFF',
      extra: '#2E2E5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Sculpture',
    tags: ['curated'],
    light: {
      primary: '#2F729E',
      primaryShadow: '#265B7E',
      accent: '#EBA254',
      accentShadow: '#BC8243',
      background: '#FFF5D9',
      cardBackground: '#F2E9CE',
      text: '#191930',
      extra: '#191930',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4E87AD',
      primaryShadow: '#2F729E',
      accent: '#EEB06E',
      accentShadow: '#EBA254',
      background: '#191930',
      cardBackground: '#222238',
      text: '#FFF5D9',
      extra: '#303045',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blaze Shore',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#24B8A0',
      primaryShadow: '#1D9380',
      accent: '#26707A',
      accentShadow: '#1E5A62',
      background: '#FCF6E7',
      cardBackground: '#EFEADB',
      text: '#213756',
      extra: '#DC67B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#45C3AE',
      primaryShadow: '#24B8A0',
      accent: '#47858E',
      accentShadow: '#26707A',
      background: '#090E11',
      cardBackground: '#101518',
      text: '#FCF6E7',
      extra: '#E076C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Heart',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C13D37',
      primaryShadow: '#9A312C',
      accent: '#DC8254',
      accentShadow: '#B06843',
      background: '#FFF9E5',
      cardBackground: '#F2EDDA',
      text: '#0D0B0D',
      extra: '#99D4AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CA5A55',
      primaryShadow: '#C13D37',
      accent: '#E1956E',
      accentShadow: '#DC8254',
      background: '#0D0B0D',
      cardBackground: '#171517',
      text: '#FFF9E5',
      extra: '#A3D8B3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Mesa',
    tags: ['dark', 'curated'],
    light: {
      primary: '#CC1242',
      primaryShadow: '#A30E35',
      accent: '#F0745C',
      accentShadow: '#C05D4A',
      background: '#EDDEE2',
      cardBackground: '#E4D5D9',
      text: '#151C18',
      extra: '#094466',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D4365E',
      primaryShadow: '#CC1242',
      accent: '#F28974',
      accentShadow: '#F0745C',
      background: '#151C18',
      cardBackground: '#1E2521',
      text: '#F0F0F0',
      extra: '#225775',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silver Palette',
    tags: ['bright', 'curated'],
    light: {
      primary: '#7982EA',
      primaryShadow: '#6168BB',
      accent: '#EA8C79',
      accentShadow: '#BB7061',
      background: '#F7F9C4',
      cardBackground: '#EBEDBA',
      text: '#1A1A1A',
      extra: '#998276',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D95ED',
      primaryShadow: '#7982EA',
      accent: '#ED9D8D',
      accentShadow: '#EA8C79',
      background: '#0C0C13',
      cardBackground: '#13131A',
      text: '#F7F9C4',
      extra: '#A38F84',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hazy Prairie',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BD516D',
      primaryShadow: '#974157',
      accent: '#CB9368',
      accentShadow: '#A27653',
      background: '#FFFBDE',
      cardBackground: '#F2EED3',
      text: '#28242E',
      extra: '#F6C4BA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C76B83',
      primaryShadow: '#BD516D',
      accent: '#D3A37F',
      accentShadow: '#CB9368',
      background: '#0E0A0F',
      cardBackground: '#151116',
      text: '#FFFBDE',
      extra: '#F7CAC1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Star',
    tags: ['curated'],
    light: {
      primary: '#9AA57C',
      primaryShadow: '#7B8463',
      accent: '#4B564D',
      accentShadow: '#3C453E',
      background: '#E0E9C4',
      cardBackground: '#D5DDBA',
      text: '#252525',
      extra: '#252525',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A9B390',
      primaryShadow: '#9AA57C',
      accent: '#666F68',
      accentShadow: '#4B564D',
      background: '#252525',
      cardBackground: '#2E2E2E',
      text: '#E0E9C4',
      extra: '#3B3B3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Fire',
    tags: ['bright', 'curated'],
    light: {
      primary: '#CE976B',
      primaryShadow: '#A57956',
      accent: '#AD6D66',
      accentShadow: '#8A5752',
      background: '#F1E4FD',
      cardBackground: '#E5D9F0',
      text: '#1A1A1A',
      extra: '#AB6581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D5A781',
      primaryShadow: '#CE976B',
      accent: '#B9837D',
      accentShadow: '#AD6D66',
      background: '#0E0D0F',
      cardBackground: '#151416',
      text: '#F1E4FD',
      extra: '#B3748E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Windy Aura',
    tags: ['dark', 'curated'],
    light: {
      primary: '#090110',
      primaryShadow: '#07010D',
      accent: '#6F653D',
      accentShadow: '#595131',
      background: '#DEDDDE',
      cardBackground: '#D5D4D5',
      text: '#090110',
      extra: '#1A1E25',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2E2734',
      primaryShadow: '#090110',
      accent: '#857C5A',
      accentShadow: '#6F653D',
      background: '#090110',
      cardBackground: '#130B1A',
      text: '#F0F0F0',
      extra: '#31353B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Satin Seed',
    tags: ['curated'],
    light: {
      primary: '#04BF79',
      primaryShadow: '#039961',
      accent: '#008B12',
      accentShadow: '#006F0E',
      background: '#FCFCFC',
      cardBackground: '#EFEFEF',
      text: '#000000',
      extra: '#26232F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2AC98D',
      primaryShadow: '#04BF79',
      accent: '#269C36',
      accentShadow: '#008B12',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FCFCFC',
      extra: '#3C3944',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Synthetic Sanctuary',
    tags: ['cool', 'curated'],
    light: {
      primary: '#2CA53E',
      primaryShadow: '#238432',
      accent: '#8A1181',
      accentShadow: '#6E0E67',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#1F246A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4CB35B',
      primaryShadow: '#2CA53E',
      accent: '#9C3594',
      accentShadow: '#8A1181',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#353A79',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Twilight Desert',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#C75061',
      primaryShadow: '#9F404E',
      accent: '#FEA47D',
      accentShadow: '#CB8364',
      background: '#FEF7EF',
      cardBackground: '#F1EBE3',
      text: '#403037',
      extra: '#403037',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF6A79',
      primaryShadow: '#C75061',
      accent: '#FEB291',
      accentShadow: '#FEA47D',
      background: '#0E0A0F',
      cardBackground: '#151116',
      text: '#FEF7EF',
      extra: '#53454B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ancient Valley',
    tags: ['curated'],
    light: {
      primary: '#208800',
      primaryShadow: '#1A6D00',
      accent: '#FC4848',
      accentShadow: '#CA3A3A',
      background: '#FCFCFC',
      cardBackground: '#EFEFEF',
      text: '#000000',
      extra: '#68605C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#419A26',
      primaryShadow: '#208800',
      accent: '#FC6363',
      accentShadow: '#FC4848',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FCFCFC',
      extra: '#77706C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Sky',
    tags: ['curated'],
    light: {
      primary: '#00BBFF',
      primaryShadow: '#0096CC',
      accent: '#008BF5',
      accentShadow: '#006FC4',
      background: '#FFF3F0',
      cardBackground: '#F2E7E4',
      text: '#000221',
      extra: '#4D004C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C5FF',
      primaryShadow: '#00BBFF',
      accent: '#269CF7',
      accentShadow: '#008BF5',
      background: '#000221',
      cardBackground: '#0A0C2A',
      text: '#FFF3F0',
      extra: '#5F1A5E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Lava',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C98D3F',
      primaryShadow: '#A17132',
      accent: '#83443E',
      accentShadow: '#693632',
      background: '#F5EFE8',
      cardBackground: '#E9E3DC',
      text: '#17111A',
      extra: '#593A31',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D19E5C',
      primaryShadow: '#C98D3F',
      accent: '#96605B',
      accentShadow: '#83443E',
      background: '#17111A',
      cardBackground: '#201B23',
      text: '#F5EFE8',
      extra: '#6A4E46',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bronze Wave',
    tags: ['warm', 'curated'],
    light: {
      primary: '#00ABC4',
      primaryShadow: '#00899D',
      accent: '#00719D',
      accentShadow: '#005A7E',
      background: '#FCF5E8',
      cardBackground: '#EFE9DC',
      text: '#170F07',
      extra: '#484542',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B8CD',
      primaryShadow: '#00ABC4',
      accent: '#2686AC',
      accentShadow: '#00719D',
      background: '#170F07',
      cardBackground: '#201911',
      text: '#FCF5E8',
      extra: '#5A5855',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Shade',
    tags: ['curated'],
    light: {
      primary: '#582918',
      primaryShadow: '#462113',
      accent: '#FBAF71',
      accentShadow: '#C98C5A',
      background: '#FBE5C9',
      cardBackground: '#EEDABF',
      text: '#0A151B',
      extra: '#713A29',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#71493B',
      primaryShadow: '#582918',
      accent: '#FCBB86',
      accentShadow: '#FBAF71',
      background: '#0A151B',
      cardBackground: '#141E24',
      text: '#FBE5C9',
      extra: '#7F4E3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Mountain',
    tags: ['curated'],
    light: {
      primary: '#137056',
      primaryShadow: '#0F5A45',
      accent: '#044C4C',
      accentShadow: '#033D3D',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#D2D7D3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#36856F',
      primaryShadow: '#137056',
      accent: '#2A6767',
      accentShadow: '#044C4C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#D7DBD7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silver Dusk',
    tags: ['curated'],
    light: {
      primary: '#C74634',
      primaryShadow: '#9F382A',
      accent: '#100F24',
      accentShadow: '#0D0C1D',
      background: '#EDEADA',
      cardBackground: '#E1DECF',
      text: '#100F24',
      extra: '#5D8D60',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF6252',
      primaryShadow: '#C74634',
      accent: '#343345',
      accentShadow: '#100F24',
      background: '#100F24',
      cardBackground: '#1A192D',
      text: '#EDEADA',
      extra: '#6D9870',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silver Waterfall',
    tags: ['bright', 'curated'],
    light: {
      primary: '#939ADF',
      primaryShadow: '#767BB2',
      accent: '#EFA9C5',
      accentShadow: '#BF879E',
      background: '#E9E9EF',
      cardBackground: '#E0E0E5',
      text: '#1A1A1A',
      extra: '#F9C5C4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A3A9E4',
      primaryShadow: '#939ADF',
      accent: '#F1B6CE',
      accentShadow: '#EFA9C5',
      background: '#0C0D13',
      cardBackground: '#13141A',
      text: '#F9C5C4',
      extra: '#FACBCA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Basin',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C75F56',
      primaryShadow: '#9F4C45',
      accent: '#E69577',
      accentShadow: '#B8775F',
      background: '#E7DCDC',
      cardBackground: '#DBD1D1',
      text: '#342529',
      extra: '#9FB265',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF776F',
      primaryShadow: '#C75F56',
      accent: '#EAA58B',
      accentShadow: '#E69577',
      background: '#0E0B0F',
      cardBackground: '#151216',
      text: '#E7DCDC',
      extra: '#A9BA74',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Comet',
    tags: ['dark', 'curated'],
    light: {
      primary: '#BE794F',
      primaryShadow: '#98613F',
      accent: '#8D402F',
      accentShadow: '#713326',
      background: '#ECE6E3',
      cardBackground: '#E3DDDA',
      text: '#060B11',
      extra: '#182634',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C88D69',
      primaryShadow: '#BE794F',
      accent: '#9E5D4E',
      accentShadow: '#8D402F',
      background: '#060B11',
      cardBackground: '#10151B',
      text: '#E3B47A',
      extra: '#2F3C48',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Shade',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#941416',
      primaryShadow: '#761012',
      accent: '#350D24',
      accentShadow: '#2A0A1D',
      background: '#E9DEDF',
      cardBackground: '#E0D5D6',
      text: '#1C0818',
      extra: '#4C1026',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A43739',
      primaryShadow: '#941416',
      accent: '#533145',
      accentShadow: '#350D24',
      background: '#1C0818',
      cardBackground: '#251221',
      text: '#F0F0F0',
      extra: '#5E283C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Western Nebula',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4B849A',
      primaryShadow: '#3C6A7B',
      accent: '#1F285D',
      accentShadow: '#19204A',
      background: '#ECF2CB',
      cardBackground: '#E0E6C1',
      text: '#1F285D',
      extra: '#98D8B1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6696A9',
      primaryShadow: '#4B849A',
      accent: '#414875',
      accentShadow: '#1F285D',
      background: '#0A0C11',
      cardBackground: '#111318',
      text: '#ECF2CB',
      extra: '#A2DCB9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Steppe',
    tags: ['curated'],
    light: {
      primary: '#00D6BA',
      primaryShadow: '#00AB95',
      accent: '#007080',
      accentShadow: '#005A66',
      background: '#FFFAD6',
      cardBackground: '#F2EECB',
      text: '#0D2357',
      extra: '#0D2357',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26DCC4',
      primaryShadow: '#00D6BA',
      accent: '#268593',
      accentShadow: '#007080',
      background: '#0D2357',
      cardBackground: '#172C5E',
      text: '#FFFAD6',
      extra: '#253968',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mellow Rampart',
    tags: ['curated'],
    light: {
      primary: '#C1660B',
      primaryShadow: '#9A5209',
      accent: '#E99507',
      accentShadow: '#BA7706',
      background: '#EBFF8E',
      cardBackground: '#DFF287',
      text: '#120E14',
      extra: '#312B30',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CA7D30',
      primaryShadow: '#C1660B',
      accent: '#ECA52C',
      accentShadow: '#E99507',
      background: '#120E14',
      cardBackground: '#1B181D',
      text: '#EBFF8E',
      extra: '#464045',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lasting Cove',
    tags: ['curated'],
    light: {
      primary: '#A98143',
      primaryShadow: '#876736',
      accent: '#9A5D40',
      accentShadow: '#7B4A33',
      background: '#EAE7E2',
      cardBackground: '#E1DED9',
      text: '#2F2A35',
      extra: '#2F2A35',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B6945F',
      primaryShadow: '#A98143',
      accent: '#A9755D',
      accentShadow: '#9A5D40',
      background: '#0D0C0E',
      cardBackground: '#141315',
      text: '#D9DBBA',
      extra: '#443F49',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shine Pack',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C27F6F',
      primaryShadow: '#9B6659',
      accent: '#CCA795',
      accentShadow: '#A38677',
      background: '#ECE7E6',
      cardBackground: '#E3DEDD',
      text: '#23232E',
      extra: '#23232E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CB9285',
      primaryShadow: '#C27F6F',
      accent: '#D4B4A5',
      accentShadow: '#CCA795',
      background: '#23232E',
      cardBackground: '#2C2C36',
      text: '#DED0C7',
      extra: '#393943',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Arid Ridge',
    tags: ['curated'],
    light: {
      primary: '#569BC9',
      primaryShadow: '#457CA1',
      accent: '#554C80',
      accentShadow: '#443D66',
      background: '#E4E9ED',
      cardBackground: '#DBE0E4',
      text: '#4C282F',
      extra: '#4C282F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6FAAD1',
      primaryShadow: '#569BC9',
      accent: '#6F6793',
      accentShadow: '#554C80',
      background: '#0B0D12',
      cardBackground: '#121419',
      text: '#D3D6B2',
      extra: '#5E3E44',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vivid Parlor',
    tags: ['curated'],
    light: {
      primary: '#22203C',
      primaryShadow: '#1B1A30',
      accent: '#87717D',
      accentShadow: '#6C5A64',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#090A12',
      extra: '#F0BBC2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#434159',
      primaryShadow: '#22203C',
      accent: '#998691',
      accentShadow: '#87717D',
      background: '#090A12',
      cardBackground: '#13141B',
      text: '#FFFFFF',
      extra: '#F2C2C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Misty Iceberg',
    tags: ['bright', 'curated'],
    light: {
      primary: '#D4577C',
      primaryShadow: '#AA4663',
      accent: '#4C64BA',
      accentShadow: '#3D5095',
      background: '#FFFDEB',
      cardBackground: '#F2F0DF',
      text: '#302459',
      extra: '#302459',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA7090',
      primaryShadow: '#D4577C',
      accent: '#677BC4',
      accentShadow: '#4C64BA',
      background: '#0E0B10',
      cardBackground: '#151217',
      text: '#FFFDEB',
      extra: '#453A6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy Lava',
    tags: ['dark', 'curated'],
    light: {
      primary: '#BE9585',
      primaryShadow: '#98776A',
      accent: '#7E5A64',
      accentShadow: '#654850',
      background: '#F3EBD4',
      cardBackground: '#E7DFC9',
      text: '#000000',
      extra: '#222728',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C8A597',
      primaryShadow: '#BE9585',
      accent: '#91737B',
      accentShadow: '#7E5A64',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F3EBD4',
      extra: '#383D3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shadowy Terrace',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CD2561',
      primaryShadow: '#A41E4E',
      accent: '#DE5568',
      accentShadow: '#B24453',
      background: '#FFFEF0',
      cardBackground: '#F2F1E4',
      text: '#251534',
      extra: '#251534',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D54679',
      primaryShadow: '#CD2561',
      accent: '#E36F7F',
      accentShadow: '#DE5568',
      background: '#251534',
      cardBackground: '#2E1E3C',
      text: '#FFFEF0',
      extra: '#3B2C48',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Charcoal Dream',
    tags: ['warm', 'curated'],
    light: {
      primary: '#833121',
      primaryShadow: '#69271A',
      accent: '#DE8D7D',
      accentShadow: '#B27164',
      background: '#E1D8CB',
      cardBackground: '#D6CDC1',
      text: '#0A0D0A',
      extra: '#E1D8CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#965042',
      primaryShadow: '#833121',
      accent: '#E39E91',
      accentShadow: '#DE8D7D',
      background: '#0A0D0A',
      cardBackground: '#141714',
      text: '#E1D8CB',
      extra: '#E4DCD0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Glade',
    tags: ['dark', 'curated'],
    light: {
      primary: '#B05621',
      primaryShadow: '#8D451A',
      accent: '#C9AD23',
      accentShadow: '#A18A1C',
      background: '#EBE4DF',
      cardBackground: '#E2DBD6',
      text: '#0E0C19',
      extra: '#9D1F2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BC6F42',
      primaryShadow: '#B05621',
      accent: '#D1B944',
      accentShadow: '#C9AD23',
      background: '#0E0C19',
      cardBackground: '#181622',
      text: '#D8E1A9',
      extra: '#A73544',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fog Cove',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#D78612',
      primaryShadow: '#AC6B0E',
      accent: '#EE0E64',
      accentShadow: '#BE0B50',
      background: '#FFF6F9',
      cardBackground: '#F2EAED',
      text: '#673146',
      extra: '#A16470',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DD9836',
      primaryShadow: '#D78612',
      accent: '#F1327B',
      accentShadow: '#EE0E64',
      background: '#0E0C0D',
      cardBackground: '#151314',
      text: '#FFF6F9',
      extra: '#AA747E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Satin Rampart',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#DE024E',
      primaryShadow: '#B2023E',
      accent: '#FC4E51',
      accentShadow: '#CA3E41',
      background: '#EFDDE3',
      cardBackground: '#E5D4DA',
      text: '#401B20',
      extra: '#FFAB89',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E32869',
      primaryShadow: '#DE024E',
      accent: '#FC696B',
      accentShadow: '#FC4E51',
      background: '#0F080E',
      cardBackground: '#160F15',
      text: '#FFAB89',
      extra: '#FFB395',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Luminous Comet',
    tags: ['curated'],
    light: {
      primary: '#E83562',
      primaryShadow: '#BA2A4E',
      accent: '#782C96',
      accentShadow: '#602378',
      background: '#DFE9F5',
      cardBackground: '#D4DDE9',
      text: '#0A080D',
      extra: '#697594',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EB537A',
      primaryShadow: '#E83562',
      accent: '#8C4CA6',
      accentShadow: '#782C96',
      background: '#0A080D',
      cardBackground: '#141217',
      text: '#DFE9F5',
      extra: '#78839F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Promenade',
    tags: ['curated'],
    light: {
      primary: '#B47139',
      primaryShadow: '#905A2E',
      accent: '#081431',
      accentShadow: '#061027',
      background: '#EBE6E1',
      cardBackground: '#E2DDD8',
      text: '#000000',
      extra: '#312441',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF8657',
      primaryShadow: '#B47139',
      accent: '#2D3750',
      accentShadow: '#081431',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#BDCACD',
      extra: '#463A54',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Aura',
    tags: ['curated'],
    light: {
      primary: '#9D6335',
      primaryShadow: '#7E4F2A',
      accent: '#622B1C',
      accentShadow: '#4E2216',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#4F2224',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AC7A53',
      primaryShadow: '#9D6335',
      accent: '#7A4B3E',
      accentShadow: '#622B1C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#61383A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Mind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#EF1A1A',
      primaryShadow: '#BF1515',
      accent: '#B18B8B',
      accentShadow: '#8E6F6F',
      background: '#F3D8D8',
      cardBackground: '#E7CDCD',
      text: '#1B1A1D',
      extra: '#AC1717',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F13C3C',
      primaryShadow: '#EF1A1A',
      accent: '#BD9C9C',
      accentShadow: '#B18B8B',
      background: '#1B1A1D',
      cardBackground: '#242326',
      text: '#F3D8D8',
      extra: '#B42E2E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Alpine Bastion',
    tags: ['curated'],
    light: {
      primary: '#8B2DDC',
      primaryShadow: '#6F24B0',
      accent: '#FF45E9',
      accentShadow: '#CC37BA',
      background: '#E8E0EE',
      cardBackground: '#DFD7E4',
      text: '#0A0A28',
      extra: '#351567',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C4DE1',
      primaryShadow: '#8B2DDC',
      accent: '#FF61EC',
      accentShadow: '#FF45E9',
      background: '#0A0A28',
      cardBackground: '#141431',
      text: '#FFC5A0',
      extra: '#492C76',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Permafrost',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D3AE21',
      primaryShadow: '#A98B1A',
      accent: '#1C1412',
      accentShadow: '#16100E',
      background: '#EEEBDF',
      cardBackground: '#E4E2D6',
      text: '#1C1412',
      extra: '#635650',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DABA42',
      primaryShadow: '#D3AE21',
      accent: '#3E3736',
      accentShadow: '#1C1412',
      background: '#1C1412',
      cardBackground: '#251D1B',
      text: '#D4C9C3',
      extra: '#736762',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Seed',
    tags: ['curated'],
    light: {
      primary: '#C56129',
      primaryShadow: '#9E4E21',
      accent: '#B41C39',
      accentShadow: '#90162E',
      background: '#E1E5D8',
      cardBackground: '#D6DACD',
      text: '#000000',
      extra: '#524C52',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CE7949',
      primaryShadow: '#C56129',
      accent: '#BF3E57',
      accentShadow: '#B41C39',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#E1E5D8',
      extra: '#635E63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noon Studio',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CE812C',
      primaryShadow: '#A56723',
      accent: '#653019',
      accentShadow: '#512614',
      background: '#EDE7E0',
      cardBackground: '#E4DED7',
      text: '#0E0705',
      extra: '#E7D99C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D5944C',
      primaryShadow: '#CE812C',
      accent: '#7C4F3C',
      accentShadow: '#653019',
      background: '#0E0705',
      cardBackground: '#18110F',
      text: '#E7D99C',
      extra: '#E9DDA6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rocky Altar',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#B87652',
      primaryShadow: '#935E42',
      accent: '#ACB965',
      accentShadow: '#8A9451',
      background: '#F5F29E',
      cardBackground: '#E9E696',
      text: '#1A1A1A',
      extra: '#774346',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C38B6C',
      primaryShadow: '#B87652',
      accent: '#B8C47C',
      accentShadow: '#ACB965',
      background: '#0E0C0E',
      cardBackground: '#151315',
      text: '#F5F29E',
      extra: '#855659',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vibrant Rapids',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A7609D',
      primaryShadow: '#864D7E',
      accent: '#6F3F7A',
      accentShadow: '#593262',
      background: '#E5DABF',
      cardBackground: '#DACFB5',
      text: '#3F1F32',
      extra: '#E5DABF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B478AC',
      primaryShadow: '#A7609D',
      accent: '#855C8E',
      accentShadow: '#6F3F7A',
      background: '#0D0B11',
      cardBackground: '#141218',
      text: '#E5DABF',
      extra: '#E8DEC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Winter Coast',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#823F3A',
      primaryShadow: '#68322E',
      accent: '#432015',
      accentShadow: '#361A11',
      background: '#E7E2E1',
      cardBackground: '#DED9D8',
      text: '#1C1009',
      extra: '#C0C188',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#955C58',
      primaryShadow: '#823F3A',
      accent: '#5F4138',
      accentShadow: '#432015',
      background: '#1C1009',
      cardBackground: '#251A13',
      text: '#C0C188',
      extra: '#C6C794',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fertile Earth',
    tags: ['curated'],
    light: {
      primary: '#CC702F',
      primaryShadow: '#A35A26',
      accent: '#952008',
      accentShadow: '#771A06',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#221F3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D4854E',
      primaryShadow: '#CC702F',
      accent: '#A5412D',
      accentShadow: '#952008',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#383551',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Grove',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#B15C34',
      primaryShadow: '#8E4A2A',
      accent: '#D58941',
      accentShadow: '#AA6E34',
      background: '#FFE257',
      cardBackground: '#F2D753',
      text: '#1A1C22',
      extra: '#1A1C22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BD7452',
      primaryShadow: '#B15C34',
      accent: '#DB9B5E',
      accentShadow: '#D58941',
      background: '#1A1C22',
      cardBackground: '#23252B',
      text: '#FFE257',
      extra: '#313338',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Galaxy',
    tags: ['curated'],
    light: {
      primary: '#2152A5',
      primaryShadow: '#1A4284',
      accent: '#DC6250',
      accentShadow: '#B04E40',
      background: '#DFE3EA',
      cardBackground: '#D6DAE1',
      text: '#272A32',
      extra: '#DEADA5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#426CB3',
      primaryShadow: '#2152A5',
      accent: '#E17A6A',
      accentShadow: '#DC6250',
      background: '#090A11',
      cardBackground: '#101118',
      text: '#FFD183',
      extra: '#E1B5AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Crater',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A54E14',
      primaryShadow: '#843E10',
      accent: '#860210',
      accentShadow: '#6B020D',
      background: '#EBEAB2',
      cardBackground: '#DFDEA9',
      text: '#2D0E03',
      extra: '#7F3A28',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B36937',
      primaryShadow: '#A54E14',
      accent: '#982834',
      accentShadow: '#860210',
      background: '#2D0E03',
      cardBackground: '#35180D',
      text: '#EBEAB2',
      extra: '#8C4E3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cotton Balcony',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BE772B',
      primaryShadow: '#985F22',
      accent: '#DE9E41',
      accentShadow: '#B27E34',
      background: '#EBEDE9',
      cardBackground: '#DFE1DD',
      text: '#090A14',
      extra: '#172038',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C88B4B',
      primaryShadow: '#BE772B',
      accent: '#E3AD5E',
      accentShadow: '#DE9E41',
      background: '#090A14',
      cardBackground: '#13141D',
      text: '#EBEDE9',
      extra: '#2E364C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oceanic Hue',
    tags: ['warm', 'curated'],
    light: {
      primary: '#2DA1C4',
      primaryShadow: '#24819D',
      accent: '#D01F6C',
      accentShadow: '#A61956',
      background: '#F7F1E4',
      cardBackground: '#EBE5D9',
      text: '#0F0D0F',
      extra: '#732050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4DAFCD',
      primaryShadow: '#2DA1C4',
      accent: '#D74182',
      accentShadow: '#D01F6C',
      background: '#0F0D0F',
      cardBackground: '#191719',
      text: '#F7F1E4',
      extra: '#813662',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eternal Aura',
    tags: ['curated'],
    light: {
      primary: '#D53BE3',
      primaryShadow: '#AA2FB6',
      accent: '#8E20A7',
      accentShadow: '#721A86',
      background: '#FCF2DE',
      cardBackground: '#EFE6D3',
      text: '#1E0B37',
      extra: '#45176E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DB58E7',
      primaryShadow: '#D53BE3',
      accent: '#9F41B4',
      accentShadow: '#8E20A7',
      background: '#1E0B37',
      cardBackground: '#27153F',
      text: '#FCF2DE',
      extra: '#582E7D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Soul',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#A06689',
      primaryShadow: '#80526E',
      accent: '#FF99A1',
      accentShadow: '#CC7A81',
      background: '#FFFFDB',
      cardBackground: '#F2F2D0',
      text: '#1A1A1A',
      extra: '#FFC5BB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AE7D9B',
      primaryShadow: '#A06689',
      accent: '#FFA8AF',
      accentShadow: '#FF99A1',
      background: '#0D0B10',
      cardBackground: '#141217',
      text: '#FFFFDB',
      extra: '#FFCBC2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Tundra',
    tags: ['curated'],
    light: {
      primary: '#BB863C',
      primaryShadow: '#966B30',
      accent: '#774126',
      accentShadow: '#5F341E',
      background: '#ECE8E2',
      cardBackground: '#E3DFD9',
      text: '#1D2B21',
      extra: '#1D2B21',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C59859',
      primaryShadow: '#BB863C',
      accent: '#8B5E47',
      accentShadow: '#774126',
      background: '#1D2B21',
      cardBackground: '#26332A',
      text: '#D2BDBD',
      extra: '#344037',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Futuristic Branch',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B45E4E',
      primaryShadow: '#904B3E',
      accent: '#CF8C52',
      accentShadow: '#A67042',
      background: '#F8E6D0',
      cardBackground: '#ECDBC6',
      text: '#200816',
      extra: '#BC8B96',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF7669',
      primaryShadow: '#B45E4E',
      accent: '#D69D6C',
      accentShadow: '#CF8C52',
      background: '#200816',
      cardBackground: '#29121F',
      text: '#F8E6D0',
      extra: '#C397A1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indigo Caldera',
    tags: ['curated'],
    light: {
      primary: '#A4628A',
      primaryShadow: '#834E6E',
      accent: '#D38595',
      accentShadow: '#A96A77',
      background: '#F0D7B1',
      cardBackground: '#E4CCA8',
      text: '#4D456D',
      extra: '#4D456D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B27A9C',
      primaryShadow: '#A4628A',
      accent: '#DA97A5',
      accentShadow: '#D38595',
      background: '#0D0B10',
      cardBackground: '#141217',
      text: '#F0D7B1',
      extra: '#5F587C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Vineyard',
    tags: ['cool', 'curated'],
    light: {
      primary: '#377D99',
      primaryShadow: '#2C647A',
      accent: '#1E4676',
      accentShadow: '#18385E',
      background: '#EEF3AA',
      cardBackground: '#E2E7A2',
      text: '#06041B',
      extra: '#431C3F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5591A8',
      primaryShadow: '#377D99',
      accent: '#40628B',
      accentShadow: '#1E4676',
      background: '#06041B',
      cardBackground: '#100E24',
      text: '#EEF3AA',
      extra: '#563352',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neo Hotspring',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C77331',
      primaryShadow: '#9F5C27',
      accent: '#933633',
      accentShadow: '#762B29',
      background: '#F7F6DB',
      cardBackground: '#EBEAD0',
      text: '#1D1819',
      extra: '#1D1819',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF8850',
      primaryShadow: '#C77331',
      accent: '#A35452',
      accentShadow: '#933633',
      background: '#1D1819',
      cardBackground: '#262122',
      text: '#F7F6DB',
      extra: '#342F30',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rust Clearing',
    tags: ['cool', 'curated'],
    light: {
      primary: '#37ADA2',
      primaryShadow: '#2C8A82',
      accent: '#5B29A6',
      accentShadow: '#492185',
      background: '#CBF5EF',
      cardBackground: '#C1E9E3',
      text: '#221529',
      extra: '#221529',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#55B9B0',
      primaryShadow: '#37ADA2',
      accent: '#7449B3',
      accentShadow: '#5B29A6',
      background: '#221529',
      cardBackground: '#2B1E32',
      text: '#CBF5EF',
      extra: '#382C3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shadowy Waterfall',
    tags: ['warm', 'curated'],
    light: {
      primary: '#7F2C44',
      primaryShadow: '#662336',
      accent: '#5E759C',
      accentShadow: '#4B5E7D',
      background: '#E7E0E2',
      cardBackground: '#DED7D9',
      text: '#3B252E',
      extra: '#D1ADA1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#924C60',
      primaryShadow: '#7F2C44',
      accent: '#768AAB',
      accentShadow: '#5E759C',
      background: '#0C090E',
      cardBackground: '#131015',
      text: '#D1ADA1',
      extra: '#D6B5AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Springs',
    tags: ['curated'],
    light: {
      primary: '#553800',
      primaryShadow: '#442D00',
      accent: '#8E5029',
      accentShadow: '#724021',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#4A4A4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6F5626',
      primaryShadow: '#553800',
      accent: '#9F6A49',
      accentShadow: '#8E5029',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#5C5C5C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Heart',
    tags: ['warm', 'curated'],
    light: {
      primary: '#8C593B',
      primaryShadow: '#70472F',
      accent: '#702F3E',
      accentShadow: '#5A2632',
      background: '#F0E0D6',
      cardBackground: '#E4D5CB',
      text: '#291E16',
      extra: '#291E16',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9D7258',
      primaryShadow: '#8C593B',
      accent: '#854E5B',
      accentShadow: '#702F3E',
      background: '#291E16',
      cardBackground: '#32271F',
      text: '#F0E0D6',
      extra: '#3E352D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Keep',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DA3467',
      primaryShadow: '#AE2A52',
      accent: '#FFA49A',
      accentShadow: '#CC837B',
      background: '#F1E0CD',
      cardBackground: '#E5D5C3',
      text: '#35333F',
      extra: '#F1E0CD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0527E',
      primaryShadow: '#DA3467',
      accent: '#FFB2A9',
      accentShadow: '#FFA49A',
      background: '#0F0A0F',
      cardBackground: '#161116',
      text: '#F1E0CD',
      extra: '#F2E3D2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Balmy Caldera',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#0F9595',
      primaryShadow: '#0C7777',
      accent: '#C1115A',
      accentShadow: '#9A0E48',
      background: '#DEE9E9',
      cardBackground: '#D5E0E0',
      text: '#0B001B',
      extra: '#53EBE4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#33A5A5',
      primaryShadow: '#0F9595',
      accent: '#CA3573',
      accentShadow: '#C1115A',
      background: '#0B001B',
      cardBackground: '#150A24',
      text: '#ECA6C0',
      extra: '#64EDE7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crisp Spectrum',
    tags: ['warm', 'curated'],
    light: {
      primary: '#2B9E62',
      primaryShadow: '#227E4E',
      accent: '#A61E49',
      accentShadow: '#85183A',
      background: '#E6E3D8',
      cardBackground: '#DBD8CD',
      text: '#150A2B',
      extra: '#E3AA71',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4BAD7A',
      primaryShadow: '#2B9E62',
      accent: '#B34064',
      accentShadow: '#A61E49',
      background: '#150A2B',
      cardBackground: '#1E1433',
      text: '#E6E3D8',
      extra: '#E6B37F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Gallery',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D60A40',
      primaryShadow: '#AB0833',
      accent: '#F77E69',
      accentShadow: '#C66554',
      background: '#FCD5AC',
      cardBackground: '#EFCAA3',
      text: '#140208',
      extra: '#FCD5AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DC2F5D',
      primaryShadow: '#D60A40',
      accent: '#F89180',
      accentShadow: '#F77E69',
      background: '#140208',
      cardBackground: '#1D0C12',
      text: '#FCD5AC',
      extra: '#FCD9B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Winter Wave',
    tags: ['cool', 'curated'],
    light: {
      primary: '#3A7BAA',
      primaryShadow: '#2E6288',
      accent: '#012036',
      accentShadow: '#011A2B',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#7D8FAE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#588FB7',
      primaryShadow: '#3A7BAA',
      accent: '#274154',
      accentShadow: '#012036',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#8A9AB6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Moss',
    tags: ['curated'],
    light: {
      primary: '#382B00',
      primaryShadow: '#2D2200',
      accent: '#E07AB3',
      accentShadow: '#B3628F',
      background: '#E1E0DD',
      cardBackground: '#D8D7D4',
      text: '#382B00',
      extra: '#716CB0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#564B26',
      primaryShadow: '#382B00',
      accent: '#E58EBE',
      accentShadow: '#E07AB3',
      background: '#0A090C',
      cardBackground: '#111013',
      text: '#FFD090',
      extra: '#7F7BB8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Blizzard',
    tags: ['curated'],
    light: {
      primary: '#B35447',
      primaryShadow: '#8F4339',
      accent: '#692E4B',
      accentShadow: '#54253C',
      background: '#E8DCD8',
      cardBackground: '#DCD1CD',
      text: '#111323',
      extra: '#374566',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BE6E63',
      primaryShadow: '#B35447',
      accent: '#804D66',
      accentShadow: '#692E4B',
      background: '#111323',
      cardBackground: '#1B1C2C',
      text: '#E8DCD8',
      extra: '#4B5875',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Timeless Courtyard',
    tags: ['curated'],
    light: {
      primary: '#B5671F',
      primaryShadow: '#915219',
      accent: '#963E1E',
      accentShadow: '#783218',
      background: '#F5F8FA',
      cardBackground: '#E9ECEE',
      text: '#0E0E1A',
      extra: '#2B1F4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C07E41',
      primaryShadow: '#B5671F',
      accent: '#A65B40',
      accentShadow: '#963E1E',
      background: '#0E0E1A',
      cardBackground: '#181823',
      text: '#F5F8FA',
      extra: '#40355F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Twilight Coast',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#99683D',
      primaryShadow: '#7A5331',
      accent: '#664930',
      accentShadow: '#523A26',
      background: '#E9E5E2',
      cardBackground: '#E0DCD9',
      text: '#332920',
      extra: '#CCA66E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A87F5A',
      primaryShadow: '#99683D',
      accent: '#7D644F',
      accentShadow: '#664930',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#F0F0F0',
      extra: '#D1AF7D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Granite Geyser',
    tags: ['curated'],
    light: {
      primary: '#3D0517',
      primaryShadow: '#310412',
      accent: '#78482A',
      accentShadow: '#603A22',
      background: '#D3EED3',
      cardBackground: '#C8E2C8',
      text: '#3D0517',
      extra: '#D3EED3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5A2B3A',
      primaryShadow: '#3D0517',
      accent: '#8C634A',
      accentShadow: '#78482A',
      background: '#3D0517',
      cardBackground: '#450F20',
      text: '#D3EED3',
      extra: '#D7F0D7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spring Bay',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B4543C',
      primaryShadow: '#904330',
      accent: '#C1824B',
      accentShadow: '#9A683C',
      background: '#EBE4E2',
      cardBackground: '#E2DBD9',
      text: '#0E0B1E',
      extra: '#1E233A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF6E59',
      primaryShadow: '#B4543C',
      accent: '#CA9566',
      accentShadow: '#C1824B',
      background: '#0E0B1E',
      cardBackground: '#181527',
      text: '#DCD8CA',
      extra: '#35394E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Grove',
    tags: ['curated'],
    light: {
      primary: '#A22FC9',
      primaryShadow: '#8226A1',
      accent: '#FF8B40',
      accentShadow: '#CC6F33',
      background: '#FFF4B8',
      cardBackground: '#F2E8AF',
      text: '#290143',
      extra: '#FF8B40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B04ED1',
      primaryShadow: '#A22FC9',
      accent: '#FF9C5D',
      accentShadow: '#FF8B40',
      background: '#290143',
      cardBackground: '#320B4B',
      text: '#FFF4B8',
      extra: '#FF9753',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Arid Storm',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#BD6E56',
      primaryShadow: '#975845',
      accent: '#8E4E4E',
      accentShadow: '#723E3E',
      background: '#FBFFF3',
      cardBackground: '#EEF2E7',
      text: '#352A2D',
      extra: '#A5839A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C7846F',
      primaryShadow: '#BD6E56',
      accent: '#9F6969',
      accentShadow: '#8E4E4E',
      background: '#0E0B0F',
      cardBackground: '#151216',
      text: '#FBFFF3',
      extra: '#AE8FA4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Clear Waterfall',
    tags: ['bright', 'curated'],
    light: {
      primary: '#B03E80',
      primaryShadow: '#8D3266',
      accent: '#4AEDFF',
      accentShadow: '#3BBECC',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1A1A1A',
      extra: '#FF8ACD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BC5B93',
      primaryShadow: '#B03E80',
      accent: '#65F0FF',
      accentShadow: '#4AEDFF',
      background: '#0D0A10',
      cardBackground: '#141117',
      text: '#FFFFFF',
      extra: '#FF96D2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Spirit',
    tags: ['cool', 'curated'],
    light: {
      primary: '#B441CC',
      primaryShadow: '#9034A3',
      accent: '#A32858',
      accentShadow: '#822046',
      background: '#D1F8FF',
      cardBackground: '#C7ECF2',
      text: '#04152B',
      extra: '#390947',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF5ED4',
      primaryShadow: '#B441CC',
      accent: '#B14871',
      accentShadow: '#A32858',
      background: '#04152B',
      cardBackground: '#0E1E33',
      text: '#D1F8FF',
      extra: '#4D2259',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Prairie',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D67654',
      primaryShadow: '#AB5E43',
      accent: '#974133',
      accentShadow: '#793429',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#100B2A',
      extra: '#2A213A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DC8B6E',
      primaryShadow: '#D67654',
      accent: '#A75E52',
      accentShadow: '#974133',
      background: '#100B2A',
      cardBackground: '#1A1533',
      text: '#FFFFFF',
      extra: '#3F374E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Gorge',
    tags: ['warm', 'curated'],
    light: {
      primary: '#8F5450',
      primaryShadow: '#724340',
      accent: '#B59766',
      accentShadow: '#917952',
      background: '#DEE6B8',
      cardBackground: '#D3DBAF',
      text: '#0B0D0A',
      extra: '#383730',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A06E6A',
      primaryShadow: '#8F5450',
      accent: '#C0A77D',
      accentShadow: '#B59766',
      background: '#0B0D0A',
      cardBackground: '#151714',
      text: '#DEE6B8',
      extra: '#4C4B45',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Earth',
    tags: ['bright', 'curated'],
    light: {
      primary: '#64B9CA',
      primaryShadow: '#5094A2',
      accent: '#674577',
      accentShadow: '#52375F',
      background: '#FFEBE5',
      cardBackground: '#F2DFDA',
      text: '#1A1A1A',
      extra: '#FFA3D6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7BC4D2',
      primaryShadow: '#64B9CA',
      accent: '#7E618B',
      accentShadow: '#674577',
      background: '#0B0E12',
      cardBackground: '#121519',
      text: '#FFEBE5',
      extra: '#FFACDA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Parlor',
    tags: ['curated'],
    light: {
      primary: '#C6801D',
      primaryShadow: '#9E6617',
      accent: '#B41E40',
      accentShadow: '#901833',
      background: '#EDE7DF',
      cardBackground: '#E4DED6',
      text: '#2F1E45',
      extra: '#2F1E45',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF933F',
      primaryShadow: '#C6801D',
      accent: '#BF405D',
      accentShadow: '#B41E40',
      background: '#0E0C0D',
      cardBackground: '#151314',
      text: '#A6C4BF',
      extra: '#443558',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Study',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#2F7571',
      primaryShadow: '#265E5A',
      accent: '#5A9470',
      accentShadow: '#48765A',
      background: '#E1E6E6',
      cardBackground: '#D8DDDD',
      text: '#2A2A2B',
      extra: '#2A2A2B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4E8A86',
      primaryShadow: '#2F7571',
      accent: '#73A485',
      accentShadow: '#5A9470',
      background: '#090C0F',
      cardBackground: '#101316',
      text: '#F0F0F0',
      extra: '#3F3F40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Winter Vine',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AD4030',
      primaryShadow: '#8A3326',
      accent: '#EF9E4E',
      accentShadow: '#BF7E3E',
      background: '#F6F2C3',
      cardBackground: '#EAE6B9',
      text: '#160D13',
      extra: '#31293E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B95D4F',
      primaryShadow: '#AD4030',
      accent: '#F1AD69',
      accentShadow: '#EF9E4E',
      background: '#160D13',
      cardBackground: '#1F171C',
      text: '#F6F2C3',
      extra: '#463E51',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teal Valley',
    tags: ['warm', 'curated'],
    light: {
      primary: '#2E2622',
      primaryShadow: '#251E1B',
      accent: '#77746F',
      accentShadow: '#5F5D59',
      background: '#E0E0E0',
      cardBackground: '#D7D7D7',
      text: '#2E2622',
      extra: '#CCCEC7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4D4743',
      primaryShadow: '#2E2622',
      accent: '#8B8985',
      accentShadow: '#77746F',
      background: '#09090D',
      cardBackground: '#101014',
      text: '#CCCEC7',
      extra: '#D1D3CD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Charcoal Stream',
    tags: ['curated'],
    light: {
      primary: '#AA6D3C',
      primaryShadow: '#885730',
      accent: '#744538',
      accentShadow: '#5D372D',
      background: '#C7F0D8',
      cardBackground: '#BDE4CD',
      text: '#312B25',
      extra: '#C7F0D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B78359',
      primaryShadow: '#AA6D3C',
      accent: '#896156',
      accentShadow: '#744538',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#C7F0D8',
      extra: '#CDF2DC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Misty Snowfall',
    tags: ['cool', 'curated'],
    light: {
      primary: '#D85452',
      primaryShadow: '#AD4342',
      accent: '#3A159C',
      accentShadow: '#2E117D',
      background: '#EEE4E3',
      cardBackground: '#E4DBDA',
      text: '#0F0D11',
      extra: '#93C7E3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE6E6C',
      primaryShadow: '#D85452',
      accent: '#5838AB',
      accentShadow: '#3A159C',
      background: '#0F0D11',
      cardBackground: '#19171B',
      text: '#EEB8B4',
      extra: '#9ECDE6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Petal',
    tags: ['cool', 'curated'],
    light: {
      primary: '#425D87',
      primaryShadow: '#354A6C',
      accent: '#7D95DE',
      accentShadow: '#6477B2',
      background: '#FBFEF9',
      cardBackground: '#EEF1ED',
      text: '#2E364D',
      extra: '#2E364D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E7599',
      primaryShadow: '#425D87',
      accent: '#91A5E3',
      accentShadow: '#7D95DE',
      background: '#0A0B10',
      cardBackground: '#111217',
      text: '#FBFEF9',
      extra: '#434A5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pearl Clearing',
    tags: ['warm', 'curated'],
    light: {
      primary: '#943054',
      primaryShadow: '#762643',
      accent: '#DB7BAE',
      accentShadow: '#AF628B',
      background: '#E9E1E4',
      cardBackground: '#E0D8DB',
      text: '#351522',
      extra: '#351522',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A44F6E',
      primaryShadow: '#943054',
      accent: '#E08FBA',
      accentShadow: '#DB7BAE',
      background: '#351522',
      cardBackground: '#3D1E2B',
      text: '#F5A1A1',
      extra: '#492C38',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Courtyard',
    tags: ['curated'],
    light: {
      primary: '#127687',
      primaryShadow: '#0E5E6C',
      accent: '#0A4684',
      accentShadow: '#08386A',
      background: '#E7E3C7',
      cardBackground: '#DBD8BD',
      text: '#181C38',
      extra: '#979596',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#368B99',
      primaryShadow: '#127687',
      accent: '#2F6296',
      accentShadow: '#0A4684',
      background: '#181C38',
      cardBackground: '#212540',
      text: '#E7E3C7',
      extra: '#A1A0A1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Tapestry',
    tags: ['bright', 'curated'],
    light: {
      primary: '#442D6E',
      primaryShadow: '#362458',
      accent: '#F0D063',
      accentShadow: '#C0A64F',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#442D6E',
      extra: '#D075B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#604D84',
      primaryShadow: '#442D6E',
      accent: '#F2D77A',
      accentShadow: '#F0D063',
      background: '#0A090F',
      cardBackground: '#111016',
      text: '#FFFFFF',
      extra: '#D583BE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Spire',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#34ACBA',
      primaryShadow: '#2A8A95',
      accent: '#2D6778',
      accentShadow: '#245260',
      background: '#FEFAD9',
      cardBackground: '#F1EECE',
      text: '#1A1A1A',
      extra: '#ADA98E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#52B8C4',
      primaryShadow: '#34ACBA',
      accent: '#4D7E8C',
      accentShadow: '#2D6778',
      background: '#0A0D12',
      cardBackground: '#111419',
      text: '#FEFAD9',
      extra: '#B5B299',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Ravine',
    tags: ['cool', 'curated'],
    light: {
      primary: '#007582',
      primaryShadow: '#005E68',
      accent: '#F2504B',
      accentShadow: '#C2403C',
      background: '#DDE4FF',
      cardBackground: '#D2D9F2',
      text: '#27234C',
      extra: '#27234C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#268A95',
      primaryShadow: '#007582',
      accent: '#F46A66',
      accentShadow: '#F2504B',
      background: '#080C10',
      cardBackground: '#0F1317',
      text: '#DDE4FF',
      extra: '#3D395E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystic Floe',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#7C3838',
      primaryShadow: '#632D2D',
      accent: '#B5A37E',
      accentShadow: '#918265',
      background: '#E7E1E1',
      cardBackground: '#DED8D8',
      text: '#3A3522',
      extra: '#3A3522',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#905656',
      primaryShadow: '#7C3838',
      accent: '#C0B191',
      accentShadow: '#B5A37E',
      background: '#0C0A0E',
      cardBackground: '#131115',
      text: '#F0F0F0',
      extra: '#4E4938',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ivory Garden',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FF8741',
      primaryShadow: '#CC6C34',
      accent: '#D04153',
      accentShadow: '#A63442',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#17111A',
      extra: '#FFCE95',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF995E',
      primaryShadow: '#FF8741',
      accent: '#D75E6D',
      accentShadow: '#D04153',
      background: '#17111A',
      cardBackground: '#201B23',
      text: '#FFFFFF',
      extra: '#FFD3A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jade Rain',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A6652B',
      primaryShadow: '#855122',
      accent: '#D9A443',
      accentShadow: '#AE8336',
      background: '#EAE5E0',
      cardBackground: '#E1DCD7',
      text: '#291E1A',
      extra: '#291E1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B37C4B',
      primaryShadow: '#A6652B',
      accent: '#DFB25F',
      accentShadow: '#D9A443',
      background: '#291E1A',
      cardBackground: '#322723',
      text: '#D4D3D1',
      extra: '#3E3531',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Clear Obelisk',
    tags: ['curated'],
    light: {
      primary: '#877459',
      primaryShadow: '#6C5D47',
      accent: '#534B3A',
      accentShadow: '#423C2E',
      background: '#EAE5E2',
      cardBackground: '#DEDAD7',
      text: '#191D1E',
      extra: '#191D1E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#998972',
      primaryShadow: '#877459',
      accent: '#6D6658',
      accentShadow: '#534B3A',
      background: '#191D1E',
      cardBackground: '#222627',
      text: '#EAE5E2',
      extra: '#303435',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Peak',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D7793F',
      primaryShadow: '#AC6132',
      accent: '#AB3229',
      accentShadow: '#892821',
      background: '#FFFACE',
      cardBackground: '#F2EEC4',
      text: '#120E1F',
      extra: '#9DC1C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DD8D5C',
      primaryShadow: '#D7793F',
      accent: '#B85149',
      accentShadow: '#AB3229',
      background: '#120E1F',
      cardBackground: '#1B1828',
      text: '#FFFACE',
      extra: '#A7C7C6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sage Galaxy',
    tags: ['curated'],
    light: {
      primary: '#29AB7D',
      primaryShadow: '#218964',
      accent: '#00408F',
      accentShadow: '#003372',
      background: '#F7F7C7',
      cardBackground: '#EBEBBD',
      text: '#110F3E',
      extra: '#FF9072',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#49B891',
      primaryShadow: '#29AB7D',
      accent: '#265DA0',
      accentShadow: '#00408F',
      background: '#110F3E',
      cardBackground: '#1B1946',
      text: '#F7F7C7',
      extra: '#FF9B80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Storm',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#14C8F9',
      primaryShadow: '#10A0C7',
      accent: '#6ADCEA',
      accentShadow: '#55B0BB',
      background: '#FCFEFF',
      cardBackground: '#EFF1F2',
      text: '#1A1A1A',
      extra: '#946AA3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#37D0FA',
      primaryShadow: '#14C8F9',
      accent: '#80E1ED',
      accentShadow: '#6ADCEA',
      background: '#090E13',
      cardBackground: '#10151A',
      text: '#FCFEFF',
      extra: '#9F79AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Reef',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C92E70',
      primaryShadow: '#A1255A',
      accent: '#FF5B4F',
      accentShadow: '#CC493F',
      background: '#FFFFE1',
      cardBackground: '#F2F2D6',
      text: '#533A44',
      extra: '#FFD8A9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D14D85',
      primaryShadow: '#C92E70',
      accent: '#FF7469',
      accentShadow: '#FF5B4F',
      background: '#0E090F',
      cardBackground: '#151016',
      text: '#FFFFE1',
      extra: '#FFDCB2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eternal Bay',
    tags: ['curated'],
    light: {
      primary: '#C74E33',
      primaryShadow: '#9F3E29',
      accent: '#F0783E',
      accentShadow: '#C06032',
      background: '#E9F1F2',
      cardBackground: '#DDE5E6',
      text: '#020405',
      extra: '#15212B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF6952',
      primaryShadow: '#C74E33',
      accent: '#F28C5B',
      accentShadow: '#F0783E',
      background: '#020405',
      cardBackground: '#0C0E0F',
      text: '#E9F1F2',
      extra: '#2C3740',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Garden',
    tags: ['curated'],
    light: {
      primary: '#57AAB6',
      primaryShadow: '#468892',
      accent: '#64739C',
      accentShadow: '#505C7D',
      background: '#FFF1DA',
      cardBackground: '#F2E5CF',
      text: '#341A1A',
      extra: '#341A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#70B7C1',
      primaryShadow: '#57AAB6',
      accent: '#7B88AB',
      accentShadow: '#64739C',
      background: '#341A1A',
      cardBackground: '#3C2323',
      text: '#FFF1DA',
      extra: '#483131',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Prism',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#C76B2A',
      primaryShadow: '#9F5622',
      accent: '#5E2D20',
      accentShadow: '#4B241A',
      background: '#EDE5E0',
      cardBackground: '#E4DCD7',
      text: '#211210',
      extra: '#F0C260',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF814A',
      primaryShadow: '#C76B2A',
      accent: '#764D41',
      accentShadow: '#5E2D20',
      background: '#211210',
      cardBackground: '#2A1B1A',
      text: '#F0C260',
      extra: '#F2C870',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Geyser',
    tags: ['warm', 'curated'],
    light: {
      primary: '#6284A1',
      primaryShadow: '#4E6A81',
      accent: '#C79275',
      accentShadow: '#9F755E',
      background: '#E5E7EA',
      cardBackground: '#DCDEE1',
      text: '#494252',
      extra: '#494252',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7A96AF',
      primaryShadow: '#6284A1',
      accent: '#CFA28A',
      accentShadow: '#C79275',
      background: '#0B0C11',
      cardBackground: '#121318',
      text: '#C7B68D',
      extra: '#5B5563',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Steppe',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FF4589',
      primaryShadow: '#CC376E',
      accent: '#A64777',
      accentShadow: '#85395F',
      background: '#EBE5CE',
      cardBackground: '#DFDAC4',
      text: '#3E2653',
      extra: '#3E2653',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF619B',
      primaryShadow: '#FF4589',
      accent: '#B3638B',
      accentShadow: '#A64777',
      background: '#100A10',
      cardBackground: '#171117',
      text: '#EBE5CE',
      extra: '#513C64',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Crater',
    tags: ['curated'],
    light: {
      primary: '#945A50',
      primaryShadow: '#764840',
      accent: '#2E4861',
      accentShadow: '#253A4E',
      background: '#E6EBD6',
      cardBackground: '#DBDFCB',
      text: '#24222E',
      extra: '#4A3337',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A4736A',
      primaryShadow: '#945A50',
      accent: '#4D6379',
      accentShadow: '#2E4861',
      background: '#24222E',
      cardBackground: '#2D2B36',
      text: '#E6EBD6',
      extra: '#5C474B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fleeting Prism',
    tags: ['cool', 'curated'],
    light: {
      primary: '#407292',
      primaryShadow: '#335B75',
      accent: '#56A3B2',
      accentShadow: '#45828E',
      background: '#D5FFCC',
      cardBackground: '#CAF2C2',
      text: '#0E0314',
      extra: '#95FFCF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5D87A2',
      primaryShadow: '#407292',
      accent: '#6FB1BE',
      accentShadow: '#56A3B2',
      background: '#0E0314',
      cardBackground: '#180D1D',
      text: '#D5FFCC',
      extra: '#A0FFD4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Reef',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#B86735',
      primaryShadow: '#93522A',
      accent: '#99342C',
      accentShadow: '#7A2A23',
      background: '#E6D7CC',
      cardBackground: '#DBCCC2',
      text: '#000000',
      extra: '#3B221C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C37E53',
      primaryShadow: '#B86735',
      accent: '#A8524C',
      accentShadow: '#99342C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#E6D7CC',
      extra: '#4F3833',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Tundra',
    tags: ['curated'],
    light: {
      primary: '#CC77AF',
      primaryShadow: '#A35F8C',
      accent: '#9E6695',
      accentShadow: '#7E5277',
      background: '#EDE6EB',
      cardBackground: '#E4DDE2',
      text: '#313D59',
      extra: '#313D59',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D48BBB',
      primaryShadow: '#CC77AF',
      accent: '#AD7DA5',
      accentShadow: '#9E6695',
      background: '#0E0C11',
      cardBackground: '#151318',
      text: '#F0F0F0',
      extra: '#46506A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ivory Bay',
    tags: ['curated'],
    light: {
      primary: '#AA7F00',
      primaryShadow: '#886600',
      accent: '#55DF00',
      accentShadow: '#44B200',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#800000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B79226',
      primaryShadow: '#AA7F00',
      accent: '#6FE426',
      accentShadow: '#55DF00',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#8D1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ancient Clearing',
    tags: ['curated'],
    light: {
      primary: '#8B5851',
      primaryShadow: '#6F4641',
      accent: '#613832',
      accentShadow: '#4E2D28',
      background: '#FFDEB2',
      cardBackground: '#F2D3A9',
      text: '#2F4447',
      extra: '#BF8F75',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C716B',
      primaryShadow: '#8B5851',
      accent: '#795651',
      accentShadow: '#613832',
      background: '#0C0B0E',
      cardBackground: '#131215',
      text: '#FFDEB2',
      extra: '#C59A83',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Stream',
    tags: ['curated'],
    light: {
      primary: '#10D275',
      primaryShadow: '#0DA85E',
      accent: '#007899',
      accentShadow: '#00607A',
      background: '#FFFFF5',
      cardBackground: '#F2F2E9',
      text: '#120E23',
      extra: '#5A265E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#34D98A',
      primaryShadow: '#10D275',
      accent: '#268CA8',
      accentShadow: '#007899',
      background: '#120E23',
      cardBackground: '#1B182C',
      text: '#FFFFF5',
      extra: '#6B3C6E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Orchard',
    tags: ['curated'],
    light: {
      primary: '#E06E16',
      primaryShadow: '#B35812',
      accent: '#19692C',
      accentShadow: '#145423',
      background: '#F7DB7E',
      cardBackground: '#EBD078',
      text: '#142B23',
      extra: '#142B23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E58439',
      primaryShadow: '#E06E16',
      accent: '#3C804C',
      accentShadow: '#19692C',
      background: '#142B23',
      cardBackground: '#1D332C',
      text: '#F7DB7E',
      extra: '#2C4039',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snowy Eclipse',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#4C3012',
      primaryShadow: '#3D260E',
      accent: '#B1954C',
      accentShadow: '#8E773D',
      background: '#E3E1DE',
      cardBackground: '#DAD8D5',
      text: '#160402',
      extra: '#585931',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#674F36',
      primaryShadow: '#4C3012',
      accent: '#BDA567',
      accentShadow: '#B1954C',
      background: '#160402',
      cardBackground: '#1F0E0C',
      text: '#EFD98B',
      extra: '#696A46',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Moon',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#99AA00',
      primaryShadow: '#7A8800',
      accent: '#DD0975',
      accentShadow: '#B1075E',
      background: '#E9EADD',
      cardBackground: '#E0E1D4',
      text: '#171900',
      extra: '#5E3801',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A8B726',
      primaryShadow: '#99AA00',
      accent: '#E22E8A',
      accentShadow: '#DD0975',
      background: '#171900',
      cardBackground: '#20220A',
      text: '#F0F0F0',
      extra: '#6E4C1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Granite Pasture',
    tags: ['curated'],
    light: {
      primary: '#CC1424',
      primaryShadow: '#A3101D',
      accent: '#2E994E',
      accentShadow: '#257A3E',
      background: '#FFF9B3',
      cardBackground: '#F2EDAA',
      text: '#0A010D',
      extra: '#B9C5CC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D43745',
      primaryShadow: '#CC1424',
      accent: '#4DA869',
      accentShadow: '#2E994E',
      background: '#0A010D',
      cardBackground: '#140B17',
      text: '#FFF9B3',
      extra: '#C0CBD1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Library',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4D80AB',
      primaryShadow: '#3E6689',
      accent: '#244F55',
      accentShadow: '#1D3F44',
      background: '#FFFEF9',
      cardBackground: '#F2F1ED',
      text: '#211729',
      extra: '#6B9A71',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6893B8',
      primaryShadow: '#4D80AB',
      accent: '#45696F',
      accentShadow: '#244F55',
      background: '#211729',
      cardBackground: '#2A2032',
      text: '#FFFEF9',
      extra: '#7AA47F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Mosaic',
    tags: ['curated'],
    light: {
      primary: '#FF8000',
      primaryShadow: '#CC6600',
      accent: '#008000',
      accentShadow: '#006600',
      background: '#FFFFAA',
      cardBackground: '#F2F2A2',
      text: '#000000',
      extra: '#B30000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9326',
      primaryShadow: '#FF8000',
      accent: '#269326',
      accentShadow: '#008000',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFAA',
      extra: '#BB1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Matte Patio',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A90623',
      primaryShadow: '#87051C',
      accent: '#CF3D51',
      accentShadow: '#A63141',
      background: '#D6F4E0',
      cardBackground: '#CBE8D5',
      text: '#180415',
      extra: '#F5737F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B62B44',
      primaryShadow: '#A90623',
      accent: '#D65A6B',
      accentShadow: '#CF3D51',
      background: '#180415',
      cardBackground: '#210E1E',
      text: '#D6F4E0',
      extra: '#F6818C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Futuristic Mind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#73841B',
      primaryShadow: '#5C6A16',
      accent: '#C4B441',
      accentShadow: '#9D9034',
      background: '#F8F3CA',
      cardBackground: '#ECE7C0',
      text: '#211515',
      extra: '#392C13',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#88963D',
      primaryShadow: '#73841B',
      accent: '#CDBF5E',
      accentShadow: '#C4B441',
      background: '#211515',
      cardBackground: '#2A1E1E',
      text: '#F8F3CA',
      extra: '#4D412B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Stream',
    tags: ['curated'],
    light: {
      primary: '#F1892D',
      primaryShadow: '#C16E24',
      accent: '#E93841',
      accentShadow: '#BA2D34',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#100820',
      extra: '#FFD19D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F39B4D',
      primaryShadow: '#F1892D',
      accent: '#EC565E',
      accentShadow: '#E93841',
      background: '#100820',
      cardBackground: '#1A1229',
      text: '#FFFFFF',
      extra: '#FFD6A7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Heath',
    tags: ['warm', 'curated'],
    light: {
      primary: '#7D5C51',
      primaryShadow: '#644A41',
      accent: '#CD9F83',
      accentShadow: '#A47F69',
      background: '#F2ECE9',
      cardBackground: '#E6E0DD',
      text: '#272524',
      extra: '#272524',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#91746B',
      primaryShadow: '#7D5C51',
      accent: '#D5AD96',
      accentShadow: '#CD9F83',
      background: '#272524',
      cardBackground: '#302E2D',
      text: '#F2ECE9',
      extra: '#3D3B3A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Leaf',
    tags: ['cool', 'curated'],
    light: {
      primary: '#9729FC',
      primaryShadow: '#7921CA',
      accent: '#6420A8',
      accentShadow: '#501A86',
      background: '#F6E1FF',
      cardBackground: '#EAD6F2',
      text: '#11002A',
      extra: '#F6BAFB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A749FC',
      primaryShadow: '#9729FC',
      accent: '#7B41B5',
      accentShadow: '#6420A8',
      background: '#11002A',
      cardBackground: '#1B0A33',
      text: '#F6E1FF',
      extra: '#F7C1FB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fleeting Palette',
    tags: ['cool', 'curated'],
    light: {
      primary: '#8660AF',
      primaryShadow: '#6B4D8C',
      accent: '#4F3676',
      accentShadow: '#3F2B5E',
      background: '#EFDDEE',
      cardBackground: '#E3D2E2',
      text: '#25153F',
      extra: '#25153F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9878BB',
      primaryShadow: '#8660AF',
      accent: '#69548B',
      accentShadow: '#4F3676',
      background: '#25153F',
      cardBackground: '#2E1E47',
      text: '#EFDDEE',
      extra: '#3B2C52',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Misty Seed',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D99D62',
      primaryShadow: '#AE7E4E',
      accent: '#804055',
      accentShadow: '#663344',
      background: '#FFF2D9',
      cardBackground: '#F2E6CE',
      text: '#171726',
      extra: '#D99D62',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DFAC7A',
      primaryShadow: '#D99D62',
      accent: '#935D6F',
      accentShadow: '#804055',
      background: '#171726',
      cardBackground: '#20202F',
      text: '#FFF2D9',
      extra: '#DDA772',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Peninsula',
    tags: ['cool', 'curated'],
    light: {
      primary: '#BD3506',
      primaryShadow: '#972A05',
      accent: '#97B516',
      accentShadow: '#799112',
      background: '#FCFCFC',
      cardBackground: '#EFEFEF',
      text: '#000000',
      extra: '#043D46',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C7532B',
      primaryShadow: '#BD3506',
      accent: '#A7C039',
      accentShadow: '#97B516',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FCFCFC',
      extra: '#1D5059',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Horizon',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E0864A',
      primaryShadow: '#B36B3B',
      accent: '#AB3737',
      accentShadow: '#892C2C',
      background: '#F9FFF2',
      cardBackground: '#EDF2E6',
      text: '#202026',
      extra: '#F5D678',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E59865',
      primaryShadow: '#E0864A',
      accent: '#B85555',
      accentShadow: '#AB3737',
      background: '#202026',
      cardBackground: '#29292F',
      text: '#F9FFF2',
      extra: '#F6DA86',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Atrium',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4868E8',
      primaryShadow: '#3A53BA',
      accent: '#48BD68',
      accentShadow: '#3A9753',
      background: '#E8F8F8',
      cardBackground: '#DCECEC',
      text: '#162432',
      extra: '#F8B888',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#637FEB',
      primaryShadow: '#4868E8',
      accent: '#63C77F',
      accentShadow: '#48BD68',
      background: '#162432',
      cardBackground: '#1F2D3A',
      text: '#E8F8F8',
      extra: '#F9BF94',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rust Aurora',
    tags: ['curated'],
    light: {
      primary: '#CC9966',
      primaryShadow: '#A37A52',
      accent: '#AA6677',
      accentShadow: '#88525F',
      background: '#EDE9E5',
      cardBackground: '#E4E0DC',
      text: '#332233',
      extra: '#332233',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D4A87D',
      primaryShadow: '#CC9966',
      accent: '#B77D8B',
      accentShadow: '#AA6677',
      background: '#0E0D0F',
      cardBackground: '#151416',
      text: '#DDCC99',
      extra: '#473847',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy Cloud',
    tags: ['curated'],
    light: {
      primary: '#A9597A',
      primaryShadow: '#874762',
      accent: '#68C5B5',
      accentShadow: '#539E91',
      background: '#F7F9E8',
      cardBackground: '#EBEDDC',
      text: '#272237',
      extra: '#272237',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B6728E',
      primaryShadow: '#A9597A',
      accent: '#7FCEC0',
      accentShadow: '#68C5B5',
      background: '#272237',
      cardBackground: '#302B3F',
      text: '#F7F9E8',
      extra: '#3D384B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Magenta Relief',
    tags: ['warm', 'curated'],
    light: {
      primary: '#F1641F',
      primaryShadow: '#C15019',
      accent: '#B9451D',
      accentShadow: '#943717',
      background: '#F4EDD8',
      cardBackground: '#E8E1CD',
      text: '#0D0D0C',
      extra: '#2A1E23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F37B41',
      primaryShadow: '#F1641F',
      accent: '#C4613F',
      accentShadow: '#B9451D',
      background: '#0D0D0C',
      cardBackground: '#171716',
      text: '#F4EDD8',
      extra: '#3F3539',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mediterranean Arcade',
    tags: ['curated'],
    light: {
      primary: '#0CE7A7',
      primaryShadow: '#0AB986',
      accent: '#CE186A',
      accentShadow: '#A51355',
      background: '#FDFFFF',
      cardBackground: '#F0F2F2',
      text: '#53034B',
      extra: '#FF8686',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30EBB4',
      primaryShadow: '#0CE7A7',
      accent: '#D53B80',
      accentShadow: '#CE186A',
      background: '#53034B',
      cardBackground: '#5A0D52',
      text: '#FDFFFF',
      extra: '#FF9292',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jade Coast',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D8352C',
      primaryShadow: '#AD2A23',
      accent: '#971D28',
      accentShadow: '#791720',
      background: '#FBF5DC',
      cardBackground: '#EEE9D1',
      text: '#1B0203',
      extra: '#402F32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE534C',
      primaryShadow: '#D8352C',
      accent: '#A73F48',
      accentShadow: '#971D28',
      background: '#1B0203',
      cardBackground: '#240C0D',
      text: '#FBF5DC',
      extra: '#534447',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Contemporary Courtyard',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#4D1313',
      primaryShadow: '#3E0F0F',
      accent: '#633838',
      accentShadow: '#4F2D2D',
      background: '#E3DEDE',
      cardBackground: '#DAD5D5',
      text: '#0E0707',
      extra: '#7E6A6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#683636',
      primaryShadow: '#4D1313',
      accent: '#7A5656',
      accentShadow: '#633838',
      background: '#0E0707',
      cardBackground: '#181111',
      text: '#C1B5B5',
      extra: '#8B7979',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fierce Bloom',
    tags: ['curated'],
    light: {
      primary: '#028FA0',
      primaryShadow: '#027280',
      accent: '#1BDC8D',
      accentShadow: '#16B071',
      background: '#FFFBEF',
      cardBackground: '#F2EEE3',
      text: '#391F46',
      extra: '#FFCDCD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#28A0AE',
      primaryShadow: '#028FA0',
      accent: '#3DE19E',
      accentShadow: '#1BDC8D',
      background: '#080C11',
      cardBackground: '#0F1318',
      text: '#FFFBEF',
      extra: '#FFD2D2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oceanic Wind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BD6D30',
      primaryShadow: '#975726',
      accent: '#9E3420',
      accentShadow: '#7E2A1A',
      background: '#F6DBC4',
      cardBackground: '#EAD0BA',
      text: '#101417',
      extra: '#6A1D39',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C7834F',
      primaryShadow: '#BD6D30',
      accent: '#AD5241',
      accentShadow: '#9E3420',
      background: '#101417',
      cardBackground: '#1A1D20',
      text: '#F6DBC4',
      extra: '#79344D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Fumarole',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4B2D28',
      primaryShadow: '#3C2420',
      accent: '#8AB0D8',
      accentShadow: '#6E8DAD',
      background: '#F2F9FF',
      cardBackground: '#E6EDF2',
      text: '#141A0D',
      extra: '#744E65',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#664D48',
      primaryShadow: '#4B2D28',
      accent: '#9CBCDE',
      accentShadow: '#8AB0D8',
      background: '#141A0D',
      cardBackground: '#1D2317',
      text: '#F2F9FF',
      extra: '#826074',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Gorge',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#CF331E',
      primaryShadow: '#A62918',
      accent: '#F29D2C',
      accentShadow: '#C27E23',
      background: '#EDE1DF',
      cardBackground: '#E4D8D6',
      text: '#301922',
      extra: '#726059',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D65240',
      primaryShadow: '#CF331E',
      accent: '#F4AC4C',
      accentShadow: '#F29D2C',
      background: '#301922',
      cardBackground: '#38222B',
      text: '#F0F0F0',
      extra: '#80706A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Vision',
    tags: ['cool', 'curated'],
    light: {
      primary: '#AB5C74',
      primaryShadow: '#894A5D',
      accent: '#B89D6E',
      accentShadow: '#937E58',
      background: '#EAE4E6',
      cardBackground: '#E1DBDD',
      text: '#2E253D',
      extra: '#BFBFBF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B87489',
      primaryShadow: '#AB5C74',
      accent: '#C3AC84',
      accentShadow: '#B89D6E',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#BFBFBF',
      extra: '#C5C5C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Nave',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B38C31',
      primaryShadow: '#8F7027',
      accent: '#785C25',
      accentShadow: '#604A1E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#372D30',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BE9D50',
      primaryShadow: '#B38C31',
      accent: '#8C7446',
      accentShadow: '#785C25',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#4B4245',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Storm',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C16A2A',
      primaryShadow: '#9A5522',
      accent: '#EFCA22',
      accentShadow: '#BFA21B',
      background: '#ECE5E0',
      cardBackground: '#E3DCD7',
      text: '#2A111C',
      extra: '#CDBD65',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CA804A',
      primaryShadow: '#C16A2A',
      accent: '#F1D243',
      accentShadow: '#EFCA22',
      background: '#2A111C',
      cardBackground: '#331B25',
      text: '#D4D4D4',
      extra: '#D2C474',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Balmy Springs',
    tags: ['cool', 'curated'],
    light: {
      primary: '#428AB3',
      primaryShadow: '#356E8F',
      accent: '#42B3C2',
      accentShadow: '#358F9B',
      background: '#DFF6F5',
      cardBackground: '#D4EAE9',
      text: '#181B21',
      extra: '#181B21',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E9CBE',
      primaryShadow: '#428AB3',
      accent: '#5EBECB',
      accentShadow: '#42B3C2',
      background: '#181B21',
      cardBackground: '#21242A',
      text: '#DFF6F5',
      extra: '#2F3237',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Earth',
    tags: ['curated'],
    light: {
      primary: '#B44DB7',
      primaryShadow: '#903E92',
      accent: '#783A8D',
      accentShadow: '#602E71',
      background: '#FDF1F2',
      cardBackground: '#F0E5E6',
      text: '#2F2442',
      extra: '#F3C4D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF68C2',
      primaryShadow: '#B44DB7',
      accent: '#8C589E',
      accentShadow: '#783A8D',
      background: '#0D0A11',
      cardBackground: '#141118',
      text: '#FDF1F2',
      extra: '#F4CADC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Gorge',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B7577A',
      primaryShadow: '#924662',
      accent: '#295868',
      accentShadow: '#214653',
      background: '#E6E1CA',
      cardBackground: '#DBD6C0',
      text: '#383131',
      extra: '#383131',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C2708E',
      primaryShadow: '#B7577A',
      accent: '#49717F',
      accentShadow: '#295868',
      background: '#0D0B10',
      cardBackground: '#141217',
      text: '#E6E1CA',
      extra: '#4C4646',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Patio',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D97E16',
      primaryShadow: '#AE6512',
      accent: '#BF481D',
      accentShadow: '#993A17',
      background: '#EEE7DF',
      cardBackground: '#E4DED6',
      text: '#330D10',
      extra: '#4D130F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DF9139',
      primaryShadow: '#D97E16',
      accent: '#C9633F',
      accentShadow: '#BF481D',
      background: '#330D10',
      cardBackground: '#3B171A',
      text: '#F2E749',
      extra: '#5F2B27',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neo Comet',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#FF0015',
      primaryShadow: '#CC0011',
      accent: '#860020',
      accentShadow: '#6B001A',
      background: '#FFFCFE',
      cardBackground: '#F2EFF1',
      text: '#11070A',
      extra: '#860020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF2638',
      primaryShadow: '#FF0015',
      accent: '#982641',
      accentShadow: '#860020',
      background: '#11070A',
      cardBackground: '#1B1114',
      text: '#FFFCFE',
      extra: '#921A36',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ancient Mirage',
    tags: ['curated'],
    light: {
      primary: '#279098',
      primaryShadow: '#1F737A',
      accent: '#48C39A',
      accentShadow: '#3A9C7B',
      background: '#FAFDEA',
      cardBackground: '#EEF0DE',
      text: '#122230',
      extra: '#122230',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#47A1A7',
      primaryShadow: '#279098',
      accent: '#63CCA9',
      accentShadow: '#48C39A',
      background: '#122230',
      cardBackground: '#1B2B38',
      text: '#FAFDEA',
      extra: '#2A3845',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rich Rain',
    tags: ['curated'],
    light: {
      primary: '#6FA341',
      primaryShadow: '#598234',
      accent: '#A3C255',
      accentShadow: '#829B44',
      background: '#E8F1A6',
      cardBackground: '#DCE59E',
      text: '#16161C',
      extra: '#CAD670',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#85B15E',
      primaryShadow: '#6FA341',
      accent: '#B1CB6F',
      accentShadow: '#A3C255',
      background: '#16161C',
      cardBackground: '#1F1F25',
      text: '#E8F1A6',
      extra: '#CFDA7E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Citadel',
    tags: ['dark', 'curated'],
    light: {
      primary: '#D65B49',
      primaryShadow: '#AB493A',
      accent: '#AD2340',
      accentShadow: '#8A1C33',
      background: '#EEE4E3',
      cardBackground: '#E4DBDA',
      text: '#060423',
      extra: '#2B0D46',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DC7464',
      primaryShadow: '#D65B49',
      accent: '#B9445D',
      accentShadow: '#AD2340',
      background: '#060423',
      cardBackground: '#100E2C',
      text: '#FAB269',
      extra: '#402559',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mellow Twilight',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B61030',
      primaryShadow: '#920D26',
      accent: '#CA713C',
      accentShadow: '#A25A30',
      background: '#FFFFF2',
      cardBackground: '#F2F2E6',
      text: '#1C0810',
      extra: '#E24050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C1344F',
      primaryShadow: '#B61030',
      accent: '#D28659',
      accentShadow: '#CA713C',
      background: '#1C0810',
      cardBackground: '#25121A',
      text: '#FFFFF2',
      extra: '#E55362',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Dream',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E67216',
      primaryShadow: '#B85B12',
      accent: '#0E8743',
      accentShadow: '#0B6C36',
      background: '#FEF6DD',
      cardBackground: '#F1EAD2',
      text: '#212425',
      extra: '#F3E46D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EA8739',
      primaryShadow: '#E67216',
      accent: '#32995F',
      accentShadow: '#0E8743',
      background: '#212425',
      cardBackground: '#2A2D2E',
      text: '#FEF6DD',
      extra: '#F4E77C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crisp Cove',
    tags: ['warm', 'curated'],
    light: {
      primary: '#74AF34',
      primaryShadow: '#5D8C2A',
      accent: '#CAF532',
      accentShadow: '#A2C428',
      background: '#E6EBE1',
      cardBackground: '#DDE2D8',
      text: '#561D17',
      extra: '#561D17',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#89BB52',
      primaryShadow: '#74AF34',
      accent: '#D2F751',
      accentShadow: '#CAF532',
      background: '#0B0D0E',
      cardBackground: '#121415',
      text: '#CAF532',
      extra: '#67342E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Nebula',
    tags: ['cool', 'curated'],
    light: {
      primary: '#6882B6',
      primaryShadow: '#536892',
      accent: '#0C0A1B',
      accentShadow: '#0A0816',
      background: '#CBDBFC',
      cardBackground: '#C1D0EF',
      text: '#0C0A1B',
      extra: '#222034',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7F95C1',
      primaryShadow: '#6882B6',
      accent: '#302F3D',
      accentShadow: '#0C0A1B',
      background: '#0C0A1B',
      cardBackground: '#161424',
      text: '#CBDBFC',
      extra: '#383648',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rust Atrium',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#00C38F',
      primaryShadow: '#009C72',
      accent: '#7521EE',
      accentShadow: '#5E1ABE',
      background: '#DDECE8',
      cardBackground: '#D4E3DF',
      text: '#041026',
      extra: '#1F0398',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26CCA0',
      primaryShadow: '#00C38F',
      accent: '#8A42F1',
      accentShadow: '#7521EE',
      background: '#041026',
      cardBackground: '#0E1A2F',
      text: '#F0F0F0',
      extra: '#351CA2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Contemporary Twilight',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#F49461',
      primaryShadow: '#C3764E',
      accent: '#D76C6C',
      accentShadow: '#AC5656',
      background: '#FFFCF1',
      cardBackground: '#F2EFE5',
      text: '#3E3E3E',
      extra: '#FFDD7E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F6A479',
      primaryShadow: '#F49461',
      accent: '#DD8282',
      accentShadow: '#D76C6C',
      background: '#0F0C0F',
      cardBackground: '#161316',
      text: '#FFFCF1',
      extra: '#FFE08B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scarlet Woodland',
    tags: ['bright', 'curated'],
    light: {
      primary: '#00A3DE',
      primaryShadow: '#0082B2',
      accent: '#F20095',
      accentShadow: '#C20077',
      background: '#FDFFF7',
      cardBackground: '#F0F2EB',
      text: '#6816BF',
      extra: '#6816BF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B1E3',
      primaryShadow: '#00A3DE',
      accent: '#F426A5',
      accentShadow: '#F20095',
      background: '#080D13',
      cardBackground: '#0F141A',
      text: '#FDFFF7',
      extra: '#772DC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Winter Palette',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C4663D',
      primaryShadow: '#9D5231',
      accent: '#9C323C',
      accentShadow: '#7D2830',
      background: '#EAE5D1',
      cardBackground: '#DEDAC7',
      text: '#100A0A',
      extra: '#333025',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CD7D5A',
      primaryShadow: '#C4663D',
      accent: '#AB5159',
      accentShadow: '#9C323C',
      background: '#100A0A',
      cardBackground: '#1A1414',
      text: '#EAE5D1',
      extra: '#47453B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Atrium',
    tags: ['curated'],
    light: {
      primary: '#1B96BA',
      primaryShadow: '#167895',
      accent: '#F1BE43',
      accentShadow: '#C19836',
      background: '#E2E9E9',
      cardBackground: '#D7DDDD',
      text: '#232221',
      extra: '#232221',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3DA6C4',
      primaryShadow: '#1B96BA',
      accent: '#F3C85F',
      accentShadow: '#F1BE43',
      background: '#232221',
      cardBackground: '#2C2B2A',
      text: '#E2E9E9',
      extra: '#393837',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classic Promenade',
    tags: ['cool', 'curated'],
    light: {
      primary: '#25A2A8',
      primaryShadow: '#1E8286',
      accent: '#4B56EB',
      accentShadow: '#3C45BC',
      background: '#DAF0D1',
      cardBackground: '#CFE4C7',
      text: '#2C203D',
      extra: '#703075',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#46B0B5',
      primaryShadow: '#25A2A8',
      accent: '#666FEE',
      accentShadow: '#4B56EB',
      background: '#090D11',
      cardBackground: '#101418',
      text: '#DAF0D1',
      extra: '#7E4583',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lasting Tone',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#3F6ECC',
      primaryShadow: '#3258A3',
      accent: '#62A1C7',
      accentShadow: '#4E819F',
      background: '#E7EDEB',
      cardBackground: '#DBE1DF',
      text: '#1A1A1A',
      extra: '#8ECECE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5C84D4',
      primaryShadow: '#3F6ECC',
      accent: '#7AAFCF',
      accentShadow: '#62A1C7',
      background: '#0A0B12',
      cardBackground: '#111219',
      text: '#E7EDEB',
      extra: '#99D3D3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mediterranean Desert',
    tags: ['dark', 'curated'],
    light: {
      primary: '#F94D15',
      primaryShadow: '#C73E11',
      accent: '#FE981E',
      accentShadow: '#CB7A18',
      background: '#F1E3DE',
      cardBackground: '#E7DAD5',
      text: '#0C061B',
      extra: '#17225C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FA6838',
      primaryShadow: '#F94D15',
      accent: '#FEA740',
      accentShadow: '#FE981E',
      background: '#0C061B',
      cardBackground: '#161024',
      text: '#F0F0F0',
      extra: '#2E386C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rust Blizzard',
    tags: ['curated'],
    light: {
      primary: '#B7635B',
      primaryShadow: '#924F49',
      accent: '#E39669',
      accentShadow: '#B67854',
      background: '#F4F0EA',
      cardBackground: '#E8E4DE',
      text: '#242E36',
      extra: '#242E36',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C27A74',
      primaryShadow: '#B7635B',
      accent: '#E7A680',
      accentShadow: '#E39669',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#F4F0EA',
      extra: '#3A434A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Wave',
    tags: ['curated'],
    light: {
      primary: '#BF5F43',
      primaryShadow: '#994C36',
      accent: '#083B42',
      accentShadow: '#062F35',
      background: '#F1D6C5',
      cardBackground: '#E5CBBB',
      text: '#292F25',
      extra: '#155355',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C9775F',
      primaryShadow: '#BF5F43',
      accent: '#2D585E',
      accentShadow: '#083B42',
      background: '#0E0B0E',
      cardBackground: '#151215',
      text: '#F1D6C5',
      extra: '#2C6466',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artisan Horizon',
    tags: ['warm', 'curated'],
    light: {
      primary: '#773344',
      primaryShadow: '#5F2936',
      accent: '#928779',
      accentShadow: '#756C61',
      background: '#FCDEBE',
      cardBackground: '#EFD3B5',
      text: '#3A445D',
      extra: '#3A445D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8B5260',
      primaryShadow: '#773344',
      accent: '#A2998D',
      accentShadow: '#928779',
      background: '#0C0A0E',
      cardBackground: '#131115',
      text: '#FCDEBE',
      extra: '#4E576D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Cascade',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#007281',
      primaryShadow: '#005B67',
      accent: '#6F989B',
      accentShadow: '#597A7C',
      background: '#DDE6E7',
      cardBackground: '#D4DDDE',
      text: '#423030',
      extra: '#423030',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#268794',
      primaryShadow: '#007281',
      accent: '#85A7AA',
      accentShadow: '#6F989B',
      background: '#080B10',
      cardBackground: '#0F1217',
      text: '#C0C5C9',
      extra: '#554545',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Spectrum',
    tags: ['cool', 'curated'],
    light: {
      primary: '#E15E6E',
      primaryShadow: '#B44B58',
      accent: '#280732',
      accentShadow: '#200628',
      background: '#E6ECEF',
      cardBackground: '#DBE0E3',
      text: '#280732',
      extra: '#B2B1C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E67684',
      primaryShadow: '#E15E6E',
      accent: '#482C51',
      accentShadow: '#280732',
      background: '#280732',
      cardBackground: '#31113A',
      text: '#E6ECEF',
      extra: '#BAB9C6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vivid Leaf',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C96016',
      primaryShadow: '#A14D12',
      accent: '#E28E18',
      accentShadow: '#B57213',
      background: '#FFF39B',
      cardBackground: '#F2E793',
      text: '#3D0A13',
      extra: '#333435',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D17839',
      primaryShadow: '#C96016',
      accent: '#E69F3B',
      accentShadow: '#E28E18',
      background: '#3D0A13',
      cardBackground: '#45141C',
      text: '#FFF39B',
      extra: '#474849',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fleeting Rain',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D94E6A',
      primaryShadow: '#AE3E55',
      accent: '#A63169',
      accentShadow: '#852754',
      background: '#FEFFCF',
      cardBackground: '#F1F2C5',
      text: '#1F0A1F',
      extra: '#4D1059',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DF6980',
      primaryShadow: '#D94E6A',
      accent: '#B35080',
      accentShadow: '#A63169',
      background: '#1F0A1F',
      cardBackground: '#281428',
      text: '#FEFFCF',
      extra: '#5F286A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Glacier',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#055555',
      primaryShadow: '#044444',
      accent: '#050555',
      accentShadow: '#040444',
      background: '#A5F5F5',
      cardBackground: '#9DE9E9',
      text: '#050505',
      extra: '#55A5A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2B6F6F',
      primaryShadow: '#055555',
      accent: '#2B2B6F',
      accentShadow: '#050555',
      background: '#050505',
      cardBackground: '#0F0F0F',
      text: '#A5F5F5',
      extra: '#66AEAE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gleam Lava',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CC785B',
      primaryShadow: '#A36049',
      accent: '#94554D',
      accentShadow: '#76443E',
      background: '#EDE6E4',
      cardBackground: '#E4DDDB',
      text: '#443331',
      extra: '#443331',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D48C74',
      primaryShadow: '#CC785B',
      accent: '#A46F68',
      accentShadow: '#94554D',
      background: '#0E0C0F',
      cardBackground: '#151316',
      text: '#D1C19E',
      extra: '#574746',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Thicket',
    tags: ['cool', 'curated'],
    light: {
      primary: '#39809C',
      primaryShadow: '#2E667D',
      accent: '#5FCC86',
      accentShadow: '#4CA36B',
      background: '#FFF37B',
      cardBackground: '#F2E775',
      text: '#28375B',
      extra: '#28375B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5793AB',
      primaryShadow: '#39809C',
      accent: '#77D498',
      accentShadow: '#5FCC86',
      background: '#0A0C11',
      cardBackground: '#111318',
      text: '#FFF37B',
      extra: '#3E4B6B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amber Rain',
    tags: ['curated'],
    light: {
      primary: '#854576',
      primaryShadow: '#6A375E',
      accent: '#301221',
      accentShadow: '#260E1A',
      background: '#E7E2E6',
      cardBackground: '#DED9DD',
      text: '#301221',
      extra: '#9E81D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#97618B',
      primaryShadow: '#854576',
      accent: '#4F3642',
      accentShadow: '#301221',
      background: '#301221',
      cardBackground: '#381B2A',
      text: '#EEBFF5',
      extra: '#A88ED5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Plaza',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#3F2324',
      primaryShadow: '#321C1D',
      accent: '#5D3F3B',
      accentShadow: '#4A322F',
      background: '#E2E0E0',
      cardBackground: '#D9D7D7',
      text: '#120A0C',
      extra: '#533C43',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5C4445',
      primaryShadow: '#3F2324',
      accent: '#755C58',
      accentShadow: '#5D3F3B',
      background: '#120A0C',
      cardBackground: '#1B1416',
      text: '#F0F0F0',
      extra: '#645056',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noon Wave',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9E703C',
      primaryShadow: '#7E5A30',
      accent: '#C49853',
      accentShadow: '#9D7A42',
      background: '#E9E6E2',
      cardBackground: '#E0DDD9',
      text: '#3D534F',
      extra: '#9C4941',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD8559',
      primaryShadow: '#9E703C',
      accent: '#CDA76D',
      accentShadow: '#C49853',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#BED2B3',
      extra: '#A65B54',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ephemeral Savanna',
    tags: ['cool', 'curated'],
    light: {
      primary: '#2C8A9A',
      primaryShadow: '#236E7B',
      accent: '#782B8C',
      accentShadow: '#602270',
      background: '#FDFFEF',
      cardBackground: '#F0F2E3',
      text: '#000000',
      extra: '#FFCE96',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4C9CA9',
      primaryShadow: '#2C8A9A',
      accent: '#8C4B9D',
      accentShadow: '#782B8C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FDFFEF',
      extra: '#FFD3A1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gleam Horizon',
    tags: ['cool', 'curated'],
    light: {
      primary: '#FF08FF',
      primaryShadow: '#CC06CC',
      accent: '#FF8F00',
      accentShadow: '#CC7200',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#FF032B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF2DFF',
      primaryShadow: '#FF08FF',
      accent: '#FFA026',
      accentShadow: '#FF8F00',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#FF1C40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Vineyard',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#A6607D',
      primaryShadow: '#854D64',
      accent: '#F98282',
      accentShadow: '#C76868',
      background: '#FCFCFC',
      cardBackground: '#EFEFEF',
      text: '#292828',
      extra: '#292828',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B37891',
      primaryShadow: '#A6607D',
      accent: '#FA9595',
      accentShadow: '#F98282',
      background: '#0D0B10',
      cardBackground: '#141217',
      text: '#FCFCFC',
      extra: '#3E3E3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Moon',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#B570EE',
      primaryShadow: '#915ABE',
      accent: '#EEB570',
      accentShadow: '#BE915A',
      background: '#ECFFBC',
      cardBackground: '#E0F2B3',
      text: '#474751',
      extra: '#EE70B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C085F1',
      primaryShadow: '#B570EE',
      accent: '#F1C085',
      accentShadow: '#EEB570',
      background: '#0D0B13',
      cardBackground: '#14121A',
      text: '#ECFFBC',
      extra: '#F07EBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bold Geyser',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#BC2F01',
      primaryShadow: '#962601',
      accent: '#F9960F',
      accentShadow: '#C7780C',
      background: '#ECE1DD',
      cardBackground: '#E3D8D4',
      text: '#151244',
      extra: '#60117F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C64E27',
      primaryShadow: '#BC2F01',
      accent: '#FAA633',
      accentShadow: '#F9960F',
      background: '#151244',
      cardBackground: '#1E1B4B',
      text: '#F9CB60',
      extra: '#70298C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Seed',
    tags: ['cool', 'curated'],
    light: {
      primary: '#17B6B6',
      primaryShadow: '#129292',
      accent: '#193CE8',
      accentShadow: '#1430BA',
      background: '#DFEBEB',
      cardBackground: '#D6E2E2',
      text: '#1F0763',
      extra: '#47F7B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3AC1C1',
      primaryShadow: '#17B6B6',
      accent: '#3C59EB',
      accentShadow: '#193CE8',
      background: '#1F0763',
      cardBackground: '#281169',
      text: '#47F7B5',
      extra: '#59F8BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Glade',
    tags: ['cool', 'curated'],
    light: {
      primary: '#000000',
      primaryShadow: '#000000',
      accent: '#282828',
      accentShadow: '#202020',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#383838',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#262626',
      primaryShadow: '#000000',
      accent: '#484848',
      accentShadow: '#282828',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#4C4C4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Caldera',
    tags: ['curated'],
    light: {
      primary: '#00DA00',
      primaryShadow: '#00AE00',
      accent: '#00DAFF',
      accentShadow: '#00AECC',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#6D0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26E026',
      primaryShadow: '#00DA00',
      accent: '#26E0FF',
      accentShadow: '#00DAFF',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#7C1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Windy Parlor',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C48A74',
      primaryShadow: '#9D6E5D',
      accent: '#93695D',
      accentShadow: '#76544A',
      background: '#F9E8CA',
      cardBackground: '#EDDCC0',
      text: '#423734',
      extra: '#423734',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CD9C89',
      primaryShadow: '#C48A74',
      accent: '#A38075',
      accentShadow: '#93695D',
      background: '#0E0C0F',
      cardBackground: '#151316',
      text: '#F9E8CA',
      extra: '#554B48',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crisp Stream',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CF685A',
      primaryShadow: '#A65348',
      accent: '#F3936A',
      accentShadow: '#C27655',
      background: '#FFF9BB',
      cardBackground: '#F2EDB2',
      text: '#4B414A',
      extra: '#4B414A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D67F73',
      primaryShadow: '#CF685A',
      accent: '#F5A380',
      accentShadow: '#F3936A',
      background: '#0E0B0F',
      cardBackground: '#151216',
      text: '#FFF9BB',
      extra: '#5D545C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Contemporary Jungle',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#2D1A22',
      primaryShadow: '#24151B',
      accent: '#8488AA',
      accentShadow: '#6A6D88',
      background: '#E0DFE0',
      cardBackground: '#D7D6D7',
      text: '#17131B',
      extra: '#22161E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4D3C43',
      primaryShadow: '#2D1A22',
      accent: '#969AB7',
      accentShadow: '#8488AA',
      background: '#17131B',
      cardBackground: '#201C24',
      text: '#E3B3DB',
      extra: '#382D35',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Field',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#00D459',
      primaryShadow: '#00AA47',
      accent: '#039A5D',
      accentShadow: '#027B4A',
      background: '#DDEEE4',
      cardBackground: '#D4E4DB',
      text: '#000C2C',
      extra: '#003645',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26DA72',
      primaryShadow: '#00D459',
      accent: '#29A975',
      accentShadow: '#039A5D',
      background: '#000C2C',
      cardBackground: '#0A1634',
      text: '#66F390',
      extra: '#1A4A58',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scarlet Keep',
    tags: ['bright', 'curated'],
    light: {
      primary: '#FF4AA7',
      primaryShadow: '#CC3B86',
      accent: '#6A1483',
      accentShadow: '#551069',
      background: '#FEFFBE',
      cardBackground: '#F1F2B5',
      text: '#482254',
      extra: '#F5C9EB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF65B4',
      primaryShadow: '#FF4AA7',
      accent: '#803796',
      accentShadow: '#6A1483',
      background: '#100A11',
      cardBackground: '#171118',
      text: '#FEFFBE',
      extra: '#F6CEED',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Earth',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D25B1A',
      primaryShadow: '#A84915',
      accent: '#C03716',
      accentShadow: '#9A2C12',
      background: '#EEE4DF',
      cardBackground: '#E4DBD6',
      text: '#4E0C16',
      extra: '#EDCD6E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D9743C',
      primaryShadow: '#D25B1A',
      accent: '#C95539',
      accentShadow: '#C03716',
      background: '#4E0C16',
      cardBackground: '#55161F',
      text: '#EDCD6E',
      extra: '#EFD27D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snowy Moss',
    tags: ['warm', 'curated'],
    light: {
      primary: '#F1CA00',
      primaryShadow: '#C1A200',
      accent: '#EA1F25',
      accentShadow: '#BB191E',
      background: '#ECDDBE',
      cardBackground: '#E0D2B5',
      text: '#1A1616',
      extra: '#4F186B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F3D226',
      primaryShadow: '#F1CA00',
      accent: '#ED4146',
      accentShadow: '#EA1F25',
      background: '#1A1616',
      cardBackground: '#231F1F',
      text: '#ECDDBE',
      extra: '#612F7A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noon Geyser',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A07754',
      primaryShadow: '#805F43',
      accent: '#845640',
      accentShadow: '#6A4533',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#343128',
      extra: '#343128',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AE8B6E',
      primaryShadow: '#A07754',
      accent: '#966F5D',
      accentShadow: '#845640',
      background: '#0D0C0F',
      cardBackground: '#141316',
      text: '#FFFFFF',
      extra: '#48463E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Iron Cloister',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CA563E',
      primaryShadow: '#A24532',
      accent: '#E8864E',
      accentShadow: '#BA6B3E',
      background: '#F1F0D9',
      cardBackground: '#E5E4CE',
      text: '#232323',
      extra: '#F1BA8E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D26F5B',
      primaryShadow: '#CA563E',
      accent: '#EB9869',
      accentShadow: '#E8864E',
      background: '#232323',
      cardBackground: '#2C2C2C',
      text: '#F1F0D9',
      extra: '#F2C199',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Tone',
    tags: ['cool', 'curated'],
    light: {
      primary: '#389BC2',
      primaryShadow: '#2D7C9B',
      accent: '#780C72',
      accentShadow: '#600A5B',
      background: '#F2FFFE',
      cardBackground: '#E6F2F1',
      text: '#1A1226',
      extra: '#515270',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#56AACB',
      primaryShadow: '#389BC2',
      accent: '#8C3087',
      accentShadow: '#780C72',
      background: '#1A1226',
      cardBackground: '#231B2F',
      text: '#F2FFFE',
      extra: '#62637E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Misty Parlor',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#17072B',
      primaryShadow: '#120622',
      accent: '#845A78',
      accentShadow: '#6A4860',
      background: '#DFDDE0',
      cardBackground: '#D6D4D7',
      text: '#17072B',
      extra: '#CFB690',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3A2C4B',
      primaryShadow: '#17072B',
      accent: '#96738C',
      accentShadow: '#845A78',
      background: '#17072B',
      cardBackground: '#201133',
      text: '#CFB690',
      extra: '#D4BD9B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ancient Desert',
    tags: ['warm', 'curated'],
    light: {
      primary: '#00A193',
      primaryShadow: '#008176',
      accent: '#929E0A',
      accentShadow: '#757E08',
      background: '#FFF9C9',
      cardBackground: '#F2EDBF',
      text: '#25170B',
      extra: '#FFE482',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26AFA3',
      primaryShadow: '#00A193',
      accent: '#A2AD2F',
      accentShadow: '#929E0A',
      background: '#25170B',
      cardBackground: '#2E2015',
      text: '#FFF9C9',
      extra: '#FFE78F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blaze Hue',
    tags: ['curated'],
    light: {
      primary: '#E4752B',
      primaryShadow: '#B65E22',
      accent: '#146B75',
      accentShadow: '#10565E',
      background: '#FFFEDA',
      cardBackground: '#F2F1CF',
      text: '#040304',
      extra: '#201D24',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E88A4B',
      primaryShadow: '#E4752B',
      accent: '#37818A',
      accentShadow: '#146B75',
      background: '#040304',
      cardBackground: '#0E0D0E',
      text: '#FFFEDA',
      extra: '#36343A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ruby Prairie',
    tags: ['curated'],
    light: {
      primary: '#47767E',
      primaryShadow: '#395E65',
      accent: '#2C5352',
      accentShadow: '#234242',
      background: '#EBF9ED',
      cardBackground: '#DFEDE1',
      text: '#191F20',
      extra: '#ABD6CD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#638B91',
      primaryShadow: '#47767E',
      accent: '#4C6D6C',
      accentShadow: '#2C5352',
      background: '#191F20',
      cardBackground: '#222829',
      text: '#EBF9ED',
      extra: '#B3DAD2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Heart',
    tags: ['dark', 'curated'],
    light: {
      primary: '#9E7E34',
      primaryShadow: '#7E652A',
      accent: '#2B7B74',
      accentShadow: '#22625D',
      background: '#E9E7E1',
      cardBackground: '#E0DED8',
      text: '#1D0D2B',
      extra: '#391633',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD9152',
      primaryShadow: '#9E7E34',
      accent: '#4B8F89',
      accentShadow: '#2B7B74',
      background: '#1D0D2B',
      cardBackground: '#261733',
      text: '#BEC187',
      extra: '#4D2D47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Garden',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C96D45',
      primaryShadow: '#A15737',
      accent: '#A52D27',
      accentShadow: '#84241F',
      background: '#D4EDED',
      cardBackground: '#C9E1E1',
      text: '#28221F',
      extra: '#521E2E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D18361',
      primaryShadow: '#C96D45',
      accent: '#B34D47',
      accentShadow: '#A52D27',
      background: '#28221F',
      cardBackground: '#312B28',
      text: '#D4EDED',
      extra: '#633543',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Nebula',
    tags: ['warm', 'curated'],
    light: {
      primary: '#6E9202',
      primaryShadow: '#587502',
      accent: '#E11800',
      accentShadow: '#B41300',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#211F21',
      extra: '#D7D6CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#84A228',
      primaryShadow: '#6E9202',
      accent: '#E63B26',
      accentShadow: '#E11800',
      background: '#211F21',
      cardBackground: '#2A282A',
      text: '#FFFFFF',
      extra: '#DBDAD3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Nova',
    tags: ['curated'],
    light: {
      primary: '#1E8F73',
      primaryShadow: '#18725C',
      accent: '#42CC39',
      accentShadow: '#35A32E',
      background: '#FFFFF0',
      cardBackground: '#F2F2E4',
      text: '#0F141C',
      extra: '#39CC79',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40A088',
      primaryShadow: '#1E8F73',
      accent: '#5ED457',
      accentShadow: '#42CC39',
      background: '#0F141C',
      cardBackground: '#191D25',
      text: '#FFFFF0',
      extra: '#4DD186',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Shade',
    tags: ['curated'],
    light: {
      primary: '#9E2281',
      primaryShadow: '#7E1B67',
      accent: '#50A9CF',
      accentShadow: '#4087A6',
      background: '#F6EDDB',
      cardBackground: '#EAE1D0',
      text: '#1B1E23',
      extra: '#EC8D75',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD4394',
      primaryShadow: '#9E2281',
      accent: '#6AB6D6',
      accentShadow: '#50A9CF',
      background: '#1B1E23',
      cardBackground: '#24272C',
      text: '#F6EDDB',
      extra: '#EE9883',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mellow Root',
    tags: ['curated'],
    light: {
      primary: '#BD4D32',
      primaryShadow: '#973E28',
      accent: '#D89236',
      accentShadow: '#AD752B',
      background: '#ECE3E1',
      cardBackground: '#E3DAD8',
      text: '#0D1C24',
      extra: '#B99C9B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C76851',
      primaryShadow: '#BD4D32',
      accent: '#DEA254',
      accentShadow: '#D89236',
      background: '#0D1C24',
      cardBackground: '#17252D',
      text: '#DDBBA0',
      extra: '#C0A6A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Hue',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9A5848',
      primaryShadow: '#7B463A',
      accent: '#B17C63',
      accentShadow: '#8E634F',
      background: '#E9E4E3',
      cardBackground: '#E0DBDA',
      text: '#322935',
      extra: '#B7B1AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A97163',
      primaryShadow: '#9A5848',
      accent: '#BD907A',
      accentShadow: '#B17C63',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#F0F0F0',
      extra: '#BEB9B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Iron Moss',
    tags: ['curated'],
    light: {
      primary: '#FF6A14',
      primaryShadow: '#CC5510',
      accent: '#9B6621',
      accentShadow: '#7C521A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#404040',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8037',
      primaryShadow: '#FF6A14',
      accent: '#AA7D42',
      accentShadow: '#9B6621',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#535353',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Library',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00D800',
      primaryShadow: '#00AD00',
      accent: '#D800D8',
      accentShadow: '#AD00AD',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#0000FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26DE26',
      primaryShadow: '#00D800',
      accent: '#DE26DE',
      accentShadow: '#D800D8',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#1A1AFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Emerald Caldera',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#2E879E',
      primaryShadow: '#256C7E',
      accent: '#2E4B7D',
      accentShadow: '#253C64',
      background: '#E0E8E9',
      cardBackground: '#D7DFE0',
      text: '#07171F',
      extra: '#2C244E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4D99AD',
      primaryShadow: '#2E879E',
      accent: '#4D6691',
      accentShadow: '#2E4B7D',
      background: '#07171F',
      cardBackground: '#112028',
      text: '#F0F0F0',
      extra: '#413A60',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Chapel',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BB5E25',
      primaryShadow: '#964B1E',
      accent: '#A11920',
      accentShadow: '#81141A',
      background: '#FEFEDF',
      cardBackground: '#F1F1D4',
      text: '#23192D',
      extra: '#F4EAA3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C57646',
      primaryShadow: '#BB5E25',
      accent: '#AF3C41',
      accentShadow: '#A11920',
      background: '#23192D',
      cardBackground: '#2C2235',
      text: '#FEFEDF',
      extra: '#F5ECAC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rich Tundra',
    tags: ['dark', 'curated'],
    light: {
      primary: '#05617A',
      primaryShadow: '#044E62',
      accent: '#C44348',
      accentShadow: '#9D363A',
      background: '#F6EEE3',
      cardBackground: '#EAE2D8',
      text: '#03051C',
      extra: '#382947',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2B798E',
      primaryShadow: '#05617A',
      accent: '#CD5F63',
      accentShadow: '#C44348',
      background: '#03051C',
      cardBackground: '#0D0F25',
      text: '#F6EEE3',
      extra: '#4C3E59',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Orchard',
    tags: ['curated'],
    light: {
      primary: '#ED9F2A',
      primaryShadow: '#BE7F22',
      accent: '#DF4FCD',
      accentShadow: '#B23FA4',
      background: '#F6FBD9',
      cardBackground: '#EAEECE',
      text: '#1E1A20',
      extra: '#8F43C6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F0AD4A',
      primaryShadow: '#ED9F2A',
      accent: '#E469D5',
      accentShadow: '#DF4FCD',
      background: '#1E1A20',
      cardBackground: '#272329',
      text: '#F6FBD9',
      extra: '#9A56CC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Futuristic Steppe',
    tags: ['curated'],
    light: {
      primary: '#03324E',
      primaryShadow: '#02283E',
      accent: '#C74148',
      accentShadow: '#9F343A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#03324E',
      extra: '#DBB9A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#295169',
      primaryShadow: '#03324E',
      accent: '#CF5E63',
      accentShadow: '#C74148',
      background: '#080A0E',
      cardBackground: '#0F1115',
      text: '#FFFFFF',
      extra: '#DFC0AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Calm Pasture',
    tags: ['curated'],
    light: {
      primary: '#1B84C2',
      primaryShadow: '#166A9B',
      accent: '#62B824',
      accentShadow: '#4E931D',
      background: '#FFF3DB',
      cardBackground: '#F2E7D0',
      text: '#1C0911',
      extra: '#FF533F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3D96CB',
      primaryShadow: '#1B84C2',
      accent: '#7AC345',
      accentShadow: '#62B824',
      background: '#1C0911',
      cardBackground: '#25131B',
      text: '#FFF3DB',
      extra: '#FF6452',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Flame Oasis',
    tags: ['cool', 'curated'],
    light: {
      primary: '#DB3CF0',
      primaryShadow: '#AF30C0',
      accent: '#314AD4',
      accentShadow: '#273BAA',
      background: '#B5E6FF',
      cardBackground: '#ACDBF2',
      text: '#221569',
      extra: '#221569',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E059F2',
      primaryShadow: '#DB3CF0',
      accent: '#5065DA',
      accentShadow: '#314AD4',
      background: '#221569',
      cardBackground: '#2B1E6F',
      text: '#B5E6FF',
      extra: '#382C78',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy Rapids',
    tags: ['warm', 'curated'],
    light: {
      primary: '#F8414E',
      primaryShadow: '#C6343E',
      accent: '#D22247',
      accentShadow: '#A81B39',
      background: '#FFDCDE',
      cardBackground: '#F2D1D3',
      text: '#760F42',
      extra: '#FFB2B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F95E69',
      primaryShadow: '#F8414E',
      accent: '#D94363',
      accentShadow: '#D22247',
      background: '#0F0A0E',
      cardBackground: '#161115',
      text: '#FFDCDE',
      extra: '#FFBABD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Tone',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B22A79',
      primaryShadow: '#8E2261',
      accent: '#601ABC',
      accentShadow: '#4D1596',
      background: '#ECE5FF',
      cardBackground: '#E0DAF2',
      text: '#26181D',
      extra: '#701A6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BE4A8D',
      primaryShadow: '#B22A79',
      accent: '#783CC6',
      accentShadow: '#601ABC',
      background: '#26181D',
      cardBackground: '#2F2126',
      text: '#ECE5FF',
      extra: '#7E3179',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Burgundy Vent',
    tags: ['warm', 'curated'],
    light: {
      primary: '#932F7B',
      primaryShadow: '#762662',
      accent: '#E67B8B',
      accentShadow: '#B8626F',
      background: '#F5D2B8',
      cardBackground: '#E9C8AF',
      text: '#21193C',
      extra: '#21193C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A34E8F',
      primaryShadow: '#932F7B',
      accent: '#EA8F9C',
      accentShadow: '#E67B8B',
      background: '#21193C',
      cardBackground: '#2A2244',
      text: '#F5D2B8',
      extra: '#373050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Prism',
    tags: ['warm', 'curated'],
    light: {
      primary: '#994551',
      primaryShadow: '#7A3741',
      accent: '#573746',
      accentShadow: '#462C38',
      background: '#E9E2E3',
      cardBackground: '#E0D9DA',
      text: '#573746',
      extra: '#703D57',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A8616B',
      primaryShadow: '#994551',
      accent: '#705562',
      accentShadow: '#573746',
      background: '#0D0A0E',
      cardBackground: '#141115',
      text: '#F0F0F0',
      extra: '#7E5068',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Western Fire',
    tags: ['curated'],
    light: {
      primary: '#007FFF',
      primaryShadow: '#0066CC',
      accent: '#5F0E52',
      accentShadow: '#4C0B42',
      background: '#FBF8FD',
      cardBackground: '#EEECF0',
      text: '#141218',
      extra: '#A1A9D1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2692FF',
      primaryShadow: '#007FFF',
      accent: '#77326C',
      accentShadow: '#5F0E52',
      background: '#141218',
      cardBackground: '#1D1B21',
      text: '#FBF8FD',
      extra: '#AAB2D6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coral Crystal',
    tags: ['dark', 'curated'],
    light: {
      primary: '#AC8876',
      primaryShadow: '#8A6D5E',
      accent: '#906776',
      accentShadow: '#73525E',
      background: '#EBE8E6',
      cardBackground: '#E2DFDD',
      text: '#353546',
      extra: '#634D69',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B89A8B',
      primaryShadow: '#AC8876',
      accent: '#A17E8B',
      accentShadow: '#906776',
      background: '#0D0C10',
      cardBackground: '#141317',
      text: '#F0F0F0',
      extra: '#735F78',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble Oasis',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9B6859',
      primaryShadow: '#7C5347',
      accent: '#BEBC6A',
      accentShadow: '#989655',
      background: '#EDF8C8',
      cardBackground: '#E1ECBE',
      text: '#553840',
      extra: '#553840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AA7F72',
      primaryShadow: '#9B6859',
      accent: '#C8C680',
      accentShadow: '#BEBC6A',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#EDF8C8',
      extra: '#664C53',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Storm',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9F484E',
      primaryShadow: '#7F3A3E',
      accent: '#B77B79',
      accentShadow: '#926261',
      background: '#EAE3E3',
      cardBackground: '#E1DADA',
      text: '#3B2933',
      extra: '#3B2933',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD6369',
      primaryShadow: '#9F484E',
      accent: '#C28F8D',
      accentShadow: '#B77B79',
      background: '#0D0A0E',
      cardBackground: '#141115',
      text: '#F0F0F0',
      extra: '#4F3E47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Satin Peak',
    tags: ['cool', 'curated'],
    light: {
      primary: '#0EC83F',
      primaryShadow: '#0BA032',
      accent: '#0E926C',
      accentShadow: '#0B7556',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#FFF841',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#32D05C',
      primaryShadow: '#0EC83F',
      accent: '#32A282',
      accentShadow: '#0E926C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#FFF954',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Avant Clearing',
    tags: ['warm', 'curated'],
    light: {
      primary: '#F47300',
      primaryShadow: '#C35C00',
      accent: '#E12D03',
      accentShadow: '#B42402',
      background: '#FFFFFE',
      cardBackground: '#F2F2F1',
      text: '#010101',
      extra: '#551C13',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F68826',
      primaryShadow: '#F47300',
      accent: '#E64D29',
      accentShadow: '#E12D03',
      background: '#010101',
      cardBackground: '#0B0B0B',
      text: '#FFFFFE',
      extra: '#66332B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Springs',
    tags: ['curated'],
    light: {
      primary: '#D6761C',
      primaryShadow: '#AB5E16',
      accent: '#F69D18',
      accentShadow: '#C57E13',
      background: '#FFDCD6',
      cardBackground: '#F2D1CB',
      text: '#120E2F',
      extra: '#1B1B52',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DC8B3E',
      primaryShadow: '#D6761C',
      accent: '#F7AC3B',
      accentShadow: '#F69D18',
      background: '#120E2F',
      cardBackground: '#1B1837',
      text: '#FFDCD6',
      extra: '#323263',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Eclipse',
    tags: ['curated'],
    light: {
      primary: '#CD683D',
      primaryShadow: '#A45331',
      accent: '#993D41',
      accentShadow: '#7A3134',
      background: '#F2EC8B',
      cardBackground: '#E6E084',
      text: '#2E222F',
      extra: '#2E222F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D57F5A',
      primaryShadow: '#CD683D',
      accent: '#A85A5E',
      accentShadow: '#993D41',
      background: '#0E0B0E',
      cardBackground: '#151215',
      text: '#F2EC8B',
      extra: '#433844',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Shore',
    tags: ['bright', 'curated'],
    light: {
      primary: '#2CB1B1',
      primaryShadow: '#238E8E',
      accent: '#25868B',
      accentShadow: '#1E6B6F',
      background: '#FCFCFC',
      cardBackground: '#EFEFEF',
      text: '#000000',
      extra: '#C0B9F5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4CBDBD',
      primaryShadow: '#2CB1B1',
      accent: '#46989C',
      accentShadow: '#25868B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FCFCFC',
      extra: '#C6C0F6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Floe',
    tags: ['curated'],
    light: {
      primary: '#C76B3E',
      primaryShadow: '#9F5632',
      accent: '#B3343E',
      accentShadow: '#8F2A32',
      background: '#FFFAF7',
      cardBackground: '#F2EEEB',
      text: '#09090D',
      extra: '#2A2C3B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF815B',
      primaryShadow: '#C76B3E',
      accent: '#BE525B',
      accentShadow: '#B3343E',
      background: '#09090D',
      cardBackground: '#131317',
      text: '#FFFAF7',
      extra: '#3F414F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Futuristic Shore',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B15B35',
      primaryShadow: '#8E492A',
      accent: '#EAA44A',
      accentShadow: '#BB833B',
      background: '#FCF8F4',
      cardBackground: '#EFECE8',
      text: '#150C21',
      extra: '#332232',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BD7453',
      primaryShadow: '#B15B35',
      accent: '#EDB265',
      accentShadow: '#EAA44A',
      background: '#150C21',
      cardBackground: '#1E162A',
      text: '#FCF8F4',
      extra: '#473847',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Winter Vineyard',
    tags: ['warm', 'curated'],
    light: {
      primary: '#61AD36',
      primaryShadow: '#4E8A2B',
      accent: '#106836',
      accentShadow: '#0D532B',
      background: '#DEEED6',
      cardBackground: '#D3E2CB',
      text: '#120919',
      extra: '#1B1F4B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#79B954',
      primaryShadow: '#61AD36',
      accent: '#347F54',
      accentShadow: '#106836',
      background: '#120919',
      cardBackground: '#1B1322',
      text: '#DEEED6',
      extra: '#32355D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Minimalist Shade',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#F23E3E',
      primaryShadow: '#C23232',
      accent: '#88312B',
      accentShadow: '#6D2722',
      background: '#F0E2E2',
      cardBackground: '#E6D9D9',
      text: '#000000',
      extra: '#341227',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F45B5B',
      primaryShadow: '#F23E3E',
      accent: '#9A504B',
      accentShadow: '#88312B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F0F0F0',
      extra: '#482A3D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Cloud',
    tags: ['curated'],
    light: {
      primary: '#162C27',
      primaryShadow: '#12231F',
      accent: '#6F9B8A',
      accentShadow: '#597C6E',
      background: '#EAD7BE',
      cardBackground: '#DECCB5',
      text: '#162C27',
      extra: '#534650',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#394C47',
      primaryShadow: '#162C27',
      accent: '#85AA9C',
      accentShadow: '#6F9B8A',
      background: '#162C27',
      cardBackground: '#1F3430',
      text: '#EAD7BE',
      extra: '#645962',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tranquil Gorge',
    tags: ['bright', 'curated'],
    light: {
      primary: '#0CC0D4',
      primaryShadow: '#0A9AAA',
      accent: '#FE9192',
      accentShadow: '#CB7475',
      background: '#FCDEBE',
      cardBackground: '#EFD3B5',
      text: '#1A1A1A',
      extra: '#FCDEBE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30C9DA',
      primaryShadow: '#0CC0D4',
      accent: '#FEA2A2',
      accentShadow: '#FE9192',
      background: '#080E12',
      cardBackground: '#0F1519',
      text: '#FCDEBE',
      extra: '#FCE1C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Floe',
    tags: ['warm', 'curated'],
    light: {
      primary: '#1E624E',
      primaryShadow: '#184E3E',
      accent: '#122303',
      accentShadow: '#0E1C02',
      background: '#F5E1E8',
      cardBackground: '#E9D6DC',
      text: '#010E05',
      extra: '#2B2C21',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#407A69',
      primaryShadow: '#1E624E',
      accent: '#364429',
      accentShadow: '#122303',
      background: '#010E05',
      cardBackground: '#0B180F',
      text: '#F5E1E8',
      extra: '#404137',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Ravine',
    tags: ['curated'],
    light: {
      primary: '#2C89B3',
      primaryShadow: '#236E8F',
      accent: '#024C43',
      accentShadow: '#023D36',
      background: '#FAFAFA',
      cardBackground: '#EEEEEE',
      text: '#010001',
      extra: '#CED7DB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4C9BBE',
      primaryShadow: '#2C89B3',
      accent: '#28675F',
      accentShadow: '#024C43',
      background: '#010001',
      cardBackground: '#0B0A0B',
      text: '#FAFAFA',
      extra: '#D3DBDF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Crater',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#8A5C0A',
      primaryShadow: '#6E4A08',
      accent: '#AF8E2C',
      accentShadow: '#8C7223',
      background: '#E6DAC5',
      cardBackground: '#DBCFBB',
      text: '#000000',
      extra: '#E6DAC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C742F',
      primaryShadow: '#8A5C0A',
      accent: '#BB9F4C',
      accentShadow: '#AF8E2C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#E6DAC5',
      extra: '#E9DECB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artisan Moss',
    tags: ['curated'],
    light: {
      primary: '#1AAB87',
      primaryShadow: '#15896C',
      accent: '#851694',
      accentShadow: '#6A1276',
      background: '#FFFFE1',
      cardBackground: '#F2F2D6',
      text: '#000833',
      extra: '#330A80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3CB899',
      primaryShadow: '#1AAB87',
      accent: '#9739A4',
      accentShadow: '#851694',
      background: '#000833',
      cardBackground: '#0A123B',
      text: '#FFFFE1',
      extra: '#47238D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Mesa',
    tags: ['curated'],
    light: {
      primary: '#3A90D0',
      primaryShadow: '#2E73A6',
      accent: '#1B8060',
      accentShadow: '#16664D',
      background: '#F5EED9',
      cardBackground: '#E9E2CE',
      text: '#1A1A21',
      extra: '#1A1A21',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#58A1D7',
      primaryShadow: '#3A90D0',
      accent: '#3D9378',
      accentShadow: '#1B8060',
      background: '#1A1A21',
      cardBackground: '#23232A',
      text: '#F5EED9',
      extra: '#313137',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Fumarole',
    tags: ['curated'],
    light: {
      primary: '#CD6157',
      primaryShadow: '#A44E46',
      accent: '#DD984B',
      accentShadow: '#B17A3C',
      background: '#F1F1F1',
      cardBackground: '#E5E5E5',
      text: '#1C1C1C',
      extra: '#1C1C1C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D57970',
      primaryShadow: '#CD6157',
      accent: '#E2A766',
      accentShadow: '#DD984B',
      background: '#1C1C1C',
      cardBackground: '#252525',
      text: '#F1F1F1',
      extra: '#333333',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Sunset',
    tags: ['curated'],
    light: {
      primary: '#CB5E5C',
      primaryShadow: '#A24B4A',
      accent: '#72334C',
      accentShadow: '#5B293D',
      background: '#E2D9E4',
      cardBackground: '#D7CED9',
      text: '#1F2025',
      extra: '#E2D9E4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D37674',
      primaryShadow: '#CB5E5C',
      accent: '#875267',
      accentShadow: '#72334C',
      background: '#1F2025',
      cardBackground: '#28292E',
      text: '#E2D9E4',
      extra: '#E5DDE7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Dusk',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#3E3B6A',
      primaryShadow: '#322F55',
      accent: '#505983',
      accentShadow: '#404769',
      background: '#FCFFFE',
      cardBackground: '#EFF2F1',
      text: '#322A51',
      extra: '#CCE0DE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5B5880',
      primaryShadow: '#3E3B6A',
      accent: '#6A7296',
      accentShadow: '#505983',
      background: '#0A0A0F',
      cardBackground: '#111116',
      text: '#FCFFFE',
      extra: '#D1E3E1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunny Glacier',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#9D67FF',
      primaryShadow: '#7E52CC',
      accent: '#FF67FF',
      accentShadow: '#CC52CC',
      background: '#D3F7FF',
      cardBackground: '#C8EBF2',
      text: '#1A1A1A',
      extra: '#FFB9FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AC7EFF',
      primaryShadow: '#9D67FF',
      accent: '#FF7EFF',
      accentShadow: '#FF67FF',
      background: '#0D0B14',
      cardBackground: '#14121B',
      text: '#D3F7FF',
      extra: '#FFC0FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen Pack',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#FFA936',
      primaryShadow: '#CC872B',
      accent: '#FFD647',
      accentShadow: '#CCAB39',
      background: '#FFFF78',
      cardBackground: '#F2F272',
      text: '#464678',
      extra: '#FFC247',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFB654',
      primaryShadow: '#FFA936',
      accent: '#FFDC63',
      accentShadow: '#FFD647',
      background: '#100D0E',
      cardBackground: '#171415',
      text: '#FFFF78',
      extra: '#FFC859',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cool Storm',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D87E46',
      primaryShadow: '#AD6538',
      accent: '#CF4F4F',
      accentShadow: '#A63F3F',
      background: '#E6E1CC',
      cardBackground: '#DBD6C2',
      text: '#2E1B37',
      extra: '#2E1B37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE9162',
      primaryShadow: '#D87E46',
      accent: '#D66969',
      accentShadow: '#CF4F4F',
      background: '#2E1B37',
      cardBackground: '#36243F',
      text: '#E6E1CC',
      extra: '#43324B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Gradient',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#D0988A',
      primaryShadow: '#A67A6E',
      accent: '#EECBA9',
      accentShadow: '#BEA287',
      background: '#F7E0C9',
      cardBackground: '#EBD5BF',
      text: '#1A1A1A',
      extra: '#A9A490',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D7A79C',
      primaryShadow: '#D0988A',
      accent: '#F1D3B6',
      accentShadow: '#EECBA9',
      background: '#0E0D10',
      cardBackground: '#151417',
      text: '#F7E0C9',
      extra: '#B2AD9B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Forest',
    tags: ['curated'],
    light: {
      primary: '#00AAFB',
      primaryShadow: '#0088C9',
      accent: '#00967B',
      accentShadow: '#007862',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#F6092F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B7FC',
      primaryShadow: '#00AAFB',
      accent: '#26A68F',
      accentShadow: '#00967B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#F72244',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Spectrum',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B4A56A',
      primaryShadow: '#908455',
      accent: '#E6D69C',
      accentShadow: '#B8AB7D',
      background: '#EBEAE5',
      cardBackground: '#E2E1DC',
      text: '#393829',
      extra: '#7B7162',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BFB380',
      primaryShadow: '#B4A56A',
      accent: '#EADCAB',
      accentShadow: '#E6D69C',
      background: '#0D0D0F',
      cardBackground: '#141416',
      text: '#E6D69C',
      extra: '#887F72',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Luminous Mural',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A0938E',
      primaryShadow: '#807672',
      accent: '#7D7071',
      accentShadow: '#645A5A',
      background: '#FFF2E6',
      cardBackground: '#F2E6DB',
      text: '#201D1F',
      extra: '#BDB5A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AEA39F',
      primaryShadow: '#A0938E',
      accent: '#918586',
      accentShadow: '#7D7071',
      background: '#201D1F',
      cardBackground: '#292628',
      text: '#FFF2E6',
      extra: '#C4BCB1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glossy Moor',
    tags: ['curated'],
    light: {
      primary: '#442A8C',
      primaryShadow: '#362270',
      accent: '#A8BF4C',
      accentShadow: '#86993D',
      background: '#F4F5E9',
      cardBackground: '#E8E9DD',
      text: '#09091A',
      extra: '#A8BF4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#604A9D',
      primaryShadow: '#442A8C',
      accent: '#B5C967',
      accentShadow: '#A8BF4C',
      background: '#09091A',
      cardBackground: '#131323',
      text: '#F4F5E9',
      extra: '#B1C55E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Synthetic Ocean',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#CD5981',
      primaryShadow: '#A44767',
      accent: '#FF708B',
      accentShadow: '#CC5A6F',
      background: '#FFD7D7',
      cardBackground: '#F2CCCC',
      text: '#1A1A1A',
      extra: '#FFD7D7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D57294',
      primaryShadow: '#CD5981',
      accent: '#FF859C',
      accentShadow: '#FF708B',
      background: '#0E0B10',
      cardBackground: '#151217',
      text: '#FFD7D7',
      extra: '#FFDBDB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rust Dusk',
    tags: ['curated'],
    light: {
      primary: '#CC3385',
      primaryShadow: '#A3296A',
      accent: '#6E1FB1',
      accentShadow: '#58198E',
      background: '#F8FBF3',
      cardBackground: '#ECEEE7',
      text: '#000000',
      extra: '#CC3385',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D45297',
      primaryShadow: '#CC3385',
      accent: '#8441BD',
      accentShadow: '#6E1FB1',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F8FBF3',
      extra: '#D14791',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Grove',
    tags: ['curated'],
    light: {
      primary: '#496C88',
      primaryShadow: '#3A566D',
      accent: '#DCAC7F',
      accentShadow: '#B08A66',
      background: '#E3DDCE',
      cardBackground: '#D8D2C4',
      text: '#1E2733',
      extra: '#1E2733',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#64829A',
      primaryShadow: '#496C88',
      accent: '#E1B892',
      accentShadow: '#DCAC7F',
      background: '#1E2733',
      cardBackground: '#27303B',
      text: '#E3DDCE',
      extra: '#353D47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Plaza',
    tags: ['curated'],
    light: {
      primary: '#0EB968',
      primaryShadow: '#0B9453',
      accent: '#13E67B',
      accentShadow: '#0FB862',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#3C3C3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#32C47F',
      primaryShadow: '#0EB968',
      accent: '#36EA8F',
      accentShadow: '#13E67B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#505050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen Hotspring',
    tags: ['cool', 'curated'],
    light: {
      primary: '#667480',
      primaryShadow: '#525D66',
      accent: '#8598A6',
      accentShadow: '#6A7A85',
      background: '#E5E6E7',
      cardBackground: '#DCDDDE',
      text: '#0B0C0D',
      extra: '#222426',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7D8993',
      primaryShadow: '#667480',
      accent: '#97A7B3',
      accentShadow: '#8598A6',
      background: '#0B0C0D',
      cardBackground: '#151617',
      text: '#C3DCE5',
      extra: '#383A3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Clearing',
    tags: ['curated'],
    light: {
      primary: '#B96234',
      primaryShadow: '#944E2A',
      accent: '#8E243F',
      accentShadow: '#721D32',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#11151A',
      extra: '#75CEFB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C47A52',
      primaryShadow: '#B96234',
      accent: '#9F455C',
      accentShadow: '#8E243F',
      background: '#11151A',
      cardBackground: '#1B1E23',
      text: '#FFFFFF',
      extra: '#83D3FB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Peak',
    tags: ['cool', 'curated'],
    light: {
      primary: '#1B9C95',
      primaryShadow: '#167D77',
      accent: '#0D6D80',
      accentShadow: '#0A5766',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1F102A',
      extra: '#F2AE99',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3DABA5',
      primaryShadow: '#1B9C95',
      accent: '#318393',
      accentShadow: '#0D6D80',
      background: '#1F102A',
      cardBackground: '#281A33',
      text: '#FFFFFF',
      extra: '#F3B6A3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Sculpture',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#238C8C',
      primaryShadow: '#1C7070',
      accent: '#39BFBF',
      accentShadow: '#2E9999',
      background: '#B2FAFF',
      cardBackground: '#A9EEF2',
      text: '#040D09',
      extra: '#0F332E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#449D9D',
      primaryShadow: '#238C8C',
      accent: '#57C9C9',
      accentShadow: '#39BFBF',
      background: '#040D09',
      cardBackground: '#0E1713',
      text: '#B2FAFF',
      extra: '#274743',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amber Desert',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#5F65B5',
      primaryShadow: '#4C5191',
      accent: '#9779D1',
      accentShadow: '#7961A7',
      background: '#E7E0E4',
      cardBackground: '#DBD5D9',
      text: '#1C1529',
      extra: '#E7E0E4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#777CC0',
      primaryShadow: '#5F65B5',
      accent: '#A78DD8',
      accentShadow: '#9779D1',
      background: '#1C1529',
      cardBackground: '#251E32',
      text: '#E7E0E4',
      extra: '#E9E3E7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Dream',
    tags: ['curated'],
    light: {
      primary: '#2DAD6B',
      primaryShadow: '#248A56',
      accent: '#B52D6D',
      accentShadow: '#912457',
      background: '#FDFFCF',
      cardBackground: '#F0F2C5',
      text: '#01061C',
      extra: '#EAF257',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4DB981',
      primaryShadow: '#2DAD6B',
      accent: '#C04D83',
      accentShadow: '#B52D6D',
      background: '#01061C',
      cardBackground: '#0B1025',
      text: '#FDFFCF',
      extra: '#ECF368',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Matte Reef',
    tags: ['curated'],
    light: {
      primary: '#CF552F',
      primaryShadow: '#A64426',
      accent: '#7E24FA',
      accentShadow: '#651DC8',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#160117',
      extra: '#181835',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D66F4E',
      primaryShadow: '#CF552F',
      accent: '#9145FB',
      accentShadow: '#7E24FA',
      background: '#160117',
      cardBackground: '#1F0B20',
      text: '#FFFFFF',
      extra: '#2F2F49',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Field',
    tags: ['curated'],
    light: {
      primary: '#05B5CC',
      primaryShadow: '#0491A3',
      accent: '#007DA8',
      accentShadow: '#006486',
      background: '#FFFEFD',
      cardBackground: '#F2F1F0',
      text: '#190931',
      extra: '#3D193F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2BC0D4',
      primaryShadow: '#05B5CC',
      accent: '#2691B5',
      accentShadow: '#007DA8',
      background: '#190931',
      cardBackground: '#221339',
      text: '#FFFEFD',
      extra: '#503052',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Arid Current',
    tags: ['curated'],
    light: {
      primary: '#DF5F36',
      primaryShadow: '#B24C2B',
      accent: '#9F1D2E',
      accentShadow: '#7F1725',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#555555',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E47754',
      primaryShadow: '#DF5F36',
      accent: '#AD3F4D',
      accentShadow: '#9F1D2E',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#666666',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunny Sculpture',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#FF5900',
      primaryShadow: '#CC4700',
      accent: '#892D00',
      accentShadow: '#6E2400',
      background: '#F1E4DD',
      cardBackground: '#E7DBD4',
      text: '#000000',
      extra: '#402E20',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF7226',
      primaryShadow: '#FF5900',
      accent: '#9B4D26',
      accentShadow: '#892D00',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFB77D',
      extra: '#534336',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Foyer',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BD502F',
      primaryShadow: '#974026',
      accent: '#34D1AF',
      accentShadow: '#2AA78C',
      background: '#F7ECB9',
      cardBackground: '#EBE0B0',
      text: '#291F23',
      extra: '#291F23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C76A4E',
      primaryShadow: '#BD502F',
      accent: '#52D8BB',
      accentShadow: '#34D1AF',
      background: '#291F23',
      cardBackground: '#32282C',
      text: '#F7ECB9',
      extra: '#3E3539',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Warm Spectrum',
    tags: ['bright', 'curated'],
    light: {
      primary: '#34A5D2',
      primaryShadow: '#2A84A8',
      accent: '#466AC0',
      accentShadow: '#38559A',
      background: '#F1DFC6',
      cardBackground: '#E5D4BC',
      text: '#1A1A1A',
      extra: '#EB7EA2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#52B3D9',
      primaryShadow: '#34A5D2',
      accent: '#6280C9',
      accentShadow: '#466AC0',
      background: '#0A0D12',
      cardBackground: '#111419',
      text: '#F1DFC6',
      extra: '#ED8BAB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Western Mirage',
    tags: ['curated'],
    light: {
      primary: '#629098',
      primaryShadow: '#4E737A',
      accent: '#4C3457',
      accentShadow: '#3D2A46',
      background: '#FFE7CD',
      cardBackground: '#F2DBC3',
      text: '#4C3457',
      extra: '#E4A39F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7AA1A7',
      primaryShadow: '#629098',
      accent: '#675270',
      accentShadow: '#4C3457',
      background: '#0B0C11',
      cardBackground: '#121318',
      text: '#FFE7CD',
      extra: '#E7ACA9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classic Moon',
    tags: ['warm', 'curated'],
    light: {
      primary: '#4C2A1B',
      primaryShadow: '#3D2216',
      accent: '#664A4D',
      accentShadow: '#523B3E',
      background: '#E3E0DF',
      cardBackground: '#DAD7D6',
      text: '#4C2A1B',
      extra: '#513E30',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#674A3D',
      primaryShadow: '#4C2A1B',
      accent: '#7D6568',
      accentShadow: '#664A4D',
      background: '#0A090D',
      cardBackground: '#111014',
      text: '#B0CAD9',
      extra: '#625145',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Vineyard',
    tags: ['curated'],
    light: {
      primary: '#B48A60',
      primaryShadow: '#906E4D',
      accent: '#9A5762',
      accentShadow: '#7B464E',
      background: '#EBE8E4',
      cardBackground: '#E2DFDB',
      text: '#1B161D',
      extra: '#483659',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF9C78',
      primaryShadow: '#B48A60',
      accent: '#A9707A',
      accentShadow: '#9A5762',
      background: '#1B161D',
      cardBackground: '#241F26',
      text: '#BDB39F',
      extra: '#5A4A6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silk Heath',
    tags: ['warm', 'curated'],
    light: {
      primary: '#767E3B',
      primaryShadow: '#5E652F',
      accent: '#49642C',
      accentShadow: '#3A5023',
      background: '#EEE9D0',
      cardBackground: '#E2DDC6',
      text: '#303030',
      extra: '#B8B752',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8B9158',
      primaryShadow: '#767E3B',
      accent: '#647B4C',
      accentShadow: '#49642C',
      background: '#0C0C0E',
      cardBackground: '#131315',
      text: '#EEE9D0',
      extra: '#BFBE63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Contemporary Waterfall',
    tags: ['curated'],
    light: {
      primary: '#B65F1B',
      primaryShadow: '#924C16',
      accent: '#004E78',
      accentShadow: '#003E60',
      background: '#F9F4F4',
      cardBackground: '#EDE8E8',
      text: '#08030E',
      extra: '#1B1F1F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C1773D',
      primaryShadow: '#B65F1B',
      accent: '#26698C',
      accentShadow: '#004E78',
      background: '#08030E',
      cardBackground: '#120D18',
      text: '#F9F4F4',
      extra: '#323535',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artisan Arcade',
    tags: ['curated'],
    light: {
      primary: '#A76609',
      primaryShadow: '#865207',
      accent: '#444800',
      accentShadow: '#363A00',
      background: '#E6EAEA',
      cardBackground: '#DBDEDE',
      text: '#222734',
      extra: '#2F1E45',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B47D2E',
      primaryShadow: '#A76609',
      accent: '#606326',
      accentShadow: '#444800',
      background: '#0D0B0C',
      cardBackground: '#141213',
      text: '#E6EAEA',
      extra: '#443558',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neon Turret',
    tags: ['curated'],
    light: {
      primary: '#BF53C9',
      primaryShadow: '#9942A1',
      accent: '#503197',
      accentShadow: '#402779',
      background: '#F6E0C8',
      cardBackground: '#EAD5BE',
      text: '#18215D',
      extra: '#18215D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C96DD1',
      primaryShadow: '#BF53C9',
      accent: '#6A50A7',
      accentShadow: '#503197',
      background: '#18215D',
      cardBackground: '#212A63',
      text: '#F6E0C8',
      extra: '#2F376D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Sculpture',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#BA41D9',
      primaryShadow: '#9534AE',
      accent: '#6C29A6',
      accentShadow: '#562185',
      background: '#ECE2EE',
      cardBackground: '#E3D9E4',
      text: '#141433',
      extra: '#17174D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C45EDF',
      primaryShadow: '#BA41D9',
      accent: '#8249B3',
      accentShadow: '#6C29A6',
      background: '#141433',
      cardBackground: '#1D1D3B',
      text: '#ED9DF2',
      extra: '#2E2E5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bright Pack',
    tags: ['cool', 'curated'],
    light: {
      primary: '#1E1E3C',
      primaryShadow: '#181830',
      accent: '#50506E',
      accentShadow: '#404058',
      background: '#E6E6FF',
      cardBackground: '#DBDBF2',
      text: '#1E1E3C',
      extra: '#BEBEE6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#404059',
      primaryShadow: '#1E1E3C',
      accent: '#6A6A84',
      accentShadow: '#50506E',
      background: '#1E1E3C',
      cardBackground: '#272744',
      text: '#E6E6FF',
      extra: '#C5C5E9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunny Palette',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#367DD8',
      primaryShadow: '#2B64AD',
      accent: '#2A308B',
      accentShadow: '#22266F',
      background: '#E1E7EE',
      cardBackground: '#D8DEE4',
      text: '#1C1530',
      extra: '#1C1530',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5491DE',
      primaryShadow: '#367DD8',
      accent: '#4A4F9C',
      accentShadow: '#2A308B',
      background: '#1C1530',
      cardBackground: '#251E38',
      text: '#8DE2F6',
      extra: '#332C45',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snowy Avalanche',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BB643A',
      primaryShadow: '#96502E',
      accent: '#8D3E29',
      accentShadow: '#713221',
      background: '#ECE5E1',
      cardBackground: '#E3DCD8',
      text: '#3E2B22',
      extra: '#DECEB4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C57B58',
      primaryShadow: '#BB643A',
      accent: '#9E5B49',
      accentShadow: '#8D3E29',
      background: '#0E0B0E',
      cardBackground: '#151215',
      text: '#DECEB4',
      extra: '#E1D3BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Futuristic Vision',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#0E0C1F',
      primaryShadow: '#0B0A19',
      accent: '#4D5964',
      accentShadow: '#3E4750',
      background: '#DEDEDF',
      cardBackground: '#D5D5D6',
      text: '#0E0C1F',
      extra: '#2F323E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#323041',
      primaryShadow: '#0E0C1F',
      accent: '#68727B',
      accentShadow: '#4D5964',
      background: '#0E0C1F',
      cardBackground: '#181628',
      text: '#B8C7BF',
      extra: '#444751',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Mind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#91554A',
      primaryShadow: '#74443B',
      accent: '#B88563',
      accentShadow: '#936A4F',
      background: '#F7E5C8',
      cardBackground: '#EBDABE',
      text: '#42282E',
      extra: '#E0C092',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A26F65',
      primaryShadow: '#91554A',
      accent: '#C3977A',
      accentShadow: '#B88563',
      background: '#0C0B0E',
      cardBackground: '#131215',
      text: '#F7E5C8',
      extra: '#E3C69D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Foyer',
    tags: ['cool', 'curated'],
    light: {
      primary: '#183C5C',
      primaryShadow: '#13304A',
      accent: '#6D6E46',
      accentShadow: '#575838',
      background: '#DFE2E4',
      cardBackground: '#D6D9DB',
      text: '#153457',
      extra: '#153457',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3B5974',
      primaryShadow: '#183C5C',
      accent: '#838462',
      accentShadow: '#6D6E46',
      background: '#090A0F',
      cardBackground: '#101116',
      text: '#CBD5FC',
      extra: '#2C4868',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Meadow',
    tags: ['warm', 'curated'],
    light: {
      primary: '#27D5B2',
      primaryShadow: '#1FAA8E',
      accent: '#910C5C',
      accentShadow: '#740A4A',
      background: '#E0EEEB',
      cardBackground: '#D7E4E2',
      text: '#141013',
      extra: '#580909',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#47DBBE',
      primaryShadow: '#27D5B2',
      accent: '#A23074',
      accentShadow: '#910C5C',
      background: '#141013',
      cardBackground: '#1D1A1C',
      text: '#EEDA28',
      extra: '#692222',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystic Balcony',
    tags: ['bright', 'curated'],
    light: {
      primary: '#EB8D95',
      primaryShadow: '#BC7177',
      accent: '#F5BEAE',
      accentShadow: '#C4988B',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#484C57',
      extra: '#484C57',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EE9EA5',
      primaryShadow: '#EB8D95',
      accent: '#F7C8BA',
      accentShadow: '#F5BEAE',
      background: '#0F0C10',
      cardBackground: '#161317',
      text: '#FFFFFF',
      extra: '#5A5E68',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bright Blizzard',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#B01550',
      primaryShadow: '#8D1140',
      accent: '#FE5BA9',
      accentShadow: '#CB4987',
      background: '#FFCFB9',
      cardBackground: '#F2C5B0',
      text: '#B01550',
      extra: '#FFCFB9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BC386A',
      primaryShadow: '#B01550',
      accent: '#FE74B6',
      accentShadow: '#FE5BA9',
      background: '#0D090E',
      cardBackground: '#141015',
      text: '#FFCFB9',
      extra: '#FFD4C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Relief',
    tags: ['curated'],
    light: {
      primary: '#9AAE56',
      primaryShadow: '#7B8B45',
      accent: '#5B3C64',
      accentShadow: '#493050',
      background: '#EBEFD7',
      cardBackground: '#DFE3CC',
      text: '#1B1920',
      extra: '#1B1920',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A9BA6F',
      primaryShadow: '#9AAE56',
      accent: '#74597B',
      accentShadow: '#5B3C64',
      background: '#1B1920',
      cardBackground: '#242229',
      text: '#EBEFD7',
      extra: '#323036',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Gallery',
    tags: ['cool', 'curated'],
    light: {
      primary: '#3A3277',
      primaryShadow: '#2E285F',
      accent: '#6772A9',
      accentShadow: '#525B87',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#3A3277',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#58518B',
      primaryShadow: '#3A3277',
      accent: '#7E87B6',
      accentShadow: '#6772A9',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#4E4785',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Clear Steppe',
    tags: ['curated'],
    light: {
      primary: '#389900',
      primaryShadow: '#2D7A00',
      accent: '#23B5EF',
      accentShadow: '#1C91BF',
      background: '#FFF9BF',
      cardBackground: '#F2EDB5',
      text: '#140228',
      extra: '#A397D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#56A826',
      primaryShadow: '#389900',
      accent: '#44C0F1',
      accentShadow: '#23B5EF',
      background: '#140228',
      cardBackground: '#1D0C31',
      text: '#FFF9BF',
      extra: '#ACA1DC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Steppe',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#489A0D',
      primaryShadow: '#3A7B0A',
      accent: '#DAF222',
      accentShadow: '#AEC21B',
      background: '#E3E9DE',
      cardBackground: '#DAE0D5',
      text: '#060601',
      extra: '#0B3E08',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#63A931',
      primaryShadow: '#489A0D',
      accent: '#E0F443',
      accentShadow: '#DAF222',
      background: '#060601',
      cardBackground: '#10100B',
      text: '#DAF222',
      extra: '#235121',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Valley',
    tags: ['curated'],
    light: {
      primary: '#3596BA',
      primaryShadow: '#2A7895',
      accent: '#BE2E30',
      accentShadow: '#982526',
      background: '#E5FFB4',
      cardBackground: '#DAF2AB',
      text: '#312031',
      extra: '#312031',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#53A6C4',
      primaryShadow: '#3596BA',
      accent: '#C84D4F',
      accentShadow: '#BE2E30',
      background: '#0A0D12',
      cardBackground: '#111419',
      text: '#E5FFB4',
      extra: '#463646',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystic Sanctuary',
    tags: ['curated'],
    light: {
      primary: '#99AA55',
      primaryShadow: '#7A8844',
      accent: '#336644',
      accentShadow: '#295236',
      background: '#E9EAE4',
      cardBackground: '#E0E1DB',
      text: '#221122',
      extra: '#332233',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A8B76F',
      primaryShadow: '#99AA55',
      accent: '#527D60',
      accentShadow: '#336644',
      background: '#221122',
      cardBackground: '#2B1B2B',
      text: '#DDCCBB',
      extra: '#473847',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scarlet Dream',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B8630F',
      primaryShadow: '#934F0C',
      accent: '#CEAC11',
      accentShadow: '#A58A0E',
      background: '#FAEBBC',
      cardBackground: '#EEDFB3',
      text: '#2F0015',
      extra: '#483332',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C37A33',
      primaryShadow: '#B8630F',
      accent: '#D5B835',
      accentShadow: '#CEAC11',
      background: '#2F0015',
      cardBackground: '#370A1E',
      text: '#FAEBBC',
      extra: '#5A4747',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Daybreak Dream',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4F53AB',
      primaryShadow: '#3F4289',
      accent: '#3E2469',
      accentShadow: '#321D54',
      background: '#E3E3EA',
      cardBackground: '#DADAE1',
      text: '#3E2469',
      extra: '#83E6CD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#696DB8',
      primaryShadow: '#4F53AB',
      accent: '#5B4580',
      accentShadow: '#3E2469',
      background: '#0A0A11',
      cardBackground: '#111118',
      text: '#83E6CD',
      extra: '#8FE9D2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Star',
    tags: ['curated'],
    light: {
      primary: '#249E4F',
      primaryShadow: '#1D7E3F',
      accent: '#F28941',
      accentShadow: '#C26E34',
      background: '#FFF1A9',
      cardBackground: '#F2E5A1',
      text: '#120E23',
      extra: '#472859',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#45AD69',
      primaryShadow: '#249E4F',
      accent: '#F49B5E',
      accentShadow: '#F28941',
      background: '#120E23',
      cardBackground: '#1B182C',
      text: '#FFF1A9',
      extra: '#593E6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Peak',
    tags: ['cool', 'curated'],
    light: {
      primary: '#7900C9',
      primaryShadow: '#6100A1',
      accent: '#DD42F5',
      accentShadow: '#B135C4',
      background: '#B5FDFF',
      cardBackground: '#ACF0F2',
      text: '#7900C9',
      extra: '#8F9EFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8D26D1',
      primaryShadow: '#7900C9',
      accent: '#E25EF7',
      accentShadow: '#DD42F5',
      background: '#0C0812',
      cardBackground: '#130F19',
      text: '#B5FDFF',
      extra: '#9AA8FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Pasture',
    tags: ['cool', 'curated'],
    light: {
      primary: '#C25E22',
      primaryShadow: '#9B4B1B',
      accent: '#BC1334',
      accentShadow: '#960F2A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#4B003B',
      extra: '#ACF693',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CB7643',
      primaryShadow: '#C25E22',
      accent: '#C63652',
      accentShadow: '#BC1334',
      background: '#4B003B',
      cardBackground: '#520A43',
      text: '#FFFFFF',
      extra: '#B4F79E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fleeting Jungle',
    tags: ['curated'],
    light: {
      primary: '#E66A6A',
      primaryShadow: '#B85555',
      accent: '#253870',
      accentShadow: '#1E2D5A',
      background: '#FFFFF2',
      cardBackground: '#F2F2E6',
      text: '#18143B',
      extra: '#18143B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EA8080',
      primaryShadow: '#E66A6A',
      accent: '#465685',
      accentShadow: '#253870',
      background: '#18143B',
      cardBackground: '#211D43',
      text: '#FFFFF2',
      extra: '#2F2C4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cosmic Tapestry',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#8F3808',
      primaryShadow: '#722D06',
      accent: '#CF7A3A',
      accentShadow: '#A6622E',
      background: '#FADD93',
      cardBackground: '#EED28C',
      text: '#2E0D20',
      extra: '#451C1C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A0562D',
      primaryShadow: '#8F3808',
      accent: '#D68E58',
      accentShadow: '#CF7A3A',
      background: '#2E0D20',
      cardBackground: '#361729',
      text: '#FADD93',
      extra: '#583333',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Daybreak Grove',
    tags: ['curated'],
    light: {
      primary: '#BD46B7',
      primaryShadow: '#973892',
      accent: '#A13567',
      accentShadow: '#812A52',
      background: '#ECE2EB',
      cardBackground: '#E3D9E2',
      text: '#252945',
      extra: '#252945',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C762C2',
      primaryShadow: '#BD46B7',
      accent: '#AF537E',
      accentShadow: '#A13567',
      background: '#0E0A11',
      cardBackground: '#151118',
      text: '#D9D9CC',
      extra: '#3B3E58',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Turret',
    tags: ['bright', 'curated'],
    light: {
      primary: '#498F94',
      primaryShadow: '#3A7276',
      accent: '#EF9E6D',
      accentShadow: '#BF7E57',
      background: '#F2FFE8',
      cardBackground: '#E6F2DC',
      text: '#322F3D',
      extra: '#C2BFC8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#64A0A4',
      primaryShadow: '#498F94',
      accent: '#F1AD83',
      accentShadow: '#EF9E6D',
      background: '#0A0C10',
      cardBackground: '#111317',
      text: '#F2FFE8',
      extra: '#C8C5CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Coast',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#7557A8',
      primaryShadow: '#5E4686',
      accent: '#4A3D85',
      accentShadow: '#3B316A',
      background: '#E6E4EA',
      cardBackground: '#DDDBE1',
      text: '#020408',
      extra: '#131934',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8A70B5',
      primaryShadow: '#7557A8',
      accent: '#655A97',
      accentShadow: '#4A3D85',
      background: '#020408',
      cardBackground: '#0C0E12',
      text: '#F0F0F0',
      extra: '#2B3048',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fierce Dusk',
    tags: ['warm', 'curated'],
    light: {
      primary: '#72424F',
      primaryShadow: '#5B353F',
      accent: '#8F626F',
      accentShadow: '#724E59',
      background: '#E6E2E3',
      cardBackground: '#DDD9DA',
      text: '#392B35',
      extra: '#392B35',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#875E69',
      primaryShadow: '#72424F',
      accent: '#A07A85',
      accentShadow: '#8F626F',
      background: '#0B0A0E',
      cardBackground: '#121115',
      text: '#F0F0F0',
      extra: '#4D4049',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crisp Tundra',
    tags: ['curated'],
    light: {
      primary: '#0C5066',
      primaryShadow: '#0A4052',
      accent: '#D99741',
      accentShadow: '#AE7934',
      background: '#F0E1D1',
      cardBackground: '#E4D6C7',
      text: '#181B24',
      extra: '#181B24',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#306A7D',
      primaryShadow: '#0C5066',
      accent: '#DFA75E',
      accentShadow: '#D99741',
      background: '#181B24',
      cardBackground: '#21242D',
      text: '#F0E1D1',
      extra: '#2F323A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Soul',
    tags: ['cool', 'curated'],
    light: {
      primary: '#BF277E',
      primaryShadow: '#991F65',
      accent: '#7B1787',
      accentShadow: '#62126C',
      background: '#E3EDE7',
      cardBackground: '#D8E1DB',
      text: '#161439',
      extra: '#C2DAA5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C94791',
      primaryShadow: '#BF277E',
      accent: '#8F3A99',
      accentShadow: '#7B1787',
      background: '#161439',
      cardBackground: '#1F1D41',
      text: '#E3EDE7',
      extra: '#C8DEAE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Keep',
    tags: ['curated'],
    light: {
      primary: '#D45211',
      primaryShadow: '#AA420E',
      accent: '#FF9537',
      accentShadow: '#CC772C',
      background: '#FFECD6',
      cardBackground: '#F2E0CB',
      text: '#000809',
      extra: '#09243B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA6C35',
      primaryShadow: '#D45211',
      accent: '#FFA555',
      accentShadow: '#FF9537',
      background: '#000809',
      cardBackground: '#0A1213',
      text: '#FFECD6',
      extra: '#223A4F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bright Rapids',
    tags: ['warm', 'curated'],
    light: {
      primary: '#946663',
      primaryShadow: '#76524F',
      accent: '#BF9A84',
      accentShadow: '#997B6A',
      background: '#E9E5E5',
      cardBackground: '#E0DCDC',
      text: '#2E2626',
      extra: '#2E2626',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A47D7A',
      primaryShadow: '#946663',
      accent: '#C9A996',
      accentShadow: '#BF9A84',
      background: '#0C0B0F',
      cardBackground: '#131216',
      text: '#D4C9BA',
      extra: '#433C3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Lava',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DA298E',
      primaryShadow: '#AE2172',
      accent: '#2E2B12',
      accentShadow: '#25220E',
      background: '#F8F3FD',
      cardBackground: '#ECE7F0',
      text: '#2E2B12',
      extra: '#FAC6B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0499F',
      primaryShadow: '#DA298E',
      accent: '#4D4B36',
      accentShadow: '#2E2B12',
      background: '#0F0910',
      cardBackground: '#161017',
      text: '#F8F3FD',
      extra: '#FBCCBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Hue',
    tags: ['warm', 'curated'],
    light: {
      primary: '#258B5B',
      primaryShadow: '#1E6F49',
      accent: '#E5A251',
      accentShadow: '#B78241',
      background: '#E0E8E4',
      cardBackground: '#D7DFDB',
      text: '#2B2E25',
      extra: '#2B2E25',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#469C74',
      primaryShadow: '#258B5B',
      accent: '#E9B06B',
      accentShadow: '#E5A251',
      background: '#090C0F',
      cardBackground: '#101316',
      text: '#DBDA7B',
      extra: '#40433B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lunar Mind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#09B4AC',
      primaryShadow: '#07908A',
      accent: '#CF981F',
      accentShadow: '#A67A19',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#390C14',
      extra: '#390C14',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2EBFB8',
      primaryShadow: '#09B4AC',
      accent: '#D6A741',
      accentShadow: '#CF981F',
      background: '#390C14',
      cardBackground: '#41161D',
      text: '#FFFFFF',
      extra: '#4D242C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blaze Ravine',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FD6C09',
      primaryShadow: '#CA5607',
      accent: '#DB4422',
      accentShadow: '#AF361B',
      background: '#FFF2B9',
      cardBackground: '#F2E6B0',
      text: '#290E0F',
      extra: '#FFDA80',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FD822E',
      primaryShadow: '#FD6C09',
      accent: '#E06043',
      accentShadow: '#DB4422',
      background: '#290E0F',
      cardBackground: '#321819',
      text: '#FFF2B9',
      extra: '#FFDE8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Canyon',
    tags: ['warm', 'curated'],
    light: {
      primary: '#833735',
      primaryShadow: '#692C2A',
      accent: '#492622',
      accentShadow: '#3A1E1B',
      background: '#F6E5D3',
      cardBackground: '#EADAC8',
      text: '#0D1013',
      extra: '#C9C1B0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#965553',
      primaryShadow: '#833735',
      accent: '#644743',
      accentShadow: '#492622',
      background: '#0D1013',
      cardBackground: '#171A1C',
      text: '#F6E5D3',
      extra: '#CEC7B8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Warm Lake',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00AAFF',
      primaryShadow: '#0088CC',
      accent: '#AA5500',
      accentShadow: '#884400',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#0000FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B7FF',
      primaryShadow: '#00AAFF',
      accent: '#B76F26',
      accentShadow: '#AA5500',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#1A1AFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Springs',
    tags: ['warm', 'curated'],
    light: {
      primary: '#338E00',
      primaryShadow: '#297200',
      accent: '#63CE08',
      accentShadow: '#4FA506',
      background: '#DEFFDE',
      cardBackground: '#D3F2D3',
      text: '#383838',
      extra: '#383838',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#529F26',
      primaryShadow: '#338E00',
      accent: '#7AD52D',
      accentShadow: '#63CE08',
      background: '#0A0C0C',
      cardBackground: '#111313',
      text: '#DEFFDE',
      extra: '#4C4C4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Amber Cloister',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CE300E',
      primaryShadow: '#A5260B',
      accent: '#92003A',
      accentShadow: '#75002E',
      background: '#F7FFE5',
      cardBackground: '#EBF2DA',
      text: '#160020',
      extra: '#B0D7B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D54F32',
      primaryShadow: '#CE300E',
      accent: '#A22658',
      accentShadow: '#92003A',
      background: '#160020',
      cardBackground: '#1F0A29',
      text: '#F7FFE5',
      extra: '#B8DBBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Floe',
    tags: ['cool', 'curated'],
    light: {
      primary: '#1B9C95',
      primaryShadow: '#167D77',
      accent: '#0D6D80',
      accentShadow: '#0A5766',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1F102A',
      extra: '#F2AE99',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3DABA5',
      primaryShadow: '#1B9C95',
      accent: '#318393',
      accentShadow: '#0D6D80',
      background: '#1F102A',
      cardBackground: '#281A33',
      text: '#FFFFFF',
      extra: '#F3B6A3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Sunset',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BF9C57',
      primaryShadow: '#997D46',
      accent: '#703D46',
      accentShadow: '#5A3138',
      background: '#ECE9E4',
      cardBackground: '#E3E0DB',
      text: '#1A1A1A',
      extra: '#E9DB71',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C9AB70',
      primaryShadow: '#BF9C57',
      accent: '#855A62',
      accentShadow: '#703D46',
      background: '#0E0D0F',
      cardBackground: '#151416',
      text: '#E9DB71',
      extra: '#EBDF7F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Aura',
    tags: ['curated'],
    light: {
      primary: '#4789B2',
      primaryShadow: '#396E8E',
      accent: '#FC9849',
      accentShadow: '#CA7A3A',
      background: '#FFF9B6',
      cardBackground: '#F2EDAD',
      text: '#29213B',
      extra: '#29213B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#639BBE',
      primaryShadow: '#4789B2',
      accent: '#FCA764',
      accentShadow: '#FC9849',
      background: '#0A0C11',
      cardBackground: '#111318',
      text: '#FFF9B6',
      extra: '#3E374F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neo Glade',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#9E6F6C',
      primaryShadow: '#7E5956',
      accent: '#CF9486',
      accentShadow: '#A6766B',
      background: '#F6EAD4',
      cardBackground: '#EADEC9',
      text: '#1A1A1A',
      extra: '#F5CCB3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD8582',
      primaryShadow: '#9E6F6C',
      accent: '#D6A498',
      accentShadow: '#CF9486',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#F6EAD4',
      extra: '#F6D1BB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Terrace',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#2B4158',
      primaryShadow: '#223446',
      accent: '#8B6F7B',
      accentShadow: '#6F5962',
      background: '#FFF9F1',
      cardBackground: '#F2EDE5',
      text: '#2B4158',
      extra: '#CAAEA3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4B5E71',
      primaryShadow: '#2B4158',
      accent: '#9C858F',
      accentShadow: '#8B6F7B',
      background: '#090A0F',
      cardBackground: '#101116',
      text: '#FFF9F1',
      extra: '#CFB6AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gleam Spectrum',
    tags: ['bright', 'curated'],
    light: {
      primary: '#F7B58C',
      primaryShadow: '#C69170',
      accent: '#5D446C',
      accentShadow: '#4A3656',
      background: '#FFF2E5',
      cardBackground: '#F2E6DA',
      text: '#1A1A1A',
      extra: '#739CA1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F8C09D',
      primaryShadow: '#F7B58C',
      accent: '#756082',
      accentShadow: '#5D446C',
      background: '#0F0D10',
      cardBackground: '#161417',
      text: '#FFF2E5',
      extra: '#81A6AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble Peak',
    tags: ['cool', 'curated'],
    light: {
      primary: '#EE964B',
      primaryShadow: '#BE783C',
      accent: '#393541',
      accentShadow: '#2E2A34',
      background: '#FCFCFC',
      cardBackground: '#EFEFEF',
      text: '#191A1F',
      extra: '#191A1F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F1A666',
      primaryShadow: '#EE964B',
      accent: '#57535E',
      accentShadow: '#393541',
      background: '#191A1F',
      cardBackground: '#222328',
      text: '#FCFCFC',
      extra: '#303135',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Sky',
    tags: ['curated'],
    light: {
      primary: '#E56500',
      primaryShadow: '#B75100',
      accent: '#BE4D00',
      accentShadow: '#983E00',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#DFDFDF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E97C26',
      primaryShadow: '#E56500',
      accent: '#C86826',
      accentShadow: '#BE4D00',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#E2E2E2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cotton Mesa',
    tags: ['bright', 'curated'],
    light: {
      primary: '#E16956',
      primaryShadow: '#B45445',
      accent: '#8D3564',
      accentShadow: '#712A50',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#342467',
      extra: '#342467',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E6806F',
      primaryShadow: '#E16956',
      accent: '#9E537B',
      accentShadow: '#8D3564',
      background: '#0F0B0F',
      cardBackground: '#161216',
      text: '#FFFFFF',
      extra: '#483A76',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Steppe',
    tags: ['cool', 'curated'],
    light: {
      primary: '#854731',
      primaryShadow: '#6A3927',
      accent: '#52A593',
      accentShadow: '#428476',
      background: '#F0FAFD',
      cardBackground: '#E4EEF0',
      text: '#1D2531',
      extra: '#1D2531',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#976350',
      primaryShadow: '#854731',
      accent: '#6CB3A3',
      accentShadow: '#52A593',
      background: '#1D2531',
      cardBackground: '#262E39',
      text: '#F0FAFD',
      extra: '#343B46',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Urban Nova',
    tags: ['curated'],
    light: {
      primary: '#F03554',
      primaryShadow: '#C02A43',
      accent: '#B8185D',
      accentShadow: '#93134A',
      background: '#CEDDF0',
      cardBackground: '#C4D2E4',
      text: '#45054D',
      extra: '#CEDDF0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F2536E',
      primaryShadow: '#F03554',
      accent: '#C33B75',
      accentShadow: '#B8185D',
      background: '#45054D',
      cardBackground: '#4C0F54',
      text: '#CEDDF0',
      extra: '#D3E0F2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hazy Bloom',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CE6B40',
      primaryShadow: '#A55633',
      accent: '#6F324E',
      accentShadow: '#59283E',
      background: '#FFF4B0',
      cardBackground: '#F2E8A7',
      text: '#180C21',
      extra: '#859999',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D5815D',
      primaryShadow: '#CE6B40',
      accent: '#855169',
      accentShadow: '#6F324E',
      background: '#180C21',
      cardBackground: '#21162A',
      text: '#FFF4B0',
      extra: '#91A3A3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Tundra',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B86D30',
      primaryShadow: '#935726',
      accent: '#913426',
      accentShadow: '#742A1E',
      background: '#ECE6E1',
      cardBackground: '#E3DDD8',
      text: '#461D2E',
      extra: '#461D2E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C3834F',
      primaryShadow: '#B86D30',
      accent: '#A25247',
      accentShadow: '#913426',
      background: '#0E0B0D',
      cardBackground: '#151214',
      text: '#CEC8B6',
      extra: '#593443',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Gallery',
    tags: ['cool', 'curated'],
    light: {
      primary: '#03A66B',
      primaryShadow: '#028556',
      accent: '#F3AC00',
      accentShadow: '#C28A00',
      background: '#FCFFFF',
      cardBackground: '#EFF2F2',
      text: '#0A0D22',
      extra: '#296BAA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#29B381',
      primaryShadow: '#03A66B',
      accent: '#F5B826',
      accentShadow: '#F3AC00',
      background: '#0A0D22',
      cardBackground: '#14172B',
      text: '#FCFFFF',
      extra: '#3E7AB3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Temple',
    tags: ['curated'],
    light: {
      primary: '#D46D48',
      primaryShadow: '#AA573A',
      accent: '#8F5732',
      accentShadow: '#724628',
      background: '#F0EBEB',
      cardBackground: '#E4DFDF',
      text: '#0B0B22',
      extra: '#3A1F2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA8363',
      primaryShadow: '#D46D48',
      accent: '#A07051',
      accentShadow: '#8F5732',
      background: '#0B0B22',
      cardBackground: '#15152B',
      text: '#F0EBEB',
      extra: '#4E3544',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ruby Garden',
    tags: ['curated'],
    light: {
      primary: '#258CB8',
      primaryShadow: '#1E7093',
      accent: '#45E02D',
      accentShadow: '#37B324',
      background: '#FCFFF2',
      cardBackground: '#EFF2E6',
      text: '#101114',
      extra: '#1D2247',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#469DC3',
      primaryShadow: '#258CB8',
      accent: '#61E54D',
      accentShadow: '#45E02D',
      background: '#101114',
      cardBackground: '#1A1B1D',
      text: '#FCFFF2',
      extra: '#343859',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gleam Wave',
    tags: ['curated'],
    light: {
      primary: '#D84710',
      primaryShadow: '#AD390D',
      accent: '#D81111',
      accentShadow: '#AD0E0E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#FF946D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE6334',
      primaryShadow: '#D84710',
      accent: '#DE3535',
      accentShadow: '#D81111',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#FF9F7C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jade Mirage',
    tags: ['curated'],
    light: {
      primary: '#BD8F00',
      primaryShadow: '#977200',
      accent: '#A56D00',
      accentShadow: '#845700',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#232429',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C7A026',
      primaryShadow: '#BD8F00',
      accent: '#B38326',
      accentShadow: '#A56D00',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#393A3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Arcade',
    tags: ['curated'],
    light: {
      primary: '#380E27',
      primaryShadow: '#2D0B1F',
      accent: '#20494B',
      accentShadow: '#1A3A3C',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#CEA19D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#563247',
      primaryShadow: '#380E27',
      accent: '#416466',
      accentShadow: '#20494B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#D3AAA7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Eclipse',
    tags: ['curated'],
    light: {
      primary: '#21BFBF',
      primaryShadow: '#1A9999',
      accent: '#E41244',
      accentShadow: '#B60E36',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#130732',
      extra: '#F6C138',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42C9C9',
      primaryShadow: '#21BFBF',
      accent: '#E83660',
      accentShadow: '#E41244',
      background: '#130732',
      cardBackground: '#1C113A',
      text: '#FFFFFF',
      extra: '#F7C74C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lunar Desert',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C36D44',
      primaryShadow: '#9C5736',
      accent: '#E39454',
      accentShadow: '#B67643',
      background: '#F7ECAE',
      cardBackground: '#EBE0A5',
      text: '#371A47',
      extra: '#371A47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CC8360',
      primaryShadow: '#C36D44',
      accent: '#E7A46E',
      accentShadow: '#E39454',
      background: '#0E0B0E',
      cardBackground: '#151215',
      text: '#F7ECAE',
      extra: '#4B3159',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Stormy Cloud',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#C71F22',
      primaryShadow: '#9F191B',
      accent: '#7C1327',
      accentShadow: '#630F1F',
      background: '#FBDFD0',
      cardBackground: '#EED4C6',
      text: '#070020',
      extra: '#580D27',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF4143',
      primaryShadow: '#C71F22',
      accent: '#903647',
      accentShadow: '#7C1327',
      background: '#070020',
      cardBackground: '#110A29',
      text: '#FBDFD0',
      extra: '#69253D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Olive Aurora',
    tags: ['curated'],
    light: {
      primary: '#259322',
      primaryShadow: '#1E761B',
      accent: '#EEB333',
      accentShadow: '#BE8F29',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1A1828',
      extra: '#1A1828',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#46A343',
      primaryShadow: '#259322',
      accent: '#F1BE52',
      accentShadow: '#EEB333',
      background: '#1A1828',
      cardBackground: '#232131',
      text: '#FFFFFF',
      extra: '#312F3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Crater',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#708BD4',
      primaryShadow: '#5A6FAA',
      accent: '#A49DD4',
      accentShadow: '#837EAA',
      background: '#E6E8EE',
      cardBackground: '#DDDFE4',
      text: '#1A1A1A',
      extra: '#F6C9D2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#859CDA',
      primaryShadow: '#708BD4',
      accent: '#B2ACDA',
      accentShadow: '#A49DD4',
      background: '#0B0C12',
      cardBackground: '#121319',
      text: '#F6C9D2',
      extra: '#F7CED7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Iron Clearing',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B1863E',
      primaryShadow: '#8E6B32',
      accent: '#7A5235',
      accentShadow: '#62422A',
      background: '#EBE8E2',
      cardBackground: '#E2DFD9',
      text: '#2A1D17',
      extra: '#E0C79F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BD985B',
      primaryShadow: '#B1863E',
      accent: '#8E6C53',
      accentShadow: '#7A5235',
      background: '#2A1D17',
      cardBackground: '#332620',
      text: '#E0C79F',
      extra: '#E3CDA9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Ocean',
    tags: ['dark', 'curated'],
    light: {
      primary: '#3E730C',
      primaryShadow: '#325C0A',
      accent: '#B8813C',
      accentShadow: '#936730',
      background: '#E2E6DE',
      cardBackground: '#D9DDD5',
      text: '#231826',
      extra: '#231826',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5B8830',
      primaryShadow: '#3E730C',
      accent: '#C39459',
      accentShadow: '#B8813C',
      background: '#231826',
      cardBackground: '#2C212F',
      text: '#F0F0F0',
      extra: '#392F3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Summer Canvas',
    tags: ['curated'],
    light: {
      primary: '#B8636E',
      primaryShadow: '#934F58',
      accent: '#74C261',
      accentShadow: '#5D9B4E',
      background: '#E8EDEC',
      cardBackground: '#DCE1E0',
      text: '#19181C',
      extra: '#57607D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C37A84',
      primaryShadow: '#B8636E',
      accent: '#89CB79',
      accentShadow: '#74C261',
      background: '#19181C',
      cardBackground: '#222125',
      text: '#E8EDEC',
      extra: '#68708A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Petal',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#599B86',
      primaryShadow: '#477C6B',
      accent: '#552F30',
      accentShadow: '#442626',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#552F30',
      extra: '#F7C8DD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#72AA98',
      primaryShadow: '#599B86',
      accent: '#6F4E4F',
      accentShadow: '#552F30',
      background: '#0B0D10',
      cardBackground: '#121417',
      text: '#FFFFFF',
      extra: '#F8CEE0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ivory Harbor',
    tags: ['cool', 'curated'],
    light: {
      primary: '#DFDFDF',
      primaryShadow: '#B2B2B2',
      accent: '#9F9F9F',
      accentShadow: '#7F7F7F',
      background: '#DFDFDF',
      cardBackground: '#D4D4D4',
      text: '#202020',
      extra: '#606060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E4E4E4',
      primaryShadow: '#DFDFDF',
      accent: '#ADADAD',
      accentShadow: '#9F9F9F',
      background: '#202020',
      cardBackground: '#292929',
      text: '#DFDFDF',
      extra: '#707070',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ice Wind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B56246',
      primaryShadow: '#914E38',
      accent: '#D88E4F',
      accentShadow: '#AD723F',
      background: '#FFFFFA',
      cardBackground: '#F2F2EE',
      text: '#0D0D0F',
      extra: '#E5E3E3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C07A62',
      primaryShadow: '#B56246',
      accent: '#DE9F69',
      accentShadow: '#D88E4F',
      background: '#0D0D0F',
      cardBackground: '#171719',
      text: '#FFFFFA',
      extra: '#E8E6E6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Mural',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#DA3C14',
      primaryShadow: '#AE3010',
      accent: '#E67B33',
      accentShadow: '#B86229',
      background: '#EEE2DE',
      cardBackground: '#E4D9D5',
      text: '#030205',
      extra: '#EBA566',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E05937',
      primaryShadow: '#DA3C14',
      accent: '#EA8F52',
      accentShadow: '#E67B33',
      background: '#030205',
      cardBackground: '#0D0C0F',
      text: '#EBA566',
      extra: '#EDAE75',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunny Iceberg',
    tags: ['dark', 'curated'],
    light: {
      primary: '#826B24',
      primaryShadow: '#68561D',
      accent: '#A9AB3A',
      accentShadow: '#87892E',
      background: '#DFE0BA',
      cardBackground: '#D4D5B1',
      text: '#0B1412',
      extra: '#1F2B24',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#958145',
      primaryShadow: '#826B24',
      accent: '#B6B858',
      accentShadow: '#A9AB3A',
      background: '#0B1412',
      cardBackground: '#151D1B',
      text: '#DFE0BA',
      extra: '#35403A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Sunset',
    tags: ['cool', 'curated'],
    light: {
      primary: '#5D91D8',
      primaryShadow: '#4A74AD',
      accent: '#3D8367',
      accentShadow: '#316952',
      background: '#EFF5F5',
      cardBackground: '#E3E9E9',
      text: '#23232E',
      extra: '#C686CA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#75A2DE',
      primaryShadow: '#5D91D8',
      accent: '#5A967E',
      accentShadow: '#3D8367',
      background: '#23232E',
      cardBackground: '#2C2C36',
      text: '#EFF5F5',
      extra: '#CC92CF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Cascade',
    tags: ['dark', 'curated'],
    light: {
      primary: '#372C38',
      primaryShadow: '#2C232D',
      accent: '#7A7272',
      accentShadow: '#625B5B',
      background: '#E1E0E1',
      cardBackground: '#D8D7D8',
      text: '#211F1F',
      extra: '#211F1F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#554C56',
      primaryShadow: '#372C38',
      accent: '#8E8787',
      accentShadow: '#7A7272',
      background: '#211F1F',
      cardBackground: '#2A2828',
      text: '#F0F0F0',
      extra: '#373535',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mediterranean Nebula',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C95B40',
      primaryShadow: '#A14933',
      accent: '#39CEC2',
      accentShadow: '#2EA59B',
      background: '#FAFFF9',
      cardBackground: '#EEF2ED',
      text: '#332422',
      extra: '#332422',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D1745D',
      primaryShadow: '#C95B40',
      accent: '#57D5CB',
      accentShadow: '#39CEC2',
      background: '#0E0B0E',
      cardBackground: '#151215',
      text: '#FAFFF9',
      extra: '#473A38',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Iron Nova',
    tags: ['curated'],
    light: {
      primary: '#156D8E',
      primaryShadow: '#115772',
      accent: '#00474F',
      accentShadow: '#00393F',
      background: '#DEE6E8',
      cardBackground: '#D5DDDF',
      text: '#00474F',
      extra: '#225054',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#38839F',
      primaryShadow: '#156D8E',
      accent: '#266369',
      accentShadow: '#00474F',
      background: '#090B10',
      cardBackground: '#101217',
      text: '#FFBDBB',
      extra: '#386265',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cosmic Patio',
    tags: ['cool', 'curated'],
    light: {
      primary: '#6C56AB',
      primaryShadow: '#564589',
      accent: '#48376C',
      accentShadow: '#3A2C56',
      background: '#E5E4EA',
      cardBackground: '#DCDBE1',
      text: '#48376C',
      extra: '#B883B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#826FB8',
      primaryShadow: '#6C56AB',
      accent: '#635582',
      accentShadow: '#48376C',
      background: '#0B0B11',
      cardBackground: '#121218',
      text: '#BFDCE1',
      extra: '#BF8FC0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cotton Springs',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9F8236',
      primaryShadow: '#7F682B',
      accent: '#D99951',
      accentShadow: '#AE7A41',
      background: '#EAE7E1',
      cardBackground: '#E1DED8',
      text: '#173F37',
      extra: '#AA5E3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD9554',
      primaryShadow: '#9F8236',
      accent: '#DFA86B',
      accentShadow: '#D99951',
      background: '#0D0C0E',
      cardBackground: '#141315',
      text: '#DBB66C',
      extra: '#B36E51',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Desert',
    tags: ['cool', 'curated'],
    light: {
      primary: '#CE2F7F',
      primaryShadow: '#A52666',
      accent: '#877AFF',
      accentShadow: '#6C62CC',
      background: '#DDE4E8',
      cardBackground: '#D2D9DC',
      text: '#301C44',
      extra: '#DDE4E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D54E92',
      primaryShadow: '#CE2F7F',
      accent: '#998EFF',
      accentShadow: '#877AFF',
      background: '#0E0910',
      cardBackground: '#151017',
      text: '#DDE4E8',
      extra: '#E0E7EA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crimson Bay',
    tags: ['cool', 'curated'],
    light: {
      primary: '#65DB73',
      primaryShadow: '#51AF5C',
      accent: '#AB6187',
      accentShadow: '#894E6C',
      background: '#D8E8E6',
      cardBackground: '#CDDCDB',
      text: '#211E33',
      extra: '#EBC686',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7CE088',
      primaryShadow: '#65DB73',
      accent: '#B87999',
      accentShadow: '#AB6187',
      background: '#211E33',
      cardBackground: '#2A273B',
      text: '#D8E8E6',
      extra: '#EDCC92',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Heath',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#BF858F',
      primaryShadow: '#996A72',
      accent: '#FFB4A2',
      accentShadow: '#CC9082',
      background: '#FFCDB2',
      cardBackground: '#F2C3A9',
      text: '#1A1A1A',
      extra: '#FFCDB2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C997A0',
      primaryShadow: '#BF858F',
      accent: '#FFBFB0',
      accentShadow: '#FFB4A2',
      background: '#0E0C10',
      cardBackground: '#151317',
      text: '#FFCDB2',
      extra: '#FFD2BA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Wave',
    tags: ['curated'],
    light: {
      primary: '#7C714A',
      primaryShadow: '#635A3B',
      accent: '#264B38',
      accentShadow: '#1E3C2D',
      background: '#E7E6E3',
      cardBackground: '#DEDDDA',
      text: '#264B38',
      extra: '#E5D8AC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#908665',
      primaryShadow: '#7C714A',
      accent: '#476656',
      accentShadow: '#264B38',
      background: '#0C0B0E',
      cardBackground: '#131215',
      text: '#E5D8AC',
      extra: '#E8DCB4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Iceberg',
    tags: ['curated'],
    light: {
      primary: '#649092',
      primaryShadow: '#507375',
      accent: '#27393F',
      accentShadow: '#1F2E32',
      background: '#E5E8E8',
      cardBackground: '#DCDFDF',
      text: '#27393F',
      extra: '#6F535B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7BA1A2',
      primaryShadow: '#649092',
      accent: '#47575C',
      accentShadow: '#27393F',
      background: '#0B0C10',
      cardBackground: '#121317',
      text: '#CFC89D',
      extra: '#7D646B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Calm Vision',
    tags: ['curated'],
    light: {
      primary: '#0EB968',
      primaryShadow: '#0B9453',
      accent: '#13E67B',
      accentShadow: '#0FB862',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#3C3C3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#32C47F',
      primaryShadow: '#0EB968',
      accent: '#36EA8F',
      accentShadow: '#13E67B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#505050',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Veranda',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FF4E20',
      primaryShadow: '#CC3E1A',
      accent: '#E08A1E',
      accentShadow: '#B36E18',
      background: '#FFEEE5',
      cardBackground: '#F2E2DA',
      text: '#171412',
      extra: '#191F45',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6941',
      primaryShadow: '#FF4E20',
      accent: '#E59C40',
      accentShadow: '#E08A1E',
      background: '#171412',
      cardBackground: '#201D1B',
      text: '#FFEEE5',
      extra: '#303558',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Prism',
    tags: ['curated'],
    light: {
      primary: '#A65805',
      primaryShadow: '#854604',
      accent: '#EA7D10',
      accentShadow: '#BB640D',
      background: '#E5FBF3',
      cardBackground: '#DAEEE7',
      text: '#020918',
      extra: '#424E66',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B3712B',
      primaryShadow: '#A65805',
      accent: '#ED9134',
      accentShadow: '#EA7D10',
      background: '#020918',
      cardBackground: '#0C1321',
      text: '#E5FBF3',
      extra: '#556075',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glossy Harbor',
    tags: ['curated'],
    light: {
      primary: '#E97C07',
      primaryShadow: '#BA6306',
      accent: '#C94814',
      accentShadow: '#A13A10',
      background: '#FFFFE0',
      cardBackground: '#F2F2D5',
      text: '#161928',
      extra: '#161928',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC902C',
      primaryShadow: '#E97C07',
      accent: '#D16337',
      accentShadow: '#C94814',
      background: '#161928',
      cardBackground: '#1F2231',
      text: '#FFFFE0',
      extra: '#2D303E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Mirage',
    tags: ['curated'],
    light: {
      primary: '#C75876',
      primaryShadow: '#9F465E',
      accent: '#60C799',
      accentShadow: '#4D9F7A',
      background: '#FFFFF2',
      cardBackground: '#F2F2E6',
      text: '#0A0A0B',
      extra: '#292640',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF718B',
      primaryShadow: '#C75876',
      accent: '#78CFA8',
      accentShadow: '#60C799',
      background: '#0A0A0B',
      cardBackground: '#141415',
      text: '#FFFFF2',
      extra: '#3E3C53',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Grove',
    tags: ['warm', 'curated'],
    light: {
      primary: '#99745C',
      primaryShadow: '#7A5D4A',
      accent: '#CC9E7A',
      accentShadow: '#A37E62',
      background: '#E9E6E4',
      cardBackground: '#E0DDDB',
      text: '#4D302E',
      extra: '#4D302E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A88974',
      primaryShadow: '#99745C',
      accent: '#D4AD8E',
      accentShadow: '#CC9E7A',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#F0F0F0',
      extra: '#5F4543',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Navy Mural',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#8A871E',
      primaryShadow: '#6E6C18',
      accent: '#793D1E',
      accentShadow: '#613118',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#1A212A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C9940',
      primaryShadow: '#8A871E',
      accent: '#8D5A40',
      accentShadow: '#793D1E',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#31373F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Radiant Study',
    tags: ['curated'],
    light: {
      primary: '#CC493D',
      primaryShadow: '#A33A31',
      accent: '#52A3CC',
      accentShadow: '#4282A3',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#030305',
      extra: '#331A23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D4645A',
      primaryShadow: '#CC493D',
      accent: '#6CB1D4',
      accentShadow: '#52A3CC',
      background: '#030305',
      cardBackground: '#0D0D0F',
      text: '#FFFFFF',
      extra: '#473139',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Tide',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4FA6AE',
      primaryShadow: '#3F858B',
      accent: '#38647B',
      accentShadow: '#2D5062',
      background: '#F4FFF7',
      cardBackground: '#E8F2EB',
      text: '#1E1A2A',
      extra: '#1E1A2A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#69B3BA',
      primaryShadow: '#4FA6AE',
      accent: '#567B8F',
      accentShadow: '#38647B',
      background: '#1E1A2A',
      cardBackground: '#272333',
      text: '#F4FFF7',
      extra: '#35313F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Dunes',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BF7329',
      primaryShadow: '#995C21',
      accent: '#8B5121',
      accentShadow: '#6F411A',
      background: '#ECE6E0',
      cardBackground: '#E3DDD7',
      text: '#0D1226',
      extra: '#3D2E22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C98849',
      primaryShadow: '#BF7329',
      accent: '#9C6B42',
      accentShadow: '#8B5121',
      background: '#0D1226',
      cardBackground: '#171B2F',
      text: '#C9BB9F',
      extra: '#504338',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Parlor',
    tags: ['curated'],
    light: {
      primary: '#AA73E0',
      primaryShadow: '#885CB3',
      accent: '#6C2E53',
      accentShadow: '#562542',
      background: '#B0ECF9',
      cardBackground: '#A7E0ED',
      text: '#280B0B',
      extra: '#B0ECF9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B788E5',
      primaryShadow: '#AA73E0',
      accent: '#824D6D',
      accentShadow: '#6C2E53',
      background: '#280B0B',
      cardBackground: '#311515',
      text: '#B0ECF9',
      extra: '#B8EEFA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noon Tapestry',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#C67839',
      primaryShadow: '#9E602E',
      accent: '#7D2819',
      accentShadow: '#642014',
      background: '#FDE194',
      cardBackground: '#F0D68D',
      text: '#000000',
      extra: '#2A161C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF8C57',
      primaryShadow: '#C67839',
      accent: '#91483C',
      accentShadow: '#7D2819',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FDE194',
      extra: '#3F2D33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Plateau',
    tags: ['cool', 'curated'],
    light: {
      primary: '#433BFF',
      primaryShadow: '#362FCC',
      accent: '#131457',
      accentShadow: '#0F1046',
      background: '#FFFFF5',
      cardBackground: '#F2F2E9',
      text: '#131457',
      extra: '#8F94C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5F58FF',
      primaryShadow: '#433BFF',
      accent: '#363770',
      accentShadow: '#131457',
      background: '#131457',
      cardBackground: '#1C1D5E',
      text: '#FFFFF5',
      extra: '#9A9FCB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Desert Snowfall',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C24E4E',
      primaryShadow: '#9B3E3E',
      accent: '#E68855',
      accentShadow: '#B86D44',
      background: '#FFF9F0',
      cardBackground: '#F2EDE4',
      text: '#000000',
      extra: '#1E1E24',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CB6969',
      primaryShadow: '#C24E4E',
      accent: '#EA9A6F',
      accentShadow: '#E68855',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFF9F0',
      extra: '#35353A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Clear Branch',
    tags: ['curated'],
    light: {
      primary: '#C93864',
      primaryShadow: '#A12D50',
      accent: '#35CBC8',
      accentShadow: '#2AA2A0',
      background: '#FFDB85',
      cardBackground: '#F2D07E',
      text: '#1B192A',
      extra: '#FFDB85',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D1567B',
      primaryShadow: '#C93864',
      accent: '#53D3D0',
      accentShadow: '#35CBC8',
      background: '#1B192A',
      cardBackground: '#242233',
      text: '#FFDB85',
      extra: '#FFDF91',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Twilight',
    tags: ['curated'],
    light: {
      primary: '#987526',
      primaryShadow: '#7A5E1E',
      accent: '#0A434C',
      accentShadow: '#08363D',
      background: '#E9E6E0',
      cardBackground: '#E0DDD7',
      text: '#11151E',
      extra: '#B6B8AD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A78A47',
      primaryShadow: '#987526',
      accent: '#2F5F67',
      accentShadow: '#0A434C',
      background: '#11151E',
      cardBackground: '#1B1E27',
      text: '#B6B8AD',
      extra: '#BDBFB5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classic Mountain',
    tags: ['dark', 'curated'],
    light: {
      primary: '#B96F2C',
      primaryShadow: '#945923',
      accent: '#774024',
      accentShadow: '#5F331D',
      background: '#ECE6E0',
      cardBackground: '#E3DDD7',
      text: '#182029',
      extra: '#CAA982',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C4854C',
      primaryShadow: '#B96F2C',
      accent: '#8B5D45',
      accentShadow: '#774024',
      background: '#182029',
      cardBackground: '#212932',
      text: '#F0F0F0',
      extra: '#CFB28F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Desert',
    tags: ['curated'],
    light: {
      primary: '#0099DB',
      primaryShadow: '#007AAF',
      accent: '#07E5A0',
      accentShadow: '#06B780',
      background: '#FFEEE5',
      cardBackground: '#F2E2DA',
      text: '#1C162D',
      extra: '#1C162D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A8E0',
      primaryShadow: '#0099DB',
      accent: '#2CE9AE',
      accentShadow: '#07E5A0',
      background: '#1C162D',
      cardBackground: '#251F35',
      text: '#FFEEE5',
      extra: '#332D42',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Stormy Patio',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B3A724',
      primaryShadow: '#8F861D',
      accent: '#6D852C',
      accentShadow: '#576A23',
      background: '#EDE8E1',
      cardBackground: '#E1DCD6',
      text: '#2E0F29',
      extra: '#0A401A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BEB445',
      primaryShadow: '#B3A724',
      accent: '#83974C',
      accentShadow: '#6D852C',
      background: '#2E0F29',
      cardBackground: '#361932',
      text: '#EDE8E1',
      extra: '#235331',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Desert',
    tags: ['curated'],
    light: {
      primary: '#B67435',
      primaryShadow: '#925D2A',
      accent: '#278C40',
      accentShadow: '#1F7033',
      background: '#E7F2F7',
      cardBackground: '#DBE6EB',
      text: '#0D0E26',
      extra: '#182748',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C18953',
      primaryShadow: '#B67435',
      accent: '#479D5D',
      accentShadow: '#278C40',
      background: '#0D0E26',
      cardBackground: '#17182F',
      text: '#E7F2F7',
      extra: '#2F3D5A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teal Altar',
    tags: ['warm', 'curated'],
    light: {
      primary: '#8A6800',
      primaryShadow: '#6E5300',
      accent: '#C48931',
      accentShadow: '#9D6E27',
      background: '#E8E5DD',
      cardBackground: '#DFDCD4',
      text: '#263B28',
      extra: '#263B28',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C7F26',
      primaryShadow: '#8A6800',
      accent: '#CD9B50',
      accentShadow: '#C48931',
      background: '#0C0B0C',
      cardBackground: '#131213',
      text: '#CCB4AF',
      extra: '#3C4F3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Phosphor Wind',
    tags: ['curated'],
    light: {
      primary: '#F76A23',
      primaryShadow: '#C6551C',
      accent: '#A33729',
      accentShadow: '#822C21',
      background: '#FFFDF0',
      cardBackground: '#F2F0E4',
      text: '#3D216E',
      extra: '#3D216E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F88044',
      primaryShadow: '#F76A23',
      accent: '#B15549',
      accentShadow: '#A33729',
      background: '#0F0B0D',
      cardBackground: '#161214',
      text: '#FFFDF0',
      extra: '#50377D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Balmy Plateau',
    tags: ['warm', 'curated'],
    light: {
      primary: '#381701',
      primaryShadow: '#2D1201',
      accent: '#E8A053',
      accentShadow: '#BA8042',
      background: '#EFEBDF',
      cardBackground: '#E3DFD4',
      text: '#381701',
      extra: '#936A4E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#563A27',
      primaryShadow: '#381701',
      accent: '#EBAE6D',
      accentShadow: '#E8A053',
      background: '#381701',
      cardBackground: '#40200B',
      text: '#EFEBDF',
      extra: '#9E7960',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ancient Mind',
    tags: ['curated'],
    light: {
      primary: '#FF6E6E',
      primaryShadow: '#CC5858',
      accent: '#A36363',
      accentShadow: '#824F4F',
      background: '#F1E6E6',
      cardBackground: '#E7DDDD',
      text: '#1A1A1A',
      extra: '#AED0CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8484',
      primaryShadow: '#FF6E6E',
      accent: '#B17A7A',
      accentShadow: '#A36363',
      background: '#100B0F',
      cardBackground: '#171216',
      text: '#AED0CB',
      extra: '#B6D5D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Ridge',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B96F2C',
      primaryShadow: '#945923',
      accent: '#BB9C3B',
      accentShadow: '#967D2F',
      background: '#ECE6E0',
      cardBackground: '#E3DDD7',
      text: '#000000',
      extra: '#E0C979',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C4854C',
      primaryShadow: '#B96F2C',
      accent: '#C5AB58',
      accentShadow: '#BB9C3B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#C2D8DB',
      extra: '#E3CE86',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Petal',
    tags: ['curated'],
    light: {
      primary: '#009C9E',
      primaryShadow: '#007D7E',
      accent: '#32B5F2',
      accentShadow: '#2891C2',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#1E1D44',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26ABAD',
      primaryShadow: '#009C9E',
      accent: '#51C0F4',
      accentShadow: '#32B5F2',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#353457',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Library',
    tags: ['curated'],
    light: {
      primary: '#D45C70',
      primaryShadow: '#AA4A5A',
      accent: '#D28D60',
      accentShadow: '#A8714D',
      background: '#F2ECDF',
      cardBackground: '#E6E0D4',
      text: '#070F2C',
      extra: '#E2E4D9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA7485',
      primaryShadow: '#D45C70',
      accent: '#D99E78',
      accentShadow: '#D28D60',
      background: '#070F2C',
      cardBackground: '#111934',
      text: '#F2ECDF',
      extra: '#E5E7DD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Radiant Pack',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#00A3CA',
      primaryShadow: '#0082A2',
      accent: '#0076AF',
      accentShadow: '#005E8C',
      background: '#DDEAED',
      cardBackground: '#D4E1E4',
      text: '#10022A',
      extra: '#002C7B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B1D2',
      primaryShadow: '#00A3CA',
      accent: '#268BBB',
      accentShadow: '#0076AF',
      background: '#10022A',
      cardBackground: '#1A0C33',
      text: '#69F0C7',
      extra: '#1A4188',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Galaxy',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B76B53',
      primaryShadow: '#925642',
      accent: '#E48362',
      accentShadow: '#B6694E',
      background: '#FFFFD5',
      cardBackground: '#F2F2CA',
      text: '#3B2524',
      extra: '#3B2524',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C2816D',
      primaryShadow: '#B76B53',
      accent: '#E8967A',
      accentShadow: '#E48362',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#FFFFD5',
      extra: '#4F3B3A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Organic Thicket',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#13B37E',
      primaryShadow: '#0F8F65',
      accent: '#07EF5C',
      accentShadow: '#06BF4A',
      background: '#DEEBE7',
      cardBackground: '#D5E2DE',
      text: '#1D2938',
      extra: '#1D2938',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#36BE91',
      primaryShadow: '#13B37E',
      accent: '#2CF174',
      accentShadow: '#07EF5C',
      background: '#090D10',
      cardBackground: '#101417',
      text: '#F0F0F0',
      extra: '#343E4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy Woodland',
    tags: ['dark', 'curated'],
    light: {
      primary: '#986818',
      primaryShadow: '#7A5313',
      accent: '#864808',
      accentShadow: '#6B3A06',
      background: '#E9E5DF',
      cardBackground: '#E0DCD6',
      text: '#181818',
      extra: '#483858',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A77F3B',
      primaryShadow: '#986818',
      accent: '#98632D',
      accentShadow: '#864808',
      background: '#181818',
      cardBackground: '#212121',
      text: '#B8B8B8',
      extra: '#5A4C69',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Smoke Earth',
    tags: ['curated'],
    light: {
      primary: '#2A62AF',
      primaryShadow: '#224E8C',
      accent: '#4AB9B9',
      accentShadow: '#3B9494',
      background: '#DEDFD5',
      cardBackground: '#D3D4CA',
      text: '#21202A',
      extra: '#DEDFD5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4A7ABB',
      primaryShadow: '#2A62AF',
      accent: '#65C4C4',
      accentShadow: '#4AB9B9',
      background: '#21202A',
      cardBackground: '#2A2933',
      text: '#DEDFD5',
      extra: '#E1E2D9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Avant Battlement',
    tags: ['curated'],
    light: {
      primary: '#9F5D2B',
      primaryShadow: '#7F4A22',
      accent: '#183F74',
      accentShadow: '#13325D',
      background: '#EAE4E0',
      cardBackground: '#E1DBD7',
      text: '#08193C',
      extra: '#2B6C9F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD754B',
      primaryShadow: '#9F5D2B',
      accent: '#3B5C89',
      accentShadow: '#183F74',
      background: '#08193C',
      cardBackground: '#122244',
      text: '#DBD3BA',
      extra: '#407BA9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Spire',
    tags: ['curated'],
    light: {
      primary: '#D06060',
      primaryShadow: '#A64D4D',
      accent: '#773971',
      accentShadow: '#5F2E5A',
      background: '#EFEFEF',
      cardBackground: '#E3E3E3',
      text: '#40263E',
      extra: '#40263E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D77878',
      primaryShadow: '#D06060',
      accent: '#8B5786',
      accentShadow: '#773971',
      background: '#0E0B0F',
      cardBackground: '#151216',
      text: '#EFEFEF',
      extra: '#533C51',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Jungle',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#BC7967',
      primaryShadow: '#966152',
      accent: '#E4C179',
      accentShadow: '#B69A61',
      background: '#F9FBF3',
      cardBackground: '#EDEEE7',
      text: '#292039',
      extra: '#292039',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C68D7E',
      primaryShadow: '#BC7967',
      accent: '#E8CA8D',
      accentShadow: '#E4C179',
      background: '#292039',
      cardBackground: '#322941',
      text: '#F9FBF3',
      extra: '#3E364D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Basin',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FA522E',
      primaryShadow: '#C84225',
      accent: '#1E7A8D',
      accentShadow: '#186271',
      background: '#F1E3E0',
      cardBackground: '#E7DAD7',
      text: '#54343A',
      extra: '#F49863',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FB6C4D',
      primaryShadow: '#FA522E',
      accent: '#408E9E',
      accentShadow: '#1E7A8D',
      background: '#100A0D',
      cardBackground: '#171114',
      text: '#F4CC8F',
      extra: '#F5A273',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Alpine Terrace',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00523C',
      primaryShadow: '#004230',
      accent: '#CBB732',
      accentShadow: '#A29228',
      background: '#EBEBEB',
      cardBackground: '#DFDFDF',
      text: '#2A002A',
      extra: '#CBB732',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#266C59',
      primaryShadow: '#00523C',
      accent: '#D3C251',
      accentShadow: '#CBB732',
      background: '#2A002A',
      cardBackground: '#330A33',
      text: '#EBEBEB',
      extra: '#D0BE47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dynamic Soul',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00FF00',
      primaryShadow: '#00CC00',
      accent: '#FF00FF',
      accentShadow: '#CC00CC',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#FF0000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26FF26',
      primaryShadow: '#00FF00',
      accent: '#FF26FF',
      accentShadow: '#FF00FF',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#FF1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Clear Mirage',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D23C4F',
      primaryShadow: '#A8303F',
      accent: '#76AA3A',
      accentShadow: '#5E882E',
      background: '#FBFDBE',
      cardBackground: '#EEF0B5',
      text: '#2B1328',
      extra: '#6DB5BA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D95969',
      primaryShadow: '#D23C4F',
      accent: '#8BB758',
      accentShadow: '#76AA3A',
      background: '#2B1328',
      cardBackground: '#331C31',
      text: '#FBFDBE',
      extra: '#7CBCC1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Daybreak Dusk',
    tags: ['cool', 'curated'],
    light: {
      primary: '#315BBD',
      primaryShadow: '#274997',
      accent: '#5081D1',
      accentShadow: '#4067A7',
      background: '#F5EDDC',
      cardBackground: '#E9E1D1',
      text: '#33166E',
      extra: '#C9B38F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5074C7',
      primaryShadow: '#315BBD',
      accent: '#6A94D8',
      accentShadow: '#5081D1',
      background: '#090B12',
      cardBackground: '#101219',
      text: '#F5EDDC',
      extra: '#CEBB9A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scarlet Permafrost',
    tags: ['curated'],
    light: {
      primary: '#3ECA6B',
      primaryShadow: '#32A256',
      accent: '#566ED0',
      accentShadow: '#4558A6',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#10101D',
      extra: '#FFDD84',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5BD281',
      primaryShadow: '#3ECA6B',
      accent: '#6F84D7',
      accentShadow: '#566ED0',
      background: '#10101D',
      cardBackground: '#1A1A26',
      text: '#FFFFFF',
      extra: '#FFE090',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gleam River',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4792EB',
      primaryShadow: '#3975BC',
      accent: '#7D37D4',
      accentShadow: '#642CAA',
      background: '#E2E8F0',
      cardBackground: '#D9DFE6',
      text: '#2B0B76',
      extra: '#74D7E9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#63A2EE',
      primaryShadow: '#4792EB',
      accent: '#9155DA',
      accentShadow: '#7D37D4',
      background: '#2B0B76',
      cardBackground: '#33157B',
      text: '#74D7E9',
      extra: '#82DBEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Futuristic Essence',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#E56349',
      primaryShadow: '#B74F3A',
      accent: '#9E3455',
      accentShadow: '#7E2A44',
      background: '#EFE5E3',
      cardBackground: '#E5DCDA',
      text: '#221228',
      extra: '#652654',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E97A64',
      primaryShadow: '#E56349',
      accent: '#AD526F',
      accentShadow: '#9E3455',
      background: '#221228',
      cardBackground: '#2B1B31',
      text: '#86DEC3',
      extra: '#743C65',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Bloom',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9C7829',
      primaryShadow: '#7D6021',
      accent: '#30408C',
      accentShadow: '#263370',
      background: '#ECD9D6',
      cardBackground: '#E0CECB',
      text: '#1B1B17',
      extra: '#1B1B17',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AB8C49',
      primaryShadow: '#9C7829',
      accent: '#4F5D9D',
      accentShadow: '#30408C',
      background: '#1B1B17',
      cardBackground: '#242420',
      text: '#ECD9D6',
      extra: '#32322E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ruby Wave',
    tags: ['cool', 'curated'],
    light: {
      primary: '#473759',
      primaryShadow: '#392C47',
      accent: '#5F6F87',
      accentShadow: '#4C596C',
      background: '#E2E1E4',
      cardBackground: '#D9D8DB',
      text: '#000000',
      extra: '#2B2232',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#635572',
      primaryShadow: '#473759',
      accent: '#778599',
      accentShadow: '#5F6F87',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#DACED3',
      extra: '#403847',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fierce Wind',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#45598B',
      primaryShadow: '#37476F',
      accent: '#617BAA',
      accentShadow: '#4E6288',
      background: '#E2E4E8',
      cardBackground: '#D9DBDF',
      text: '#1A183B',
      extra: '#302A58',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#61729C',
      primaryShadow: '#45598B',
      accent: '#798FB7',
      accentShadow: '#617BAA',
      background: '#1A183B',
      cardBackground: '#232143',
      text: '#F0F0F0',
      extra: '#453F69',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scarlet Current',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B46638',
      primaryShadow: '#90522D',
      accent: '#98312A',
      accentShadow: '#7A2722',
      background: '#DCE1F9',
      cardBackground: '#D1D6ED',
      text: '#201008',
      extra: '#DCE1F9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF7D56',
      primaryShadow: '#B46638',
      accent: '#A7504A',
      accentShadow: '#98312A',
      background: '#201008',
      cardBackground: '#291A12',
      text: '#DCE1F9',
      extra: '#E0E4FA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Plateau',
    tags: ['curated'],
    light: {
      primary: '#2E4252',
      primaryShadow: '#253542',
      accent: '#738E85',
      accentShadow: '#5C726A',
      background: '#E5DAB9',
      cardBackground: '#DACFB0',
      text: '#2E4252',
      extra: '#BEBBA1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4D5E6C',
      primaryShadow: '#2E4252',
      accent: '#889F97',
      accentShadow: '#738E85',
      background: '#090A0E',
      cardBackground: '#101115',
      text: '#E5DAB9',
      extra: '#C5C2AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Steel Monument',
    tags: ['curated'],
    light: {
      primary: '#3BA155',
      primaryShadow: '#2F8144',
      accent: '#1E4959',
      accentShadow: '#183A47',
      background: '#EBE0B2',
      cardBackground: '#DFD5A9',
      text: '#1E4959',
      extra: '#A0C76F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#58AF6F',
      primaryShadow: '#3BA155',
      accent: '#406472',
      accentShadow: '#1E4959',
      background: '#0A0D0F',
      cardBackground: '#111416',
      text: '#EBE0B2',
      extra: '#AACD7D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Prism',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#815355',
      primaryShadow: '#674244',
      accent: '#523249',
      accentShadow: '#42283A',
      background: '#DBEBC0',
      cardBackground: '#D0DFB6',
      text: '#523249',
      extra: '#BAC2B1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#946D6F',
      primaryShadow: '#815355',
      accent: '#6C5164',
      accentShadow: '#523249',
      background: '#0C0A0F',
      cardBackground: '#131116',
      text: '#DBEBC0',
      extra: '#C1C8B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Waterfall',
    tags: ['warm', 'curated'],
    light: {
      primary: '#782000',
      primaryShadow: '#601A00',
      accent: '#B07438',
      accentShadow: '#8D5D2D',
      background: '#EDFBFC',
      cardBackground: '#E1EEEF',
      text: '#270A13',
      extra: '#883624',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8C4126',
      primaryShadow: '#782000',
      accent: '#BC8956',
      accentShadow: '#B07438',
      background: '#270A13',
      cardBackground: '#30141C',
      text: '#EDFBFC',
      extra: '#944A3A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Synthetic Cloister',
    tags: ['bright', 'curated'],
    light: {
      primary: '#C671A5',
      primaryShadow: '#9E5A84',
      accent: '#C4A98F',
      accentShadow: '#9D8772',
      background: '#EEE8D5',
      cardBackground: '#E2DCCA',
      text: '#1A1A1A',
      extra: '#605868',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF86B3',
      primaryShadow: '#C671A5',
      accent: '#CDB6A0',
      accentShadow: '#C4A98F',
      background: '#0E0B11',
      cardBackground: '#151218',
      text: '#EEE8D5',
      extra: '#706977',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teal Pasture',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CA475A',
      primaryShadow: '#A23948',
      accent: '#AB933E',
      accentShadow: '#897632',
      background: '#F8E6E6',
      cardBackground: '#ECDBDB',
      text: '#1E2021',
      extra: '#1E2021',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D26373',
      primaryShadow: '#CA475A',
      accent: '#B8A35B',
      accentShadow: '#AB933E',
      background: '#1E2021',
      cardBackground: '#27292A',
      text: '#F8E6E6',
      extra: '#353637',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Springs',
    tags: ['curated'],
    light: {
      primary: '#C9643C',
      primaryShadow: '#A15030',
      accent: '#281B3A',
      accentShadow: '#20162E',
      background: '#FEFF94',
      cardBackground: '#F1F28D',
      text: '#281B3A',
      extra: '#67AA92',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D17B59',
      primaryShadow: '#C9643C',
      accent: '#483D58',
      accentShadow: '#281B3A',
      background: '#281B3A',
      cardBackground: '#312442',
      text: '#FEFF94',
      extra: '#76B39D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ethereal Waterfall',
    tags: ['curated'],
    light: {
      primary: '#077299',
      primaryShadow: '#065B7A',
      accent: '#CF3E78',
      accentShadow: '#A63260',
      background: '#E9E37F',
      cardBackground: '#DDD879',
      text: '#0D0214',
      extra: '#520644',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2C87A8',
      primaryShadow: '#077299',
      accent: '#D65B8C',
      accentShadow: '#CF3E78',
      background: '#0D0214',
      cardBackground: '#170C1D',
      text: '#E9E37F',
      extra: '#631F57',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crimson Snowfall',
    tags: ['curated'],
    light: {
      primary: '#08C49F',
      primaryShadow: '#069D7F',
      accent: '#008D6D',
      accentShadow: '#007157',
      background: '#F1F2F1',
      cardBackground: '#E5E6E5',
      text: '#0D0709',
      extra: '#AD1818',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2DCDAD',
      primaryShadow: '#08C49F',
      accent: '#269E83',
      accentShadow: '#008D6D',
      background: '#0D0709',
      cardBackground: '#171113',
      text: '#F1F2F1',
      extra: '#B52F2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Comet',
    tags: ['bright', 'curated'],
    light: {
      primary: '#FF6932',
      primaryShadow: '#CC5428',
      accent: '#7C30C8',
      accentShadow: '#6326A0',
      background: '#FFFD95',
      cardBackground: '#F2F08E',
      text: '#000000',
      extra: '#A2D9BE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8051',
      primaryShadow: '#FF6932',
      accent: '#904FD0',
      accentShadow: '#7C30C8',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFD95',
      extra: '#ABDDC5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teal Mural',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E97733',
      primaryShadow: '#BA5F29',
      accent: '#CF252C',
      accentShadow: '#A61E23',
      background: '#FFFFE1',
      cardBackground: '#F2F2D6',
      text: '#10071A',
      extra: '#360E39',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC8B52',
      primaryShadow: '#E97733',
      accent: '#D6464C',
      accentShadow: '#CF252C',
      background: '#10071A',
      cardBackground: '#1A1123',
      text: '#FFFFE1',
      extra: '#4A264D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Calm Relief',
    tags: ['dark', 'curated'],
    light: {
      primary: '#BB5042',
      primaryShadow: '#964035',
      accent: '#A48D43',
      accentShadow: '#837136',
      background: '#ECE3E2',
      cardBackground: '#E3DAD9',
      text: '#000000',
      extra: '#803934',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C56A5E',
      primaryShadow: '#BB5042',
      accent: '#B29E5F',
      accentShadow: '#A48D43',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#BCD2D4',
      extra: '#8D4D48',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Spectrum',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9D3648',
      primaryShadow: '#7E2B3A',
      accent: '#B6595E',
      accentShadow: '#92474B',
      background: '#F7F0E8',
      cardBackground: '#EBE4DC',
      text: '#2F223A',
      extra: '#B96F78',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AC5463',
      primaryShadow: '#9D3648',
      accent: '#C17276',
      accentShadow: '#B6595E',
      background: '#0D0A0E',
      cardBackground: '#141115',
      text: '#F7F0E8',
      extra: '#C07D86',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunny Waterfall',
    tags: ['curated'],
    light: {
      primary: '#3E8AB0',
      primaryShadow: '#326E8D',
      accent: '#943E5B',
      accentShadow: '#763249',
      background: '#FFEDF3',
      cardBackground: '#F2E1E7',
      text: '#09151C',
      extra: '#A9CBDB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5B9CBC',
      primaryShadow: '#3E8AB0',
      accent: '#A45B74',
      accentShadow: '#943E5B',
      background: '#09151C',
      cardBackground: '#131E25',
      text: '#FFEDF3',
      extra: '#B2D0DF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sunny Fumarole',
    tags: ['curated'],
    light: {
      primary: '#E5FF00',
      primaryShadow: '#B7CC00',
      accent: '#B8D14A',
      accentShadow: '#93A73B',
      background: '#E5FF00',
      cardBackground: '#DAF200',
      text: '#242424',
      extra: '#9AB06E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E9FF26',
      primaryShadow: '#E5FF00',
      accent: '#C3D865',
      accentShadow: '#B8D14A',
      background: '#242424',
      cardBackground: '#2D2D2D',
      text: '#E5FF00',
      extra: '#A4B87D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Patio',
    tags: ['curated'],
    light: {
      primary: '#398A75',
      primaryShadow: '#2E6E5E',
      accent: '#843C35',
      accentShadow: '#6A302A',
      background: '#FFEB94',
      cardBackground: '#F2DF8D',
      text: '#26153A',
      extra: '#26153A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#579C8A',
      primaryShadow: '#398A75',
      accent: '#965953',
      accentShadow: '#843C35',
      background: '#26153A',
      cardBackground: '#2F1E42',
      text: '#FFEB94',
      extra: '#3C2C4E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Industrial Pasture',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B37B22',
      primaryShadow: '#8F621B',
      accent: '#DAA066',
      accentShadow: '#AE8052',
      background: '#EBE7E0',
      cardBackground: '#E2DED7',
      text: '#293121',
      extra: '#293121',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BE8F43',
      primaryShadow: '#B37B22',
      accent: '#E0AE7D',
      accentShadow: '#DAA066',
      background: '#0D0C0D',
      cardBackground: '#141314',
      text: '#F0F0F0',
      extra: '#3E4637',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ruby Storm',
    tags: ['curated'],
    light: {
      primary: '#252B3A',
      primaryShadow: '#1E222E',
      accent: '#3F5956',
      accentShadow: '#324745',
      background: '#F3EECE',
      cardBackground: '#E7E2C4',
      text: '#252B3A',
      extra: '#C0AF90',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#464B58',
      primaryShadow: '#252B3A',
      accent: '#5C726F',
      accentShadow: '#3F5956',
      background: '#09090E',
      cardBackground: '#101015',
      text: '#F3EECE',
      extra: '#C6B79B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neo Horizon',
    tags: ['curated'],
    light: {
      primary: '#D46C22',
      primaryShadow: '#AA561B',
      accent: '#0D7185',
      accentShadow: '#0A5A6A',
      background: '#E1F2E9',
      cardBackground: '#D6E6DD',
      text: '#000000',
      extra: '#4D30BF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA8243',
      primaryShadow: '#D46C22',
      accent: '#318697',
      accentShadow: '#0D7185',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#E1F2E9',
      extra: '#5F45C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Charcoal Mosaic',
    tags: ['warm', 'curated'],
    light: {
      primary: '#947E2A',
      primaryShadow: '#766522',
      accent: '#8E5026',
      accentShadow: '#72401E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#332630',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A4914A',
      primaryShadow: '#947E2A',
      accent: '#9F6A47',
      accentShadow: '#8E5026',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#473C45',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ruby Turret',
    tags: ['curated'],
    light: {
      primary: '#21A65D',
      primaryShadow: '#1A854A',
      accent: '#2BBCD9',
      accentShadow: '#2296AE',
      background: '#F2ECE6',
      cardBackground: '#E6E0DB',
      text: '#000000',
      extra: '#98D9D3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42B375',
      primaryShadow: '#21A65D',
      accent: '#4BC6DF',
      accentShadow: '#2BBCD9',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F2ECE6',
      extra: '#A2DDD7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Classic Peninsula',
    tags: ['curated'],
    light: {
      primary: '#3698B6',
      primaryShadow: '#2B7A92',
      accent: '#2361A9',
      accentShadow: '#1C4E87',
      background: '#E1E9EB',
      cardBackground: '#D8E0E2',
      text: '#191919',
      extra: '#C4D4C2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#54A7C1',
      primaryShadow: '#3698B6',
      accent: '#4479B6',
      accentShadow: '#2361A9',
      background: '#191919',
      cardBackground: '#222222',
      text: '#C4D4C2',
      extra: '#CAD8C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Aurora',
    tags: ['curated'],
    light: {
      primary: '#9E4959',
      primaryShadow: '#7E3A47',
      accent: '#234F57',
      accentShadow: '#1C3F46',
      background: '#EAF5F0',
      cardBackground: '#DEE9E4',
      text: '#2F1F1A',
      extra: '#2F1F1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD6472',
      primaryShadow: '#9E4959',
      accent: '#446970',
      accentShadow: '#234F57',
      background: '#2F1F1A',
      cardBackground: '#372823',
      text: '#EAF5F0',
      extra: '#443531',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jade Mountain',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A35454',
      primaryShadow: '#824343',
      accent: '#7D3B48',
      accentShadow: '#642F3A',
      background: '#EAE4E4',
      cardBackground: '#E1DBDB',
      text: '#0D0C11',
      extra: '#472A36',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B16E6E',
      primaryShadow: '#A35454',
      accent: '#915863',
      accentShadow: '#7D3B48',
      background: '#0D0C11',
      cardBackground: '#17161B',
      text: '#FBB396',
      extra: '#593F4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Pasture',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B07032',
      primaryShadow: '#8D5A28',
      accent: '#822525',
      accentShadow: '#681E1E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#201616',
      extra: '#37403B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BC8551',
      primaryShadow: '#B07032',
      accent: '#954646',
      accentShadow: '#822525',
      background: '#201616',
      cardBackground: '#291F1F',
      text: '#FFFFFF',
      extra: '#4B534F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Tower',
    tags: ['curated'],
    light: {
      primary: '#006B86',
      primaryShadow: '#00566B',
      accent: '#2F95A1',
      accentShadow: '#267781',
      background: '#DDE5E8',
      cardBackground: '#D4DCDF',
      text: '#520B0B',
      extra: '#DCD0B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#268198',
      primaryShadow: '#006B86',
      accent: '#4EA5AF',
      accentShadow: '#2F95A1',
      background: '#520B0B',
      cardBackground: '#591515',
      text: '#DCD0B7',
      extra: '#E0D5BE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Twilight',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FD8900',
      primaryShadow: '#CA6E00',
      accent: '#BA4F2E',
      accentShadow: '#953F25',
      background: '#D7E1E9',
      cardBackground: '#CCD6DD',
      text: '#24130E',
      extra: '#362520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FD9B26',
      primaryShadow: '#FD8900',
      accent: '#C4694D',
      accentShadow: '#BA4F2E',
      background: '#24130E',
      cardBackground: '#2D1C18',
      text: '#D7E1E9',
      extra: '#4A3B36',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Avant Iceberg',
    tags: ['cool', 'curated'],
    light: {
      primary: '#A65370',
      primaryShadow: '#85425A',
      accent: '#C78E77',
      accentShadow: '#9F725F',
      background: '#EAE3E6',
      cardBackground: '#E1DADD',
      text: '#261F33',
      extra: '#261F33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B36D85',
      primaryShadow: '#A65370',
      accent: '#CF9F8B',
      accentShadow: '#C78E77',
      background: '#261F33',
      cardBackground: '#2F283B',
      text: '#CCCCCC',
      extra: '#3C3547',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fog Fumarole',
    tags: ['cool', 'curated'],
    light: {
      primary: '#564295',
      primaryShadow: '#453577',
      accent: '#9594C0',
      accentShadow: '#77769A',
      background: '#D5E6CB',
      cardBackground: '#CADBC1',
      text: '#1B1B1B',
      extra: '#D5E6CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6F5EA5',
      primaryShadow: '#564295',
      accent: '#A5A4C9',
      accentShadow: '#9594C0',
      background: '#1B1B1B',
      cardBackground: '#242424',
      text: '#D5E6CB',
      extra: '#D9E9D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Industrial Twilight',
    tags: ['curated'],
    light: {
      primary: '#0A7E8B',
      primaryShadow: '#08656F',
      accent: '#FF585F',
      accentShadow: '#CC464C',
      background: '#FCE4A8',
      cardBackground: '#EFD9A0',
      text: '#2F2041',
      extra: '#C8024F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2F919C',
      primaryShadow: '#0A7E8B',
      accent: '#FF7177',
      accentShadow: '#FF585F',
      background: '#080C10',
      cardBackground: '#0F1317',
      text: '#FCE4A8',
      extra: '#CE1B61',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle River',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A06B63',
      primaryShadow: '#80564F',
      accent: '#D29A8D',
      accentShadow: '#A87B71',
      background: '#EAE5E5',
      cardBackground: '#E1DCDC',
      text: '#1A1A1A',
      extra: '#B4C9D8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AE817A',
      primaryShadow: '#A06B63',
      accent: '#D9A99E',
      accentShadow: '#D29A8D',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#B4C9D8',
      extra: '#BCCEDC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Organic Sanctuary',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DE4C63',
      primaryShadow: '#B23D4F',
      accent: '#7C183C',
      accentShadow: '#631330',
      background: '#FFE18F',
      cardBackground: '#F2D688',
      text: '#31112D',
      extra: '#FFE18F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E3677A',
      primaryShadow: '#DE4C63',
      accent: '#903B59',
      accentShadow: '#7C183C',
      background: '#31112D',
      cardBackground: '#391B35',
      text: '#FFE18F',
      extra: '#FFE49A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Veranda',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C74061',
      primaryShadow: '#9F334E',
      accent: '#843451',
      accentShadow: '#6A2A41',
      background: '#EDE2E5',
      cardBackground: '#E4D9DC',
      text: '#363A54',
      extra: '#363A54',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF5D79',
      primaryShadow: '#C74061',
      accent: '#96526B',
      accentShadow: '#843451',
      background: '#0E0A0F',
      cardBackground: '#151116',
      text: '#D9C8BE',
      extra: '#4A4E65',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lunar Heath',
    tags: ['cool', 'curated'],
    light: {
      primary: '#D75555',
      primaryShadow: '#AC4444',
      accent: '#060010',
      accentShadow: '#05000D',
      background: '#EEE4E4',
      cardBackground: '#E4DBDB',
      text: '#060010',
      extra: '#D2B7FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DD6F6F',
      primaryShadow: '#D75555',
      accent: '#2B2634',
      accentShadow: '#060010',
      background: '#060010',
      cardBackground: '#100A1A',
      text: '#D2B7FF',
      extra: '#D7BEFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artisan Permafrost',
    tags: ['warm', 'curated'],
    light: {
      primary: '#49111C',
      primaryShadow: '#3A0E16',
      accent: '#895861',
      accentShadow: '#6E464E',
      background: '#F2F4F3',
      cardBackground: '#E6E8E7',
      text: '#23201A',
      extra: '#A9927D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#64353E',
      primaryShadow: '#49111C',
      accent: '#9B7179',
      accentShadow: '#895861',
      background: '#23201A',
      cardBackground: '#2C2923',
      text: '#F2F4F3',
      extra: '#B29D8A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Timeless Sculpture',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#E04217',
      primaryShadow: '#B33512',
      accent: '#F26D1F',
      accentShadow: '#C25719',
      background: '#EFE2DF',
      cardBackground: '#E5D9D6',
      text: '#3A0000',
      extra: '#590000',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E55E3A',
      primaryShadow: '#E04217',
      accent: '#F48341',
      accentShadow: '#F26D1F',
      background: '#3A0000',
      cardBackground: '#420A0A',
      text: '#FFCE59',
      extra: '#6A1A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Crater',
    tags: ['dark', 'curated'],
    light: {
      primary: '#176166',
      primaryShadow: '#124E52',
      accent: '#122C5E',
      accentShadow: '#0E234B',
      background: '#D4DFE2',
      cardBackground: '#C9D4D7',
      text: '#291916',
      extra: '#24134D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3A797D',
      primaryShadow: '#176166',
      accent: '#364C76',
      accentShadow: '#122C5E',
      background: '#291916',
      cardBackground: '#32221F',
      text: '#D4DFE2',
      extra: '#3A2B5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Spire',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#1E0721',
      primaryShadow: '#18061A',
      accent: '#30324D',
      accentShadow: '#26283E',
      background: '#DFDDDF',
      cardBackground: '#D6D4D6',
      text: '#1E0721',
      extra: '#9BA9AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#402C42',
      primaryShadow: '#1E0721',
      accent: '#4F5168',
      accentShadow: '#30324D',
      background: '#1E0721',
      cardBackground: '#27112A',
      text: '#F0F0F0',
      extra: '#A5B2B3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Mirage',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#953F2E',
      primaryShadow: '#773225',
      accent: '#781A2D',
      accentShadow: '#601524',
      background: '#E9E2E0',
      cardBackground: '#E0D9D7',
      text: '#05202A',
      extra: '#342A5E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A55C4D',
      primaryShadow: '#953F2E',
      accent: '#8C3C4D',
      accentShadow: '#781A2D',
      background: '#05202A',
      cardBackground: '#0F2933',
      text: '#BAC5BF',
      extra: '#483F6E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Calm Sanctuary',
    tags: ['curated'],
    light: {
      primary: '#0099DB',
      primaryShadow: '#007AAF',
      accent: '#07E5A0',
      accentShadow: '#06B780',
      background: '#FFEEE5',
      cardBackground: '#F2E2DA',
      text: '#1C162D',
      extra: '#1C162D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A8E0',
      primaryShadow: '#0099DB',
      accent: '#2CE9AE',
      accentShadow: '#07E5A0',
      background: '#1C162D',
      cardBackground: '#251F35',
      text: '#FFEEE5',
      extra: '#332D42',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Iceberg',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#25375C',
      primaryShadow: '#1E2C4A',
      accent: '#0B1424',
      accentShadow: '#09101D',
      background: '#E0E1E4',
      cardBackground: '#D7D8DB',
      text: '#0B1424',
      extra: '#5C7391',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#465574',
      primaryShadow: '#25375C',
      accent: '#303745',
      accentShadow: '#0B1424',
      background: '#0B1424',
      cardBackground: '#151D2D',
      text: '#AEC3D6',
      extra: '#6C819C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Iron Mountain',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B14B54',
      primaryShadow: '#8E3C43',
      accent: '#F08477',
      accentShadow: '#C06A5F',
      background: '#F9DDAB',
      cardBackground: '#EDD2A2',
      text: '#492045',
      extra: '#7F95B1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BD666E',
      primaryShadow: '#B14B54',
      accent: '#F2968B',
      accentShadow: '#F08477',
      background: '#0D0A0F',
      cardBackground: '#141116',
      text: '#F9DDAB',
      extra: '#8CA0B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Matte Sky',
    tags: ['curated'],
    light: {
      primary: '#0A8A71',
      primaryShadow: '#086E5A',
      accent: '#114C77',
      accentShadow: '#0E3D5F',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#FFD588',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2F9C86',
      primaryShadow: '#0A8A71',
      accent: '#35678B',
      accentShadow: '#114C77',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#FFD994',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Essence',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#006E42',
      primaryShadow: '#005835',
      accent: '#2E868C',
      accentShadow: '#256B70',
      background: '#DDE6E2',
      cardBackground: '#D4DDD9',
      text: '#060F0D',
      extra: '#9FB9CC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26845E',
      primaryShadow: '#006E42',
      accent: '#4D989D',
      accentShadow: '#2E868C',
      background: '#060F0D',
      cardBackground: '#101917',
      text: '#9FB9CC',
      extra: '#A9C0D1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dynamic Fresco',
    tags: ['bright', 'curated'],
    light: {
      primary: '#FF8E65',
      primaryShadow: '#CC7251',
      accent: '#F8C868',
      accentShadow: '#C6A053',
      background: '#F6F6F6',
      cardBackground: '#EAEAEA',
      text: '#3B3C40',
      extra: '#3B3C40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF9F7C',
      primaryShadow: '#FF8E65',
      accent: '#F9D07F',
      accentShadow: '#F8C868',
      background: '#100C0F',
      cardBackground: '#171316',
      text: '#F6F6F6',
      extra: '#4F5053',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Fresco',
    tags: ['warm', 'curated'],
    light: {
      primary: '#1E4530',
      primaryShadow: '#183726',
      accent: '#DB8888',
      accentShadow: '#AF6D6D',
      background: '#FAE8EC',
      cardBackground: '#EEDCE0',
      text: '#15170B',
      extra: '#DB8888',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#40614F',
      primaryShadow: '#1E4530',
      accent: '#E09A9A',
      accentShadow: '#DB8888',
      background: '#15170B',
      cardBackground: '#1E2015',
      text: '#FAE8EC',
      extra: '#DF9494',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cool Promenade',
    tags: ['curated'],
    light: {
      primary: '#2782D7',
      primaryShadow: '#1F68AC',
      accent: '#2ACEA3',
      accentShadow: '#22A582',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#452440',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4795DD',
      primaryShadow: '#2782D7',
      accent: '#4AD5B1',
      accentShadow: '#2ACEA3',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#583A53',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fertile Tide',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#FF0044',
      primaryShadow: '#CC0036',
      accent: '#800044',
      accentShadow: '#660036',
      background: '#F1DDE2',
      cardBackground: '#E7D4D9',
      text: '#000019',
      extra: '#FFDD4E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF2660',
      primaryShadow: '#FF0044',
      accent: '#932660',
      accentShadow: '#800044',
      background: '#000019',
      cardBackground: '#0A0A22',
      text: '#FFDD4E',
      extra: '#FFE060',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Leaf',
    tags: ['curated'],
    light: {
      primary: '#8A1FAC',
      primaryShadow: '#6E198A',
      accent: '#521296',
      accentShadow: '#420E78',
      background: '#E8DFEB',
      cardBackground: '#DFD6E2',
      text: '#521296',
      extra: '#D4864A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9C41B8',
      primaryShadow: '#8A1FAC',
      accent: '#6C36A6',
      accentShadow: '#521296',
      background: '#0C0911',
      cardBackground: '#131018',
      text: '#EBDB5E',
      extra: '#D8925C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Sculpture',
    tags: ['curated'],
    light: {
      primary: '#0B0926',
      primaryShadow: '#09071E',
      accent: '#CC9A6E',
      accentShadow: '#A37B58',
      background: '#FAF0B9',
      cardBackground: '#EEE4B0',
      text: '#0B0926',
      extra: '#594F7D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#302E47',
      primaryShadow: '#0B0926',
      accent: '#D4A984',
      accentShadow: '#CC9A6E',
      background: '#0B0926',
      cardBackground: '#15132F',
      text: '#FAF0B9',
      extra: '#6A618A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Industrial Dawn',
    tags: ['curated'],
    light: {
      primary: '#940128',
      primaryShadow: '#760120',
      accent: '#B26A3B',
      accentShadow: '#8E552F',
      background: '#D0FFD9',
      cardBackground: '#C6F2CE',
      text: '#31012D',
      extra: '#014302',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A42748',
      primaryShadow: '#940128',
      accent: '#BE8058',
      accentShadow: '#B26A3B',
      background: '#31012D',
      cardBackground: '#390B35',
      text: '#D0FFD9',
      extra: '#1A561B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Bay',
    tags: ['cool', 'curated'],
    light: {
      primary: '#E500FF',
      primaryShadow: '#B700CC',
      accent: '#00DDFF',
      accentShadow: '#00B1CC',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#FFF200',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E926FF',
      primaryShadow: '#E500FF',
      accent: '#26E2FF',
      accentShadow: '#00DDFF',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#FFF31A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cosmic Library',
    tags: ['curated'],
    light: {
      primary: '#11ABBC',
      primaryShadow: '#0E8996',
      accent: '#0B6A59',
      accentShadow: '#095547',
      background: '#E2E0D9',
      cardBackground: '#D7D5CE',
      text: '#1D1F26',
      extra: '#234EAF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#35B8C6',
      primaryShadow: '#11ABBC',
      accent: '#308072',
      accentShadow: '#0B6A59',
      background: '#1D1F26',
      cardBackground: '#26282F',
      text: '#E2E0D9',
      extra: '#3960B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Luminous Library',
    tags: ['warm', 'curated'],
    light: {
      primary: '#1773B8',
      primaryShadow: '#125C93',
      accent: '#DDAC46',
      accentShadow: '#B18A38',
      background: '#FEFED7',
      cardBackground: '#F1F1CC',
      text: '#161323',
      extra: '#DBBC96',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3A88C3',
      primaryShadow: '#1773B8',
      accent: '#E2B862',
      accentShadow: '#DDAC46',
      background: '#161323',
      cardBackground: '#1F1C2C',
      text: '#FEFED7',
      extra: '#DFC3A1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Synthetic Thicket',
    tags: ['curated'],
    light: {
      primary: '#FB6B1D',
      primaryShadow: '#C95617',
      accent: '#E527D2',
      accentShadow: '#B71FA8',
      background: '#FFFB76',
      cardBackground: '#F2EE70',
      text: '#090A14',
      extra: '#0A3639',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FC813F',
      primaryShadow: '#FB6B1D',
      accent: '#E947D9',
      accentShadow: '#E527D2',
      background: '#090A14',
      cardBackground: '#13141D',
      text: '#FFFB76',
      extra: '#234A4D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Organic Current',
    tags: ['curated'],
    light: {
      primary: '#DD7711',
      primaryShadow: '#B15F0E',
      accent: '#993311',
      accentShadow: '#7A290E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#070708',
      extra: '#332222',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E28B35',
      primaryShadow: '#DD7711',
      accent: '#A85235',
      accentShadow: '#993311',
      background: '#070708',
      cardBackground: '#111112',
      text: '#FFFFFF',
      extra: '#473838',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Courtyard',
    tags: ['curated'],
    light: {
      primary: '#E05012',
      primaryShadow: '#B3400E',
      accent: '#FA9914',
      accentShadow: '#C87A10',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#351520',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E56A36',
      primaryShadow: '#E05012',
      accent: '#FBA837',
      accentShadow: '#FA9914',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#492C36',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Dream',
    tags: ['cool', 'curated'],
    light: {
      primary: '#F8873D',
      primaryShadow: '#C66C31',
      accent: '#AE39AC',
      accentShadow: '#8B2E8A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#381232',
      extra: '#B3495B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F9995A',
      primaryShadow: '#F8873D',
      accent: '#BA57B8',
      accentShadow: '#AE39AC',
      background: '#381232',
      cardBackground: '#401B3A',
      text: '#FFFFFF',
      extra: '#BB5B6B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Iron Essence',
    tags: ['curated'],
    light: {
      primary: '#00A1DB',
      primaryShadow: '#0081AF',
      accent: '#1D5BB8',
      accentShadow: '#174993',
      background: '#F2FBFF',
      cardBackground: '#E6EEF2',
      text: '#0F0F12',
      extra: '#505359',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26AFE0',
      primaryShadow: '#00A1DB',
      accent: '#3F74C3',
      accentShadow: '#1D5BB8',
      background: '#0F0F12',
      cardBackground: '#19191B',
      text: '#F2FBFF',
      extra: '#62646A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Chapel',
    tags: ['curated'],
    light: {
      primary: '#5889A2',
      primaryShadow: '#466E82',
      accent: '#A9F05F',
      accentShadow: '#87C04C',
      background: '#FCEBB6',
      cardBackground: '#EFDFAD',
      text: '#462C4B',
      extra: '#9EE7D7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#719BB0',
      primaryShadow: '#5889A2',
      accent: '#B6F277',
      accentShadow: '#A9F05F',
      background: '#0B0C11',
      cardBackground: '#121318',
      text: '#FCEBB6',
      extra: '#A8E9DB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Forest',
    tags: ['warm', 'curated'],
    light: {
      primary: '#F27E0D',
      primaryShadow: '#C2650A',
      accent: '#12A0B0',
      accentShadow: '#0E808D',
      background: '#FAFFCE',
      cardBackground: '#EEF2C4',
      text: '#0D041A',
      extra: '#FFC888',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F49131',
      primaryShadow: '#F27E0D',
      accent: '#36AEBC',
      accentShadow: '#12A0B0',
      background: '#0D041A',
      cardBackground: '#170E23',
      text: '#FAFFCE',
      extra: '#FFCE94',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Storm',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#12021A',
      primaryShadow: '#0E0215',
      accent: '#381E5E',
      accentShadow: '#2D184B',
      background: '#DEDDDF',
      cardBackground: '#D5D4D6',
      text: '#05020D',
      extra: '#260F3F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#36283C',
      primaryShadow: '#12021A',
      accent: '#564076',
      accentShadow: '#381E5E',
      background: '#05020D',
      cardBackground: '#0F0C17',
      text: '#F0F0F0',
      extra: '#3C2752',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Summer Dream',
    tags: ['cool', 'curated'],
    light: {
      primary: '#7B7AA4',
      primaryShadow: '#626283',
      accent: '#30303D',
      accentShadow: '#262631',
      background: '#DADADA',
      cardBackground: '#CFCFCF',
      text: '#141319',
      extra: '#DADADA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8F8EB2',
      primaryShadow: '#7B7AA4',
      accent: '#4F4F5A',
      accentShadow: '#30303D',
      background: '#141319',
      cardBackground: '#1D1C22',
      text: '#DADADA',
      extra: '#DEDEDE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Timeless Comet',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#617851',
      primaryShadow: '#4E6041',
      accent: '#B3B282',
      accentShadow: '#8F8E68',
      background: '#E5E6E3',
      cardBackground: '#DCDDDA',
      text: '#1A1714',
      extra: '#38403A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#798C6B',
      primaryShadow: '#617851',
      accent: '#BEBE95',
      accentShadow: '#B3B282',
      background: '#1A1714',
      cardBackground: '#23201D',
      text: '#F0F0F0',
      extra: '#4C534E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Windy Veranda',
    tags: ['cool', 'curated'],
    light: {
      primary: '#1687A7',
      primaryShadow: '#126C86',
      accent: '#1FD5BC',
      accentShadow: '#19AA96',
      background: '#EDFFB1',
      cardBackground: '#E1F2A8',
      text: '#311F5F',
      extra: '#311F5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3999B4',
      primaryShadow: '#1687A7',
      accent: '#41DBC6',
      accentShadow: '#1FD5BC',
      background: '#090C11',
      cardBackground: '#101318',
      text: '#EDFFB1',
      extra: '#46356F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Minimalist Woodland',
    tags: ['curated'],
    light: {
      primary: '#15877F',
      primaryShadow: '#116C66',
      accent: '#41EA9E',
      accentShadow: '#34BB7E',
      background: '#FFE8F6',
      cardBackground: '#F2DCEA',
      text: '#091E26',
      extra: '#2C2259',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#389992',
      primaryShadow: '#15877F',
      accent: '#5EEDAD',
      accentShadow: '#41EA9E',
      background: '#091E26',
      cardBackground: '#13272F',
      text: '#FFE8F6',
      extra: '#41386A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shadowy Grove',
    tags: ['dark', 'curated'],
    light: {
      primary: '#1FAAB9',
      primaryShadow: '#198894',
      accent: '#1A759D',
      accentShadow: '#155E7E',
      background: '#FAFAFA',
      cardBackground: '#EEEEEE',
      text: '#000000',
      extra: '#0B1F3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#41B7C4',
      primaryShadow: '#1FAAB9',
      accent: '#3C8AAC',
      accentShadow: '#1A759D',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FAFAFA',
      extra: '#233550',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Oasis',
    tags: ['curated'],
    light: {
      primary: '#F4402A',
      primaryShadow: '#C33322',
      accent: '#E10E3C',
      accentShadow: '#B40B30',
      background: '#FAFFFF',
      cardBackground: '#EEF2F2',
      text: '#0B0C31',
      extra: '#541191',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F65D4A',
      primaryShadow: '#F4402A',
      accent: '#E63259',
      accentShadow: '#E10E3C',
      background: '#0B0C31',
      cardBackground: '#151639',
      text: '#FAFFFF',
      extra: '#65299C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neon Gradient',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E81623',
      primaryShadow: '#BA121C',
      accent: '#EC687C',
      accentShadow: '#BD5363',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#59283E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EB3944',
      primaryShadow: '#E81623',
      accent: '#EF7F90',
      accentShadow: '#EC687C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#6A3E51',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Contemporary Patio',
    tags: ['curated'],
    light: {
      primary: '#E63900',
      primaryShadow: '#B82E00',
      accent: '#5A0084',
      accentShadow: '#48006A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#5A0084',
      extra: '#FFC96B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EA5726',
      primaryShadow: '#E63900',
      accent: '#732696',
      accentShadow: '#5A0084',
      background: '#0F0A0C',
      cardBackground: '#161113',
      text: '#FFFFFF',
      extra: '#FFCE7A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Mirage',
    tags: ['curated'],
    light: {
      primary: '#3AA64F',
      primaryShadow: '#2E853F',
      accent: '#A2304D',
      accentShadow: '#82263E',
      background: '#FFF1B2',
      cardBackground: '#F2E5A9',
      text: '#3E1D3D',
      extra: '#7AADB4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#58B369',
      primaryShadow: '#3AA64F',
      accent: '#B04F68',
      accentShadow: '#A2304D',
      background: '#0A0D0E',
      cardBackground: '#111415',
      text: '#FFF1B2',
      extra: '#87B5BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Heart',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D3813D',
      primaryShadow: '#A96731',
      accent: '#B44D3A',
      accentShadow: '#903E2E',
      background: '#F0F0EE',
      cardBackground: '#E4E4E2',
      text: '#3C2329',
      extra: '#3C2329',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA945A',
      primaryShadow: '#D3813D',
      accent: '#BF6858',
      accentShadow: '#B44D3A',
      background: '#0E0C0E',
      cardBackground: '#151315',
      text: '#F0F0EE',
      extra: '#50393E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Mirage',
    tags: ['curated'],
    light: {
      primary: '#DA6D00',
      primaryShadow: '#AE5700',
      accent: '#834200',
      accentShadow: '#693500',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#323213',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E08326',
      primaryShadow: '#DA6D00',
      accent: '#965E26',
      accentShadow: '#834200',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#47472B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Calm Stream',
    tags: ['curated'],
    light: {
      primary: '#E7C488',
      primaryShadow: '#B99D6D',
      accent: '#20172B',
      accentShadow: '#1A1222',
      background: '#F0F2CA',
      cardBackground: '#E4E6C0',
      text: '#20172B',
      extra: '#807283',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EBCD9A',
      primaryShadow: '#E7C488',
      accent: '#413A4B',
      accentShadow: '#20172B',
      background: '#20172B',
      cardBackground: '#292033',
      text: '#F0F2CA',
      extra: '#8D808F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bold Heart',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DC6B56',
      primaryShadow: '#B05645',
      accent: '#E1A854',
      accentShadow: '#B48643',
      background: '#FBE9C1',
      cardBackground: '#EEDDB7',
      text: '#39363A',
      extra: '#39363A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E1816F',
      primaryShadow: '#DC6B56',
      accent: '#E6B56E',
      accentShadow: '#E1A854',
      background: '#0F0B0F',
      cardBackground: '#161216',
      text: '#FBE9C1',
      extra: '#4D4A4E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artisan Peninsula',
    tags: ['cool', 'curated'],
    light: {
      primary: '#1CB9B5',
      primaryShadow: '#169491',
      accent: '#67CBC4',
      accentShadow: '#52A29D',
      background: '#F3F3EE',
      cardBackground: '#E7E7E2',
      text: '#403F46',
      extra: '#403F46',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3EC4C0',
      primaryShadow: '#1CB9B5',
      accent: '#7ED3CD',
      accentShadow: '#67CBC4',
      background: '#090E11',
      cardBackground: '#101518',
      text: '#F3F3EE',
      extra: '#535259',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Comet',
    tags: ['curated'],
    light: {
      primary: '#330126',
      primaryShadow: '#29011E',
      accent: '#7B6149',
      accentShadow: '#624E3A',
      background: '#D9E6FA',
      cardBackground: '#CEDBEE',
      text: '#330126',
      extra: '#8BB5B3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#522747',
      primaryShadow: '#330126',
      accent: '#8F7964',
      accentShadow: '#7B6149',
      background: '#330126',
      cardBackground: '#3B0B2F',
      text: '#D9E6FA',
      extra: '#97BCBB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fleeting Turret',
    tags: ['cool', 'curated'],
    light: {
      primary: '#308E86',
      primaryShadow: '#26726B',
      accent: '#48AF7D',
      accentShadow: '#3A8C64',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#353535',
      extra: '#B8FF82',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4F9F98',
      primaryShadow: '#308E86',
      accent: '#63BB91',
      accentShadow: '#48AF7D',
      background: '#090C10',
      cardBackground: '#101317',
      text: '#FFFFFF',
      extra: '#BFFF8F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystic Sculpture',
    tags: ['curated'],
    light: {
      primary: '#DC5B52',
      primaryShadow: '#B04942',
      accent: '#AC484F',
      accentShadow: '#8A3A3F',
      background: '#ECEFF6',
      cardBackground: '#E0E3EA',
      text: '#232A36',
      extra: '#232A36',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E1746C',
      primaryShadow: '#DC5B52',
      accent: '#B86369',
      accentShadow: '#AC484F',
      background: '#0F0B0E',
      cardBackground: '#161215',
      text: '#ECEFF6',
      extra: '#393F4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hazy Mosaic',
    tags: ['curated'],
    light: {
      primary: '#EB670A',
      primaryShadow: '#BC5208',
      accent: '#CE4707',
      accentShadow: '#A53906',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#640053',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EE7E2F',
      primaryShadow: '#EB670A',
      accent: '#D5632C',
      accentShadow: '#CE4707',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#741A64',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden River',
    tags: ['curated'],
    light: {
      primary: '#EF4C1A',
      primaryShadow: '#BF3D15',
      accent: '#FF006F',
      accentShadow: '#CC0059',
      background: '#FCFBFF',
      cardBackground: '#EFEEF2',
      text: '#1E0740',
      extra: '#B7B3EF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F1673C',
      primaryShadow: '#EF4C1A',
      accent: '#FF2685',
      accentShadow: '#FF006F',
      background: '#1E0740',
      cardBackground: '#271148',
      text: '#FCFBFF',
      extra: '#BEBBF1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Copse',
    tags: ['bright', 'curated'],
    light: {
      primary: '#EA7286',
      primaryShadow: '#BB5B6B',
      accent: '#EAB281',
      accentShadow: '#BB8E67',
      background: '#EEEDE3',
      cardBackground: '#E2E1D8',
      text: '#1A1A1A',
      extra: '#E3E19F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#ED8798',
      primaryShadow: '#EA7286',
      accent: '#EDBE94',
      accentShadow: '#EAB281',
      background: '#0F0B10',
      cardBackground: '#161217',
      text: '#EEEDE3',
      extra: '#E6E4A9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sage Courtyard',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#557F8E',
      primaryShadow: '#446672',
      accent: '#2F3D55',
      accentShadow: '#263144',
      background: '#E4E7E8',
      cardBackground: '#DBDEDF',
      text: '#252A45',
      extra: '#79A5AF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6F929F',
      primaryShadow: '#557F8E',
      accent: '#4E5A6F',
      accentShadow: '#2F3D55',
      background: '#0B0C10',
      cardBackground: '#121317',
      text: '#F0F0F0',
      extra: '#86AEB7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Wild Plateau',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#BA0000',
      primaryShadow: '#950000',
      accent: '#290707',
      accentShadow: '#210606',
      background: '#ECDDDD',
      cardBackground: '#E3D4D4',
      text: '#290707',
      extra: '#766161',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C42626',
      primaryShadow: '#BA0000',
      accent: '#492C2C',
      accentShadow: '#290707',
      background: '#290707',
      cardBackground: '#321111',
      text: '#F0F0F0',
      extra: '#847171',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Desert Cloister',
    tags: ['cool', 'curated'],
    light: {
      primary: '#608189',
      primaryShadow: '#4D676E',
      accent: '#49556C',
      accentShadow: '#3A4456',
      background: '#E4E7E8',
      cardBackground: '#DBDEDF',
      text: '#292736',
      extra: '#BEB09F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#78949B',
      primaryShadow: '#608189',
      accent: '#646F82',
      accentShadow: '#49556C',
      background: '#0B0C10',
      cardBackground: '#121317',
      text: '#F0F0F0',
      extra: '#C5B8A9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spark Aura',
    tags: ['cool', 'curated'],
    light: {
      primary: '#4C5671',
      primaryShadow: '#3D455A',
      accent: '#91A3AA',
      accentShadow: '#748288',
      background: '#F5F5F5',
      cardBackground: '#E9E9E9',
      text: '#050505',
      extra: '#4C5671',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#676F86',
      primaryShadow: '#4C5671',
      accent: '#A2B1B7',
      accentShadow: '#91A3AA',
      background: '#050505',
      cardBackground: '#0F0F0F',
      text: '#F5F5F5',
      extra: '#5E677F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble Gradient',
    tags: ['curated'],
    light: {
      primary: '#3F7A63',
      primaryShadow: '#32624F',
      accent: '#F4A374',
      accentShadow: '#C3825D',
      background: '#FFFBD1',
      cardBackground: '#F2EEC7',
      text: '#1B1829',
      extra: '#1B1829',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5C8E7A',
      primaryShadow: '#3F7A63',
      accent: '#F6B189',
      accentShadow: '#F4A374',
      background: '#1B1829',
      cardBackground: '#242132',
      text: '#FFFBD1',
      extra: '#322F3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bold Garden',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#D43E1F',
      primaryShadow: '#AA3219',
      accent: '#A40038',
      accentShadow: '#83002D',
      background: '#EEE2DF',
      cardBackground: '#E4D9D6',
      text: '#01000E',
      extra: '#DF892E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DA5B41',
      primaryShadow: '#D43E1F',
      accent: '#B22656',
      accentShadow: '#A40038',
      background: '#01000E',
      cardBackground: '#0B0A18',
      text: '#F5C1C5',
      extra: '#E29543',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Warm Cliff',
    tags: ['bright', 'curated'],
    light: {
      primary: '#D1AD82',
      primaryShadow: '#A78A68',
      accent: '#6A9490',
      accentShadow: '#557673',
      background: '#EEEBE7',
      cardBackground: '#E4E2DE',
      text: '#1A1A1A',
      extra: '#DBD1B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D8B995',
      primaryShadow: '#D1AD82',
      accent: '#80A4A1',
      accentShadow: '#6A9490',
      background: '#0E0D10',
      cardBackground: '#151417',
      text: '#DBD1B4',
      extra: '#DFD6BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Granite Petal',
    tags: ['curated'],
    light: {
      primary: '#00DF00',
      primaryShadow: '#00B200',
      accent: '#FF00FF',
      accentShadow: '#CC00CC',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#E4E4E4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26E426',
      primaryShadow: '#00DF00',
      accent: '#FF26FF',
      accentShadow: '#FF00FF',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#E7E7E7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Jungle',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9E6333',
      primaryShadow: '#7E4F29',
      accent: '#BD9353',
      accentShadow: '#977642',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1C071A',
      extra: '#1F1833',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD7A52',
      primaryShadow: '#9E6333',
      accent: '#C7A36D',
      accentShadow: '#BD9353',
      background: '#1C071A',
      cardBackground: '#251123',
      text: '#FFFFFF',
      extra: '#352F47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indigo Eclipse',
    tags: ['curated'],
    light: {
      primary: '#BD622A',
      primaryShadow: '#974E22',
      accent: '#127A3E',
      accentShadow: '#0E6232',
      background: '#FFFECE',
      cardBackground: '#F2F1C4',
      text: '#300633',
      extra: '#252C45',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C77A4A',
      primaryShadow: '#BD622A',
      accent: '#368E5B',
      accentShadow: '#127A3E',
      background: '#300633',
      cardBackground: '#38103B',
      text: '#FFFECE',
      extra: '#3B4158',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ivory Coast',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B96234',
      primaryShadow: '#944E2A',
      accent: '#8B2F3B',
      accentShadow: '#6F262F',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#11151A',
      extra: '#B2C7AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C47A52',
      primaryShadow: '#B96234',
      accent: '#9C4E58',
      accentShadow: '#8B2F3B',
      background: '#11151A',
      cardBackground: '#1B1E23',
      text: '#FFFFFF',
      extra: '#BACDB3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Rampart',
    tags: ['curated'],
    light: {
      primary: '#E64E35',
      primaryShadow: '#B83E2A',
      accent: '#9C3A2B',
      accentShadow: '#7D2E22',
      background: '#D9D9D9',
      cardBackground: '#CECECE',
      text: '#0D0D0D',
      extra: '#383838',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EA6953',
      primaryShadow: '#E64E35',
      accent: '#AB584B',
      accentShadow: '#9C3A2B',
      background: '#0D0D0D',
      cardBackground: '#171717',
      text: '#D9D9D9',
      extra: '#4C4C4C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Canvas',
    tags: ['curated'],
    light: {
      primary: '#A1156E',
      primaryShadow: '#811158',
      accent: '#D43763',
      accentShadow: '#AA2C4F',
      background: '#FFECB3',
      cardBackground: '#F2E0AA',
      text: '#000000',
      extra: '#261626',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AF3884',
      primaryShadow: '#A1156E',
      accent: '#DA557A',
      accentShadow: '#D43763',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFECB3',
      extra: '#3C2D3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Courtyard',
    tags: ['warm', 'curated'],
    light: {
      primary: '#00B16B',
      primaryShadow: '#008E56',
      accent: '#FFB700',
      accentShadow: '#CC9200',
      background: '#E6E1C8',
      cardBackground: '#DBD6BE',
      text: '#1D151B',
      extra: '#E6E1C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26BD81',
      primaryShadow: '#00B16B',
      accent: '#FFC226',
      accentShadow: '#FFB700',
      background: '#1D151B',
      cardBackground: '#261E24',
      text: '#E6E1C8',
      extra: '#E9E4CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neon Courtyard',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#1CB099',
      primaryShadow: '#168D7A',
      accent: '#0B586E',
      accentShadow: '#094658',
      background: '#DFEBE9',
      cardBackground: '#D6E2E0',
      text: '#051E45',
      extra: '#051E45',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3EBCA8',
      primaryShadow: '#1CB099',
      accent: '#307184',
      accentShadow: '#0B586E',
      background: '#051E45',
      cardBackground: '#0F274C',
      text: '#42F4AF',
      extra: '#1E3558',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Alpine Palette',
    tags: ['curated'],
    light: {
      primary: '#522546',
      primaryShadow: '#421E38',
      accent: '#8C72A4',
      accentShadow: '#705B83',
      background: '#F5F5F5',
      cardBackground: '#E9E9E9',
      text: '#3F1D2C',
      extra: '#E7D5E2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6C4662',
      primaryShadow: '#522546',
      accent: '#9D87B2',
      accentShadow: '#8C72A4',
      background: '#0A090E',
      cardBackground: '#111015',
      text: '#F5F5F5',
      extra: '#E9D9E5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noon Savanna',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00E572',
      primaryShadow: '#00B75B',
      accent: '#666600',
      accentShadow: '#525200',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#B1B1B1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26E987',
      primaryShadow: '#00E572',
      accent: '#7D7D26',
      accentShadow: '#666600',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#B9B9B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Industrial Nebula',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B14852',
      primaryShadow: '#8E3A42',
      accent: '#BE8162',
      accentShadow: '#98674E',
      background: '#DEDCD3',
      cardBackground: '#D3D1C8',
      text: '#1E1818',
      extra: '#1E1818',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BD636C',
      primaryShadow: '#B14852',
      accent: '#C8947A',
      accentShadow: '#BE8162',
      background: '#1E1818',
      cardBackground: '#272121',
      text: '#DEDCD3',
      extra: '#352F2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Timeless Clearing',
    tags: ['curated'],
    light: {
      primary: '#FF8F00',
      primaryShadow: '#CC7200',
      accent: '#A05A0B',
      accentShadow: '#804809',
      background: '#FFFF97',
      cardBackground: '#F2F28F',
      text: '#101010',
      extra: '#A53618',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFA026',
      primaryShadow: '#FF8F00',
      accent: '#AE7330',
      accentShadow: '#A05A0B',
      background: '#101010',
      cardBackground: '#1A1A1A',
      text: '#FFFF97',
      extra: '#AE4A2F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Jungle',
    tags: ['curated'],
    light: {
      primary: '#18A46A',
      primaryShadow: '#138355',
      accent: '#395200',
      accentShadow: '#2E4200',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#83838B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3BB280',
      primaryShadow: '#18A46A',
      accent: '#576C26',
      accentShadow: '#395200',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#8F8F97',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Gradient',
    tags: ['curated'],
    light: {
      primary: '#338CCA',
      primaryShadow: '#2970A2',
      accent: '#1F7F51',
      accentShadow: '#196641',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#222B5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#529DD2',
      primaryShadow: '#338CCA',
      accent: '#41926B',
      accentShadow: '#1F7F51',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#38406F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Flame Sculpture',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00C159',
      primaryShadow: '#009A47',
      accent: '#006FC1',
      accentShadow: '#00599A',
      background: '#EFFFFB',
      cardBackground: '#E3F2EE',
      text: '#04001B',
      extra: '#97FFF7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26CA72',
      primaryShadow: '#00C159',
      accent: '#2685CA',
      accentShadow: '#006FC1',
      background: '#04001B',
      cardBackground: '#0E0A24',
      text: '#EFFFFB',
      extra: '#A1FFF8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artisan Ridge',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A80A2E',
      primaryShadow: '#860825',
      accent: '#CD283B',
      accentShadow: '#A4202F',
      background: '#FFFFFA',
      cardBackground: '#F2F2EE',
      text: '#01010D',
      extra: '#FAB9A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B52F4D',
      primaryShadow: '#A80A2E',
      accent: '#D54858',
      accentShadow: '#CD283B',
      background: '#01010D',
      cardBackground: '#0B0B17',
      text: '#FFFFFA',
      extra: '#FBC0AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Root',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A7512D',
      primaryShadow: '#864124',
      accent: '#D9903D',
      accentShadow: '#AE7331',
      background: '#FFFDDA',
      cardBackground: '#F2F0CF',
      text: '#170F0D',
      extra: '#F1D29E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B46B4D',
      primaryShadow: '#A7512D',
      accent: '#DFA15A',
      accentShadow: '#D9903D',
      background: '#170F0D',
      cardBackground: '#201917',
      text: '#FFFDDA',
      extra: '#F2D7A8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Shrine',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AB811D',
      primaryShadow: '#896717',
      accent: '#FF8B18',
      accentShadow: '#CC6F13',
      background: '#FFF0A0',
      cardBackground: '#F2E498',
      text: '#000000',
      extra: '#291A13',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B8943F',
      primaryShadow: '#AB811D',
      accent: '#FF9C3B',
      accentShadow: '#FF8B18',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFF0A0',
      extra: '#3E312B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Permafrost',
    tags: ['curated'],
    light: {
      primary: '#267B85',
      primaryShadow: '#1E626A',
      accent: '#40B374',
      accentShadow: '#338F5D',
      background: '#FFF6E0',
      cardBackground: '#F2EAD5',
      text: '#362233',
      extra: '#362233',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#478F97',
      primaryShadow: '#267B85',
      accent: '#5DBE89',
      accentShadow: '#40B374',
      background: '#090C10',
      cardBackground: '#101317',
      text: '#FFF6E0',
      extra: '#4A3847',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Emerald Cloister',
    tags: ['curated'],
    light: {
      primary: '#AC6B2F',
      primaryShadow: '#8A5626',
      accent: '#953A2F',
      accentShadow: '#772E26',
      background: '#ECF3B0',
      cardBackground: '#E0E7A7',
      text: '#0C0F00',
      extra: '#B8DDD2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B8814E',
      primaryShadow: '#AC6B2F',
      accent: '#A5584E',
      accentShadow: '#953A2F',
      background: '#0C0F00',
      cardBackground: '#16190A',
      text: '#ECF3B0',
      extra: '#BFE0D7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Mountain',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DA3C69',
      primaryShadow: '#AE3054',
      accent: '#164C56',
      accentShadow: '#123D45',
      background: '#E6E1AD',
      cardBackground: '#DBD6A4',
      text: '#3A0B33',
      extra: '#3A0B33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E05980',
      primaryShadow: '#DA3C69',
      accent: '#39676F',
      accentShadow: '#164C56',
      background: '#3A0B33',
      cardBackground: '#42153B',
      text: '#E6E1AD',
      extra: '#4E2347',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Desert Terrace',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#27B391',
      primaryShadow: '#1F8F74',
      accent: '#288C70',
      accentShadow: '#20705A',
      background: '#E0EBE8',
      cardBackground: '#D7E2DF',
      text: '#000000',
      extra: '#2B7255',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#47BEA2',
      primaryShadow: '#27B391',
      accent: '#489D85',
      accentShadow: '#288C70',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F0F0F0',
      extra: '#408066',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rich Storm',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#641010',
      primaryShadow: '#500D0D',
      accent: '#28000E',
      accentShadow: '#20000B',
      background: '#E5DEDE',
      cardBackground: '#DCD5D5',
      text: '#28000E',
      extra: '#BBA075',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7B3434',
      primaryShadow: '#641010',
      accent: '#482632',
      accentShadow: '#28000E',
      background: '#28000E',
      cardBackground: '#310A18',
      text: '#F0F0F0',
      extra: '#C2AA83',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Scarlet Copse',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A2651B',
      primaryShadow: '#825116',
      accent: '#8F2A4D',
      accentShadow: '#72223E',
      background: '#EAE5DF',
      cardBackground: '#E1DCD6',
      text: '#1F1F22',
      extra: '#1F1F22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B07C3D',
      primaryShadow: '#A2651B',
      accent: '#A04A68',
      accentShadow: '#8F2A4D',
      background: '#1F1F22',
      cardBackground: '#28282B',
      text: '#CAC9AB',
      extra: '#353538',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Stormy Field',
    tags: ['curated'],
    light: {
      primary: '#2C9CD5',
      primaryShadow: '#237DAA',
      accent: '#118C60',
      accentShadow: '#0E704D',
      background: '#FAFFFF',
      cardBackground: '#EEF2F2',
      text: '#130E26',
      extra: '#C1D7DB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4CABDB',
      primaryShadow: '#2C9CD5',
      accent: '#359D78',
      accentShadow: '#118C60',
      background: '#130E26',
      cardBackground: '#1C182F',
      text: '#FAFFFF',
      extra: '#C7DBDF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Industrial Reef',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#FFA322',
      primaryShadow: '#CC821B',
      accent: '#EC4646',
      accentShadow: '#BD3838',
      background: '#FFF5F5',
      cardBackground: '#F2E9E9',
      text: '#1D173C',
      extra: '#65471E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FFB143',
      primaryShadow: '#FFA322',
      accent: '#EF6262',
      accentShadow: '#EC4646',
      background: '#1D173C',
      cardBackground: '#262044',
      text: '#FFF5F5',
      extra: '#745935',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Volcano',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#D1491B',
      primaryShadow: '#A73A16',
      accent: '#9C2700',
      accentShadow: '#7D1F00',
      background: '#EEE3DF',
      cardBackground: '#E4DAD6',
      text: '#290C07',
      extra: '#5E0808',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D8643D',
      primaryShadow: '#D1491B',
      accent: '#AB4726',
      accentShadow: '#9C2700',
      background: '#290C07',
      cardBackground: '#321611',
      text: '#F0F0F0',
      extra: '#6E2121',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Mosaic',
    tags: ['curated'],
    light: {
      primary: '#811830',
      primaryShadow: '#671326',
      accent: '#4C041C',
      accentShadow: '#3D0316',
      background: '#F6F2EA',
      cardBackground: '#EAE6DE',
      text: '#140008',
      extra: '#EAD2BA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#943B4F',
      primaryShadow: '#811830',
      accent: '#672A3E',
      accentShadow: '#4C041C',
      background: '#140008',
      cardBackground: '#1D0A12',
      text: '#F6F2EA',
      extra: '#ECD7C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Jade Reef',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D72836',
      primaryShadow: '#AC202B',
      accent: '#F69B3A',
      accentShadow: '#C57C2E',
      background: '#F6F68F',
      cardBackground: '#EAEA88',
      text: '#3E1A23',
      extra: '#3E1A23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DD4854',
      primaryShadow: '#D72836',
      accent: '#F7AA58',
      accentShadow: '#F69B3A',
      background: '#3E1A23',
      cardBackground: '#46232C',
      text: '#F6F68F',
      extra: '#513139',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Horizon',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#BF531D',
      primaryShadow: '#994217',
      accent: '#661F1A',
      accentShadow: '#521915',
      background: '#ECE3DF',
      cardBackground: '#E3DAD6',
      text: '#1A0F16',
      extra: '#F2C549',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C96D3F',
      primaryShadow: '#BF531D',
      accent: '#7D413C',
      accentShadow: '#661F1A',
      background: '#1A0F16',
      cardBackground: '#23191F',
      text: '#F2C549',
      extra: '#F3CB5B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Evening Savanna',
    tags: ['curated'],
    light: {
      primary: '#08A0C0',
      primaryShadow: '#06809A',
      accent: '#25E2C0',
      accentShadow: '#1EB59A',
      background: '#FBFFCE',
      cardBackground: '#EEF2C4',
      text: '#260E3E',
      extra: '#B4DC25',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2DAEC9',
      primaryShadow: '#08A0C0',
      accent: '#46E6C9',
      accentShadow: '#25E2C0',
      background: '#260E3E',
      cardBackground: '#2F1846',
      text: '#FBFFCE',
      extra: '#BCE03B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Azure Plaza',
    tags: ['warm', 'curated'],
    light: {
      primary: '#69BF2B',
      primaryShadow: '#549922',
      accent: '#A03030',
      accentShadow: '#802626',
      background: '#FBF7B6',
      cardBackground: '#EEEBAD',
      text: '#151212',
      extra: '#76A1F1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#80C94B',
      primaryShadow: '#69BF2B',
      accent: '#AE4F4F',
      accentShadow: '#A03030',
      background: '#151212',
      cardBackground: '#1E1B1B',
      text: '#FBF7B6',
      extra: '#84AAF2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Nova',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#460479',
      primaryShadow: '#380361',
      accent: '#8C2D8F',
      accentShadow: '#702472',
      background: '#E2DDE6',
      cardBackground: '#D9D4DD',
      text: '#190121',
      extra: '#6D88FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#622A8D',
      primaryShadow: '#460479',
      accent: '#9D4DA0',
      accentShadow: '#8C2D8F',
      background: '#190121',
      cardBackground: '#220B2A',
      text: '#F0F0F0',
      extra: '#7C94FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Star',
    tags: ['cool', 'curated'],
    light: {
      primary: '#8E3CE1',
      primaryShadow: '#7230B4',
      accent: '#3AC9F5',
      accentShadow: '#2EA1C4',
      background: '#FFF7F0',
      cardBackground: '#F2EBE4',
      text: '#11041E',
      extra: '#FF8FCA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9F59E6',
      primaryShadow: '#8E3CE1',
      accent: '#58D1F7',
      accentShadow: '#3AC9F5',
      background: '#11041E',
      cardBackground: '#1B0E27',
      text: '#FFF7F0',
      extra: '#FF9ACF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen Obelisk',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FC6231',
      primaryShadow: '#CA4E27',
      accent: '#5A521A',
      accentShadow: '#484215',
      background: '#FCDE84',
      cardBackground: '#EFD37D',
      text: '#151C15',
      extra: '#FCDE84',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FC7A50',
      primaryShadow: '#FC6231',
      accent: '#736C3C',
      accentShadow: '#5A521A',
      background: '#151C15',
      cardBackground: '#1E251E',
      text: '#FCDE84',
      extra: '#FCE190',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Windy Rampart',
    tags: ['curated'],
    light: {
      primary: '#C93038',
      primaryShadow: '#A1262D',
      accent: '#7FA663',
      accentShadow: '#66854F',
      background: '#F0F6F0',
      cardBackground: '#E4EAE4',
      text: '#222323',
      extra: '#222323',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D14F56',
      primaryShadow: '#C93038',
      accent: '#92B37A',
      accentShadow: '#7FA663',
      background: '#222323',
      cardBackground: '#2B2C2C',
      text: '#F0F6F0',
      extra: '#383939',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Aurora',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#06D6A0',
      primaryShadow: '#05AB80',
      accent: '#26547C',
      accentShadow: '#1E4363',
      background: '#FFFCF9',
      cardBackground: '#F2EFED',
      text: '#26547C',
      extra: '#EF476F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2BDCAE',
      primaryShadow: '#06D6A0',
      accent: '#476E90',
      accentShadow: '#26547C',
      background: '#080E11',
      cardBackground: '#0F1518',
      text: '#FFFCF9',
      extra: '#F1597D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Study',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CF854B',
      primaryShadow: '#A66A3C',
      accent: '#A33F35',
      accentShadow: '#82322A',
      background: '#EDE7E3',
      cardBackground: '#E4DEDA',
      text: '#362D23',
      extra: '#EED5A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D69766',
      primaryShadow: '#CF854B',
      accent: '#B15C53',
      accentShadow: '#A33F35',
      background: '#0E0C0E',
      cardBackground: '#151315',
      text: '#EED5A5',
      extra: '#F0D9AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ivory Seed',
    tags: ['curated'],
    light: {
      primary: '#DB926D',
      primaryShadow: '#AF7557',
      accent: '#242449',
      accentShadow: '#1D1D3A',
      background: '#FFDBB6',
      cardBackground: '#F2D0AD',
      text: '#242449',
      extra: '#FFDBB6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0A283',
      primaryShadow: '#DB926D',
      accent: '#454564',
      accentShadow: '#242449',
      background: '#0F0C0F',
      cardBackground: '#161316',
      text: '#FFDBB6',
      extra: '#FFDFBD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noon Ocean',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#055682',
      primaryShadow: '#044568',
      accent: '#CE862C',
      accentShadow: '#A56B23',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#071E38',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2B6F95',
      primaryShadow: '#055682',
      accent: '#D5984C',
      accentShadow: '#CE862C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#20354C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Twilight Thicket',
    tags: ['curated'],
    light: {
      primary: '#0F9E34',
      primaryShadow: '#0C7E2A',
      accent: '#0B7240',
      accentShadow: '#095B33',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#010001',
      extra: '#002B23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#33AD52',
      primaryShadow: '#0F9E34',
      accent: '#30875D',
      accentShadow: '#0B7240',
      background: '#010001',
      cardBackground: '#0B0A0B',
      text: '#FFFFFF',
      extra: '#1A4039',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Calm Promenade',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#AD8041',
      primaryShadow: '#8A6634',
      accent: '#DBC064',
      accentShadow: '#AF9A50',
      background: '#FAFEE7',
      cardBackground: '#EEF1DB',
      text: '#2C2321',
      extra: '#674936',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B9935E',
      primaryShadow: '#AD8041',
      accent: '#E0C97B',
      accentShadow: '#DBC064',
      background: '#2C2321',
      cardBackground: '#342C2A',
      text: '#FAFEE7',
      extra: '#765B4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Western Atrium',
    tags: ['warm', 'curated'],
    light: {
      primary: '#2FB6C3',
      primaryShadow: '#26929C',
      accent: '#229443',
      accentShadow: '#1B7636',
      background: '#FFFB76',
      cardBackground: '#F2EE70',
      text: '#0F0E11',
      extra: '#2D2C33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4EC1CC',
      primaryShadow: '#2FB6C3',
      accent: '#43A45F',
      accentShadow: '#229443',
      background: '#0F0E11',
      cardBackground: '#19181B',
      text: '#FFFB76',
      extra: '#424147',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Caldera',
    tags: ['warm', 'curated'],
    light: {
      primary: '#88985B',
      primaryShadow: '#6D7A49',
      accent: '#B3B37E',
      accentShadow: '#8F8F65',
      background: '#F0F0E4',
      cardBackground: '#E4E4D9',
      text: '#333030',
      extra: '#333030',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9AA774',
      primaryShadow: '#88985B',
      accent: '#BEBE91',
      accentShadow: '#B3B37E',
      background: '#0C0D0F',
      cardBackground: '#131416',
      text: '#F0F0E4',
      extra: '#474545',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Canyon',
    tags: ['dark', 'curated'],
    light: {
      primary: '#914E3C',
      primaryShadow: '#743E30',
      accent: '#C7955C',
      accentShadow: '#9F774A',
      background: '#E8E3E2',
      cardBackground: '#DFDAD9',
      text: '#100F13',
      extra: '#3A213A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A26959',
      primaryShadow: '#914E3C',
      accent: '#CFA574',
      accentShadow: '#C7955C',
      background: '#100F13',
      cardBackground: '#1A191C',
      text: '#F0F0F0',
      extra: '#4E374E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mystic Study',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#2F1C62',
      primaryShadow: '#26164E',
      accent: '#563482',
      accentShadow: '#452A68',
      background: '#E1DFE5',
      cardBackground: '#D8D6DC',
      text: '#1B0D35',
      extra: '#6F5D9D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4E3E7A',
      primaryShadow: '#2F1C62',
      accent: '#6F5295',
      accentShadow: '#563482',
      background: '#1B0D35',
      cardBackground: '#24173D',
      text: '#F0F0F0',
      extra: '#7D6DA7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sage Arcade',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CA7125',
      primaryShadow: '#A25A1E',
      accent: '#8D2C30',
      accentShadow: '#712326',
      background: '#FEFEF8',
      cardBackground: '#F1F1EC',
      text: '#05051B',
      extra: '#411D34',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D28646',
      primaryShadow: '#CA7125',
      accent: '#9E4C4F',
      accentShadow: '#8D2C30',
      background: '#05051B',
      cardBackground: '#0F0F24',
      text: '#FEFEF8',
      extra: '#543448',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shadowy Dream',
    tags: ['warm', 'curated'],
    light: {
      primary: '#BEA87D',
      primaryShadow: '#988664',
      accent: '#291A1F',
      accentShadow: '#211519',
      background: '#F0E0CF',
      cardBackground: '#E4D5C5',
      text: '#291A1F',
      extra: '#675650',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C8B591',
      primaryShadow: '#BEA87D',
      accent: '#493C41',
      accentShadow: '#291A1F',
      background: '#291A1F',
      cardBackground: '#322328',
      text: '#F0E0CF',
      extra: '#766762',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Matte Vineyard',
    tags: ['cool', 'curated'],
    light: {
      primary: '#45568D',
      primaryShadow: '#374571',
      accent: '#12121B',
      accentShadow: '#0E0E16',
      background: '#E1D8D4',
      cardBackground: '#D6CDC9',
      text: '#12121B',
      extra: '#E1D8D4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#616F9E',
      primaryShadow: '#45568D',
      accent: '#36363D',
      accentShadow: '#12121B',
      background: '#12121B',
      cardBackground: '#1B1B24',
      text: '#E1D8D4',
      extra: '#E4DCD8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pure Studio',
    tags: ['cool', 'curated'],
    light: {
      primary: '#FF007E',
      primaryShadow: '#CC0065',
      accent: '#410E43',
      accentShadow: '#340B36',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#410E43',
      extra: '#DE6BEB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF2691',
      primaryShadow: '#FF007E',
      accent: '#5E325F',
      accentShadow: '#410E43',
      background: '#410E43',
      cardBackground: '#49184B',
      text: '#FFFFFF',
      extra: '#E17AED',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spark Floe',
    tags: ['curated'],
    light: {
      primary: '#0B8633',
      primaryShadow: '#096B29',
      accent: '#97CB1D',
      accentShadow: '#79A217',
      background: '#F8F3FD',
      cardBackground: '#ECE7F0',
      text: '#2E2B12',
      extra: '#FAC6B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#309852',
      primaryShadow: '#0B8633',
      accent: '#A7D33F',
      accentShadow: '#97CB1D',
      background: '#080C0E',
      cardBackground: '#0F1315',
      text: '#F8F3FD',
      extra: '#FBCCBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shine Nebula',
    tags: ['cool', 'curated'],
    light: {
      primary: '#26013F',
      primaryShadow: '#1E0132',
      accent: '#42436E',
      accentShadow: '#353658',
      background: '#E6E6C0',
      cardBackground: '#DBDBB6',
      text: '#26013F',
      extra: '#B494B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#47275C',
      primaryShadow: '#26013F',
      accent: '#5E5F84',
      accentShadow: '#42436E',
      background: '#26013F',
      cardBackground: '#2F0B47',
      text: '#E6E6C0',
      extra: '#BC9FBE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Chapel',
    tags: ['curated'],
    light: {
      primary: '#7B8046',
      primaryShadow: '#626638',
      accent: '#C5AF63',
      accentShadow: '#9E8C4F',
      background: '#F8F4E4',
      cardBackground: '#ECE8D9',
      text: '#0A0A10',
      extra: '#181B22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8F9362',
      primaryShadow: '#7B8046',
      accent: '#CEBB7A',
      accentShadow: '#C5AF63',
      background: '#0A0A10',
      cardBackground: '#14141A',
      text: '#F8F4E4',
      extra: '#2F3238',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Alpine Sanctuary',
    tags: ['dark', 'curated'],
    light: {
      primary: '#CF8A0A',
      primaryShadow: '#A66E08',
      accent: '#0E97C4',
      accentShadow: '#0B799D',
      background: '#FFFDE3',
      cardBackground: '#F2F0D8',
      text: '#0D0103',
      extra: '#70032D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D69C2F',
      primaryShadow: '#CF8A0A',
      accent: '#32A7CD',
      accentShadow: '#0E97C4',
      background: '#0D0103',
      cardBackground: '#170B0D',
      text: '#FFFDE3',
      extra: '#7E1C42',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble Monument',
    tags: ['curated'],
    light: {
      primary: '#0BA0A7',
      primaryShadow: '#098086',
      accent: '#13C5B5',
      accentShadow: '#0F9E91',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#151B33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#30AEB4',
      primaryShadow: '#0BA0A7',
      accent: '#36CEC0',
      accentShadow: '#13C5B5',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#2C3247',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ice Thicket',
    tags: ['warm', 'curated'],
    light: {
      primary: '#4E3F2A',
      primaryShadow: '#3E3222',
      accent: '#887B6A',
      accentShadow: '#6D6255',
      background: '#E3E2E0',
      cardBackground: '#DAD9D7',
      text: '#4E3F2A',
      extra: '#AEA691',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#695C4A',
      primaryShadow: '#4E3F2A',
      accent: '#9A8F80',
      accentShadow: '#887B6A',
      background: '#0A0A0D',
      cardBackground: '#111114',
      text: '#F0F0F0',
      extra: '#B6AF9C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spark Glade',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CC5B45',
      primaryShadow: '#A34937',
      accent: '#AA3330',
      accentShadow: '#882926',
      background: '#FFEBDE',
      cardBackground: '#F2DFD3',
      text: '#0F060E',
      extra: '#381A23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D47461',
      primaryShadow: '#CC5B45',
      accent: '#B7524F',
      accentShadow: '#AA3330',
      background: '#0F060E',
      cardBackground: '#191018',
      text: '#FFEBDE',
      extra: '#4C3139',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Dawn',
    tags: ['curated'],
    light: {
      primary: '#D78D42',
      primaryShadow: '#AC7135',
      accent: '#AF3F52',
      accentShadow: '#8C3242',
      background: '#EFF0E9',
      cardBackground: '#E3E4DD',
      text: '#0C0B0F',
      extra: '#5F3959',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DD9E5E',
      primaryShadow: '#D78D42',
      accent: '#BB5C6C',
      accentShadow: '#AF3F52',
      background: '#0C0B0F',
      cardBackground: '#161519',
      text: '#EFF0E9',
      extra: '#6F4D6A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy River',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#377A5D',
      primaryShadow: '#2C624A',
      accent: '#2B5744',
      accentShadow: '#224636',
      background: '#E1E7E4',
      cardBackground: '#D8DEDB',
      text: '#0D0B08',
      extra: '#1B2621',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#558E75',
      primaryShadow: '#377A5D',
      accent: '#4B7060',
      accentShadow: '#2B5744',
      background: '#0D0B08',
      cardBackground: '#171512',
      text: '#95D9BC',
      extra: '#323C37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Mosaic',
    tags: ['curated'],
    light: {
      primary: '#AD601A',
      primaryShadow: '#8A4D15',
      accent: '#5A340E',
      accentShadow: '#482A0B',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#95CECD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B9783C',
      primaryShadow: '#AD601A',
      accent: '#735232',
      accentShadow: '#5A340E',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#A0D3D2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Violet Peninsula',
    tags: ['curated'],
    light: {
      primary: '#739A56',
      primaryShadow: '#5C7B45',
      accent: '#42665A',
      accentShadow: '#355248',
      background: '#E6E9E4',
      cardBackground: '#DDE0DB',
      text: '#353D46',
      extra: '#B2C27D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#88A96F',
      primaryShadow: '#739A56',
      accent: '#5E7D73',
      accentShadow: '#42665A',
      background: '#0B0D0F',
      cardBackground: '#121416',
      text: '#B2C27D',
      extra: '#BAC88A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Mosaic',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#B00005',
      primaryShadow: '#8D0004',
      accent: '#2B0B1D',
      accentShadow: '#220917',
      background: '#EBDDDD',
      cardBackground: '#E2D4D4',
      text: '#000000',
      extra: '#921315',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BC262B',
      primaryShadow: '#B00005',
      accent: '#4B303F',
      accentShadow: '#2B0B1D',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F0F0F0',
      extra: '#9D2B2C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cyan Temple',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#26213E',
      primaryShadow: '#1E1A32',
      accent: '#080B12',
      accentShadow: '#06090E',
      background: '#F8F5FF',
      cardBackground: '#ECE9F2',
      text: '#080B12',
      extra: '#635C84',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#47425B',
      primaryShadow: '#26213E',
      accent: '#2D3036',
      accentShadow: '#080B12',
      background: '#080B12',
      cardBackground: '#12151B',
      text: '#F8F5FF',
      extra: '#736C90',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Misty Nebula',
    tags: ['curated'],
    light: {
      primary: '#DD8855',
      primaryShadow: '#B16D44',
      accent: '#BB5555',
      accentShadow: '#964444',
      background: '#EEDDCC',
      cardBackground: '#E2D2C2',
      text: '#333344',
      extra: '#333344',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E29A6F',
      primaryShadow: '#DD8855',
      accent: '#C56F6F',
      accentShadow: '#BB5555',
      background: '#0F0C0F',
      cardBackground: '#161316',
      text: '#EEDDCC',
      extra: '#474757',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Balmy Courtyard',
    tags: ['cool', 'curated'],
    light: {
      primary: '#36AD69',
      primaryShadow: '#2B8A54',
      accent: '#E2D6B5',
      accentShadow: '#B5AB91',
      background: '#E1EBE5',
      cardBackground: '#D8E2DC',
      text: '#343434',
      extra: '#343434',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#54B980',
      primaryShadow: '#36AD69',
      accent: '#E6DCC0',
      accentShadow: '#E2D6B5',
      background: '#0A0D0F',
      cardBackground: '#111416',
      text: '#E2D6B5',
      extra: '#484848',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Chapel',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D3AB0C',
      primaryShadow: '#A9890A',
      accent: '#C67439',
      accentShadow: '#9E5D2E',
      background: '#D8D8E5',
      cardBackground: '#CDCDDA',
      text: '#121C1C',
      extra: '#CD372F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DAB830',
      primaryShadow: '#D3AB0C',
      accent: '#CF8957',
      accentShadow: '#C67439',
      background: '#121C1C',
      cardBackground: '#1B2525',
      text: '#D8D8E5',
      extra: '#D24B44',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Luminous Nave',
    tags: ['cool', 'curated'],
    light: {
      primary: '#1E014B',
      primaryShadow: '#18013C',
      accent: '#58E1C3',
      accentShadow: '#46B49C',
      background: '#FFDAFF',
      cardBackground: '#F2CFF2',
      text: '#1E014B',
      extra: '#78478E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#402766',
      primaryShadow: '#1E014B',
      accent: '#71E6CC',
      accentShadow: '#58E1C3',
      background: '#1E014B',
      cardBackground: '#270B52',
      text: '#FFDAFF',
      extra: '#865999',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spring Tide',
    tags: ['curated'],
    light: {
      primary: '#938009',
      primaryShadow: '#766607',
      accent: '#705E00',
      accentShadow: '#5A4B00',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#222222',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A3932E',
      primaryShadow: '#938009',
      accent: '#857626',
      accentShadow: '#705E00',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#383838',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Bay',
    tags: ['warm', 'curated'],
    light: {
      primary: '#61301F',
      primaryShadow: '#4E2619',
      accent: '#270E09',
      accentShadow: '#1F0B07',
      background: '#EED9D4',
      cardBackground: '#E2CEC9',
      text: '#270E09',
      extra: '#EED9D4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#794F41',
      primaryShadow: '#61301F',
      accent: '#47322E',
      accentShadow: '#270E09',
      background: '#270E09',
      cardBackground: '#301813',
      text: '#EED9D4',
      extra: '#F0DDD8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Translucent Lava',
    tags: ['curated'],
    light: {
      primary: '#00B6B6',
      primaryShadow: '#009292',
      accent: '#009191',
      accentShadow: '#007474',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#004800',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C1C1',
      primaryShadow: '#00B6B6',
      accent: '#26A2A2',
      accentShadow: '#009191',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#1A5A1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tropical Mosaic',
    tags: ['bright', 'curated'],
    light: {
      primary: '#A8734A',
      primaryShadow: '#865C3B',
      accent: '#772D26',
      accentShadow: '#5F241E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#E9B287',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B58865',
      primaryShadow: '#A8734A',
      accent: '#8B4D47',
      accentShadow: '#772D26',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#EBBA93',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Fresco',
    tags: ['curated'],
    light: {
      primary: '#E84545',
      primaryShadow: '#BA3737',
      accent: '#9E2083',
      accentShadow: '#7E1A69',
      background: '#F2E9F2',
      cardBackground: '#E6DDE6',
      text: '#1B112C',
      extra: '#543E54',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EB6161',
      primaryShadow: '#E84545',
      accent: '#AD4196',
      accentShadow: '#9E2083',
      background: '#1B112C',
      cardBackground: '#241B34',
      text: '#F2E9F2',
      extra: '#655165',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Moss',
    tags: ['curated'],
    light: {
      primary: '#AD6908',
      primaryShadow: '#8A5406',
      accent: '#D69221',
      accentShadow: '#AB751A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#181840',
      extra: '#181840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B9802D',
      primaryShadow: '#AD6908',
      accent: '#DCA242',
      accentShadow: '#D69221',
      background: '#181840',
      cardBackground: '#212148',
      text: '#FFFFFF',
      extra: '#2F2F53',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mediterranean Copse',
    tags: ['curated'],
    light: {
      primary: '#DC584A',
      primaryShadow: '#B0463B',
      accent: '#AB3246',
      accentShadow: '#892838',
      background: '#EFF1E2',
      cardBackground: '#E3E5D7',
      text: '#21162E',
      extra: '#8A3D53',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E17165',
      primaryShadow: '#DC584A',
      accent: '#B85162',
      accentShadow: '#AB3246',
      background: '#21162E',
      cardBackground: '#2A1F36',
      text: '#EFF1E2',
      extra: '#965064',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dew Temple',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#FF4537',
      primaryShadow: '#CC372C',
      accent: '#BA332D',
      accentShadow: '#952924',
      background: '#F1E2E1',
      cardBackground: '#E7D9D8',
      text: '#060607',
      extra: '#1D1E21',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6155',
      primaryShadow: '#FF4537',
      accent: '#C4524D',
      accentShadow: '#BA332D',
      background: '#060607',
      cardBackground: '#101011',
      text: '#FFCBB5',
      extra: '#343537',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Tone',
    tags: ['curated'],
    light: {
      primary: '#EB564B',
      primaryShadow: '#BC453C',
      accent: '#F2A65E',
      accentShadow: '#C2854B',
      background: '#FFFFEB',
      cardBackground: '#F2F2DF',
      text: '#222323',
      extra: '#222323',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EE6F66',
      primaryShadow: '#EB564B',
      accent: '#F4B376',
      accentShadow: '#F2A65E',
      background: '#222323',
      cardBackground: '#2B2C2C',
      text: '#FFFFEB',
      extra: '#383939',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vibrant Ravine',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#27886A',
      primaryShadow: '#1F6D55',
      accent: '#143F64',
      accentShadow: '#103250',
      background: '#E0E8E5',
      cardBackground: '#D7DFDC',
      text: '#060628',
      extra: '#0B1B41',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#479A80',
      primaryShadow: '#27886A',
      accent: '#375C7B',
      accentShadow: '#143F64',
      background: '#060628',
      cardBackground: '#101031',
      text: '#F0F0F0',
      extra: '#233254',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Peninsula',
    tags: ['curated'],
    light: {
      primary: '#37A4E8',
      primaryShadow: '#2C83BA',
      accent: '#C7286C',
      accentShadow: '#9F2056',
      background: '#FFFCF8',
      cardBackground: '#F2EFEC',
      text: '#200A2A',
      extra: '#8C8294',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#55B2EB',
      primaryShadow: '#37A4E8',
      accent: '#CF4882',
      accentShadow: '#C7286C',
      background: '#200A2A',
      cardBackground: '#291433',
      text: '#FFFCF8',
      extra: '#988F9F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coral Relief',
    tags: ['cool', 'curated'],
    light: {
      primary: '#7155C6',
      primaryShadow: '#5A449E',
      accent: '#1E0C51',
      accentShadow: '#180A41',
      background: '#E6E4ED',
      cardBackground: '#DDDBE4',
      text: '#1E0C51',
      extra: '#2C298C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#866FCF',
      primaryShadow: '#7155C6',
      accent: '#40306B',
      accentShadow: '#1E0C51',
      background: '#1E0C51',
      cardBackground: '#271658',
      text: '#FCBCE8',
      extra: '#413E98',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neo Moss',
    tags: ['curated'],
    light: {
      primary: '#0AB2FF',
      primaryShadow: '#088ECC',
      accent: '#B4BE00',
      accentShadow: '#909800',
      background: '#FFFFF4',
      cardBackground: '#F2F2E8',
      text: '#000000',
      extra: '#FFC9ED',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2FBEFF',
      primaryShadow: '#0AB2FF',
      accent: '#BFC826',
      accentShadow: '#B4BE00',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFF4',
      extra: '#FFCEEF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Golden Grove',
    tags: ['cool', 'curated'],
    light: {
      primary: '#39A74C',
      primaryShadow: '#2E863D',
      accent: '#63D34A',
      accentShadow: '#4FA93B',
      background: '#E1EAE3',
      cardBackground: '#D8E1DA',
      text: '#0C1218',
      extra: '#ABE97C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#57B467',
      primaryShadow: '#39A74C',
      accent: '#7ADA65',
      accentShadow: '#63D34A',
      background: '#0C1218',
      cardBackground: '#161B21',
      text: '#ABE97C',
      extra: '#B3EB89',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Shrine',
    tags: ['cool', 'curated'],
    light: {
      primary: '#333343',
      primaryShadow: '#292936',
      accent: '#5D6872',
      accentShadow: '#4A535B',
      background: '#E1E1E2',
      cardBackground: '#D8D8D9',
      text: '#333343',
      extra: '#C5CCB8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#52525F',
      primaryShadow: '#333343',
      accent: '#757F87',
      accentShadow: '#5D6872',
      background: '#0A0A0E',
      cardBackground: '#111115',
      text: '#C5CCB8',
      extra: '#CBD1BF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Indigo Wind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#140000',
      primaryShadow: '#100000',
      accent: '#9D724D',
      accentShadow: '#7E5B3E',
      background: '#ECF1F0',
      cardBackground: '#E0E5E4',
      text: '#140000',
      extra: '#492020',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#372626',
      primaryShadow: '#140000',
      accent: '#AC8768',
      accentShadow: '#9D724D',
      background: '#140000',
      cardBackground: '#1D0A0A',
      text: '#ECF1F0',
      extra: '#5B3636',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Veranda',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#BE1D47',
      primaryShadow: '#981739',
      accent: '#7C0A72',
      accentShadow: '#63085B',
      background: '#FFFDCB',
      cardBackground: '#F2F0C1',
      text: '#050027',
      extra: '#FFA33F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C83F63',
      primaryShadow: '#BE1D47',
      accent: '#902F87',
      accentShadow: '#7C0A72',
      background: '#050027',
      cardBackground: '#0F0A30',
      text: '#FFFDCB',
      extra: '#FFAC52',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oceanic Prairie',
    tags: ['cool', 'curated'],
    light: {
      primary: '#162EB4',
      primaryShadow: '#122590',
      accent: '#0A1624',
      accentShadow: '#08121D',
      background: '#EAF3FC',
      cardBackground: '#DEE7EF',
      text: '#0A1624',
      extra: '#F5A8CA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#394DBF',
      primaryShadow: '#162EB4',
      accent: '#2F3945',
      accentShadow: '#0A1624',
      background: '#0A1624',
      cardBackground: '#141F2D',
      text: '#EAF3FC',
      extra: '#F6B1CF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fog Seed',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#A85D8B',
      primaryShadow: '#864A6F',
      accent: '#DB80AC',
      accentShadow: '#AF668A',
      background: '#FCF1DF',
      cardBackground: '#EFE5D4',
      text: '#1A1A1A',
      extra: '#F0BFB2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B5759C',
      primaryShadow: '#A85D8B',
      accent: '#E093B8',
      accentShadow: '#DB80AC',
      background: '#0D0B10',
      cardBackground: '#141217',
      text: '#FCF1DF',
      extra: '#F2C5BA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shine Fire',
    tags: ['curated'],
    light: {
      primary: '#FF3F14',
      primaryShadow: '#CC3210',
      accent: '#007738',
      accentShadow: '#005F2D',
      background: '#FFD8CC',
      cardBackground: '#F2CDC2',
      text: '#0C0C0C',
      extra: '#273DD9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5C37',
      primaryShadow: '#FF3F14',
      accent: '#268B56',
      accentShadow: '#007738',
      background: '#0C0C0C',
      cardBackground: '#161616',
      text: '#FFD8CC',
      extra: '#3D50DD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Cliff',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#C94B98',
      primaryShadow: '#A13C7A',
      accent: '#FF63A2',
      accentShadow: '#CC4F82',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1A1A1A',
      extra: '#FF90B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D166A7',
      primaryShadow: '#C94B98',
      accent: '#FF7AB0',
      accentShadow: '#FF63A2',
      background: '#0E0A11',
      cardBackground: '#151118',
      text: '#FFFFFF',
      extra: '#FF9BBE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Navy Vine',
    tags: ['curated'],
    light: {
      primary: '#8E43A1',
      primaryShadow: '#723681',
      accent: '#CAA860',
      accentShadow: '#A2864D',
      background: '#E9EDFF',
      cardBackground: '#DDE1F2',
      text: '#381A31',
      extra: '#381A31',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9F5FAF',
      primaryShadow: '#8E43A1',
      accent: '#D2B578',
      accentShadow: '#CAA860',
      background: '#381A31',
      cardBackground: '#402339',
      text: '#E9EDFF',
      extra: '#4C3146',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cyan Plateau',
    tags: ['curated'],
    light: {
      primary: '#9E5E58',
      primaryShadow: '#7E4B46',
      accent: '#B0B263',
      accentShadow: '#8D8E4F',
      background: '#E5DFF0',
      cardBackground: '#DAD4E4',
      text: '#0C0711',
      extra: '#E5DFF0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD7671',
      primaryShadow: '#9E5E58',
      accent: '#BCBE7A',
      accentShadow: '#B0B263',
      background: '#0C0711',
      cardBackground: '#16111B',
      text: '#E5DFF0',
      extra: '#E8E2F2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Opaque Courtyard',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DB9E48',
      primaryShadow: '#AF7E3A',
      accent: '#943030',
      accentShadow: '#762626',
      background: '#FFE5C3',
      cardBackground: '#F2DAB9',
      text: '#3D3136',
      extra: '#3D3136',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E0AD63',
      primaryShadow: '#DB9E48',
      accent: '#A44F4F',
      accentShadow: '#943030',
      background: '#0F0D0E',
      cardBackground: '#161415',
      text: '#FFE5C3',
      extra: '#50464A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blaze Thicket',
    tags: ['curated'],
    light: {
      primary: '#E14C43',
      primaryShadow: '#B43D36',
      accent: '#E47D4B',
      accentShadow: '#B6643C',
      background: '#EBEDE6',
      cardBackground: '#DFE1DB',
      text: '#000000',
      extra: '#181F27',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E6675F',
      primaryShadow: '#E14C43',
      accent: '#E89166',
      accentShadow: '#E47D4B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#EBEDE6',
      extra: '#2F353D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen Aurora',
    tags: ['curated'],
    light: {
      primary: '#DA7D22',
      primaryShadow: '#AE641B',
      accent: '#D32734',
      accentShadow: '#A91F2A',
      background: '#FDFDF8',
      cardBackground: '#F0F0EC',
      text: '#1B1C33',
      extra: '#E6DA29',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E09143',
      primaryShadow: '#DA7D22',
      accent: '#DA4752',
      accentShadow: '#D32734',
      background: '#1B1C33',
      cardBackground: '#24253B',
      text: '#FDFDF8',
      extra: '#E9DE3E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Arid Courtyard',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CC6600',
      primaryShadow: '#A35200',
      accent: '#CC9900',
      accentShadow: '#A37A00',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#CCCC99',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D47D26',
      primaryShadow: '#CC6600',
      accent: '#D4A826',
      accentShadow: '#CC9900',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#D1D1A3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy Rain',
    tags: ['curated'],
    light: {
      primary: '#D19100',
      primaryShadow: '#A77400',
      accent: '#00A712',
      accentShadow: '#00860E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#1F003C',
      extra: '#481E11',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D8A226',
      primaryShadow: '#D19100',
      accent: '#26B436',
      accentShadow: '#00A712',
      background: '#1F003C',
      cardBackground: '#280A44',
      text: '#FFFFFF',
      extra: '#5A3529',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Balmy Blizzard',
    tags: ['dark', 'curated'],
    light: {
      primary: '#1D1D2E',
      primaryShadow: '#171725',
      accent: '#39394B',
      accentShadow: '#2E2E3C',
      background: '#DFDFE0',
      cardBackground: '#D6D6D7',
      text: '#1D1D2E',
      extra: '#807C79',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3F3F4D',
      primaryShadow: '#1D1D2E',
      accent: '#575766',
      accentShadow: '#39394B',
      background: '#1D1D2E',
      cardBackground: '#262636',
      text: '#F0F0F0',
      extra: '#8D8986',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Earthy Tapestry',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#C86F15',
      primaryShadow: '#A05911',
      accent: '#BF9C29',
      accentShadow: '#997D21',
      background: '#EDE6DE',
      cardBackground: '#E4DDD5',
      text: '#21170B',
      extra: '#E2C423',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D08538',
      primaryShadow: '#C86F15',
      accent: '#C9AB49',
      accentShadow: '#BF9C29',
      background: '#21170B',
      cardBackground: '#2A2015',
      text: '#EBC070',
      extra: '#E5CA39',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mediterranean Permafrost',
    tags: ['cool', 'curated'],
    light: {
      primary: '#924A92',
      primaryShadow: '#753B75',
      accent: '#413164',
      accentShadow: '#342750',
      background: '#E8E3E8',
      cardBackground: '#DFDADF',
      text: '#413164',
      extra: '#B47D8B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A265A2',
      primaryShadow: '#924A92',
      accent: '#5E507B',
      accentShadow: '#413164',
      background: '#0C0A10',
      cardBackground: '#131117',
      text: '#CCDCC9',
      extra: '#BC8A97',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rainy Orchard',
    tags: ['warm', 'curated'],
    light: {
      primary: '#508714',
      primaryShadow: '#406C10',
      accent: '#6D3E0F',
      accentShadow: '#57320C',
      background: '#E3E8DE',
      cardBackground: '#DADFD5',
      text: '#26171B',
      extra: '#26171B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6A9937',
      primaryShadow: '#508714',
      accent: '#835B33',
      accentShadow: '#6D3E0F',
      background: '#26171B',
      cardBackground: '#2F2024',
      text: '#E0DCA4',
      extra: '#3C2E32',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glow Stream',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#3A5683',
      primaryShadow: '#2E4569',
      accent: '#5A80A6',
      accentShadow: '#486685',
      background: '#F4F0BB',
      cardBackground: '#E8E4B2',
      text: '#1A1A1A',
      extra: '#EBA6A9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#586F96',
      primaryShadow: '#3A5683',
      accent: '#7393B3',
      accentShadow: '#5A80A6',
      background: '#0A0B10',
      cardBackground: '#111217',
      text: '#F4F0BB',
      extra: '#EDAFB2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Coast',
    tags: ['curated'],
    light: {
      primary: '#671087',
      primaryShadow: '#520D6C',
      accent: '#955AB9',
      accentShadow: '#774894',
      background: '#D8EEF0',
      cardBackground: '#CDE2E4',
      text: '#392B29',
      extra: '#E1D4CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7E3499',
      primaryShadow: '#671087',
      accent: '#A573C4',
      accentShadow: '#955AB9',
      background: '#0B0810',
      cardBackground: '#120F17',
      text: '#D8EEF0',
      extra: '#E4D8D0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Foyer',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#32C982',
      primaryShadow: '#28A168',
      accent: '#9B2E8E',
      accentShadow: '#7C2572',
      background: '#F9FCFF',
      cardBackground: '#EDEFF2',
      text: '#091E26',
      extra: '#B7E8E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#51D195',
      primaryShadow: '#32C982',
      accent: '#AA4D9F',
      accentShadow: '#9B2E8E',
      background: '#091E26',
      cardBackground: '#13272F',
      text: '#F9FCFF',
      extra: '#BEEAEA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Cove',
    tags: ['warm', 'curated'],
    light: {
      primary: '#18B7B1',
      primaryShadow: '#13928E',
      accent: '#0B5FA4',
      accentShadow: '#094C83',
      background: '#F0EEE2',
      cardBackground: '#E4E2D7',
      text: '#3D322B',
      extra: '#3D322B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3BC2BD',
      primaryShadow: '#18B7B1',
      accent: '#3077B2',
      accentShadow: '#0B5FA4',
      background: '#090D11',
      cardBackground: '#101418',
      text: '#F0EEE2',
      extra: '#504740',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Summit',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CBB234',
      primaryShadow: '#A28E2A',
      accent: '#2B5E91',
      accentShadow: '#224B74',
      background: '#EDEBE1',
      cardBackground: '#E4E2D8',
      text: '#2C2111',
      extra: '#2C2111',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D3BE52',
      primaryShadow: '#CBB234',
      accent: '#4B76A2',
      accentShadow: '#2B5E91',
      background: '#2C2111',
      cardBackground: '#342A1B',
      text: '#DED598',
      extra: '#413729',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Snowfall',
    tags: ['curated'],
    light: {
      primary: '#20A1BF',
      primaryShadow: '#1A8199',
      accent: '#04358D',
      accentShadow: '#032A71',
      background: '#FFF9E5',
      cardBackground: '#F2EDDA',
      text: '#250A57',
      extra: '#3EE0DB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#41AFC9',
      primaryShadow: '#20A1BF',
      accent: '#2A539E',
      accentShadow: '#04358D',
      background: '#250A57',
      cardBackground: '#2E145E',
      text: '#FFF9E5',
      extra: '#51E3DF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Thicket',
    tags: ['bright', 'curated'],
    light: {
      primary: '#266B90',
      primaryShadow: '#1E5673',
      accent: '#9B63BF',
      accentShadow: '#7C4F99',
      background: '#FFF3F3',
      cardBackground: '#F2E7E7',
      text: '#1A1A1A',
      extra: '#E477B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4781A1',
      primaryShadow: '#266B90',
      accent: '#AA7AC9',
      accentShadow: '#9B63BF',
      background: '#090B10',
      cardBackground: '#101217',
      text: '#FFF3F3',
      extra: '#E785BD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Cliff',
    tags: ['curated'],
    light: {
      primary: '#423568',
      primaryShadow: '#352A53',
      accent: '#B67780',
      accentShadow: '#925F66',
      background: '#F8F3FF',
      cardBackground: '#ECE7F2',
      text: '#0F0F0C',
      extra: '#423568',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E537F',
      primaryShadow: '#423568',
      accent: '#C18B93',
      accentShadow: '#B67780',
      background: '#0F0F0C',
      cardBackground: '#191916',
      text: '#F8F3FF',
      extra: '#554977',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Tapestry',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9D5550',
      primaryShadow: '#7E4440',
      accent: '#F9B18E',
      accentShadow: '#C78E72',
      background: '#FAE8BC',
      cardBackground: '#EEDCB3',
      text: '#291D22',
      extra: '#291D22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AC6F6A',
      primaryShadow: '#9D5550',
      accent: '#FABD9F',
      accentShadow: '#F9B18E',
      background: '#291D22',
      cardBackground: '#32262B',
      text: '#FAE8BC',
      extra: '#3E3438',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Peninsula',
    tags: ['cool', 'curated'],
    light: {
      primary: '#1C0820',
      primaryShadow: '#16061A',
      accent: '#3C3468',
      accentShadow: '#302A53',
      background: '#D0F4F8',
      cardBackground: '#C6E8EC',
      text: '#1C0820',
      extra: '#70B0C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3E2D41',
      primaryShadow: '#1C0820',
      accent: '#59527F',
      accentShadow: '#3C3468',
      background: '#1C0820',
      cardBackground: '#251229',
      text: '#D0F4F8',
      extra: '#7EB8C6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Turret',
    tags: ['curated'],
    light: {
      primary: '#DD4455',
      primaryShadow: '#B13644',
      accent: '#773333',
      accentShadow: '#5F2929',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#99AABB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E2606F',
      primaryShadow: '#DD4455',
      accent: '#8B5252',
      accentShadow: '#773333',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#A3B3C2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Luminous Eclipse',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#917F4E',
      primaryShadow: '#74663E',
      accent: '#685A35',
      accentShadow: '#53482A',
      background: '#FFFEF0',
      cardBackground: '#F2F1E4',
      text: '#3C2D1C',
      extra: '#3C2D1C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A29269',
      primaryShadow: '#917F4E',
      accent: '#7F7353',
      accentShadow: '#685A35',
      background: '#0C0C0E',
      cardBackground: '#131315',
      text: '#FFFEF0',
      extra: '#504233',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Desert Temple',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#EF8FA4',
      primaryShadow: '#BF7283',
      accent: '#FCB7A4',
      accentShadow: '#CA9283',
      background: '#FCFBF9',
      cardBackground: '#EFEEED',
      text: '#1A1A1A',
      extra: '#F4A9B9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F1A0B2',
      primaryShadow: '#EF8FA4',
      accent: '#FCC2B2',
      accentShadow: '#FCB7A4',
      background: '#0F0C11',
      cardBackground: '#161318',
      text: '#FCFBF9',
      extra: '#F5B2C0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Slate Petal',
    tags: ['cool', 'curated'],
    light: {
      primary: '#009048',
      primaryShadow: '#00733A',
      accent: '#0024D8',
      accentShadow: '#001DAD',
      background: '#D8FCFC',
      cardBackground: '#CDEFEF',
      text: '#000000',
      extra: '#D86C90',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A163',
      primaryShadow: '#009048',
      accent: '#2645DE',
      accentShadow: '#0024D8',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#D8FCFC',
      extra: '#DC7B9B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mellow Lake',
    tags: ['dark', 'curated'],
    light: {
      primary: '#D24646',
      primaryShadow: '#A83838',
      accent: '#8A1F52',
      accentShadow: '#6E1942',
      background: '#F7D69B',
      cardBackground: '#EBCB93',
      text: '#221B28',
      extra: '#F7D69B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D96262',
      primaryShadow: '#D24646',
      accent: '#9C416C',
      accentShadow: '#8A1F52',
      background: '#221B28',
      cardBackground: '#2B2431',
      text: '#F7D69B',
      extra: '#F8DAA5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cotton Valley',
    tags: ['curated'],
    light: {
      primary: '#3685A1',
      primaryShadow: '#2B6A81',
      accent: '#170443',
      accentShadow: '#120336',
      background: '#E1E7EA',
      cardBackground: '#D8DEE1',
      text: '#170443',
      extra: '#5FC79F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5497AF',
      primaryShadow: '#3685A1',
      accent: '#3A2A5F',
      accentShadow: '#170443',
      background: '#170443',
      cardBackground: '#200E4B',
      text: '#E6D996',
      extra: '#6FCDA9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Granite Steppe',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A54371',
      primaryShadow: '#84365A',
      accent: '#4C388A',
      accentShadow: '#3D2D6E',
      background: '#FBD6AD',
      cardBackground: '#EECBA4',
      text: '#4C388A',
      extra: '#FBD6AD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B35F86',
      primaryShadow: '#A54371',
      accent: '#67569C',
      accentShadow: '#4C388A',
      background: '#0D0A0F',
      cardBackground: '#141116',
      text: '#FBD6AD',
      extra: '#FBDAB5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Autumn Star',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#E13A94',
      primaryShadow: '#B42E76',
      accent: '#1F2A5B',
      accentShadow: '#192249',
      background: '#EFE1E9',
      cardBackground: '#E5D8E0',
      text: '#000000',
      extra: '#2E3F5D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E658A4',
      primaryShadow: '#E13A94',
      accent: '#414A74',
      accentShadow: '#1F2A5B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F0F0F0',
      extra: '#43526D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Warm Plaza',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#8F5B5B',
      primaryShadow: '#724949',
      accent: '#453D34',
      accentShadow: '#37312A',
      background: '#E8E4E4',
      cardBackground: '#DFDBDB',
      text: '#18181E',
      extra: '#BDAFA8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A07474',
      primaryShadow: '#8F5B5B',
      accent: '#615A52',
      accentShadow: '#453D34',
      background: '#18181E',
      cardBackground: '#212127',
      text: '#F0F0F0',
      extra: '#C4B7B1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silk Nova',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#0092A9',
      primaryShadow: '#007587',
      accent: '#D564FF',
      accentShadow: '#AA50CC',
      background: '#D8FFB9',
      cardBackground: '#CDF2B0',
      text: '#1A1A1A',
      extra: '#0D8300',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26A2B6',
      primaryShadow: '#0092A9',
      accent: '#DB7BFF',
      accentShadow: '#D564FF',
      background: '#080C11',
      cardBackground: '#0F1318',
      text: '#D8FFB9',
      extra: '#258F1A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frost Fumarole',
    tags: ['curated'],
    light: {
      primary: '#AC8000',
      primaryShadow: '#8A6600',
      accent: '#0078FC',
      accentShadow: '#0060CA',
      background: '#FCF8FC',
      cardBackground: '#EFECEF',
      text: '#000000',
      extra: '#2C2C2C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B89326',
      primaryShadow: '#AC8000',
      accent: '#268CFC',
      accentShadow: '#0078FC',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FCF8FC',
      extra: '#414141',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Leaf',
    tags: ['curated'],
    light: {
      primary: '#C78749',
      primaryShadow: '#9F6C3A',
      accent: '#A36043',
      accentShadow: '#824D36',
      background: '#F5F6B8',
      cardBackground: '#E9EAAF',
      text: '#000000',
      extra: '#ACC765',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF9964',
      primaryShadow: '#C78749',
      accent: '#B1785F',
      accentShadow: '#A36043',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F5F6B8',
      extra: '#B4CD74',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shadowy Cloister',
    tags: ['curated'],
    light: {
      primary: '#709B1A',
      primaryShadow: '#5A7C15',
      accent: '#C12C15',
      accentShadow: '#9A2311',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#29283A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#85AA3C',
      primaryShadow: '#709B1A',
      accent: '#CA4C38',
      accentShadow: '#C12C15',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#3E3E4E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Modern Aura',
    tags: ['curated'],
    light: {
      primary: '#F07508',
      primaryShadow: '#C05E06',
      accent: '#0F7373',
      accentShadow: '#0C5C5C',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#FFF23B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F28A2D',
      primaryShadow: '#F07508',
      accent: '#338888',
      accentShadow: '#0F7373',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#FFF34F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Organic Magma',
    tags: ['curated'],
    light: {
      primary: '#458575',
      primaryShadow: '#376A5E',
      accent: '#70384C',
      accentShadow: '#5A2D3D',
      background: '#F0ECC7',
      cardBackground: '#E4E0BD',
      text: '#1C1C1F',
      extra: '#1C1C1F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#61978A',
      primaryShadow: '#458575',
      accent: '#855667',
      accentShadow: '#70384C',
      background: '#1C1C1F',
      cardBackground: '#252528',
      text: '#F0ECC7',
      extra: '#333335',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crisp Studio',
    tags: ['curated'],
    light: {
      primary: '#DB5341',
      primaryShadow: '#AF4234',
      accent: '#BD2D51',
      accentShadow: '#972441',
      background: '#FEFFF2',
      cardBackground: '#F1F2E6',
      text: '#211421',
      extra: '#2C253D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E06D5E',
      primaryShadow: '#DB5341',
      accent: '#C74D6B',
      accentShadow: '#BD2D51',
      background: '#211421',
      cardBackground: '#2A1D2A',
      text: '#FEFFF2',
      extra: '#413B50',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Blaze Current',
    tags: ['curated'],
    light: {
      primary: '#C7506B',
      primaryShadow: '#9F4056',
      accent: '#E3755F',
      accentShadow: '#B65E4C',
      background: '#FFEFC9',
      cardBackground: '#F2E3BF',
      text: '#1F2E52',
      extra: '#3A224F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CF6A81',
      primaryShadow: '#C7506B',
      accent: '#E78A77',
      accentShadow: '#E3755F',
      background: '#0E0A0F',
      cardBackground: '#151116',
      text: '#FFEFC9',
      extra: '#4E3861',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Olive Veranda',
    tags: ['dark', 'curated'],
    light: {
      primary: '#6E8A2B',
      primaryShadow: '#586E22',
      accent: '#A1A23C',
      accentShadow: '#818230',
      background: '#E3DACC',
      cardBackground: '#D8CFC2',
      text: '#18040D',
      extra: '#B6357C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#849C4B',
      primaryShadow: '#6E8A2B',
      accent: '#AFB059',
      accentShadow: '#A1A23C',
      background: '#18040D',
      cardBackground: '#210E17',
      text: '#E3DACC',
      extra: '#BD4989',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bright Bay',
    tags: ['cool', 'curated'],
    light: {
      primary: '#8361FF',
      primaryShadow: '#694ECC',
      accent: '#B588FF',
      accentShadow: '#916DCC',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#262536',
      extra: '#262536',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9679FF',
      primaryShadow: '#8361FF',
      accent: '#C09AFF',
      accentShadow: '#B588FF',
      background: '#0C0B14',
      cardBackground: '#13121B',
      text: '#FFFFFF',
      extra: '#3C3B4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Midnight Field',
    tags: ['cool', 'curated'],
    light: {
      primary: '#A05248',
      primaryShadow: '#80423A',
      accent: '#59B561',
      accentShadow: '#47914E',
      background: '#EFEFEF',
      cardBackground: '#E3E3E3',
      text: '#17111A',
      extra: '#D5CAD8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AE6C63',
      primaryShadow: '#A05248',
      accent: '#72C079',
      accentShadow: '#59B561',
      background: '#17111A',
      cardBackground: '#201B23',
      text: '#EFEFEF',
      extra: '#D9CFDC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Granite Heath',
    tags: ['curated'],
    light: {
      primary: '#D66F24',
      primaryShadow: '#AB591D',
      accent: '#21913B',
      accentShadow: '#1A742F',
      background: '#EBD5BD',
      cardBackground: '#DFCAB4',
      text: '#08050E',
      extra: '#8C1E2C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DC8545',
      primaryShadow: '#D66F24',
      accent: '#42A258',
      accentShadow: '#21913B',
      background: '#08050E',
      cardBackground: '#120F18',
      text: '#EBD5BD',
      extra: '#983541',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Matte Sunset',
    tags: ['warm', 'curated'],
    light: {
      primary: '#693909',
      primaryShadow: '#542E07',
      accent: '#B8412D',
      accentShadow: '#933424',
      background: '#FFF9DA',
      cardBackground: '#F2EDCF',
      text: '#0F0C00',
      extra: '#D09049',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#80572E',
      primaryShadow: '#693909',
      accent: '#C35E4D',
      accentShadow: '#B8412D',
      background: '#0F0C00',
      cardBackground: '#19160A',
      text: '#FFF9DA',
      extra: '#D59B5B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Dawn',
    tags: ['dark', 'curated'],
    light: {
      primary: '#536A43',
      primaryShadow: '#425536',
      accent: '#2D3C33',
      accentShadow: '#243029',
      background: '#E3E5E2',
      cardBackground: '#DADCD9',
      text: '#23232B',
      extra: '#23232B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6D805F',
      primaryShadow: '#536A43',
      accent: '#4D5952',
      accentShadow: '#2D3C33',
      background: '#23232B',
      cardBackground: '#2C2C33',
      text: '#F0F0F0',
      extra: '#393940',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shadowy Galaxy',
    tags: ['curated'],
    light: {
      primary: '#3A71A6',
      primaryShadow: '#2E5A85',
      accent: '#C76652',
      accentShadow: '#9F5242',
      background: '#EFECE8',
      cardBackground: '#E3E0DC',
      text: '#060D23',
      extra: '#C7AF42',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5886B3',
      primaryShadow: '#3A71A6',
      accent: '#CF7D6C',
      accentShadow: '#C76652',
      background: '#060D23',
      cardBackground: '#10172C',
      text: '#EFECE8',
      extra: '#CDB755',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silver Relief',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9E2E9D',
      primaryShadow: '#7E257E',
      accent: '#141852',
      accentShadow: '#101342',
      background: '#E9E0E9',
      cardBackground: '#E0D7E0',
      text: '#141852',
      extra: '#E779A0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD4DAC',
      primaryShadow: '#9E2E9D',
      accent: '#373B6C',
      accentShadow: '#141852',
      background: '#141852',
      cardBackground: '#1D2159',
      text: '#DAC4B9',
      extra: '#E986AA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Frosty Rapids',
    tags: ['cool', 'curated'],
    light: {
      primary: '#25034E',
      primaryShadow: '#1E023E',
      accent: '#2C399C',
      accentShadow: '#232E7D',
      background: '#D4E1FF',
      cardBackground: '#C9D6F2',
      text: '#25034E',
      extra: '#D4E1FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#462969',
      primaryShadow: '#25034E',
      accent: '#4C57AB',
      accentShadow: '#2C399C',
      background: '#25034E',
      cardBackground: '#2E0D55',
      text: '#D4E1FF',
      extra: '#D8E4FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ethereal Vent',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D88038',
      primaryShadow: '#AD662D',
      accent: '#A13D3B',
      accentShadow: '#81312F',
      background: '#F9F5EF',
      cardBackground: '#EDE9E3',
      text: '#040303',
      extra: '#47416B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE9356',
      primaryShadow: '#D88038',
      accent: '#AF5A58',
      accentShadow: '#A13D3B',
      background: '#040303',
      cardBackground: '#0E0D0D',
      text: '#F9F5EF',
      extra: '#59547A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Vent',
    tags: ['cool', 'curated'],
    light: {
      primary: '#D55175',
      primaryShadow: '#AA415E',
      accent: '#4AD0E2',
      accentShadow: '#3BA6B5',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#32313B',
      extra: '#FD5E64',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DB6B8A',
      primaryShadow: '#D55175',
      accent: '#65D7E6',
      accentShadow: '#4AD0E2',
      background: '#0E0A10',
      cardBackground: '#151117',
      text: '#FFFFFF',
      extra: '#FD6E74',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cool Atrium',
    tags: ['cool', 'curated'],
    light: {
      primary: '#319682',
      primaryShadow: '#277868',
      accent: '#2E6980',
      accentShadow: '#255466',
      background: '#FEFFF7',
      cardBackground: '#F1F2EB',
      text: '#18181A',
      extra: '#2F3238',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#50A695',
      primaryShadow: '#319682',
      accent: '#4D8093',
      accentShadow: '#2E6980',
      background: '#18181A',
      cardBackground: '#212123',
      text: '#FEFFF7',
      extra: '#44474C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble Vent',
    tags: ['curated'],
    light: {
      primary: '#9B6E2D',
      primaryShadow: '#7C5824',
      accent: '#316F23',
      accentShadow: '#27591C',
      background: '#FEFEFE',
      cardBackground: '#F1F1F1',
      text: '#050403',
      extra: '#221F31',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AA844D',
      primaryShadow: '#9B6E2D',
      accent: '#508544',
      accentShadow: '#316F23',
      background: '#050403',
      cardBackground: '#0F0E0D',
      text: '#FEFEFE',
      extra: '#383546',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spark Volcano',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#496B22',
      primaryShadow: '#3A561B',
      accent: '#0E450B',
      accentShadow: '#0B3709',
      background: '#E3E5E0',
      cardBackground: '#DADCD7',
      text: '#1B2A09',
      extra: '#1B2A09',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#648143',
      primaryShadow: '#496B22',
      accent: '#326130',
      accentShadow: '#0E450B',
      background: '#1B2A09',
      cardBackground: '#243313',
      text: '#F0F0F0',
      extra: '#323F22',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Futuristic Copse',
    tags: ['curated'],
    light: {
      primary: '#E93F59',
      primaryShadow: '#BA3247',
      accent: '#A0B035',
      accentShadow: '#808D2A',
      background: '#F0EFDF',
      cardBackground: '#E4E3D4',
      text: '#191520',
      extra: '#2A2D3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC5C72',
      primaryShadow: '#E93F59',
      accent: '#AEBC53',
      accentShadow: '#A0B035',
      background: '#191520',
      cardBackground: '#221E29',
      text: '#F0EFDF',
      extra: '#3F4250',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Mural',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#D95338',
      primaryShadow: '#AE422D',
      accent: '#C92631',
      accentShadow: '#A11E27',
      background: '#EDEDD5',
      cardBackground: '#E1E1CA',
      text: '#050103',
      extra: '#940F37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DF6D56',
      primaryShadow: '#D95338',
      accent: '#D14750',
      accentShadow: '#C92631',
      background: '#050103',
      cardBackground: '#0F0B0D',
      text: '#EDEDD5',
      extra: '#9F274B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Futuristic Avalanche',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#E36D04',
      primaryShadow: '#B65703',
      accent: '#CB4600',
      accentShadow: '#A23800',
      background: '#DEEEC7',
      cardBackground: '#D3E2BD',
      text: '#000000',
      extra: '#DEEEC7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E7832A',
      primaryShadow: '#E36D04',
      accent: '#D36226',
      accentShadow: '#CB4600',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#DEEEC7',
      extra: '#E1F0CD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Turret',
    tags: ['cool', 'curated'],
    light: {
      primary: '#527BBC',
      primaryShadow: '#426296',
      accent: '#2F5D82',
      accentShadow: '#264A68',
      background: '#E5EDF3',
      cardBackground: '#DAE1E7',
      text: '#121922',
      extra: '#172B3A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6C8FC6',
      primaryShadow: '#527BBC',
      accent: '#4E7595',
      accentShadow: '#2F5D82',
      background: '#121922',
      cardBackground: '#1B222B',
      text: '#E5EDF3',
      extra: '#2E404E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ruby Basin',
    tags: ['curated'],
    light: {
      primary: '#5AB00F',
      primaryShadow: '#488D0C',
      accent: '#F0007D',
      accentShadow: '#C00064',
      background: '#FAFAFA',
      cardBackground: '#EEEEEE',
      text: '#071822',
      extra: '#FF7196',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#73BC33',
      primaryShadow: '#5AB00F',
      accent: '#F22691',
      accentShadow: '#F0007D',
      background: '#071822',
      cardBackground: '#11212B',
      text: '#FAFAFA',
      extra: '#FF7FA1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Pack',
    tags: ['warm', 'curated'],
    light: {
      primary: '#20220C',
      primaryShadow: '#1A1B0A',
      accent: '#423E25',
      accentShadow: '#35321E',
      background: '#DFE0DE',
      cardBackground: '#D6D7D5',
      text: '#20220C',
      extra: '#746754',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#414330',
      primaryShadow: '#20220C',
      accent: '#5E5B46',
      accentShadow: '#423E25',
      background: '#20220C',
      cardBackground: '#292B16',
      text: '#DFD7CC',
      extra: '#827665',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble Sunrise',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#E00080',
      primaryShadow: '#B30066',
      accent: '#800080',
      accentShadow: '#660066',
      background: '#EFDDE7',
      cardBackground: '#E5D4DE',
      text: '#000080',
      extra: '#400080',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E52693',
      primaryShadow: '#E00080',
      accent: '#932693',
      accentShadow: '#800080',
      background: '#000080',
      cardBackground: '#0A0A85',
      text: '#F0F0F0',
      extra: '#531A8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Stormy Soul',
    tags: ['curated'],
    light: {
      primary: '#C9597A',
      primaryShadow: '#A14762',
      accent: '#C4B152',
      accentShadow: '#9D8E42',
      background: '#E0E0E0',
      cardBackground: '#D5D5D5',
      text: '#373545',
      extra: '#373545',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D1728E',
      primaryShadow: '#C9597A',
      accent: '#CDBD6C',
      accentShadow: '#C4B152',
      background: '#0E0B10',
      cardBackground: '#151217',
      text: '#E0E0E0',
      extra: '#4B4958',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Plaza',
    tags: ['warm', 'curated'],
    light: {
      primary: '#2B0B27',
      primaryShadow: '#22091F',
      accent: '#592656',
      accentShadow: '#471E45',
      background: '#E0DEE0',
      cardBackground: '#D7D5D7',
      text: '#2B0B27',
      extra: '#5B4358',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4B3047',
      primaryShadow: '#2B0B27',
      accent: '#72476F',
      accentShadow: '#592656',
      background: '#2B0B27',
      cardBackground: '#331530',
      text: '#C9C6B7',
      extra: '#6B5669',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Navy Stream',
    tags: ['curated'],
    light: {
      primary: '#DE6A38',
      primaryShadow: '#B2552D',
      accent: '#2764A1',
      accentShadow: '#1F5081',
      background: '#F1FFBF',
      cardBackground: '#E5F2B5',
      text: '#2B1709',
      extra: '#15452B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E38056',
      primaryShadow: '#DE6A38',
      accent: '#477BAF',
      accentShadow: '#2764A1',
      background: '#2B1709',
      cardBackground: '#332013',
      text: '#F1FFBF',
      extra: '#2C5840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Dream',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#9F673E',
      primaryShadow: '#7F5232',
      accent: '#080400',
      accentShadow: '#060300',
      background: '#EAE5E2',
      cardBackground: '#E1DCD9',
      text: '#080400',
      extra: '#CDBEAC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#AD7E5B',
      primaryShadow: '#9F673E',
      accent: '#2D2A26',
      accentShadow: '#080400',
      background: '#080400',
      cardBackground: '#120E0A',
      text: '#CDBEAC',
      extra: '#D2C5B4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Meadow',
    tags: ['cool', 'curated'],
    light: {
      primary: '#E955AE',
      primaryShadow: '#BA448B',
      accent: '#943C59',
      accentShadow: '#763047',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#0F0D1C',
      extra: '#7B86A9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC6FBA',
      primaryShadow: '#E955AE',
      accent: '#A45972',
      accentShadow: '#943C59',
      background: '#0F0D1C',
      cardBackground: '#191725',
      text: '#FFFFFF',
      extra: '#8892B2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rust Springs',
    tags: ['curated'],
    light: {
      primary: '#CF7862',
      primaryShadow: '#A6604E',
      accent: '#4A7A96',
      accentShadow: '#3B6278',
      background: '#F9F9F9',
      cardBackground: '#EDEDED',
      text: '#181818',
      extra: '#292831',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D68C7A',
      primaryShadow: '#CF7862',
      accent: '#658EA6',
      accentShadow: '#4A7A96',
      background: '#181818',
      cardBackground: '#212121',
      text: '#F9F9F9',
      extra: '#3E3E46',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cyan Sculpture',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00E988',
      primaryShadow: '#00BA6D',
      accent: '#E00B55',
      accentShadow: '#B30944',
      background: '#F4F4F4',
      cardBackground: '#E8E8E8',
      text: '#2B2045',
      extra: '#872361',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26EC9A',
      primaryShadow: '#00E988',
      accent: '#E5306F',
      accentShadow: '#E00B55',
      background: '#080F10',
      cardBackground: '#0F1617',
      text: '#F4F4F4',
      extra: '#933971',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bronze Sunset',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#C355DA',
      primaryShadow: '#9C44AE',
      accent: '#84266E',
      accentShadow: '#6A1E58',
      background: '#ECE4EE',
      cardBackground: '#E3DBE4',
      text: '#050002',
      extra: '#31144F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CC6FE0',
      primaryShadow: '#C355DA',
      accent: '#964784',
      accentShadow: '#84266E',
      background: '#050002',
      cardBackground: '#0F0A0C',
      text: '#F0F0F0',
      extra: '#462C61',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ephemeral Crater',
    tags: ['cool', 'curated'],
    light: {
      primary: '#414600',
      primaryShadow: '#343800',
      accent: '#E545FF',
      accentShadow: '#B737CC',
      background: '#FFCEFF',
      cardBackground: '#F2C4F2',
      text: '#000000',
      extra: '#5E397A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E6226',
      primaryShadow: '#414600',
      accent: '#E961FF',
      accentShadow: '#E545FF',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFCEFF',
      extra: '#6E4D87',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Silk Grove',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#20759C',
      primaryShadow: '#1A5E7D',
      accent: '#084062',
      accentShadow: '#06334E',
      background: '#DFE6E9',
      cardBackground: '#D6DDE0',
      text: '#000408',
      extra: '#002841',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#418AAB',
      primaryShadow: '#20759C',
      accent: '#2D5D7A',
      accentShadow: '#084062',
      background: '#000408',
      cardBackground: '#0A0E12',
      text: '#F0F0F0',
      extra: '#1A3E54',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Fumarole',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E98549',
      primaryShadow: '#BA6A3A',
      accent: '#C6434E',
      accentShadow: '#9E363E',
      background: '#F4F9E6',
      cardBackground: '#E8EDDB',
      text: '#1B0F28',
      extra: '#4C6684',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC9764',
      primaryShadow: '#E98549',
      accent: '#CF5F69',
      accentShadow: '#C6434E',
      background: '#1B0F28',
      cardBackground: '#241931',
      text: '#F4F9E6',
      extra: '#5E7590',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Velvet Soul',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AD3450',
      primaryShadow: '#8A2A40',
      accent: '#D98257',
      accentShadow: '#AE6846',
      background: '#F2D5C2',
      cardBackground: '#E6CAB8',
      text: '#541230',
      extra: '#541230',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B9526A',
      primaryShadow: '#AD3450',
      accent: '#DF9570',
      accentShadow: '#D98257',
      background: '#0D0A0E',
      cardBackground: '#141115',
      text: '#F2D5C2',
      extra: '#652A45',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Twilight Storm',
    tags: ['curated'],
    light: {
      primary: '#08839B',
      primaryShadow: '#06697C',
      accent: '#F7B32C',
      accentShadow: '#C68F23',
      background: '#FBF5B1',
      cardBackground: '#EEE9A8',
      text: '#2C353C',
      extra: '#2C353C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2D96AA',
      primaryShadow: '#08839B',
      accent: '#F8BE4C',
      accentShadow: '#F7B32C',
      background: '#080C11',
      cardBackground: '#0F1318',
      text: '#FBF5B1',
      extra: '#414950',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artisan Fresco',
    tags: ['curated'],
    light: {
      primary: '#56997A',
      primaryShadow: '#457A62',
      accent: '#526391',
      accentShadow: '#424F74',
      background: '#FDFFE5',
      cardBackground: '#F0F2DA',
      text: '#292536',
      extra: '#292536',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6FA88E',
      primaryShadow: '#56997A',
      accent: '#6C7AA2',
      accentShadow: '#526391',
      background: '#0B0D10',
      cardBackground: '#121417',
      text: '#FDFFE5',
      extra: '#3E3B4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Wave',
    tags: ['curated'],
    light: {
      primary: '#6F8B6C',
      primaryShadow: '#596F56',
      accent: '#171A24',
      accentShadow: '#12151D',
      background: '#E6E8E5',
      cardBackground: '#DDDFDC',
      text: '#171A24',
      extra: '#BEBC99',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#859C82',
      primaryShadow: '#6F8B6C',
      accent: '#3A3C45',
      accentShadow: '#171A24',
      background: '#171A24',
      cardBackground: '#20232D',
      text: '#BEBC99',
      extra: '#C5C3A3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Calm Magma',
    tags: ['bright', 'curated'],
    light: {
      primary: '#FAB47E',
      primaryShadow: '#C89065',
      accent: '#9D90DA',
      accentShadow: '#7E73AE',
      background: '#FFE58F',
      cardBackground: '#F2DA88',
      text: '#1A1A1A',
      extra: '#FACFCF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FBBF91',
      primaryShadow: '#FAB47E',
      accent: '#ACA1E0',
      accentShadow: '#9D90DA',
      background: '#100D10',
      cardBackground: '#171417',
      text: '#FFE58F',
      extra: '#FBD4D4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Morning Bastion',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C2A783',
      primaryShadow: '#9B8669',
      accent: '#907577',
      accentShadow: '#735E5F',
      background: '#FBEDE4',
      cardBackground: '#EEE1D9',
      text: '#212225',
      extra: '#212225',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CBB496',
      primaryShadow: '#C2A783',
      accent: '#A18A8B',
      accentShadow: '#907577',
      background: '#212225',
      cardBackground: '#2A2B2E',
      text: '#FBEDE4',
      extra: '#37383B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Nordic Current',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D1505B',
      primaryShadow: '#A74049',
      accent: '#8B3A49',
      accentShadow: '#6F2E3A',
      background: '#FFEDD7',
      cardBackground: '#F2E1CC',
      text: '#3B132B',
      extra: '#3B132B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D86A74',
      primaryShadow: '#D1505B',
      accent: '#9C5864',
      accentShadow: '#8B3A49',
      background: '#3B132B',
      cardBackground: '#431C33',
      text: '#FFEDD7',
      extra: '#4F2B40',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Nebula',
    tags: ['cool', 'curated'],
    light: {
      primary: '#3C0197',
      primaryShadow: '#300179',
      accent: '#968F79',
      accentShadow: '#787261',
      background: '#D1D8FF',
      cardBackground: '#C7CDF2',
      text: '#2C0148',
      extra: '#D1D8FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5927A7',
      primaryShadow: '#3C0197',
      accent: '#A6A08D',
      accentShadow: '#968F79',
      background: '#2C0148',
      cardBackground: '#340B4F',
      text: '#D1D8FF',
      extra: '#D6DCFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Retro Snowfall',
    tags: ['cool', 'curated'],
    light: {
      primary: '#277BAE',
      primaryShadow: '#1F628B',
      accent: '#530078',
      accentShadow: '#420060',
      background: '#ECFF9E',
      cardBackground: '#E0F296',
      text: '#530078',
      extra: '#5EDE83',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#478FBA',
      primaryShadow: '#277BAE',
      accent: '#6D268C',
      accentShadow: '#530078',
      background: '#090C11',
      cardBackground: '#101318',
      text: '#ECFF9E',
      extra: '#6EE18F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oceanic Caldera',
    tags: ['curated'],
    light: {
      primary: '#E3B550',
      primaryShadow: '#B69140',
      accent: '#A24659',
      accentShadow: '#823847',
      background: '#EFEDEB',
      cardBackground: '#E3E1DF',
      text: '#09080B',
      extra: '#A7AFB5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E7C06A',
      primaryShadow: '#E3B550',
      accent: '#B06272',
      accentShadow: '#A24659',
      background: '#09080B',
      cardBackground: '#131215',
      text: '#EFEDEB',
      extra: '#B0B7BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Field',
    tags: ['bright', 'curated'],
    light: {
      primary: '#73AAC1',
      primaryShadow: '#5C889A',
      accent: '#5C6C6E',
      accentShadow: '#4A5658',
      background: '#EFEEE3',
      cardBackground: '#E3E2D8',
      text: '#1A1A1A',
      extra: '#D5D8CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#88B7CA',
      primaryShadow: '#73AAC1',
      accent: '#748284',
      accentShadow: '#5C6C6E',
      background: '#0B0D12',
      cardBackground: '#121419',
      text: '#EFEEE3',
      extra: '#D9DCD3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teal Nova',
    tags: ['cool', 'curated'],
    light: {
      primary: '#949400',
      primaryShadow: '#767600',
      accent: '#3333FF',
      accentShadow: '#2929CC',
      background: '#FAFFFA',
      cardBackground: '#EEF2EE',
      text: '#001E00',
      extra: '#C8C8C8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A4A426',
      primaryShadow: '#949400',
      accent: '#5252FF',
      accentShadow: '#3333FF',
      background: '#001E00',
      cardBackground: '#0A270A',
      text: '#FAFFFA',
      extra: '#CECECE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Glacier',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#2C2916',
      primaryShadow: '#232112',
      accent: '#575642',
      accentShadow: '#464535',
      background: '#E0E0DF',
      cardBackground: '#D7D7D6',
      text: '#1D1D1D',
      extra: '#424131',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4C4939',
      primaryShadow: '#2C2916',
      accent: '#706F5E',
      accentShadow: '#575642',
      background: '#1D1D1D',
      cardBackground: '#262626',
      text: '#F0F0F0',
      extra: '#555446',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Tranquil Rain',
    tags: ['curated'],
    light: {
      primary: '#DF6464',
      primaryShadow: '#B25050',
      accent: '#FF9F60',
      accentShadow: '#CC7F4D',
      background: '#E7E3AD',
      cardBackground: '#DBD8A4',
      text: '#251B2B',
      extra: '#251B2B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E47B7B',
      primaryShadow: '#DF6464',
      accent: '#FFAD78',
      accentShadow: '#FF9F60',
      background: '#251B2B',
      cardBackground: '#2E2433',
      text: '#E7E3AD',
      extra: '#3B3240',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Sunrise',
    tags: ['curated'],
    light: {
      primary: '#7B6640',
      primaryShadow: '#625233',
      accent: '#B28A62',
      accentShadow: '#8E6E4E',
      background: '#EFEDE2',
      cardBackground: '#E3E1D7',
      text: '#1A2425',
      extra: '#2A333C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8F7D5D',
      primaryShadow: '#7B6640',
      accent: '#BE9C7A',
      accentShadow: '#B28A62',
      background: '#1A2425',
      cardBackground: '#232D2E',
      text: '#EFEDE2',
      extra: '#3F4750',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Earth',
    tags: ['curated'],
    light: {
      primary: '#B0316E',
      primaryShadow: '#8D2758',
      accent: '#48B37C',
      accentShadow: '#3A8F63',
      background: '#FFFFF5',
      cardBackground: '#F2F2E9',
      text: '#18171A',
      extra: '#2E2F36',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BC5084',
      primaryShadow: '#B0316E',
      accent: '#63BE90',
      accentShadow: '#48B37C',
      background: '#18171A',
      cardBackground: '#212023',
      text: '#FFFFF5',
      extra: '#43444A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glow Mosaic',
    tags: ['curated'],
    light: {
      primary: '#FF7A00',
      primaryShadow: '#CC6200',
      accent: '#008A98',
      accentShadow: '#006E7A',
      background: '#FFFFFD',
      cardBackground: '#F2F2F0',
      text: '#000000',
      extra: '#F17BC9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF8E26',
      primaryShadow: '#FF7A00',
      accent: '#269CA7',
      accentShadow: '#008A98',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFD',
      extra: '#F288CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spark Aurora',
    tags: ['cool', 'curated'],
    light: {
      primary: '#2568A9',
      primaryShadow: '#1E5387',
      accent: '#4AADDE',
      accentShadow: '#3B8AB2',
      background: '#EBF5F7',
      cardBackground: '#DFE9EB',
      text: '#1A1B1C',
      extra: '#CACAE4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#467FB6',
      primaryShadow: '#2568A9',
      accent: '#65B9E3',
      accentShadow: '#4AADDE',
      background: '#1A1B1C',
      cardBackground: '#232425',
      text: '#EBF5F7',
      extra: '#CFCFE7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fierce Crystal',
    tags: ['curated'],
    light: {
      primary: '#F58000',
      primaryShadow: '#C46600',
      accent: '#0078FC',
      accentShadow: '#0060CA',
      background: '#FFFFFE',
      cardBackground: '#F2F2F1',
      text: '#000000',
      extra: '#CDCFCF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F79326',
      primaryShadow: '#F58000',
      accent: '#268CFC',
      accentShadow: '#0078FC',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFE',
      extra: '#D2D4D4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Hotspring',
    tags: ['warm', 'curated'],
    light: {
      primary: '#866D1D',
      primaryShadow: '#6B5717',
      accent: '#D27557',
      accentShadow: '#A85E46',
      background: '#FDF7D5',
      cardBackground: '#F0EBCA',
      text: '#27201C',
      extra: '#27201C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#98833F',
      primaryShadow: '#866D1D',
      accent: '#D98A70',
      accentShadow: '#D27557',
      background: '#27201C',
      cardBackground: '#302925',
      text: '#FDF7D5',
      extra: '#3D3633',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rocky Woodland',
    tags: ['warm', 'curated'],
    light: {
      primary: '#A27943',
      primaryShadow: '#826136',
      accent: '#ADA63E',
      accentShadow: '#8A8532',
      background: '#EDEFE2',
      cardBackground: '#E1E3D7',
      text: '#3B3243',
      extra: '#33402E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B08D5F',
      primaryShadow: '#A27943',
      accent: '#B9B35B',
      accentShadow: '#ADA63E',
      background: '#0D0C0E',
      cardBackground: '#141315',
      text: '#EDEFE2',
      extra: '#475343',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sapphire Petal',
    tags: ['curated'],
    light: {
      primary: '#FF384E',
      primaryShadow: '#CC2D3E',
      accent: '#FF7F38',
      accentShadow: '#CC662D',
      background: '#FDFFCD',
      cardBackground: '#F0F2C3',
      text: '#0E1132',
      extra: '#2E3948',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF5669',
      primaryShadow: '#FF384E',
      accent: '#FF9256',
      accentShadow: '#FF7F38',
      background: '#0E1132',
      cardBackground: '#181B3A',
      text: '#FDFFCD',
      extra: '#434D5A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cyan Woodland',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B16B33',
      primaryShadow: '#8E5629',
      accent: '#134332',
      accentShadow: '#0F3628',
      background: '#F7F9F0',
      cardBackground: '#EBEDE4',
      text: '#0C0C14',
      extra: '#C64D43',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BD8152',
      primaryShadow: '#B16B33',
      accent: '#365F51',
      accentShadow: '#134332',
      background: '#0C0C14',
      cardBackground: '#16161D',
      text: '#F7F9F0',
      extra: '#CC5F56',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Loft',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D98D3D',
      primaryShadow: '#AE7131',
      accent: '#784141',
      accentShadow: '#603434',
      background: '#E3E6C5',
      cardBackground: '#D8DBBB',
      text: '#38192D',
      extra: '#E3E6C5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DF9E5A',
      primaryShadow: '#D98D3D',
      accent: '#8C5E5E',
      accentShadow: '#784141',
      background: '#38192D',
      cardBackground: '#402235',
      text: '#E3E6C5',
      extra: '#E6E9CB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Gorge',
    tags: ['curated'],
    light: {
      primary: '#0A6874',
      primaryShadow: '#08535D',
      accent: '#031C29',
      accentShadow: '#021621',
      background: '#FBF7D1',
      cardBackground: '#EEEBC7',
      text: '#031C29',
      extra: '#88C9D1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2F7F89',
      primaryShadow: '#0A6874',
      accent: '#293E49',
      accentShadow: '#031C29',
      background: '#031C29',
      cardBackground: '#0D2532',
      text: '#FBF7D1',
      extra: '#94CED6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble Gallery',
    tags: ['curated'],
    light: {
      primary: '#00B5AE',
      primaryShadow: '#00918B',
      accent: '#017C83',
      accentShadow: '#016369',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#710042',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C0BA',
      primaryShadow: '#00B5AE',
      accent: '#279096',
      accentShadow: '#017C83',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#7F1A55',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Industrial Spectrum',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#E22929',
      primaryShadow: '#B52121',
      accent: '#800D28',
      accentShadow: '#660A20',
      background: '#E6DEDD',
      cardBackground: '#DBD3D2',
      text: '#090909',
      extra: '#E6DEDD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E64949',
      primaryShadow: '#E22929',
      accent: '#933148',
      accentShadow: '#800D28',
      background: '#090909',
      cardBackground: '#131313',
      text: '#E6DEDD',
      extra: '#E9E1E0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Crystal',
    tags: ['warm', 'curated'],
    light: {
      primary: '#F06923',
      primaryShadow: '#C0541C',
      accent: '#4D1F00',
      accentShadow: '#3E1900',
      background: '#F0E5E0',
      cardBackground: '#E6DCD7',
      text: '#4D1F00',
      extra: '#9B32AF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F28044',
      primaryShadow: '#F06923',
      accent: '#684126',
      accentShadow: '#4D1F00',
      background: '#0F0B0D',
      cardBackground: '#161214',
      text: '#BBBAB0',
      extra: '#A547B7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Spring Nave',
    tags: ['warm', 'curated'],
    light: {
      primary: '#765C42',
      primaryShadow: '#5E4A35',
      accent: '#52322F',
      accentShadow: '#422826',
      background: '#E6E4E2',
      cardBackground: '#DDDBD9',
      text: '#33302C',
      extra: '#33302C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8B745E',
      primaryShadow: '#765C42',
      accent: '#6C514E',
      accentShadow: '#52322F',
      background: '#0C0B0E',
      cardBackground: '#131215',
      text: '#C0C1C3',
      extra: '#474541',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Vision',
    tags: ['cool', 'curated'],
    light: {
      primary: '#00A8B1',
      primaryShadow: '#00868E',
      accent: '#37CFB7',
      accentShadow: '#2CA692',
      background: '#FEF3C0',
      cardBackground: '#F1E7B6',
      text: '#2323C7',
      extra: '#2323C7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B5BD',
      primaryShadow: '#00A8B1',
      accent: '#55D6C2',
      accentShadow: '#37CFB7',
      background: '#080D11',
      cardBackground: '#0F1418',
      text: '#FEF3C0',
      extra: '#3939CD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fog Desert',
    tags: ['cool', 'curated'],
    light: {
      primary: '#220022',
      primaryShadow: '#1B001B',
      accent: '#334455',
      accentShadow: '#293644',
      background: '#FFFFDD',
      cardBackground: '#F2F2D2',
      text: '#220022',
      extra: '#779988',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#432643',
      primaryShadow: '#220022',
      accent: '#52606F',
      accentShadow: '#334455',
      background: '#220022',
      cardBackground: '#2B0A2B',
      text: '#FFFFDD',
      extra: '#85A394',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Garden',
    tags: ['curated'],
    light: {
      primary: '#FF5300',
      primaryShadow: '#CC4200',
      accent: '#B74700',
      accentShadow: '#923900',
      background: '#FFFFC3',
      cardBackground: '#F2F2B9',
      text: '#000000',
      extra: '#172323',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF6D26',
      primaryShadow: '#FF5300',
      accent: '#C26326',
      accentShadow: '#B74700',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFC3',
      extra: '#2E3939',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eastern Loft',
    tags: ['curated'],
    light: {
      primary: '#AC4D00',
      primaryShadow: '#8A3E00',
      accent: '#FFA319',
      accentShadow: '#CC8214',
      background: '#DEFFB5',
      cardBackground: '#D3F2AC',
      text: '#3C1904',
      extra: '#312580',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B86826',
      primaryShadow: '#AC4D00',
      accent: '#FFB13C',
      accentShadow: '#FFA319',
      background: '#3C1904',
      cardBackground: '#44220E',
      text: '#DEFFB5',
      extra: '#463B8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen River',
    tags: ['curated'],
    light: {
      primary: '#1AB349',
      primaryShadow: '#158F3A',
      accent: '#D52941',
      accentShadow: '#AA2134',
      background: '#F9FAE8',
      cardBackground: '#EDEEDC',
      text: '#061227',
      extra: '#F3E2AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3CBE64',
      primaryShadow: '#1AB349',
      accent: '#DB495E',
      accentShadow: '#D52941',
      background: '#061227',
      cardBackground: '#101B30',
      text: '#F9FAE8',
      extra: '#F4E5B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Subtle Rapids',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#2196F3',
      primaryShadow: '#1A78C2',
      accent: '#175F8F',
      accentShadow: '#124C72',
      background: '#DFE9F0',
      cardBackground: '#D6E0E6',
      text: '#05022E',
      extra: '#7AC5FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#42A6F5',
      primaryShadow: '#2196F3',
      accent: '#3A77A0',
      accentShadow: '#175F8F',
      background: '#05022E',
      cardBackground: '#0F0C36',
      text: '#7AC5FF',
      extra: '#87CBFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ebony Shore',
    tags: ['warm', 'curated'],
    light: {
      primary: '#EB754D',
      primaryShadow: '#BC5E3E',
      accent: '#822D30',
      accentShadow: '#682426',
      background: '#F0E6E3',
      cardBackground: '#E6DDDA',
      text: '#08050C',
      extra: '#F8B581',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EE8A68',
      primaryShadow: '#EB754D',
      accent: '#954D4F',
      accentShadow: '#822D30',
      background: '#08050C',
      cardBackground: '#120F16',
      text: '#F8B581',
      extra: '#F9BC8E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sandy Essence',
    tags: ['cool', 'curated'],
    light: {
      primary: '#3D51B3',
      primaryShadow: '#31418F',
      accent: '#39155B',
      accentShadow: '#2E1149',
      background: '#FAFCE9',
      cardBackground: '#EEEFDD',
      text: '#39155B',
      extra: '#D4EDDA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5A6BBE',
      primaryShadow: '#3D51B3',
      accent: '#573874',
      accentShadow: '#39155B',
      background: '#0A0A11',
      cardBackground: '#111118',
      text: '#FAFCE9',
      extra: '#D8EFDE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neo Reef',
    tags: ['curated'],
    light: {
      primary: '#C21FFF',
      primaryShadow: '#9B19CC',
      accent: '#C11616',
      accentShadow: '#9A1212',
      background: '#FFEAE8',
      cardBackground: '#F2DEDC',
      text: '#13161D',
      extra: '#313345',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CB41FF',
      primaryShadow: '#C21FFF',
      accent: '#CA3939',
      accentShadow: '#C11616',
      background: '#13161D',
      cardBackground: '#1C1F26',
      text: '#FFEAE8',
      extra: '#464758',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rustic Keep',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CB2233',
      primaryShadow: '#A21B29',
      accent: '#E1674C',
      accentShadow: '#B4523D',
      background: '#F3DEBA',
      cardBackground: '#E7D3B1',
      text: '#420E53',
      extra: '#F3DEBA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D34352',
      primaryShadow: '#CB2233',
      accent: '#E67E67',
      accentShadow: '#E1674C',
      background: '#420E53',
      cardBackground: '#4A185A',
      text: '#F3DEBA',
      extra: '#F4E1C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Desert Vineyard',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D14787',
      primaryShadow: '#A7396C',
      accent: '#969E4C',
      accentShadow: '#787E3D',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#24201F',
      extra: '#24201F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D86399',
      primaryShadow: '#D14787',
      accent: '#A6AD67',
      accentShadow: '#969E4C',
      background: '#24201F',
      cardBackground: '#2D2928',
      text: '#FFFFFF',
      extra: '#3A3635',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Serene Grove',
    tags: ['cool', 'curated'],
    light: {
      primary: '#2C98BB',
      primaryShadow: '#237A96',
      accent: '#1D8353',
      accentShadow: '#176942',
      background: '#EDF3FF',
      cardBackground: '#E1E7F2',
      text: '#0F101A',
      extra: '#363C57',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4CA7C5',
      primaryShadow: '#2C98BB',
      accent: '#3F966D',
      accentShadow: '#1D8353',
      background: '#0F101A',
      cardBackground: '#191A23',
      text: '#EDF3FF',
      extra: '#4A5068',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusky Study',
    tags: ['curated'],
    light: {
      primary: '#10BDC6',
      primaryShadow: '#0D979E',
      accent: '#7441AE',
      accentShadow: '#5D348B',
      background: '#DEECED',
      cardBackground: '#D5E3E4',
      text: '#394072',
      extra: '#B1E2E7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#34C7CF',
      primaryShadow: '#10BDC6',
      accent: '#895EBA',
      accentShadow: '#7441AE',
      background: '#080E12',
      cardBackground: '#0F1519',
      text: '#B1E2E7',
      extra: '#B9E5E9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Snowy Mountain',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#7850B3',
      primaryShadow: '#60408F',
      accent: '#281D73',
      accentShadow: '#20175C',
      background: '#E6E3EB',
      cardBackground: '#DDDAE2',
      text: '#281D73',
      extra: '#4F388C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8C6ABE',
      primaryShadow: '#7850B3',
      accent: '#483F88',
      accentShadow: '#281D73',
      background: '#0C0A11',
      cardBackground: '#131118',
      text: '#F0F0F0',
      extra: '#614C98',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pure Citadel',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#71A8C6',
      primaryShadow: '#5A869E',
      accent: '#9EDEB5',
      accentShadow: '#7EB291',
      background: '#F7E2E3',
      cardBackground: '#EBD7D8',
      text: '#1A1A1A',
      extra: '#6FC4B2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#86B5CF',
      primaryShadow: '#71A8C6',
      accent: '#ADE3C0',
      accentShadow: '#9EDEB5',
      background: '#0B0D12',
      cardBackground: '#121419',
      text: '#F7E2E3',
      extra: '#7DCABA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mediterranean Tower',
    tags: ['curated'],
    light: {
      primary: '#98788E',
      primaryShadow: '#7A6072',
      accent: '#5A4A45',
      accentShadow: '#483B37',
      background: '#E9E6E8',
      cardBackground: '#E0DDDF',
      text: '#20211C',
      extra: '#343A36',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A78C9F',
      primaryShadow: '#98788E',
      accent: '#736561',
      accentShadow: '#5A4A45',
      background: '#20211C',
      cardBackground: '#292A25',
      text: '#BABABA',
      extra: '#484E4A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Wave',
    tags: ['dark', 'curated'],
    light: {
      primary: '#B35F47',
      primaryShadow: '#8F4C39',
      accent: '#4D0F32',
      accentShadow: '#3E0C28',
      background: '#EBE4E2',
      cardBackground: '#E2DBD9',
      text: '#080826',
      extra: '#E3DE88',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BE7763',
      primaryShadow: '#B35F47',
      accent: '#683351',
      accentShadow: '#4D0F32',
      background: '#080826',
      cardBackground: '#12122F',
      text: '#E3DE88',
      extra: '#E6E194',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Gentle Studio',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B1313A',
      primaryShadow: '#8E272E',
      accent: '#BC517C',
      accentShadow: '#964163',
      background: '#FFDEE2',
      cardBackground: '#F2D3D7',
      text: '#141D1D',
      extra: '#DA91B5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BD5058',
      primaryShadow: '#B1313A',
      accent: '#C66B90',
      accentShadow: '#BC517C',
      background: '#141D1D',
      cardBackground: '#1D2626',
      text: '#FFDEE2',
      extra: '#DE9CBC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Horizon',
    tags: ['curated'],
    light: {
      primary: '#DE9139',
      primaryShadow: '#B2742E',
      accent: '#39A441',
      accentShadow: '#2E8334',
      background: '#E0E8CD',
      cardBackground: '#D5DCC3',
      text: '#272135',
      extra: '#272135',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E3A257',
      primaryShadow: '#DE9139',
      accent: '#57B25E',
      accentShadow: '#39A441',
      background: '#272135',
      cardBackground: '#302A3D',
      text: '#E0E8CD',
      extra: '#3D3749',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Clear Canvas',
    tags: ['curated'],
    light: {
      primary: '#5C007E',
      primaryShadow: '#4A0065',
      accent: '#48CEDF',
      accentShadow: '#3AA5B2',
      background: '#E5E695',
      cardBackground: '#DADB8E',
      text: '#5C007E',
      extra: '#FE6ECD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#742691',
      primaryShadow: '#5C007E',
      accent: '#63D5E4',
      accentShadow: '#48CEDF',
      background: '#0B0810',
      cardBackground: '#120F17',
      text: '#E5E695',
      extra: '#FE7DD2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Geyser',
    tags: ['curated'],
    light: {
      primary: '#CD6627',
      primaryShadow: '#A4521F',
      accent: '#DBCE2D',
      accentShadow: '#AFA524',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#232323',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D57D47',
      primaryShadow: '#CD6627',
      accent: '#E0D54D',
      accentShadow: '#DBCE2D',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#393939',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lavender Sanctuary',
    tags: ['warm', 'curated'],
    light: {
      primary: '#18B3F5',
      primaryShadow: '#138FC4',
      accent: '#1875CC',
      accentShadow: '#135EA3',
      background: '#F5F5F7',
      cardBackground: '#E9E9EB',
      text: '#1E1526',
      extra: '#1E1526',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3BBEF7',
      primaryShadow: '#18B3F5',
      accent: '#3B8AD4',
      accentShadow: '#1875CC',
      background: '#1E1526',
      cardBackground: '#271E2F',
      text: '#F5F5F7',
      extra: '#352C3C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Lava',
    tags: ['curated'],
    light: {
      primary: '#149D63',
      primaryShadow: '#107E4F',
      accent: '#E8931F',
      accentShadow: '#BA7619',
      background: '#F4FBF8',
      cardBackground: '#E8EEEC',
      text: '#111426',
      extra: '#373D5F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#37AC7A',
      primaryShadow: '#149D63',
      accent: '#EBA341',
      accentShadow: '#E8931F',
      background: '#111426',
      cardBackground: '#1B1D2F',
      text: '#F4FBF8',
      extra: '#4B506F',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Neon Reef',
    tags: ['cool', 'curated'],
    light: {
      primary: '#100029',
      primaryShadow: '#0D0021',
      accent: '#8F56B3',
      accentShadow: '#72458F',
      background: '#F5F5F5',
      cardBackground: '#E9E9E9',
      text: '#000000',
      extra: '#361D23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#342649',
      primaryShadow: '#100029',
      accent: '#A06FBE',
      accentShadow: '#8F56B3',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F5F5F5',
      extra: '#4A3439',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Pastel Glacier',
    tags: ['warm', 'curated'],
    light: {
      primary: '#23AF00',
      primaryShadow: '#1C8C00',
      accent: '#FC9611',
      accentShadow: '#CA780E',
      background: '#F4DFCB',
      cardBackground: '#E8D4C1',
      text: '#280E16',
      extra: '#69D72C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#44BB26',
      primaryShadow: '#23AF00',
      accent: '#FCA635',
      accentShadow: '#FC9611',
      background: '#280E16',
      cardBackground: '#31181F',
      text: '#F4DFCB',
      extra: '#78DB41',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Barren Study',
    tags: ['warm', 'curated'],
    light: {
      primary: '#9A747F',
      primaryShadow: '#7B5D66',
      accent: '#4C4139',
      accentShadow: '#3D342E',
      background: '#E5DACD',
      cardBackground: '#DACFC3',
      text: '#4C4139',
      extra: '#CCB4A4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A98992',
      primaryShadow: '#9A747F',
      accent: '#675E57',
      accentShadow: '#4C4139',
      background: '#0D0B10',
      cardBackground: '#141217',
      text: '#E5DACD',
      extra: '#D1BCAD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Gorge',
    tags: ['curated'],
    light: {
      primary: '#00E436',
      primaryShadow: '#00B62B',
      accent: '#089848',
      accentShadow: '#067A3A',
      background: '#FFF1E8',
      cardBackground: '#F2E5DC',
      text: '#190025',
      extra: '#311A30',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26E854',
      primaryShadow: '#00E436',
      accent: '#2DA763',
      accentShadow: '#089848',
      background: '#190025',
      cardBackground: '#220A2E',
      text: '#FFF1E8',
      extra: '#463145',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mist Garden',
    tags: ['curated'],
    light: {
      primary: '#998A3D',
      primaryShadow: '#7A6E31',
      accent: '#995C3D',
      accentShadow: '#7A4A31',
      background: '#E6FFCC',
      cardBackground: '#DBF2C2',
      text: '#190C1F',
      extra: '#B1CC7A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A89C5A',
      primaryShadow: '#998A3D',
      accent: '#A8745A',
      accentShadow: '#995C3D',
      background: '#190C1F',
      cardBackground: '#221628',
      text: '#E6FFCC',
      extra: '#B9D187',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ephemeral Meadow',
    tags: ['curated'],
    light: {
      primary: '#00A7DF',
      primaryShadow: '#0086B2',
      accent: '#098F26',
      accentShadow: '#07721E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#22212E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B4E4',
      primaryShadow: '#00A7DF',
      accent: '#2EA047',
      accentShadow: '#098F26',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#383743',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mediterranean Promenade',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#D84444',
      primaryShadow: '#AD3636',
      accent: '#A23039',
      accentShadow: '#82262E',
      background: '#EEE2E2',
      cardBackground: '#E4D9D9',
      text: '#20181C',
      extra: '#20181C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DE6060',
      primaryShadow: '#D84444',
      accent: '#B04F57',
      accentShadow: '#A23039',
      background: '#20181C',
      cardBackground: '#292125',
      text: '#F0F0F0',
      extra: '#362F33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cotton Turret',
    tags: ['dark', 'cool', 'curated'],
    light: {
      primary: '#407C84',
      primaryShadow: '#33636A',
      accent: '#183A42',
      accentShadow: '#132E35',
      background: '#E2E7E7',
      cardBackground: '#D9DEDE',
      text: '#101E2B',
      extra: '#101E2B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5D9096',
      primaryShadow: '#407C84',
      accent: '#3B585E',
      accentShadow: '#183A42',
      background: '#101E2B',
      cardBackground: '#1A2733',
      text: '#F0F0F0',
      extra: '#283540',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vibrant Aura',
    tags: ['curated'],
    light: {
      primary: '#2EBE00',
      primaryShadow: '#259800',
      accent: '#FFD300',
      accentShadow: '#CCA900',
      background: '#FFFDDC',
      cardBackground: '#F2F0D1',
      text: '#192662',
      extra: '#C7A7CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4DC826',
      primaryShadow: '#2EBE00',
      accent: '#FFDA26',
      accentShadow: '#FFD300',
      background: '#090E0C',
      cardBackground: '#101513',
      text: '#FFFDDC',
      extra: '#CDB0D3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Volcano',
    tags: ['warm', 'curated'],
    light: {
      primary: '#200816',
      primaryShadow: '#1A0612',
      accent: '#683A34',
      accentShadow: '#532E2A',
      background: '#F8E6D0',
      cardBackground: '#ECDBC6',
      text: '#200816',
      extra: '#C08E70',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#412D39',
      primaryShadow: '#200816',
      accent: '#7F5852',
      accentShadow: '#683A34',
      background: '#200816',
      cardBackground: '#29121F',
      text: '#F8E6D0',
      extra: '#C6997E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Teal Earth',
    tags: ['warm', 'curated'],
    light: {
      primary: '#1D001C',
      primaryShadow: '#170016',
      accent: '#E06B88',
      accentShadow: '#B3566D',
      background: '#FFFFE3',
      cardBackground: '#F2F2D8',
      text: '#1D001C',
      extra: '#E28B7A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3F263E',
      primaryShadow: '#1D001C',
      accent: '#E5819A',
      accentShadow: '#E06B88',
      background: '#1D001C',
      cardBackground: '#260A25',
      text: '#FFFFE3',
      extra: '#E59787',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Evening Spire',
    tags: ['curated'],
    light: {
      primary: '#C54D71',
      primaryShadow: '#9E3E5A',
      accent: '#6D1945',
      accentShadow: '#571437',
      background: '#F7E9C4',
      cardBackground: '#EBDDBA',
      text: '#3B243E',
      extra: '#3F375D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CE6886',
      primaryShadow: '#C54D71',
      accent: '#833C61',
      accentShadow: '#6D1945',
      background: '#0E0A0F',
      cardBackground: '#151116',
      text: '#F7E9C4',
      extra: '#524B6D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Spirit',
    tags: ['curated'],
    light: {
      primary: '#E69434',
      primaryShadow: '#B8762A',
      accent: '#E15048',
      accentShadow: '#B4403A',
      background: '#E8E3BE',
      cardBackground: '#DCD8B5',
      text: '#25222B',
      extra: '#25222B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EAA452',
      primaryShadow: '#E69434',
      accent: '#E66A63',
      accentShadow: '#E15048',
      background: '#25222B',
      cardBackground: '#2E2B33',
      text: '#E8E3BE',
      extra: '#3B3840',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ruby Pack',
    tags: ['warm', 'curated'],
    light: {
      primary: '#42630D',
      primaryShadow: '#354F0A',
      accent: '#959446',
      accentShadow: '#777638',
      background: '#FCEF9C',
      cardBackground: '#EFE394',
      text: '#14241B',
      extra: '#ED9F7D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E7A31',
      primaryShadow: '#42630D',
      accent: '#A5A462',
      accentShadow: '#959446',
      background: '#14241B',
      cardBackground: '#1D2D24',
      text: '#FCEF9C',
      extra: '#EFA98A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Glow Waterfall',
    tags: ['warm', 'curated'],
    light: {
      primary: '#420B4D',
      primaryShadow: '#35093E',
      accent: '#BE9B5D',
      accentShadow: '#987C4A',
      background: '#F2FFFF',
      cardBackground: '#E6F2F2',
      text: '#27072D',
      extra: '#592940',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5E3068',
      primaryShadow: '#420B4D',
      accent: '#C8AA75',
      accentShadow: '#BE9B5D',
      background: '#27072D',
      cardBackground: '#301135',
      text: '#F2FFFF',
      extra: '#6A3E53',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Lake',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#AB94EB',
      primaryShadow: '#8976BC',
      accent: '#A7CFEB',
      accentShadow: '#86A6BC',
      background: '#E9D2F4',
      cardBackground: '#DDC8E8',
      text: '#1A1A1A',
      extra: '#E9D2F4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B8A4EE',
      primaryShadow: '#AB94EB',
      accent: '#B4D6EE',
      accentShadow: '#A7CFEB',
      background: '#0D0C13',
      cardBackground: '#14131A',
      text: '#E9D2F4',
      extra: '#EBD7F5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rust Obelisk',
    tags: ['bright', 'curated'],
    light: {
      primary: '#4997D6',
      primaryShadow: '#3A79AB',
      accent: '#F2955E',
      accentShadow: '#C2774B',
      background: '#FCFFC7',
      cardBackground: '#EFF2BD',
      text: '#463E59',
      extra: '#58578C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#64A7DC',
      primaryShadow: '#4997D6',
      accent: '#F4A576',
      accentShadow: '#F2955E',
      background: '#0A0D12',
      cardBackground: '#111419',
      text: '#FCFFC7',
      extra: '#696898',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Stormy Arcade',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#9492D0',
      primaryShadow: '#7675A6',
      accent: '#87CBC5',
      accentShadow: '#6CA29E',
      background: '#DDFEFF',
      cardBackground: '#D2F1F2',
      text: '#1A1A1A',
      extra: '#E9CCFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#A4A2D7',
      primaryShadow: '#9492D0',
      accent: '#99D3CE',
      accentShadow: '#87CBC5',
      background: '#0C0C12',
      cardBackground: '#131319',
      text: '#DDFEFF',
      extra: '#EBD1FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Marble Rain',
    tags: ['curated'],
    light: {
      primary: '#F94A22',
      primaryShadow: '#C73B1B',
      accent: '#F58825',
      accentShadow: '#C46D1E',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#1E1E1E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FA6543',
      primaryShadow: '#F94A22',
      accent: '#F79A46',
      accentShadow: '#F58825',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#353535',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bright Bastion',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CB6A06',
      primaryShadow: '#A25505',
      accent: '#F00065',
      accentShadow: '#C00051',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#2B2821',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D3802B',
      primaryShadow: '#CB6A06',
      accent: '#F2267C',
      accentShadow: '#F00065',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#403E37',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Cliff',
    tags: ['curated'],
    light: {
      primary: '#C29F2D',
      primaryShadow: '#9B7F24',
      accent: '#AB6E48',
      accentShadow: '#89583A',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#13131D',
      extra: '#BA783D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CBAD4D',
      primaryShadow: '#C29F2D',
      accent: '#B88463',
      accentShadow: '#AB6E48',
      background: '#13131D',
      cardBackground: '#1C1C26',
      text: '#FFFFFF',
      extra: '#C18650',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Bright Glade',
    tags: ['curated'],
    light: {
      primary: '#2B81DA',
      primaryShadow: '#2267AE',
      accent: '#C02243',
      accentShadow: '#9A1B36',
      background: '#EFECF6',
      cardBackground: '#E3E0EA',
      text: '#340C88',
      extra: '#D0C9DD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4B94E0',
      primaryShadow: '#2B81DA',
      accent: '#C9435F',
      accentShadow: '#C02243',
      background: '#340C88',
      cardBackground: '#3C168D',
      text: '#EFECF6',
      extra: '#D5CEE0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Alpine Rain',
    tags: ['curated'],
    light: {
      primary: '#F86C27',
      primaryShadow: '#C6561F',
      accent: '#D9472B',
      accentShadow: '#AE3922',
      background: '#F5FAFF',
      cardBackground: '#E9EEF2',
      text: '#1D1535',
      extra: '#1D1535',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#F98247',
      primaryShadow: '#F86C27',
      accent: '#DF634B',
      accentShadow: '#D9472B',
      background: '#1D1535',
      cardBackground: '#261E3D',
      text: '#F5FAFF',
      extra: '#342C49',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Muddy Loft',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D66218',
      primaryShadow: '#AB4E13',
      accent: '#DA2121',
      accentShadow: '#AE1A1A',
      background: '#FFFDFD',
      cardBackground: '#F2F0F0',
      text: '#11011B',
      extra: '#310D49',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DC7A3B',
      primaryShadow: '#D66218',
      accent: '#E04242',
      accentShadow: '#DA2121',
      background: '#11011B',
      cardBackground: '#1B0B24',
      text: '#FFFDFD',
      extra: '#46255B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Mellow Branch',
    tags: ['curated'],
    light: {
      primary: '#04B692',
      primaryShadow: '#039275',
      accent: '#0DE98E',
      accentShadow: '#0ABA72',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000037',
      extra: '#51062C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2AC1A2',
      primaryShadow: '#04B692',
      accent: '#31EC9F',
      accentShadow: '#0DE98E',
      background: '#000037',
      cardBackground: '#0A0A3F',
      text: '#FFFFFF',
      extra: '#621F41',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Oceanic Glade',
    tags: ['warm', 'curated'],
    light: {
      primary: '#68991F',
      primaryShadow: '#537A19',
      accent: '#356614',
      accentShadow: '#2A5210',
      background: '#F1F2E6',
      cardBackground: '#E5E6DB',
      text: '#040D04',
      extra: '#11260B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7FA841',
      primaryShadow: '#68991F',
      accent: '#537D37',
      accentShadow: '#356614',
      background: '#040D04',
      cardBackground: '#0E170E',
      text: '#F1F2E6',
      extra: '#293C23',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ivory Citadel',
    tags: ['curated'],
    light: {
      primary: '#2BA1C3',
      primaryShadow: '#22819C',
      accent: '#15709B',
      accentShadow: '#115A7C',
      background: '#E2E7F9',
      cardBackground: '#D7DBED',
      text: '#1D0D0D',
      extra: '#132242',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4BAFCC',
      primaryShadow: '#2BA1C3',
      accent: '#3885AA',
      accentShadow: '#15709B',
      background: '#1D0D0D',
      cardBackground: '#261717',
      text: '#E2E7F9',
      extra: '#2B3855',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Evening Summit',
    tags: ['cool', 'curated'],
    light: {
      primary: '#5A9294',
      primaryShadow: '#487576',
      accent: '#364F59',
      accentShadow: '#2B3F47',
      background: '#E4E8E9',
      cardBackground: '#DBDFE0',
      text: '#191D24',
      extra: '#191D24',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#73A2A4',
      primaryShadow: '#5A9294',
      accent: '#546972',
      accentShadow: '#364F59',
      background: '#191D24',
      cardBackground: '#22262D',
      text: '#B5E6D3',
      extra: '#30343A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ember Steppe',
    tags: ['curated'],
    light: {
      primary: '#B97649',
      primaryShadow: '#945E3A',
      accent: '#782C90',
      accentShadow: '#602373',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#160D16',
      extra: '#D7D5CF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C48B64',
      primaryShadow: '#B97649',
      accent: '#8C4CA1',
      accentShadow: '#782C90',
      background: '#160D16',
      cardBackground: '#1F171F',
      text: '#FFFFFF',
      extra: '#DBD9D4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vintage Thicket',
    tags: ['dark', 'curated'],
    light: {
      primary: '#B3242D',
      primaryShadow: '#8F1D24',
      accent: '#F26174',
      accentShadow: '#C24E5D',
      background: '#EBE0E0',
      cardBackground: '#E2D7D7',
      text: '#0A0A1A',
      extra: '#4D1313',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BE454D',
      primaryShadow: '#B3242D',
      accent: '#F47989',
      accentShadow: '#F26174',
      background: '#0A0A1A',
      cardBackground: '#141423',
      text: '#F0F0F0',
      extra: '#5F2B2B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Solar Wind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D96330',
      primaryShadow: '#AE4F26',
      accent: '#B6351B',
      accentShadow: '#922A16',
      background: '#F7E8B5',
      cardBackground: '#EBDCAC',
      text: '#630D48',
      extra: '#F0C884',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DF7A4F',
      primaryShadow: '#D96330',
      accent: '#C1533D',
      accentShadow: '#B6351B',
      background: '#0F0B0D',
      cardBackground: '#161214',
      text: '#F7E8B5',
      extra: '#F2CE90',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Temple',
    tags: ['warm', 'curated'],
    light: {
      primary: '#DA8332',
      primaryShadow: '#AE6928',
      accent: '#C1492C',
      accentShadow: '#9A3A23',
      background: '#EBDDDA',
      cardBackground: '#DFD2CF',
      text: '#000000',
      extra: '#4A5933',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E09651',
      primaryShadow: '#DA8332',
      accent: '#CA644C',
      accentShadow: '#C1492C',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#EBDDDA',
      extra: '#5C6A47',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Deep Nebula',
    tags: ['warm', 'curated'],
    light: {
      primary: '#C05330',
      primaryShadow: '#9A4226',
      accent: '#9C1722',
      accentShadow: '#7D121B',
      background: '#FFF7E3',
      cardBackground: '#F2EBD8',
      text: '#2E0D05',
      extra: '#E17970',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C96D4F',
      primaryShadow: '#C05330',
      accent: '#AB3A43',
      accentShadow: '#9C1722',
      background: '#2E0D05',
      cardBackground: '#36170F',
      text: '#FFF7E3',
      extra: '#E4867E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Seed',
    tags: ['curated'],
    light: {
      primary: '#00CF2D',
      primaryShadow: '#00A624',
      accent: '#006EC2',
      accentShadow: '#00589B',
      background: '#EBFFD4',
      cardBackground: '#DFF2C9',
      text: '#450073',
      extra: '#FF306C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26D64D',
      primaryShadow: '#00CF2D',
      accent: '#2684CB',
      accentShadow: '#006EC2',
      background: '#450073',
      cardBackground: '#4C0A79',
      text: '#EBFFD4',
      extra: '#FF457B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dusty Atrium',
    tags: ['curated'],
    light: {
      primary: '#BD2F40',
      primaryShadow: '#972633',
      accent: '#6CA84D',
      accentShadow: '#56863E',
      background: '#F5FFE0',
      cardBackground: '#E9F2D5',
      text: '#1B1926',
      extra: '#1B1926',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C74E5D',
      primaryShadow: '#BD2F40',
      accent: '#82B568',
      accentShadow: '#6CA84D',
      background: '#1B1926',
      cardBackground: '#24222F',
      text: '#F5FFE0',
      extra: '#32303C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Satin Cloud',
    tags: ['dark', 'curated'],
    light: {
      primary: '#5F6CAD',
      primaryShadow: '#4C568A',
      accent: '#8195DE',
      accentShadow: '#6777B2',
      background: '#FAF7D5',
      cardBackground: '#EEEBCA',
      text: '#020304',
      extra: '#ADC2FB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7782B9',
      primaryShadow: '#5F6CAD',
      accent: '#94A5E3',
      accentShadow: '#8195DE',
      background: '#020304',
      cardBackground: '#0C0D0E',
      text: '#FAF7D5',
      extra: '#B5C8FB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cyan Steppe',
    tags: ['dark', 'warm', 'curated'],
    light: {
      primary: '#C33149',
      primaryShadow: '#9C273A',
      accent: '#DEA257',
      accentShadow: '#B28246',
      background: '#ECE1E3',
      cardBackground: '#E3D8DA',
      text: '#382637',
      extra: '#382637',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CC5064',
      primaryShadow: '#C33149',
      accent: '#E3B070',
      accentShadow: '#DEA257',
      background: '#0E090E',
      cardBackground: '#151015',
      text: '#F0F0F0',
      extra: '#4C3C4B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cyan Clearing',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#6EFFBC',
      primaryShadow: '#58CC96',
      accent: '#5E315B',
      accentShadow: '#4B2749',
      background: '#FFF7DD',
      cardBackground: '#F2EBD2',
      text: '#5E315B',
      extra: '#B171AE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#84FFC6',
      primaryShadow: '#6EFFBC',
      accent: '#765074',
      accentShadow: '#5E315B',
      background: '#0B1012',
      cardBackground: '#121719',
      text: '#FFF7DD',
      extra: '#B97FB6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Sheer Ridge',
    tags: ['curated'],
    light: {
      primary: '#B73A20',
      primaryShadow: '#922E1A',
      accent: '#BF843A',
      accentShadow: '#996A2E',
      background: '#F8F6CE',
      cardBackground: '#ECEAC4',
      text: '#121224',
      extra: '#E57193',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#C25841',
      primaryShadow: '#B73A20',
      accent: '#C99658',
      accentShadow: '#BF843A',
      background: '#121224',
      cardBackground: '#1B1B2D',
      text: '#F8F6CE',
      extra: '#E87F9E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Natural Wave',
    tags: ['bright', 'warm', 'curated'],
    light: {
      primary: '#C15C80',
      primaryShadow: '#9A4A66',
      accent: '#804776',
      accentShadow: '#66395E',
      background: '#EDEDDE',
      cardBackground: '#E1E1D3',
      text: '#1A1A1A',
      extra: '#C7C6C1',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#CA7493',
      primaryShadow: '#C15C80',
      accent: '#93638B',
      accentShadow: '#804776',
      background: '#0E0B10',
      cardBackground: '#151217',
      text: '#EDEDDE',
      extra: '#CDCCC7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen Summit',
    tags: ['warm', 'curated'],
    light: {
      primary: '#625D10',
      primaryShadow: '#4E4A0D',
      accent: '#291C08',
      accentShadow: '#211606',
      background: '#E5E4DE',
      cardBackground: '#DCDBD5',
      text: '#181C08',
      extra: '#DECEB4',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7A7534',
      primaryShadow: '#625D10',
      accent: '#493E2D',
      accentShadow: '#291C08',
      background: '#181C08',
      cardBackground: '#212512',
      text: '#DECEB4',
      extra: '#E1D3BC',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Dynamic Prairie',
    tags: ['warm', 'curated'],
    light: {
      primary: '#E08E49',
      primaryShadow: '#B3723A',
      accent: '#842243',
      accentShadow: '#6A1B36',
      background: '#FFEE96',
      cardBackground: '#F2E28F',
      text: '#2C1415',
      extra: '#2C1415',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#E59F64',
      primaryShadow: '#E08E49',
      accent: '#96435F',
      accentShadow: '#842243',
      background: '#2C1415',
      cardBackground: '#341D1E',
      text: '#FFEE96',
      extra: '#412C2C',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Lush Oasis',
    tags: ['warm', 'curated'],
    light: {
      primary: '#B4401C',
      primaryShadow: '#903316',
      accent: '#CD7C30',
      accentShadow: '#A46326',
      background: '#EBE2DF',
      cardBackground: '#E2D9D6',
      text: '#610316',
      extra: '#67B5FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BF5D3E',
      primaryShadow: '#B4401C',
      accent: '#D5904F',
      accentShadow: '#CD7C30',
      background: '#610316',
      cardBackground: '#670D1F',
      text: '#D2D5C1',
      extra: '#76BCFF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Cool Earth',
    tags: ['curated'],
    light: {
      primary: '#2772D8',
      primaryShadow: '#1F5BAD',
      accent: '#51C0D4',
      accentShadow: '#419AAA',
      background: '#E0E6EE',
      cardBackground: '#D7DDE4',
      text: '#372535',
      extra: '#372535',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4787DE',
      primaryShadow: '#2772D8',
      accent: '#6BC9DA',
      accentShadow: '#51C0D4',
      background: '#090B12',
      cardBackground: '#101219',
      text: '#CAC373',
      extra: '#4B3B49',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Crimson Moon',
    tags: ['warm', 'curated'],
    light: {
      primary: '#2F7B9F',
      primaryShadow: '#26627F',
      accent: '#4999BE',
      accentShadow: '#3A7A98',
      background: '#E1E7EA',
      cardBackground: '#D8DEE1',
      text: '#201817',
      extra: '#73AB63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#4E8FAD',
      primaryShadow: '#2F7B9F',
      accent: '#64A8C8',
      accentShadow: '#4999BE',
      background: '#201817',
      cardBackground: '#292120',
      text: '#F3C1DA',
      extra: '#81B373',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Arid River',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AC7437',
      primaryShadow: '#8A5D2C',
      accent: '#532B08',
      accentShadow: '#422206',
      background: '#EBE6E1',
      cardBackground: '#E2DDD8',
      text: '#432408',
      extra: '#EFD4B6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B88955',
      primaryShadow: '#AC7437',
      accent: '#6D4B2D',
      accentShadow: '#532B08',
      background: '#0D0B0E',
      cardBackground: '#141215',
      text: '#EFD4B6',
      extra: '#F1D8BD',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fleeting Blizzard',
    tags: ['curated'],
    light: {
      primary: '#1094DE',
      primaryShadow: '#0D76B2',
      accent: '#F77321',
      accentShadow: '#C65C1A',
      background: '#F7FFF7',
      cardBackground: '#EBF2EB',
      text: '#080810',
      extra: '#292139',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#34A4E3',
      primaryShadow: '#1094DE',
      accent: '#F88842',
      accentShadow: '#F77321',
      background: '#080810',
      cardBackground: '#12121A',
      text: '#F7FFF7',
      extra: '#3E374D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rich Aura',
    tags: ['curated'],
    light: {
      primary: '#00AAAA',
      primaryShadow: '#008888',
      accent: '#A8A800',
      accentShadow: '#868600',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#5555FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26B7B7',
      primaryShadow: '#00AAAA',
      accent: '#B5B526',
      accentShadow: '#A8A800',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#6666FF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Orchid Mountain',
    tags: ['curated'],
    light: {
      primary: '#0F9E68',
      primaryShadow: '#0C7E53',
      accent: '#1BC485',
      accentShadow: '#169D6A',
      background: '#CFF9E7',
      cardBackground: '#C5EDDB',
      text: '#272032',
      extra: '#B5C0E8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#33AD7F',
      primaryShadow: '#0F9E68',
      accent: '#3DCD97',
      accentShadow: '#1BC485',
      background: '#272032',
      cardBackground: '#30293A',
      text: '#CFF9E7',
      extra: '#BCC6EA',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Shimmer Stone',
    tags: ['warm', 'curated'],
    light: {
      primary: '#149298',
      primaryShadow: '#10757A',
      accent: '#10625F',
      accentShadow: '#0D4E4C',
      background: '#FBFEDF',
      cardBackground: '#EEF1D4',
      text: '#000000',
      extra: '#BA504E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#37A2A7',
      primaryShadow: '#149298',
      accent: '#347A77',
      accentShadow: '#10625F',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FBFEDF',
      extra: '#C16260',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Linen Storm',
    tags: ['curated'],
    light: {
      primary: '#FA2C7E',
      primaryShadow: '#C82365',
      accent: '#21DE8A',
      accentShadow: '#1AB26E',
      background: '#CCFEFF',
      cardBackground: '#C2F1F2',
      text: '#000000',
      extra: '#661246',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FB4C91',
      primaryShadow: '#FA2C7E',
      accent: '#42E39C',
      accentShadow: '#21DE8A',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#CCFEFF',
      extra: '#752A59',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Noon Seed',
    tags: ['cool', 'curated'],
    light: {
      primary: '#151B26',
      primaryShadow: '#11161E',
      accent: '#EAE8DB',
      accentShadow: '#BBBAAF',
      background: '#EAE8DB',
      cardBackground: '#DEDCD0',
      text: '#151B26',
      extra: '#797B7A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#383D47',
      primaryShadow: '#151B26',
      accent: '#EDEBE0',
      accentShadow: '#EAE8DB',
      background: '#151B26',
      cardBackground: '#1E242F',
      text: '#EAE8DB',
      extra: '#868887',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Calm Citadel',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AB645A',
      primaryShadow: '#895048',
      accent: '#E39B7F',
      accentShadow: '#B67C66',
      background: '#EAE5E4',
      cardBackground: '#E1DCDB',
      text: '#523237',
      extra: '#523237',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#B87B73',
      primaryShadow: '#AB645A',
      accent: '#E7AA92',
      accentShadow: '#E39B7F',
      background: '#0D0B0F',
      cardBackground: '#141216',
      text: '#F9CDA6',
      extra: '#63474B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Coral Caldera',
    tags: ['curated'],
    light: {
      primary: '#3FAAD1',
      primaryShadow: '#3288A7',
      accent: '#873766',
      accentShadow: '#6C2C52',
      background: '#E2EAEE',
      cardBackground: '#D9E1E4',
      text: '#141B33',
      extra: '#141B33',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#5CB7D8',
      primaryShadow: '#3FAAD1',
      accent: '#99557D',
      accentShadow: '#873766',
      background: '#141B33',
      cardBackground: '#1D243B',
      text: '#D7D0C2',
      extra: '#2C3247',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Minimalist Ocean',
    tags: ['warm', 'curated'],
    light: {
      primary: '#D65743',
      primaryShadow: '#AB4636',
      accent: '#CBA146',
      accentShadow: '#A28138',
      background: '#EEE4E2',
      cardBackground: '#E4DBD9',
      text: '#2A201E',
      extra: '#E1CC9D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#DC705F',
      primaryShadow: '#D65743',
      accent: '#D3AF62',
      accentShadow: '#CBA146',
      background: '#2A201E',
      cardBackground: '#332927',
      text: '#E1CC9D',
      extra: '#E4D1A7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Contemporary Field',
    tags: ['curated'],
    light: {
      primary: '#802392',
      primaryShadow: '#661C75',
      accent: '#430960',
      accentShadow: '#36074D',
      background: '#E7E0E8',
      cardBackground: '#DED7DF',
      text: '#18072A',
      extra: '#B3DAEE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9344A2',
      primaryShadow: '#802392',
      accent: '#5F2E78',
      accentShadow: '#430960',
      background: '#18072A',
      cardBackground: '#211133',
      text: '#DED4D1',
      extra: '#BBDEF0',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ice Oasis',
    tags: ['curated'],
    light: {
      primary: '#CE426B',
      primaryShadow: '#A53556',
      accent: '#294239',
      accentShadow: '#21352E',
      background: '#EDE2E5',
      cardBackground: '#E4D9DC',
      text: '#294239',
      extra: '#6B9452',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D55E81',
      primaryShadow: '#CE426B',
      accent: '#495E57',
      accentShadow: '#294239',
      background: '#0E0A0F',
      cardBackground: '#151116',
      text: '#F7C6A5',
      extra: '#7A9F63',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eternal Mind',
    tags: ['warm', 'curated'],
    light: {
      primary: '#662959',
      primaryShadow: '#522147',
      accent: '#A56368',
      accentShadow: '#844F53',
      background: '#F0EBD8',
      cardBackground: '#E4DFCD',
      text: '#662959',
      extra: '#BFB1A2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#7D4972',
      primaryShadow: '#662959',
      accent: '#B37A7F',
      accentShadow: '#A56368',
      background: '#0B090F',
      cardBackground: '#121016',
      text: '#F0EBD8',
      extra: '#C5B9AB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Fog Courtyard',
    tags: ['dark', 'curated'],
    light: {
      primary: '#04ADFB',
      primaryShadow: '#038AC9',
      accent: '#FF2500',
      accentShadow: '#CC1E00',
      background: '#F6FED9',
      cardBackground: '#EAF1CE',
      text: '#0B0808',
      extra: '#291513',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2AB9FC',
      primaryShadow: '#04ADFB',
      accent: '#FF4626',
      accentShadow: '#FF2500',
      background: '#0B0808',
      cardBackground: '#151212',
      text: '#F6FED9',
      extra: '#3E2C2B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Luminous Rampart',
    tags: ['warm', 'curated'],
    light: {
      primary: '#CA5618',
      primaryShadow: '#A24513',
      accent: '#D98F1E',
      accentShadow: '#AE7218',
      background: '#E4F279',
      cardBackground: '#D9E673',
      text: '#000A4C',
      extra: '#7F55F6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D26F3B',
      primaryShadow: '#CA5618',
      accent: '#DFA040',
      accentShadow: '#D98F1E',
      background: '#000A4C',
      cardBackground: '#0A1453',
      text: '#E4F279',
      extra: '#8C66F7',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Modern Peak',
    tags: ['cool', 'curated'],
    light: {
      primary: '#054307',
      primaryShadow: '#043606',
      accent: '#B57448',
      accentShadow: '#915D3A',
      background: '#DEFFDE',
      cardBackground: '#D3F2D3',
      text: '#300030',
      extra: '#457348',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#2B5F2C',
      primaryShadow: '#054307',
      accent: '#C08963',
      accentShadow: '#B57448',
      background: '#300030',
      cardBackground: '#380A38',
      text: '#DEFFDE',
      extra: '#58815A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Humid Waterfall',
    tags: ['warm', 'curated'],
    light: {
      primary: '#89705B',
      primaryShadow: '#6E5A49',
      accent: '#A59879',
      accentShadow: '#847A61',
      background: '#E8E6E4',
      cardBackground: '#DFDDDB',
      text: '#1A1A1A',
      extra: '#CBBA8D',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#9B8574',
      primaryShadow: '#89705B',
      accent: '#B3A78D',
      accentShadow: '#A59879',
      background: '#0C0B0F',
      cardBackground: '#131216',
      text: '#CBBA8D',
      extra: '#D0C198',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Timeless Garden',
    tags: ['warm', 'curated'],
    light: {
      primary: '#81634D',
      primaryShadow: '#674F3E',
      accent: '#A5906D',
      accentShadow: '#847357',
      background: '#E7E5E3',
      cardBackground: '#DEDCDA',
      text: '#191818',
      extra: '#D4C8BB',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#947A68',
      primaryShadow: '#81634D',
      accent: '#B3A183',
      accentShadow: '#A5906D',
      background: '#191818',
      cardBackground: '#222121',
      text: '#D4C8BB',
      extra: '#D8CEC2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Copper Promenade',
    tags: ['curated'],
    light: {
      primary: '#00BEFF',
      primaryShadow: '#0098CC',
      accent: '#FF00BE',
      accentShadow: '#CC0098',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#000000',
      extra: '#A0A29E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#26C8FF',
      primaryShadow: '#00BEFF',
      accent: '#FF26C8',
      accentShadow: '#FF00BE',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#FFFFFF',
      extra: '#AAABA8',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Avant Mosaic',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#78A7F5',
      primaryShadow: '#6086C4',
      accent: '#10081A',
      accentShadow: '#0D0615',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#10081A',
      extra: '#FCD19B',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8CB4F7',
      primaryShadow: '#78A7F5',
      accent: '#342D3C',
      accentShadow: '#10081A',
      background: '#10081A',
      cardBackground: '#1A1223',
      text: '#FFFFFF',
      extra: '#FCD6A5',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Ash Cloud',
    tags: ['warm', 'curated'],
    light: {
      primary: '#AF8A3C',
      primaryShadow: '#8C6E30',
      accent: '#D2B939',
      accentShadow: '#A8942E',
      background: '#EBE8E2',
      cardBackground: '#E2DFD9',
      text: '#571442',
      extra: '#C0A13A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#BB9C59',
      primaryShadow: '#AF8A3C',
      accent: '#D9C457',
      accentShadow: '#D2B939',
      background: '#0D0C0E',
      cardBackground: '#141315',
      text: '#F0F0F0',
      extra: '#C6AA4E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Artisan Rain',
    tags: ['warm', 'curated'],
    light: {
      primary: '#FF0F87',
      primaryShadow: '#CC0C6C',
      accent: '#93CC2B',
      accentShadow: '#76A322',
      background: '#F1F1F1',
      cardBackground: '#E5E5E5',
      text: '#000000',
      extra: '#4EDAF2',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#FF3399',
      primaryShadow: '#FF0F87',
      accent: '#A3D44B',
      accentShadow: '#93CC2B',
      background: '#000000',
      cardBackground: '#0A0A0A',
      text: '#F1F1F1',
      extra: '#60DEF3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Rocky Dusk',
    tags: ['cool', 'curated'],
    light: {
      primary: '#CC107E',
      primaryShadow: '#A30D65',
      accent: '#D941D4',
      accentShadow: '#AE34AA',
      background: '#FFFFFF',
      cardBackground: '#F2F2F2',
      text: '#0E0730',
      extra: '#8614A6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#D43491',
      primaryShadow: '#CC107E',
      accent: '#DF5EDA',
      accentShadow: '#D941D4',
      background: '#0E0730',
      cardBackground: '#181138',
      text: '#FFFFFF',
      extra: '#922CAF',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Hazy Copse',
    tags: ['curated'],
    light: {
      primary: '#5AAC1D',
      primaryShadow: '#488A17',
      accent: '#F4A30C',
      accentShadow: '#C3820A',
      background: '#E0ECEB',
      cardBackground: '#D5E0DF',
      text: '#3A3A3A',
      extra: '#3A3A3A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#73B83F',
      primaryShadow: '#5AAC1D',
      accent: '#F6B130',
      accentShadow: '#F4A30C',
      background: '#0B0D0D',
      cardBackground: '#121414',
      text: '#E0ECEB',
      extra: '#4E4E4E',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Vibrant Wave',
    tags: ['warm', 'curated'],
    light: {
      primary: '#518F7A',
      primaryShadow: '#417262',
      accent: '#43375C',
      accentShadow: '#362C4A',
      background: '#DFF788',
      cardBackground: '#D4EB81',
      text: '#2B2823',
      extra: '#2B2823',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#6BA08E',
      primaryShadow: '#518F7A',
      accent: '#5F5574',
      accentShadow: '#43375C',
      background: '#0A0C10',
      cardBackground: '#111317',
      text: '#DFF788',
      extra: '#403E39',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Enduring Desert',
    tags: ['curated'],
    light: {
      primary: '#1880F8',
      primaryShadow: '#1366C6',
      accent: '#FFE737',
      accentShadow: '#CCB92C',
      background: '#FFE737',
      cardBackground: '#F2DB34',
      text: '#151515',
      extra: '#FF82CE',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#3B93F9',
      primaryShadow: '#1880F8',
      accent: '#FFEB55',
      accentShadow: '#FFE737',
      background: '#151515',
      cardBackground: '#1E1E1E',
      text: '#FFE737',
      extra: '#FF8FD3',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Desert Veranda',
    tags: ['curated'],
    light: {
      primary: '#E901FF',
      primaryShadow: '#BA01CC',
      accent: '#F4FF01',
      accentShadow: '#C3CC01',
      background: '#C3FFAE',
      cardBackground: '#B9F2A5',
      text: '#1A1A1A',
      extra: '#018801',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#EC27FF',
      primaryShadow: '#E901FF',
      accent: '#F6FF27',
      accentShadow: '#F4FF01',
      background: '#0F0814',
      cardBackground: '#160F1B',
      text: '#C3FFAE',
      extra: '#1A941A',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
  {
    name: 'Eternal Magma',
    tags: ['bright', 'cool', 'curated'],
    light: {
      primary: '#7B67A6',
      primaryShadow: '#625285',
      accent: '#9D8BC2',
      accentShadow: '#7E6F9B',
      background: '#FAF8FF',
      cardBackground: '#EEECF2',
      text: '#1A1A1A',
      extra: '#E8C7E6',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
    dark: {
      primary: '#8F7EB3',
      primaryShadow: '#7B67A6',
      accent: '#AC9CCB',
      accentShadow: '#9D8BC2',
      background: '#0C0B11',
      cardBackground: '#131218',
      text: '#FAF8FF',
      extra: '#EACDE9',
      h1Color: 'primary',
      h2Color: 'accent',
      h3Color: 'primary',
    },
  },
];

// Font pairing definitions
export interface FontPairing {
  name: string;
  heading: string;
  headingStyle: string[];
  body: string;
  bodyStyle: string[];
}

export const fontPairings: FontPairing[] = [
  // Classic Sans-Serif Pairings
  {
    name: 'Inter & Geist',
    heading: 'Inter',
    headingStyle: ['sans'],
    body: 'Geist',
    bodyStyle: ['sans'],
  },
  {
    name: 'Montserrat & Open Sans',
    heading: 'Montserrat',
    headingStyle: ['sans', 'display'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Raleway & Lato',
    heading: 'Raleway',
    headingStyle: ['sans', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Poppins & Roboto',
    heading: 'Poppins',
    headingStyle: ['sans', 'display'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Nunito & Work Sans',
    heading: 'Nunito',
    headingStyle: ['sans', 'display'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Outfit & Inter',
    heading: 'Outfit',
    headingStyle: ['sans', 'display'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Space Grotesk & DM Sans',
    heading: 'Space Grotesk',
    headingStyle: ['sans', 'display'],
    body: 'DM Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Manrope & Source Sans 3',
    heading: 'Manrope',
    headingStyle: ['sans'],
    body: 'Source Sans 3',
    bodyStyle: ['sans'],
  },
  {
    name: 'Plus Jakarta Sans & Inter',
    heading: 'Plus Jakarta Sans',
    headingStyle: ['sans', 'display'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Josefin Sans & Nunito',
    heading: 'Josefin Sans',
    headingStyle: ['sans', 'display'],
    body: 'Nunito',
    bodyStyle: ['sans'],
  },

  // Serif + Sans-Serif Pairings (Classic & Elegant)
  {
    name: 'Playfair Display & Source Sans Pro',
    heading: 'Playfair Display',
    headingStyle: ['serif', 'display'],
    body: 'Source Sans Pro',
    bodyStyle: ['sans'],
  },
  {
    name: 'Libre Baskerville & Open Sans',
    heading: 'Libre Baskerville',
    headingStyle: ['serif'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Merriweather & Lato',
    heading: 'Merriweather',
    headingStyle: ['serif', 'slab'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Lora & Nunito Sans',
    heading: 'Lora',
    headingStyle: ['serif'],
    body: 'Nunito Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Crimson Pro & Work Sans',
    heading: 'Crimson Pro',
    headingStyle: ['serif'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Cormorant Garamond & Raleway',
    heading: 'Cormorant Garamond',
    headingStyle: ['serif', 'display'],
    body: 'Raleway',
    bodyStyle: ['sans'],
  },
  {
    name: 'EB Garamond & Inter',
    heading: 'EB Garamond',
    headingStyle: ['serif'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Spectral & Karla',
    heading: 'Spectral',
    headingStyle: ['serif'],
    body: 'Karla',
    bodyStyle: ['sans'],
  },
  {
    name: 'Bitter & Nunito',
    heading: 'Bitter',
    headingStyle: ['serif', 'slab'],
    body: 'Nunito',
    bodyStyle: ['sans'],
  },
  {
    name: 'Cardo & Lato',
    heading: 'Cardo',
    headingStyle: ['serif'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // Modern Display + Sans Pairings
  {
    name: 'Bebas Neue & Roboto',
    heading: 'Bebas Neue',
    headingStyle: ['sans', 'display'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Oswald & Open Sans',
    heading: 'Oswald',
    headingStyle: ['sans', 'display'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Anton & Roboto Condensed',
    heading: 'Anton',
    headingStyle: ['sans', 'display'],
    body: 'Roboto Condensed',
    bodyStyle: ['sans'],
  },
  {
    name: 'Archivo Black & Archivo',
    heading: 'Archivo Black',
    headingStyle: ['sans', 'display'],
    body: 'Archivo',
    bodyStyle: ['sans'],
  },
  {
    name: 'Righteous & Lato',
    heading: 'Righteous',
    headingStyle: ['sans', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // Geometric Sans Pairings
  {
    name: 'Spartan & Work Sans',
    heading: 'Spartan',
    headingStyle: ['sans'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Jost & Inter',
    heading: 'Jost',
    headingStyle: ['sans'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Red Hat Display & Red Hat Text',
    heading: 'Red Hat Display',
    headingStyle: ['sans', 'display'],
    body: 'Red Hat Text',
    bodyStyle: ['sans'],
  },
  {
    name: 'Quicksand & Lato',
    heading: 'Quicksand',
    headingStyle: ['sans', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // Slab Serif Pairings
  {
    name: 'Zilla Slab & Lato',
    heading: 'Zilla Slab',
    headingStyle: ['serif', 'slab'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Arvo & Open Sans',
    heading: 'Arvo',
    headingStyle: ['serif', 'slab'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Roboto Slab & Roboto',
    heading: 'Roboto Slab',
    headingStyle: ['serif', 'slab'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Crete Round & Lato',
    heading: 'Crete Round',
    headingStyle: ['serif', 'slab'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Bree Serif & Open Sans',
    heading: 'Bree Serif',
    headingStyle: ['serif', 'slab'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Alfa Slab One & Roboto',
    heading: 'Alfa Slab One',
    headingStyle: ['serif', 'slab', 'display'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Josefin Slab & Josefin Sans',
    heading: 'Josefin Slab',
    headingStyle: ['serif', 'slab'],
    body: 'Josefin Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Volkhov & Lato',
    heading: 'Volkhov',
    headingStyle: ['serif', 'slab'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Noticia Text & Open Sans',
    heading: 'Noticia Text',
    headingStyle: ['serif', 'slab'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Rokkitt & Work Sans',
    heading: 'Rokkitt',
    headingStyle: ['serif', 'slab'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Slabo 27px & Source Sans Pro',
    heading: 'Slabo 27px',
    headingStyle: ['serif', 'slab'],
    body: 'Source Sans Pro',
    bodyStyle: ['sans'],
  },
  {
    name: 'Patua One & Lato',
    heading: 'Patua One',
    headingStyle: ['serif', 'slab', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Kreon & Inter',
    heading: 'Kreon',
    headingStyle: ['serif', 'slab'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Sanchez & Roboto',
    heading: 'Sanchez',
    headingStyle: ['serif', 'slab'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Podkova & Lato',
    heading: 'Podkova',
    headingStyle: ['serif', 'slab'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // Monospace Pairings
  {
    name: 'JetBrains Mono & Inter',
    heading: 'JetBrains Mono',
    headingStyle: ['mono'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Fira Code & Fira Sans',
    heading: 'Fira Code',
    headingStyle: ['mono'],
    body: 'Fira Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'IBM Plex Mono & IBM Plex Sans',
    heading: 'IBM Plex Mono',
    headingStyle: ['mono'],
    body: 'IBM Plex Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Space Mono & Work Sans',
    heading: 'Space Mono',
    headingStyle: ['mono'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Roboto Mono & Roboto',
    heading: 'Roboto Mono',
    headingStyle: ['mono'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },

  // Handwriting & Script Pairings
  {
    name: 'Pacifico & Lato',
    heading: 'Pacifico',
    headingStyle: ['handwriting', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Dancing Script & Josefin Sans',
    heading: 'Dancing Script',
    headingStyle: ['handwriting', 'display'],
    body: 'Josefin Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Caveat & Open Sans',
    heading: 'Caveat',
    headingStyle: ['handwriting'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Permanent Marker & Roboto',
    heading: 'Permanent Marker',
    headingStyle: ['handwriting', 'display'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },

  // Elegant & Editorial Pairings
  {
    name: 'Cinzel & Raleway',
    heading: 'Cinzel',
    headingStyle: ['serif', 'display'],
    body: 'Raleway',
    bodyStyle: ['sans'],
  },
  {
    name: 'Yeseva One & Josefin Sans',
    heading: 'Yeseva One',
    headingStyle: ['serif', 'display'],
    body: 'Josefin Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Abril Fatface & Lato',
    heading: 'Abril Fatface',
    headingStyle: ['serif', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Bodoni Moda & Montserrat',
    heading: 'Bodoni Moda',
    headingStyle: ['serif', 'display'],
    body: 'Montserrat',
    bodyStyle: ['sans'],
  },

  // Condensed Pairings
  {
    name: 'Fjalla One & Noto Sans',
    heading: 'Fjalla One',
    headingStyle: ['sans', 'display'],
    body: 'Noto Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Barlow Condensed & Barlow',
    heading: 'Barlow Condensed',
    headingStyle: ['sans', 'display'],
    body: 'Barlow',
    bodyStyle: ['sans'],
  },
  {
    name: 'Pathway Gothic One & Open Sans',
    heading: 'Pathway Gothic One',
    headingStyle: ['sans', 'display'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },

  // Unique & Creative Pairings
  {
    name: 'Comfortaa & Lato',
    heading: 'Comfortaa',
    headingStyle: ['sans', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Fredoka & Nunito',
    heading: 'Fredoka',
    headingStyle: ['sans', 'display'],
    body: 'Nunito',
    bodyStyle: ['sans'],
  },
  {
    name: 'Lexend & Inter',
    heading: 'Lexend',
    headingStyle: ['sans'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Sora & Work Sans',
    heading: 'Sora',
    headingStyle: ['sans'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Urbanist & Inter',
    heading: 'Urbanist',
    headingStyle: ['sans'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },

  // League of Moveable Type Fonts
  {
    name: 'League Gothic & League Spartan',
    heading: 'League Gothic',
    headingStyle: ['sans', 'display'],
    body: 'League Spartan',
    bodyStyle: ['sans'],
  },
  {
    name: 'League Spartan & Junction',
    heading: 'League Spartan',
    headingStyle: ['sans'],
    body: 'Junction',
    bodyStyle: ['sans'],
  },
  {
    name: 'Ostrich Sans & League Spartan',
    heading: 'Ostrich Sans',
    headingStyle: ['sans', 'display'],
    body: 'League Spartan',
    bodyStyle: ['sans'],
  },
  {
    name: 'Ultra & Junction',
    heading: 'Ultra',
    headingStyle: ['serif', 'slab', 'display'],
    body: 'Junction',
    bodyStyle: ['sans'],
  },
  {
    name: 'Blackout & League Spartan',
    heading: 'Blackout',
    headingStyle: ['sans', 'display'],
    body: 'League Spartan',
    bodyStyle: ['sans'],
  },
  {
    name: 'Raleway & Junction',
    heading: 'Raleway',
    headingStyle: ['sans', 'display'],
    body: 'Junction',
    bodyStyle: ['sans'],
  },
  {
    name: 'Sorts Mill Goudy & League Spartan',
    heading: 'Sorts Mill Goudy',
    headingStyle: ['serif'],
    body: 'League Spartan',
    bodyStyle: ['sans'],
  },
  {
    name: 'Goudy Bookletter 1911 & Junction',
    heading: 'Goudy Bookletter 1911',
    headingStyle: ['serif'],
    body: 'Junction',
    bodyStyle: ['sans'],
  },
  {
    name: 'Linden Hill & League Spartan',
    heading: 'Linden Hill',
    headingStyle: ['serif'],
    body: 'League Spartan',
    bodyStyle: ['sans'],
  },
  {
    name: 'Fanwood & Junction',
    heading: 'Fanwood',
    headingStyle: ['serif'],
    body: 'Junction',
    bodyStyle: ['sans'],
  },
  {
    name: 'Prociono & League Spartan',
    heading: 'Prociono',
    headingStyle: ['serif'],
    body: 'League Spartan',
    bodyStyle: ['sans'],
  },
  {
    name: 'Orbitron & League Spartan',
    heading: 'Orbitron',
    headingStyle: ['sans', 'display'],
    body: 'League Spartan',
    bodyStyle: ['sans'],
  },
  {
    name: 'League Mono & Junction',
    heading: 'League Mono',
    headingStyle: ['mono'],
    body: 'Junction',
    bodyStyle: ['sans'],
  },
  {
    name: 'Sniglet & League Spartan',
    heading: 'Sniglet',
    headingStyle: ['sans', 'display'],
    body: 'League Spartan',
    bodyStyle: ['sans'],
  },
  {
    name: 'League Script & Junction',
    heading: 'League Script',
    headingStyle: ['handwriting', 'display'],
    body: 'Junction',
    bodyStyle: ['sans'],
  },
  {
    name: 'Knewave & League Spartan',
    heading: 'Knewave',
    headingStyle: ['handwriting', 'display'],
    body: 'League Spartan',
    bodyStyle: ['sans'],
  },

  // LOMT + Google Fonts Combos
  {
    name: 'League Gothic & Open Sans',
    heading: 'League Gothic',
    headingStyle: ['sans', 'display'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Ostrich Sans & Roboto',
    heading: 'Ostrich Sans',
    headingStyle: ['sans', 'display'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Ultra & Lato',
    heading: 'Ultra',
    headingStyle: ['serif', 'slab', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Sorts Mill Goudy & Open Sans',
    heading: 'Sorts Mill Goudy',
    headingStyle: ['serif'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Orbitron & Work Sans',
    heading: 'Orbitron',
    headingStyle: ['sans', 'display'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'League Mono & Inter',
    heading: 'League Mono',
    headingStyle: ['mono'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'League Script & Lato',
    heading: 'League Script',
    headingStyle: ['handwriting', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // Corny Display Fonts (Fun & Quirky!)
  {
    name: 'Graduate & Roboto',
    heading: 'Graduate',
    headingStyle: ['display', 'corny'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Geostar & Work Sans',
    heading: 'Geostar',
    headingStyle: ['display', 'corny'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Geostar Fill & Work Sans',
    heading: 'Geostar Fill',
    headingStyle: ['display', 'corny'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Gajraj One & Lato',
    heading: 'Gajraj One',
    headingStyle: ['display', 'corny'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Faster One & Roboto',
    heading: 'Faster One',
    headingStyle: ['display', 'corny'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Fascinate Inline & Open Sans',
    heading: 'Fascinate Inline',
    headingStyle: ['display', 'corny'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Diplomata & Lato',
    heading: 'Diplomata',
    headingStyle: ['display', 'corny'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Codystar & Inter',
    heading: 'Codystar',
    headingStyle: ['display', 'corny'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Bungee Shade & Roboto',
    heading: 'Bungee Shade',
    headingStyle: ['display', 'corny'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Bangers & Lato',
    heading: 'Bangers',
    headingStyle: ['display', 'corny'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Audiowide & Work Sans',
    heading: 'Audiowide',
    headingStyle: ['display', 'corny'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Atomic Age & Open Sans',
    heading: 'Atomic Age',
    headingStyle: ['display', 'corny'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Monoton & Roboto',
    heading: 'Monoton',
    headingStyle: ['display', 'corny'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Press Start 2P & Inter',
    heading: 'Press Start 2P',
    headingStyle: ['display', 'corny', 'mono'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Silkscreen & Roboto',
    heading: 'Silkscreen',
    headingStyle: ['display', 'corny', 'mono'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Vast Shadow & Lato',
    heading: 'Vast Shadow',
    headingStyle: ['display', 'corny'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Honk & Work Sans',
    heading: 'Honk',
    headingStyle: ['display', 'corny'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'UnifrakturMaguntia & Lato',
    heading: 'UnifrakturMaguntia',
    headingStyle: ['display', 'corny', 'blackletter'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // Sci-Fi Fonts from DaFont (100% Free for Commercial Use)
  {
    name: 'Ethnocentric & Roboto',
    heading: 'Ethnocentric',
    headingStyle: ['display', 'sci-fi', 'corny'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Conthrax & Work Sans',
    heading: 'Conthrax',
    headingStyle: ['display', 'sci-fi', 'corny'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Nasalization & Inter',
    heading: 'Nasalization',
    headingStyle: ['display', 'sci-fi', 'corny'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Nulshock & Lato',
    heading: 'Nulshock',
    headingStyle: ['display', 'sci-fi', 'corny'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Dune Rise & Roboto',
    heading: 'Dune Rise',
    headingStyle: ['display', 'sci-fi', 'corny'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Good Timing & Open Sans',
    heading: 'Good Timing',
    headingStyle: ['display', 'sci-fi', 'corny'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Venus Rising & Work Sans',
    heading: 'Venus Rising',
    headingStyle: ['display', 'sci-fi', 'corny'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Future Earth & Inter',
    heading: 'Future Earth',
    headingStyle: ['display', 'sci-fi', 'corny'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },

  // Google Fonts Sci-Fi/Futuristic (all available on Google Fonts)
  {
    name: 'Orbitron & Roboto',
    heading: 'Orbitron',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Orbitron & Open Sans',
    heading: 'Orbitron',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Audiowide & Work Sans',
    heading: 'Audiowide',
    headingStyle: ['display', 'sci-fi'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Audiowide & Inter',
    heading: 'Audiowide',
    headingStyle: ['display', 'sci-fi'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Exo 2 & Roboto',
    heading: 'Exo 2',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Exo 2 & Lato',
    heading: 'Exo 2',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Rajdhani & Open Sans',
    heading: 'Rajdhani',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Michroma & Roboto',
    heading: 'Michroma',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Electrolize & Inter',
    heading: 'Electrolize',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Teko & Work Sans',
    heading: 'Teko',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Chakra Petch & Roboto',
    heading: 'Chakra Petch',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Chakra Petch & Inter',
    heading: 'Chakra Petch',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Share Tech Mono & Roboto',
    heading: 'Share Tech Mono',
    headingStyle: ['mono', 'display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Oxanium & Open Sans',
    heading: 'Oxanium',
    headingStyle: ['display', 'sci-fi'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Blanka & Lato',
    heading: 'Blanka',
    headingStyle: ['display', 'sci-fi'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // Additional CDNFonts Sci-Fi/Futuristic
  {
    name: 'Zeroes One & Roboto',
    heading: 'Zeroes One',
    headingStyle: ['display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'UFO Hunter & Inter',
    heading: 'UFO Hunter',
    headingStyle: ['display', 'sci-fi'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'ZOOMING track & Work Sans',
    heading: 'ZOOMING track',
    headingStyle: ['display', 'sci-fi'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Technofosiano & Open Sans',
    heading: 'Technofosiano',
    headingStyle: ['display', 'sci-fi', 'bitmap'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },

  // CDNFonts Display/Decorative
  {
    name: 'VAG Primer & Lato',
    heading: 'VAG Primer',
    headingStyle: ['sans', 'display', 'rounded'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'VAG Primer & Open Sans',
    heading: 'VAG Primer',
    headingStyle: ['sans', 'display', 'rounded'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Braile Font & Roboto',
    heading: 'Braile Font',
    headingStyle: ['display', 'novelty'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Globoface-Gothic-Display-2001 & Inter',
    heading: 'Globoface-Gothic-Display-2001',
    headingStyle: ['display', 'gothic'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'REM & Roboto',
    heading: 'REM',
    headingStyle: ['sans', 'display'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'REM & Work Sans',
    heading: 'REM',
    headingStyle: ['sans', 'display'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Grafeno St & Lato',
    heading: 'Grafeno St',
    headingStyle: ['display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Tender Goliath Small-Caps & Inter',
    heading: 'Tender Goliath Small-Caps',
    headingStyle: ['display', 'small-caps'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Fat Wandals & Roboto',
    heading: 'Fat Wandals',
    headingStyle: ['display', 'handwriting', 'decorative'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Soloist Halftone & Open Sans',
    heading: 'Soloist Halftone',
    headingStyle: ['display', 'retro', 'halftone'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Soloist Halftone & Work Sans',
    heading: 'Soloist Halftone',
    headingStyle: ['display', 'retro', 'halftone'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Aspex & Inter',
    heading: 'Aspex',
    headingStyle: ['display'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'The Blood Shack & Roboto',
    heading: 'The Blood Shack',
    headingStyle: ['display', 'horror', 'grunge'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'The Blood Shack & Lato',
    heading: 'The Blood Shack',
    headingStyle: ['display', 'horror', 'grunge'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // Additional CDNFonts Sci-Fi/Techno
  {
    name: 'Subatomic Tsoonami & Roboto',
    heading: 'Subatomic Tsoonami',
    headingStyle: ['display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Sagan & Inter',
    heading: 'Sagan',
    headingStyle: ['display', 'sci-fi'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Masterforce & Work Sans',
    heading: 'Masterforce',
    headingStyle: ['display', 'sci-fi'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Young Techs & Roboto',
    heading: 'Young Techs',
    headingStyle: ['display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'New English & Inter',
    heading: 'New English',
    headingStyle: ['display', 'sci-fi', 'alien'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Abyssopelagic & Lato',
    heading: 'Abyssopelagic',
    headingStyle: ['display', 'sci-fi'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // CDNFonts Display/Decorative continued
  {
    name: 'Cokelines & Open Sans',
    heading: 'Cokelines',
    headingStyle: ['display', 'decorative'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Deacon Blues & Roboto',
    heading: 'Deacon Blues',
    headingStyle: ['display', 'retro'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Eagle GT II & Inter',
    heading: 'Eagle GT II',
    headingStyle: ['sans', 'display'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'e-Pececito & Work Sans',
    heading: 'e-Pececito',
    headingStyle: ['display', 'experimental'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Oval Black & Lato',
    heading: 'Oval Black',
    headingStyle: ['sans', 'display', 'rounded'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Pocket Calculator & Roboto',
    heading: 'Pocket Calculator',
    headingStyle: ['display', 'retro', 'digital'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'ROBLOX Display & Inter',
    heading: 'ROBLOX Display',
    headingStyle: ['sans', 'display', 'gaming'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Nexa & Open Sans',
    heading: 'Nexa',
    headingStyle: ['sans', 'display'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Nexa & Lato',
    heading: 'Nexa',
    headingStyle: ['sans', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Nikoleta & Roboto',
    heading: 'Nikoleta',
    headingStyle: ['display'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'THUNDERBLACK DEMO & Work Sans',
    heading: 'THUNDERBLACK DEMO',
    headingStyle: ['display', 'bold'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Nagaro & Inter',
    heading: 'Nagaro',
    headingStyle: ['display', 'brush'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'g Gemos & Lato',
    heading: 'g Gemos',
    headingStyle: ['display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'B-TEAM & Roboto',
    heading: 'B-TEAM',
    headingStyle: ['display', 'stencil'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Incheon Nights & Open Sans',
    heading: 'Incheon Nights',
    headingStyle: ['display', 'neon', 'retro'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },

  // CDNFonts Pixel/Bitmap
  {
    name: 'Neon Pixel-7 & Roboto',
    heading: 'Neon Pixel-7',
    headingStyle: ['display', 'pixel', 'retro'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Monster Friend Back & Inter',
    heading: 'Monster Friend Back',
    headingStyle: ['display', 'pixel', 'gaming'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Aux DotBitC & Work Sans',
    heading: 'Aux DotBitC',
    headingStyle: ['display', 'pixel', 'retro'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },

  // CDNFonts Script/Handwriting
  {
    name: 'Breaking Road & Lato',
    heading: 'Breaking Road',
    headingStyle: ['handwriting', 'script', 'brush'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Homework & Roboto',
    heading: 'Homework',
    headingStyle: ['handwriting', 'script'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Wisdom Merry & Inter',
    heading: 'Wisdom Merry',
    headingStyle: ['handwriting', 'script'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Interval & Open Sans',
    heading: 'Interval',
    headingStyle: ['handwriting', 'script', 'calligraphy'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'junita script & Work Sans',
    heading: 'junita script',
    headingStyle: ['handwriting', 'script', 'cursive'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Hariston & Lato',
    heading: 'Hariston',
    headingStyle: ['handwriting', 'script', 'brush'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },

  // Additional CDNFonts Sci-Fi/Tech
  {
    name: 'Nuixyber Pro & Roboto',
    heading: 'Nuixyber Pro',
    headingStyle: ['sans', 'display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Mechsuit & Inter',
    heading: 'Mechsuit',
    headingStyle: ['display', 'sci-fi', 'stencil'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'SF Planetary Orbiter & Work Sans',
    heading: 'SF Planetary Orbiter',
    headingStyle: ['display', 'sci-fi'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Speedeasy Speedy & Roboto',
    heading: 'Speedeasy Speedy',
    headingStyle: ['display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'SPIDER & Inter',
    heading: 'SPIDER',
    headingStyle: ['display', 'sci-fi'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Flexsteel & Open Sans',
    heading: 'Flexsteel',
    headingStyle: ['display', 'sci-fi'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Rezzzistor4F & Lato',
    heading: 'Rezzzistor4F',
    headingStyle: ['display', 'sci-fi'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Tidy Curve TV & Roboto',
    heading: 'Tidy Curve TV',
    headingStyle: ['display', 'sci-fi'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Led Panel Station Off & Inter',
    heading: 'Led Panel Station Off',
    headingStyle: ['display', 'retro', 'digital'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },

  // Additional CDNFonts Display
  {
    name: 'Alba & Work Sans',
    heading: 'Alba',
    headingStyle: ['sans', 'display', 'groovy'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Brose & Roboto',
    heading: 'Brose',
    headingStyle: ['sans', 'display'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Sebaldus-Gotisch & Lato',
    heading: 'Sebaldus-Gotisch',
    headingStyle: ['display', 'gothic', 'medieval'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Jelmiroz & Inter',
    heading: 'Jelmiroz',
    headingStyle: ['sans', 'display'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'Micro N55 & Roboto',
    heading: 'Micro N55',
    headingStyle: ['display', 'pixel'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'China Fad & Open Sans',
    heading: 'China Fad',
    headingStyle: ['display', 'novelty'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Red October & Work Sans',
    heading: 'Red October',
    headingStyle: ['display', 'novelty'],
    body: 'Work Sans',
    bodyStyle: ['sans'],
  },
  {
    name: 'Wide awake Black & Lato',
    heading: 'Wide awake Black',
    headingStyle: ['sans', 'display'],
    body: 'Lato',
    bodyStyle: ['sans'],
  },
  {
    name: 'Suissnord & Roboto',
    heading: 'Suissnord',
    headingStyle: ['sans', 'display'],
    body: 'Roboto',
    bodyStyle: ['sans'],
  },
  {
    name: 'Crox & Inter',
    heading: 'Crox',
    headingStyle: ['sans', 'display'],
    body: 'Inter',
    bodyStyle: ['sans'],
  },
  {
    name: 'g Gelem & Open Sans',
    heading: 'g Gelem',
    headingStyle: ['sans', 'display'],
    body: 'Open Sans',
    bodyStyle: ['sans'],
  },
];

// Helper to get all unique tags from color themes
export function getAllThemeTags(): string[] {
  const tags = new Set<string>();
  colorThemes.forEach(theme => {
    theme.tags?.forEach(tag => tags.add(tag));
  });
  return Array.from(tags).sort();
}
